import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  DEMO_SHOPIFY_ORDERS,
  DEMO_PROFITABILITY_ORDERS,
  DEMO_LOTS,
  DEMO_SUPPLIES,
  DEMO_ROASTED_SKUS,
  DEMO_ROAST_BATCHES,
  DEMO_CUPPINGS,
  DEMO_CUPPING_SOURCES,
  DEMO_CUPPERS,
  DEMO_SAMPLES,
  DEMO_PRODUCED_ORDERS,
  DEMO_ROASTING_QUEUE,
  DEMO_WHOLESALE_CONFIG,
  DEMO_WHOLESALE_PRODUCTS,
  DEMO_WHOLESALE_CUSTOMERS,
  DEMO_INVOICES,
  DEMO_POS,
  DEMO_BILLING_SETTINGS,
} from "./demoData";
import {
  Package, BarChart3, Warehouse, Coffee, Settings,
  Plus, Check, Clock, AlertTriangle, TrendingDown, TrendingUp, DollarSign,
  Search, Flame, Thermometer, X, Archive, Grid3X3,
  Tag, Activity, Users, HelpCircle, ExternalLink, LayoutGrid,
  Link, Unlink, Loader, ShoppingBag, Store, RefreshCw,
  Receipt, ClipboardList, Pencil, ScrollText, SkipForward, Droplets,
  Truck, MapPin, Trash2, Navigation, GripVertical, LogOut, Shield, UserPlus,
  Radio, Cpu, Copy, Wifi, WifiOff, CheckCircle2, Globe, BookOpen,
  CreditCard, Lock, ImageIcon, Upload, SlidersHorizontal,
  FileText, Send, Building2, Calendar, ChevronDown, ChevronUp, ChevronRight,
  BadgeCheck, AlertCircle, Banknote, FilePlus, Layers, Handshake, Repeat,
  Mail, AtSign, Download
} from "lucide-react";
import { useAuth, can, isPlatformOwner, ROLE_LABELS, ROLE_DESCRIPTIONS, INVITABLE_ROLES, CAPABILITY_TO_ROLES } from "./useAuth.js";
import NotificationsBell from "./NotificationsBell.jsx";
import PendingRequestsCard from "./PendingRequestsCard.jsx";
import { PurchasingPage } from "./Purchasing.jsx";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart as ReBarChart, Bar, Legend,
  ReferenceLine, ReferenceDot, ComposedChart, Line, Brush
} from "recharts";

/* ─── Unit conversion ────────────────────────────────────────────
   Tiny helpers for the weight unit toggle. Anywhere we display a
   weight, the user should be able to swap kg ⇄ lb on the spot.
   `<UnitWeight>` is read-only display + click-to-toggle; the user
   preference for which unit to render in is sticky. */
const KG_PER_LB = 0.45359237;
const lbToKg = (n) => +(Number(n) * KG_PER_LB).toFixed(3);
const kgToLb = (n) => +(Number(n) / KG_PER_LB).toFixed(2);
function useWeightUnit() {
  return useStickyState("weightUnit", "lb"); // "lb" | "kg"
}
/* Inline weight chip — accepts a value in `valueLbs`, renders in the
   user's preferred unit, and toggles on click. Drop-in for any weight
   readout that was previously a hardcoded "{n} lb" template. */
function UnitWeight({ valueLbs, decimals, suffix = "" }) {
  const [unit, setUnit] = useWeightUnit();
  if (valueLbs == null || !Number.isFinite(Number(valueLbs))) return <span style={{ color: "#9CA3AF" }}>—</span>;
  const lbs = Number(valueLbs);
  const display = unit === "kg" ? lbToKg(lbs) : lbs;
  const fixed = decimals != null ? Number(display).toFixed(decimals) : (display % 1 === 0 ? String(display) : Number(display).toFixed(2));
  return (
    <span
      onClick={(e) => { e.stopPropagation(); setUnit(unit === "kg" ? "lb" : "kg"); }}
      role="button"
      title={`Click to convert to ${unit === "kg" ? "lb" : "kg"}`}
      style={{
        cursor: "pointer", borderBottom: "1px dotted #D1D5DB", whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {fixed}<span style={{ marginLeft: 3, color: "#6B7280", fontSize: "0.85em" }}>{unit}{suffix}</span>
    </span>
  );
}

/* ─── Order channels ─────────────────────────────────────────────
   Every order belongs to a channel — Shopify (both online retail and
   B2B / draft orders all roll up here, since they're the same source
   platform), EDI (1:1 with retailer trading partners), Transfer
   (manual / POS / cafe internal), or Wholesale Lite (orders placed
   through Elion's own portal once it ships). Online vs B2B-via-Shopify
   used to be split but the distinction wasn't useful — both are
   Shopify orders, both flow through the same fulfilment pipeline. */
const CHANNEL_TINT = {
  shopify:        { bg: "#F0FDF4", fg: "#047857", border: "#BBF7D0", label: "Shopify" },
  edi:            { bg: "#F5F3FF", fg: "#6D28D9", border: "#DDD6FE", label: "EDI" },
  transfer:       { bg: "#FEF3C7", fg: "#92400E", border: "#FDE68A", label: "Transfer" },
  wholesale_lite: { bg: "#EFF6FF", fg: "#1D4ED8", border: "#BFDBFE", label: "Wholesale" },
};
// Single source of truth for turning a raw Shopify order into the
// shape the Orders table expects. Used by both the bulk fetch and the
// live order-number search so paid/fulfilled orders pulled on demand
// render identically to the unfulfilled set.
function mapShopifyOrder(o) {
  return {
    id: `#${o.order_number}`,
    shopifyId: o.id,
    customer: o.customer ? `${o.customer.first_name} ${o.customer.last_name}` : (o.billing_address?.name || "Guest"),
    date: new Date(o.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    items: (o.line_items || []).map(li => ({
      productId: li.product_id,
      name: li.title,
      variant: li.variant_title || "",
      sku: li.sku || "",
      qty: li.quantity,
      grams: li.grams,
    })),
    total: parseFloat(o.total_price),
    tagged: (o.tags || "").split(", ").some(t => t.trim().toLowerCase() === "production"),
    tags: o.tags || "",
    financialStatus: o.financial_status,
    fulfillmentStatus: o.fulfillment_status,
    shippingAddress: o.shipping_address
      ? {
          address1: o.shipping_address.address1 || "",
          city: o.shipping_address.city || "",
          province: o.shipping_address.province || "",
          province_code: o.shipping_address.province_code || "",
          zip: o.shipping_address.zip || "",
          latitude: o.shipping_address.latitude ?? null,
          longitude: o.shipping_address.longitude ?? null,
          name: o.shipping_address.name || "",
        }
      : null,
  };
}
function deriveOrderChannel(order) {
  if (!order) return "shopify";
  if (order.channel && CHANNEL_TINT[order.channel]) return order.channel;
  // Legacy values from earlier persistence (pre-Shopify-collapse) —
  // route them to the consolidated Shopify channel.
  if (order.channel === "online" || order.channel === "wholesale") return "shopify";
  const src = String(order.source_name || order.sourceName || "").toLowerCase();
  if (src.includes("edi"))   return "edi";
  if (src.includes("pos") || src.includes("manual") || src.includes("transfer")) return "transfer";
  // Anything else from Shopify (online / draft / b2b) is just "Shopify".
  return "shopify";
}
function ChannelPill({ channel, size = "sm" }) {
  const t = CHANNEL_TINT[channel] || CHANNEL_TINT.online;
  const padding = size === "lg" ? "4px 11px" : "2px 8px";
  const fontSize = size === "lg" ? 11 : 10;
  return (
    <span style={{
      display: "inline-block", padding, borderRadius: 99,
      fontSize, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase",
      background: t.bg, color: t.fg, border: `1px solid ${t.border}`,
      fontFamily: "Inter, sans-serif", whiteSpace: "nowrap",
    }}>{t.label}</span>
  );
}

/* ─── Sticky state ───────────────────────────────────────────────
   Drop-in replacement for useState that persists to localStorage so
   navigation / selection survives a refresh. Values are JSON-serialised;
   functions are not supported. Pass `null` to clear. */
function useStickyState(key, initial) {
  const fullKey = `pr:${key}`;
  const [val, setVal] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" ? localStorage.getItem(fullKey) : null;
      if (raw == null) return typeof initial === "function" ? initial() : initial;
      return JSON.parse(raw);
    } catch { return typeof initial === "function" ? initial() : initial; }
  });
  useEffect(() => {
    try {
      if (val === null || val === undefined) localStorage.removeItem(fullKey);
      else localStorage.setItem(fullKey, JSON.stringify(val));
    } catch { /* ignore */ }
  }, [fullKey, val]);
  return [val, setVal];
}

/* ─── FONT ─── */
(() => {
  if (typeof document !== "undefined" && !document.querySelector('link[href*="Inter"]')) {
    const l = document.createElement("link");
    l.href = "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap";
    l.rel = "stylesheet";
    document.head.appendChild(l);
  }
})();

/* ─── Leaflet (for Routes map) — loaded lazily from CDN, grayscaled
       via CSS so the basemap stays monochrome. ─── */
(() => {
  if (typeof document === "undefined") return;
  if (!document.getElementById("leaflet-css")) {
    const l = document.createElement("link");
    l.id = "leaflet-css";
    l.rel = "stylesheet";
    l.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(l);
  }
  if (!document.getElementById("leaflet-js")) {
    const s = document.createElement("script");
    s.id = "leaflet-js";
    s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    s.async = true;
    document.head.appendChild(s);
  }
  if (!document.getElementById("pr-map-grayscale")) {
    const st = document.createElement("style");
    st.id = "pr-map-grayscale";
    st.textContent = `
      .pr-map .leaflet-tile {
        filter: grayscale(1) contrast(0.95) brightness(1.05);
      }
      .pr-map .leaflet-control-attribution { font-size: 10px; color: #9CA3AF; }
      .pr-map .leaflet-control-zoom a { background: #fff; color: #374151; border-color: #E5E7EB; }
      .pr-map .leaflet-control-zoom a:hover { background: #F3F4F6; }
      .pr-map .leaflet-popup-content-wrapper { background: #000000; color: #fff; border-radius: 6px; font-family: Inter, sans-serif; }
      .pr-map .leaflet-popup-tip { background: #000000; }
      .pr-map .leaflet-popup-content { margin: 6px 10px; font-size: 12px; }
      .pr-map .pr-map-pin {
        width: 24px; height: 24px; border-radius: 50%;
        background: #000000; color: #fff;
        display: flex; align-items: center; justify-content: center;
        font-size: 11px; font-weight: 700; font-family: Inter, sans-serif;
        border: 2px solid #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.2);
      }
      .pr-map .pr-map-pin.start { background: #000000; }
      .pr-map .pr-map-pin.end { background: #6B7280; }
      .pr-map .pr-map-pin.stop { background: #374151; }
      /* Driver-map pin variants keyed by per-stop delivery status. */
      .pr-map .pr-map-pin.done    { background: #10B981; }
      .pr-map .pr-map-pin.skip    { background: #F59E0B; }
      .pr-map .pr-map-pin.pending { background: #9CA3AF; }
      .pr-map .pr-map-pin.next    {
        background: #22D3EE; color: #042328;
        box-shadow: 0 0 0 3px #fff, 0 0 0 6px rgba(34,211,238,0.5), 0 0 14px rgba(34,211,238,0.65);
        animation: pr-map-pin-pulse 1.6s ease-in-out infinite;
      }
      @keyframes pr-map-pin-pulse {
        0%, 100% { box-shadow: 0 0 0 3px #fff, 0 0 0 6px rgba(34,211,238,0.55), 0 0 14px rgba(34,211,238,0.7); }
        50%      { box-shadow: 0 0 0 3px #fff, 0 0 0 9px rgba(34,211,238,0.25), 0 0 22px rgba(34,211,238,0.35); }
      }
    `;
    document.head.appendChild(st);
  }
})();

/* ─── Global gold-shine keyframes for Excellence-tier scores ─── */
(() => {
  if (typeof document !== "undefined" && !document.getElementById("pr-gold-shine-style")) {
    const s = document.createElement("style");
    s.id = "pr-gold-shine-style";
    s.textContent = `
      @keyframes pr-gold-shine {
        0%   { background-position: 300% 50%; }
        100% { background-position: 0%   50%; }
      }
      .pr-gold {
        background: linear-gradient(105deg,
          #B8860B 0%,
          #B8860B 30%,
          #BE8F1C 38%,
          #C89D30 44%,
          #D2AA46 48%,
          #D8B252 50%,
          #D2AA46 52%,
          #C89D30 56%,
          #BE8F1C 62%,
          #B8860B 70%,
          #B8860B 100%);
        background-size: 300% auto;
        background-position: 300% 50%;
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        -webkit-text-fill-color: transparent;
        animation: pr-gold-shine 7s linear infinite;
      }
    `;
    document.head.appendChild(s);
  }
})();

/* ─── ELION LOGO ─── */
const ElionLogo = ({ size = 30, color = "#fff" }) => (
  <svg width={size} height={size * (44.9 / 75.8)} viewBox="0 0 75.8 44.9" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.7,44.9H3.1c-1.7,0-3.1-1.4-3.1-3.1v-8.6c0-.8.3-1.6.9-2.2L31,.9c.6-.6,1.4-.9,2.2-.9h8.6c1.7,0,3.1,1.4,3.1,3.1v8.6c0,.8-.3,1.6-.9,2.2l-30.1,30.1c-.6.6-1.4.9-2.2.9Z" fill={color} />
    <path d="M42.6,44.9h-8.6c-1.7,0-3.1-1.4-3.1-3.1v-8.6c0-.8.3-1.6.9-2.2L61.9.9c.6-.6,1.4-.9,2.2-.9h8.6c1.7,0,3.1,1.4,3.1,3.1v8.6c0,.8-.3,1.6-.9,2.2l-30.1,30.1c-.6.6-1.4.9-2.2.9Z" fill={color} />
  </svg>
);

/* ─── MOCK DATA ─── */
const PRODUCTS_BASE = [
  { id: 1, name: "La Esperanza", sub: "Washed Caturra", origin: "Huila, Colombia", score: 87, process: "Washed", variety: "Caturra", altitude: "1,750m", roastLevel: "Medium", greenPerLb: 4.2, retailPrice: 22, sku: "PCF-ESP-WC", greenStockLbs: 312, roastedStockLbs: 48 },
  { id: 2, name: "El Paraíso", sub: "Honey Gesha", origin: "Cauca, Colombia", score: 92, process: "Honey", variety: "Gesha", altitude: "1,950m", roastLevel: "Light", greenPerLb: 12.5, retailPrice: 48, sku: "PCF-PAR-HG", greenStockLbs: 85, roastedStockLbs: 12 },
  { id: 3, name: "PCF Decaf", sub: "EA Process", origin: "Nariño, Colombia", score: 84, process: "EA Decaf", variety: "Colombia", altitude: "1,600m", roastLevel: "Medium", greenPerLb: 5.8, retailPrice: 20, sku: "PCF-DCF-MD", greenStockLbs: 150, roastedStockLbs: 22 },
  { id: 4, name: "Finca La Riviera", sub: "Natural Bourbon", origin: "Tolima, Colombia", score: 89, process: "Natural", variety: "Bourbon", altitude: "1,850m", roastLevel: "Medium-Light", greenPerLb: 6.4, retailPrice: 28, sku: "PCF-RIV-NB", greenStockLbs: 200, roastedStockLbs: 35 },
  { id: 5, name: "Elion Blend", sub: "House Blend", origin: "Huila / Tolima", score: 85, process: "Washed / Natural", variety: "Blend", altitude: "1,700m", roastLevel: "Medium", greenPerLb: 3.9, retailPrice: 18, sku: "PCF-BLD-HS", greenStockLbs: 420, roastedStockLbs: 65 },
];

const genCurve = (seed, level) => {
  const pts = [];
  const charge = 370 + (seed % 30), tp = 175 + (seed % 15);
  const fc = level === "Light" ? 395 : level === "Medium-Light" ? 400 : 405;
  const drop = level === "Light" ? 410 : level === "Medium-Light" ? 418 : level === "Medium" ? 425 : 430;
  const total = level === "Light" ? 10.5 : level === "Medium-Light" ? 11.5 : 12.5;
  const fcT = total * 0.72;
  for (let i = 0; i <= 100; i++) {
    const t = (i / 100) * total, n = i / 100;
    let bt = n < 0.05 ? charge - (charge - tp) * (n / 0.05) : n < 0.72 ? tp + (fc - tp) * (1 - Math.pow(1 - (n - 0.05) / 0.67, 1.6)) : fc + (drop - fc) * ((n - 0.72) / 0.28);
    bt += Math.sin(seed * i * 0.1) * 1.5;
    let ror = i > 5 ? ((bt - (pts[i - 3]?.bt || bt)) / ((3 / 100) * total)) * 0.5 : 0;
    pts.push({ time: +(t).toFixed(2), timeLabel: `${Math.floor(t)}:${String(Math.round((t % 1) * 60)).padStart(2, "0")}`, bt: +bt.toFixed(1), ror: Math.max(0, +ror.toFixed(1)), et: +((bt + 45 + Math.sin(n * 3) * 10)).toFixed(1) });
  }
  return { points: pts, fcTime: +fcT.toFixed(2), totalTime: total, dropTemp: Math.round(drop), chargeTemp: charge };
};
const PROFILES = {}; PRODUCTS_BASE.forEach(p => { PROFILES[p.id] = genCurve(p.id * 17 + 3, p.roastLevel); });

const INIT_ORDERS = [
  { id: "ORD-4821", customer: "Blue Bottle — Oakland", date: "Apr 15", items: [{ productId: 1, name: "La Esperanza", qty: 30 }, { productId: 5, name: "Elion Blend", qty: 20 }], total: 342, tagged: false },
  { id: "ORD-4822", customer: "Verve Coffee — Santa Cruz", date: "Apr 15", items: [{ productId: 2, name: "El Paraíso", qty: 10 }, { productId: 4, name: "Finca La Riviera", qty: 15 }], total: 517, tagged: false },
  { id: "ORD-4823", customer: "Onyx Coffee Lab", date: "Apr 14", items: [{ productId: 1, name: "La Esperanza", qty: 50 }, { productId: 3, name: "PCF Decaf", qty: 25 }], total: 555, tagged: true },
  { id: "ORD-4824", customer: "Sey Coffee — Brooklyn", date: "Apr 14", items: [{ productId: 2, name: "El Paraíso", qty: 20 }], total: 960, tagged: false },
  { id: "ORD-4825", customer: "Methodical Coffee", date: "Apr 13", items: [{ productId: 5, name: "Elion Blend", qty: 40 }, { productId: 4, name: "Finca La Riviera", qty: 20 }], total: 488, tagged: true },
  { id: "ORD-4826", customer: "Equator Coffees — SF", date: "Apr 13", items: [{ productId: 1, name: "La Esperanza", qty: 25 }, { productId: 3, name: "PCF Decaf", qty: 50 }], total: 395, tagged: false },
  { id: "ORD-4827", customer: "Cat & Cloud", date: "Apr 12", items: [{ productId: 5, name: "Elion Blend", qty: 15 }], total: 135, tagged: true },
  { id: "ORD-4828", customer: "George Howell Coffee", date: "Apr 12", items: [{ productId: 2, name: "El Paraíso", qty: 5 }, { productId: 1, name: "La Esperanza", qty: 20 }], total: 286, tagged: false },
];

const ACTIVITY = [
  { date: "Apr 15", type: "roast", product: "La Esperanza", detail: "60 lb green → 50.4 lb roasted" },
  { date: "Apr 14", type: "roast", product: "Elion Blend", detail: "80 lb green → 67.2 lb roasted" },
  { date: "Apr 13", type: "receive", product: "El Paraíso", detail: "150 lb from Daniel — Cauca" },
  { date: "Apr 12", type: "roast", product: "PCF Decaf", detail: "45 lb green → 37.8 lb roasted" },
  { date: "Apr 11", type: "receive", product: "La Esperanza", detail: "300 lb from Daniel — Huila" },
];

/* ─── UPGRADE WALL ─────────────────────────────────────────────────
   Rendered in place of the page body when the user clicks a locked
   nav item. Pure inline-style, no external deps. Per-module copy +
   a single CTA that opens the public pricing page in a new tab so
   the trial user can pick a bundle and check out via Stripe. */
const UPGRADE_WALL_COPY = {
  lab: {
    label: "Lab",
    price: "$129/mo",
    headline: "Cup, score, and trace every sample back to a roast.",
    bullets: [
      "Cupping sessions with score sheets and flavor wheel scoring",
      "Sample tracking — origin, importer, vendor, lot",
      "Sample → PO flow: approve a sample, drop it into a vendor PO",
      "External guest cuppers: invite buyers, importers, judges",
      "Standalone samples library for sessions in progress",
    ],
  },
  finance: {
    label: "Finance",
    price: "$99/mo",
    headline: "Get paid faster with automated statements + AR.",
    bullets: [
      "Account statements via Resend — clean PDF + Excel",
      "Inbound auto-reply: customers email **{handle}@elionlabs.org** and get a statement back",
      "Shopify unpaid order aggregation across all your customers",
      "Automated overdue reminders on a weekly or biweekly schedule",
      "Purchase orders → vendor → receiving flow",
    ],
  },
  inventory: {
    label: "Inventory",
    price: "$79/mo",
    headline: "Track every green lot, supply, and allocation.",
    bullets: [
      "Green coffee lot tracking with origin/importer/cost basis",
      "Supplies — bags, labels, valves, tape — with low-stock alerts",
      "Receiving log: every PO line item received, with weights",
      "Lot allocations to roast batches with weight-loss tracking",
      "Two years of historical lot data for cost-per-lb trends",
    ],
  },
  production: {
    label: "Production",
    price: "$129/mo",
    headline: "Roast, pack, and ship — one connected flow.",
    bullets: [
      "Roast log with Artisan integration (.alog drag-and-drop)",
      "Batch tracking with weight-loss, drop temp, first crack",
      "Packing workflow — Ready / Produced / Skipped + packing list",
      "Routes & shipping — driver assignments, label printing",
      "Live roast sessions (coming soon)",
    ],
  },
  analytics: {
    label: "Analytics",
    price: "$99/mo",
    headline: "Profitability, throughput, and produced-by-product.",
    bullets: [
      "Revenue + cost over time, real-time profitability",
      "Produced-by-product breakdown across any time range",
      "Customer cohort analysis — by orders, by revenue, by months",
      "Trend arrows + 12-month sparklines on every stat tile",
      "Forecasting based on month-end snapshots, not in-progress months",
    ],
    footnote: "Analytics requires at least one other module — there's no data to chart standalone.",
  },
  wholesale_lite: {
    label: "Wholesale Lite",
    price: "$59/mo + $0.20/order",
    headline: "Customer-facing online ordering portal — minimal and fast.",
    bullets: [
      "Your wholesale clients log in and see THEIR pricing",
      "Place orders directly — flow into Finance + Production",
      "No retraining: looks like Shopify, behaves like an order form",
      "Add-on. Stacks on top of any bundle or à la carte plan.",
    ],
  },
};

// Slugify a company name into a safe email local-part. e.g. "Roblox Coffee Co." → "roblox-coffee-co".
function emailHandleFromName(name) {
  if (!name) return null;
  const slug = String(name).toLowerCase().trim()
    .replace(/[&+]/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
  return slug || null;
}

// Render a string with **bold** markers as <strong> spans. Used inside
// upgrade-wall bullets so we can highlight a workspace's inbound email.
function renderBoldMarkup(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i} style={{ color: "#000", fontWeight: 700 }}>{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}

function UpgradeWall({ moduleKey, onDismiss, companyName }) {
  const copy = UPGRADE_WALL_COPY[moduleKey] || {
    label: "This module",
    price: "Upgrade required",
    headline: "Upgrade your plan to unlock this module.",
    bullets: [],
  };
  const handle = emailHandleFromName(companyName) || "[your-company]";
  const interpolate = (s) => String(s).replace(/\{handle\}/g, handle);
  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "8px 0 80px" }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 99, background: "#F4F4F5", color: "#6B7280", fontSize: 12, fontWeight: 600, letterSpacing: 0.04, textTransform: "uppercase", marginBottom: 18 }}>
        <Lock size={11} strokeWidth={2.4} /> Locked module
      </div>
      <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: "-0.02em", color: "#000", lineHeight: 1.15, margin: "0 0 8px" }}>
        {copy.label} <span style={{ color: "#9CA3AF", fontWeight: 600 }}>· {copy.price}</span>
      </h1>
      <p style={{ fontSize: 17, color: "#3F3F46", lineHeight: 1.55, margin: "0 0 28px" }}>
        {copy.headline}
      </p>
      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: "22px 26px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", letterSpacing: 0.16, textTransform: "uppercase", marginBottom: 12 }}>What you get</div>
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {copy.bullets.map((b, i) => (
            <li key={i} style={{ fontSize: 14, color: "#000", lineHeight: 1.55, padding: "8px 0 8px 26px", position: "relative", borderTop: i === 0 ? "none" : "1px solid #F3F4F6" }}>
              <span style={{ position: "absolute", left: 0, top: 11, width: 16, height: 16, borderRadius: "50%", background: "#ECFDF5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="9" height="9" viewBox="0 0 9 9"><path d="M1 4.5L3.5 7L8 1.5" stroke="#047857" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
              {renderBoldMarkup(interpolate(b))}
            </li>
          ))}
        </ul>
        {copy.footnote && (
          <div style={{ marginTop: 14, padding: "10px 12px", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, fontSize: 12, color: "#92400E", lineHeight: 1.5 }}>
            {copy.footnote}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
        <a
          href="/pricing"
          target="_blank"
          rel="noopener noreferrer"
          style={{ padding: "12px 22px", background: "#000", color: "#fff", borderRadius: 10, fontSize: 14, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          See pricing & upgrade →
        </a>
        <button
          onClick={onDismiss}
          style={{ padding: "12px 18px", background: "#fff", color: "#000", border: "1px solid #E5E7EB", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
        >
          Back to app
        </button>
      </div>
    </div>
  );
}

/* ─── ROAST CURVE ─── */
/* Deterministic per-product synthetic curve for demo mode. Seeded by
   productId so each coffee gets its own shape that's stable across
   renders. Returns points in {time(sec), bt, et, ror} format. */
function syntheticCurveFor(productId) {
  let h = 2166136261;
  const s = String(productId || "");
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  const rng = () => { h = Math.imul(h ^ (h >>> 15), 2246822507); h = Math.imul(h ^ (h >>> 13), 3266489909); return ((h ^= h >>> 16) >>> 0) / 4294967296; };
  const durationMin = 9.5 + rng() * 4;       // 9.5–13.5 min
  const intervalSec = 15;
  const totalSec = Math.round(durationMin * 60);
  const steps = Math.floor(totalSec / intervalSec);
  const chargeTemp = 345 + rng() * 12;       // 345–357°F
  const tpDip      = 8 + rng() * 8;          // 8–16°F dip
  const tpTemp     = chargeTemp - tpDip;
  const dropTemp   = 395 + rng() * 22;       // 395–417°F
  const tpPct      = 0.08 + rng() * 0.08;    // TP at 8–16% through roast
  const curveShape = 0.3 + rng() * 0.45;     // accel exponent: light → dark
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const pct = i / steps;
    let bt;
    if (pct <= tpPct) {
      bt = chargeTemp + (tpTemp - chargeTemp) * (pct / tpPct);
    } else {
      const rise = (pct - tpPct) / (1 - tpPct);
      bt = tpTemp + (dropTemp - tpTemp) * (rise * rise * curveShape + rise * (1 - curveShape));
    }
    bt += (rng() - 0.5) * 1.0;
    const et = bt + 26 + rng() * 16;
    const rorBase = pct > tpPct && pct < 0.85 ? 11 - (pct - tpPct) * 9 : 2;
    const ror = Math.max(0, rorBase + (rng() - 0.5) * 1.5);
    pts.push({ time: i * intervalSec, bt: Math.round(bt * 10) / 10, et: Math.round(et * 10) / 10, ror: Math.round(ror * 10) / 10 });
  }
  return pts;
}

/* Fetches the most recent uploaded curve for a product and renders it
   inline. In demo mode, falls back to a deterministic per-id synthetic
   curve so every coffee gets its own shape. */
const ProductMiniCurve = ({ productId, demoMode = false }) => {
  const [points, setPoints] = useState(null);
  useEffect(() => {
    if (demoMode) { setPoints(syntheticCurveFor(productId)); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/roast-curves?productId=${encodeURIComponent(productId)}`);
        if (!r.ok) return;
        const d = await r.json();
        const first = (d.curves || [])[0];
        if (!first) { if (!cancelled) setPoints([]); return; }
        const r2 = await fetch(`/api/roast-curves/${first.id}`);
        if (!r2.ok) return;
        const d2 = await r2.json();
        if (!cancelled) setPoints(d2.curve?.points || []);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [productId, demoMode]);
  if (points == null) return null;
  if (points.length === 0) return null;
  return <RoastCurve productId={productId} height={40} mini points={points} />;
};

/* Renders a unified-shape points array [{time,bt,et?,ror?,air?,drum?,
   damper?,burner?}] from roast-curves.js. Returns null when there's no
   real data — no mock fallback. Mirrors the Artisan layout: temp on the
   left axis (0 → ~250 °C), RoR on the right axis (0 → ~30 °C/min), and
   knob channels (Air / Drum / Damper / Burner) plotted on the temp axis
   so their 0–100% values land naturally below the bean curve. */
const RoastCurve = ({ productId, height = 300, mini = false, points, metadata = null, emptyPlaceholder = null }) => {
  if (!Array.isArray(points) || points.length === 0) return emptyPlaceholder;

  /* Normalise to recharts shape. Keep nulls as nulls so step/line
     gaps don't draw zero baselines. */
  const rawPoints = points.map(p => {
    const secs = Number(p.time) || 0;
    const mm = Math.floor(secs / 60);
    const ss = String(Math.round(secs % 60)).padStart(2, "0");
    const num = (v) => v == null ? null : Number(v);
    return {
      timeMin: +(secs / 60).toFixed(3),
      timeLabel: `${mm}:${ss}`,
      bt: num(p.bt), et: num(p.et), ror: num(p.ror),
      air: num(p.air), drum: num(p.drum), damper: num(p.damper), burner: num(p.burner),
    };
  });

  const hasChannel = (k) => rawPoints.some(p => p[k] != null);
  const showET     = !mini && hasChannel("et");
  const showRoR    = !mini && hasChannel("ror");
  const showAir    = !mini && hasChannel("air");
  const showDrum   = !mini && hasChannel("drum");
  const showDamper = !mini && hasChannel("damper");
  const showBurner = !mini && hasChannel("burner");

  /* Temp axis: round up to next 50 above the max BT/ET so the curve
     never crowds the top edge. Floor at 0 so knob channels (0-100%)
     fit at the bottom of the chart. */
  const allTemps = rawPoints.flatMap(p => [p.bt, p.et]).filter(x => x != null);
  const tempMax = Math.max(250, Math.ceil(Math.max(...allTemps, 0) / 50) * 50);

  /* RoR axis: cap at the next multiple of 5 above max RoR. */
  const allRor = rawPoints.map(p => p.ror).filter(x => x != null);
  const rorMax = Math.max(25, Math.ceil(Math.max(...allRor, 0) / 5) * 5);

  /* Phase markers — mapped to minute-domain x via timeMin. Values come
     from metadata (in seconds). Look up nearest BT for the dot's y. */
  const m = metadata || {};
  const nearestBt = (sec) => {
    const target = sec / 60;
    let closest = null, bestDt = Infinity;
    for (const p of rawPoints) {
      const dt = Math.abs(p.timeMin - target);
      if (dt < bestDt && p.bt != null) { bestDt = dt; closest = p; }
    }
    return closest?.bt ?? null;
  };
  const fmtMmSs = (sec) => {
    if (sec == null || !Number.isFinite(sec)) return "";
    const mm = Math.floor(sec / 60), ss = String(Math.round(sec % 60)).padStart(2, "0");
    return `${mm}:${ss}`;
  };
  const phaseDots = !mini ? [
    { sec: 0,                 bt: m.chargeTemp ?? rawPoints[0]?.bt ?? null, label: "CHARGE" },
    m.tpTime     != null && { sec: m.tpTime,     bt: nearestBt(m.tpTime),     label: "TP" },
    m.dryEndTime != null && { sec: m.dryEndTime, bt: nearestBt(m.dryEndTime), label: "DE" },
    m.fcTime     != null && { sec: m.fcTime,     bt: nearestBt(m.fcTime),     label: "1Cs" },
    m.scTime     != null && { sec: m.scTime,     bt: nearestBt(m.scTime),     label: "2Cs" },
    m.totalTime  != null && { sec: m.totalTime,  bt: m.dropTemp ?? rawPoints[rawPoints.length - 1]?.bt ?? null, label: "DROP" },
  ].filter(Boolean) : [];

  /* Phase percentages for the top bar — Dry (charge → DE), Maillard
     (DE → 1Cs), Dev (1Cs → DROP). Only render when we have all of DE,
     FC, and totalTime. */
  const phaseBar = (() => {
    if (mini) return null;
    const total = m.totalTime;
    const de = m.dryEndTime, fc = m.fcTime;
    if (total == null || de == null || fc == null) return null;
    const dryPct = +(de / total * 100).toFixed(1);
    const mailPct = +((fc - de) / total * 100).toFixed(1);
    const devPct = +(((total - fc) / total) * 100).toFixed(1);
    return { dryPct, mailPct, devPct, dryT: de, mailT: fc - de, devT: total - fc };
  })();

  /* Downsample for the mini sparkline only. Keep full resolution for
     the main view so the curve is smooth. */
  const data = mini ? rawPoints.filter((_, i) => i % 5 === 0) : rawPoints;

  /* Mini variant — same BT line + Y-domain as the detail chart so the
     two visually agree. No axes/legend; margins zeroed so the curve
     fills the cell edge-to-edge. */
  if (mini) {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          <defs>
            <linearGradient id={`bt${productId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#000000" stopOpacity={0.10} />
              <stop offset="100%" stopColor="#000000" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis hide dataKey="timeMin" type="number" domain={["dataMin", "dataMax"]} />
          <YAxis hide yAxisId="temp" domain={[0, tempMax]} />
          <Area yAxisId="temp" type="monotone" dataKey="bt" stroke="#000000" strokeWidth={1.6} fill={`url(#bt${productId})`} dot={false} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  return (
    <div>
      {/* Phase bar — Dry / Maillard / Dev with time + percentage.
          Monochrome-first: three steps of gray with a black Dev pill.
          Left/right padding matches the chart's YAxis widths + chart
          margins so the bar sits exactly over the plot area. */}
      {phaseBar && (
        <div style={{
          display: "flex",
          height: 24,
          borderRadius: 4,
          overflow: "hidden",
          marginBottom: 12,
          fontFamily: "Inter, sans-serif",
          border: "1px solid #E5E7EB",
          // Match ComposedChart inset: margin.left (0) + YAxis width (40)
          // on the left; margin.right (16) + RoR YAxis width (32 when
          // shown, 0 otherwise) on the right.
          marginLeft: 40,
          marginRight: 16 + (showRoR ? 32 : 0),
        }}>
          <div style={{ flex: phaseBar.dryPct, background: "#F4F4F5", color: "#3F3F46", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 500, gap: 6, minWidth: 0 }}>
            <span>Dry</span><span style={{ opacity: 0.5 }}>·</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtMmSs(phaseBar.dryT)}</span><span style={{ opacity: 0.5 }}>·</span><span>{phaseBar.dryPct}%</span>
          </div>
          <div style={{ flex: phaseBar.mailPct, background: "#E4E4E7", color: "#27272A", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 500, gap: 6, minWidth: 0, borderLeft: "1px solid #fff", borderRight: "1px solid #fff" }}>
            <span>Maillard</span><span style={{ opacity: 0.5 }}>·</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtMmSs(phaseBar.mailT)}</span><span style={{ opacity: 0.5 }}>·</span><span>{phaseBar.mailPct}%</span>
          </div>
          <div style={{ flex: phaseBar.devPct, background: "#000000", color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 500, gap: 6, minWidth: 0 }}>
            <span>Dev</span><span style={{ opacity: 0.6 }}>·</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtMmSs(phaseBar.devT)}</span><span style={{ opacity: 0.6 }}>·</span><span>{phaseBar.devPct}%</span>
          </div>
        </div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 16, right: 16, left: 0, bottom: 4 }}>
          <defs>
            <linearGradient id={`bt${productId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#000000" stopOpacity={0.10} />
              <stop offset="100%" stopColor="#000000" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
          <XAxis
            dataKey="timeMin"
            type="number"
            domain={["dataMin", "dataMax"]}
            tickFormatter={(v) => {
              const s = Math.round(v * 60);
              const mm = Math.floor(s / 60), ss = String(s % 60).padStart(2, "0");
              return `${mm}:${ss}`;
            }}
            tick={{ fontSize: 11, fill: "#9CA3AF" }}
            axisLine={false} tickLine={false}
          />
          <YAxis yAxisId="temp" domain={[0, tempMax]} tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} width={40} />
          {showRoR && <YAxis yAxisId="ror" orientation="right" domain={[0, rorMax]} tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} width={32} />}
          <Tooltip
            labelFormatter={(v) => {
              const s = Math.round(v * 60);
              const mm = Math.floor(s / 60), ss = String(s % 60).padStart(2, "0");
              return `${mm}:${ss}`;
            }}
            formatter={(value, name) => {
              if (value == null) return ["—", name];
              if (name === "RoR") return [`${value} °C/min`, name];
              if (["Air", "Drum", "Damper", "Burner"].includes(name)) return [`${value}%`, name];
              return [`${value} °C`, name];
            }}
            contentStyle={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, fontFamily: "Inter", boxShadow: "0 4px 12px rgba(0,0,0,0.04)" }}
          />
          <Legend verticalAlign="top" align="right" iconSize={10} wrapperStyle={{ fontSize: 12, paddingBottom: 4 }} />

          {/* BT — primary line, pure black with a soft fill underneath */}
          <Area yAxisId="temp" type="monotone" dataKey="bt" stroke="#000000" strokeWidth={2.2} fill={`url(#bt${productId})`} name="Bean Temp" dot={false} isAnimationActive={false} />
          {/* ET — Israeli blue */}
          {showET && <Line yAxisId="temp" type="monotone" dataKey="et" stroke="#0038B8" strokeWidth={1.6} dot={false} name="Env Temp" isAnimationActive={false} />}
          {/* RoR — cyan, dashed; on the right axis, only after TP */}
          {showRoR && <Line yAxisId="ror" type="monotone" dataKey="ror" stroke="#22D3EE" strokeWidth={1.8} strokeDasharray="5 4" dot={false} name="RoR" connectNulls={false} isAnimationActive={false} />}
          {/* Knob channels — order Drum → Burner → Damper → Air to match
              roaster convention. Stepped gray scale keeps them recessive.
              A two-letter abbreviation tag (Dr / Bu / Da / Ai) is rendered
              at the very first point so each line is readable without
              hunting in the legend. */}
          {(() => {
            const firstIdx = (key) => data.findIndex(d => d[key] != null);
            const tag = (abbr, color) => ({ x, y, index }) => index === firstIdx(abbr.key) && y != null ? (
              <text x={x - 4} y={y} dy={3} textAnchor="end" fontSize={10} fontWeight={700} fontFamily="Inter, sans-serif" fill={color} letterSpacing={0.4}>{abbr.label}</text>
            ) : null;
            return (
              <>
                {showDrum   && <Line yAxisId="temp" type="stepAfter" dataKey="drum"   stroke="#27272A" strokeWidth={1.2} dot={false} name="Drum"   connectNulls isAnimationActive={false} label={tag({ key: "drum",   label: "Dr" }, "#27272A")} />}
                {showBurner && <Line yAxisId="temp" type="stepAfter" dataKey="burner" stroke="#52525B" strokeWidth={1.2} strokeDasharray="2 2" dot={false} name="Burner" connectNulls isAnimationActive={false} label={tag({ key: "burner", label: "Bu" }, "#52525B")} />}
                {showDamper && <Line yAxisId="temp" type="stepAfter" dataKey="damper" stroke="#6B7280" strokeWidth={1.2} dot={false} name="Damper" connectNulls isAnimationActive={false} label={tag({ key: "damper", label: "Da" }, "#6B7280")} />}
                {showAir    && <Line yAxisId="temp" type="stepAfter" dataKey="air"    stroke="#9CA3AF" strokeWidth={1.2} dot={false} name="Air"    connectNulls isAnimationActive={false} label={tag({ key: "air",    label: "Ai" }, "#9CA3AF")} />}
              </>
            );
          })()}

          {/* Phase markers — labeled dots pinned to the BT curve */}
          {phaseDots.map((d, i) => d.bt != null && (
            <ReferenceDot
              key={`${d.label}-${i}`}
              yAxisId="temp"
              x={+(d.sec / 60).toFixed(3)}
              y={d.bt}
              r={4}
              fill="#fff"
              stroke="#111827"
              strokeWidth={2}
              ifOverflow="extendDomain"
              label={{
                position: d.label === "CHARGE" || d.label === "TP" ? "bottom" : "top",
                value: `${d.label} · ${fmtMmSs(d.sec)} · ${Math.round(d.bt)}°`,
                fontSize: 10,
                fontWeight: 600,
                fontFamily: "Inter, sans-serif",
                fill: "#111827",
                offset: 8,
              }}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ═════════════════════════════════════════════════════════════════
   LIVE ROAST CARD — sits at the top of Production → Breakdown when a
   Pi bridge device is actively streaming. Polls /api/roasts?status=live
   every 2s for the summary, then fetches the full sample array for
   whichever roast is currently active so we can render the live curve.
   Quietly returns null when nothing's streaming — no empty state.
   ═════════════════════════════════════════════════════════════════ */
function LiveRoastCard({ pendingBatch = null, onStartRequested = null, rorWindowSec = 30, demoMode = false } = {}) {
  const [liveSummary, setLiveSummary] = useState(null); // { id, startedAt, latest, ... } | null
  const [fullRoast, setFullRoast] = useState(null);     // { samples: [...] } | null
  const [ending, setEnding] = useState(false);
  const [starting, setStarting] = useState(false);

  /* Poll the summary list on a 2s cadence. Cheap endpoint — just filters
     .roasts.json by status. */
  const fetchSummary = useCallback(async () => {
    try {
      const r = await fetch("/api/roasts?status=live&limit=1");
      if (!r.ok) return;
      const d = await r.json();
      setLiveSummary((d.roasts && d.roasts[0]) || null);
    } catch { /* ignore; next tick will retry */ }
  }, []);
  useEffect(() => {
    fetchSummary();
    const t = setInterval(fetchSummary, 2000);
    return () => clearInterval(t);
  }, [fetchSummary]);

  /* When the active roast changes (or we first learn about one), pull the
     full sample array. Re-fetch on a 2s cadence while it's live so the
     curve grows with new samples. */
  useEffect(() => {
    if (!liveSummary?.id) { setFullRoast(null); return; }
    let cancelled = false;
    const fetchFull = async () => {
      try {
        const r = await fetch(`/api/roasts/${encodeURIComponent(liveSummary.id)}`);
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        setFullRoast(d.roast || null);
      } catch { /* ignore */ }
    };
    fetchFull();
    const t = setInterval(fetchFull, 2000);
    return () => { cancelled = true; clearInterval(t); };
  }, [liveSummary?.id]);

  const startRoast = async () => {
    setStarting(true);
    try {
      if (onStartRequested) {
        /* Parent owns the "start" action — it knows which batch is
           staged in Next and wires the resulting roastId to the
           launch-pad state. */
        await onStartRequested();
      } else {
        /* Standalone fallback (e.g. if the card is used without a
           launch pad): plain demo roast. */
        await fetch("/api/demo/roast/start", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
      }
      /* Kick off an immediate summary fetch so the card flips to live
         without waiting for the next 2s tick. */
      setTimeout(fetchSummary, 600);
    } finally {
      setStarting(false);
    }
  };
  const saveRoast = async () => {
    if (!liveSummary?.id) return;
    setEnding(true);
    try {
      /* Save = mark ended. Record stays in .roasts.json so it shows up
         in Activity. Also stop any simulator pumping into it. */
      await fetch(`/api/roasts/${encodeURIComponent(liveSummary.id)}/end`, { method: "POST" });
      await fetch("/api/demo/roast/stop", { method: "POST" }).catch(() => {});
      setLiveSummary(null);
      setFullRoast(null);
    } finally {
      setEnding(false);
    }
  };
  const cancelRoast = async () => {
    if (!liveSummary?.id) return;
    /* Destructive — drops the whole record. Confirm so a fat-finger
       doesn't erase a good roast. */
    if (!window.confirm("Cancel this roast? The samples will be discarded and won't appear in Activity.")) return;
    setEnding(true);
    try {
      await fetch("/api/demo/roast/stop", { method: "POST" }).catch(() => {});
      await fetch(`/api/roasts/${encodeURIComponent(liveSummary.id)}`, { method: "DELETE" });
      setLiveSummary(null);
      setFullRoast(null);
    } finally {
      setEnding(false);
    }
  };

  /* Shared card shell — subtle cyan-tinted near-black gradient. Shadow
     is intentionally light so the card sits flush with the page
     instead of floating; the 1px cyan border carries most of the
     "lit panel" feel. Used by both the idle CTA and the live state. */
  const shellStyle = {
    background: "linear-gradient(135deg, #05060A 0%, #0A0E14 55%, #070C12 100%)",
    color: "#fff",
    borderRadius: 14,
    padding: 18,
    marginBottom: 20,
    fontFamily: "Inter, sans-serif",
    position: "relative",
    overflow: "hidden",
    border: "1px solid rgba(34,211,238,0.18)",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
  };
  /* Decorative corner glow — rendered behind the content so it feels
     like the panel is lit from the inside. */
  const cornerGlow = (
    <>
      <div style={{ position: "absolute", top: -60, right: -60, width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle, rgba(34,211,238,0.18) 0%, rgba(34,211,238,0) 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -80, left: -40, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(167,139,250,0.14) 0%, rgba(167,139,250,0) 70%)", pointerEvents: "none" }} />
    </>
  );

  /* curvePoints must be declared unconditionally (Rules of Hooks) even
     though it's only used in the live state below. When there's no active
     roast the samples array is empty and the result is []. */
  const firstTs = fullRoast?.samples?.[0]?.ts || Date.now();
  const curvePoints = useMemo(() => {
    const samples = fullRoast?.samples || [];
    return samples.map((s, i) => {
      const timeSec = Math.max(0, (s.ts - firstTs) / 1000);
      let ror = 0;
      if (rorWindowSec <= 1) {
        if (i > 0) {
          const prev = samples[i - 1];
          const dt = (s.ts - prev.ts) / 60_000;
          if (dt > 0) ror = (s.bt - prev.bt) / dt;
        }
      } else {
        let j = i - 1;
        while (j >= 0 && (s.ts - samples[j].ts) / 1000 < rorWindowSec) j--;
        const ref = samples[Math.max(0, j + 1)];
        const dt = (s.ts - ref.ts) / 60_000;
        if (dt > 0) ror = (s.bt - ref.bt) / dt;
      }
      return { time: timeSec, bt: s.bt, et: s.et, ror: +ror.toFixed(1) };
    });
  }, [fullRoast, firstTs, rorWindowSec]);

  /* Idle state — no live roast.
     - In demo mode: show the gradient "Roast now" CTA, which spins up
       the simulator on the server.
     - Outside demo mode: real customers without paired hardware just see
       a polite "contact support" notice. We don't want to dangle a
       Start button that fakes a roast for live users. */
  if (!liveSummary) {
    if (!demoMode) {
      return (
        <div style={{ ...shellStyle, display: "flex", alignItems: "center", gap: 14 }}>
          {cornerGlow}
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <WifiOff size={20} color="rgba(255,255,255,0.5)" strokeWidth={1.8} />
          </div>
          <div style={{ position: "relative" }}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.005em" }}>
              Hardware not connected
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>
              Please contact support to pair your roaster — <a href="mailto:hello@elionlabs.org" style={{ color: "#22D3EE", textDecoration: "none" }}>hello@elionlabs.org</a>.
            </div>
          </div>
        </div>
      );
    }
    const stagedLabel = pendingBatch
      ? `${pendingBatch.productName} · Batch ${pendingBatch.batchIndex + 1} of ${pendingBatch.totalBatches}`
      : null;
    return (
      <div style={{ ...shellStyle, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        {cornerGlow}
        <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative" }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: "linear-gradient(135deg, rgba(34,211,238,0.18) 0%, rgba(167,139,250,0.14) 100%)",
            border: "1px solid rgba(34,211,238,0.3)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <Flame size={22} color="#22D3EE" strokeWidth={1.8} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.005em" }}>
              {stagedLabel ? "Ready to roast" : "Roast now"}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>
              {stagedLabel
                ? <>On deck: <span style={{ color: "#22D3EE", fontWeight: 600 }}>{stagedLabel}</span></>
                : "Stage a batch in Next, or start a free-form demo roast."}
            </div>
          </div>
        </div>
        <button
          onClick={startRoast}
          disabled={starting}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "10px 18px", borderRadius: 10,
            border: "1px solid rgba(34,211,238,0.6)",
            background: "linear-gradient(135deg, #22D3EE 0%, #06B6D4 100%)",
            color: "#042328", fontSize: 14, fontWeight: 700,
            cursor: starting ? "default" : "pointer",
            fontFamily: "Inter, sans-serif",
            opacity: starting ? 0.6 : 1,
            boxShadow: "0 0 20px rgba(34,211,238,0.4)",
            position: "relative",
          }}
        >
          <Flame size={14} strokeWidth={2.4} />
          {starting ? "Starting…" : stagedLabel ? "Start roast" : "Roast now"}
        </button>
      </div>
    );
  }

  const latest = liveSummary.latest || {};
  const elapsedSec = Math.max(0, Math.floor((Date.now() - (liveSummary.startedAt || Date.now())) / 1000));
  const mm = Math.floor(elapsedSec / 60);
  const ss = String(elapsedSec % 60).padStart(2, "0");
  const fmtTemp = (v) => v == null ? "—" : `${Math.round(v)}°F`;
  const fmtRor  = (v) => v == null ? "—" : `${v > 0 ? "+" : ""}${Math.round(v)}/min`;

  /* curvePoints and firstTs are already declared above the idle-state
     early return (Rules of Hooks — hooks must be unconditional). */
  /* Downsample for longer roasts so recharts doesn't eat frames. Keep
     the last 10 samples unconditionally so the curve looks live. */
  const chartData = curvePoints.length > 400
    ? curvePoints.filter((_, i, arr) => i % Math.ceil(arr.length / 300) === 0 || i >= arr.length - 10)
    : curvePoints;

  /* Roast landmarks the operator cares about — pulled from the
     server-detected events list and mapped onto the chart's x-axis
     (seconds from firstTs). Newer records carry `events`; older ones
     just won't render markers, which is fine. */
  const EVENT_LABELS = {
    charge:       { short: "CHG",  long: "Charge" },
    tp:           { short: "TP",   long: "Turning point" },
    yellowing:    { short: "C+",   long: "Color change" },
    firstCrack:   { short: "1C",   long: "First crack" },
    secondCrack:  { short: "2C",   long: "Second crack" },
    drop:         { short: "DROP", long: "Drop" },
  };
  const events = (fullRoast?.events || [])
    .map(e => ({ ...e, t: Math.max(0, (e.ts - firstTs) / 1000), meta: EVENT_LABELS[e.type] || { short: e.type, long: e.type } }))
    .filter(e => e.meta);

  return (
    <div style={shellStyle}>
      {cornerGlow}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 12, flexWrap: "wrap", position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "4px 10px", borderRadius: 99,
            background: "rgba(34,211,238,0.15)", color: "#7DD3FC",
            border: "1px solid rgba(34,211,238,0.3)",
            fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: 99, background: "#22D3EE",
              boxShadow: "0 0 8px rgba(34,211,238,0.9)",
              animation: "pr-live-pulse 1.6s ease-in-out infinite",
            }} />
            Live
          </span>
          <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.005em" }}>
            {liveSummary.productName || "Roast in progress"}
          </span>
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.45)" }}>
            · {liveSummary.sampleCount || 0} samples
          </span>
        </div>
        {/* Save (hold) archives the roast to history. Cancel (hold)
            discards it. Both require a 2-second long-press so a
            fat-finger tap can't burn a batch. Label flips to
            "Confirm" the moment the user starts holding. */}
        <div style={{ display: "inline-flex", gap: 6 }}>
          <LongPressButton
            onComplete={cancelRoast}
            disabled={ending}
            label="Cancel roast"
            confirmLabel="Confirm"
            holdMs={1000}
            fillColor="rgba(248,113,113,0.4)"
            idleStyle={{
              border: "1px solid rgba(248,113,113,0.35)",
              background: "rgba(248,113,113,0.06)",
              color: "#FCA5A5",
              opacity: ending ? 0.5 : 1,
            }}
            pressingStyle={{
              border: "1px solid rgba(248,113,113,0.7)",
              color: "#fff",
            }}
          />
          <LongPressButton
            onComplete={saveRoast}
            disabled={ending}
            label="Save roast"
            confirmLabel="Confirm"
            holdMs={1000}
            fillColor="linear-gradient(135deg, #22D3EE 0%, #06B6D4 100%)"
            idleStyle={{
              border: "1px solid rgba(34,211,238,0.5)",
              background: "rgba(34,211,238,0.12)",
              color: "#67E8F9",
              opacity: ending ? 0.6 : 1,
              boxShadow: "0 0 14px rgba(34,211,238,0.15)",
            }}
            pressingStyle={{
              border: "1px solid rgba(34,211,238,0.9)",
              color: "#042328",
            }}
          />
        </div>
      </div>

      {/* Tile row — live metrics first (BT / ET / RoR), elapsed time
          last. Roasters watch BT and RoR most closely; time is a
          secondary reference. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 18, position: "relative" }}>
        {[
          { l: "BT",    v: fmtTemp(latest.bt), color: "#22D3EE", accent: "rgba(34,211,238,0.22)"  },
          { l: "ET",    v: fmtTemp(latest.et), color: "#A78BFA", accent: "rgba(167,139,250,0.22)" },
          { l: "RoR",   v: fmtRor(latest.ror), color: "#CBD5E1", accent: "rgba(148,163,184,0.18)" },
          { l: "Time",  v: `${mm}:${ss}`,      color: "#E5E7EB", accent: "rgba(255,255,255,0.08)" },
        ].map(s => (
          <div key={s.l} style={{
            background: "rgba(255,255,255,0.025)",
            border: `1px solid ${s.accent}`,
            borderRadius: 10, padding: "12px 14px",
            position: "relative", overflow: "hidden",
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: 0.4 }}>{s.l}</div>
            <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4, color: s.color, fontVariantNumeric: "tabular-nums" }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Event chips — mirror the reference lines on the chart so
          operators can see at a glance which landmarks have already
          fired. Stays visible even before the chart has enough data. */}
      {events.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14, position: "relative" }}>
          {events.map((e, i) => {
            const mins = Math.floor(e.t / 60);
            const secs = Math.round(e.t % 60);
            return (
              <span key={`${e.type}-${i}`} style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "3px 10px", borderRadius: 99,
                background: "rgba(167,139,250,0.1)",
                border: "1px solid rgba(167,139,250,0.3)",
                color: "#DDD6FE",
                fontSize: 11, fontWeight: 600, letterSpacing: 0.3, textTransform: "uppercase",
                fontVariantNumeric: "tabular-nums",
              }}>
                <span style={{ color: "#F0ABFC" }}>{e.meta.short}</span>
                <span style={{ color: "rgba(255,255,255,0.4)" }}>{mins}:{String(secs).padStart(2, "0")}</span>
              </span>
            );
          })}
        </div>
      )}

      {chartData.length > 1 && (() => {
        /* Full-window view — x-axis spans the whole expected roast
           (12 min), so the curve reads left-to-right toward the
           finish line the same way Cropster's live view does. The
           "now" line + head dot make the current position unmistakable. */
        /* Demo simulator runs on a 6-minute compressed timeline; real
           roasts usually land around 12. Keep the tick list fixed so
           tick density reads consistently across both. */
        const ROAST_WINDOW_SEC = 6 * 60;
        const latest = chartData[chartData.length - 1];
        const latestBt = latest?.bt;
        const progressPct = Math.min(100, Math.round((elapsedSec / ROAST_WINDOW_SEC) * 100));
        return (
        <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "12px 8px 4px", position: "relative" }}>
          {/* Legend + thin progress bar so the full-window context
              stays readable at a glance, even if the curve itself
              occupies only 20% of the chart width. */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "0 10px 8px", fontSize: 11, color: "rgba(255,255,255,0.55)", fontWeight: 500, letterSpacing: 0.3 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 3, background: "#22D3EE", borderRadius: 2, boxShadow: "0 0 6px rgba(34,211,238,0.7)" }} />BT</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 0, borderTop: "2px dashed #A78BFA" }} />ET</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 2, background: "#94A3B8", borderRadius: 2 }} />RoR</span>
            <span style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ flex: 1, height: 4, background: "rgba(255,255,255,0.16)", borderRadius: 99, overflow: "hidden", position: "relative" }}>
                <span style={{
                  position: "absolute", left: 0, top: 0, bottom: 0,
                  width: `${progressPct}%`,
                  background: "linear-gradient(90deg, #22D3EE 0%, #A78BFA 100%)",
                  boxShadow: "0 0 10px rgba(34,211,238,0.7)",
                  transition: "width 0.5s ease-out",
                }} />
              </span>
              <span style={{ fontSize: 11, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: "rgba(255,255,255,0.65)", minWidth: 36, textAlign: "right" }}>
                {progressPct}%
              </span>
            </span>
          </div>
          <ResponsiveContainer width="100%" height={460}>
            <AreaChart data={chartData} margin={{ top: 18, right: 16, left: -16, bottom: 4 }}>
              <defs>
                <linearGradient id="liveBtGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="#22D3EE" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#22D3EE" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="liveEtGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="#A78BFA" stopOpacity={0.12} />
                  <stop offset="100%" stopColor="#A78BFA" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(148,163,184,0.08)" strokeDasharray="3 3" />
              <XAxis
                dataKey="time"
                type="number"
                domain={[0, ROAST_WINDOW_SEC]}
                ticks={[0, 60, 120, 180, 240, 300, ROAST_WINDOW_SEC]}
                allowDataOverflow
                tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(sec) => {
                  const m = Math.floor(sec / 60), s = Math.round(sec % 60);
                  return `${m}:${String(s).padStart(2, "0")}`;
                }}
              />
              <YAxis
                yAxisId="temp"
                domain={[150, 460]}
                tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="ror"
                orientation="right"
                domain={[0, 40]}
                tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{ background: "rgba(5,6,10,0.95)", border: "1px solid rgba(34,211,238,0.25)", borderRadius: 8, fontSize: 12, fontFamily: "Inter", color: "#fff" }}
                itemStyle={{ color: "#fff" }}
                labelStyle={{ color: "rgba(255,255,255,0.6)", fontWeight: 500 }}
                labelFormatter={(sec) => {
                  const m = Math.floor(sec / 60), s = Math.round(sec % 60);
                  return `${m}:${String(s).padStart(2, "0")}`;
                }}
                formatter={(v, name) => {
                  if (name === "bt" || name === "et") return [`${Math.round(v)}°F`, name.toUpperCase()];
                  if (name === "ror") return [`${v > 0 ? "+" : ""}${Math.round(v)}/min`, "RoR"];
                  return [v, name];
                }}
              />
              {/* Roast landmarks as vertical reference lines. The dashed
                  violet line + short label matches the chip palette above
                  so eye-tracking between the two is easy. */}
              {events.map((e, i) => (
                <ReferenceLine
                  key={`evt-${i}`}
                  yAxisId="temp"
                  x={e.t}
                  stroke="rgba(240,171,252,0.6)"
                  strokeDasharray="3 4"
                  strokeWidth={1}
                  ifOverflow="extendDomain"
                  label={{ value: e.meta.short, position: "top", fill: "#F0ABFC", fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}
                />
              ))}
              {/* "You are here" — a bright cyan vertical at the current
                  elapsed time plus a glowing dot at the curve head so
                  the operator can spot where the roast is at a glance. */}
              <ReferenceLine
                yAxisId="temp"
                x={elapsedSec}
                stroke="#22D3EE"
                strokeWidth={1.5}
                strokeDasharray="2 3"
                ifOverflow="extendDomain"
              />
              <Area yAxisId="temp" type="monotone" dataKey="bt"  stroke="#22D3EE" strokeWidth={2.2} fill="url(#liveBtGrad)" dot={false} name="bt" />
              <Area yAxisId="temp" type="monotone" dataKey="et"  stroke="#A78BFA" strokeWidth={1.4} fill="url(#liveEtGrad)" dot={false} strokeDasharray="5 5" name="et" opacity={0.9} />
              <Area yAxisId="ror"  type="monotone" dataKey="ror" stroke="#94A3B8" strokeWidth={1.4} fill="none" dot={false} name="ror" />
              {/* Outer cyan halo behind a bright white center dot so the
                  head reads cleanly against the cyan BT line. Rendered
                  as two sibling ReferenceDots (NOT inside a Fragment —
                  recharts won't traverse fragments and will silently
                  drop them). */}
              {latestBt != null && (
                <ReferenceDot
                  yAxisId="temp"
                  x={latest.time}
                  y={latestBt}
                  r={10}
                  fill="rgba(34,211,238,0.35)"
                  stroke="none"
                  ifOverflow="extendDomain"
                  isFront
                />
              )}
              {latestBt != null && (
                <ReferenceDot
                  yAxisId="temp"
                  x={latest.time}
                  y={latestBt}
                  r={5}
                  fill="#fff"
                  stroke="#22D3EE"
                  strokeWidth={2}
                  ifOverflow="extendDomain"
                  isFront
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
        );
      })()}
    </div>
  );
}

/* Inject the pulse keyframes once per session so the live dot animates. */
(() => {
  if (typeof document !== "undefined" && !document.getElementById("pr-live-pulse")) {
    const s = document.createElement("style");
    s.id = "pr-live-pulse";
    s.textContent = `@keyframes pr-live-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.45; transform: scale(0.85); } }`;
    document.head.appendChild(s);
  }
})();

/* ═════════════════════════════════════════════════════════════════
   LONG-PRESS BUTTON — hold to trigger. Fills from left to right over
   `holdMs` milliseconds; releases cleanly if the pointer lifts early.
   Used for destructive/irreversible actions (Save / Cancel roast) so
   a fat-finger tap can't burn a roast. Label flips to "Confirm" the
   moment the user starts holding so intent is unambiguous. Keyboard-
   operable (Space / Enter), focus-visible outline, and short-tap
   discoverability hint.
   ═════════════════════════════════════════════════════════════════ */
function LongPressButton({
  onComplete,
  label,
  confirmLabel = "Confirm",
  holdMs = 2000,
  disabled = false,
  fillColor,
  idleStyle,
  pressingStyle,
  icon,
  focusRingColor = "#22D3EE",
}) {
  const [fill, setFill] = useState(0);
  const [pressing, setPressing] = useState(false);
  /* Short-tap hint: if the operator taps-and-releases in <250ms they
     haven't figured out this is a hold button. Show an inline caption
     below prompting them to hold. */
  const [hinting, setHinting] = useState(false);
  const startRef = useRef(null);
  const rafRef = useRef(null);
  const hintTimerRef = useRef(null);

  const stop = (opts = {}) => {
    const held = startRef.current != null ? Date.now() - startRef.current : 0;
    startRef.current = null;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    setPressing(false);
    setFill(0);
    if (opts.source !== "complete" && held > 0 && held < 250) {
      setHinting(true);
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
      hintTimerRef.current = setTimeout(() => setHinting(false), 2200);
    }
  };

  const tick = () => {
    if (startRef.current == null) return;
    const elapsed = Date.now() - startRef.current;
    const pct = Math.min(100, (elapsed / holdMs) * 100);
    setFill(pct);
    if (pct >= 100) {
      stop({ source: "complete" });
      onComplete();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  const start = (e) => {
    if (disabled) return;
    if (e && e.preventDefault) e.preventDefault();
    setHinting(false);
    startRef.current = Date.now();
    setPressing(true);
    rafRef.current = requestAnimationFrame(tick);
  };

  /* Keyboard path — Space / Enter start the hold while the key is
     held; releasing resets. Keeps the destructive action reachable
     via keyboard without reverting to a single-tap fire-off. */
  const onKeyDown = (e) => {
    if (e.repeat) return;
    if (e.key === " " || e.key === "Spacebar" || e.key === "Enter") {
      start(e);
    }
  };
  const onKeyUp = (e) => {
    if (e.key === " " || e.key === "Spacebar" || e.key === "Enter") stop();
  };

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
  }, []);

  const base = idleStyle || {};
  const pressed = pressingStyle || {};
  return (
    <div style={{ position: "relative", display: "inline-flex", flexDirection: "column", alignItems: "stretch" }}>
      <button
        disabled={disabled}
        onPointerDown={start}
        onPointerUp={() => stop()}
        onPointerLeave={() => stop()}
        onPointerCancel={() => stop()}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        style={{
          position: "relative", overflow: "hidden",
          padding: "12px 20px", borderRadius: 10,
          fontSize: 13, fontWeight: 700,
          fontFamily: "Inter, sans-serif",
          cursor: disabled ? "default" : "pointer",
          userSelect: "none",
          transition: "border-color 0.15s, background 0.15s, box-shadow 0.15s",
          minWidth: 138, minHeight: 44,
          outline: "none",
          ...base,
          ...(pressing ? pressed : {}),
        }}
        onFocus={e => { e.currentTarget.style.boxShadow = `0 0 0 2px ${focusRingColor}80`; }}
        onBlur={e => { e.currentTarget.style.boxShadow = base.boxShadow || "none"; }}
      >
        <span style={{
          position: "absolute", left: 0, top: 0, bottom: 0,
          width: `${fill}%`,
          background: fillColor,
          transition: pressing ? "none" : "width 0.2s ease-out",
          pointerEvents: "none",
        }} />
        <span style={{ position: "relative", zIndex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          {icon}
          {pressing ? confirmLabel : label}
        </span>
      </button>
      <span style={{
        position: "absolute", left: 0, right: 0, top: "calc(100% + 4px)",
        fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.6)",
        textAlign: "center", textTransform: "uppercase", letterSpacing: 0.6,
        opacity: hinting ? 1 : 0,
        transition: "opacity 0.18s ease-out",
        pointerEvents: "none",
      }}>
        Hold to confirm · 1s
      </span>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   BATCH ROW — a single one-line card used in both the Next slot and
   the Batches queue. Carries the product label, batch index, and the
   target roast profile so everything a roaster needs to load the drum
   fits in one row. Drag handle on the left (grab-anywhere on the card
   body too — matches the task-DnD feel).
   ═════════════════════════════════════════════════════════════════ */
function BatchRow({ batch, batchNum, variant = "queue", isDragging, onDragStart, onDragEnd, onReturn }) {
  const fmtTime = (mins) => {
    if (mins == null) return null;
    const m = Math.floor(mins);
    const s = Math.round((mins - m) * 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };
  const profileBits = [
    batch.greenLbs != null && `${batch.greenLbs} lb → ${batch.roastedLbs} lb`,
    batch.chargeTemp != null && `Ch ${batch.chargeTemp}°F`,
    batch.dropTemp != null && `Drop ${batch.dropTemp}°F`,
    batch.totalTime != null && `${fmtTime(batch.totalTime)} total`,
    batch.fcTime != null && `1C ${fmtTime(batch.fcTime)}`,
  ].filter(Boolean);
  const isDraggable = variant === "queue";
  /* Stock status → single dot glyph next to the grip. green = enough
     green, amber = low, red = none. Lets the operator spot a blocked
     batch at a glance without scanning numbers. */
  const stockColor = batch.stockStatus === "ready" ? "#10B981"
    : batch.stockStatus === "low" ? "#F59E0B"
    : batch.stockStatus === "none" ? "#EF4444"
    : null;
  const stockTooltip = batch.stockStatus === "ready" ? "Green in stock"
    : batch.stockStatus === "low" ? "Low green — may run short"
    : batch.stockStatus === "none" ? "No green in stock"
    : "";
  return (
    <div
      draggable={isDraggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onMouseEnter={e => {
        if (variant !== "queue") return;
        e.currentTarget.style.borderColor = "#22D3EE";
        e.currentTarget.style.background = "#FAFBFC";
      }}
      onMouseLeave={e => {
        if (variant !== "queue") return;
        e.currentTarget.style.borderColor = "#E5E7EB";
        e.currentTarget.style.background = "#fff";
      }}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        background: variant === "next" ? "rgba(255,255,255,0.92)" : "#fff",
        border: variant === "next" ? "1px solid rgba(34,211,238,0.35)" : "1px solid #E5E7EB",
        borderRadius: 10, padding: "10px 12px",
        opacity: isDragging ? 0.35 : 1,
        cursor: isDraggable ? "grab" : "default",
        transition: "opacity 0.15s, transform 0.18s, border-color 0.15s, background 0.15s",
        transform: isDragging ? "scale(0.985)" : "scale(1)",
        width: "100%", boxSizing: "border-box",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        <GripVertical size={14} strokeWidth={1.8} style={{ color: "#C4C4C4" }} />
        {stockColor && (
          <span
            title={stockTooltip}
            aria-label={stockTooltip}
            style={{
              display: "inline-block",
              width: 7, height: 7, borderRadius: 99,
              background: stockColor,
              boxShadow: `0 0 0 2px ${stockColor}22`,
            }}
          />
        )}
      </span>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          {batchNum
            ? <span style={{ display: "inline-block", background: "#111827", color: "#fff", borderRadius: 99, padding: "2px 9px", fontSize: 11, fontWeight: 700, letterSpacing: "0.02em", flexShrink: 0 }}>{batchNum}</span>
            : <span style={{ display: "inline-block", background: "#F3F4F6", color: "#9CA3AF", borderRadius: 99, padding: "2px 9px", fontSize: 11, fontWeight: 600 }}>—</span>
          }
          <span style={{ fontSize: 14, fontWeight: 700, color: "#000000", letterSpacing: "-0.005em" }}>{batch.productName}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: "#6B7280" }}>
            Batch {batch.batchIndex + 1} of {batch.totalBatches}
          </span>
          {batch.roastLevel && <span style={{ fontSize: 11, color: "#9CA3AF" }}>· {batch.roastLevel}</span>}
          {batch.origin && <span style={{ fontSize: 11, color: "#9CA3AF" }}>· {batch.origin}</span>}
        </div>
        {profileBits.length > 0 && (
          <div style={{ fontSize: 11, color: "#6B7280", fontVariantNumeric: "tabular-nums", display: "flex", flexWrap: "wrap", gap: 10 }}>
            {profileBits.map((b, i) => <span key={i}>{b}</span>)}
          </div>
        )}
      </div>
      {variant === "next" && onReturn && (
        <button
          onClick={onReturn}
          title="Return to batches queue"
          style={{ background: "transparent", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 6, borderRadius: 6, flexShrink: 0 }}
        >
          <X size={14} strokeWidth={1.8} />
        </button>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   COMPLETED ROAST ROW — one-line summary of a finished batch: check
   mark, name/index, a compact string with duration + 1C + drop temp,
   and a 28px mini curve preview. Entire row is a button so a click
   opens the detail modal. Newest completions land on top, matching
   the user's "top-down in order of roasting" ask.
   ═════════════════════════════════════════════════════════════════ */
function CompletedRoastRow({ entry, onClick }) {
  const durationSec = entry.completedAt && entry.startedAt
    ? Math.max(0, Math.floor((entry.completedAt - entry.startedAt) / 1000))
    : 0;
  const dm = Math.floor(durationSec / 60);
  const ds = String(durationSec % 60).padStart(2, "0");
  const fcEvent = (entry.events || []).find(e => e.type === "firstCrack");
  const fcAtSec = fcEvent && entry.startedAt ? Math.max(0, Math.floor((fcEvent.ts - entry.startedAt) / 1000)) : null;
  const fcLabel = fcAtSec != null ? `${Math.floor(fcAtSec / 60)}:${String(fcAtSec % 60).padStart(2, "0")}` : null;

  /* Mini curve — downsample to ~40 points for a snappy sparkline. */
  const samples = entry.samples || [];
  const firstTs = samples[0]?.ts || 0;
  const miniData = samples.length > 40
    ? samples.filter((_, i) => i % Math.ceil(samples.length / 40) === 0).map(s => ({ t: (s.ts - firstTs) / 1000, bt: s.bt }))
    : samples.map(s => ({ t: (s.ts - firstTs) / 1000, bt: s.bt }));

  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10,
        padding: "8px 12px", cursor: "pointer", textAlign: "left",
        fontFamily: "Inter, sans-serif", width: "100%", boxSizing: "border-box",
        transition: "border-color 0.15s, background 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "#22D3EE"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E7EB"; }}
    >
      <CheckCircle2 size={14} strokeWidth={2.2} color="#10B981" style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          {entry.batchNum && (
            <span style={{ display: "inline-block", background: "#111827", color: "#fff", borderRadius: 99, padding: "2px 9px", fontSize: 11, fontWeight: 700, letterSpacing: "0.02em" }}>{entry.batchNum}</span>
          )}
          <span style={{ fontSize: 13, fontWeight: 700, color: "#000000" }}>{entry.productName}</span>
          <span style={{ fontSize: 11, fontWeight: 500, color: "#6B7280" }}>
            Batch {entry.batchIndex + 1} of {entry.totalBatches}
          </span>
        </div>
        <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2, fontVariantNumeric: "tabular-nums", display: "flex", gap: 10, flexWrap: "wrap" }}>
          <span>{dm}:{ds} total</span>
          {fcLabel && <span>1C {fcLabel}</span>}
          {entry.finalBt != null && <span>drop {Math.round(entry.finalBt)}°F</span>}
          {entry.events?.some(e => e.type === "secondCrack") && <span style={{ color: "#7C3AED" }}>2C fired</span>}
        </div>
      </div>
      {miniData.length > 2 && (() => {
        const bts = miniData.map(d => d.bt).filter(v => v != null);
        const yMin = Math.floor(Math.min(...bts) - 8);
        const yMax = Math.ceil(Math.max(...bts) + 8);
        return (
          <div style={{ width: 84, height: 30, flexShrink: 0, pointerEvents: "none" }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={miniData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                <defs>
                  <linearGradient id={`pr-mini-${entry.batchId}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22D3EE" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#22D3EE" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="t" type="number" domain={[0, "dataMax"]} hide />
                <YAxis type="number" domain={[yMin, yMax]} hide />
                <Area type="monotone" dataKey="bt" stroke="#22D3EE" strokeWidth={1.6} fill={`url(#pr-mini-${entry.batchId})`} dot={false} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        );
      })()}
    </button>
  );
}

/* ═════════════════════════════════════════════════════════════════
   COMPLETED ROAST MODAL — full details for a finished batch. Reuses
   the same cyan/violet chart palette as the live card so the history
   view feels like the same instrument panel, paused.
   ═════════════════════════════════════════════════════════════════ */
function CompletedRoastModal({ entry, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const samples = entry.samples || [];
  const firstTs = samples[0]?.ts || 0;
  const data = samples.map(s => ({
    t: (s.ts - firstTs) / 1000,
    bt: s.bt, et: s.et, ror: s.ror,
  }));
  const durationSec = entry.completedAt && entry.startedAt
    ? Math.max(0, Math.floor((entry.completedAt - entry.startedAt) / 1000))
    : 0;
  const EVENT_LABELS = {
    charge: "CHG", tp: "TP", yellowing: "C+", firstCrack: "1C", secondCrack: "2C", drop: "DROP",
  };
  const events = (entry.events || []).map(e => ({
    ...e, t: Math.max(0, (e.ts - firstTs) / 1000), short: EVENT_LABELS[e.type] || e.type,
  }));

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000 }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        width: "min(960px, 94vw)", maxHeight: "92vh", overflow: "auto", zIndex: 1001,
        background: "linear-gradient(135deg, #05060A 0%, #0A0E14 55%, #070C12 100%)",
        color: "#fff", borderRadius: 16, fontFamily: "Inter, sans-serif",
        border: "1px solid rgba(34,211,238,0.2)",
      }}>
        <div style={{ padding: "20px 22px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.01em" }}>{entry.productName}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 3 }}>
              Batch {entry.batchIndex + 1} of {entry.totalBatches} · {new Date(entry.completedAt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.5)", padding: 6 }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: "18px 22px 22px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
            {[
              { l: "Total time", v: `${Math.floor(durationSec/60)}:${String(durationSec%60).padStart(2,"0")}` },
              { l: "Drop BT", v: entry.finalBt != null ? `${Math.round(entry.finalBt)}°F` : "—" },
              { l: "1C", v: events.find(e => e.type === "firstCrack")
                  ? (() => { const fc = events.find(e => e.type === "firstCrack"); return `${Math.floor(fc.t/60)}:${String(Math.round(fc.t%60)).padStart(2,"0")}`; })()
                  : "—" },
              { l: "2C", v: events.find(e => e.type === "secondCrack")
                  ? (() => { const sc = events.find(e => e.type === "secondCrack"); return `${Math.floor(sc.t/60)}:${String(Math.round(sc.t%60)).padStart(2,"0")}`; })()
                  : "—" },
            ].map(s => (
              <div key={s.l} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: 0.4 }}>{s.l}</div>
                <div style={{ fontSize: 20, fontWeight: 700, marginTop: 3, fontVariantNumeric: "tabular-nums" }}>{s.v}</div>
              </div>
            ))}
          </div>

          {data.length > 1 && (
            <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: "10px 6px 4px" }}>
              <ResponsiveContainer width="100%" height={360}>
                <AreaChart data={data} margin={{ top: 18, right: 14, left: -16, bottom: 4 }}>
                  <defs>
                    <linearGradient id="pr-done-bt" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22D3EE" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#22D3EE" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(148,163,184,0.08)" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="t"
                    type="number"
                    domain={[0, Math.max(720, Math.ceil(durationSec/60)*60)]}
                    ticks={[0, 120, 240, 360, 480, 600, 720]}
                    tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(s) => `${Math.floor(s/60)}:${String(Math.round(s%60)).padStart(2,"0")}`}
                  />
                  <YAxis yAxisId="temp" domain={[150, 460]} tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="ror" orientation="right" domain={[0, 40]} tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: "rgba(5,6,10,0.95)", border: "1px solid rgba(34,211,238,0.25)", borderRadius: 8, fontSize: 12, fontFamily: "Inter", color: "#fff" }}
                    labelFormatter={(s) => `${Math.floor(s/60)}:${String(Math.round(s%60)).padStart(2,"0")}`}
                    formatter={(v, name) => {
                      if (name === "bt" || name === "et") return [`${Math.round(v)}°F`, name.toUpperCase()];
                      if (name === "ror") return [`${v > 0 ? "+" : ""}${Math.round(v)}/min`, "RoR"];
                      return [v, name];
                    }}
                  />
                  {events.map((e, i) => (
                    <ReferenceLine key={i} yAxisId="temp" x={e.t} stroke="rgba(240,171,252,0.55)" strokeDasharray="3 4" strokeWidth={1}
                      label={{ value: e.short, position: "top", fill: "#F0ABFC", fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }} />
                  ))}
                  <Area yAxisId="temp" type="monotone" dataKey="bt" stroke="#22D3EE" strokeWidth={2} fill="url(#pr-done-bt)" dot={false} name="bt" />
                  <Area yAxisId="temp" type="monotone" dataKey="et" stroke="#A78BFA" strokeWidth={1.3} fill="none" dot={false} strokeDasharray="5 5" name="et" opacity={0.85} />
                  <Area yAxisId="ror" type="monotone" dataKey="ror" stroke="#94A3B8" strokeWidth={1.3} fill="none" dot={false} name="ror" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════
   PANEL NAV ITEM — tall rows, big icons, generous spacing
   ═══════════════════════════════════════════════════════ */
/* Primary call-to-action at the top of a page's sub-nav column (Elion
   pattern — "Compose" / "New task"). One button per page, the single
   most important action. Black pill, full-width, plus icon + label. */
const PanelCTA = ({ icon: Icon = Plus, label, onClick }) => (
  <button onClick={onClick} style={{
    display: "flex", alignItems: "center", justifyContent: "center",
    gap: 8, width: "100%",
    padding: "14px 18px", borderRadius: 12, border: "none", cursor: "pointer",
    background: "#000000", color: "#ffffff",
    fontSize: 15, fontWeight: 600, fontFamily: "Inter, sans-serif",
    letterSpacing: "-0.005em",
    marginBottom: 16,
    transition: "opacity 0.12s",
  }}
    onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
    onMouseLeave={e => e.currentTarget.style.opacity = "1"}
  >
    {Icon && <Icon size={16} strokeWidth={2.2} />}
    {label}
  </button>
);

/* ── Green coffee view ──────────────────────────────────────────────
   A green coffee is the post-approval representation of a cupping
   sample. Approved samples are auto-promoted server-side; this view
   lists them with their cupping snapshot (origin, process, score,
   cost/lb, descriptors) plus editable fields the user fills in
   AFTER promotion: expected weight loss %, density, default roast
   target, default development %, notes. */
function GreenCoffeeView({ header, greenCoffees, loaded, refresh }) {
  const list = (greenCoffees || []).filter(g => !g.archivedAt);
  const patch = async (id, patch) => {
    try {
      const r = await fetch(`/api/green-coffees/${id}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (r.ok) refresh();
    } catch { /* ignore */ }
  };

  if (!loaded) {
    return (
      <div>
        {header}
        <div style={{ padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>Loading…</div>
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <div>
        {header}
        <div style={{ background: "#F9FAFB", borderRadius: 12, padding: 48, textAlign: "center", color: "#6B7280", fontSize: 14, lineHeight: 1.6 }}>
          No green coffees yet. Approve samples in <strong style={{ color: "#000" }}>Cupping &amp; QC</strong> and they'll show up here.
        </div>
      </div>
    );
  }

  const cellInput = {
    padding: "6px 8px", borderRadius: 6, border: "1px solid #E5E7EB",
    fontSize: 13, fontFamily: "Inter, sans-serif", background: "#fff", outline: "none",
    width: "100%", boxSizing: "border-box",
  };
  const labelCss = { fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 };

  return (
    <div>
      {header}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {list.map(g => (
          <div key={g.id} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: 18 }}>
            {/* Top row: name + cupping snapshot */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 18, marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid #F3F4F6" }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#000", marginBottom: 4 }}>{g.coffeeName || "(unnamed)"}</div>
                <div style={{ fontSize: 12, color: "#6B7280", display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {g.origin && <span>{g.origin}</span>}
                  {g.process && <><span>·</span><span>{g.process}</span></>}
                  {g.sourcedFrom && <><span>·</span><span>From {g.sourcedFrom}</span></>}
                  {g.cuppedAt && <><span>·</span><span>Cupped {new Date(g.cuppedAt + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span></>}
                </div>
                {g.descriptors && (g.descriptors.fragrance || g.descriptors.flavor || g.descriptors.aftertaste) && (
                  <div style={{ fontSize: 12, color: "#3F3F46", marginTop: 6, fontStyle: "italic" }}>
                    {[g.descriptors.fragrance, g.descriptors.flavor, g.descriptors.aftertaste].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 12, flexShrink: 0 }}>
                {g.score != null && (
                  <div style={{ textAlign: "right" }}>
                    <div style={labelCss}>Score</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: g.score >= 85 ? "#16A34A" : "#000", fontVariantNumeric: "tabular-nums" }}>{Number(g.score).toFixed(1)}</div>
                  </div>
                )}
                {g.costPerLb != null && (
                  <div style={{ textAlign: "right" }}>
                    <div style={labelCss}>Cost / lb</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#000", fontVariantNumeric: "tabular-nums" }}>${Number(g.costPerLb).toFixed(2)}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Editable bench fields */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 12 }}>
              <div>
                <div style={labelCss}>Expected weight loss</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="number" min="0" max="50" step="0.5"
                    style={cellInput}
                    defaultValue={g.expectedLossPct != null ? g.expectedLossPct : ""}
                    placeholder="e.g. 14"
                    onBlur={e => {
                      const v = e.target.value;
                      patch(g.id, { expectedLossPct: v === "" ? null : parseFloat(v) });
                    }}
                  />
                  <span style={{ fontSize: 13, color: "#6B7280" }}>%</span>
                </div>
              </div>
              <div>
                <div style={labelCss}>Default roast time</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="number" min="0" step="0.25"
                    style={cellInput}
                    defaultValue={g.defaultRoastMins != null ? g.defaultRoastMins : ""}
                    placeholder="e.g. 11.5"
                    onBlur={e => {
                      const v = e.target.value;
                      patch(g.id, { defaultRoastMins: v === "" ? null : parseFloat(v) });
                    }}
                  />
                  <span style={{ fontSize: 13, color: "#6B7280" }}>min</span>
                </div>
              </div>
              <div>
                <div style={labelCss}>Default dev %</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="number" min="0" max="50" step="0.5"
                    style={cellInput}
                    defaultValue={g.defaultDevPct != null ? g.defaultDevPct : ""}
                    placeholder="e.g. 18"
                    onBlur={e => {
                      const v = e.target.value;
                      patch(g.id, { defaultDevPct: v === "" ? null : parseFloat(v) });
                    }}
                  />
                  <span style={{ fontSize: 13, color: "#6B7280" }}>%</span>
                </div>
              </div>
              <div>
                <div style={labelCss}>Density</div>
                <input
                  type="text"
                  style={cellInput}
                  defaultValue={g.density || ""}
                  placeholder="e.g. 0.74 g/ml"
                  onBlur={e => patch(g.id, { density: e.target.value })}
                />
              </div>
            </div>
            <div>
              <div style={labelCss}>Bench notes</div>
              <textarea
                rows={2}
                style={{ ...cellInput, resize: "vertical", minHeight: 50 }}
                defaultValue={g.notes || ""}
                placeholder="Roast curve target, machine-specific notes, anything useful…"
                onBlur={e => patch(g.id, { notes: e.target.value })}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Sub-nav row, Elion-style — selected row gets a soft warm-gray pill.
   No per-row borders; section dividers are rendered by the parent. Used
   across every page's sub-nav so one change here restyles them all. */
/* Modal for creating a new coffee record. Tiny form — we only ask for
   the fields that actually matter at intake; everything else can be
   filled in later from the detail view. */
function AddCoffeeModal({ onClose, onCreate, approvedSamples = [] }) {
  /* Single unified flow: every coffee is built from one or more
     approved cupping samples with a percentage. One sample at 100%
     = single origin. Two or more = blend. The first sample seeds the
     coffee's name / origin / process / roast level. */
  const [components, setComponents] = useState([{ sampleId: "", pct: 100 }]);
  const [name, setName] = useState("");
  const [roastLevel, setRoastLevel] = useState("Medium");
  const [notes, setNotes] = useState("");
  const [autoName, setAutoName] = useState(true); // stop syncing once user types
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  /* Component math. */
  const validComps = components.filter(c => c.sampleId && Number(c.pct) > 0);
  const totalPct = components.reduce((a, c) => a + (Number(c.pct) || 0), 0);
  const isBlend = validComps.length > 1;
  const weightedFor = (key) => {
    let weighted = 0, total = 0;
    for (const c of validComps) {
      const s = approvedSamples.find(x => x.id === c.sampleId);
      if (!s || s[key] == null) continue;
      const pct = Number(c.pct) || 0;
      weighted += Number(s[key]) * pct;
      total += pct;
    }
    return total > 0 ? +(weighted / total).toFixed(2) : null;
  };
  const computedCost = weightedFor("costPerLb");
  const computedPrice = weightedFor("pricePerLb");

  /* Auto-generate a coffee name from the components: a single origin
     defaults to the sample's coffee name; a blend defaults to a comma
     list. The user can override at any time — once they type, autoName
     is set false and we stop syncing. */
  useEffect(() => {
    if (!autoName) return;
    const names = validComps
      .map(c => approvedSamples.find(x => x.id === c.sampleId)?.coffeeName)
      .filter(Boolean);
    if (names.length === 0) { setName(""); return; }
    if (names.length === 1) { setName(names[0]); return; }
    setName(names.join(" + ") + " Blend");
  }, [components, approvedSamples, autoName]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateComp = (i, patch) => setComponents(cs => {
    // Sample-id changes don't touch percentages.
    if (!("pct" in patch)) {
      return cs.map((c, j) => j === i ? { ...c, ...patch } : c);
    }
    // Percentage change → auto-rebalance the other rows so the total
    // stays at 100%. Distribute the remaining (100 - new) across the
    // other rows proportionally to their current values; if all the
    // others are zero, split equally.
    const newPct = Math.max(0, Math.min(100, Number(patch.pct) || 0));
    const others = cs.map((c, j) => ({ c, j })).filter(x => x.j !== i);
    const remaining = 100 - newPct;
    const otherSum = others.reduce((s, x) => s + (Number(x.c.pct) || 0), 0);
    return cs.map((c, j) => {
      if (j === i) return { ...c, pct: newPct };
      const cur = Number(c.pct) || 0;
      let next;
      if (otherSum > 0) next = +(remaining * (cur / otherSum)).toFixed(2);
      else next = +(remaining / others.length).toFixed(2);
      return { ...c, pct: next };
    });
  });
  const removeComp = (i) => setComponents(cs => {
    if (cs.length <= 1) return cs;
    const dropped = Number(cs[i].pct) || 0;
    const remaining = cs.filter((_, j) => j !== i);
    // Redistribute the removed slot's % across the survivors so the
    // total still hits 100.
    const sum = remaining.reduce((s, c) => s + (Number(c.pct) || 0), 0);
    return remaining.map(c => {
      if (sum > 0) return { ...c, pct: +(((Number(c.pct) || 0) + dropped * ((Number(c.pct) || 0) / sum)).toFixed(2)) };
      return { ...c, pct: +(dropped / remaining.length).toFixed(2) };
    });
  });
  const addComp = () => setComponents(cs => [...cs, { sampleId: "", pct: 0 }]);

  const submit = async () => {
    setError("");
    if (validComps.length === 0) { setError("Pick at least one approved cupping sample."); return; }
    if (Math.abs(totalPct - 100) > 0.01) { setError(`Components must total 100% (currently ${totalPct}%).`); return; }
    if (!name.trim()) { setError("Name is required."); return; }
    setSaving(true);

    // Pre-fill metadata from the leading component for single-origin
    // coffees. For blends the per-coffee origin/process don't apply.
    const lead = isBlend ? null : approvedSamples.find(x => x.id === validComps[0].sampleId);
    const desc = lead?.descriptors || {};
    const descBits = [desc.fragrance, desc.flavor, desc.aftertaste].filter(Boolean).join(", ");

    const payload = {
      name: name.trim(),
      roastLevel,
      costPerLb: computedCost,
      retailPrice: computedPrice,
      notes: notes || (descBits ? `Cupping notes: ${descBits}` : null),
      isBlend,
      // Components reference green coffees (gc-<sampleId>). We also
      // pass the underlying sampleId so older readers / migrations
      // can still resolve the source sample.
      blendComponents: validComps.map(c => {
        const samp = approvedSamples.find(x => x.id === c.sampleId);
        return {
          greenCoffeeId: samp?.greenCoffeeId || (c.sampleId ? `gc-${c.sampleId}` : null),
          sampleId: c.sampleId,
          pct: Number(c.pct),
        };
      }),
      // Single-origin: also seed the convenience fields from the sample.
      ...(lead && {
        origin: lead.origin || lead.sourcedFrom || null,
        process: lead.process || null,
        score: lead.score != null ? lead.score : null,
        sourcedFromSampleId: lead.id,
        sourcedFromSessionId: lead.sessionId || null,
      }),
    };

    const res = await onCreate(payload);
    setSaving(false);
    if (!res.ok) { setError(res.error || "Save failed"); return; }
    onClose(res.product);
  };

  const inputStyle = { padding: "10px 12px", borderRadius: 10, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff", outline: "none" };
  const canSubmit = validComps.length > 0 && Math.abs(totalPct - 100) < 0.01 && name.trim();

  return (
    <ModalShell
      title="New coffee"
      subtitle="Build a coffee from one or more approved cupping samples. One sample is a single origin; two or more is a blend."
      onClose={() => onClose(null)}
      actions={
        <>
          <button onClick={() => onClose(null)} style={{ padding: "10px 16px", borderRadius: 10, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 14, fontWeight: 500, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>Cancel</button>
          <button onClick={submit} disabled={saving || !canSubmit} style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: (saving || !canSubmit) ? "#D1D5DB" : "#000", color: "#fff", fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: (saving || !canSubmit) ? "default" : "pointer" }}>
            {saving ? "Saving…" : "Create coffee"}
          </button>
        </>
      }
    >
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
        {error && (
          <div style={{ padding: "10px 14px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, color: "#DC2626", fontSize: 13 }}>{error}</div>
        )}

        {approvedSamples.length === 0 && (
          <div style={{ padding: "12px 14px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, color: "#92400E", fontSize: 13 }}>
            No approved cupping samples yet. Approve samples in Cupping &amp; QC first, then come back here.
          </div>
        )}

        {/* Components */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", letterSpacing: "0.08em", textTransform: "uppercase" }}>
              {isBlend ? "Components" : "Sample"}
            </span>
            {components.length > 1 && (
              <span style={{ fontSize: 12, color: Math.abs(totalPct - 100) < 0.01 ? "#166534" : "#CC785C", fontVariantNumeric: "tabular-nums" }}>
                Total {totalPct}%{Math.abs(totalPct - 100) >= 0.01 ? " — must equal 100%" : ""}
              </span>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {components.map((c, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: components.length > 1 ? "1fr 110px 32px" : "1fr", gap: 8, alignItems: "center" }}>
                <select
                  value={c.sampleId}
                  onChange={e => updateComp(i, { sampleId: e.target.value })}
                  style={{ ...inputStyle, appearance: "auto", minWidth: 0 }}
                  disabled={approvedSamples.length === 0}
                  autoFocus={i === 0}
                >
                  <option value="">Choose approved sample…</option>
                  {approvedSamples.map(s => (
                    <option key={s.id} value={s.id}>{s.label || s.coffeeName}</option>
                  ))}
                </select>
                {components.length > 1 && (
                  <>
                    {/* % wrapper: input as a flex child so the % suffix
                        always sits inside the column instead of being
                        pushed out of frame. */}
                    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                      <input
                        type="number" min="0" max="100" step="1" value={c.pct}
                        onChange={e => updateComp(i, { pct: e.target.value === "" ? 0 : parseFloat(e.target.value) || 0 })}
                        style={{ ...inputStyle, width: "100%", textAlign: "right", paddingRight: 26, boxSizing: "border-box" }}
                      />
                      <span style={{ position: "absolute", right: 10, fontSize: 13, color: "#6B7280", pointerEvents: "none" }}>%</span>
                    </div>
                    <button
                      onClick={() => removeComp(i)}
                      style={{ padding: 0, width: 32, height: 32, borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#6B7280", cursor: "pointer", fontSize: 16, lineHeight: 1 }}
                    >×</button>
                  </>
                )}
              </div>
            ))}
          </div>
          <button
            onClick={addComp}
            disabled={approvedSamples.length === 0}
            style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: approvedSamples.length === 0 ? "#D1D5DB" : "#000", fontSize: 12, fontWeight: 500, fontFamily: "Inter, sans-serif", cursor: approvedSamples.length === 0 ? "default" : "pointer" }}
          >+ Add sample</button>
        </div>

        {(computedCost != null || computedPrice != null) && (
          <div style={{ padding: "12px 14px", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, color: "#3F3F46", display: "flex", flexDirection: "column", gap: 6 }}>
            {computedCost != null && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  {isBlend ? "Weighted cost / lb" : "Cost / lb"}
                </span>
                <span style={{ fontWeight: 600, color: "#000", fontVariantNumeric: "tabular-nums" }}>${computedCost.toFixed(2)}</span>
              </div>
            )}
            {computedPrice != null && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  {isBlend ? "Weighted price / lb" : "Price / lb"}
                </span>
                <span style={{ fontWeight: 600, color: "#000", fontVariantNumeric: "tabular-nums" }}>${computedPrice.toFixed(2)}</span>
              </div>
            )}
          </div>
        )}

        <div>
          <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 6 }}>Name *</label>
          <input
            value={name}
            onChange={e => { setName(e.target.value); setAutoName(false); }}
            placeholder={isBlend ? "House Blend" : "La Esperanza"}
            style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
          />
        </div>

        <div>
          <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 6 }}>Roast level</label>
          <select value={roastLevel} onChange={e => setRoastLevel(e.target.value)} style={{ ...inputStyle, appearance: "auto", width: "100%", boxSizing: "border-box" }}>
            {["Light", "Medium-Light", "Medium", "Medium-Dark", "Dark"].map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div>
          <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 6 }}>Notes</label>
          <textarea
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Cupping impressions, roaster notes, anything useful."
            rows={3}
            style={{ ...inputStyle, width: "100%", boxSizing: "border-box", resize: "vertical", minHeight: 70 }}
          />
        </div>
      </div>
    </ModalShell>
  );
}

/* Product-detail panel that lists uploaded roast curves and lets the user
   drop in a new one (Artisan .alog or CSV). When no curves exist we show
   the baked-in mock so Demo-mode keeps rendering something pretty. */
function RoastProfilePanel({ productId }) {
  const [curves, setCurves] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const [selectedPoints, setSelectedPoints] = useState(null);
  const [selectedMeta, setSelectedMeta] = useState(null);
  const inputRef = useRef(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/roast-curves?productId=${encodeURIComponent(productId)}`);
      if (r.ok) {
        const d = await r.json();
        setCurves(d.curves || []);
        if ((d.curves || []).length > 0 && !selectedId) setSelectedId(d.curves[0].id);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setSelectedId(null); setSelectedPoints(null); setSelectedMeta(null); fetchList(); }, [productId, fetchList]);

  /* When the selected curve changes, fetch its point array. */
  useEffect(() => {
    if (!selectedId) { setSelectedPoints(null); setSelectedMeta(null); return; }
    let cancelled = false;
    fetch(`/api/roast-curves/${selectedId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d?.curve) { setSelectedPoints(d.curve.points); setSelectedMeta(d.curve); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedId]);

  const uploadFile = async (file) => {
    if (!file) return;
    setError(""); setUploading(true);
    try {
      const content = await file.text();
      const r = await fetch("/api/roast-curves", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productId, filename: file.name, content }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || `Upload failed (HTTP ${r.status})`); return; }
      /* Put the new curve on top + select it. */
      setCurves(prev => [d.curve, ...prev]);
      setSelectedId(d.curve.id);
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  };

  const deleteCurve = async (id) => {
    if (!confirm("Delete this roast curve?")) return;
    const r = await fetch(`/api/roast-curves/${id}`, { method: "DELETE" });
    if (r.ok) {
      setCurves(prev => prev.filter(c => c.id !== id));
      if (selectedId === id) setSelectedId(null);
    }
  };

  /* Inline rename — double-click a chip to enter edit mode. Enter
     commits, Escape cancels, blur commits. */
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const startRename = (c) => {
    setRenamingId(c.id);
    setRenameDraft(c.name || c.filename?.replace(/\.[^.]+$/, "") || "");
  };
  const commitRename = async () => {
    const id = renamingId;
    const next = renameDraft.trim();
    setRenamingId(null);
    if (!id) return;
    const curr = curves.find(c => c.id === id);
    if (curr && (curr.name || "") === next) return;
    try {
      const r = await fetch(`/api/roast-curves/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: next }),
      });
      if (r.ok) setCurves(prev => prev.map(c => c.id === id ? { ...c, name: next || null } : c));
    } catch { /* ignore */ }
  };
  const cancelRename = () => { setRenamingId(null); setRenameDraft(""); };

  const fmtTime = (secs) => {
    if (secs == null || !Number.isFinite(secs)) return "—";
    const m = Math.floor(secs / 60), s = Math.round(secs % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  return (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: "#000000" }}>Roast profile</div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ display: "flex", gap: 14, fontSize: 13, fontWeight: 500, color: "#9CA3AF" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 12, height: 2, borderRadius: 1, background: "#374151", display: "inline-block" }} /> BT</span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 12, height: 2, borderRadius: 1, background: "#93C5FD", display: "inline-block" }} /> ET</span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 12, height: 2, borderRadius: 1, background: "#9CA3AF", display: "inline-block" }} /> RoR</span>
          </div>
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "8px 14px", borderRadius: 8, border: "1px solid #000000",
              background: uploading ? "#D1D5DB" : "#000000", color: "#fff",
              fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif",
              cursor: uploading ? "default" : "pointer",
            }}
          >
            <Plus size={14} strokeWidth={2} /> {uploading ? "Uploading…" : "Upload curve"}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept=".alog,.json,.csv,text/csv,application/json"
            onChange={e => uploadFile(e.target.files?.[0])}
            style={{ display: "none" }}
          />
        </div>
      </div>

      {/* List of uploaded curves */}
      {curves.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
          {curves.map(c => {
            const isSelected = selectedId === c.id;
            const isRenaming = renamingId === c.id;
            return (
              <div
                key={c.id}
                onClick={() => !isRenaming && setSelectedId(c.id)}
                onDoubleClick={(e) => { e.stopPropagation(); startRename(c); }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "6px 12px", borderRadius: 99,
                  border: `1px solid ${isSelected ? "#000" : "#E5E7EB"}`,
                  background: isSelected ? "#000" : "#fff",
                  color: isSelected ? "#fff" : "#374151",
                  fontSize: 13, fontFamily: "Inter, sans-serif",
                  cursor: isRenaming ? "text" : "pointer",
                }}
                title={isRenaming ? "" : "Double-click to rename"}
              >
                {isRenaming ? (
                  <input
                    autoFocus
                    value={renameDraft}
                    onChange={e => setRenameDraft(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                      else if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
                    }}
                    onBlur={commitRename}
                    onClick={e => e.stopPropagation()}
                    style={{
                      background: "transparent", border: "none", outline: "none",
                      color: "inherit", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif",
                      minWidth: 80, width: `${Math.max(8, renameDraft.length)}ch`,
                    }}
                  />
                ) : (
                  <span style={{ fontWeight: 600 }}>{c.name || c.filename?.replace(/\.[^.]+$/, "") || "Roast"}</span>
                )}
                <span style={{ opacity: 0.7 }}>
                  {c.roastedAt ? new Date(c.roastedAt).toLocaleDateString() : new Date(c.createdAt).toLocaleDateString()}
                </span>
                <span
                  onClick={e => { e.stopPropagation(); deleteCurve(c.id); }}
                  style={{ marginLeft: 4, opacity: 0.6, cursor: "pointer" }}
                  title="Delete"
                ><X size={12} strokeWidth={2} /></span>
              </div>
            );
          })}
        </div>
      )}

      {/* Drop zone when there are no curves yet */}
      {curves.length === 0 && !loading && (
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? "#000" : "#E5E7EB"}`,
            background: dragOver ? "#F9FAFB" : "#FAFAFA",
            borderRadius: 12, padding: "36px 20px", textAlign: "center",
            cursor: "pointer", marginBottom: 16,
          }}
        >
          <div style={{ fontSize: 15, color: "#374151", fontWeight: 500, marginBottom: 4 }}>
            Drop a roast curve here
          </div>
          <div style={{ fontSize: 13, color: "#6B7280" }}>
            Artisan <code>.alog</code> or CSV export (Cropster / RoastLog / RoastMaster / Roast World). Max 5 MB.
          </div>
        </div>
      )}

      {error && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", marginBottom: 14, color: "#DC2626", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Chart: render the selected real curve, with phase markers and
          knob channels. Returns nothing when no curve is selected. */}
      <RoastCurve
        productId={productId}
        height={540}
        points={selectedPoints || undefined}
        metadata={selectedMeta?.metadata || null}
      />

      {/* Metadata from the real curve */}
      {selectedMeta?.metadata && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginTop: 16, paddingTop: 16, borderTop: "1px solid #F3F4F6" }}>
          {[
            { l: "Total time",     v: fmtTime(selectedMeta.metadata.totalTime) },
            { l: "Charge",         v: selectedMeta.metadata.chargeTemp ? `${selectedMeta.metadata.chargeTemp}°` : "—" },
            { l: "Drop",           v: selectedMeta.metadata.dropTemp   ? `${selectedMeta.metadata.dropTemp}°`   : "—" },
            { l: "First crack",    v: fmtTime(selectedMeta.metadata.fcTime) },
            { l: "Turning point",  v: fmtTime(selectedMeta.metadata.tpTime) },
            { l: "Weight loss",    v: selectedMeta.metadata.weightLossPct != null ? `${selectedMeta.metadata.weightLossPct}%` : "—" },
            { l: "Machine",        v: selectedMeta.metadata.machine || "—" },
            { l: "Operator",       v: selectedMeta.metadata.operator || "—" },
          ].map(s => (
            <div key={s.l}>
              <div style={{ fontSize: 12, fontWeight: 500, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.04em" }}>{s.l}</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#000", marginTop: 2 }}>{s.v}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Canonical side-nav row dimensions ────────────────────────────
   Every nav row across every tab must use these. Don't fork the
   numbers locally — if a row needs to look different, make it not
   look like a nav row. */
const NAV_ROW_HEIGHT = 40;
const NAV_ROW_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: 11,
  width: "100%",
  padding: "8px 12px",
  borderRadius: 9,
  border: "none",
  cursor: "pointer",
  fontFamily: "Inter, sans-serif",
  textAlign: "left",
  background: "transparent",
  transition: "background 0.12s",
  minHeight: NAV_ROW_HEIGHT,
  boxSizing: "border-box",
  lineHeight: 1.25,
};
const NAV_ROW_LABEL_STYLE = (active) => ({
  flex: 1,
  fontSize: 15,
  fontWeight: active ? 700 : 500,
  color: active ? "#000000" : "#3F3F46",
  textAlign: "left",
  lineHeight: "20px",
  letterSpacing: "-0.005em",
});
const NAV_ROW_BADGE_STYLE = (active) => ({
  fontSize: 11,
  fontWeight: 600,
  color: active ? "#3F3F46" : "#71717A",
  background: active ? "rgba(0,0,0,0.08)" : "#F4F4F5",
  borderRadius: 6,
  padding: "1px 7px",
  lineHeight: "16px",
  fontFamily: "Inter, sans-serif",
  flexShrink: 0,
});
const NAV_ROW_ICON_SIZE = 17;
// Active icons read pure black; inactive icons sit at a softer gray so
// they're visible without competing with the label.
const NAV_ROW_ICON_COLOR = (active) => active ? "#000000" : "#9CA3AF";
const NAV_ROW_ICON_STROKE = 1.85;

const PanelNavItem = ({ icon: Icon, label, active, onClick, rightIcon: RightIcon, badge }) => (
  <button onClick={onClick} style={{ ...NAV_ROW_STYLE, background: active ? "#EDEBEA" : "transparent" }}
    onMouseEnter={e => { if (!active) e.currentTarget.style.background = "#F7F6F5"; }}
    onMouseLeave={e => { e.currentTarget.style.background = active ? "#EDEBEA" : "transparent"; }}
  >
    {Icon && <Icon size={NAV_ROW_ICON_SIZE} color={NAV_ROW_ICON_COLOR(active)} strokeWidth={NAV_ROW_ICON_STROKE} />}
    <span style={NAV_ROW_LABEL_STYLE(active)}>{label}</span>
    {badge != null && <span style={NAV_ROW_BADGE_STYLE(active)}>{badge}</span>}
    {RightIcon && <RightIcon size={14} color="#A1A1AA" strokeWidth={1.4} />}
  </button>
);

/* PanelNavItem variant for cupping-session rows: same hover-revealed
   rename/delete pattern as DayNavRow, plus a colored status pip so
   pending / approved / rejected reads at a glance. */
// Stable 3-char code derived from a cupping session's id (or name as
// a last resort). Used as the visible "session ID" in the side-nav,
// the All-sessions table, and any other surface that needs to show
// which session a sample belongs to. djb2 → base36 → 3-char prefix.
function getSessionCode(s) {
  const src = (s && (s.id || s.name)) || "session";
  let h = 5381;
  for (let i = 0; i < src.length; i++) h = ((h << 5) + h) + src.charCodeAt(i);
  return Math.abs(h).toString(36).toUpperCase().slice(0, 3).padStart(3, "X");
}

// Deterministic 6-char base36 hash of (sessionId, sampleId). Used in
// printed QR-code labels so the URL stays short enough to encode at a
// low QR version — long URLs produce dense codes that don't survive a
// 1-inch print. Both the server (when looking up /api/s/:code) and the
// client (when rendering QR labels) compute the same hash.
export function sampleShortCode(sessionId, sampleId) {
  const src = `${sessionId || ""}|${sampleId || ""}`;
  // 32-bit FNV-1a — small, stable across server (Node) and browser.
  let h = 0x811c9dc5;
  for (let i = 0; i < src.length; i++) {
    h ^= src.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(36).toUpperCase().padStart(6, "0").slice(0, 6);
}

// SCA total for a single { scores, defects } record. Returns 0 unless
// at least one primary attribute is present, so a sample with nothing
// but the secondary defaults doesn't fake a 30.
function totalFromScoreRecord(rec) {
  const s = rec?.scores;
  if (!s) return 0;
  const pk = ["fragrance", "flavor", "aftertaste", "acidity", "body", "balance", "overall"];
  if (!pk.some(k => s[k] != null)) return 0;
  const prim = pk.reduce((a, k) => a + (Number(s[k]) || 0), 0);
  const secV = (k) => (s[k] != null ? Number(s[k]) : 10);
  const sec = secV("uniformity") + secV("cleanCup") + secV("sweetness");
  return +(prim + sec - (Number(rec?.defects) || 0)).toFixed(2);
}

// Average across non-excluded per-cupper records on a sample. Returns
// null if no eligible records exist.
function cuppingsAverageRecord(coffee) {
  const list = (coffee && coffee.cuppings) || [];
  const withScores = list.filter(x => x.scores && !x.excludedFromAverage);
  if (withScores.length === 0) return null;
  const keys = ["fragrance", "flavor", "aftertaste", "acidity", "body", "balance", "overall", "uniformity", "cleanCup", "sweetness"];
  const avg = {};
  for (const k of keys) {
    const vals = withScores.map(x => x.scores[k] != null ? Number(x.scores[k]) : null).filter(v => v != null);
    if (vals.length > 0) avg[k] = +(vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(2);
  }
  const defects = withScores.reduce((s, x) => s + (Number(x.defects) || 0), 0) / withScores.length;
  return { scores: avg, defects: +defects.toFixed(2) };
}

// Live total for a sample: prefer the admin-set Main override, fall
// back to the average across non-excluded per-cupper records.
function sampleLiveTotal(coffee) {
  const main = totalFromScoreRecord({ scores: coffee?.scores, defects: coffee?.defects });
  if (main > 0) return main;
  const avg = cuppingsAverageRecord(coffee);
  return avg ? totalFromScoreRecord(avg) : 0;
}

// One-per-page label print for either format. `mode` is "small" or
// "shipping". `rows` is normalized to {autoId, coffeeName, sourcedFrom,
// roastDate, liveTotal, sessionId, sampleId}. Small labels only show
// the auto-ID + a QR code that deep-links into the public sample view.
function printSampleLabels(rows, mode) {
  if (!rows || rows.length === 0) return;
  const win = window.open("", "_blank", "width=900,height=1100");
  if (!win) return;
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
  const grade = (t) => t >= 90 ? "Outstanding" : t >= 85 ? "Excellent" : t >= 80 ? "Very Good" : t > 0 ? "Below Specialty" : "Unscored";

  // Small label = QR code on the left + black ID pill on the right,
  // sized for 1.1in × 3.5in label tape. We:
  //   - encode a 6-char short code (vs sessionId/sampleId pair) so the
  //     URL stays around 38 chars
  //   - shout the URL in uppercase. QR has a dense "alphanumeric"
  //     encoding mode for 0-9 A-Z and a few symbols (5.5 bits/char vs
  //     8 for byte mode); uppercase URLs hit it. Servers treat the
  //     scheme + host as case-insensitive; we match /s/ and /S/ on the
  //     server route.
  //   - ecc=L (low) instead of M; printed labels are clean enough.
  // Net effect: QR drops from ~v3 (29×29) to ~v2 (25×25).
  const qrUrl = (co) => {
    const code = sampleShortCode(co.sessionId, co.sampleId);
    const target = `${window.location.origin}/s/${code}`.toUpperCase();
    return `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=4&ecc=L&data=${encodeURIComponent(target)}`;
  };
  const renderSmall = (co) => `<section class="page small">
    <div class="qr"><img src="${qrUrl(co)}" alt="QR" /></div>
    <div class="pill">${esc(co.autoId)}</div>
  </section>`;
  const renderShipping = (co) => {
    const score = co.liveTotal > 0 ? co.liveTotal.toFixed(2) : "—";
    return `<section class="page shipping">
      <div class="row top">
        <div class="pill big">${esc(co.autoId)}</div>
        <div class="score">${score}<span class="g">${esc(grade(co.liveTotal))}</span></div>
      </div>
      <div class="name">${esc(co.coffeeName || "Unnamed")}</div>
      <div class="meta">
        <div><span class="lbl-l">Source</span><span class="lbl-v">${esc(co.sourcedFrom || "—")}</span></div>
        <div><span class="lbl-l">Roasted</span><span class="lbl-v">${esc(fmtDate(co.roastDate))}</span></div>
      </div>
    </section>`;
  };

  const labels = rows.map(co => mode === "small" ? renderSmall(co) : renderShipping(co)).join("");

  const styles = `
    *{box-sizing:border-box}
    html,body{margin:0;padding:0;background:#fff;color:#111;font-family:'Inter',-apple-system,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .page{page-break-after:always;break-after:page;display:flex;flex-direction:column;align-items:center;justify-content:center;}
    .page:last-child{page-break-after:auto;break-after:auto;}
    .pill{display:inline-block;background:#000;color:#fff;border-radius:9999px;font-weight:800;letter-spacing:0.04em;font-variant-numeric:tabular-nums;text-align:center;}
    .page.small{width:3.5in;height:1.1in;padding:0.05in 0.08in;flex-direction:row;align-items:center;justify-content:flex-start;gap:0.12in;}
    .page.small .qr{flex-shrink:0;width:1in;height:1in;}
    .page.small .qr img{display:block;width:100%;height:100%;object-fit:contain;image-rendering:pixelated;image-rendering:-webkit-optimize-contrast;}
    .page.small .pill{flex:1;font-size:0.42in;padding:0.12in 0.18in;line-height:1;text-align:center;}
    .page.shipping{width:100vw;height:100vh;padding:40px 48px;justify-content:flex-start;}
    .page.shipping .row.top{display:flex;justify-content:space-between;align-items:center;width:100%;margin-bottom:24px;}
    .page.shipping .pill.big{font-size:48px;padding:14px 26px;}
    .page.shipping .score{font-size:64px;font-weight:800;color:#000;text-align:right;line-height:1;font-variant-numeric:tabular-nums;}
    .page.shipping .score .g{display:block;font-size:12px;font-weight:700;color:#9CA3AF;text-transform:uppercase;letter-spacing:0.07em;margin-top:6px;}
    .page.shipping .name{font-size:36px;font-weight:700;color:#111;line-height:1.2;width:100%;margin-bottom:24px;}
    .page.shipping .meta{display:grid;grid-template-columns:1fr 1fr;gap:16px;width:100%;margin-top:auto;padding-top:18px;border-top:1px solid #E5E7EB;}
    .page.shipping .meta > div{display:flex;flex-direction:column;gap:4px;}
    .page.shipping .lbl-l{font-size:11px;color:#9CA3AF;text-transform:uppercase;letter-spacing:0.07em;font-weight:700;}
    .page.shipping .lbl-v{font-size:18px;color:#111;font-weight:600;}
    @page{margin:0;${mode === "shipping" ? "size:4in 6in;" : "size:3.5in 1.1in;"}}
    @media print{.page{height:auto;}}
  `;

  win.document.write(`<!doctype html><html><head><title>Sample labels — ${rows.length}</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
<style>${styles}</style></head><body>
${labels}
<script>window.addEventListener('load',function(){setTimeout(function(){window.print()},250)})<\/script>
</body></html>`);
  win.document.close();
}

// Decorate raw cupping coffees from a session into the row shape
// printSampleLabels() expects.
function buildLabelRowsForSession(session) {
  const prefix = getSessionCode(session);
  return (session?.coffees || []).map((co, idx) => ({
    autoId: `${prefix}-${String(idx + 1).padStart(2, "0")}`,
    coffeeName: co.coffeeName || "",
    sourcedFrom: co.sourcedFrom || "",
    roastDate: co.roastDate || "",
    liveTotal: sampleLiveTotal(co),
    sessionId: session.id,
    sampleId: co.id,
  }));
}

const SessionNavRow = ({ label, count, sessionCode, active, onClick, onRename, onDelete }) => {
  const [hovered, setHovered] = useState(false);
  const showActions = hovered || active;
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ ...NAV_ROW_STYLE, background: active ? "#EDEBEA" : "transparent" }}
      onClick={onClick}
    >
      {sessionCode && (
        <span style={{
          fontSize: 10, fontWeight: 700, color: active ? "#18181B" : "#71717A",
          background: active ? "rgba(0,0,0,0.08)" : "#F4F4F5",
          borderRadius: 5, padding: "2px 6px", lineHeight: "12px",
          fontFamily: "Inter, sans-serif", flexShrink: 0,
          letterSpacing: "0.04em", fontVariantNumeric: "tabular-nums",
        }}>{sessionCode}</span>
      )}
      <span style={{ ...NAV_ROW_LABEL_STYLE(active), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      {!showActions && (
        <span style={NAV_ROW_BADGE_STYLE(active)}>{count}</span>
      )}
      {showActions && (
        <span style={{ display: "inline-flex", gap: 2, flexShrink: 0 }}>
          <button
            onClick={e => { e.stopPropagation(); onRename(); }}
            title="Rename session"
            style={{ width: 22, height: 22, borderRadius: 5, padding: 0, border: "none", background: "transparent", color: "#71717A", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(0,0,0,0.06)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          ><Pencil size={11} strokeWidth={1.8} /></button>
          <button
            onClick={e => { e.stopPropagation(); onDelete(); }}
            title="Delete session"
            style={{ width: 22, height: 22, borderRadius: 5, padding: 0, border: "none", background: "transparent", color: "#B91C1C", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(220,38,38,0.08)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          ><Trash2 size={11} strokeWidth={1.8} /></button>
        </span>
      )}
    </div>
  );
};

/* PanelNavItem variant for production-day rows: same look as a normal
   nav row, but reveals small rename/delete icons on hover (or while
   active) so day management lives entirely in the side-nav without
   another pop-up surface. */
const DayNavRow = ({ label, count, active, canDelete, locked = false, onClick, onRename, onDelete }) => {
  const [hovered, setHovered] = useState(false);
  const showActions = hovered || active;
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ ...NAV_ROW_STYLE, background: active ? "#EDEBEA" : "transparent" }}
      onClick={onClick}
      onMouseDown={e => { if (!active && hovered) e.currentTarget.style.background = "#F7F6F5"; }}
    >
      <Calendar size={NAV_ROW_ICON_SIZE} color={NAV_ROW_ICON_COLOR(active)} strokeWidth={NAV_ROW_ICON_STROKE} />
      <span style={{ ...NAV_ROW_LABEL_STYLE(active), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      {locked && (
        <Lock size={12} color="#9CA3AF" strokeWidth={2.2} style={{ flexShrink: 0 }} aria-label="Locked — a roast has started for this day" />
      )}
      {!showActions && count != null && (
        <span style={NAV_ROW_BADGE_STYLE(active)}>{count}</span>
      )}
      {showActions && (
        <span style={{ display: "inline-flex", gap: 2, flexShrink: 0 }}>
          <button
            onClick={e => { e.stopPropagation(); onRename(); }}
            title="Rename day"
            style={{
              width: 22, height: 22, borderRadius: 5, padding: 0,
              border: "none", background: "transparent",
              color: "#71717A", cursor: "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(0,0,0,0.06)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          ><Pencil size={11} strokeWidth={1.8} /></button>
          {canDelete && (
            <button
              onClick={e => { e.stopPropagation(); onDelete(); }}
              title="Delete day"
              style={{
                width: 22, height: 22, borderRadius: 5, padding: 0,
                border: "none", background: "transparent",
                color: "#B91C1C", cursor: "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(220,38,38,0.08)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            ><Trash2 size={11} strokeWidth={1.8} /></button>
          )}
        </span>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   GREEN INVENTORY — Modals
   ═══════════════════════════════════════════════════════ */
function ModalShell({ title, subtitle, onClose, children, actions }) {
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(17, 24, 39, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, fontFamily: "Inter, sans-serif" }}
    >
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "92%", maxWidth: 640, maxHeight: "88vh", overflowY: "auto", boxShadow: "0 20px 50px rgba(0,0,0,0.25)" }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#000000" }}>{title}</div>
            {subtitle && <div style={{ fontSize: 14, color: "#6B7280", marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4 }} aria-label="Close"><X size={18} /></button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
        {actions && <div style={{ padding: "12px 24px 20px", borderTop: "1px solid #F3F4F6", display: "flex", justifyContent: "flex-end", gap: 8 }}>{actions}</div>}
      </div>
    </div>
  );
}

function FormRow({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

const textInputStyle = {
  width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB",
  background: "#fff", fontSize: 15, color: "#000000",
  fontFamily: "Inter, sans-serif", outline: "none", boxSizing: "border-box",
};

function AddLotModal({ onClose, onCreate }) {
  const [form, setForm] = useState({
    name: "",
    sourceName: "",
    origin: "",
    farm: "",
    variety: "",
    process: "",
    score: "",
    arrivalDate: new Date().toISOString().slice(0, 10),
    initialWeightLbs: "",
    pricePerLb: "",
    sourceType: "",
    supplier: "",
    location: "",
    moisture: "",
    bagSizeLbs: 154,
    lowStockLbs: 20,
    notes: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = form.name.trim() && parseFloat(form.initialWeightLbs) > 0;
  return (
    <ModalShell
      title="Add green source"
      subtitle="Records an initial receipt event — the starting weight is event-logged, not edited in place."
      onClose={onClose}
      actions={
        <>
          <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 500, color: "#374151", cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
          <button
            disabled={!canSubmit || submitting}
            onClick={async () => {
              setSubmitting(true);
              try { await onCreate(form); } finally { setSubmitting(false); }
            }}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #000000", background: canSubmit ? "#000000" : "#9CA3AF", color: "#fff", fontSize: 14, fontWeight: 600, cursor: canSubmit ? "pointer" : "not-allowed", fontFamily: "Inter, sans-serif", opacity: submitting ? 0.6 : 1 }}
          >
            {submitting ? "Creating…" : "Create source"}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div style={{ gridColumn: "1 / span 2" }}>
          <FormRow label="Coffee *" hint="Display name for this coffee">
            <input style={textInputStyle} value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Ethiopia Yirgacheffe — Kochere" />
          </FormRow>
        </div>
        <div style={{ gridColumn: "1 / span 2" }}>
          <FormRow label="Name" hint="Farmer name, product code, or any specific identifier">
            <input style={textInputStyle} value={form.sourceName} onChange={e => set("sourceName", e.target.value)} placeholder="e.g. Kochere Washing Station, COF-2025-001" />
          </FormRow>
        </div>
        <FormRow label="Origin"><input style={textInputStyle} value={form.origin} onChange={e => set("origin", e.target.value)} placeholder="Ethiopia" /></FormRow>
        <FormRow label="Farm / producer"><input style={textInputStyle} value={form.farm} onChange={e => set("farm", e.target.value)} placeholder="Kochere" /></FormRow>
        <FormRow label="Variety"><input style={textInputStyle} value={form.variety} onChange={e => set("variety", e.target.value)} placeholder="Heirloom" /></FormRow>
        <FormRow label="Process"><input style={textInputStyle} value={form.process} onChange={e => set("process", e.target.value)} placeholder="Washed" /></FormRow>
        <FormRow label="Score" hint="SCA score, 0–100"><input type="number" min="0" max="100" step="0.25" style={textInputStyle} value={form.score} onChange={e => set("score", e.target.value)} placeholder="87" /></FormRow>
        <FormRow label="Arrival date"><input type="date" style={textInputStyle} value={form.arrivalDate} onChange={e => set("arrivalDate", e.target.value)} /></FormRow>
        <FormRow label="Initial weight (lb) *"><input type="number" min="0" step="0.1" style={textInputStyle} value={form.initialWeightLbs} onChange={e => set("initialWeightLbs", e.target.value)} placeholder="154" /></FormRow>
        <FormRow label="Bag size (lb)" hint="Weight of one green bag from this supplier"><input type="number" min="1" step="1" style={textInputStyle} value={form.bagSizeLbs} onChange={e => set("bagSizeLbs", e.target.value)} placeholder="154" /></FormRow>
        <FormRow label="$ / lb"><input type="number" min="0" step="0.01" style={textInputStyle} value={form.pricePerLb} onChange={e => set("pricePerLb", e.target.value)} placeholder="6.25" /></FormRow>
        <FormRow label="Moisture %"><input type="number" min="0" step="0.1" style={textInputStyle} value={form.moisture} onChange={e => set("moisture", e.target.value)} placeholder="10.5" /></FormRow>
        <FormRow label="Low-stock threshold (lb)" hint="Alerts when weight drops to this level"><input type="number" min="0" style={textInputStyle} value={form.lowStockLbs} onChange={e => set("lowStockLbs", e.target.value)} /></FormRow>
        <FormRow label="Source type">
          <select style={textInputStyle} value={form.sourceType} onChange={e => set("sourceType", e.target.value)}>
            <option value="">Select…</option>
            <option value="Farmer">Farmer (direct trade)</option>
            <option value="Importer">Importer</option>
            <option value="Broker">Broker</option>
            <option value="Distributor">Distributor</option>
            <option value="Cooperative">Cooperative</option>
            <option value="Other">Other</option>
          </select>
        </FormRow>
        <FormRow label="Source name"><input style={textInputStyle} value={form.supplier} onChange={e => set("supplier", e.target.value)} placeholder="e.g. Daniel's Imports, Café Imports" /></FormRow>
        <FormRow label="Storage location"><input style={textInputStyle} value={form.location} onChange={e => set("location", e.target.value)} placeholder="Bin A" /></FormRow>
        <div style={{ gridColumn: "1 / span 2" }}>
          <FormRow label="Notes">
            <textarea style={{ ...textInputStyle, minHeight: 64, resize: "vertical" }} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Cupping notes, importer details, storage conditions…" />
          </FormRow>
        </div>
      </div>
    </ModalShell>
  );
}

function LotDetailModal({ lot, onClose, onEvent, onReconcile, onArchive }) {
  const [action, setAction] = useState(null); // "reconcile" | "sample" | "adjust" | null
  const [actionValue, setActionValue] = useState("");
  const [actionNote, setActionNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      if (action === "reconcile") {
        await onReconcile(lot.id, actionValue, actionNote);
      } else if (action === "sample") {
        const qty = parseFloat(actionValue);
        if (!Number.isFinite(qty) || qty <= 0) return;
        await onEvent(lot.id, { type: "sample", weightLbs: -qty, note: actionNote || "Sample / cupping" });
      } else if (action === "adjust") {
        const qty = parseFloat(actionValue);
        if (!Number.isFinite(qty)) return;
        await onEvent(lot.id, { type: "adjust", weightLbs: qty, note: actionNote });
      }
      setAction(null); setActionValue(""); setActionNote("");
    } catch (err) {
      alert(err.message || "Action failed");
    } finally { setBusy(false); }
  };

  const events = [...(lot.events || [])].sort((a, b) => (b.at || 0) - (a.at || 0));
  const fmtDate = (ts) => new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  const eventColor = { receipt: "#16A34A", roast: "#EA580C", sample: "#2563EB", adjust: "#7C3AED", transfer: "#0891B2" };

  return (
    <ModalShell
      title={lot.name}
      subtitle={[lot.sourceName, lot.origin, lot.process, lot.farm, lot.sourceType && lot.supplier ? `${lot.sourceType} · ${lot.supplier}` : (lot.sourceType || lot.supplier)].filter(Boolean).join(" · ") || undefined}
      onClose={onClose}
    >
      {/* Metadata strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 18 }}>
        {[
          { l: "Weight", v: <UnitWeight valueLbs={lot.currentWeightLbs} decimals={1} />, bold: true },
          { l: "Bags remaining", v: lot.bagSizeLbs && lot.currentWeightLbs != null ? `${(lot.currentWeightLbs / lot.bagSizeLbs).toFixed(1)} bags` : "—", hint: lot.bagSizeLbs ? <span><UnitWeight valueLbs={lot.bagSizeLbs} decimals={0} />/bag</span> : null },
          { l: "Age", v: lot.ageDays != null ? `${lot.ageDays}d` : "—" },
          { l: "Arrived", v: lot.arrivalDate || "—" },
          ...(lot.variety ? [{ l: "Variety", v: lot.variety }] : []),
          ...(lot.score != null ? [{ l: "Score", v: `${lot.score}`, hint: "SCA" }] : []),
        ].map(s => (
          <div key={s.l} style={{ background: "#F9FAFB", borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4 }}>{s.l}</div>
            <div style={{ fontSize: 15, fontWeight: s.bold ? 700 : 500, color: "#000000", marginTop: 2 }}>{s.v}</div>
            {s.hint && <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}>{s.hint}</div>}
          </div>
        ))}
      </div>

      {/* Action row */}
      {!action ? (
        <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
          <button onClick={() => setAction("reconcile")} style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Reconcile</button>
          <button onClick={() => setAction("sample")} style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Record sample</button>
          <button onClick={() => setAction("adjust")} style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Adjust</button>
          <button onClick={async () => { if (window.confirm("Archive this source? It'll stop showing in active inventory.")) { await onArchive(lot.id); onClose(); } }} style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#B91C1C", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Archive</button>
        </div>
      ) : (
        <div style={{ background: "#F9FAFB", borderRadius: 10, padding: 14, marginBottom: 18 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#000000", marginBottom: 10 }}>
            {action === "reconcile" && "Reconcile against physical count"}
            {action === "sample" && "Record sample / cupping quantity"}
            {action === "adjust" && "Manual adjustment"}
          </div>
          <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 10, lineHeight: 1.4 }}>
            {action === "reconcile" && `Weigh the bag, enter the actual number. We'll log an adjustment event for the delta (current: ${lot.currentWeightLbs?.toFixed(2)} lb).`}
            {action === "sample" && "Small quantity pulled for cupping or QC. Enter the amount — will be subtracted from weight."}
            {action === "adjust" && "Positive for additions, negative for losses. Always include a reason."}
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input
              type="number"
              step="0.1"
              value={actionValue}
              onChange={e => setActionValue(e.target.value)}
              placeholder={action === "reconcile" ? "Physical weight (lb)" : action === "sample" ? "Quantity (lb)" : "Delta (lb, e.g. -0.5)"}
              style={{ flex: 1, ...textInputStyle }}
            />
          </div>
          <input
            value={actionNote}
            onChange={e => setActionNote(e.target.value)}
            placeholder="Reason / note (e.g. bag moisture loss, spilled, sample roast)"
            style={{ ...textInputStyle, marginBottom: 10 }}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={() => { setAction(null); setActionValue(""); setActionNote(""); }} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, color: "#374151", cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
            <button disabled={busy || !actionValue} onClick={submit} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #000000", background: "#000000", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif", opacity: busy || !actionValue ? 0.5 : 1 }}>Save event</button>
          </div>
        </div>
      )}

      {/* Event history */}
      <div style={{ fontSize: 12, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>Event history ({events.length})</div>
      <div style={{ border: "1px solid #F3F4F6", borderRadius: 8, overflow: "hidden" }}>
        {events.map((e, i) => (
          <div key={e.id || i} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 14px", borderBottom: i < events.length - 1 ? "1px solid #F3F4F6" : "none" }}>
            <div style={{ width: 8, height: 8, borderRadius: 4, background: eventColor[e.type] || "#9CA3AF", marginTop: 6, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#000000", textTransform: "capitalize" }}>{e.type}{e.reconciliation ? " (reconciliation)" : ""}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: e.weightLbs >= 0 ? "#16A34A" : "#DC2626" }}>
                  {e.weightLbs >= 0 ? "+" : ""}{e.weightLbs.toFixed(2)} lb
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>{fmtDate(e.at)}{e.note ? ` · ${e.note}` : ""}</div>
            </div>
          </div>
        ))}
      </div>
    </ModalShell>
  );
}

/* Cupping is now a session: a named project that holds 0+ coffees,
   each scored individually. This modal just collects the session
   metadata; coffees are added (and scored) inside the detail view. */
/* ── QC notes modal (Roasting) ────────────────────────────────────
   Opens whenever a roaster marks a batch "Roasted" — collects the
   final roasted weight, color, optional rejection reason, and free-
   form notes. Compares the actual weight loss against the green
   coffee's expectedLossPct (set in Coffees & Blends → Green coffee)
   so the roaster sees if they're tracking against profile.

   Reuses the modal shell. Stamps the active user on the QC entry
   so attribution flows. */
function QcNotesModal({ batch, mode = "complete", existingQc = null, expectedLossPct = null, user, onClose, onSubmit }) {
  const [roastedLbs, setRoastedLbs] = useState(() => existingQc?.roastedLbs != null ? String(existingQc.roastedLbs) : (batch?.roastedLbs != null ? String(batch.roastedLbs) : ""));
  const [color, setColor] = useState(existingQc?.color || "");
  const [rejected, setRejected] = useState(!!existingQc?.rejected);
  const [rejectReason, setRejectReason] = useState(existingQc?.rejectReason || "");
  const [notes, setNotes] = useState(existingQc?.notes || "");
  const [busy, setBusy] = useState(false);

  const greenLbs = Number(batch?.greenLbs) || 0;
  const actualLossPct = (() => {
    const r = Number(roastedLbs);
    if (!greenLbs || !Number.isFinite(r) || r <= 0) return null;
    return +(((greenLbs - r) / greenLbs) * 100).toFixed(2);
  })();
  const lossDelta = (actualLossPct != null && expectedLossPct != null)
    ? +(actualLossPct - expectedLossPct).toFixed(2)
    : null;
  const deltaColor = lossDelta == null ? "#9CA3AF" : Math.abs(lossDelta) <= 1.0 ? "#16A34A" : Math.abs(lossDelta) <= 2.5 ? "#CC785C" : "#B91C1C";
  const deltaLabel = lossDelta == null ? "—" : `${lossDelta > 0 ? "+" : ""}${lossDelta.toFixed(2)}%`;

  const inputCss = { padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff", outline: "none", width: "100%", boxSizing: "border-box" };
  const labelCss = { display: "block", fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 };
  const canSubmit = (Number(roastedLbs) > 0 || rejected) && (!rejected || rejectReason.trim());

  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit({
        roastedLbs: Number(roastedLbs) || 0,
        color: color || null,
        rejected,
        rejectReason: rejected ? rejectReason.trim() : null,
        notes: notes.trim(),
      });
      onClose();
    } catch (err) { alert(err.message || "Couldn't save"); }
    finally { setBusy(false); }
  };

  const COLORS = [
    { value: "very-light", label: "Very light" },
    { value: "light",      label: "Light" },
    { value: "medium-light", label: "Medium-light" },
    { value: "medium",     label: "Medium" },
    { value: "medium-dark", label: "Medium-dark" },
    { value: "dark",       label: "Dark" },
    { value: "very-dark",  label: "Very dark" },
  ];

  return (
    <ModalShell
      title={mode === "edit" ? `QC notes — ${batch?.batchNum || "batch"}` : `Roast complete — QC notes`}
      subtitle={batch ? `${batch.productName || "Batch"} · ${greenLbs ? greenLbs.toFixed(1) + " lb green" : "untracked green"}` : ""}
      onClose={onClose}
      actions={
        <>
          <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
          <button onClick={submit} disabled={!canSubmit || busy} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: !canSubmit || busy ? "#D1D5DB" : (rejected ? "#B91C1C" : "#000"), color: "#fff", fontSize: 14, fontWeight: 600, cursor: !canSubmit || busy ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>
            {busy ? "Saving…" : (rejected ? "Save & reject" : (mode === "edit" ? "Save QC notes" : "Mark roasted"))}
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <label style={labelCss}>Roasted weight *</label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="number" min="0" step="0.1" value={roastedLbs} onChange={e => setRoastedLbs(e.target.value)} placeholder="e.g. 8.5" style={{ ...inputCss, maxWidth: 180 }} autoFocus />
            <span style={{ fontSize: 13, color: "#6B7280" }}>lb</span>
            {actualLossPct != null && (
              <span style={{ fontSize: 12, color: "#3F3F46" }}>
                ⇒ <strong style={{ color: "#000" }}>{actualLossPct.toFixed(2)}%</strong> weight loss
              </span>
            )}
          </div>
          {/* Expected vs actual loss compare */}
          {expectedLossPct != null && (
            <div style={{ marginTop: 10, padding: "10px 12px", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, display: "flex", justifyContent: "space-between", fontSize: 13 }}>
              <span style={{ color: "#6B7280" }}>Expected loss for this coffee</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                <strong>{Number(expectedLossPct).toFixed(1)}%</strong>
                {actualLossPct != null && <>
                  <span style={{ margin: "0 6px", color: "#9CA3AF" }}>·</span>
                  <span style={{ color: deltaColor, fontWeight: 600 }}>{deltaLabel}</span>
                </>}
              </span>
            </div>
          )}
          {expectedLossPct == null && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#9CA3AF" }}>
              No expected weight loss set for this coffee yet — fill it in on Coffees &amp; Blends → Green coffee.
            </div>
          )}
        </div>

        <div>
          <label style={labelCss}>Color</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {COLORS.map(c => {
              const active = color === c.value;
              return (
                <button key={c.value} onClick={() => setColor(c.value)} style={{
                  padding: "6px 11px", borderRadius: 99,
                  border: `1px solid ${active ? "#000" : "#E5E7EB"}`,
                  background: active ? "#000" : "#fff",
                  color: active ? "#fff" : "#3F3F46",
                  fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif",
                  cursor: "pointer",
                }}>{c.label}</button>
              );
            })}
          </div>
        </div>

        <div>
          <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, border: `1px solid ${rejected ? "#FECACA" : "#E5E7EB"}`, background: rejected ? "#FEF2F2" : "#fff", cursor: "pointer" }}>
            <input type="checkbox" checked={rejected} onChange={e => setRejected(e.target.checked)} style={{ accentColor: "#B91C1C" }} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: rejected ? "#991B1B" : "#000" }}>Reject this batch</div>
              <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
                The batch was roasted but isn't sellable. Inventory is still drawn down (the bag was used) and the batch is logged in QC history with the reason. Won't appear in producible stock.
              </div>
            </div>
          </label>
          {rejected && (
            <textarea
              value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              placeholder="What went wrong? (scorched, baked, defects, off taste…)"
              rows={2}
              style={{ ...inputCss, marginTop: 8, minHeight: 60, resize: "vertical" }}
              autoFocus={!rejectReason}
            />
          )}
        </div>

        <div>
          <label style={labelCss}>Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Anything to remember — first crack timing, ambient conditions, profile tweaks…" style={{ ...inputCss, minHeight: 60, resize: "vertical" }} />
        </div>

        <div style={{ paddingTop: 8, borderTop: "1px solid #F3F4F6", fontSize: 12, color: "#9CA3AF" }}>
          Logged as <strong style={{ color: "#000", fontWeight: 600 }}>{user?.name || user?.email || "current user"}</strong>
          {existingQc && existingQc.byName && existingQc.byName !== (user?.name || "") && (
            <span> · originally by <strong style={{ color: "#000", fontWeight: 600 }}>{existingQc.byName}</strong></span>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

/* ── Single sample intake ─────────────────────────────────────────
   Quick way to log a sample that's just arrived on the bench, before
   you've decided which session it'll cup with. The user fills in the
   minimum (name + maybe origin/source), saves, and the sample is then
   pickable from the New-cupping picker. */
function AddSampleModal({ onClose, onCreate }) {
  const [form, setForm] = useState({
    coffeeName: "", sourcedFrom: "", origin: "", process: "",
    region: "", variety: "", costPerLb: "", pricePerLb: "",
    expectedNotes: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const canSubmit = form.coffeeName.trim();
  const submit = async () => {
    setBusy(true);
    try {
      await onCreate([form]);
      onClose();
    } catch (err) { alert(err.message || "Couldn't save"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell
      title="New sample"
      subtitle="Intake a single sample. You can score it later from a cupping session."
      onClose={onClose}
      actions={
        <>
          <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 500, color: "#374151", cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
          <button onClick={submit} disabled={!canSubmit || busy} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: !canSubmit || busy ? "#D1D5DB" : "#000", color: "#fff", fontSize: 14, fontWeight: 600, cursor: !canSubmit || busy ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>
            {busy ? "Saving…" : "Save sample"}
          </button>
        </>
      }
    >
      <FormRow label="Coffee name *" hint="Origin + farm/lot name as the importer labelled it">
        <input style={textInputStyle} autoFocus value={form.coffeeName} onChange={e => set("coffeeName", e.target.value)} placeholder="Yirgacheffe — Kochere" />
      </FormRow>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14 }}>
        <FormRow label="Source"><input style={textInputStyle} value={form.sourcedFrom} onChange={e => set("sourcedFrom", e.target.value)} placeholder="Importer / farmer" /></FormRow>
        <FormRow label="Origin"><input style={textInputStyle} value={form.origin} onChange={e => set("origin", e.target.value)} placeholder="Ethiopia" /></FormRow>
        <FormRow label="Process"><input style={textInputStyle} value={form.process} onChange={e => set("process", e.target.value)} placeholder="Washed" /></FormRow>
        <FormRow label="Region / farm"><input style={textInputStyle} value={form.region} onChange={e => set("region", e.target.value)} placeholder="Kochere" /></FormRow>
        <FormRow label="Variety"><input style={textInputStyle} value={form.variety} onChange={e => set("variety", e.target.value)} placeholder="Heirloom" /></FormRow>
        <FormRow label="Cost / lb">
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: "#6B7280" }}>$</span>
            <input type="number" min="0" step="0.01" style={textInputStyle} value={form.costPerLb} onChange={e => set("costPerLb", e.target.value)} placeholder="6.85" />
          </div>
        </FormRow>
        <FormRow label="Price / lb">
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: "#6B7280" }}>$</span>
            <input type="number" min="0" step="0.01" style={textInputStyle} value={form.pricePerLb} onChange={e => set("pricePerLb", e.target.value)} placeholder="22.00" />
          </div>
        </FormRow>
      </div>
      <FormRow label="Expected flavor notes"><input style={textInputStyle} value={form.expectedNotes} onChange={e => set("expectedNotes", e.target.value)} placeholder="Stone fruit, jasmine, tea-like" /></FormRow>
      <FormRow label="Notes"><textarea style={{ ...textInputStyle, minHeight: 56, resize: "vertical" }} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Anything to remember when cupping…" /></FormRow>
    </ModalShell>
  );
}

/* ── Bulk sample intake ───────────────────────────────────────────
   Repeater table. Each row = one sample. Click + to add a row,
   ✕ to remove. Empty rows are dropped on save. Tab through cells
   like a spreadsheet. */
function BulkAddSamplesModal({ onClose, onCreate }) {
  const blank = () => ({ coffeeName: "", sourcedFrom: "", origin: "", process: "", costPerLb: "" });
  const [rows, setRows] = useState([blank(), blank(), blank()]);
  const [busy, setBusy] = useState(false);
  const setRow = (i, patch) => setRows(rs => rs.map((r, j) => j === i ? { ...r, ...patch } : r));
  const addRow = () => setRows(rs => [...rs, blank()]);
  const removeRow = (i) => setRows(rs => rs.length > 1 ? rs.filter((_, j) => j !== i) : rs);
  const validRows = rows.filter(r => r.coffeeName.trim());
  const canSubmit = validRows.length > 0;
  const submit = async () => {
    setBusy(true);
    try {
      await onCreate(validRows);
      onClose();
    } catch (err) { alert(err.message || "Couldn't save"); }
    finally { setBusy(false); }
  };
  const cellInput = { padding: "8px 10px", borderRadius: 6, border: "1px solid #E5E7EB", fontSize: 13, fontFamily: "Inter, sans-serif", background: "#fff", outline: "none", width: "100%", boxSizing: "border-box" };
  const headCss = { padding: "6px 10px", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "left", whiteSpace: "nowrap" };
  return (
    <ModalShell
      title="Bulk add samples"
      subtitle={`Intake several samples at once. ${validRows.length} ready to save.`}
      onClose={onClose}
      actions={
        <>
          <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 500, color: "#374151", cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
          <button onClick={submit} disabled={!canSubmit || busy} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: !canSubmit || busy ? "#D1D5DB" : "#000", color: "#fff", fontSize: 14, fontWeight: 600, cursor: !canSubmit || busy ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>
            {busy ? "Saving…" : `Save ${validRows.length} sample${validRows.length === 1 ? "" : "s"}`}
          </button>
        </>
      }
    >
      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "auto" }} />
            <col style={{ width: 140 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 36 }} />
          </colgroup>
          <thead>
            <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
              <th style={headCss}>Coffee name *</th>
              <th style={headCss}>Source</th>
              <th style={headCss}>Origin</th>
              <th style={headCss}>Process</th>
              <th style={headCss}>Cost / lb</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderBottom: i === rows.length - 1 ? "none" : "1px solid #F3F4F6" }}>
                <td style={{ padding: 6 }}><input style={cellInput} value={r.coffeeName} onChange={e => setRow(i, { coffeeName: e.target.value })} placeholder="Yirgacheffe Kochere" autoFocus={i === 0} /></td>
                <td style={{ padding: 6 }}><input style={cellInput} value={r.sourcedFrom} onChange={e => setRow(i, { sourcedFrom: e.target.value })} placeholder="Importer" /></td>
                <td style={{ padding: 6 }}><input style={cellInput} value={r.origin} onChange={e => setRow(i, { origin: e.target.value })} placeholder="Ethiopia" /></td>
                <td style={{ padding: 6 }}><input style={cellInput} value={r.process} onChange={e => setRow(i, { process: e.target.value })} placeholder="Washed" /></td>
                <td style={{ padding: 6 }}><input type="number" min="0" step="0.01" style={cellInput} value={r.costPerLb} onChange={e => setRow(i, { costPerLb: e.target.value })} placeholder="6.85" /></td>
                <td style={{ padding: 6, textAlign: "center" }}>
                  <button onClick={() => removeRow(i)} disabled={rows.length <= 1} title="Remove row" style={{ width: 24, height: 24, padding: 0, borderRadius: 6, border: "none", background: "transparent", color: rows.length <= 1 ? "#D1D5DB" : "#9CA3AF", cursor: rows.length <= 1 ? "default" : "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        onClick={addRow}
        style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 12, fontWeight: 500, fontFamily: "Inter, sans-serif", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
      ><Plus size={12} strokeWidth={2.2} /> Add row</button>
    </ModalShell>
  );
}

function AddCuppingModal({ onClose, onCreate, cupperSuggestions = [], sourcingSuggestions = [], availableSamples = [] }) {
  const [form, setForm] = useState({
    name: "",
    cupperName: "",
    sourcingCompany: "",
    cuppingDate: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  // Picked sample ids (subset of availableSamples) — these will be
  // moved out of the standalone store and into the new session.
  const [pickedSamples, setPickedSamples] = useState(() => new Set());
  const togglePicked = (id) => setPickedSamples(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const [busy, setBusy] = useState(false);
  const canSubmit = form.name.trim();

  return (
    <ModalShell
      title="New cupping session"
      subtitle="A session is a project. You'll add samples and score them inside it."
      onClose={onClose}
      actions={
        <>
          <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 500, color: "#374151", cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
          <button
            disabled={!canSubmit || busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onCreate({ ...form, coffees: [], attachSampleIds: Array.from(pickedSamples) });
              } catch (err) { alert(err.message || "Couldn't save"); }
              finally { setBusy(false); }
            }}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #000000", background: canSubmit ? "#000000" : "#9CA3AF", color: "#fff", fontSize: 14, fontWeight: 600, cursor: canSubmit ? "pointer" : "not-allowed", fontFamily: "Inter, sans-serif", opacity: busy ? 0.6 : 1 }}
          >
            {busy ? "Creating…" : "Create session"}
          </button>
        </>
      }
    >
      <FormRow label="Session name *" hint="e.g. April lineup, Daniel's samples, Tuesday QC">
        <input
          style={textInputStyle}
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder="April lineup"
          autoFocus
        />
      </FormRow>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14 }}>
        <FormRow label="Cupper">
          <input
            list="cupping-cupper-suggestions"
            style={textInputStyle}
            value={form.cupperName}
            onChange={e => setForm(f => ({ ...f, cupperName: e.target.value }))}
            placeholder="Pick or type a name"
          />
          <datalist id="cupping-cupper-suggestions">
            {cupperSuggestions.map(s => <option key={s} value={s} />)}
          </datalist>
        </FormRow>
        <FormRow label="Source" hint="Importer, farmer, distributor — optional">
          <input
            list="cupping-sourcing-suggestions"
            style={textInputStyle}
            value={form.sourcingCompany}
            onChange={e => setForm(f => ({ ...f, sourcingCompany: e.target.value }))}
            placeholder="Daniel's Imports, Café Imports…"
          />
          <datalist id="cupping-sourcing-suggestions">
            {sourcingSuggestions.map(s => <option key={s} value={s} />)}
          </datalist>
        </FormRow>
        <FormRow label="Cupping date">
          <input type="date" style={textInputStyle} value={form.cuppingDate} onChange={e => setForm(f => ({ ...f, cuppingDate: e.target.value }))} />
        </FormRow>
      </div>
      <FormRow label="Session notes">
        <textarea style={{ ...textInputStyle, minHeight: 64, resize: "vertical" }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Brew method, table setup, anything that applies to the whole session…" />
      </FormRow>

      {/* ── Pre-cupping sample picker ──────────────────────────────
          Only shows samples NOT already attached to a session, per the
          product requirement. If the workspace has none, the section
          is suppressed (we still let the user create an empty session
          and add coffees from the detail view). */}
      {availableSamples.length > 0 && (
        <div style={{ marginTop: 18, padding: "14px 14px 10px", background: "#FAFAFA", borderRadius: 10, border: "1px solid #F3F4F6" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Attach samples to this session
            </span>
            <span style={{ fontSize: 12, color: "#9CA3AF" }}>
              {pickedSamples.size} of {availableSamples.length} picked
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto" }}>
            {availableSamples.map(s => {
              const checked = pickedSamples.has(s.id);
              return (
                <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 6, cursor: "pointer", background: checked ? "#fff" : "transparent", border: `1px solid ${checked ? "#000" : "transparent"}` }}>
                  <input type="checkbox" checked={checked} onChange={() => togglePicked(s.id)} style={{ accentColor: "#000" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#000", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.coffeeName || "(unnamed)"}</div>
                    <div style={{ fontSize: 11, color: "#6B7280" }}>
                      {[s.origin, s.process, s.sourcedFrom].filter(Boolean).join(" · ") || "no metadata"}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ marginTop: 16, padding: "12px 14px", background: "#F9FAFB", borderRadius: 10, fontSize: 13, color: "#6B7280", lineHeight: 1.55 }}>
        After you create the session, click into it to add samples and score each one. Approval status (pending → approved/rejected) is set per sample.
      </div>
    </ModalShell>
  );
}

/* OLD: legacy single-coffee cupping modal — keep as a private helper
   used inside the session detail view to add/edit one coffee at a
   time. Same SCA scoresheet, just scoped to one coffee. */
function CoffeeScoreModal({ onClose, onSave, coffee = null, coffeeSuggestions = [], sources = [] }) {
  const isNew = !coffee;
  const [form, setForm] = useState(() => ({
    id: coffee?.id || null,
    coffeeName: coffee?.coffeeName || "",
    roastDate: coffee?.roastDate || new Date().toISOString().slice(0, 10),
    sourcedFrom: coffee?.sourcedFrom || "",
    expectedNotes: coffee?.expectedNotes || "",
    scores: coffee?.scores || {
      fragrance: 7.5, flavor: 7.5, aftertaste: 7.5, acidity: 7.5,
      body: 7.5, balance: 7.5, overall: 7.5,
      uniformity: 10, cleanCup: 10, sweetness: 10,
    },
    descriptors: coffee?.descriptors || { fragrance: "", flavor: "", aftertaste: "" },
    defects: coffee?.defects || 0,
    notes: coffee?.notes || "",
  }));
  const setScore = (k, v) => setForm(f => ({ ...f, scores: { ...f.scores, [k]: v } }));
  const setDescriptor = (k, v) => setForm(f => ({ ...f, descriptors: { ...f.descriptors, [k]: v } }));
  const primary = ["fragrance", "flavor", "aftertaste", "acidity", "body", "balance", "overall"];
  const primarySum = primary.reduce((s, k) => s + (Number(form.scores[k]) || 0), 0);
  const secondarySum = (Number(form.scores.uniformity) || 0) + (Number(form.scores.cleanCup) || 0) + (Number(form.scores.sweetness) || 0);
  const total = +(primarySum + secondarySum - (Number(form.defects) || 0)).toFixed(2);
  const [busy, setBusy] = useState(false);
  const canSubmit = form.coffeeName.trim();
  const scoreLabel = (n) => n >= 90 ? "Outstanding" : n >= 85 ? "Excellent" : n >= 80 ? "Very Good" : "Below Specialty";
  const scoreColor = (n) => n >= 90 ? "#B8860B" : n >= 85 ? "#16A34A" : n >= 80 ? "#111111" : "#6B7280";

  return (
    <ModalShell
      title={isNew ? "Add sample to session" : "Edit sample score"}
      subtitle={isNew
        ? "Just the basics now — score it later from the session view."
        : "SCA scoresheet · primary attributes + secondary checks · 0–100 total"}
      onClose={onClose}
      actions={
        <>
          <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 500, color: "#374151", cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
          <button
            disabled={!canSubmit || busy}
            onClick={async () => {
              setBusy(true);
              try {
                // Brand-new coffee: drop the default scoring / descriptors / defects /
                // notes so the row reads as "Not scored" until someone actually cups
                // it. We only persist the basics they typed in the form.
                const toSave = isNew
                  ? { id: form.id, coffeeName: form.coffeeName, roastDate: form.roastDate, sourcedFrom: form.sourcedFrom, expectedNotes: form.expectedNotes }
                  : form;
                await onSave(toSave);
              } catch (err) { alert(err.message || "Couldn't save"); }
              finally { setBusy(false); }
            }}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #000000", background: canSubmit ? "#000000" : "#9CA3AF", color: "#fff", fontSize: 14, fontWeight: 600, cursor: canSubmit ? "pointer" : "not-allowed", fontFamily: "Inter, sans-serif", opacity: busy ? 0.6 : 1 }}
          >
            {busy ? "Saving…" : (isNew ? "Add sample" : `Save · ${total.toFixed(2)}`)}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <FormRow label="Name *">
          <input
            list="cupping-coffee-suggestions"
            style={textInputStyle}
            value={form.coffeeName}
            onChange={e => setForm(f => ({ ...f, coffeeName: e.target.value }))}
            placeholder="Ethiopia Yirgacheffe — Kochere"
            autoFocus
          />
          <datalist id="cupping-coffee-suggestions">
            {coffeeSuggestions.map(c => <option key={c} value={c} />)}
          </datalist>
        </FormRow>
        <FormRow label="Roast date">
          <input type="date" style={textInputStyle} value={form.roastDate} onChange={e => setForm(f => ({ ...f, roastDate: e.target.value }))} />
        </FormRow>
        <FormRow label="Sourced from" hint="Manage sources in the side-nav under All sessions.">
          <select
            value={form.sourcedFrom}
            onChange={e => setForm(f => ({ ...f, sourcedFrom: e.target.value }))}
            style={textInputStyle}
          >
            <option value="">— Pick a source —</option>
            {sources.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
            {/* Keep whatever was previously stored, even if the source was removed */}
            {form.sourcedFrom && !sources.some(s => s.name === form.sourcedFrom) && (
              <option value={form.sourcedFrom}>{form.sourcedFrom} (removed)</option>
            )}
          </select>
        </FormRow>
        <FormRow label="Expected notes" hint="What you expect this sample to taste like.">
          <input
            style={textInputStyle}
            value={form.expectedNotes}
            onChange={e => setForm(f => ({ ...f, expectedNotes: e.target.value }))}
            placeholder="floral, citrus, jasmine"
          />
        </FormRow>
      </div>
      {/* New-coffee form ends with the basics above; scoring fields
          only render when editing an existing coffee. */}
      {!isNew && (<>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 18, marginBottom: 10 }}>Primary attributes (6–10, 0.25 steps)</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {primary.map(k => (
          <div key={k} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#F9FAFB", borderRadius: 8 }}>
            <span style={{ fontSize: 14, color: "#374151", textTransform: "capitalize" }}>{k}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="range" min={6} max={10} step={0.25}
                value={form.scores[k]}
                onChange={e => setScore(k, parseFloat(e.target.value))}
                style={{ width: 100 }}
              />
              <span style={{ width: 42, textAlign: "right", fontWeight: 700, fontSize: 14, color: "#111" }}>{Number(form.scores[k]).toFixed(2)}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 18, marginBottom: 10 }}>Secondary checks (0 or 10)</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        {["uniformity", "cleanCup", "sweetness"].map(k => (
          <button
            key={k}
            onClick={() => setScore(k, form.scores[k] === 10 ? 0 : 10)}
            style={{
              padding: "10px 12px", borderRadius: 8,
              border: `1px solid ${form.scores[k] === 10 ? "#000000" : "#E5E7EB"}`,
              background: form.scores[k] === 10 ? "#000000" : "#fff",
              color: form.scores[k] === 10 ? "#fff" : "#374151",
              fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
              textTransform: "capitalize",
            }}
          >
            {k === "cleanCup" ? "Clean cup" : k} · {form.scores[k] === 10 ? "10" : "0"}
          </button>
        ))}
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 18, marginBottom: 10 }}>Defects (subtract from total)</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <input type="number" min="0" step="2" style={{ ...textInputStyle, width: 100 }} value={form.defects} onChange={e => setForm(f => ({ ...f, defects: parseFloat(e.target.value) || 0 }))} />
        <span style={{ fontSize: 13, color: "#9CA3AF" }}>points subtracted</span>
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 18, marginBottom: 10 }}>Descriptors</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        {["fragrance", "flavor", "aftertaste"].map(k => (
          <FormRow key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}>
            <input style={textInputStyle} value={form.descriptors[k]} onChange={e => setDescriptor(k, e.target.value)} placeholder={k === "fragrance" ? "floral, citrus, jasmine" : k === "flavor" ? "stone fruit, cocoa" : "clean, lingering"} />
          </FormRow>
        ))}
      </div>
      <FormRow label="Notes">
        <textarea style={{ ...textInputStyle, minHeight: 64, resize: "vertical" }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Brew method, TDS, cupping table, anything worth remembering…" />
      </FormRow>

      <div style={{ marginTop: 16, padding: "14px 16px", background: "#F9FAFB", borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4 }}>Total score</div>
          <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>{scoreLabel(total)}</div>
        </div>
        <div style={{ fontSize: 36, fontWeight: 800, color: scoreColor(total), letterSpacing: "-0.02em" }}>{total.toFixed(2)}</div>
      </div>
      </>)}
    </ModalShell>
  );
}

/* In-row inline editor: same fields as the score modal, but lives in
   the expanded coffee row so the user can score multiple coffees side-
   by-side without opening dialogs. Auto-saves debounced. Treats a
   coffee with no scores as truly unscored — dragging any slider seeds
   the score object with sensible defaults. */
function CoffeeInlineEditor({ coffee, record = null, onPatch, sources = [], readOnly = false, hideBasics = false, basicsLocked = false }) {
  // `record` (when supplied) overrides the per-tab scoring fields so
  // the editor can render an average / per-cupper view while still
  // showing the coffee's basics (name, roast date, source, expected
  // notes). Falls back to the coffee's top-level fields for back-
  // compat with single-cupper coffees.
  const initial = {
    ...coffee,
    scores: record ? record.scores : coffee.scores,
    descriptors: record ? record.descriptors : coffee.descriptors,
    defects: record ? (record.defects || 0) : (coffee.defects || 0),
    notes: record ? (record.notes || "") : (coffee.notes || ""),
  };
  const [local, setLocal] = useState(initial);
  // External updates (status flip, tab switch, average refresh) re-sync.
  useEffect(() => { setLocal(initial); /* eslint-disable-next-line */ }, [coffee.id, coffee.status, coffee.coffeeName, coffee.roastDate, coffee.sourcedFrom, coffee.expectedNotes, JSON.stringify(record)]);

  const update = (patch) => {
    if (readOnly) return;
    const next = typeof patch === "function" ? patch(local) : { ...local, ...patch };
    setLocal(next);
    onPatch && onPatch(next);
  };
  const updateOnBlur = (k) => (e) => update({ [k]: e.target.value });
  const setScore = (k, v) => {
    const seedScores = { fragrance: 7.5, flavor: 7.5, aftertaste: 7.5, acidity: 7.5, body: 7.5, balance: 7.5, overall: 7.5, uniformity: 10, cleanCup: 10, sweetness: 10 };
    const base = local.scores || seedScores;
    update({ scores: { ...base, [k]: v } });
  };
  const setDescriptor = (k, v) => {
    const base = local.descriptors || { fragrance: "", flavor: "", aftertaste: "" };
    update({ descriptors: { ...base, [k]: v } });
  };

  const primary = ["fragrance", "flavor", "aftertaste", "acidity", "body", "balance", "overall"];
  const secondary = ["uniformity", "cleanCup", "sweetness"];
  const sc = local.scores || {};
  // "Scored" requires at least one primary attribute set; secondary-
  // only or empty scores objects don't count (they'd otherwise show
  // a fake 30 from the SCA secondary defaults).
  const isScored = !!local.scores && primary.some(k => local.scores[k] != null);
  const inputCss = { ...textInputStyle, fontSize: 13 };

  // Live total — SCA-compliant. Per the SCAA / SCA protocol:
  //   - Each of the 7 primary attributes is scored 6.00–10.00 (max 70).
  //   - Each of the 3 secondary checks (Uniformity / Clean Cup /
  //     Sweetness) defaults to 10/10/10 (full marks) and is reduced
  //     by 2 per defective cup (5 cups per sample). We model the
  //     simplified case here: the cupper toggles each attribute to
  //     0 if any cup fails, otherwise it stays at 10. An *unset*
  //     secondary score (undefined) is treated as 10 — the SCA
  //     "default until you find a fault" behavior.
  //   - Defects are subtracted from the running total.
  // Updates live as sliders move so the radar + total reflect the
  // in-flight state, not just what's been saved.
  const primVal = (k) => Number(sc[k]) || 0;
  const secVal  = (k) => (sc[k] != null ? Number(sc[k]) : 10);
  const liveTotal = (() => {
    if (!isScored) return 0;
    const primSum = primary.reduce((s, k) => s + primVal(k), 0);
    const secSum  = secondary.reduce((s, k) => s + secVal(k), 0);
    const def = Number(local.defects) || 0;
    return +(primSum + secSum - def).toFixed(2);
  })();
  // SCA grade bands:
  //   90.00+        Outstanding
  //   85.00–89.99   Excellent
  //   80.00–84.99   Very Good   (Specialty threshold)
  //   <80           Below Specialty
  const totalColor = liveTotal >= 90 ? "#B8860B"
    : liveTotal >= 85 ? "#16A34A"
    : liveTotal >= 80 ? "#111111"
    : "#6B7280";
  const totalGrade = liveTotal >= 90 ? "Outstanding"
    : liveTotal >= 85 ? "Excellent"
    : liveTotal >= 80 ? "Very Good"
    : liveTotal > 0 ? "Below Specialty"
    : "Unscored";

  return (
    <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
      {/* ─── Left: editable cupping properties ───────────────── */}
      <div style={{ flex: "1 1 360px", minWidth: 320, display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Basics — hidden / locked depending on caller. The basics
            belong to the coffee, not a single cupper, so per-cupper
            tabs render them read-only. */}
        {!hideBasics && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Basics</div>
          {/* Name + expected notes only — source and roast date live in
              the coffee row header so they read at a glance and don't
              take editor real estate. */}
          <FormRow label="Name">
            <input style={inputCss} defaultValue={local.coffeeName || ""} onBlur={updateOnBlur("coffeeName")} placeholder="Ethiopia Yirgacheffe — Kochere" disabled={readOnly || basicsLocked} />
          </FormRow>
          <FormRow label="Expected notes">
            <input style={inputCss} defaultValue={local.expectedNotes || ""} onBlur={updateOnBlur("expectedNotes")} placeholder="floral, citrus, jasmine" disabled={readOnly || basicsLocked} />
          </FormRow>
        </div>
        )}

        {/* Primary attributes */}
        <div>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em" }}>Primary attributes (6–10)</div>
            {!isScored && (
              <span style={{ fontSize: 11, color: "#9CA3AF", fontStyle: "italic" }}>Drag a slider to start scoring.</span>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {primary.map(k => {
              const v = sc[k] != null ? Number(sc[k]) : 7.5;
              return (
                <div key={k} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 12px", background: "#F9FAFB", borderRadius: 8 }}>
                  <span style={{ fontSize: 13, color: "#374151", textTransform: "capitalize", width: 90, flexShrink: 0 }}>{k}</span>
                  <input
                    type="range" min={6} max={10} step={0.25} value={v}
                    onChange={e => setScore(k, parseFloat(e.target.value))}
                    disabled={readOnly}
                    style={{ flex: 1, minWidth: 100, opacity: readOnly ? 0.6 : 1 }}
                  />
                  <span style={{ width: 40, textAlign: "right", fontWeight: 700, fontSize: 13, color: isScored ? "#111" : "#9CA3AF", flexShrink: 0 }}>{v.toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Secondary checks */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Secondary (0 or 10)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {secondary.map(k => {
              // SCA: secondary defaults to full marks (10) — the cupper
              // toggles to 0 only when a cup fails. Treat undefined as 10.
              const full = secVal(k) === 10;
              return (
                <button
                  key={k}
                  onClick={() => setScore(k, full ? 0 : 10)}
                  disabled={readOnly}
                  title={full ? "All cups good (full marks). Click if any cup fails." : "Marked as failed. Click to restore full marks."}
                  style={{ padding: "8px 10px", borderRadius: 8, border: `1px solid ${full ? "#000" : "#FCA5A5"}`, background: full ? "#000" : "#FEF2F2", color: full ? "#fff" : "#B91C1C", fontSize: 13, fontWeight: 500, cursor: readOnly ? "default" : "pointer", fontFamily: "Inter, sans-serif", textTransform: "capitalize", opacity: readOnly ? 0.7 : 1 }}
                >
                  {k === "cleanCup" ? "Clean cup" : k} · {full ? "10" : "0"}
                </button>
              );
            })}
          </div>
        </div>

        {/* Defects + descriptors */}
        <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 12 }}>
          <FormRow label="Defects" hint="Subtracted from the total.">
            <input
              type="number" min="0" step="2"
              style={inputCss}
              value={local.defects || 0}
              onChange={e => update({ defects: parseFloat(e.target.value) || 0 })}
              disabled={readOnly}
            />
          </FormRow>
          <FormRow label="Descriptors (flavor)" hint="Comma-separated, shows on the printed scorecard.">
            <input style={inputCss} defaultValue={(local.descriptors || {}).flavor || ""} onBlur={e => setDescriptor("flavor", e.target.value)} placeholder="stone fruit, cocoa…" disabled={readOnly} />
          </FormRow>
        </div>

        {/* Cost per lb — optional. If filled, it flows into Coffees &
            Blends so a coffee built from this sample auto-prices, and
            blend cost is computed as a weighted average of components. */}
        <FormRow label="Cost per lb (optional)" hint="Pre-fills the coffee cost when this sample becomes a roasted product. Blends weight-average across their components.">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#9CA3AF" }}>$</span>
            <input
              type="number" min="0" step="0.01"
              style={{ ...inputCss, maxWidth: 140 }}
              defaultValue={local.costPerLb != null ? local.costPerLb : ""}
              onBlur={e => {
                const v = e.target.value;
                update({ costPerLb: v === "" ? null : (parseFloat(v) || 0) });
              }}
              placeholder="e.g. 6.85"
              disabled={readOnly}
            />
            <span style={{ fontSize: 12, color: "#9CA3AF" }}>per lb green</span>
          </div>
        </FormRow>

        {/* Price per lb — what we sell roasted at. Flows into Coffees &
            Blends as the retail price for any coffee built from this
            sample. Independent of green cost. */}
        <FormRow label="Price per lb (optional)" hint="Roasted retail price. Pre-fills the coffee's price when this sample becomes a product.">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#9CA3AF" }}>$</span>
            <input
              type="number" min="0" step="0.01"
              style={{ ...inputCss, maxWidth: 140 }}
              defaultValue={local.pricePerLb != null ? local.pricePerLb : ""}
              onBlur={e => {
                const v = e.target.value;
                update({ pricePerLb: v === "" ? null : (parseFloat(v) || 0) });
              }}
              placeholder="e.g. 22.00"
              disabled={readOnly}
            />
            <span style={{ fontSize: 12, color: "#9CA3AF" }}>per lb roasted</span>
          </div>
        </FormRow>

        <FormRow label="Cupping notes">
          <textarea
            style={{ ...inputCss, minHeight: 60, resize: "vertical" }}
            defaultValue={local.notes || ""}
            onBlur={updateOnBlur("notes")}
            placeholder="Brew method, TDS, table impressions, anything worth remembering…"
            disabled={readOnly}
          />
        </FormRow>
      </div>

      {/* ─── Right: live cupping graph + total ───────────────── */}
      <div style={{ flex: "0 0 320px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
        {isScored ? (
          <>
            <CuppingRadar scores={sc} size={300} />
            <div style={{ width: "100%", padding: "14px 16px", background: "#fff", borderRadius: 10, border: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em" }}>Total</div>
                <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>{totalGrade}</div>
              </div>
              <div style={{ fontSize: 30, fontWeight: 800, color: totalColor, letterSpacing: "-0.02em" }}>{liveTotal.toFixed(2)}</div>
            </div>
          </>
        ) : (
          <div style={{
            width: "100%", aspectRatio: "1 / 1",
            border: "1px dashed #D1D5DB", borderRadius: 12,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            color: "#9CA3AF", textAlign: "center", padding: 24, gap: 6,
          }}>
            <Droplets size={28} strokeWidth={1.5} />
            <div style={{ fontSize: 14, fontWeight: 600, color: "#6B7280" }}>Not scored yet</div>
            <div style={{ fontSize: 12, lineHeight: 1.4 }}>Drag a primary slider on the left and the radar will fill in here in real time.</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* Add/edit a cupper — name + optional email. The roster shows up as
   tabs inside each coffee so each cupper can record their own scores. */
function CupperModal({ cupper = null, onClose, onSave }) {
  const isNew = !cupper;
  const [form, setForm] = useState({ id: cupper?.id || null, name: cupper?.name || "", email: cupper?.email || "" });
  const [busy, setBusy] = useState(false);
  const canSubmit = form.name.trim();
  return (
    <ModalShell
      title={isNew ? "Add cupper" : "Edit cupper"}
      subtitle="Anyone who scores samples in your sessions."
      onClose={onClose}
      actions={
        <>
          <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 500, color: "#374151", cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
          <button
            disabled={!canSubmit || busy}
            onClick={async () => { setBusy(true); try { await onSave(form); } catch (err) { alert(err.message || "Couldn't save"); } finally { setBusy(false); } }}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #000", background: canSubmit ? "#000" : "#9CA3AF", color: "#fff", fontSize: 14, fontWeight: 600, cursor: canSubmit ? "pointer" : "not-allowed", fontFamily: "Inter, sans-serif", opacity: busy ? 0.6 : 1 }}
          >{busy ? "Saving…" : (isNew ? "Add cupper" : "Save")}</button>
        </>
      }
    >
      <FormRow label="Name *">
        <input style={textInputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Maria Lopez" autoFocus />
      </FormRow>
      <FormRow label="Email" hint="Optional. Used when sharing a session by email later.">
        <input type="email" style={textInputStyle} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="maria@roastery.com" />
      </FormRow>
    </ModalShell>
  );
}

/* Add/edit a coffee source — minimal form. Stored fields: name (req),
   type (importer/farm/broker/other), region, contactName, contactEmail,
   notes. */
function CuppingSourceModal({ source = null, onClose, onSave }) {
  const isNew = !source;
  const [form, setForm] = useState({
    id: source?.id || null,
    name: source?.name || "",
    type: source?.type || "importer",
    region: source?.region || "",
    contactName: source?.contactName || "",
    contactEmail: source?.contactEmail || "",
    notes: source?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const canSubmit = form.name.trim();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <ModalShell
      title={isNew ? "Add source" : "Edit source"}
      subtitle="Importer, farm, broker, or anywhere else you buy green coffee from."
      onClose={onClose}
      actions={
        <>
          <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 500, color: "#374151", cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
          <button
            disabled={!canSubmit || busy}
            onClick={async () => { setBusy(true); try { await onSave(form); } catch (err) { alert(err.message || "Couldn't save"); } finally { setBusy(false); } }}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #000000", background: canSubmit ? "#000000" : "#9CA3AF", color: "#fff", fontSize: 14, fontWeight: 600, cursor: canSubmit ? "pointer" : "not-allowed", fontFamily: "Inter, sans-serif", opacity: busy ? 0.6 : 1 }}
          >{busy ? "Saving…" : (isNew ? "Add source" : "Save")}</button>
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <FormRow label="Name *">
          <input style={textInputStyle} value={form.name} onChange={e => set("name", e.target.value)} placeholder="Café Imports" autoFocus />
        </FormRow>
        <FormRow label="Type">
          <select style={textInputStyle} value={form.type} onChange={e => set("type", e.target.value)}>
            <option value="importer">Importer</option>
            <option value="farm">Farm / direct</option>
            <option value="broker">Broker</option>
            <option value="other">Other</option>
          </select>
        </FormRow>
        <FormRow label="Region / country">
          <input style={textInputStyle} value={form.region} onChange={e => set("region", e.target.value)} placeholder="Colombia, Ethiopia, …" />
        </FormRow>
        <FormRow label="Contact name">
          <input style={textInputStyle} value={form.contactName} onChange={e => set("contactName", e.target.value)} placeholder="Sales rep" />
        </FormRow>
        <FormRow label="Contact email">
          <input type="email" style={textInputStyle} value={form.contactEmail} onChange={e => set("contactEmail", e.target.value)} placeholder="rep@source.com" />
        </FormRow>
      </div>
      <FormRow label="Notes">
        <textarea style={{ ...textInputStyle, minHeight: 64, resize: "vertical" }} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Payment terms, lead times, anything worth remembering…" />
      </FormRow>
    </ModalShell>
  );
}

/* Radar / spider chart for cupping scores. Custom SVG so the look can be
   opinionated — faceted gem-like polygon with subtle per-wedge shading. */
function CuppingRadar({ scores, size = 380, primaryColor = "#000000", accentColor = "#06B6D4", compact = false }) {
  const axes = [
    { k: "fragrance", l: "Fragrance" },
    { k: "flavor", l: "Flavor" },
    { k: "aftertaste", l: "Aftertaste" },
    { k: "acidity", l: "Acidity" },
    { k: "body", l: "Body" },
    { k: "balance", l: "Balance" },
    { k: "overall", l: "Overall" },
  ];
  const cx = size / 2, cy = size / 2;
  const maxR = size * 0.36; // radius at score 10
  const minScore = 6, maxScore = 10;
  const labelR = maxR + size * 0.09;
  const N = axes.length;
  const angleFor = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / N;
  const scoreToR = (s) => {
    const clamped = Math.max(minScore, Math.min(maxScore, s));
    return ((clamped - minScore) / (maxScore - minScore)) * maxR;
  };
  const point = (i, s) => {
    const a = angleFor(i);
    const r = scoreToR(s);
    return [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
  };
  const gridLevels = [6, 7, 8, 9, 10];
  const gridPath = (level) => {
    const pts = axes.map((_, i) => {
      const a = angleFor(i);
      const r = scoreToR(level);
      return `${cx + Math.cos(a) * r},${cy + Math.sin(a) * r}`;
    });
    return `M${pts.join(" L")} Z`;
  };
  const dataPoints = axes.map(({ k }, i) => point(i, Number(scores?.[k]) || 6));
  const dataPath = `M${dataPoints.map(([x, y]) => `${x},${y}`).join(" L")} Z`;

  // Faceted triangles: center → vertex i → vertex i+1
  const facets = axes.map((_, i) => {
    const [x1, y1] = dataPoints[i];
    const [x2, y2] = dataPoints[(i + 1) % N];
    return { path: `M${cx},${cy} L${x1},${y1} L${x2},${y2} Z`, i };
  });

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" height={size} style={{ display: "block" }}>
      <defs>
        {facets.map((f, i) => (
          <linearGradient key={i} id={`facet-${i}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={primaryColor} stopOpacity={0.08 + (i % 3) * 0.06} />
            <stop offset="100%" stopColor={primaryColor} stopOpacity={0.18 + (i % 3) * 0.06} />
          </linearGradient>
        ))}
      </defs>
      {/* Grid rings */}
      {gridLevels.map(lvl => (
        <path key={lvl} d={gridPath(lvl)} fill="none" stroke="#E5E7EB" strokeWidth={lvl === maxScore ? 1.2 : 0.7} />
      ))}
      {/* Axis spokes */}
      {axes.map((_, i) => {
        const [x, y] = point(i, maxScore);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#E5E7EB" strokeWidth={0.7} />;
      })}
      {/* Faceted data polygon */}
      {facets.map((f, i) => (
        <path key={i} d={f.path} fill={`url(#facet-${i})`} stroke="none" />
      ))}
      {/* Outline over the facets */}
      <path d={dataPath} fill="none" stroke={primaryColor} strokeWidth={1.8} strokeLinejoin="round" />
      {/* Data vertices */}
      {dataPoints.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={3.2} fill={primaryColor} stroke="#fff" strokeWidth={1.5} />
      ))}
      {/* Score-level numbers on one axis (top spoke) */}
      {!compact && gridLevels.map(lvl => {
        const a = angleFor(0);
        const r = scoreToR(lvl);
        const x = cx + Math.cos(a) * r + 6;
        const y = cy + Math.sin(a) * r + 3;
        return <text key={lvl} x={x} y={y} fontSize={9} fill="#9CA3AF" fontFamily="Inter, sans-serif">{lvl}</text>;
      })}
      {/* Axis labels + score values */}
      {axes.map(({ k, l }, i) => {
        const a = angleFor(i);
        const lx = cx + Math.cos(a) * labelR;
        const ly = cy + Math.sin(a) * labelR;
        const anchor = Math.abs(Math.cos(a)) < 0.3 ? "middle" : (Math.cos(a) > 0 ? "start" : "end");
        const s = Number(scores?.[k]) || 0;
        return (
          <g key={k}>
            <text x={lx} y={ly} fontSize={11} fontWeight={600} fill="#111" fontFamily="Inter, sans-serif" textAnchor={anchor} letterSpacing="0.06em" style={{ textTransform: "uppercase" }}>{l}</text>
            <text x={lx} y={ly + 14} fontSize={13} fontWeight={800} fill={accentColor} fontFamily="Inter, sans-serif" textAnchor={anchor} letterSpacing="-0.01em">{s.toFixed(2)}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ═════════════════════════ EASY ROUTES ═════════════════════════
   Route planner for the Ready bucket.
    - First run: prompt the user for their facility (depot) address.
    - Build a suggested delivery order from departure → orders → arrival.
    - Use lat/lng if any are present (greedy nearest-neighbor + Haversine
      total miles); fall back to grouping by city / zip when geocoded
      data isn't available.
    - Allow adding alternate departure/arrival locations.
   The backend integration with the EasyRoutes Shopify app stays a future
   step; today this is the in-app planner that decides the best order.
   ═══════════════════════════════════════════════════════════════ */

// Haversine distance in miles between two {lat, lng} pairs.
function haversineMiles(a, b) {
  if (a == null || b == null || a.lat == null || a.lng == null || b.lat == null || b.lng == null) return null;
  const R = 3958.8;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
}

/* Total mileage for a given stop sequence (departure → stops → arrival). */
function routeTotalMiles(departure, stops, arrival) {
  if (!departure || stops.length === 0) return 0;
  let total = 0;
  let prev = { lat: departure.lat, lng: departure.lng };
  for (const s of stops) {
    const d = haversineMiles(prev, s); if (d != null) total += d;
    prev = { lat: s.lat, lng: s.lng };
  }
  if (arrival && arrival.lat != null && arrival.lng != null) {
    const d = haversineMiles(prev, { lat: arrival.lat, lng: arrival.lng }); if (d != null) total += d;
  }
  return total;
}

/* Greedy nearest-neighbor ordering + 2-opt polish.
   Greedy: from the current point, pick the closest unvisited stop, repeat.
   2-opt: repeatedly flip any segment [i..j] that would shorten the route.
   Works well for <50 stops (typical delivery routes) with no external API. */
function optimizeRouteOrder(departure, stops, arrival) {
  if (!departure || stops.length < 2) return stops;
  if (!stops.every(s => s.lat != null && s.lng != null)) return stops;

  // 1) Greedy nearest-neighbor initial pass
  const remaining = [...stops];
  const ordered = [];
  let current = { lat: departure.lat, lng: departure.lng };
  while (remaining.length > 0) {
    let bestIdx = 0, bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const d = haversineMiles(current, remaining[i]);
      if (d != null && d < bestDist) { bestDist = d; bestIdx = i; }
    }
    current = remaining[bestIdx];
    ordered.push(remaining.splice(bestIdx, 1)[0]);
  }

  // 2) 2-opt refinement: pad with the fixed endpoints so swap math covers
  // every leg including the depot-to-first and last-to-arrival hops.
  const endArrival = arrival && arrival.lat != null ? arrival : departure;
  const path = [{ lat: departure.lat, lng: departure.lng }, ...ordered, { lat: endArrival.lat, lng: endArrival.lng }];
  let improved = true, guard = 0;
  while (improved && guard++ < 50) {
    improved = false;
    for (let i = 1; i < path.length - 2; i++) {
      for (let j = i + 1; j < path.length - 1; j++) {
        const a = path[i - 1], b = path[i], c = path[j], d = path[j + 1];
        const before = (haversineMiles(a, b) || 0) + (haversineMiles(c, d) || 0);
        const after  = (haversineMiles(a, c) || 0) + (haversineMiles(b, d) || 0);
        if (after < before - 0.0005) {
          // Reverse the middle segment
          const rev = path.slice(i, j + 1).reverse();
          path.splice(i, j - i + 1, ...rev);
          improved = true;
        }
      }
    }
  }
  // Drop the depot/arrival anchors, map back to the ordered stops array
  return path.slice(1, -1).map(p => ordered.find(s => s.lat === p.lat && s.lng === p.lng)).filter(Boolean);
}

function LocationFormModal({ initial, onClose, onSave, title = "Add location", forceFacility = false }) {
  const [name, setName] = useState(initial?.name || "");
  const [address, setAddress] = useState(initial?.address || "");
  const [kind, setKind] = useState(forceFacility ? "facility" : (initial?.kind || "both"));
  const submit = (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return;
    const loc = {
      id: initial?.id || `loc_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name: name.trim(),
      address: address.trim(),
      lat: initial?.lat ?? null,
      lng: initial?.lng ?? null,
      kind,
    };
    onSave(loc);
  };
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(17, 24, 39, 0.45)", zIndex: 1000 }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        width: "min(520px, 92vw)", background: "#fff", borderRadius: 12, zIndex: 1001,
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", fontFamily: "Inter, sans-serif",
      }}>
        <div style={{ padding: "18px 24px 8px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#000000" }}>{title}</div>
            {forceFacility && (
              <div style={{ fontSize: 13, color: "#6B7280", marginTop: 4 }}>
                We'll start every route from your facility. You can change it any time.
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4, marginTop: -2 }}
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} style={{ padding: "12px 24px 20px" }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 12 }}>Name</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={forceFacility ? "Roastery" : "e.g. East Hub, San Jose Pickup"}
            autoFocus
            style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", marginTop: 4, fontSize: 15, fontFamily: "Inter, sans-serif", boxSizing: "border-box" }}
          />
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 14 }}>Address</label>
          <input
            value={address}
            onChange={e => setAddress(e.target.value)}
            placeholder="123 Main St, San Francisco, CA 94110"
            style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", marginTop: 4, fontSize: 15, fontFamily: "Inter, sans-serif", boxSizing: "border-box" }}
          />
          {!forceFacility && (
            <>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 14 }}>Use as</label>
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                {[
                  { v: "both", l: "Departure or arrival" },
                  { v: "departure", l: "Departure only" },
                  { v: "arrival", l: "Arrival only" },
                ].map(o => (
                  <button key={o.v} type="button" onClick={() => setKind(o.v)} style={{
                    padding: "6px 12px", borderRadius: 8,
                    border: `1px solid ${kind === o.v ? "#000000" : "#E5E7EB"}`,
                    background: kind === o.v ? "#000000" : "#fff",
                    color: kind === o.v ? "#fff" : "#374151",
                    fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                  }}>{o.l}</button>
                ))}
              </div>
            </>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 22 }}>
            {!forceFacility && (
              <button type="button" onClick={onClose} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
            )}
            <button type="submit" disabled={!name.trim()} style={{
              padding: "8px 16px", borderRadius: 8, border: "1px solid #000000",
              background: name.trim() ? "#000000" : "#9CA3AF",
              color: "#fff", fontSize: 14, fontWeight: 500, cursor: name.trim() ? "pointer" : "not-allowed",
              fontFamily: "Inter, sans-serif",
            }}>{initial ? "Save changes" : "Save location"}</button>
          </div>
        </form>
      </div>
    </>
  );
}

/* Grayscale Leaflet map showing departure → stops → arrival.
   If no location has coordinates, renders a hint instead of an empty map. */
function RouteMap({ departure, arrival, stops, geocodeStatus }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef([]);
  const [ready, setReady] = useState(typeof window !== "undefined" && !!window.L);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.L) { setReady(true); return; }
    const t = setInterval(() => {
      if (window.L) { setReady(true); clearInterval(t); }
    }, 60);
    return () => clearInterval(t);
  }, []);

  // Initialize the map once Leaflet is ready.
  useEffect(() => {
    if (!ready || !ref.current || mapRef.current) return;
    const L = window.L;
    const map = L.map(ref.current, { zoomControl: true, attributionControl: true, scrollWheelZoom: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap",
      maxZoom: 19,
    }).addTo(map);
    map.setView([37.773972, -122.431297], 11); // fallback center (SF)
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [ready]);

  // Redraw markers + polyline whenever the route inputs change.
  useEffect(() => {
    const L = typeof window !== "undefined" ? window.L : null;
    const map = mapRef.current;
    if (!L || !map) return;

    // Clear old layers
    for (const lyr of layersRef.current) map.removeLayer(lyr);
    layersRef.current = [];

    const pin = (label, cls) => L.divIcon({
      className: "",
      html: `<div class="pr-map-pin ${cls}">${label}</div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });

    const pts = [];
    if (departure && departure.lat != null && departure.lng != null) {
      const m = L.marker([departure.lat, departure.lng], { icon: pin("A", "start") })
        .bindPopup(`<strong>${escapeHtml(departure.name || "Depart")}</strong>${departure.address ? "<br>" + escapeHtml(departure.address) : ""}`)
        .addTo(map);
      layersRef.current.push(m);
      pts.push([departure.lat, departure.lng]);
    }
    stops.forEach((s, i) => {
      if (s.lat == null || s.lng == null) return;
      const m = L.marker([s.lat, s.lng], { icon: pin(String(i + 1), "stop") })
        .bindPopup(`<strong>${escapeHtml(s.orderId || "")}</strong> — ${escapeHtml(s.customer || "")}${s.address ? "<br>" + escapeHtml(s.address) : ""}`)
        .addTo(map);
      layersRef.current.push(m);
      pts.push([s.lat, s.lng]);
    });
    if (arrival && arrival.lat != null && arrival.lng != null) {
      const same = departure && arrival.lat === departure.lat && arrival.lng === departure.lng;
      if (!same) {
        const m = L.marker([arrival.lat, arrival.lng], { icon: pin("B", "end") })
          .bindPopup(`<strong>${escapeHtml(arrival.name || "Arrive")}</strong>${arrival.address ? "<br>" + escapeHtml(arrival.address) : ""}`)
          .addTo(map);
        layersRef.current.push(m);
      }
      pts.push([arrival.lat, arrival.lng]);
    }

    if (pts.length >= 2) {
      const line = L.polyline(pts, { color: "#374151", weight: 3, opacity: 0.85, dashArray: "6, 6" }).addTo(map);
      layersRef.current.push(line);
    }

    if (pts.length > 0) {
      if (pts.length === 1) map.setView(pts[0], 12);
      else map.fitBounds(pts, { padding: [28, 28], maxZoom: 14 });
    }
  }, [departure, arrival, stops]);

  const hasAny = (departure && departure.lat != null) ||
    (arrival && arrival.lat != null) ||
    stops.some(s => s.lat != null && s.lng != null);

  if (!hasAny) {
    const geocoding = geocodeStatus === "geocoding";
    return (
      <div style={{
        height: 260, borderRadius: 10, border: "1px dashed #E5E7EB", background: "#F9FAFB",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20, textAlign: "center",
      }}>
        <div>
          <MapPin size={18} strokeWidth={1.6} color="#9CA3AF" style={{ marginBottom: 6 }} />
          <div style={{ fontSize: 13, color: "#6B7280", maxWidth: 340 }}>
            {geocoding
              ? "Looking up addresses… the map will appear once coordinates resolve."
              : "Couldn't place these addresses on the map. Check the spelling on your facility and customer addresses."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className="pr-map"
      style={{ height: 320, borderRadius: 10, overflow: "hidden", border: "1px solid #E5E7EB" }}
    />
  );
}

function escapeHtml(s) {
  return String(s == null ? "" : s).replace(/[&<>"']/g, x => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[x]));
}

/* ═════════════════════════════════════════════════════════════════
   DRIVER ROUTE MAP — status-aware variant of RouteMap for the detail
   modal. Pins are colored by stop.status:
     done    = delivered (green)
     skip    = skipped   (amber)
     next    = first pending stop (cyan, pulsing)
     pending = subsequent pending stops (gray)
   The polyline is split: completed legs render solid green, active
   leg (last completed → next) pulses cyan, remaining legs dash gray.
   Same Leaflet bootstrap as RouteMap — reuses the injected stylesheet.
   ═════════════════════════════════════════════════════════════════ */
function DriverRouteMap({ departure, arrival, stops, geocoding = false }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef([]);
  const [ready, setReady] = useState(typeof window !== "undefined" && !!window.L);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.L) { setReady(true); return; }
    const t = setInterval(() => { if (window.L) { setReady(true); clearInterval(t); } }, 60);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!ready || !ref.current || mapRef.current) return;
    const L = window.L;
    const map = L.map(ref.current, { zoomControl: true, attributionControl: true, scrollWheelZoom: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap", maxZoom: 19,
    }).addTo(map);
    map.setView([37.773972, -122.431297], 11);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, [ready]);

  useEffect(() => {
    const L = typeof window !== "undefined" ? window.L : null;
    const map = mapRef.current;
    if (!L || !map) return;

    for (const lyr of layersRef.current) map.removeLayer(lyr);
    layersRef.current = [];

    const pin = (label, cls) => L.divIcon({
      className: "",
      html: `<div class="pr-map-pin ${cls}">${label}</div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });

    /* Find the first pending stop — that's the "next" marker. */
    const nextIdx = stops.findIndex(s => s.status === "pending" || !s.status);
    /* Build a typed sequence of points so we can color each polyline
       segment by the status of the stops that bookend it. */
    const seq = [];
    if (departure && departure.lat != null && departure.lng != null) {
      seq.push({ lat: departure.lat, lng: departure.lng, kind: "depot", label: "A", classN: "start", name: departure.name || "Depart", addr: departure.address });
    }
    stops.forEach((s, i) => {
      if (s.lat == null || s.lng == null) return;
      let cls;
      if (s.status === "delivered") cls = "done";
      else if (s.status === "skipped") cls = "skip";
      else if (i === nextIdx) cls = "next";
      else cls = "pending";
      seq.push({
        lat: s.lat, lng: s.lng, kind: s.status || "pending",
        label: cls === "done" ? "✓" : cls === "skip" ? "✕" : String(i + 1),
        classN: cls,
        name: s.customer || "", addr: s.address || "", order: s.orderId || "",
      });
    });
    if (arrival && arrival.lat != null && arrival.lng != null) {
      const sameAsDep = departure && arrival.lat === departure.lat && arrival.lng === departure.lng;
      if (!sameAsDep) {
        seq.push({ lat: arrival.lat, lng: arrival.lng, kind: "depot", label: "B", classN: "end", name: arrival.name || "Arrive", addr: arrival.address });
      }
    }

    /* Drop markers. */
    for (const p of seq) {
      const popup = p.order
        ? `<strong>${escapeHtml(p.order)}</strong> — ${escapeHtml(p.name)}${p.addr ? "<br>" + escapeHtml(p.addr) : ""}`
        : `<strong>${escapeHtml(p.name)}</strong>${p.addr ? "<br>" + escapeHtml(p.addr) : ""}`;
      const m = L.marker([p.lat, p.lng], { icon: pin(p.label, p.classN) }).bindPopup(popup).addTo(map);
      layersRef.current.push(m);
    }

    /* Draw polyline segments. Color each segment by the later endpoint:
         to delivered/skipped → solid green (completed history)
         to next pending     → solid cyan (active leg)
         to pending/arrival  → dashed gray (remaining)
       Keeps the whole route legible at a glance without a legend. */
    for (let i = 1; i < seq.length; i++) {
      const prev = seq[i - 1];
      const cur = seq[i];
      let color = "#9CA3AF", dash = "6, 6", weight = 3, opacity = 0.85;
      if (cur.classN === "done" || cur.classN === "skip") { color = "#10B981"; dash = null; opacity = 0.9; }
      else if (cur.classN === "next") { color = "#22D3EE"; dash = null; weight = 4; opacity = 0.95; }
      const line = L.polyline([[prev.lat, prev.lng], [cur.lat, cur.lng]], {
        color, weight, opacity, dashArray: dash,
      }).addTo(map);
      layersRef.current.push(line);
    }

    const pts = seq.map(p => [p.lat, p.lng]);
    if (pts.length === 1) map.setView(pts[0], 12);
    else if (pts.length > 1) map.fitBounds(pts, { padding: [28, 28], maxZoom: 14 });
  }, [departure, arrival, stops]);

  const hasAny = (departure && departure.lat != null) ||
    (arrival && arrival.lat != null) ||
    stops.some(s => s.lat != null && s.lng != null);

  if (!hasAny) {
    return (
      <div style={{
        height: 240, borderRadius: 10, border: "1px dashed #E5E7EB", background: "#F9FAFB",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16, textAlign: "center",
      }}>
        <div style={{ fontSize: 13, color: "#6B7280" }}>
          {geocoding ? "Locating stops…" : "Map unavailable — no coordinates on stops."}
        </div>
      </div>
    );
  }
  return (
    <div
      ref={ref}
      className="pr-map"
      style={{ height: 260, borderRadius: 10, overflow: "hidden", border: "1px solid #E5E7EB" }}
    />
  );
}

/* ═════════════════════════════════════════════════════════════════
   DRIVER ROUTE DETAIL MODAL — click an "Out with drivers" card to see
   every stop's status, delivery time, proof photo, signature, and
   notes. Polls the server while open so the admin can watch progress
   live. Driver URL + End route still reachable from the header.
   ═════════════════════════════════════════════════════════════════ */
function DriverRouteDetailModal({ route: initialRoute, onClose, onEnd }) {
  const [route, setRoute] = useState(initialRoute);
  const [zoomPhotoUrl, setZoomPhotoUrl] = useState(null);
  const [geocoding, setGeocoding] = useState(false);

  /* On open: if stops are missing coords, trigger server-side geocoding
     then wait for the poll to pick up the updated coordinates. */
  useEffect(() => {
    const needsGeocode = (initialRoute.stops || []).some(s => s.lat == null && s.lng == null);
    if (!needsGeocode) return;
    setGeocoding(true);
    fetch(`/api/driver-routes/${encodeURIComponent(initialRoute.id)}/geocode`, { method: "POST" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.route) setRoute(d.route); })
      .catch(() => {})
      .finally(() => setGeocoding(false));
  }, [initialRoute.id]);  // eslint-disable-line

  /* Poll for fresh stop statuses every 4s while the modal is open. */
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`/api/driver-routes/${encodeURIComponent(initialRoute.id)}`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled && d.route) setRoute(d.route);
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(t); };
  }, [initialRoute.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { zoomPhotoUrl ? setZoomPhotoUrl(null) : onClose(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, zoomPhotoUrl]);

  const driverUrl = typeof window !== "undefined" ? `${window.location.origin}/drive/${route.token}` : "";
  const stops = route.stops || [];
  const total = stops.length;
  const delivered = stops.filter(s => s.status === "delivered").length;
  const skipped = stops.filter(s => s.status === "skipped").length;
  const pending = total - delivered - skipped;
  const pct = total > 0 ? Math.round(((delivered + skipped) / total) * 100) : 0;
  const fmtTime = (ts) => ts ? new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : null;
  const statusStyle = (s) => s === "delivered" ? { bg: "#ECFDF5", fg: "#065F46", bd: "#A7F3D0" }
    : s === "skipped"   ? { bg: "#FFFBEB", fg: "#92400E", bd: "#FDE68A" }
    : { bg: "#F3F4F6", fg: "#6B7280", bd: "#E5E7EB" };

  return (
    <>
      {/* Full-bleed backdrop. Click to close. */}
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.45)", zIndex: 1000 }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        width: "min(840px, 95vw)", maxHeight: "92vh", display: "flex", flexDirection: "column",
        background: "#fff", borderRadius: 14, zIndex: 1001,
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", fontFamily: "Inter, sans-serif",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ padding: "18px 24px 14px", borderBottom: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#000" }}>{route.name || "Delivery route"}</div>
            <div style={{ fontSize: 13, color: "#6B7280", marginTop: 3 }}>
              {route.driverName ? `${route.driverName} · ` : ""}
              Started {route.createdAt ? new Date(route.createdAt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
            </div>
            <div style={{ display: "flex", gap: 14, marginTop: 10, fontSize: 12, color: "#374151", flexWrap: "wrap", fontVariantNumeric: "tabular-nums" }}>
              <span><strong style={{ color: "#000" }}>{delivered}</strong> delivered</span>
              <span>·</span>
              <span><strong style={{ color: "#000" }}>{skipped}</strong> skipped</span>
              <span>·</span>
              <span><strong style={{ color: "#000" }}>{pending}</strong> to go</span>
              <span>·</span>
              <span style={{ color: pct === 100 ? "#16A34A" : "#2563EB", fontWeight: 700 }}>{pct}%</span>
            </div>
            <div style={{ height: 4, background: "#F3F4F6", borderRadius: 99, marginTop: 8, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? "#16A34A" : "#2563EB", transition: "width 0.3s" }} />
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4 }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Map + legend — status-colored markers + split polylines so
            the admin can see progress at a glance. Sits above the
            scrollable stop list inside the modal body. */}
        {stops.length > 0 && (
          <div style={{ padding: "12px 24px 6px" }}>
            {geocoding && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#6B7280", marginBottom: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#22D3EE", animation: "pulse 1.4s ease-in-out infinite" }} />
                Locating stops on map…
              </div>
            )}
            <DriverRouteMap departure={route.departure} arrival={route.arrival} stops={stops} geocoding={geocoding} />
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8, fontSize: 11, color: "#6B7280" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 99, background: "#10B981" }} />Delivered</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 99, background: "#F59E0B" }} />Skipped</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 99, background: "#22D3EE", boxShadow: "0 0 6px rgba(34,211,238,0.5)" }} />Next stop</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 99, background: "#9CA3AF" }} />Pending</span>
            </div>
          </div>
        )}

        {/* Stop list — scrollable, one row per stop with proof. */}
        <div style={{ flex: 1, overflowY: "auto", padding: "6px 0" }}>
          {stops.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>
              No stops on this route.
            </div>
          ) : (
            stops.map((s, i) => {
              const ss = statusStyle(s.status);
              const deliveredAt = s.deliveredAt ? fmtTime(s.deliveredAt) : null;
              return (
                <div key={s.key || i} style={{ padding: "14px 24px", borderBottom: i === stops.length - 1 ? "none" : "1px solid #F3F4F6", display: "flex", gap: 14, alignItems: "flex-start" }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 14, flexShrink: 0,
                    background: s.status === "delivered" ? "#10B981" : s.status === "skipped" ? "#F59E0B" : "#0A0A0A",
                    color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                  }}>
                    {s.status === "delivered" ? <Check size={14} strokeWidth={3} /> : s.status === "skipped" ? <X size={14} strokeWidth={3} /> : (i + 1)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "#000" }}>{s.customer || "—"}</span>
                      {s.orderId && <span style={{ fontSize: 12, color: "#9CA3AF", fontFamily: "ui-monospace, Menlo, monospace" }}>{s.orderId}</span>}
                      <span style={{
                        fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
                        padding: "2px 8px", borderRadius: 99,
                        background: ss.bg, color: ss.fg, border: `1px solid ${ss.bd}`,
                      }}>{s.status || "pending"}</span>
                      {deliveredAt && <span style={{ fontSize: 12, color: "#6B7280" }}>· {deliveredAt}</span>}
                    </div>
                    <div style={{ fontSize: 13, color: "#6B7280", marginTop: 3, lineHeight: 1.5 }}>
                      {s.address || "—"}
                    </div>
                    {s.notes && (
                      <div style={{ fontSize: 12, color: "#374151", marginTop: 6, padding: "6px 10px", background: "#F9FAFB", borderRadius: 6, border: "1px solid #F3F4F6" }}>
                        {s.notes}
                      </div>
                    )}
                    {(s.photoUrl || s.signatureDataUrl) && (
                      <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                        {s.photoUrl && (
                          <button
                            onClick={() => setZoomPhotoUrl(s.photoUrl)}
                            style={{ padding: 0, border: "1px solid #E5E7EB", borderRadius: 8, overflow: "hidden", cursor: "pointer", background: "#fff" }}
                            title="Expand photo"
                          >
                            <img src={s.photoUrl} alt="Delivery proof"
                              style={{ display: "block", width: 96, height: 96, objectFit: "cover" }} />
                          </button>
                        )}
                        {s.signatureDataUrl && (
                          <div style={{ width: 180, height: 96, border: "1px solid #E5E7EB", borderRadius: 8, background: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 4, overflow: "hidden" }}>
                            <img src={s.signatureDataUrl} alt="Customer signature"
                              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer actions */}
        <div style={{ padding: "12px 24px", borderTop: "1px solid #F3F4F6", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="text"
            value={driverUrl}
            readOnly
            onFocus={e => e.target.select()}
            style={{ flex: 1, minWidth: 180, padding: "8px 10px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12, fontFamily: "ui-monospace, Menlo, monospace", color: "#374151", background: "#F9FAFB" }}
          />
          <button
            onClick={() => navigator.clipboard?.writeText(driverUrl)}
            style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
          >Copy link</button>
          <a href={driverUrl} target="_blank" rel="noopener noreferrer"
            style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 500, textDecoration: "none", fontFamily: "Inter, sans-serif" }}
          >Open as driver</a>
          {!route.endedAt && (
            <button
              onClick={onEnd}
              style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#DC2626", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
            >End route</button>
          )}
        </div>
      </div>

      {/* Photo zoom — click a proof thumbnail to see full-size. */}
      {zoomPhotoUrl && (
        <>
          <div onClick={() => setZoomPhotoUrl(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 1002 }} />
          <img src={zoomPhotoUrl} alt="Delivery proof"
            onClick={() => setZoomPhotoUrl(null)}
            style={{
              position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
              maxWidth: "92vw", maxHeight: "92vh", zIndex: 1003, borderRadius: 8,
              boxShadow: "0 20px 60px rgba(0,0,0,0.6)", cursor: "zoom-out",
            }}
          />
        </>
      )}
    </>
  );
}

function EasyRoutesView({
  readyOrders, locations, setLocations, facilityLocation,
  departureId, setDepartureId, arrivalId, setArrivalId,
  setupOpen, setSetupOpen, locationModal, setLocationModal,
  routeStopKeys, setRouteStopKeys,
}) {
  // Native HTML5 drag state for reordering route stops.
  const [draggedKey, setDraggedKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  // Driver dispatch modal + active dispatched routes list
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [activeRoutes, setActiveRoutes] = useState([]);
  /* Per-route detail modal — click any "Out with drivers" card to
     open a full breakdown (stops, statuses, photos, signatures). */
  const [viewingRouteId, setViewingRouteId] = useState(null);
  const fetchActive = useCallback(async () => {
    try {
      const r = await fetch("/api/driver-routes");
      if (r.ok) { const d = await r.json(); setActiveRoutes(d.routes || []); }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    fetchActive();
    const t = setInterval(fetchActive, 15000);
    return () => clearInterval(t);
  }, [fetchActive]);
  const openActive = activeRoutes.filter(r => !r.endedAt && (!r.expiresAt || Date.now() < r.expiresAt));

  const dispatchToDriver = async ({ name, requirePhoto, requireSignature }) => {
    const payload = {
      name,
      requirePhoto: !!requirePhoto,
      requireSignature: !!requireSignature,
      stops: route.stops.map(s => ({
        key: s.key, orderId: s.orderId, customer: s.customer, address: s.address,
        city: s.city, zip: s.zip, lat: s.lat, lng: s.lng,
      })),
      departure: departure ? { name: departure.name, address: departure.address, lat: departure.lat, lng: departure.lng } : null,
      arrival: arrival ? { name: arrival.name, address: arrival.address, lat: arrival.lat, lng: arrival.lng } : null,
    };
    const r = await fetch("/api/driver-routes", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || "Dispatch failed"); }
    const { route: created } = await r.json();
    await fetchActive();
    return created;
  };

  const endDispatchedRoute = async (id) => {
    await fetch(`/api/driver-routes/${id}/end`, { method: "POST" });
    await fetchActive();
  };
  // Once the user dismisses the first-time facility prompt, don't reopen it.
  const [setupDismissed, setSetupDismissed] = useState(false);
  // First-time prompt: no facility set
  useEffect(() => {
    if (!facilityLocation && !setupOpen && !setupDismissed) setSetupOpen(true);
  }, [facilityLocation, setupOpen, setupDismissed, setSetupOpen]);

  // Departure/arrival default to the facility once it exists.
  useEffect(() => {
    if (!departureId && facilityLocation) setDepartureId(facilityLocation.id);
    if (!arrivalId && facilityLocation) setArrivalId(facilityLocation.id);
  }, [facilityLocation, departureId, arrivalId, setDepartureId, setArrivalId]);

  /* ── Address geocoder (OSM Nominatim, free, no API key) ────────
     Results cached in localStorage so we don't re-hit Nominatim for
     the same address twice. Queue is processed one address per 1.2s
     to respect Nominatim's usage policy. */
  const [geocodeCache, setGeocodeCache] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:geocodeCache");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:geocodeCache", JSON.stringify(geocodeCache)); } catch { /* ignore */ }
  }, [geocodeCache]);
  const geocodeKey = (addr) => String(addr || "").trim().toLowerCase();
  const [geocodeStatus, setGeocodeStatus] = useState("idle"); // idle | geocoding | done

  const departureOptions = locations.filter(l => l.kind === "facility" || l.kind === "departure" || l.kind === "both");
  const arrivalOptions = locations.filter(l => l.kind === "facility" || l.kind === "arrival" || l.kind === "both");
  const departureRaw = locations.find(l => l.id === departureId) || facilityLocation;
  const arrivalRaw = locations.find(l => l.id === arrivalId) || facilityLocation;

  // Fill in coords from the geocode cache when a point has an address
  // but no lat/lng yet. Returns null if nothing resolves.
  const resolveCoords = (address, lat, lng) => {
    if (lat != null && lng != null) return { lat: Number(lat), lng: Number(lng) };
    if (!address) return { lat: null, lng: null };
    const hit = geocodeCache[geocodeKey(address)];
    if (hit && hit.lat != null) return { lat: hit.lat, lng: hit.lng };
    return { lat: null, lng: null };
  };
  const departure = departureRaw
    ? { ...departureRaw, ...resolveCoords(departureRaw.address, departureRaw.lat, departureRaw.lng) }
    : null;
  const arrival = arrivalRaw
    ? { ...arrivalRaw, ...resolveCoords(arrivalRaw.address, arrivalRaw.lat, arrivalRaw.lng) }
    : null;

  // Pull a usable shipping address out of the saved order snapshot and
  // enrich with geocoded coordinates when available.
  const orderToStop = (o) => {
    const a = o.shippingAddress || o.shipping_address || o.addressInfo || {};
    const address = [a.address1, a.city, a.province_code || a.province, a.zip].filter(Boolean).join(", ");
    const rawLat = a.latitude != null ? Number(a.latitude) : null;
    const rawLng = a.longitude != null ? Number(a.longitude) : null;
    const resolved = resolveCoords(address, rawLat, rawLng);
    return {
      key: o.shopifyId || o.id,
      orderId: o.id,
      customer: o.customer || a.name || "Order",
      address,
      city: a.city || "",
      zip: a.zip || "",
      lat: resolved.lat,
      lng: resolved.lng,
    };
  };

  // Split ready orders into two groups by what the user has explicitly
  // added to the route. `inRoute` preserves the user's chosen order,
  // `available` is what's still sitting in the ready bucket waiting to
  // be added. Orphaned keys (order no longer ready) are filtered out.
  const { inRoute, available } = useMemo(() => {
    const stopByKey = new Map();
    for (const o of readyOrders) {
      const s = orderToStop(o);
      stopByKey.set(s.key, s);
    }
    const inRouteList = routeStopKeys
      .map(k => stopByKey.get(k))
      .filter(Boolean);
    const inRouteSet = new Set(inRouteList.map(s => s.key));
    const availableList = readyOrders
      .map(orderToStop)
      .filter(s => !inRouteSet.has(s.key));
    return { inRoute: inRouteList, available: availableList };
  }, [readyOrders, routeStopKeys]);

  // Leg distances + total miles follow the user-chosen order. Only
  // computed when every stop (and the endpoints) have coordinates.
  const route = useMemo(() => {
    if (inRoute.length === 0 || !departure) return { stops: inRoute.map(s => ({ ...s, leg: null })), totalMiles: null, geoCoded: false };
    const endpointsGeo = departure.lat != null && departure.lng != null && (!arrival || (arrival.lat != null && arrival.lng != null));
    const allGeo = endpointsGeo && inRoute.every(s => s.lat != null && s.lng != null);
    if (!allGeo) return { stops: inRoute.map(s => ({ ...s, leg: null })), totalMiles: null, geoCoded: false };
    let total = 0;
    let prev = { lat: departure.lat, lng: departure.lng };
    const stops = inRoute.map(s => {
      const d = haversineMiles(prev, s) || 0;
      total += d;
      prev = { lat: s.lat, lng: s.lng };
      return { ...s, leg: d };
    });
    if (arrival && arrival.lat != null && arrival.lng != null) {
      total += haversineMiles(prev, { lat: arrival.lat, lng: arrival.lng }) || 0;
    }
    return { stops, totalMiles: total, geoCoded: true };
  }, [inRoute, departure, arrival]);

  // Queue up addresses that need geocoding and resolve them one-by-one.
  // Nominatim asks for at most 1 request per second — we use 1.2s to leave
  // a margin. Cache is persisted so we only hit the service once per address.
  useEffect(() => {
    const pending = new Set();
    const maybeQueue = (address) => {
      if (!address) return;
      const k = geocodeKey(address);
      if (geocodeCache[k]) return;
      pending.add(address);
    };
    if (departureRaw && (departureRaw.lat == null || departureRaw.lng == null)) maybeQueue(departureRaw.address);
    if (arrivalRaw && (arrivalRaw.lat == null || arrivalRaw.lng == null)) maybeQueue(arrivalRaw.address);
    for (const o of readyOrders) {
      const a = o.shippingAddress || o.shipping_address || o.addressInfo || {};
      if (a.latitude != null && a.longitude != null) continue;
      const addr = [a.address1, a.city, a.province_code || a.province, a.zip].filter(Boolean).join(", ");
      maybeQueue(addr);
    }
    if (pending.size === 0) { setGeocodeStatus("done"); return; }
    setGeocodeStatus("geocoding");

    let cancelled = false;
    const list = Array.from(pending);
    (async () => {
      for (const addr of list) {
        if (cancelled) return;
        try {
          const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(addr)}`;
          const r = await fetch(url, { headers: { "Accept-Language": "en" } });
          if (r.ok) {
            const d = await r.json();
            const hit = d && d[0];
            const result = hit
              ? { lat: parseFloat(hit.lat), lng: parseFloat(hit.lon), resolvedAt: Date.now() }
              : { lat: null, lng: null, resolvedAt: Date.now(), miss: true };
            if (!cancelled) {
              setGeocodeCache(prev => ({ ...prev, [geocodeKey(addr)]: result }));
            }
          }
        } catch (err) {
          // Network error — leave the cache unchanged so we retry on the next
          // render. No toast spam; the empty-state copy explains the fallback.
          console.warn("Geocode failed for", addr, err);
        }
        // Nominatim rate limit
        await new Promise(r => setTimeout(r, 1200));
      }
      if (!cancelled) setGeocodeStatus("done");
    })();
    return () => { cancelled = true; };
  }, [departureRaw, arrivalRaw, readyOrders, geocodeCache]);

  const addToRoute = (key) => setRouteStopKeys(prev => prev.includes(key) ? prev : [...prev, key]);
  const removeFromRoute = (key) => setRouteStopKeys(prev => prev.filter(k => k !== key));
  const clearRoute = () => setRouteStopKeys([]);

  /* How much shorter would the route be if we re-ordered it? We compute
     the optimized sequence and compare to the current one. If the user
     has already hand-tuned their route to the optimal path the button
     renders disabled-looking (0.0 mi saved). */
  const optimizedPreview = useMemo(() => {
    if (inRoute.length < 2 || !departure || !route.geoCoded) return null;
    const optimized = optimizeRouteOrder(departure, inRoute, arrival);
    const optMiles = routeTotalMiles(departure, optimized, arrival);
    const currentMiles = route.totalMiles || 0;
    const savings = Math.max(0, currentMiles - optMiles);
    const keysSame = optimized.length === inRoute.length &&
      optimized.every((s, i) => s.key === inRoute[i].key);
    return { optimized, optMiles, savings, keysSame };
  }, [inRoute, departure, arrival, route.geoCoded, route.totalMiles]);

  const optimizeRoute = () => {
    if (!optimizedPreview || optimizedPreview.keysSame) return;
    setRouteStopKeys(optimizedPreview.optimized.map(s => s.key));
  };

  /* Auto-fill the route with every available ready order on land —
     when there's nothing routed yet but ready orders exist, drop them
     all in. Past that, the user is in control: removing an order from
     the route doesn't get re-added on the next render. */
  const autoFilledRef = useRef(false);
  useEffect(() => {
    if (autoFilledRef.current) return;
    if (routeStopKeys.length > 0) { autoFilledRef.current = true; return; }
    if ((readyOrders || []).length === 0) return;
    const allKeys = readyOrders.map(orderToStop).map(s => s.key);
    if (allKeys.length === 0) return;
    setRouteStopKeys(allKeys);
    autoFilledRef.current = true;
  }, [readyOrders, routeStopKeys.length]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Auto-optimize when stops are added to an already-optimal route so the
     order stays tight without the user having to click after each add.
     We compare by key sets: only re-run if the set of stops changed since
     last optimize. Manual drag-reorders skip this by putting an entry in
     a ref we check against. */
  const lastOptimizedSetRef = useRef("");
  useEffect(() => {
    if (!optimizedPreview || optimizedPreview.keysSame) return;
    const setKey = [...inRoute.map(s => s.key)].sort().join("|");
    // Only auto-optimize if the SET of stops changed (not just the order)
    if (setKey === lastOptimizedSetRef.current) return;
    lastOptimizedSetRef.current = setKey;
    setRouteStopKeys(optimizedPreview.optimized.map(s => s.key));
  }, [optimizedPreview, inRoute]); // eslint-disable-line react-hooks/exhaustive-deps
  const reorderRoute = (draggedK, targetK) => {
    if (!draggedK || draggedK === targetK) return;
    setRouteStopKeys(prev => {
      const list = prev.filter(k => k !== draggedK);
      const idx = list.indexOf(targetK);
      if (idx < 0) return [...list, draggedK];
      list.splice(idx, 0, draggedK);
      return list;
    });
  };

  const saveLocation = (loc, options = {}) => {
    setLocations(prev => {
      const exists = prev.some(p => p.id === loc.id);
      // First location is always a facility — make sure exactly one facility exists
      let next = exists ? prev.map(p => (p.id === loc.id ? loc : p)) : [...prev, loc];
      if (loc.kind === "facility") {
        next = next.map(p => (p.id === loc.id ? p : (p.kind === "facility" ? { ...p, kind: "both" } : p)));
      }
      return next;
    });
    if (options.setAsDeparture) setDepartureId(loc.id);
    if (options.setAsArrival) setArrivalId(loc.id);
    setLocationModal(null);
    setSetupOpen(false);
  };

  const removeLocation = (id) => {
    setLocations(prev => prev.filter(l => l.id !== id));
    if (departureId === id) setDepartureId(facilityLocation?.id || null);
    if (arrivalId === id) setArrivalId(facilityLocation?.id || null);
  };

  return (
    <>
      {setupOpen && !facilityLocation && (
        <LocationFormModal
          forceFacility
          title="Set your facility"
          onClose={() => { setSetupOpen(false); setSetupDismissed(true); }}
          onSave={(loc) => saveLocation({ ...loc, kind: "facility" }, { setAsDeparture: true, setAsArrival: true })}
        />
      )}
      {locationModal === "new" && (
        <LocationFormModal
          title="Add location"
          onClose={() => setLocationModal(null)}
          onSave={(loc) => saveLocation(loc)}
        />
      )}
      {locationModal && locationModal !== "new" && (
        <LocationFormModal
          title="Edit location"
          initial={locationModal}
          forceFacility={locationModal.kind === "facility"}
          onClose={() => setLocationModal(null)}
          onSave={(loc) => saveLocation(loc)}
        />
      )}

      <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: "#000000", margin: 0, lineHeight: "28px" }}>Routes</h1>
          <p style={{ fontSize: 16, color: "#6B7280", margin: "2px 0 0", lineHeight: "20px" }}>Plan today's deliveries from the Ready queue.</p>
        </div>
        <button onClick={() => setLocationModal("new")} style={{
          display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
          borderRadius: 8, border: "1px solid #000000", background: "#000000",
          color: "#fff", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
        }}>
          <Plus size={13} strokeWidth={1.8} /> Add location
        </button>
      </div>

      {/* Departure / arrival selector */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
        {[
          { l: "Departure", value: departureId, set: setDepartureId, opts: departureOptions },
          { l: "Arrival", value: arrivalId, set: setArrivalId, opts: arrivalOptions },
        ].map(s => (
          <div key={s.l} style={{ padding: "12px 14px", border: "1px solid #F3F4F6", borderRadius: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4 }}>{s.l}</div>
            {s.opts.length === 0 ? (
              <div style={{ fontSize: 14, color: "#9CA3AF", marginTop: 6 }}>No locations yet — add one to get started.</div>
            ) : (
              <select
                value={s.value || ""}
                onChange={e => s.set(e.target.value || null)}
                style={{ width: "100%", marginTop: 6, padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 15, fontFamily: "Inter, sans-serif", background: "#fff", color: "#000000", outline: "none", cursor: "pointer" }}
              >
                {s.opts.map(o => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            )}
          </div>
        ))}
      </div>

      {/* Per-route detail modal — renders when a card is clicked.
          Lives at the top level of the view so it floats above all
          the stops/map/picker. */}
      {viewingRouteId && (() => {
        const entry = activeRoutes.find(x => x.id === viewingRouteId);
        if (!entry) return null;
        return (
          <DriverRouteDetailModal
            route={entry}
            onClose={() => setViewingRouteId(null)}
            onEnd={async () => {
              if (!window.confirm("End this route? The driver URL will stop working.")) return;
              await endDispatchedRoute(entry.id);
              setViewingRouteId(null);
            }}
          />
        );
      })()}

      {/* ═══ Active deliveries (live driver status) ═══ */}
      {openActive.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#18181B", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Out with drivers
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
            {openActive.map(r => {
              const total = r.stops.length;
              const delivered = r.stops.filter(s => s.status === "delivered").length;
              const skipped = r.stops.filter(s => s.status === "skipped").length;
              const pct = total > 0 ? Math.round(((delivered + skipped) / total) * 100) : 0;
              const url = `${window.location.origin}/drive/${r.token}`;
              return (
                /* Card body is a button so the whole card is
                   clickable; the inline action buttons below
                   stopPropagation so they don't also open the modal. */
                <button
                  key={r.id}
                  onClick={() => setViewingRouteId(r.id)}
                  style={{
                    background: "#fff", border: "1px solid #F3F4F6", borderRadius: 12,
                    padding: "14px 16px", textAlign: "left", cursor: "pointer",
                    fontFamily: "Inter, sans-serif", width: "100%",
                    transition: "border-color 0.15s, box-shadow 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#000"; e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.04)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#F3F4F6"; e.currentTarget.style.boxShadow = "none"; }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#000" }}>{r.name}</div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 99, background: pct === 100 ? "#F0FDF4" : "#EFF6FF", color: pct === 100 ? "#16A34A" : "#2563EB", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      {pct}%
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 10 }}>
                    {delivered} delivered · {skipped} skipped · {total - delivered - skipped} to go
                  </div>
                  <div style={{ height: 5, background: "#F3F4F6", borderRadius: 3, overflow: "hidden", marginBottom: 12 }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? "#16A34A" : "#2563EB", transition: "width 0.3s" }} />
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <span
                      onClick={e => { e.stopPropagation(); navigator.clipboard?.writeText(url); }}
                      style={{ flex: 1, padding: "7px 10px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 12, fontWeight: 500, color: "#374151", cursor: "pointer", fontFamily: "Inter, sans-serif", textAlign: "center" }}
                    >Copy link</span>
                    <a href={url} target="_blank" rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      style={{ flex: 1, padding: "7px 10px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 12, fontWeight: 500, color: "#374151", textDecoration: "none", fontFamily: "Inter, sans-serif", textAlign: "center" }}
                    >Open as driver</a>
                    <span
                      onClick={e => { e.stopPropagation(); if (confirm("End this route? The driver URL will stop working.")) endDispatchedRoute(r.id); }}
                      style={{ padding: "7px 10px", borderRadius: 8, border: "1px solid #FCA5A5", background: "#FEF2F2", fontSize: 12, fontWeight: 500, color: "#DC2626", cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                    >End</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ Today's route ═══ */}
      {!facilityLocation ? (
        <div style={{ padding: "20px 24px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, color: "#92400E", fontSize: 14, marginBottom: 24 }}>
          Set your facility to start planning a route.
        </div>
      ) : inRoute.length === 0 ? (
        <div style={{ padding: "32px 20px", textAlign: "center", color: "#9CA3AF", fontSize: 15, border: "1px dashed #E5E7EB", borderRadius: 10, marginBottom: 32 }}>
          No stops in today's route yet. Add orders from the Ready list below.
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 20 }}>
            <RouteMap departure={departure} arrival={arrival} stops={route.stops} geocodeStatus={geocodeStatus} />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
              Today's route ({route.stops.length} stop{route.stops.length === 1 ? "" : "s"})
              {route.geoCoded && route.totalMiles != null && (
                <span style={{ marginLeft: 10, fontSize: 12, color: "#6B7280", fontWeight: 400 }}>
                  ~{route.totalMiles.toFixed(1)} mi total
                </span>
              )}
              {!route.geoCoded && (
                <span style={{ marginLeft: 10, fontSize: 12, color: "#9CA3AF", fontWeight: 400 }}>
                  {geocodeStatus === "geocoding" ? "resolving addresses…" : "drag to reorder"}
                </span>
              )}
            </div>
            {inRoute.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {optimizedPreview && (
                  <button
                    onClick={optimizeRoute}
                    disabled={optimizedPreview.keysSame}
                    title={optimizedPreview.keysSame
                      ? "Route is already the shortest path we could find."
                      : `Re-order stops to save about ${optimizedPreview.savings.toFixed(1)} mi`}
                    style={{
                      padding: "5px 10px", borderRadius: 6,
                      border: "1px solid #000",
                      background: optimizedPreview.keysSame ? "#F3F4F6" : "#000",
                      color: optimizedPreview.keysSame ? "#9CA3AF" : "#fff",
                      fontSize: 12, fontWeight: 600,
                      cursor: optimizedPreview.keysSame ? "default" : "pointer",
                      fontFamily: "Inter, sans-serif",
                      display: "inline-flex", alignItems: "center", gap: 5,
                    }}
                  >
                    <Navigation size={12} strokeWidth={2} />
                    {optimizedPreview.keysSame
                      ? "Route optimized"
                      : `Optimize (−${optimizedPreview.savings.toFixed(1)} mi)`}
                  </button>
                )}
                <button
                  onClick={() => setDispatchOpen(true)}
                  title="Generate a share link the driver opens on their phone"
                  style={{
                    padding: "5px 12px", borderRadius: 6, border: "1px solid #000",
                    background: "#000", color: "#fff",
                    fontSize: 12, fontWeight: 700, cursor: "pointer",
                    fontFamily: "Inter, sans-serif",
                    display: "inline-flex", alignItems: "center", gap: 5,
                  }}
                >
                  <Truck size={12} strokeWidth={2} /> Send to driver
                </button>
                <button onClick={clearRoute} style={{
                  padding: "5px 10px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff",
                  color: "#6B7280", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                }}>Clear route</button>
              </div>
            )}
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: 36 }} />
              <col style={{ width: 36 }} />
              <col style={{ width: 180 }} />
              <col style={{ width: 400 }} />
              <col />
              <col style={{ width: 70 }} />
              <col style={{ width: 44 }} />
            </colgroup>
            <thead>
              <tr>
                {["", "#", "Order", "Customer", "Address", route.geoCoded ? "Leg" : "", ""].map((h, i) => (
                  <th key={i} style={{ padding: "10px 14px", textAlign: i === 1 || i === 5 ? "right" : "left", fontSize: 13, fontWeight: 500, color: "#9CA3AF", borderBottom: "1px solid #F3F4F6" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr style={{ background: "#F9FAFB" }}>
                <td />
                <td style={{ padding: "10px 14px", textAlign: "right", fontSize: 13, color: "#9CA3AF" }}>—</td>
                <td colSpan={4} style={{ padding: "10px 14px", fontSize: 14, color: "#000000", fontWeight: 600 }}>
                  <Navigation size={12} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 6, color: "#374151" }} />
                  Depart from {departure?.name || "—"}{departure?.address ? ` · ${departure.address}` : ""}
                </td>
                <td />
              </tr>
              {route.stops.map((s, i) => {
                const isDragging = draggedKey === s.key;
                const isDragOver = dragOverKey === s.key && draggedKey !== s.key;
                return (
                  <tr key={s.key}
                    draggable
                    onDragStart={(e) => { setDraggedKey(s.key); e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", s.key); } catch { /* ignore */ } }}
                    onDragEnter={() => setDragOverKey(s.key)}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                    onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOverKey(k => (k === s.key ? null : k)); }}
                    onDrop={(e) => { e.preventDefault(); reorderRoute(draggedKey, s.key); setDraggedKey(null); setDragOverKey(null); }}
                    onDragEnd={() => { setDraggedKey(null); setDragOverKey(null); }}
                    style={{
                      borderBottom: "1px solid #F9FAFB",
                      opacity: isDragging ? 0.4 : 1,
                      background: isDragOver ? "#F3F4F6" : "transparent",
                      boxShadow: isDragOver ? "inset 0 2px 0 #000000" : "none",
                      transition: "background 0.12s",
                    }}
                  >
                    <td style={{ padding: "10px 6px 10px 14px", color: "#9CA3AF", cursor: "grab", userSelect: "none" }} title="Drag to reorder">
                      <GripVertical size={14} strokeWidth={1.8} />
                    </td>
                    <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 600, color: "#9CA3AF", textAlign: "right" }}>{i + 1}</td>
                    <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 600, color: "#000000", whiteSpace: "nowrap" }}>{s.orderId}</td>
                    <td style={{ padding: "12px 14px", fontSize: 14, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.customer}</td>
                    <td style={{ padding: "12px 14px", fontSize: 13, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.address}>{s.address || "—"}</td>
                    <td style={{ padding: "12px 14px", fontSize: 13, color: "#9CA3AF", textAlign: "right" }}>
                      {route.geoCoded && s.leg != null ? `${s.leg.toFixed(1)} mi` : "—"}
                    </td>
                    <td style={{ padding: "12px 10px 12px 4px", textAlign: "right" }}>
                      <button onClick={() => removeFromRoute(s.key)} title="Remove from route" style={{
                        background: "transparent", border: "none", padding: 6,
                        color: "#9CA3AF", cursor: "pointer", borderRadius: 6,
                      }}><X size={14} strokeWidth={1.8} /></button>
                    </td>
                  </tr>
                );
              })}
              <tr style={{ background: "#F9FAFB" }}>
                <td />
                <td style={{ padding: "10px 14px", textAlign: "right", fontSize: 13, color: "#9CA3AF" }}>—</td>
                <td colSpan={4} style={{ padding: "10px 14px", fontSize: 14, color: "#000000", fontWeight: 600 }}>
                  <Navigation size={12} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 6, color: "#6B7280", transform: "rotate(180deg)" }} />
                  Arrive at {arrival?.name || "—"}{arrival?.address ? ` · ${arrival.address}` : ""}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </>
      )}

      {/* ═══ Ready orders picker ═══ */}
      <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid #F3F4F6" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
            Ready orders
            <span style={{ marginLeft: 8, fontSize: 12, color: "#9CA3AF", fontWeight: 400 }}>
              {available.length} not in route{available.length === 0 && readyOrders.length > 0 ? " · all added" : ""}
            </span>
          </div>
          {available.length > 0 && (
            <button
              onClick={() => setRouteStopKeys(prev => {
                const have = new Set(prev);
                const additions = available.map(s => s.key).filter(k => !have.has(k));
                return [...prev, ...additions];
              })}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "6px 12px", borderRadius: 6,
                border: "1px solid #000000", background: "#000000",
                color: "#fff", fontSize: 13, fontWeight: 500,
                cursor: "pointer", fontFamily: "Inter, sans-serif",
              }}
            >
              <Plus size={11} strokeWidth={2} /> Add all ({available.length})
            </button>
          )}
        </div>
        {readyOrders.length === 0 ? (
          <div style={{ padding: "32px 20px", textAlign: "center", color: "#9CA3AF", fontSize: 15, border: "1px dashed #E5E7EB", borderRadius: 10 }}>
            Nothing ready yet. Mark scheduled orders as ready and they'll show up here.
          </div>
        ) : available.length === 0 ? (
          <div style={{ padding: "20px", textAlign: "center", color: "#9CA3AF", fontSize: 14, border: "1px dashed #E5E7EB", borderRadius: 10 }}>
            Every ready order is already in today's route.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: 180 }} />
              <col style={{ width: 400 }} />
              <col />
              <col style={{ width: 110 }} />
            </colgroup>
            <thead>
              <tr>
                {["Order", "Customer", "Address", ""].map((h, i) => (
                  <th key={i} style={{ padding: "10px 14px", textAlign: "left", fontSize: 13, fontWeight: 500, color: "#9CA3AF", borderBottom: "1px solid #F3F4F6" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {available.map(s => (
                <tr key={s.key} style={{ borderBottom: "1px solid #F9FAFB" }}>
                  <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 600, color: "#000000", whiteSpace: "nowrap" }}>{s.orderId}</td>
                  <td style={{ padding: "12px 14px", fontSize: 14, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.customer}</td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.address}>{s.address || "—"}</td>
                  <td style={{ padding: "12px 14px", textAlign: "right" }}>
                    <button onClick={() => addToRoute(s.key)} style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      padding: "5px 12px", borderRadius: 6, border: "1px solid #000000", background: "#000000",
                      color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                    }}>
                      <Plus size={11} strokeWidth={2} /> Add
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {dispatchOpen && (
        <DispatchDialog
          onClose={() => setDispatchOpen(false)}
          onDispatch={dispatchToDriver}
          stopCount={inRoute.length}
        />
      )}
    </>
  );
}

/* Dispatch-to-driver modal. Small form: optional route name, proof
   requirements. On submit we hand back the created route (with token)
   so the dialog can show the share URL + Copy button inline. */
function DispatchDialog({ onClose, onDispatch, stopCount }) {
  const [name, setName] = useState(`Route ${new Date().toLocaleDateString()}`);
  const [requirePhoto, setRequirePhoto] = useState(false);
  const [requireSignature, setRequireSignature] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const url = created ? `${window.location.origin}/drive/${created.token}` : "";
  const submit = async () => {
    setError(""); setSending(true);
    try { setCreated(await onDispatch({ name: name.trim(), requirePhoto, requireSignature })); }
    catch (e) { setError(e.message || "Dispatch failed"); }
    finally { setSending(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ } };
  const fieldStyle = { padding: "10px 12px", borderRadius: 10, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff", outline: "none", width: "100%", boxSizing: "border-box" };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "Inter, sans-serif" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 24px 64px rgba(0,0,0,0.24)" }}>
        <div style={{ padding: "24px 28px 8px", borderBottom: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <div>
            <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: "-0.01em" }}>Send to driver</div>
            <div style={{ fontSize: 13, color: "#6B7280", marginTop: 4 }}>{stopCount} stop{stopCount === 1 ? "" : "s"} in this route</div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#6B7280", padding: 4 }}><X size={20} strokeWidth={1.6} /></button>
        </div>

        {!created ? (
          <div style={{ padding: "20px 28px 24px" }}>
            {error && <div style={{ padding: "10px 14px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, color: "#DC2626", fontSize: 13, marginBottom: 14 }}>{error}</div>}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 6 }}>Route name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Tuesday PM — Oakland loop" style={fieldStyle} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
              {[
                { label: "Require a photo per delivery", v: requirePhoto, set: setRequirePhoto },
                { label: "Require a signature per delivery", v: requireSignature, set: setRequireSignature },
              ].map(row => (
                <label key={row.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: "1px solid #E5E7EB", borderRadius: 10, cursor: "pointer" }}>
                  <input type="checkbox" checked={row.v} onChange={e => row.set(e.target.checked)} />
                  <span style={{ fontSize: 14, color: "#000" }}>{row.label}</span>
                </label>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={onClose} style={{ padding: "10px 16px", borderRadius: 10, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 14, fontWeight: 500, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>Cancel</button>
              <button onClick={submit} disabled={sending} style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: sending ? "#D1D5DB" : "#000", color: "#fff", fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: sending ? "default" : "pointer" }}>
                {sending ? "Creating…" : "Create driver link"}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: "22px 28px 26px" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#16A34A", marginBottom: 10 }}>✓ Route dispatched</div>
            <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.55, marginBottom: 14 }}>Share this link with your driver. Opens on any phone — no login needed. You'll see deliveries come in live.</div>
            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
              <input readOnly value={url} style={{ ...fieldStyle, fontFamily: "monospace", fontSize: 12 }} onFocus={e => e.target.select()} />
              <button onClick={copy} style={{ padding: "10px 16px", borderRadius: 10, border: "none", background: copied ? "#F0FDF4" : "#000", color: copied ? "#16A34A" : "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            <button onClick={onClose} style={{ padding: "10px 18px", borderRadius: 10, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 14, fontWeight: 500, fontFamily: "Inter, sans-serif", cursor: "pointer", width: "100%" }}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═════════════════════════ CLIENTS VIEW ═════════════════════════
   Two modes, same page:
    - List (selectedClient == null): every customer + revenue + orders
    - Profile: mini analytics + forecast toggle + full order history
   Shopify admin URLs: https://admin.shopify.com/store/<handle>/orders/<id>
   where <handle> is the myshopify subdomain (e.g. "holy-nations").
   ═══════════════════════════════════════════════════════════════ */
function ClientsView({
  clients, selectedClient, setSelectedClientId, analytics,
  forecastEnabled, setForecastEnabled, connectedShop, shopConnected,
  clientSearch, setClientSearch,
}) {
  const shopHandle = connectedShop ? connectedShop.replace(".myshopify.com", "") : "";
  const shopifyOrderUrl = (o) => shopHandle && o?.id
    ? `https://admin.shopify.com/store/${shopHandle}/orders/${o.id}`
    : null;
  const fmtDate = (ts) => ts && isFinite(ts)
    ? new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";
  const fmtMoney = (n) => `$${Math.round(n || 0).toLocaleString()}`;
  const daysAgo = (ts) => ts && isFinite(ts)
    ? Math.max(0, Math.floor((Date.now() - ts) / 86400000))
    : null;

  /* ── List view ───────────────────────────────────────── */
  if (!selectedClient) {
    const filtered = clientSearch.trim()
      ? clients.filter(c => {
          const q = clientSearch.toLowerCase();
          return c.name.toLowerCase().includes(q)
            || (c.email || "").toLowerCase().includes(q)
            || (c.company || "").toLowerCase().includes(q);
        })
      : clients;
    return (
      <div>
        <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #F3F4F6" }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: "#000000", margin: 0, lineHeight: "28px" }}>Clients</h1>
          <p style={{ fontSize: 16, color: "#6B7280", margin: "2px 0 0", lineHeight: "20px" }}>Customer profiles, lifetime revenue, and order history.</p>
        </div>
        {!shopConnected && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "#FFFBEB", borderRadius: 8, border: "1px solid #FDE68A", marginBottom: 16 }}>
            <AlertTriangle size={16} color="#D97706" strokeWidth={1.6} />
            <span style={{ fontSize: 15, color: "#92400E" }}>Connect Shopify to see your clients.</span>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
            <Search size={15} color="#9CA3AF" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
            <input
              value={clientSearch}
              onChange={e => setClientSearch(e.target.value)}
              placeholder="Search by name, email, or company..."
              style={{ width: "100%", padding: "8px 12px 8px 36px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000000", fontSize: 15, fontFamily: "Inter, sans-serif", outline: "none", boxSizing: "border-box" }}
            />
          </div>
          <span style={{ fontSize: 13, color: "#9CA3AF", marginLeft: "auto" }}>
            {filtered.length} client{filtered.length === 1 ? "" : "s"}
          </span>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: "40px 20px", textAlign: "center", color: "#9CA3AF", fontSize: 15 }}>
            {clients.length === 0 ? "No clients yet — orders will appear here." : "No clients match that search."}
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <colgroup>
              <col />
              <col style={{ width: 200 }} />
              <col style={{ width: 80 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 120 }} />
            </colgroup>
            <thead>
              <tr>
                {["Client", "Company / Email", "Orders", "Lifetime revenue", "Avg order", "Last order"].map((h, i) => (
                  <th key={i} style={{ padding: "10px 14px", textAlign: i >= 2 ? "right" : "left", fontSize: 13, fontWeight: 500, color: "#9CA3AF", borderBottom: "1px solid #F3F4F6" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const lastDays = daysAgo(c.lastOrder);
                return (
                  <tr key={c.id} style={{ borderBottom: "1px solid #F9FAFB", cursor: "pointer" }}
                    onClick={() => setSelectedClientId(c.id)}
                    onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "12px 14px", fontWeight: 600, fontSize: 15, color: "#000000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</td>
                    <td style={{ padding: "12px 14px", fontSize: 14, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.company || c.email || "—"}
                    </td>
                    <td style={{ padding: "12px 14px", fontSize: 15, color: "#374151", textAlign: "right" }}>{c.orderCount}</td>
                    <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 600, color: "#000000", textAlign: "right" }}>{fmtMoney(c.totalRevenue)}</td>
                    <td style={{ padding: "12px 14px", fontSize: 15, color: "#374151", textAlign: "right" }}>{fmtMoney(c.avgOrder)}</td>
                    <td style={{ padding: "12px 14px", fontSize: 14, color: "#9CA3AF", textAlign: "right" }}>
                      {lastDays == null ? "—" : lastDays === 0 ? "Today" : `${lastDays}d ago`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  /* ── Profile view ────────────────────────────────────── */
  const c = selectedClient;
  const a = analytics;
  const growthPct = a ? Math.round(a.growthRate * 100) : 0;

  return (
    <div>
      {/* Top row: back link + name */}
      <div style={{ marginBottom: 20 }}>
        <button onClick={() => setSelectedClientId(null)} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "transparent", border: "none", padding: "4px 8px 4px 0",
          color: "#6B7280", fontSize: 14, fontFamily: "Inter, sans-serif", cursor: "pointer", marginBottom: 4,
        }}>
          <X size={13} /> Back to clients
        </button>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: "#000000", margin: 0, lineHeight: 1.2 }}>{c.name}</h1>
            <p style={{ fontSize: 14, color: "#6B7280", margin: "4px 0 0", lineHeight: 1.3 }}>
              {[c.company, c.email, [c.city, c.province].filter(Boolean).join(", ")].filter(Boolean).join(" · ") || "—"}
            </p>
          </div>
          <button
            onClick={() => setForecastEnabled(v => !v)}
            title={forecastEnabled ? "Hide projection" : "Show projection"}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "7px 12px", borderRadius: 8,
              border: `1px solid ${forecastEnabled ? "#2563EB" : "#E5E7EB"}`,
              background: forecastEnabled ? "#2563EB" : "#fff",
              color: forecastEnabled ? "#fff" : "#6B7280",
              fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
            }}
          >
            <div style={{
              width: 28, height: 16, borderRadius: 99,
              background: forecastEnabled ? "#fff" : "#D1D5DB", transition: "background 0.15s",
              position: "relative", flexShrink: 0,
            }}>
              <div style={{
                position: "absolute", top: 2, left: forecastEnabled ? 14 : 2,
                width: 12, height: 12, borderRadius: "50%",
                background: forecastEnabled ? "#2563EB" : "#fff",
                transition: "left 0.15s", boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
              }} />
            </div>
            Projection
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${forecastEnabled ? 5 : 4}, minmax(0, 1fr))`, gap: 12, marginBottom: 20 }}>
        {[
          { l: "Lifetime revenue", v: fmtMoney(c.totalRevenue), sub: `since ${fmtDate(c.firstOrder)}` },
          { l: "Orders", v: c.orderCount, sub: c.orderCount > 1 ? `first ${fmtDate(c.firstOrder)}` : "first order" },
          { l: "Avg order", v: fmtMoney(c.avgOrder) },
          { l: "Last order", v: fmtDate(c.lastOrder), sub: (() => { const d = daysAgo(c.lastOrder); return d == null ? null : d === 0 ? "Today" : `${d}d ago`; })() },
          ...(forecastEnabled && a ? [{
            l: "Projected 30d",
            v: fmtMoney(a.projected30d),
            sub: growthPct === 0 ? "flat trend" : growthPct > 0 ? `trending +${growthPct}%` : `trending ${growthPct}%`,
            accent: "#2563EB",
          }] : []),
        ].map((s, i) => (
          <div key={i} style={{
            padding: "12px 14px", borderRadius: 10, border: "1px solid #F3F4F6", background: "#fff",
            borderColor: s.accent ? "#DBEAFE" : "#F3F4F6",
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4 }}>{s.l}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: s.accent || "#000000", marginTop: 4, lineHeight: 1.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.v}</div>
            {s.sub && <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* Chart */}
      {a && a.daily && a.daily.length > 0 && (
        <div style={{ padding: "16px 4px 4px", border: "1px solid #F3F4F6", borderRadius: 10, marginBottom: 24, background: "#fff" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", padding: "0 16px 12px", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span>Revenue over time</span>
            <span style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 400, textTransform: "uppercase", letterSpacing: 0.4 }}>
              {a.granularity === "monthly" ? "Monthly" : a.granularity === "weekly" ? "Weekly" : "Daily"}
              {forecastEnabled && a.hasForecast && " · forecast shaded"}
            </span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={a.daily} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
              <defs>
                <linearGradient id="clientRevenueFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#000000" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#000000" stopOpacity={0.01} />
                </linearGradient>
                <linearGradient id="clientForecastFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.12} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9CA3AF" }} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} tickFormatter={(d) => {
                const dt = new Date(d + (d.length === 10 ? "T00:00:00" : ""));
                return a.granularity === "monthly"
                  ? dt.toLocaleDateString("en-US", { month: "short", year: "2-digit" })
                  : dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
              }} minTickGap={24} />
              <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} tickLine={false} axisLine={false} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E5E7EB", fontFamily: "Inter, sans-serif" }}
                formatter={(v, name) => v == null ? null : [`$${Math.round(v).toLocaleString()}`, name === "revenue" ? "Revenue" : "Projected"]}
                labelFormatter={(d) => new Date(d + (d.length === 10 ? "T00:00:00" : "")).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              />
              <Area type="monotone" dataKey="revenue" stroke="#000000" strokeWidth={2} fill="url(#clientRevenueFill)" connectNulls={false} isAnimationActive={false} />
              {forecastEnabled && a.hasForecast && (
                <Area type="monotone" dataKey="forecast" stroke="#2563EB" strokeWidth={2} strokeDasharray="4 4" fill="url(#clientForecastFill)" connectNulls={false} isAnimationActive={false} />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Orders table */}
      <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 10 }}>All orders ({c.orders.length})</div>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: 110 }} />
          <col style={{ width: 200 }} />
          <col />
          <col style={{ width: 80 }} />
          <col style={{ width: 110 }} />
          <col style={{ width: 130 }} />
        </colgroup>
        <thead>
          <tr>
            {["Date", "Order", "Items", "Qty", "Total", ""].map((h, i) => (
              <th key={i} style={{ padding: "10px 14px", textAlign: i === 3 || i === 4 ? "right" : "left", fontSize: 13, fontWeight: 500, color: "#9CA3AF", borderBottom: "1px solid #F3F4F6" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {c.orders.map(o => {
            const url = shopifyOrderUrl(o);
            const lineTitles = (o.line_items || []).map(li => li.title).filter(Boolean);
            const qty = (o.line_items || []).reduce((s, li) => s + (li.quantity || 0), 0);
            return (
              <tr key={o.id} style={{ borderBottom: "1px solid #F9FAFB" }}>
                <td style={{ padding: "12px 14px", fontSize: 14, color: "#9CA3AF", whiteSpace: "nowrap" }}>{fmtDate(new Date(o.created_at).getTime())}</td>
                <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 600, color: "#000000", whiteSpace: "nowrap" }}>#{o.order_number}</td>
                <td style={{ padding: "12px 14px", fontSize: 14, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={lineTitles.join(", ")}>{lineTitles.join(", ") || "—"}</td>
                <td style={{ padding: "12px 14px", fontSize: 15, color: "#374151", textAlign: "right" }}>{qty}</td>
                <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 600, color: "#000000", textAlign: "right" }}>{fmtMoney(parseFloat(o.total_price || 0))}</td>
                <td style={{ padding: "12px 14px", textAlign: "right" }}>
                  {url ? (
                    <a href={url} target="_blank" rel="noopener noreferrer" style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      padding: "5px 10px", borderRadius: 6, fontSize: 13, fontWeight: 500,
                      border: "1px solid #E5E7EB", background: "#fff", color: "#374151",
                      textDecoration: "none", fontFamily: "Inter, sans-serif",
                    }}>
                      Shopify <ExternalLink size={11} strokeWidth={1.8} />
                    </a>
                  ) : (
                    <span style={{ fontSize: 13, color: "#D1D5DB" }}>—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RightSlidePanel({ title, subtitle, onClose, children, footer, headerAction, width = 640 }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <>
      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(17, 24, 39, 0.35)", zIndex: 1000, animation: "fade-in 0.18s ease-out" }}
      />
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: `min(${width}px, 92vw)`,
          background: "#fff",
          zIndex: 1001,
          display: "flex", flexDirection: "column",
          boxShadow: "-8px 0 40px rgba(0,0,0,0.12)",
          fontFamily: "Inter, sans-serif",
          animation: "slide-in-right 0.22s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <div style={{ padding: "18px 24px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#000000", lineHeight: 1.2 }}>{title}</div>
            {subtitle && <div style={{ fontSize: 14, color: "#6B7280", marginTop: 4 }}>{subtitle}</div>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {headerAction}
            <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4 }} aria-label="Close"><X size={20} /></button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>{children}</div>
        {footer && <div style={{ padding: "14px 24px", borderTop: "1px solid #F3F4F6", flexShrink: 0 }}>{footer}</div>}
      </div>
    </>
  );
}

// Inline "+ Guest cupper" chip that morphs into an email field on
// click. Submit fires the guest-invite flow; Esc / blur (with empty
// value) collapses back to the chip.
function GuestCupperChip({ onInvite }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      await onInvite(email.trim());
      setEmail("");
      setOpen(false);
    } finally { setBusy(false); }
  };
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title="Email a guest cupper a phone-friendly link to score every sample. Guests aren't added to your roster."
        style={{
          padding: "7px 11px", borderRadius: 8,
          border: "1px dashed #D1D5DB", background: "#fff",
          color: "#6B7280", fontSize: 13, fontWeight: 500,
          cursor: "pointer", fontFamily: "Inter, sans-serif",
          display: "inline-flex", alignItems: "center", gap: 4,
          whiteSpace: "nowrap",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#6B7280"; e.currentTarget.style.color = "#111827"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#D1D5DB"; e.currentTarget.style.color = "#6B7280"; }}
      ><Plus size={11} strokeWidth={2} /> Guest cupper</button>
    );
  }
  return (
    <form onSubmit={submit} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <input
        ref={inputRef}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") { setEmail(""); setOpen(false); } }}
        onBlur={() => { if (!email.trim() && !busy) setOpen(false); }}
        placeholder="cupper@email.com"
        autoComplete="off"
        style={{
          padding: "7px 11px", borderRadius: 8,
          border: "1px dashed #6B7280", background: "#fff",
          color: "#111", fontSize: 13, fontWeight: 500,
          fontFamily: "Inter, sans-serif", width: 200,
          whiteSpace: "nowrap",
        }}
      />
      <button
        type="submit"
        disabled={busy || !email.trim()}
        style={{
          padding: "7px 12px", borderRadius: 8,
          border: "1px solid #000",
          background: email.trim() ? "#000" : "#E5E7EB",
          color: email.trim() ? "#fff" : "#9CA3AF",
          fontSize: 13, fontWeight: 600,
          cursor: busy ? "default" : (email.trim() ? "pointer" : "default"),
          fontFamily: "Inter, sans-serif",
        }}
      >{busy ? "…" : "Invite"}</button>
    </form>
  );
}

function GuestInviteForm({ onInvite, hint }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || busy) return;
    setBusy(true);
    try { await onInvite(email.trim()); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} style={{ padding: "8px 10px 10px" }}>
      {hint && (
        <div style={{ fontSize: 11, color: "#9CA3AF", fontStyle: "italic", marginBottom: 8, lineHeight: 1.4 }}>
          {hint} Invite a guest:
        </div>
      )}
      <input
        type="email"
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="cupper@example.com"
        style={{ width: "100%", padding: "8px 10px", border: "1px solid #E5E7EB", borderRadius: 7, fontSize: 13, fontFamily: "Inter, sans-serif", marginBottom: 6 }}
      />
      <button
        type="submit"
        disabled={busy || !email.trim()}
        style={{
          width: "100%", padding: "8px 12px", borderRadius: 7,
          border: "1px solid #000", background: email.trim() ? "#000" : "#E5E7EB",
          color: email.trim() ? "#fff" : "#9CA3AF",
          fontSize: 13, fontWeight: 600,
          cursor: busy ? "default" : (email.trim() ? "pointer" : "default"),
          fontFamily: "Inter, sans-serif",
        }}
      >{busy ? "Sending…" : "Send invite"}</button>
      <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 6, lineHeight: 1.4 }}>
        Guest cuppers won't be saved to your roster. They'll pick a first name when they open the link.
      </div>
    </form>
  );
}

function CuppingDetailModal({ cupping, user, onClose, onDelete, onSetStatus, onUpdateCoffees, onUpdateMeta, coffeeSuggestions = [], sources = [], cuppers = [], effectiveDemoMode = false, onSaveCupper = null }) {
  const c = cupping;
  const scoreColor = (n) => n >= 90 ? "#B8860B" : n >= 85 ? "#16A34A" : n >= 80 ? "#111111" : "#6B7280";
  const coffees = c.coffees || [];
  const isAdmin = (user?.role || "admin") === "admin";
  // Session-level cupper roster: union of cupperIds attached to any
  // sample. Cuppers added at session level are mirrored to every
  // sample, so this stays consistent even before re-saving.
  const sessionCupperIds = useMemo(() => {
    const seen = new Set();
    const order = [];
    for (const co of coffees) {
      for (const cup of (co.cuppings || [])) {
        if (cup.cupperId && !seen.has(cup.cupperId)) {
          seen.add(cup.cupperId);
          order.push(cup.cupperId);
        }
      }
    }
    return order;
  }, [coffees]);
  const sessionCupperLabel = (id) => {
    const live = cuppers.find(x => x.id === id);
    if (live?.name) return live.name;
    for (const co of coffees) {
      const r = (co.cuppings || []).find(x => x.cupperId === id);
      if (r?.cupperName) return r.cupperName;
    }
    return "Cupper";
  };
  // The session-level tab — "avg" (default) or a specific cupperId.
  // Drives both the collapsed-row score and the expanded card's
  // active record. Resets back to "avg" if the active cupper gets
  // removed.
  const [sessionTab, setSessionTab] = useState("avg");
  useEffect(() => {
    if (sessionTab !== "avg" && !sessionCupperIds.includes(sessionTab)) setSessionTab("avg");
  }, [sessionTab, sessionCupperIds]);
  // SCA total from a single { scores, defects } record. Returns 0 if
  // no primary attribute is set.
  const totalFromRecord = (rec) => {
    const s = rec?.scores;
    if (!s) return 0;
    const pk = ["fragrance", "flavor", "aftertaste", "acidity", "body", "balance", "overall"];
    if (!pk.some(k => s[k] != null)) return 0;
    const prim = pk.reduce((a, k) => a + (Number(s[k]) || 0), 0);
    const secV = (k) => (s[k] != null ? Number(s[k]) : 10);
    const sec = secV("uniformity") + secV("cleanCup") + secV("sweetness");
    return +(prim + sec - (Number(rec?.defects) || 0)).toFixed(2);
  };
  // Recompute totals on the fly so the session avg ignores stale
  // server-side totals from before the SCA fix. If Main has no
  // override, fall back to the average of non-excluded per-cupper
  // records so the collapsed row mirrors what the Main tab shows.
  const liveCoffeeTotal = (cof) => {
    const fromMain = totalFromRecord({ scores: cof.scores, defects: cof.defects });
    if (fromMain > 0) return fromMain;
    const avg = computeCuppingsAverage(cof);
    return avg ? totalFromRecord(avg) : 0;
  };
  const liveTotals = coffees.map(liveCoffeeTotal);
  const scored = coffees.filter((_x, i) => liveTotals[i] > 0);
  const scoredVals = liveTotals.filter(t => t > 0);
  const sessionAvg = scoredVals.length > 0
    ? +(scoredVals.reduce((s, t) => s + t, 0) / scoredVals.length).toFixed(2)
    : 0;
  const grade = sessionAvg >= 90 ? "Outstanding" : sessionAvg >= 85 ? "Excellent" : sessionAvg >= 80 ? "Very Good" : "Below Specialty";
  const secondary = [
    { k: "uniformity", l: "Uniformity" },
    { k: "cleanCup", l: "Clean cup" },
    { k: "sweetness", l: "Sweetness" },
  ];

  // Per-coffee editing: open the score modal seeded with the picked coffee.
  const [editingCoffee, setEditingCoffee] = useState(null); // null | "new" | coffeeObj
  const [expandedCoffeeId, setExpandedCoffeeId] = useState(null);
  // Per-coffee active tab key (cupper id, or "main"). Defaults to "main".
  // Per-coffee edit mode flag. null = nobody editing. Stored as a tab
  // key string `${coffeeId}::${tabKey}` so we can tell whether the
  // currently-shown tab is in edit mode.
  const [editingTabKey, setEditingTabKey] = useState(null);
  // Buffer of pending edits keyed by `${coffeeId}::${tabKey}`. Lives in
  // a ref so dragging sliders doesn't re-render the whole modal — we
  // commit on Save.
  const pendingPatchRef = useRef({});
  // Helper: read/write a coffee's per-cupper or main scoring record
  // out of a single normalized shape. Coffees from before this feature
  // existed only have top-level scores/descriptors/etc. — those are
  // treated as the "main" record.
  const readScoreRecord = (coffee, tabKey) => {
    if (tabKey === "main" || !tabKey) {
      return {
        scores: coffee.scores || null,
        descriptors: coffee.descriptors || null,
        defects: coffee.defects || 0,
        notes: coffee.notes || "",
      };
    }
    const entry = (coffee.cuppings || []).find(x => x.cupperId === tabKey);
    return {
      scores: entry?.scores || null,
      descriptors: entry?.descriptors || null,
      defects: entry?.defects || 0,
      notes: entry?.notes || "",
    };
  };
  const writeScoreRecord = (coffee, tabKey, patch) => {
    if (tabKey === "main" || !tabKey) {
      return {
        ...coffee,
        scores: patch.scores ?? coffee.scores,
        descriptors: patch.descriptors ?? coffee.descriptors,
        defects: patch.defects ?? coffee.defects,
        notes: patch.notes ?? coffee.notes,
      };
    }
    const list = coffee.cuppings || [];
    const idx = list.findIndex(x => x.cupperId === tabKey);
    const merged = idx >= 0 ? { ...list[idx], ...patch } : { cupperId: tabKey, ...patch };
    const nextList = idx >= 0
      ? list.map((x, i) => i === idx ? merged : x)
      : [...list, merged];
    return { ...coffee, cuppings: nextList };
  };
  // Average across all per-cupper records (excluding the "main"
  // override and any cupper records flagged `excludedFromAverage`).
  // Returns null if no eligible per-cupper records exist.
  function computeCuppingsAverage(coffee) {
    const list = coffee.cuppings || [];
    const withScores = list.filter(x => x.scores && !x.excludedFromAverage);
    if (withScores.length === 0) return null;
    const keys = ["fragrance", "flavor", "aftertaste", "acidity", "body", "balance", "overall", "uniformity", "cleanCup", "sweetness"];
    const avgScores = {};
    for (const k of keys) {
      const vals = withScores
        .map(x => x.scores && x.scores[k] != null ? Number(x.scores[k]) : null)
        .filter(v => v != null);
      if (vals.length > 0) avgScores[k] = +(vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(2);
    }
    const defects = withScores.reduce((s, x) => s + (Number(x.defects) || 0), 0) / withScores.length;
    return { scores: avgScores, defects: +defects.toFixed(2) };
  }

  const saveCoffee = async (form) => {
    const updated = { ...form, id: form.id || `cof_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}` };
    const next = form.id
      ? coffees.map(x => x.id === form.id ? { ...x, ...updated } : x)
      : [...coffees, updated];
    await onUpdateCoffees(c.id, next);
    setEditingCoffee(null);
  };
  const removeCoffee = async (id) => {
    if (!window.confirm("Remove this sample from the session?")) return;
    const next = coffees.filter(x => x.id !== id);
    await onUpdateCoffees(c.id, next);
  };

  // Session-level: add a cupper to every sample + fire the invite
  // email. Cuppers are session-scoped — there is no per-sample roster.
  const addCupperToSession = async (cupperId) => {
    const cu = cuppers.find(x => x.id === cupperId);
    if (!cu) return;
    let email = (cu.email || "").trim();
    if (!email) {
      const v = window.prompt(
        `Email address for ${cu.name}?\nThey'll get a phone-friendly link to score this session.`,
        ""
      );
      if (v == null) return;
      email = v.trim();
      if (!email) return;
      if (onSaveCupper) onSaveCupper({ ...cu, email });
    }
    const blank = { cupperId, cupperName: cu.name, scores: null, descriptors: null, defects: 0, notes: "" };
    const nextCoffees = coffees.map(co => {
      const list = co.cuppings || [];
      if (list.some(x => x.cupperId === cupperId)) return co;
      return { ...co, cuppings: [...list, { ...blank }] };
    });
    await onUpdateCoffees(c.id, nextCoffees);
    setSessionTab(cupperId);
    const synced = await ensureSessionSynced({ ...c, coffees: nextCoffees });
    if (!synced) return;
    try {
      const r = await fetch(`/api/cuppings/${c.id}/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cupperId, cupperName: cu.name, cupperEmail: email }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || "Couldn't send invite");
      }
    } catch (err) { alert(err.message || "Couldn't send invite"); }
  };

  // In demo mode the cupping data lives only in the browser, so the
  // server has no record of the session by the time we try to send an
  // invite. Push a snapshot to /api/cuppings/sync first so the link
  // in the email actually resolves.
  const ensureSessionSynced = async (sessionWithUpdates) => {
    if (!effectiveDemoMode) return true;
    try {
      const r = await fetch(`/api/cuppings/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sessionWithUpdates),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || "Couldn't sync session");
      }
      return true;
    } catch (err) {
      alert(err.message || "Couldn't sync session for the invite");
      return false;
    }
  };

  // Guest cupper invite — used when there's no one to add from the
  // roster. Mints an ephemeral cupperId, mirrors a blank record onto
  // every sample (with no name yet — the cupper will fill it in on
  // their phone), and fires the invite email. Guest is intentionally
  // NOT added to the cupping roster — they're a one-off for this
  // session. Their tab will show their first name once they've
  // identified themselves on the mobile page.
  const inviteGuestCupper = async (rawEmail) => {
    const email = (rawEmail || "").trim();
    if (!email) return;
    const guestId = `guest-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    const blank = { cupperId: guestId, cupperName: "", scores: null, descriptors: null, defects: 0, notes: "" };
    const nextCoffees = coffees.map(co => ({
      ...co,
      cuppings: [...(co.cuppings || []), { ...blank }],
    }));
    await onUpdateCoffees(c.id, nextCoffees);
    setSessionTab(guestId);
    const synced = await ensureSessionSynced({ ...c, coffees: nextCoffees });
    if (!synced) return;
    try {
      const r = await fetch(`/api/cuppings/${c.id}/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cupperId: guestId, cupperName: "", cupperEmail: email }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || "Couldn't send invite");
      }
    } catch (err) { alert(err.message || "Couldn't send invite"); }
  };

  const removeCupperFromSession = async (cupperId) => {
    const cu = cuppers.find(x => x.id === cupperId);
    const name = cu?.name || "this cupper";
    if (!window.confirm(`Remove ${name} from this entire session?\nTheir scores on every sample will be deleted.`)) return;
    const nextCoffees = coffees.map(co => ({
      ...co,
      cuppings: (co.cuppings || []).filter(x => x.cupperId !== cupperId),
    }));
    await onUpdateCoffees(c.id, nextCoffees);
    if (sessionTab === cupperId) setSessionTab("avg");
  };

  const resendCupperInvite = async (cupperId) => {
    const cu = cuppers.find(x => x.id === cupperId);
    if (!cu) return;
    let email = (cu.email || "").trim();
    if (!email) {
      const v = window.prompt(`Email address for ${cu.name}?`, "");
      if (v == null) return;
      email = v.trim();
      if (!email) return;
      if (onSaveCupper) onSaveCupper({ ...cu, email });
    }
    const synced = await ensureSessionSynced(c);
    if (!synced) return;
    try {
      const r = await fetch(`/api/cuppings/${c.id}/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cupperId, cupperName: cu.name, cupperEmail: email }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || "Couldn't send invite");
      }
      alert(`Invite resent to ${email}.`);
    } catch (err) { alert(err.message || "Couldn't send invite"); }
  };

  // Open a print-optimized scorecard in a new window — frame it like a
  // printed certificate: big radar, large score, descriptors as chips,
  // Elion wordmark, signature line for the cupper. Now per-coffee:
  // pass the coffee object you want to print.
  const printScorecard = (coffee) => {
    // Resolve the effective score record. The "main" override takes
    // precedence; if it's empty (the common case — admin hasn't
    // hand-overridden), fall back to the cupping average across all
    // contributing cuppers. Without this, the PDF rendered 0.00s
    // for any coffee whose scores live only in `cuppings[]`.
    const hasMainScores = coffee?.scores && Object.values(coffee.scores).some(v => Number(v) > 0);
    let scores = coffee?.scores || {};
    let defects = Number(coffee?.defects) || 0;
    let total = Number(coffee?.total) || 0;
    if (!hasMainScores) {
      const avg = computeCuppingsAverage(coffee);
      if (avg) {
        scores  = avg.scores;
        defects = Number(avg.defects) || 0;
        total   = totalFromRecord({ scores, defects });
      }
    } else if (!total) {
      // Have scores but no precomputed total — derive on the fly.
      total = totalFromRecord({ scores, defects });
    }
    const co = { ...coffee, scores, defects, total };
    const coDescriptors = co.descriptors || {};
    const coGrade = co.total >= 90 ? "Outstanding" : co.total >= 85 ? "Excellent" : co.total >= 80 ? "Very Good" : "Below Specialty";
    const win = window.open("", "_blank", "width=900,height=1200");
    if (!win) return;
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, x => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[x]));
    const fmtDate = (iso) => iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—";
    const origin = window.location.origin;

    // Rebuild the radar SVG as a string for the popup
    const axes = [
      { k: "fragrance", l: "Fragrance" }, { k: "flavor", l: "Flavor" },
      { k: "aftertaste", l: "Aftertaste" }, { k: "acidity", l: "Acidity" },
      { k: "body", l: "Body" }, { k: "balance", l: "Balance" }, { k: "overall", l: "Overall" },
    ];
    const size = 520, pcx = size / 2, pcy = size / 2, maxR = size * 0.36, labelR = maxR + size * 0.095;
    const angleFor = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / axes.length;
    const scoreToR = (s) => ((Math.max(6, Math.min(10, s)) - 6) / 4) * maxR;
    const gridLevels = [6, 7, 8, 9, 10];
    const gridPath = (level) => "M" + axes.map((_, i) => {
      const a = angleFor(i); const r = scoreToR(level);
      return `${pcx + Math.cos(a) * r},${pcy + Math.sin(a) * r}`;
    }).join(" L") + " Z";
    const dataPoints = axes.map(({ k }, i) => {
      const a = angleFor(i); const r = scoreToR(Number(co.scores?.[k]) || 6);
      return [pcx + Math.cos(a) * r, pcy + Math.sin(a) * r];
    });
    const dataPath = "M" + dataPoints.map(([x, y]) => `${x},${y}`).join(" L") + " Z";
    const facets = axes.map((_, i) => {
      const [x1, y1] = dataPoints[i];
      const [x2, y2] = dataPoints[(i + 1) % axes.length];
      return `<path d="M${pcx},${pcy} L${x1},${y1} L${x2},${y2} Z" fill="url(#facet-${i})" stroke="none" />`;
    }).join("");
    const gradients = axes.map((_, i) => (
      `<linearGradient id="facet-${i}" x1="0" y1="0" x2="1" y2="1">
         <stop offset="0%" stop-color="#111" stop-opacity="${(0.08 + (i % 3) * 0.06).toFixed(2)}" />
         <stop offset="100%" stop-color="#111" stop-opacity="${(0.18 + (i % 3) * 0.06).toFixed(2)}" />
       </linearGradient>`
    )).join("");
    const ringsStr = gridLevels.map(lvl => `<path d="${gridPath(lvl)}" fill="none" stroke="#E5E7EB" stroke-width="${lvl === 10 ? 1.2 : 0.7}" />`).join("");
    const spokesStr = axes.map((_, i) => {
      const a = angleFor(i); const r = scoreToR(10);
      return `<line x1="${pcx}" y1="${pcy}" x2="${pcx + Math.cos(a) * r}" y2="${pcy + Math.sin(a) * r}" stroke="#E5E7EB" stroke-width="0.7" />`;
    }).join("");
    const verticesStr = dataPoints.map(([x, y]) => `<circle cx="${x}" cy="${y}" r="4" fill="#111" stroke="#fff" stroke-width="2" />`).join("");
    const labelsStr = axes.map(({ k, l }, i) => {
      const a = angleFor(i);
      const lx = pcx + Math.cos(a) * labelR;
      const ly = pcy + Math.sin(a) * labelR;
      const anchor = Math.abs(Math.cos(a)) < 0.3 ? "middle" : (Math.cos(a) > 0 ? "start" : "end");
      const s = Number(co.scores?.[k]) || 0;
      return `<g>
        <text x="${lx}" y="${ly}" font-size="12" font-weight="600" fill="#111" font-family="Inter, sans-serif" text-anchor="${anchor}" letter-spacing="0.08em" style="text-transform:uppercase">${esc(l)}</text>
        <text x="${lx}" y="${ly + 18}" font-size="16" font-weight="800" fill="#06B6D4" font-family="Inter, sans-serif" text-anchor="${anchor}" letter-spacing="-0.01em">${s.toFixed(2)}</text>
      </g>`;
    }).join("");
    const scoreLevelsStr = gridLevels.map(lvl => {
      const a = angleFor(0); const r = scoreToR(lvl);
      return `<text x="${pcx + Math.cos(a) * r + 7}" y="${pcy + Math.sin(a) * r + 3}" font-size="10" fill="#9CA3AF" font-family="Inter, sans-serif">${lvl}</text>`;
    }).join("");

    const descriptorChips = [
      ...(coDescriptors.fragrance || "").split(",").map(s => s.trim()).filter(Boolean),
      ...(coDescriptors.flavor || "").split(",").map(s => s.trim()).filter(Boolean),
      ...(coDescriptors.aftertaste || "").split(",").map(s => s.trim()).filter(Boolean),
    ];

    win.document.write(`<!doctype html>
<html><head><title>Cupping Scorecard — ${esc(co.coffeeName)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet">
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Inter', -apple-system, sans-serif; margin: 0; padding: 48px 56px; color: #111; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .wrap { max-width: 720px; margin: 0 auto; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111; padding-bottom: 20px; margin-bottom: 28px; }
  .head .eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: #2C5766; margin-bottom: 6px; }
  .head h1 { margin: 0 0 8px; font-size: 32px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.08; }
  .head .meta { font-size: 13px; color: #6B7280; }
  .head .logo img { height: 22px; display: block; }
  .center { display: flex; flex-direction: column; align-items: center; margin: 16px 0 28px; }
  .score-block { text-align: center; margin-bottom: 24px; }
  .score-block .n { font-size: 72px; font-weight: 900; letter-spacing: -0.04em; line-height: 1; color: #111; }
  .score-block .g { font-size: 11px; font-weight: 700; letter-spacing: 0.24em; text-transform: uppercase; color: #6B7280; margin-top: 10px; }
  .radar-wrap { width: 100%; max-width: 560px; }
  .chips { display: flex; flex-wrap: wrap; gap: 8px; margin: 24px 0; justify-content: center; }
  .chip { padding: 6px 14px; border-radius: 99px; background: #F3F4F6; font-size: 13px; color: #111; font-weight: 500; }
  .secondary { display: flex; gap: 12px; justify-content: center; margin-bottom: 28px; }
  .sbox { padding: 10px 18px; border-radius: 8px; border: 1px solid #E5E7EB; font-size: 12px; color: #6B7280; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 600; }
  .sbox.full { background: #111; color: #fff; border-color: #111; }
  .notes { font-size: 14px; color: #374151; line-height: 1.6; padding: 16px 20px; background: #FAFAFA; border-left: 3px solid #2C5766; margin-bottom: 32px; white-space: pre-wrap; }
  footer { display: flex; justify-content: space-between; align-items: flex-end; border-top: 2px solid #111; padding-top: 20px; margin-top: 28px; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: #6B7280; font-weight: 600; }
  footer .sig { flex: 1; border-bottom: 1px solid #111; padding-bottom: 4px; margin: 0 24px 0 8px; min-width: 180px; }
  footer .sig-label { font-size: 10px; color: #9CA3AF; margin-top: 4px; text-transform: uppercase; letter-spacing: 0.14em; }
  footer .url { font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase; font-weight: 700; color: #111; }
  @media print { body { padding: 24px 30px; } }
</style></head>
<body>
  <div class="wrap">
    <div class="head">
      <div>
        <div class="eyebrow">Cupping Scorecard${c.name ? ` · ${esc(c.name)}` : ""}</div>
        <h1>${esc(co.coffeeName)}</h1>
        <div class="meta">Cupped ${fmtDate(c.cuppingDate)}${co.roastDate ? ` · Roasted ${fmtDate(co.roastDate)}` : ""}${c.cupperName ? ` · By ${esc(c.cupperName)}` : ""}</div>
      </div>
      <div class="logo"><img src="${origin}/brand/elion-roaster.svg" alt="Elion" /></div>
    </div>

    <div class="center">
      <div class="score-block">
        <div class="n">${(co.total || 0).toFixed(2)}</div>
        <div class="g">${esc(coGrade)}</div>
      </div>
      <div class="radar-wrap">
        <svg viewBox="0 0 ${size} ${size}" width="100%" height="${size}" style="display:block">
          <defs>${gradients}</defs>
          ${ringsStr}
          ${spokesStr}
          ${facets}
          <path d="${dataPath}" fill="none" stroke="#111" stroke-width="2.2" stroke-linejoin="round" />
          ${verticesStr}
          ${scoreLevelsStr}
          ${labelsStr}
        </svg>
      </div>
    </div>

    ${descriptorChips.length > 0 ? `<div class="chips">${descriptorChips.slice(0, 24).map(d => `<span class="chip">${esc(d)}</span>`).join("")}</div>` : ""}

    <div class="secondary">
      ${secondary.map(s => `<div class="sbox${(Number(co.scores?.[s.k]) === 10) ? ' full' : ''}">${esc(s.l)}</div>`).join("")}
      ${(co.defects || 0) > 0 ? `<div class="sbox" style="border-color:#FCA5A5; color:#DC2626;">−${co.defects} defects</div>` : ""}
    </div>

    ${co.notes ? `<div class="notes">${esc(co.notes)}</div>` : ""}

    <footer>
      <div>
        <div class="sig">${esc(c.cupperName || "")}</div>
        <div class="sig-label">Graded by</div>
      </div>
      <div class="url">elionlabs.org</div>
    </footer>
  </div>
  <script>window.addEventListener('load', function(){ setTimeout(function(){ window.print(); }, 350); });<\/script>
</body></html>`);
    win.document.close();
  };

  // Per-coffee approve / reject / reset, written through the existing
  // saveCoffee path (which patches one coffee in the session). Score-
  // sheet metrics (the SCA total) live separately on the coffee
  // record; status is independent so a coffee can be approved before
  // it's even formally scored.
  const setCoffeeStatus = async (coffeeId, status) => {
    const coffee = coffees.find(x => x.id === coffeeId);
    if (!coffee) return;
    try { await saveCoffee({ ...coffee, status }); }
    catch (err) { alert(err.message || "Couldn't update"); }
  };

  const footer = (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <button
        onClick={() => { if (window.confirm("Delete this cupping session? This can't be undone.")) { onDelete(c.id); onClose(); } }}
        style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#9CA3AF", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
      >
        Delete session
      </button>
    </div>
  );
  const sessionTitle = c.name || c.coffeeName || "Cupping session";
  const subtitleText = [
    c.cupperName,
    c.sourcingCompany && `from ${c.sourcingCompany}`,
    c.cuppingDate && `Cupped ${new Date(c.cuppingDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`,
  ].filter(Boolean).join(" · ");
  // Aggregate status pill on the session header: derived from the
  // per-coffee statuses since the session itself no longer carries
  // approve/reject. Shows the dominant signal — all approved, all
  // rejected, or "mixed" (which we render as Pending).
  const aggStatusBadge = (() => {
    if (coffees.length === 0) return null;
    const all = coffees.every(x => x.status === "approved");
    const allRej = coffees.every(x => x.status === "rejected");
    if (all) return { label: `${coffees.length} approved`, bg: "#F0FDF4", fg: "#16A34A", border: "#BBF7D0" };
    if (allRej) return { label: `${coffees.length} rejected`, bg: "#FEF2F2", fg: "#DC2626", border: "#FECACA" };
    return null;
  })();
  return (
    <>
    <div>
      {/* ── Page header ───────────────────────────────────────────── */}
      <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: "#000", margin: 0, lineHeight: "28px", letterSpacing: "-0.01em" }}>{sessionTitle}</h1>
            <button
              onClick={() => {
                const v = window.prompt("Rename this session", sessionTitle);
                if (v != null && v.trim() && onUpdateMeta) onUpdateMeta(c.id, { name: v.trim() });
              }}
              title="Rename session"
              aria-label="Rename session"
              style={{ width: 28, height: 28, padding: 0, borderRadius: 7, border: "1px solid #E5E7EB", background: "#fff", color: "#9CA3AF", cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#374151"; e.currentTarget.style.borderColor = "#9CA3AF"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#9CA3AF"; e.currentTarget.style.borderColor = "#E5E7EB"; }}
            ><Pencil size={12} strokeWidth={1.8} /></button>
            {aggStatusBadge && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "3px 10px", borderRadius: 99,
                background: aggStatusBadge.bg, color: aggStatusBadge.fg,
                border: `1px solid ${aggStatusBadge.border}`,
                fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em",
              }}>{aggStatusBadge.label}</span>
            )}
          </div>
          {subtitleText && <p style={{ fontSize: 14, color: "#6B7280", margin: "4px 0 0", lineHeight: 1.5 }}>{subtitleText}</p>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {coffees.length > 0 && (
            <button
              onClick={() => printSampleLabels(buildLabelRowsForSession(c), "small")}
              title="Print sample labels for this session — QR + ID per label, one per page (1.1in × 3.5in)."
              style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 5 }}
            ><ClipboardList size={12} strokeWidth={1.8} /> Print Labels</button>
          )}
          <button
            onClick={() => setEditingCoffee("new")}
            title="Add a sample to this session"
            style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #000000", background: "#000000", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 5 }}
          ><Plus size={12} strokeWidth={2} /> Add sample</button>
        </div>
      </div>
      {/* Session-level cupper tabs — filter every sample below to a
          single cupper's view. Average (default) shows the consolidated
          score across all non-excluded cuppers. The "+ Cupper" chip
          sits at the end so the affordance is always visible right
          next to the existing roster. */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
          {sessionCupperIds.length > 0 && (
            <button
              onClick={() => setSessionTab("avg")}
              style={{
                padding: "7px 13px", borderRadius: 8,
                border: `1px solid ${sessionTab === "avg" ? "#000" : "#E5E7EB"}`,
                background: sessionTab === "avg" ? "#000" : "#fff",
                color: sessionTab === "avg" ? "#fff" : "#374151",
                fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif",
                whiteSpace: "nowrap",
              }}
            >Average</button>
          )}
          {sessionCupperIds.map(id => (
            <button
              key={id}
              onClick={() => setSessionTab(id)}
              style={{
                padding: "7px 13px", borderRadius: 8,
                border: `1px solid ${sessionTab === id ? "#000" : "#E5E7EB"}`,
                background: sessionTab === id ? "#000" : "#fff",
                color: sessionTab === id ? "#fff" : "#374151",
                fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif",
                whiteSpace: "nowrap",
              }}
            >{sessionCupperLabel(id)}</button>
          ))}
          <GuestCupperChip
            onInvite={async (email) => { await inviteGuestCupper(email); }}
          />
          {sessionTab !== "avg" && (() => {
            // Session-wide exclude: count how many samples currently
            // have this cupper's record marked excluded. If all of them
            // are excluded, the toggle re-includes everywhere; otherwise
            // it excludes everywhere.
            const recs = coffees
              .map(co => (co.cuppings || []).find(x => x.cupperId === sessionTab))
              .filter(Boolean);
            const excludedCount = recs.filter(r => r.excludedFromAverage).length;
            const allExcluded = recs.length > 0 && excludedCount === recs.length;
            const partiallyExcluded = excludedCount > 0 && !allExcluded;
            const excludeAll = async () => {
              try {
                const nextCoffees = coffees.map(co => ({
                  ...co,
                  cuppings: (co.cuppings || []).map(x =>
                    x.cupperId === sessionTab ? { ...x, excludedFromAverage: !allExcluded } : x
                  ),
                }));
                await onUpdateCoffees(c.id, nextCoffees);
              } catch (err) { alert(err.message || "Couldn't update"); }
            };
            return (
              <div style={{ marginLeft: "auto", display: "inline-flex", gap: 6, alignItems: "center" }}>
                <button
                  onClick={excludeAll}
                  title={allExcluded
                    ? "Currently excluded from the Average across the whole session — click to re-include everywhere"
                    : "Drop this cupper's scores from the Average on every sample in this session"}
                  style={{
                    padding: "6px 11px", borderRadius: 7,
                    border: `1px solid ${allExcluded || partiallyExcluded ? "#FCD34D" : "#E5E7EB"}`,
                    background: allExcluded || partiallyExcluded ? "#FFFBEB" : "#fff",
                    color: allExcluded || partiallyExcluded ? "#92400E" : "#374151",
                    fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                  }}
                >{allExcluded ? "Excluded — include" : partiallyExcluded ? `Excluded on ${excludedCount}/${recs.length} — exclude all` : "Exclude from average"}</button>
                <button
                  onClick={() => resendCupperInvite(sessionTab)}
                  style={{ padding: "6px 11px", borderRadius: 7, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                >Resend invite</button>
                <button
                  onClick={() => removeCupperFromSession(sessionTab)}
                  title="Remove this cupper from the entire session"
                  style={{ padding: "6px 11px", borderRadius: 7, border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#B91C1C", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                >Remove from session</button>
              </div>
            );
          })()}
        </div>
      {/* Session-level rollup */}
      <div style={{ background: "#F9FAFB", borderRadius: 10, padding: "14px 18px", marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4 }}>
            {coffees.length} sample{coffees.length === 1 ? "" : "s"} in this session
          </div>
          <div style={{ fontSize: 13, marginTop: 2, color: "#6B7280" }}>
            {scored.length === 0 ? "No samples scored yet" : (
              <>
                {scored.length === coffees.length ? "All scored" : `${scored.length} of ${coffees.length} scored`}
                {sessionAvg >= 80 && <> · {sessionAvg >= 85 ? <span className="pr-gold" style={{ fontWeight: 700 }}>{grade}</span> : grade}</>}
              </>
            )}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4 }}>Avg score</div>
          <div style={{ fontSize: 36, fontWeight: 800, color: scoreColor(sessionAvg), letterSpacing: "-0.02em", lineHeight: 1, marginTop: 2 }}>
            {scored.length === 0
              ? <span style={{ color: "#D1D5DB", fontSize: 24, fontWeight: 600 }}>—</span>
              : (sessionAvg >= 85 ? <span className="pr-gold">{sessionAvg.toFixed(2)}</span> : sessionAvg.toFixed(2))}
          </div>
        </div>
      </div>

      {/* Session notes */}
      {c.notes && (
        <div style={{ padding: "12px 14px", background: "#FAFAFA", borderLeft: "3px solid #2C5766", borderRadius: "0 8px 8px 0", fontSize: 14, color: "#374151", lineHeight: 1.55, marginBottom: 18, whiteSpace: "pre-wrap" }}>{c.notes}</div>
      )}

      {/* Coffees list */}
      {coffees.length === 0 ? (
        <div style={{ background: "#F9FAFB", borderRadius: 10, padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 14, border: "1px dashed #E5E7EB" }}>
          <div style={{ marginBottom: 12 }}>No samples yet.</div>
          <button
            onClick={() => setEditingCoffee("new")}
            style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #000000", background: "#000000", color: "#fff", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
          ><Plus size={12} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 6 }} />Add the first sample</button>
        </div>
      ) : (() => {
        // Same code used in the side-nav and the All-sessions table —
        // pulled from the module-level helper so the labels match
        // everywhere.
        const sessionPrefix = getSessionCode(c);
        return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {coffees.map((coffee, sampleIdx) => {
            const isOpen = expandedCoffeeId === coffee.id;
            const sampleAutoId = `${sessionPrefix}-${String(sampleIdx + 1).padStart(2, "0")}`;
            // Recompute the SCA total locally — only count the coffee
            // as scored once at least one primary attribute is set, so
            // unscored coffees don't summed-up to 30 from the secondary
            // defaults.
            // Score reflects the active session-tab: Average shows the
            // consolidated total (admin override or cupper average);
            // a specific cupper tab shows just that cupper's score.
            const coTotal = sessionTab === "avg"
              ? sampleLiveTotal(coffee)
              : (() => {
                  const r = (coffee.cuppings || []).find(x => x.cupperId === sessionTab);
                  return r ? totalFromScoreRecord(r) : 0;
                })();
            // SCA grade bands.
            const coGrade = coTotal >= 90 ? "Outstanding"
              : coTotal >= 85 ? "Excellent"
              : coTotal >= 80 ? "Very Good"
              : coTotal > 0 ? "Below Specialty"
              : "Unscored";
            const coDescriptors = coffee.descriptors || {};
            const chips = [
              ...(coDescriptors.fragrance || "").split(",").map(s => s.trim()).filter(Boolean),
              ...(coDescriptors.flavor || "").split(",").map(s => s.trim()).filter(Boolean),
              ...(coDescriptors.aftertaste || "").split(",").map(s => s.trim()).filter(Boolean),
            ];
            return (
              <div key={coffee.id} style={{ border: "1px solid #E5E7EB", borderRadius: 10, overflow: "hidden", background: "#fff" }}>
                {/* Coffee row header */}
                <div
                  onClick={() => setExpandedCoffeeId(isOpen ? null : coffee.id)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", cursor: "pointer", userSelect: "none" }}
                >
                  <ChevronRight
                    size={14}
                    strokeWidth={2}
                    color="#9CA3AF"
                    style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s", flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{
                        display: "inline-block",
                        background: "#111827", color: "#fff",
                        borderRadius: 99, padding: "2px 9px",
                        fontSize: 11, fontWeight: 700,
                        letterSpacing: "0.02em", flexShrink: 0,
                        fontVariantNumeric: "tabular-nums",
                      }}>{sampleAutoId}</span>
                      <span style={{ fontSize: 15, fontWeight: 600, color: "#000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {coffee.coffeeName || <span style={{ color: "#9CA3AF" }}>(unnamed)</span>}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>
                      {(() => {
                        const parts = [];
                        if (coffee.sourcedFrom) parts.push(coffee.sourcedFrom);
                        if (coffee.roastDate) parts.push(`Roasted ${new Date(coffee.roastDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}`);
                        return parts.length > 0 ? parts.join(" — ") : <span style={{ fontStyle: "italic" }}>No source · roast date set</span>;
                      })()}
                    </div>
                  </div>
                  {/* Approve / Reject — calmer treatment: pending = quiet
                      gray pair, active = soft tinted background instead
                      of saturated black/red. Removes the 4th-of-July
                      stack when many rows are scored. */}
                  {(() => {
                    const st = coffee.status || "pending";
                    const styleFor = (kind, active) => {
                      const tint = kind === "approve"
                        ? { bg: "#ECFDF5", border: "#BBF7D0", fg: "#047857" }
                        : { bg: "#FEF2F2", border: "#FECACA", fg: "#B91C1C" };
                      return {
                        display: "inline-flex", alignItems: "center", gap: 5,
                        padding: "5px 12px", borderRadius: 6,
                        border: `1px solid ${active ? tint.border : "#E5E7EB"}`,
                        background: active ? tint.bg : "transparent",
                        color: active ? tint.fg : "#9CA3AF",
                        fontSize: 13, fontWeight: 500,
                        cursor: "pointer", fontFamily: "Inter, sans-serif",
                        transition: "background 0.12s, color 0.12s, border-color 0.12s",
                      };
                    };
                    return (
                      <div onClick={e => e.stopPropagation()} style={{ display: "inline-flex", gap: 6, flexShrink: 0 }}>
                        <button
                          onClick={() => setCoffeeStatus(coffee.id, st === "approved" ? "pending" : "approved")}
                          title={st === "approved" ? "Approved — click to reset" : "Approve"}
                          style={styleFor("approve", st === "approved")}
                        ><Check size={12} strokeWidth={2} /> Approve</button>
                        <button
                          onClick={() => setCoffeeStatus(coffee.id, st === "rejected" ? "pending" : "rejected")}
                          title={st === "rejected" ? "Rejected — click to reset" : "Reject"}
                          style={styleFor("reject", st === "rejected")}
                        ><X size={12} strokeWidth={2} /> Reject</button>
                      </div>
                    );
                  })()}
                  {/* Fixed width so the Approve/Reject pair to the
                      left lines up across rows regardless of how long
                      the grade label is ("Very Good" vs "Outstanding"
                      vs "Below Specialty"). */}
                  <div style={{ textAlign: "right", flexShrink: 0, width: 120 }}>
                    {coTotal > 0 ? (
                      <>
                        <div style={{ fontSize: 22, fontWeight: 800, color: scoreColor(coTotal), letterSpacing: "-0.01em", lineHeight: 1 }}>
                          {coTotal >= 85 ? <span className="pr-gold">{coTotal.toFixed(2)}</span> : coTotal.toFixed(2)}
                        </div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: scoreColor(coTotal), textTransform: "uppercase", letterSpacing: "0.07em", marginTop: 2 }}>
                          {coGrade}
                        </div>
                      </>
                    ) : (
                      <span style={{ fontSize: 12, color: "#9CA3AF", fontStyle: "italic" }}>Not scored</span>
                    )}
                  </div>
                </div>

                {/* Expanded view — tabs (Main + cuppers + add) + read /
                    edit modes + actions row */}
                {isOpen && (() => {
                  // Per-sample tabs are gone; the session-level tab
                  // chooses which record to show inside every sample.
                  const activeTab = sessionTab === "avg" ? "main" : sessionTab;
                  const cuppingsList = coffee.cuppings || [];
                  // Main is editable by default while it has nothing to
                  // show — no override on the coffee, no per-cupper
                  // records yet. Skips the Edit-button → fields-go-live
                  // dance for the empty-state cup.
                  const mainIsEmpty = activeTab === "main"
                    && !coffee.scores
                    && (coffee.cuppings || []).length === 0
                    && isAdmin;
                  const explicitlyEditing = editingTabKey === `${coffee.id}::${activeTab}`;
                  const editing = explicitlyEditing || mainIsEmpty;
                  // Main tab is the admin override; only admins can
                  // edit it. Per-cupper tabs stay open to anyone.
                  const canEditThisTab = activeTab === "main" ? isAdmin : true;
                  const activeCupping = activeTab !== "main"
                    ? cuppingsList.find(x => x.cupperId === activeTab)
                    : null;
                  const isExcluded = !!activeCupping?.excludedFromAverage;
                  // Resolve the record being viewed/edited.
                  let record = readScoreRecord(coffee, activeTab);
                  let usingAverage = false;
                  if (activeTab === "main" && !record.scores && cuppingsList.length > 0) {
                    const avg = computeCuppingsAverage(coffee);
                    if (avg) { record = { scores: avg.scores, descriptors: null, defects: avg.defects, notes: "" }; usingAverage = true; }
                  }
                  // Pending edits live here while the user has the form
                  // open; on Save we flush them through saveCoffee.
                  const stage = (next) => {
                    pendingPatchRef.current[`${coffee.id}::${activeTab}`] = next;
                  };
                  const enterEdit = () => {
                    pendingPatchRef.current[`${coffee.id}::${activeTab}`] = {
                      ...coffee,
                      scores: record.scores,
                      descriptors: record.descriptors,
                      defects: record.defects,
                      notes: record.notes,
                    };
                    setEditingTabKey(`${coffee.id}::${activeTab}`);
                  };
                  const cancelEdit = () => {
                    delete pendingPatchRef.current[`${coffee.id}::${activeTab}`];
                    setEditingTabKey(null);
                  };
                  const commitEdit = async () => {
                    const next = pendingPatchRef.current[`${coffee.id}::${activeTab}`];
                    if (!next) { setEditingTabKey(null); return; }
                    const patch = {
                      scores: next.scores,
                      descriptors: next.descriptors,
                      defects: next.defects,
                      notes: next.notes,
                    };
                    let updated = writeScoreRecord(coffee, activeTab, patch);
                    // Basics edits are always written through (only the
                    // Main tab shows them as editable; per-cupper tabs
                    // render them locked).
                    if (activeTab === "main") {
                      updated = {
                        ...updated,
                        coffeeName: next.coffeeName ?? updated.coffeeName,
                        roastDate: next.roastDate ?? updated.roastDate,
                        sourcedFrom: next.sourcedFrom ?? updated.sourcedFrom,
                        expectedNotes: next.expectedNotes ?? updated.expectedNotes,
                        costPerLb: next.costPerLb !== undefined ? next.costPerLb : updated.costPerLb,
                        pricePerLb: next.pricePerLb !== undefined ? next.pricePerLb : updated.pricePerLb,
                      };
                    }
                    try { await saveCoffee(updated); }
                    catch (err) { alert(err.message || "Couldn't save"); }
                    delete pendingPatchRef.current[`${coffee.id}::${activeTab}`];
                    setEditingTabKey(null);
                  };
                  return (
                  <div style={{ borderTop: "1px solid #F3F4F6", padding: "16px 18px 18px", background: "#FAFAFA" }}>
                    {/* Action row — sits at the top of the card so the
                        primary verbs (Edit / Print / Exclude / Remove)
                        are the first thing reachable. */}
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginBottom: 14, flexWrap: "wrap" }}>
                      {coTotal > 0 && !editing && (
                        <button
                          onClick={() => printScorecard(coffee)}
                          style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 5 }}
                        ><ClipboardList size={11} strokeWidth={1.8} /> Print</button>
                      )}
                      {!editing && canEditThisTab && (
                        <button
                          onClick={enterEdit}
                          style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 5 }}
                        ><Pencil size={11} strokeWidth={1.8} /> Edit</button>
                      )}
                      {!editing && !canEditThisTab && activeTab === "main" && (
                        <span style={{ padding: "6px 12px", fontSize: 12, color: "#9CA3AF", fontStyle: "italic", fontFamily: "Inter, sans-serif" }}>
                          Admin only — Main is the consolidated override
                        </span>
                      )}
                      {editing && (
                        <>
                          {explicitlyEditing && (
                            <button
                              onClick={cancelEdit}
                              style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                            >Cancel</button>
                          )}
                          <button
                            onClick={commitEdit}
                            style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                          >Save</button>
                        </>
                      )}
                      {!editing && (
                        <button
                          onClick={() => removeCoffee(coffee.id)}
                          title="Remove sample from session"
                          aria-label="Remove sample"
                          style={{ width: 32, height: 32, padding: 0, borderRadius: 6, border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#B91C1C", cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                        ><Trash2 size={13} strokeWidth={1.8} /></button>
                      )}
                    </div>
                    {usingAverage && (() => {
                      const counted = cuppingsList.filter(x => x.scores && !x.excludedFromAverage).length;
                      const skipped = cuppingsList.filter(x => x.scores && x.excludedFromAverage).length;
                      return (
                        <div style={{ marginBottom: 12, fontSize: 11, color: "#9CA3AF", fontStyle: "italic" }}>
                          Average of {counted} cupper{counted === 1 ? "" : "s"}
                          {skipped > 0 ? ` · ${skipped} excluded` : ""}
                          {isAdmin ? " · Edit to override" : ""}
                        </div>
                      );
                    })()}

                    {/* Editor / read-only view */}
                    <CoffeeInlineEditor
                      key={`${coffee.id}::${activeTab}::${editing ? "edit" : "view"}`}
                      coffee={coffee}
                      record={record}
                      sources={sources}
                      readOnly={!editing}
                      hideBasics
                      onPatch={stage}
                    />
                  </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
        );
      })()}
      {/* Inline action footer (status + delete) */}
      <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid #F3F4F6" }}>
        {footer}
      </div>
    </div>
    {editingCoffee && (
      <CoffeeScoreModal
        coffee={editingCoffee === "new" ? null : editingCoffee}
        onClose={() => setEditingCoffee(null)}
        onSave={saveCoffee}
        coffeeSuggestions={(() => {
          // Hide names that are already in THIS session so the operator
          // doesn't accidentally duplicate a sample. Keep all suggestions
          // when editing an existing entry — they're correcting that
          // sample and might want to keep its own name.
          const current = new Set(
            (cupping?.coffees || []).map(c => String(c.coffeeName || "").toLowerCase())
          );
          if (editingCoffee === "new") {
            return coffeeSuggestions.filter(n => !current.has(String(n || "").toLowerCase()));
          }
          // Editing — leave the list intact, but allow the existing name
          // to stay (it's already excluded from `current` filter not
          // applied here).
          return coffeeSuggestions;
        })()}
        sources={sources}
      />
    )}
    </>
  );
}

/* ═══════════════════════ Team Members card ═══════════════════════
   Admin-only Settings card. Lists current users, lets admin invite new
   ones, change roles, and remove teammates. Hits /api/users (CRUD). */
/* ─── Roles & permissions card ────────────────────────────────────
   Settings → Team includes this card so admins can audit which built-
   in role grants which capability, and define custom roles with
   tailored capability bundles. Custom roles persist to localStorage
   for V1 (per-workspace, per-browser); promoting them to a server-
   backed store is a follow-up so multiple admins on the same
   workspace see the same role set.

   The capability registry is sourced from useAuth's CAPABILITY_TO_ROLES
   so the UI never drifts from the actual auth lookup. Built-in roles
   are read-only — flipping a built-in capability would diverge from
   the source-of-truth in code; custom roles inherit from a base and
   then add/remove capabilities on top. */
const CAPABILITY_GROUPS = [
  { label: "Pages", caps: ["page:orders", "page:production", "page:inventory", "page:cupping", "page:products", "page:purchasing", "page:shipping", "page:wholesale", "page:analytics", "page:settings"] },
  { label: "Production", caps: ["prod:breakdown", "prod:activity", "prod:packing"] },
  { label: "Orders & shipping", caps: ["orders:schedule", "orders:mark-shipped", "orders:skip", "shipping:label", "shipping:routes"] },
  { label: "Inventory & cupping", caps: ["inventory:write", "inventory:delete", "cupping:write"] },
  { label: "Settings & admin", caps: ["settings:connectors", "settings:team", "settings:preferences", "settings:pricing", "settings:profile", "fsma:export"] },
];

const CAPABILITY_LABELS = {
  "page:orders": "View Orders",
  "page:production": "View Production",
  "page:inventory": "View Inventory",
  "page:cupping": "View Cupping & QC",
  "page:products": "View Coffees & Blends",
  "page:purchasing": "View Finance",
  "page:shipping": "View Shipping",
  "page:wholesale": "View Wholesale",
  "page:analytics": "View Analytics",
  "page:settings": "View Settings",
  "prod:breakdown": "Production breakdown",
  "prod:activity": "Production activity",
  "prod:packing": "Packing workflow",
  "orders:schedule": "Schedule orders",
  "orders:mark-shipped": "Mark orders shipped",
  "orders:skip": "Skip orders",
  "shipping:label": "Buy shipping labels",
  "shipping:routes": "Plan delivery routes",
  "inventory:write": "Edit inventory",
  "inventory:delete": "Delete lots",
  "cupping:write": "Score & manage cuppings",
  "settings:connectors": "Manage integrations",
  "settings:team": "Manage team members",
  "settings:preferences": "Workspace preferences",
  "settings:pricing": "Pricing & billing",
  "settings:profile": "Edit own profile",
  "fsma:export": "Export FSMA traceability",
};

function RolesCard() {
  const ALL_CAPS = useMemo(() => Object.keys(CAPABILITY_TO_ROLES), []);
  // Built-in role → list of caps (derived from CAPABILITY_TO_ROLES).
  const builtInRoles = useMemo(() => {
    const out = {};
    for (const role of ["admin", "roaster", "packer", "cupper", "delivery", "inventory"]) {
      out[role] = ALL_CAPS.filter(c => (CAPABILITY_TO_ROLES[c] || []).includes(role));
    }
    return out;
  }, [ALL_CAPS]);

  const [customRoles, setCustomRoles] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem("pr:customRoles");
      if (raw) return JSON.parse(raw) || [];
    } catch {}
    return [];
  });
  useEffect(() => {
    try { window.localStorage.setItem("pr:customRoles", JSON.stringify(customRoles)); } catch {}
  }, [customRoles]);

  const [editing, setEditing] = useState(null); // null | "new" | role obj
  const [expandedRole, setExpandedRole] = useState(null);

  const startNew = () => setEditing({ id: null, name: "", description: "", baseRole: "roaster", capabilities: [...(builtInRoles.roaster || [])] });
  const saveRole = (role) => {
    if (!role.name?.trim()) return;
    if (role.id) {
      setCustomRoles(rs => rs.map(r => r.id === role.id ? role : r));
    } else {
      setCustomRoles(rs => [...rs, { ...role, id: `role-${Date.now().toString(36)}` }]);
    }
    setEditing(null);
  };
  const removeRole = (id) => {
    if (!window.confirm("Delete this role? Users assigned to it fall back to their built-in role.")) return;
    setCustomRoles(rs => rs.filter(r => r.id !== id));
  };

  const renderRoleCard = (id, name, description, caps, opts = {}) => {
    const isExpanded = expandedRole === id;
    return (
      <div key={id} style={{ border: "1px solid #F3F4F6", borderRadius: 8, marginBottom: 8, overflow: "hidden" }}>
        <button
          onClick={() => setExpandedRole(isExpanded ? null : id)}
          style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center", padding: "12px 14px", background: isExpanded ? "#FAFAFA" : "transparent", border: "none", width: "100%", textAlign: "left", cursor: "pointer", fontFamily: "Inter, sans-serif" }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#000" }}>{name}</span>
              {opts.builtIn && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", background: "#F4F4F5", border: "1px solid #E4E4E7", borderRadius: 99, padding: "2px 7px" }}>Built-in</span>}
              {!opts.builtIn && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2C5766", background: "#EAF1F3", border: "1px solid #B8CED5", borderRadius: 99, padding: "2px 7px" }}>Custom</span>}
            </div>
            {description && <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2, lineHeight: 1.45 }}>{description}</div>}
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>{caps.length} permission{caps.length === 1 ? "" : "s"}</div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {!opts.builtIn && (
              <>
                <span
                  role="button" tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); setEditing(opts.role); }}
                  style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                >Edit</span>
                <span
                  role="button" tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); removeRole(id); }}
                  style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#B91C1C", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                >Delete</span>
              </>
            )}
            <span style={{ fontSize: 12, color: "#9CA3AF" }}>{isExpanded ? "▼" : "▶"}</span>
          </div>
        </button>
        {isExpanded && (
          <div style={{ padding: "14px 16px", background: "#FAFAFA", borderTop: "1px solid #F3F4F6" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
              {CAPABILITY_GROUPS.map(group => (
                <div key={group.label}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{group.label}</div>
                  {group.caps.map(c => {
                    const has = caps.includes(c);
                    return (
                      <div key={c} style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 0", fontSize: 12, color: has ? "#000" : "#9CA3AF" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, borderRadius: 3, border: `1px solid ${has ? "#000" : "#D1D5DB"}`, background: has ? "#000" : "transparent" }}>
                          {has && <Check size={9} color="#fff" strokeWidth={3} />}
                        </span>
                        {CAPABILITY_LABELS[c] || c}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 24, marginTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Shield size={20} color="#6B7280" strokeWidth={1.6} />
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 600, color: "#000" }}>Roles &amp; permissions</div>
              <div style={{ fontSize: 14, color: "#6B7280", marginTop: 1 }}>Built-in roles plus custom roles you define for your team.</div>
            </div>
          </div>
          <button
            onClick={startNew}
            style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6 }}
          ><Plus size={13} strokeWidth={1.8} /> Custom role</button>
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Built-in</div>
        {Object.entries(builtInRoles).map(([key, caps]) =>
          renderRoleCard(`builtin:${key}`, ROLE_LABELS[key] || key, ROLE_DESCRIPTIONS[key] || "", caps, { builtIn: true })
        )}

        {customRoles.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em", margin: "16px 0 8px" }}>Custom</div>
            {customRoles.map(r =>
              renderRoleCard(r.id, r.name, r.description, r.capabilities || [], { role: r })
            )}
          </>
        )}

        <div style={{ marginTop: 14, padding: "10px 12px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, fontSize: 12, color: "#92400E", lineHeight: 1.5 }}>
          <strong>Note:</strong> custom roles are saved to your browser. Server-side enforcement (so a custom role actually unlocks the right pages for a teammate) lands in the next release — for now this card is the source of truth for documenting access expectations.
        </div>
      </div>

      {editing && (
        <RoleEditModal
          role={editing}
          allCaps={ALL_CAPS}
          builtInRoles={builtInRoles}
          onSave={saveRole}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function RoleEditModal({ role, allCaps, builtInRoles, onSave, onClose }) {
  const [form, setForm] = useState(role);
  const toggleCap = (c) => setForm(f => ({
    ...f,
    capabilities: f.capabilities.includes(c)
      ? f.capabilities.filter(x => x !== c)
      : [...f.capabilities, c],
  }));
  const setBase = (baseKey) => {
    const baseCaps = builtInRoles[baseKey] || [];
    setForm(f => ({ ...f, baseRole: baseKey, capabilities: baseCaps }));
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <div style={{ width: "100%", maxWidth: 640, maxHeight: "90vh", background: "#fff", borderRadius: 14, border: "1px solid #E4E4E7", overflow: "hidden", display: "flex", flexDirection: "column", fontFamily: "Inter, sans-serif" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 24px 12px", borderBottom: "1px solid #F3F4F6" }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: "#000" }}>{role.id ? "Edit role" : "New custom role"}</div>
          <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>Pick a starting role, then add or remove capabilities.</div>
        </div>
        <div style={{ padding: "16px 24px", overflowY: "auto", flex: 1 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: "#6B7280", marginBottom: 6 }}>Name</label>
          <input
            type="text"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Cupping Lead"
            autoFocus
            style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, color: "#000", outline: "none", boxSizing: "border-box", marginBottom: 14 }}
          />
          <label style={{ display: "block", fontSize: 12, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: "#6B7280", marginBottom: 6 }}>Description</label>
          <input
            type="text"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="What this role does"
            style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, color: "#000", outline: "none", boxSizing: "border-box", marginBottom: 14 }}
          />
          <label style={{ display: "block", fontSize: 12, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: "#6B7280", marginBottom: 6 }}>Start from</label>
          <select
            value={form.baseRole}
            onChange={e => setBase(e.target.value)}
            style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, color: "#000", outline: "none", boxSizing: "border-box", marginBottom: 16, background: "#fff", cursor: "pointer" }}
          >
            {Object.keys(builtInRoles).map(k => <option key={k} value={k}>{ROLE_LABELS[k] || k}</option>)}
          </select>

          <div style={{ fontSize: 12, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: "#6B7280", marginBottom: 8 }}>Permissions</div>
          {CAPABILITY_GROUPS.map(group => (
            <div key={group.label} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{group.label}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                {group.caps.map(c => {
                  const has = form.capabilities.includes(c);
                  return (
                    <label key={c} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", borderRadius: 6, cursor: "pointer", background: has ? "#F4F4F5" : "transparent", fontSize: 13 }}>
                      <input type="checkbox" checked={has} onChange={() => toggleCap(c)} style={{ accentColor: "#000" }} />
                      <span style={{ color: "#000" }}>{CAPABILITY_LABELS[c] || c}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: "12px 24px", borderTop: "1px solid #F3F4F6", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
          <button onClick={() => onSave(form)} disabled={!form.name?.trim()} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #000", background: form.name?.trim() ? "#000" : "#F4F4F5", color: form.name?.trim() ? "#fff" : "#9CA3AF", fontSize: 13, fontWeight: 600, cursor: form.name?.trim() ? "pointer" : "default", fontFamily: "Inter, sans-serif" }}>Save role</button>
        </div>
      </div>
    </div>
  );
}

function TeamMembersCard({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("roaster");
  const [inviting, setInviting] = useState(false);
  const [lastInvite, setLastInvite] = useState(null); // { email }

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await fetch("/api/users", { credentials: "same-origin" });
      if (!r.ok) throw new Error(`Failed to load (${r.status})`);
      const body = await r.json();
      setUsers(body.users || []);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true); setErr(""); setLastInvite(null);
    try {
      const r = await fetch("/api/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ email: inviteEmail.trim(), name: inviteName.trim(), role: inviteRole }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Invite failed (${r.status})`);
      setInviteEmail(""); setInviteName(""); setInviteRole("roaster"); setShowInvite(false);
      setLastInvite({ email: body.user.email });
      load();
    } catch (e) { setErr(e.message); } finally { setInviting(false); }
  };

  const changeRole = async (id, role) => {
    setErr("");
    try {
      const r = await fetch(`/api/users/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ role }),
      });
      if (!r.ok) { const body = await r.json().catch(() => ({})); throw new Error(body.error || `Update failed (${r.status})`); }
      load();
    } catch (e) { setErr(e.message); }
  };

  const removeUser = async (u) => {
    if (!confirm(`Remove ${u.name || u.email} from the team? They'll lose access immediately.`)) return;
    setErr("");
    try {
      const r = await fetch(`/api/users/${u.id}`, { method: "DELETE", credentials: "same-origin" });
      if (!r.ok) { const body = await r.json().catch(() => ({})); throw new Error(body.error || `Remove failed (${r.status})`); }
      load();
    } catch (e) { setErr(e.message); }
  };

  // Transfer the super-admin role from the current user to another
  // teammate. Caller drops to plain admin.
  const transferSuperAdmin = async (u) => {
    if (!confirm(`Pass super-admin to ${u.name || u.email}?\nYou'll be demoted to admin and keep team access. They become the new owner.`)) return;
    setErr("");
    try {
      const r = await fetch(`/api/users/${u.id}/transfer-super-admin`, {
        method: "POST", credentials: "same-origin",
      });
      if (!r.ok) { const body = await r.json().catch(() => ({})); throw new Error(body.error || `Transfer failed (${r.status})`); }
      load();
    } catch (e) { setErr(e.message); }
  };

  // Toggle a plain admin's access to the Team page. Super-admin only.
  const toggleTeamAccess = async (u) => {
    setErr("");
    try {
      const r = await fetch(`/api/users/${u.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ teamAccess: !u.teamAccess }),
      });
      if (!r.ok) { const body = await r.json().catch(() => ({})); throw new Error(body.error || `Update failed (${r.status})`); }
      load();
    } catch (e) { setErr(e.message); }
  };

  const isSuperAdmin = currentUser?.role === "super-admin";

  return (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 24, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: "#EEF2FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Shield size={20} color="#4F46E5" strokeWidth={1.6} />
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 600, color: "#000000" }}>Team members</div>
            <div style={{ fontSize: 13, color: "#6B7280", marginTop: 1 }}>Who can sign in, and what they can see.</div>
          </div>
        </div>
        <button
          onClick={() => { setShowInvite(v => !v); setLastInvite(null); setErr(""); }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 14px", borderRadius: 8, border: "none",
            background: showInvite ? "#F3F4F6" : "#000000",
            color: showInvite ? "#374151" : "#fff",
            fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif",
          }}
        >
          {showInvite ? <>✕ Cancel</> : <><UserPlus size={14} strokeWidth={1.8} /> Invite teammate</>}
        </button>
      </div>

      {showInvite && (
        <form onSubmit={invite} style={{ padding: 14, background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10, marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <label style={teamLabel}>Email</label>
              <input
                type="email" required placeholder="teammate@yourroastery.com"
                value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                style={teamInput}
              />
            </div>
            <div>
              <label style={teamLabel}>Name</label>
              <input
                type="text" placeholder="Optional"
                value={inviteName} onChange={e => setInviteName(e.target.value)}
                style={teamInput}
              />
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={teamLabel}>Role</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginTop: 6 }}>
              {INVITABLE_ROLES.map(r => {
                const active = inviteRole === r;
                return (
                  <button
                    type="button"
                    key={r}
                    onClick={() => setInviteRole(r)}
                    style={{
                      padding: "10px 12px", borderRadius: 8, cursor: "pointer",
                      border: active ? "1.5px solid #000000" : "1px solid #E5E7EB",
                      background: active ? "#000000" : "#fff",
                      color: active ? "#fff" : "#000000",
                      fontFamily: "Inter, sans-serif", textAlign: "left",
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{ROLE_LABELS[r]}</div>
                    <div style={{ fontSize: 12, color: active ? "rgba(255,255,255,0.75)" : "#6B7280", lineHeight: 1.4 }}>{ROLE_DESCRIPTIONS[r]}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <button
            type="submit" disabled={inviting || !inviteEmail.trim()}
            style={{
              padding: "9px 16px", borderRadius: 8, border: "1px solid #000000",
              background: inviting ? "#9CA3AF" : "#000000", color: "#fff",
              fontSize: 14, fontWeight: 600, cursor: inviting ? "default" : "pointer",
              fontFamily: "Inter, sans-serif",
            }}
          >
            {inviting ? "Adding…" : "Add teammate"}
          </button>
        </form>
      )}

      {lastInvite && (
        <div style={{
          padding: "10px 14px", borderRadius: 8, marginBottom: 12,
          background: "#ECFDF5", border: "1px solid #A7F3D0", color: "#065F46", fontSize: 13, lineHeight: 1.5,
        }}>
          <strong>{lastInvite.email}</strong> has been invited. They can now sign in at{" "}
          <code style={{ fontSize: 12, background: "#fff", padding: "2px 5px", borderRadius: 4, border: "1px solid #A7F3D0" }}>
            {typeof window !== "undefined" ? window.location.origin : ""}
          </code>{" "}
          by clicking <strong>Continue with Google</strong> using this email.
        </div>
      )}

      {err && (
        <div style={{
          padding: "10px 14px", borderRadius: 8, marginBottom: 12,
          background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontSize: 13, lineHeight: 1.5,
        }}>{err}</div>
      )}

      {loading ? (
        <div style={{ padding: "20px 4px", color: "#9CA3AF", fontSize: 14 }}>Loading team…</div>
      ) : users.length === 0 ? (
        <div style={{ padding: "20px 4px", color: "#9CA3AF", fontSize: 14 }}>No team members yet.</div>
      ) : (
        <div style={{ border: "1px solid #F3F4F6", borderRadius: 10, overflow: "hidden" }}>
          {users.map((u, i) => (
            <div
              key={u.id}
              style={{
                display: "grid",
                gridTemplateColumns: "36px 1fr 140px 80px",
                alignItems: "center", gap: 12,
                padding: "12px 14px",
                borderBottom: i === users.length - 1 ? "none" : "1px solid #F3F4F6",
                background: "#fff",
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "#EEF2FF", color: "#4F46E5",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 600,
              }}>
                {avatarInitials(u)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#000000", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {u.name || u.email}
                  {u.id === currentUser?.id && (
                    <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: "#6B7280", background: "#F3F4F6", padding: "2px 6px", borderRadius: 99 }}>you</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2, display: "flex", gap: 8, alignItems: "center" }}>
                  <span>{u.email}</span>
                  {u.googleLinked && <span style={{ fontSize: 11, color: "#4F46E5" }}>• Google linked</span>}
                  {u.lastLoginAt
                    ? <span>• last seen {new Date(u.lastLoginAt).toLocaleDateString()}</span>
                    : <span style={{ color: "#D97706" }}>• pending first sign-in</span>}
                </div>
              </div>
              {u.role === "super-admin" ? (
                <span style={{
                  padding: "7px 10px", borderRadius: 8,
                  background: "#FEF3C7", color: "#92400E",
                  border: "1px solid #FDE68A",
                  fontSize: 12, fontWeight: 700, textAlign: "center",
                  textTransform: "uppercase", letterSpacing: 0.4,
                  fontFamily: "Inter, sans-serif",
                }}>Super-admin</span>
              ) : (
                <select
                  value={u.role}
                  onChange={(e) => changeRole(u.id, e.target.value)}
                  disabled={u.id === currentUser?.id}
                  title={u.id === currentUser?.id ? "You can't change your own role" : "Change role"}
                  style={{
                    padding: "7px 10px", borderRadius: 8, border: "1px solid #E5E7EB",
                    background: "#fff", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#000000",
                    cursor: u.id === currentUser?.id ? "not-allowed" : "pointer",
                    opacity: u.id === currentUser?.id ? 0.6 : 1,
                  }}
                >
                  {INVITABLE_ROLES.map(r => (
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  ))}
                </select>
              )}
              <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
                {/* Super-admins can hand power to another teammate.
                    Visible only on rows other than your own when you
                    yourself are the super-admin. */}
                {isSuperAdmin && u.id !== currentUser?.id && u.role !== "super-admin" && (
                  <button
                    onClick={() => transferSuperAdmin(u)}
                    title={`Pass super-admin to ${u.name || u.email}`}
                    style={{
                      padding: "7px 10px", borderRadius: 8,
                      border: "1px solid #FDE68A", background: "#FEFCE8",
                      color: "#92400E", fontSize: 12, fontWeight: 600,
                      cursor: "pointer", fontFamily: "Inter, sans-serif",
                    }}
                  >Make super</button>
                )}
                {/* Team-access toggle for plain admins. Only the
                    super-admin can flip it. */}
                {isSuperAdmin && u.role === "admin" && (
                  <button
                    onClick={() => toggleTeamAccess(u)}
                    title={u.teamAccess ? "Revoke Team-page access" : "Grant Team-page access"}
                    style={{
                      padding: "7px 10px", borderRadius: 8,
                      border: `1px solid ${u.teamAccess ? "#86EFAC" : "#E5E7EB"}`,
                      background: u.teamAccess ? "#ECFDF5" : "#fff",
                      color: u.teamAccess ? "#047857" : "#374151",
                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >{u.teamAccess ? "Team ✓" : "Grant team"}</button>
                )}
                {u.role !== "super-admin" && (
                  <button
                    onClick={() => removeUser(u)}
                    disabled={u.id === currentUser?.id}
                    title={u.id === currentUser?.id ? "You can't remove yourself" : "Remove from team"}
                    style={{
                      padding: "7px 10px", borderRadius: 8,
                      border: "1px solid #FCA5A5",
                      background: "#fff",
                      color: "#DC2626", fontSize: 13, fontWeight: 500,
                      cursor: u.id === currentUser?.id ? "not-allowed" : "pointer",
                      opacity: u.id === currentUser?.id ? 0.4 : 1,
                      fontFamily: "Inter, sans-serif",
                    }}
                  >Remove</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <CuppingGuestsList />
    </div>
  );
}

/* List of cupping invite recipients, surfaced on the Team page as
   "Cupping Guests". They're ephemeral — not real workspace users — so
   they don't count against the plan user limit. */
function CuppingGuestsList() {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/guests", { credentials: "same-origin" });
        if (r.ok) {
          const body = await r.json();
          if (!cancelled) setGuests(body.guests || []);
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);
  if (loading || guests.length === 0) return null;
  return (
    <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid #F3F4F6" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>Cupping guests</div>
        <div style={{ fontSize: 11, color: "#9CA3AF" }}>Don't count against your plan</div>
      </div>
      <div style={{ border: "1px solid #F3F4F6", borderRadius: 10, overflow: "hidden" }}>
        {guests.map((g, i) => (
          <div key={`${g.cupperId}-${g.sessionId}`} style={{
            display: "grid", gridTemplateColumns: "1fr 200px 100px",
            alignItems: "center", gap: 12, padding: "10px 14px",
            borderBottom: i === guests.length - 1 ? "none" : "1px solid #F3F4F6",
            background: "#fff",
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#111", display: "flex", alignItems: "center", gap: 8 }}>
                {g.name || g.email}
                <span style={{ fontSize: 10, fontWeight: 700, color: "#92400E", background: "#FEF3C7", padding: "2px 7px", borderRadius: 99, textTransform: "uppercase", letterSpacing: 0.4 }}>{g.typeLabel}</span>
              </div>
              <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
                {g.name ? <>{g.email} · </> : null}{g.sessionLabel}
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#6B7280" }}>
              Invited {g.sentAt ? new Date(g.sentAt).toLocaleDateString() : "—"}
            </div>
            <div style={{
              padding: "4px 9px", borderRadius: 99, fontSize: 11, fontWeight: 600,
              textTransform: "uppercase", letterSpacing: 0.4, textAlign: "center",
              background: g.status === "scored" ? "#ECFDF5" : g.status === "expired" ? "#F3F4F6" : "#FEF3C7",
              color:      g.status === "scored" ? "#047857" : g.status === "expired" ? "#6B7280" : "#92400E",
            }}>{g.status}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const teamLabel = { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 };
const teamInput = {
  width: "100%", boxSizing: "border-box", padding: "8px 12px", borderRadius: 8,
  border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, color: "#000000",
  fontFamily: "Inter, sans-serif", outline: "none",
};

/* Build 1–2-letter initials for the user avatar. Prefers the user's
   name; falls back to the email localpart. Returns "?" if nothing usable. */
function avatarInitials(user) {
  if (!user) return "?";
  const src = (user.name || user.email || "").trim();
  if (!src) return "?";
  if (user.name) {
    const parts = user.name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  return src.split("@")[0].slice(0, 2).toUpperCase();
}

/* Demo-only persona picker. Lists every user seeded into the demo
   workspace and lets the visitor swap session to any of them so
   notes/comments/logs get attributed to a different operator. Hides
   itself outside demo. */
function DemoUserSwitcher({ activeUser }) {
  const [open, setOpen] = useState(false);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || team) return;
    setLoading(true);
    fetch("/api/demo/team", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : null)
      .then(d => setTeam(d?.team || []))
      .catch(() => setTeam([]))
      .finally(() => setLoading(false));
  }, [open, team]);
  const switchTo = async (userId) => {
    if (userId === activeUser.id) { setOpen(false); return; }
    try {
      const r = await fetch("/api/demo/switch-user", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      if (r.ok) location.reload();
    } catch { /* ignore */ }
  };
  return (
    <div style={{ position: "relative", marginBottom: 4 }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="Switch demo persona"
        style={{
          width: 42, height: 28, borderRadius: 8, border: "none", cursor: "pointer",
          background: "rgba(34,211,238,0.12)", color: "#22D3EE",
          fontSize: 10, fontWeight: 700, fontFamily: "Inter, sans-serif",
          letterSpacing: "0.05em", textTransform: "uppercase",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        as<span style={{ marginLeft: 4 }}>↕</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 90 }} />
          <div style={{
            position: "absolute", left: 50, bottom: 0, zIndex: 91,
            background: "#fff", borderRadius: 10, border: "1px solid #E5E7EB",
            boxShadow: "0 12px 32px rgba(0,0,0,0.18)", minWidth: 220, padding: 6,
            fontFamily: "Inter, sans-serif",
          }}>
            <div style={{ padding: "6px 10px", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Switch demo persona
            </div>
            {loading && <div style={{ padding: 12, fontSize: 12, color: "#9CA3AF" }}>Loading…</div>}
            {team && team.length > 0 && team.map(u => (
              <button
                key={u.id}
                onClick={() => switchTo(u.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
                  padding: "8px 10px", borderRadius: 7, border: "none",
                  background: u.id === activeUser.id ? "#F4F4F5" : "transparent",
                  cursor: "pointer", fontFamily: "Inter, sans-serif",
                }}
                onMouseEnter={e => { if (u.id !== activeUser.id) e.currentTarget.style.background = "#FAFAFA"; }}
                onMouseLeave={e => { if (u.id !== activeUser.id) e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#000", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                  {avatarInitials(u)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#000" }}>{u.name || u.email}</div>
                  <div style={{ fontSize: 11, color: "#6B7280" }}>{ROLE_LABELS[u.role] || u.role}</div>
                </div>
                {u.id === activeUser.id && <Check size={14} strokeWidth={2.4} color="#22D3EE" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Shipping helpers ───────────────────────────────────────
   Small pure helpers used by ShipModal / BatchShipModal. Keeping
   them here (outside App) so they don't re-allocate on every render. */
function addressLooksComplete(a) {
  if (!a) return false;
  return Boolean(a.name && a.street1 && a.city && a.state && a.zip);
}
function formatAddress(a) {
  if (!a) return "";
  const l1 = a.name || "";
  const l2 = a.street1 + (a.street2 ? `, ${a.street2}` : "");
  const l3 = `${a.city}, ${a.state} ${a.zip}`;
  return [l1, l2, l3].filter(Boolean).join(" · ");
}
function parcelLabel(p) {
  if (!p) return "Parcel";
  const base = p.name || "Parcel";
  const dims = `${p.length}×${p.width}×${p.height}"`;
  const weight = `${p.weight} oz`;
  return `${base} — ${dims}, ${weight}`;
}

function ShipModal({ order, fromAddress, parcelPresets, onClose, onGetRates, onBuy, onGoToSettings }) {
  const [phase, setPhase] = useState("rates"); // rates | buying | bought | error
  const [selectedPreset, setSelectedPreset] = useState(parcelPresets[0] || null);
  const [rates, setRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState("");
  const [selectedRate, setSelectedRate] = useState(null);
  const [purchased, setPurchased] = useState(null);
  const [buyError, setBuyError] = useState("");

  const toAddress = order.shippingAddress;
  const canShip = addressLooksComplete(toAddress) && addressLooksComplete(fromAddress);

  const loadRates = async () => {
    if (!selectedPreset) return;
    setRatesLoading(true);
    setRatesError("");
    setRates([]);
    setSelectedRate(null);
    const res = await onGetRates({ order, parcel: selectedPreset });
    setRatesLoading(false);
    if (!res.ok) { setRatesError(res.error || "Rate lookup failed"); return; }
    const sorted = [...(res.rates || [])].sort((a, b) => parseFloat(a.rate) - parseFloat(b.rate));
    setRates(sorted);
    if (sorted[0]) setSelectedRate(sorted[0]);
  };

  // Auto-load rates on open if we can.
  useEffect(() => {
    if (canShip && selectedPreset) loadRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreset?.name]);

  const buy = async () => {
    if (!selectedRate) return;
    setPhase("buying");
    setBuyError("");
    const res = await onBuy([{
      orderId: order.shopifyId || order.id,
      toAddress,
      parcel: selectedPreset,
      rateId: selectedRate.id,
      carrier: selectedRate.carrier,
      service: selectedRate.service,
      cost: selectedRate.rate,
    }]);
    if (!res.ok) {
      setPhase("error");
      setBuyError(res.error || "Purchase failed");
      return;
    }
    const first = (res.results || [])[0];
    if (first && first.ok && first.shipment) {
      setPurchased(first.shipment);
      setPhase("bought");
    } else {
      setPhase("error");
      setBuyError(first?.error || "Purchase failed");
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(17, 24, 39, 0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1100, fontFamily: "Inter, sans-serif",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 12, width: "90%", maxWidth: 580,
          maxHeight: "85vh", overflowY: "auto",
          boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
        }}
      >
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600, color: "#000000" }}>Ship {order.id}</div>
            <div style={{ fontSize: 14, color: "#6B7280", marginTop: 2 }}>{order.customer}</div>
          </div>
          <button
            onClick={onClose}
            style={{ border: "none", background: "transparent", cursor: "pointer", padding: 4, color: "#9CA3AF" }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: "20px 24px" }}>
          {/* From / To addresses */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div style={{ padding: 12, border: "1px solid #F3F4F6", borderRadius: 8, background: "#F9FAFB" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.08, marginBottom: 4 }}>From</div>
              {addressLooksComplete(fromAddress) ? (
                <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 600, color: "#000" }}>{fromAddress.name}</div>
                  <div>{fromAddress.street1}{fromAddress.street2 ? `, ${fromAddress.street2}` : ""}</div>
                  <div>{fromAddress.city}, {fromAddress.state} {fromAddress.zip}</div>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#B91C1C", lineHeight: 1.5 }}>
                  No return address yet. <button onClick={onGoToSettings} style={{ background: "none", border: "none", color: "#0369A1", textDecoration: "underline", cursor: "pointer", padding: 0, fontFamily: "Inter, sans-serif", fontSize: 13 }}>Add one</button>.
                </div>
              )}
            </div>
            <div style={{ padding: 12, border: "1px solid #F3F4F6", borderRadius: 8, background: "#F9FAFB" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.08, marginBottom: 4 }}>To</div>
              {addressLooksComplete(toAddress) ? (
                <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 600, color: "#000" }}>{toAddress.name}</div>
                  <div>{toAddress.street1}{toAddress.street2 ? `, ${toAddress.street2}` : ""}</div>
                  <div>{toAddress.city}, {toAddress.state} {toAddress.zip}</div>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#B91C1C", lineHeight: 1.5 }}>
                  This order has no complete shipping address on Shopify.
                </div>
              )}
            </div>
          </div>

          {!canShip && (
            <div style={{ padding: 12, background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, fontSize: 13, color: "#92400E", marginBottom: 16, display: "flex", gap: 8, alignItems: "flex-start" }}>
              <AlertTriangle size={14} color="#D97706" strokeWidth={1.8} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>Fill in both addresses to fetch rates.</div>
            </div>
          )}

          {/* Parcel preset */}
          {canShip && (
            <>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Parcel</div>
                {parcelPresets.length === 0 ? (
                  <div style={{ padding: 12, background: "#F9FAFB", borderRadius: 8, fontSize: 13, color: "#6B7280" }}>
                    No parcel presets. <button onClick={onGoToSettings} style={{ background: "none", border: "none", color: "#0369A1", textDecoration: "underline", cursor: "pointer", padding: 0, fontFamily: "Inter, sans-serif", fontSize: 13 }}>Add one in Settings</button>.
                  </div>
                ) : (
                  <select
                    value={selectedPreset?.name || ""}
                    onChange={e => {
                      const preset = parcelPresets.find(p => p.name === e.target.value);
                      setSelectedPreset(preset);
                    }}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff" }}
                  >
                    {parcelPresets.map(p => (
                      <option key={p.name} value={p.name}>{parcelLabel(p)}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Rates */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Rates</div>
                  <button
                    onClick={loadRates}
                    disabled={ratesLoading || !selectedPreset}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", fontSize: 13, color: "#374151", cursor: ratesLoading ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}
                  >
                    <RefreshCw size={12} strokeWidth={1.6} /> {ratesLoading ? "Loading…" : "Refresh"}
                  </button>
                </div>
                {ratesLoading && rates.length === 0 && (
                  <div style={{ padding: 20, textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>
                    Fetching rates…
                  </div>
                )}
                {ratesError && (
                  <div style={{ padding: 12, background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, fontSize: 13, color: "#B91C1C" }}>
                    {ratesError}
                  </div>
                )}
                {rates.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {rates.map(r => {
                      const isSelected = selectedRate?.id === r.id;
                      return (
                        <label
                          key={r.id}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "10px 12px", borderRadius: 8, cursor: "pointer",
                            border: `1px solid ${isSelected ? "#0369A1" : "#E5E7EB"}`,
                            background: isSelected ? "#F0F9FF" : "#fff",
                          }}
                        >
                          <input
                            type="radio"
                            checked={isSelected}
                            onChange={() => setSelectedRate(r)}
                            style={{ cursor: "pointer" }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: "#000" }}>
                              {r.carrier} · {r.service}
                            </div>
                            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 1 }}>
                              {r.delivery_days ? `${r.delivery_days} day${r.delivery_days === 1 ? "" : "s"}` : "Transit time varies"}
                            </div>
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: "#000" }}>${parseFloat(r.rate).toFixed(2)}</div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {phase === "bought" && purchased && (
            <div style={{ padding: 14, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Check size={16} color="#16A34A" strokeWidth={2} />
                <div style={{ fontSize: 14, fontWeight: 600, color: "#166534" }}>Label purchased</div>
              </div>
              <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
                <div><strong>Tracking:</strong> {purchased.tracking}</div>
                <div><strong>Carrier:</strong> {purchased.carrier} {purchased.service}</div>
                <div><strong>Cost:</strong> ${parseFloat(purchased.cost).toFixed(2)}</div>
              </div>
              {purchased.labelUrl && (
                <a
                  href={purchased.labelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10, padding: "7px 12px", borderRadius: 6, background: "#0369A1", color: "#fff", textDecoration: "none", fontSize: 13, fontWeight: 500 }}
                >
                  <ExternalLink size={12} /> Download label
                </a>
              )}
            </div>
          )}

          {phase === "error" && (
            <div style={{ padding: 12, background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, fontSize: 13, color: "#B91C1C", marginBottom: 12 }}>
              {buyError}
            </div>
          )}
        </div>

        <div style={{ padding: "14px 24px", borderTop: "1px solid #F3F4F6", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {phase === "bought" ? (
            <button
              onClick={onClose}
              style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 500, color: "#374151", cursor: "pointer", fontFamily: "Inter, sans-serif" }}
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 500, color: "#374151", cursor: "pointer", fontFamily: "Inter, sans-serif" }}
              >
                Cancel
              </button>
              <button
                onClick={buy}
                disabled={!selectedRate || phase === "buying"}
                style={{
                  padding: "8px 16px", borderRadius: 8, border: "none",
                  background: (!selectedRate || phase === "buying") ? "#D1D5DB" : "#0369A1",
                  fontSize: 14, fontWeight: 600, color: "#fff",
                  cursor: (!selectedRate || phase === "buying") ? "default" : "pointer",
                  fontFamily: "Inter, sans-serif",
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                <Truck size={13} /> {phase === "buying" ? "Buying…" : (selectedRate ? `Buy label · $${parseFloat(selectedRate.rate).toFixed(2)}` : "Buy label")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BatchShipModal({ orders, fromAddress, parcelPresets, onClose, onBuy, onGoToSettings }) {
  const defaultPreset = parcelPresets[0] || null;
  // Per-order row state: { order, preset, status: "ready"|"buying"|"done"|"error", result }
  const [rows, setRows] = useState(() =>
    orders.map(o => ({
      order: o,
      preset: defaultPreset,
      status: addressLooksComplete(o.shippingAddress) ? "ready" : "skip",
      result: null,
      error: "",
    }))
  );
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [globalPreset, setGlobalPreset] = useState(defaultPreset);

  const applyGlobalPreset = () => {
    setRows(prev => prev.map(r => r.status === "ready" ? { ...r, preset: globalPreset } : r));
  };

  const buyAll = async () => {
    if (!addressLooksComplete(fromAddress)) return;
    setRunning(true);
    const readyRows = rows.filter(r => r.status === "ready" && r.preset);
    if (readyRows.length === 0) { setRunning(false); setDone(true); return; }
    // Mark all ready rows as buying.
    setRows(prev => prev.map(r => (r.status === "ready" && r.preset) ? { ...r, status: "buying" } : r));
    const jobs = readyRows.map(r => ({
      orderId: r.order.shopifyId || r.order.id,
      toAddress: r.order.shippingAddress,
      parcel: r.preset,
    }));
    const res = await onBuy(jobs);
    setRows(prev => prev.map(r => {
      if (r.status !== "buying") return r;
      const match = (res.results || []).find(x => x.orderId === (r.order.shopifyId || r.order.id));
      if (!match) return { ...r, status: "error", error: res.error || "Unknown failure" };
      if (match.ok) return { ...r, status: "done", result: match.shipment };
      return { ...r, status: "error", error: match.error || "Failed" };
    }));
    setRunning(false);
    setDone(true);
  };

  const readyCount = rows.filter(r => r.status === "ready").length;
  const doneCount = rows.filter(r => r.status === "done").length;
  const errorCount = rows.filter(r => r.status === "error").length;
  const skipCount = rows.filter(r => r.status === "skip").length;

  return (
    <div
      onClick={running ? undefined : onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(17, 24, 39, 0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1100, fontFamily: "Inter, sans-serif",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 12, width: "92%", maxWidth: 760,
          maxHeight: "85vh", display: "flex", flexDirection: "column",
          boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
        }}
      >
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600, color: "#000000" }}>Ship {orders.length} order{orders.length === 1 ? "" : "s"}</div>
            <div style={{ fontSize: 14, color: "#6B7280", marginTop: 2 }}>
              Rates are fetched and labels purchased as one batch.
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={running}
            style={{ border: "none", background: "transparent", cursor: running ? "default" : "pointer", padding: 4, color: "#9CA3AF" }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: "16px 24px", borderBottom: "1px solid #F3F4F6" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.08, marginBottom: 4 }}>Apply to all</div>
              <select
                value={globalPreset?.name || ""}
                onChange={e => setGlobalPreset(parcelPresets.find(p => p.name === e.target.value))}
                style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff" }}
              >
                {parcelPresets.map(p => (
                  <option key={p.name} value={p.name}>{parcelLabel(p)}</option>
                ))}
              </select>
            </div>
            <button
              onClick={applyGlobalPreset}
              disabled={!globalPreset || running}
              style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 500, color: "#374151", cursor: running ? "default" : "pointer", fontFamily: "Inter, sans-serif", alignSelf: "flex-end" }}
            >
              Apply
            </button>
          </div>
          {!addressLooksComplete(fromAddress) && (
            <div style={{ padding: 10, background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, fontSize: 13, color: "#92400E", marginTop: 12, display: "flex", gap: 8, alignItems: "flex-start" }}>
              <AlertTriangle size={14} color="#D97706" strokeWidth={1.8} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>No return address set. <button onClick={onGoToSettings} style={{ background: "none", border: "none", color: "#92400E", textDecoration: "underline", cursor: "pointer", padding: 0, fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 600 }}>Add one in Settings</button>.</div>
            </div>
          )}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "8px 24px" }}>
          {rows.map((r, i) => (
            <div
              key={r.order.id}
              style={{
                padding: "12px 0", borderBottom: i === rows.length - 1 ? "none" : "1px solid #F3F4F6",
                display: "grid", gridTemplateColumns: "1fr 220px 140px", gap: 12, alignItems: "center",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.order.id} · {r.order.customer}
                </div>
                <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {addressLooksComplete(r.order.shippingAddress) ? formatAddress(r.order.shippingAddress) : "No shipping address"}
                </div>
              </div>
              <select
                value={r.preset?.name || ""}
                disabled={r.status === "skip" || r.status === "buying" || r.status === "done"}
                onChange={e => {
                  const preset = parcelPresets.find(p => p.name === e.target.value);
                  setRows(prev => prev.map((x, idx) => idx === i ? { ...x, preset } : x));
                }}
                style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #E5E7EB", fontSize: 13, fontFamily: "Inter, sans-serif", background: "#fff" }}
              >
                {parcelPresets.map(p => (
                  <option key={p.name} value={p.name}>{p.name}</option>
                ))}
              </select>
              <div style={{ fontSize: 13, textAlign: "right" }}>
                {r.status === "ready" && <span style={{ color: "#9CA3AF" }}>Ready</span>}
                {r.status === "buying" && <span style={{ color: "#0369A1" }}><Loader size={12} style={{ verticalAlign: "middle", marginRight: 4 }} /> Buying…</span>}
                {r.status === "done" && r.result && (
                  <span style={{ color: "#16A34A", display: "inline-flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                    <Check size={12} /> ${parseFloat(r.result.cost || 0).toFixed(2)}
                  </span>
                )}
                {r.status === "error" && <span style={{ color: "#B91C1C" }} title={r.error}>Failed</span>}
                {r.status === "skip" && <span style={{ color: "#9CA3AF" }} title="No shipping address">Skipped</span>}
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: "14px 24px", borderTop: "1px solid #F3F4F6", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ fontSize: 13, color: "#6B7280" }}>
            {done ? (
              <>
                {doneCount} shipped · {errorCount > 0 && <>{errorCount} failed · </>}{skipCount > 0 && <>{skipCount} skipped</>}
              </>
            ) : (
              <>
                {readyCount} ready · {skipCount > 0 && <>{skipCount} skipped (missing address)</>}
              </>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {done ? (
              <button
                onClick={onClose}
                style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 500, color: "#374151", cursor: "pointer", fontFamily: "Inter, sans-serif" }}
              >
                Close
              </button>
            ) : (
              <>
                <button
                  onClick={onClose}
                  disabled={running}
                  style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 500, color: "#374151", cursor: running ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}
                >
                  Cancel
                </button>
                <button
                  onClick={buyAll}
                  disabled={running || readyCount === 0 || !addressLooksComplete(fromAddress)}
                  style={{
                    padding: "8px 16px", borderRadius: 8, border: "none",
                    background: (running || readyCount === 0 || !addressLooksComplete(fromAddress)) ? "#D1D5DB" : "#0369A1",
                    fontSize: 14, fontWeight: 600, color: "#fff",
                    cursor: (running || readyCount === 0 || !addressLooksComplete(fromAddress)) ? "default" : "pointer",
                    fontFamily: "Inter, sans-serif",
                    display: "inline-flex", alignItems: "center", gap: 6,
                  }}
                >
                  <Truck size={13} /> {running ? "Buying labels…" : `Buy ${readyCount} label${readyCount === 1 ? "" : "s"}`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Devices card — pair a Elion bridge (Pi on the roaster) to this
   workspace. Admin creates a slot, gets a short registration code, pastes
   it into the agent on the Pi. From then on the device streams telemetry
   and the Live Roast card in Production lights up.
   ══════════════════════════════════════════════════════════════════ */
function DevicesCard({ demoMode = false }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", roasterModel: "", protocol: "loring" });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [demoRunning, setDemoRunning] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);

  /* Pull the device list on mount, then refresh every 10s so the online/
     offline pill stays roughly current without forcing the user to reload. */
  const fetchList = useCallback(async () => {
    try {
      const r = await fetch("/api/devices");
      if (!r.ok) return;
      const d = await r.json();
      setDevices(d.devices || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    fetchList();
    const t = setInterval(fetchList, 10_000);
    return () => clearInterval(t);
  }, [fetchList]);

  /* Poll demo status so the button flips to "Stop demo" while one is
     running, even if it was started from another tab. */
  const fetchDemoStatus = useCallback(async () => {
    try {
      const r = await fetch("/api/demo/roast/status");
      if (!r.ok) return;
      const d = await r.json();
      setDemoRunning(!!d.running);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    fetchDemoStatus();
    const t = setInterval(fetchDemoStatus, 3000);
    return () => clearInterval(t);
  }, [fetchDemoStatus]);

  const toggleDemo = async () => {
    setDemoBusy(true);
    try {
      const path = demoRunning ? "/api/demo/roast/stop" : "/api/demo/roast/start";
      const r = await fetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
      if (r.ok) {
        setDemoRunning(!demoRunning);
        fetchList();
      }
    } finally {
      setDemoBusy(false);
    }
  };

  const submit = async () => {
    setError("");
    if (!form.name.trim()) { setError("Name is required"); return; }
    setCreating(true);
    try {
      const r = await fetch("/api/devices", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || r.statusText); }
      setCreateOpen(false);
      setForm({ name: "", location: "", roasterModel: "", protocol: "loring" });
      fetchList();
    } catch (e) {
      setError(e.message || "Failed to create device");
    } finally {
      setCreating(false);
    }
  };

  const rotate = async (id) => {
    if (!confirm("Rotate this device's code? The agent will need to re-register with the new code.")) return;
    try {
      const r = await fetch(`/api/devices/${encodeURIComponent(id)}/rotate-code`, { method: "POST" });
      if (r.ok) fetchList();
    } catch { /* ignore */ }
  };
  const revoke = async (id) => {
    if (!confirm("Revoke this device? It will stop streaming immediately.")) return;
    try {
      const r = await fetch(`/api/devices/${encodeURIComponent(id)}/revoke`, { method: "POST" });
      if (r.ok) fetchList();
    } catch { /* ignore */ }
  };
  const remove = async (id) => {
    if (!confirm("Delete this device and its recorded roasts? Cannot be undone.")) return;
    try {
      const r = await fetch(`/api/devices/${encodeURIComponent(id)}`, { method: "DELETE" });
      if (r.ok) fetchList();
    } catch { /* ignore */ }
  };

  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { /* clipboard blocked */ }
  };

  const isOnline = (d) => d.lastSeenAt && (Date.now() - d.lastSeenAt) < 2 * 60 * 1000;

  return (
    <>
      {createOpen && (
        <>
          <div onClick={() => setCreateOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.45)", zIndex: 1000 }} />
          <div style={{
            position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: "min(520px, 94vw)", background: "#fff", borderRadius: 12, zIndex: 1001,
            boxShadow: "0 20px 60px rgba(0,0,0,0.18)", fontFamily: "Inter, sans-serif",
          }}>
            <div style={{ padding: "18px 24px 10px", borderBottom: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "#000000" }}>New device</div>
                <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>We'll issue a one-time code you paste into the agent.</div>
              </div>
              <button onClick={() => setCreateOpen(false)} aria-label="Close" style={{ background: "transparent", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4 }}><X size={18} /></button>
            </div>
            <div style={{ padding: "16px 24px" }}>
              {error && <div style={{ padding: "10px 14px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, color: "#DC2626", fontSize: 13, marginBottom: 12 }}>{error}</div>}
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Name</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Roastery bridge #1" autoFocus style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", boxSizing: "border-box", marginBottom: 12 }} />
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Location (optional)</label>
              <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Main roastery" style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", boxSizing: "border-box", marginBottom: 12 }} />
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Roaster model (optional)</label>
              <input value={form.roasterModel} onChange={e => setForm(f => ({ ...f, roasterModel: e.target.value }))} placeholder="Loring S15 Falcon" style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", boxSizing: "border-box", marginBottom: 12 }} />
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Protocol</label>
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  { v: "loring", l: "Loring · Modbus TCP" },
                  { v: "probat", l: "Probat · OPC UA" },
                  { v: "generic", l: "Generic" },
                ].map(o => (
                  <button key={o.v} type="button" onClick={() => setForm(f => ({ ...f, protocol: o.v }))} style={{
                    padding: "6px 12px", borderRadius: 8,
                    border: `1px solid ${form.protocol === o.v ? "#000000" : "#E5E7EB"}`,
                    background: form.protocol === o.v ? "#000000" : "#fff",
                    color: form.protocol === o.v ? "#fff" : "#374151",
                    fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                  }}>{o.l}</button>
                ))}
              </div>
            </div>
            <div style={{ padding: "12px 24px 18px", display: "flex", justifyContent: "flex-end", gap: 8, borderTop: "1px solid #F3F4F6" }}>
              <button onClick={() => setCreateOpen(false)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
              <button onClick={submit} disabled={creating || !form.name.trim()} style={{
                padding: "8px 16px", borderRadius: 8, border: "1px solid #000000",
                background: creating || !form.name.trim() ? "#9CA3AF" : "#000000",
                color: "#fff", fontSize: 14, fontWeight: 500,
                cursor: creating || !form.name.trim() ? "not-allowed" : "pointer",
                fontFamily: "Inter, sans-serif",
              }}>{creating ? "Creating…" : "Create device"}</button>
            </div>
          </div>
        </>
      )}

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 24, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 6, gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "#F5F3FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Cpu size={20} color="#7C3AED" strokeWidth={1.6} />
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 600, color: "#000000" }}>Bridge devices</div>
              <div style={{ fontSize: 15, color: "#6B7280", marginTop: 1 }}>Roaster hardware streaming telemetry into this workspace.</div>
            </div>
          </div>
          <button onClick={() => setCreateOpen(true)} style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 14px", borderRadius: 8,
            border: "1px solid #000000", background: "#000000",
            color: "#fff", fontSize: 14, fontWeight: 500,
            cursor: "pointer", fontFamily: "Inter, sans-serif", flexShrink: 0,
          }}>
            <Plus size={13} strokeWidth={1.8} /> New device
          </button>
        </div>

        {/* Demo shortcut — spins up a synthetic roast on the server so
            the Live Roast card in Production lights up without requiring
            a Pi. Only shown in demo mode so real customers can't kick
            it off accidentally. */}
        {demoMode && (
        <div style={{
          marginTop: 14, padding: "12px 14px", borderRadius: 10,
          background: demoRunning ? "#000000" : "#F9FAFB",
          border: `1px solid ${demoRunning ? "#000000" : "#E5E7EB"}`,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: demoRunning ? "rgba(255,255,255,0.08)" : "#fff", border: demoRunning ? "none" : "1px solid #E5E7EB", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <Radio size={16} color={demoRunning ? "#86EFAC" : "#6B7280"} strokeWidth={1.8} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: demoRunning ? "#fff" : "#000000" }}>
                {demoRunning ? "Demo roast streaming" : "No hardware yet?"}
              </div>
              <div style={{ fontSize: 12, color: demoRunning ? "rgba(255,255,255,0.6)" : "#6B7280", marginTop: 1 }}>
                {demoRunning
                  ? "Open Production → Breakdown to see the live card. ~12 min auto-ends."
                  : "Run a simulated 12-minute roast to see the pipeline light up."}
              </div>
            </div>
          </div>
          <button onClick={toggleDemo} disabled={demoBusy} style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "7px 14px", borderRadius: 8,
            border: demoRunning ? "1px solid rgba(255,255,255,0.3)" : "1px solid #000000",
            background: demoRunning ? "transparent" : "#000000",
            color: "#fff", fontSize: 13, fontWeight: 500,
            cursor: demoBusy ? "default" : "pointer",
            fontFamily: "Inter, sans-serif",
            opacity: demoBusy ? 0.6 : 1,
          }}>
            {demoRunning ? (<><X size={12} strokeWidth={2} /> Stop demo</>) : (<><Radio size={12} strokeWidth={2} /> Start demo roast</>)}
          </button>
        </div>
        )}

        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>Loading…</div>
        ) : devices.length === 0 ? (
          <div style={{ marginTop: 14, padding: "28px 20px", textAlign: "center", color: "#9CA3AF", fontSize: 14, border: "1px dashed #E5E7EB", borderRadius: 10 }}>
            No devices yet. Click <strong style={{ color: "#374151" }}>New device</strong> to pair your first Elion bridge.
          </div>
        ) : (
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            {devices.map(d => {
              const online = isOnline(d);
              const since = d.lastSeenAt ? Math.max(0, Math.round((Date.now() - d.lastSeenAt) / 1000)) : null;
              const seen = since == null
                ? "Never connected"
                : since < 60 ? `${since}s ago`
                : since < 3600 ? `${Math.round(since / 60)}m ago`
                : since < 86400 ? `${Math.round(since / 3600)}h ago`
                : `${Math.round(since / 86400)}d ago`;
              return (
                <div key={d.id} style={{ border: "1px solid #F3F4F6", borderRadius: 10, padding: "14px 16px", background: d.revokedAt ? "#FAFAFA" : "#fff" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: d.revokedAt ? "#9CA3AF" : "#000000", textDecoration: d.revokedAt ? "line-through" : "none" }}>{d.name}</span>
                        {d.revokedAt ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, color: "#9CA3AF", background: "#F3F4F6", padding: "2px 8px", borderRadius: 99, textTransform: "uppercase", letterSpacing: 0.4 }}>Revoked</span>
                        ) : online ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, color: "#047857", background: "#ECFDF5", padding: "2px 8px", borderRadius: 99, textTransform: "uppercase", letterSpacing: 0.4 }}>
                            <Wifi size={10} strokeWidth={2} /> Online
                          </span>
                        ) : (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, color: "#6B7280", background: "#F3F4F6", padding: "2px 8px", borderRadius: 99, textTransform: "uppercase", letterSpacing: 0.4 }}>
                            <WifiOff size={10} strokeWidth={2} /> Offline
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: "#6B7280", marginTop: 4 }}>
                        {d.roasterModel ? `${d.roasterModel} · ` : ""}
                        {d.protocol} · Last seen {seen}
                      </div>
                      {d.location && <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>{d.location}</div>}

                      {!d.claimed && d.registrationCode && (
                        <div style={{ marginTop: 10, padding: "10px 12px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Registration code</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <code style={{ fontSize: 18, fontWeight: 700, letterSpacing: 2, color: "#78350F", background: "#fff", padding: "4px 10px", borderRadius: 6, border: "1px solid #FCD34D" }}>{d.registrationCode}</code>
                            <button onClick={() => copy(d.registrationCode, d.id)} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 6, border: "1px solid #FCD34D", background: "#fff", color: "#92400E", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
                              <Copy size={11} strokeWidth={1.8} /> {copiedId === d.id ? "Copied" : "Copy"}
                            </button>
                          </div>
                          <div style={{ fontSize: 11, color: "#92400E", marginTop: 6, lineHeight: 1.5 }}>
                            Paste into <code style={{ background: "rgba(0,0,0,0.05)", padding: "1px 5px", borderRadius: 3 }}>/etc/roaster-agent/config.json</code> on the Pi as <code style={{ background: "rgba(0,0,0,0.05)", padding: "1px 5px", borderRadius: 3 }}>deviceCode</code>, then restart the service.
                          </div>
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      {!d.revokedAt && (
                        <button onClick={() => rotate(d.id)} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#6B7280", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Rotate code</button>
                      )}
                      {!d.revokedAt && (
                        <button onClick={() => revoke(d.id)} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #FECACA", background: "#fff", color: "#DC2626", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Revoke</button>
                      )}
                      <button onClick={() => remove(d.id)} title="Delete" style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#9CA3AF", cursor: "pointer", fontFamily: "Inter, sans-serif" }}><Trash2 size={12} strokeWidth={1.8} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Integrations page — friendly card grid. Each card is a big logo +
   title + status pill. Clicking opens a modal with step-by-step
   instructions and the actual connect controls. Keeps the Settings
   page from reading like a technical connector list.
   ══════════════════════════════════════════════════════════════════ */
function IntegrationsGrid({
  shopConnected, connectedShop, shopifyStores, connectInput, setConnectInput, connectError, setConnectError,
  connectShopify, disconnectShopify, fetchShopifyOrders,
  sosStatus, sosLoading, disconnectSos,
  qboStatus, qboLoading, disconnectQbo,
  user,
}) {
  const [open, setOpen] = useState(null); // "shopify" | "sos" | "qbo" | "edi" | null

  const integrations = [
    {
      id: "shopify",
      name: "Shopify",
      tagline: "Sync orders, products, and inventory automatically.",
      icon: ShoppingBag,
      brandColor: "#95BF47",
      brandTint: "#F0FDF4",
      connected: shopConnected,
      status: !shopConnected
        ? "Not connected"
        : (shopifyStores?.length || 0) > 1
          ? `${shopifyStores.length} stores connected`
          : "Connected",
    },
    {
      id: "sos",
      name: "SOS Inventory",
      tagline: "Stock levels, purchase orders, and QuickBooks sync.",
      icon: Warehouse,
      brandColor: "#2563EB",
      brandTint: "#EFF6FF",
      connected: sosStatus.connected,
      status: sosLoading ? "Checking…" : (sosStatus.connected ? "Connected" : "Not connected"),
    },
    {
      id: "qbo",
      name: "QuickBooks",
      tagline: "Push invoices, sync customers, and pull payments automatically.",
      icon: BookOpen,
      brandColor: "#2CA01C",
      brandTint: "#F0FDF4",
      connected: qboStatus.connected,
      status: qboLoading ? "Checking…" : (qboStatus.connected ? (qboStatus.companyName || "Connected") : "Not connected"),
    },
    {
      id: "edi",
      name: "Retail EDI",
      tagline: "Pull wholesale POs from Target, Trader Joe\u2019s, Whole Foods and more.",
      icon: Store,
      brandColor: "#CC0000",
      brandTint: "#FEF2F2",
      connected: false,
      status: "Private beta",
    },
  ];

  return (
    <>
      <div id="integrations" style={{ scrollMarginTop: 24 }}>
        <div style={{ fontSize: 15, color: "#52525B", marginBottom: 16, lineHeight: 1.55 }}>
          Connect the services you already use. We&rsquo;ll guide you through each one step-by-step — no technical stuff.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
          {integrations.map(x => (
            <IntegrationCard
              key={x.id}
              {...x}
              onClick={() => { if (!x.comingSoon) setOpen(x.id); }}
            />
          ))}
        </div>
      </div>

      {open === "shopify" && (
        <ShopifyModal
          onClose={() => setOpen(null)}
          stores={shopifyStores || []}
          connectInput={connectInput}
          setConnectInput={setConnectInput}
          connectError={connectError}
          setConnectError={setConnectError}
          connectShopify={connectShopify}
          disconnectShopify={disconnectShopify}
          fetchShopifyOrders={fetchShopifyOrders}
          user={user}
        />
      )}

      {open === "sos" && (
        <IntegrationModal
          title="SOS Inventory"
          icon={Warehouse}
          brandColor="#2563EB"
          brandTint="#EFF6FF"
          description="SOS Inventory keeps your stock levels, purchase orders, and QuickBooks data in sync. Once connected, Roaster pulls these alongside your Shopify orders for one unified view."
          onClose={() => setOpen(null)}
          connected={sosStatus.connected}
          onDisconnect={disconnectSos}
          steps={[
            "Make sure you have an SOS Inventory account at <strong>sosinventory.com</strong>. You&rsquo;ll need login credentials.",
            "Click <strong>Connect to SOS Inventory</strong> below.",
            "You&rsquo;ll be sent to SOS to sign in and approve access.",
            "SOS redirects you back here automatically — you&rsquo;re done.",
          ]}
          connectForm={(
            !sosStatus.configured ? (
              <div style={{ padding: "14px 16px", background: "#FEF3C7", borderRadius: 10, fontSize: 13, color: "#92400E", border: "1px solid #FDE68A", lineHeight: 1.55 }}>
                Almost there — the server still needs <code style={{ fontSize: 12, background: "rgba(0,0,0,0.05)", padding: "1px 5px", borderRadius: 4 }}>SOS_CLIENT_ID</code> and <code style={{ fontSize: 12, background: "rgba(0,0,0,0.05)", padding: "1px 5px", borderRadius: 4 }}>SOS_CLIENT_SECRET</code> set before this can connect. Your admin needs to set these once.
              </div>
            ) : (
              <a
                href="/api/sos/auth"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "12px 20px", borderRadius: 10,
                  background: "#000000", color: "#fff",
                  fontSize: 15, fontWeight: 600, fontFamily: "Inter, sans-serif",
                  textDecoration: "none",
                }}
              >
                <Link size={14} strokeWidth={1.8} /> Connect to SOS Inventory
              </a>
            )
          )}
        />
      )}

      {open === "qbo" && (
        <IntegrationModal
          title="QuickBooks Online"
          icon={BookOpen}
          brandColor="#2CA01C"
          brandTint="#F0FDF4"
          description="Push fulfilled orders as QBO invoices, sync your wholesale customers, and pull payment status back into Roaster — automatically."
          onClose={() => setOpen(null)}
          connected={qboStatus.connected}
          connectedSubtitle={qboStatus.connected ? (qboStatus.companyName || "Connected") : null}
          onDisconnect={disconnectQbo}
          steps={[
            "Make sure you have a <strong>QuickBooks Online</strong> account at <strong>quickbooks.intuit.com</strong>.",
            "Click <strong>Connect to QuickBooks</strong> below.",
            "You&rsquo;ll be sent to Intuit to sign in and approve access.",
            "Intuit redirects you back here — customers, items, and invoices will sync automatically.",
          ]}
          connectForm={(
            !qboStatus.configured ? (
              <div style={{ padding: "14px 16px", background: "#FEF3C7", borderRadius: 10, fontSize: 13, color: "#92400E", border: "1px solid #FDE68A", lineHeight: 1.55 }}>
                Almost there — the server still needs <code style={{ fontSize: 12, background: "rgba(0,0,0,0.05)", padding: "1px 5px", borderRadius: 4 }}>QBO_CLIENT_ID</code> and <code style={{ fontSize: 12, background: "rgba(0,0,0,0.05)", padding: "1px 5px", borderRadius: 4 }}>QBO_CLIENT_SECRET</code> set before this can connect. Your admin needs to set these once.
              </div>
            ) : (
              <a
                href="/api/qbo/auth"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "12px 20px", borderRadius: 10,
                  background: "#2CA01C", color: "#fff",
                  fontSize: 15, fontWeight: 600, fontFamily: "Inter, sans-serif",
                  textDecoration: "none",
                }}
              >
                <Link size={14} strokeWidth={1.8} /> Connect to QuickBooks
              </a>
            )
          )}
        />
      )}

      {open === "edi" && (
        <EdiModal onClose={() => setOpen(null)} />
      )}
    </>
  );
}

/* ═════════════════════════ Retail EDI modal ════════════════════════════
   Real EDI console: register trading partners per retailer, drop in 850
   purchase orders, generate 856 ASNs and 810 invoices. Calls the
   /api/edi/* endpoints in edi.js. File-drop transport only for MVP — a
   VAN or AS2 shim can post to /api/edi/inbound the same way. */

function EdiModal({ onClose }) {
  const [state, setState] = useState({ partners: [], documents: [], retailers: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("inbox"); // inbox | identity | partners | upload
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [lastImport, setLastImport] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/edi/state");
      if (r.ok) setState(await r.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const uploadFile = async (file) => {
    setUploadError(""); setLastImport(null); setUploadBusy(true);
    try {
      const text = await file.text();
      const r = await fetch("/api/edi/inbound", {
        method: "POST", headers: { "Content-Type": "text/plain" }, body: text,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setUploadError(d.error || "Upload failed"); return; }
      setLastImport(d);
      await refresh();
      setTab("inbox");
    } catch (e) { setUploadError(e.message || "Upload failed"); }
    finally { setUploadBusy(false); }
  };

  const generate = async (docId, kind) => {
    const r = await fetch(`/api/edi/outbound/${kind}/${docId}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}),
    });
    if (r.ok) refresh();
  };

  const downloadRaw = (docId) => {
    window.open(`/api/edi/documents/${docId}/raw`, "_blank");
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 9000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "Inter, sans-serif" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 720, maxHeight: "88vh", overflow: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.24)" }}>
        <div style={{ padding: "24px 28px 16px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Store size={22} color="#CC0000" strokeWidth={1.75} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.01em" }}>Retail EDI</div>
            <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>X12 850 / 855 / 856 / 810 — Target, Trader Joe’s, Whole Foods, Sprouts, Kroger, Costco</div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6, color: "#9CA3AF" }}>
            <X size={20} strokeWidth={1.75} />
          </button>
        </div>

        <div style={{ display: "flex", gap: 4, padding: "14px 28px 0", borderBottom: "1px solid #F3F4F6" }}>
          {[
            { id: "inbox", label: "Documents", count: state.documents.length },
            { id: "identity", label: "Our identity" },
            { id: "partners", label: "Trading partners", count: state.partners.length },
            { id: "upload", label: "Upload 850" },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: "10px 14px", border: "none", background: "transparent",
              fontFamily: "Inter, sans-serif", fontSize: 14,
              fontWeight: tab === t.id ? 600 : 500,
              color: tab === t.id ? "#000" : "#6B7280",
              borderBottom: tab === t.id ? "2px solid #000" : "2px solid transparent",
              cursor: "pointer", marginBottom: -1,
            }}>
              {t.label}{typeof t.count === "number" ? ` (${t.count})` : ""}
            </button>
          ))}
        </div>

        <div style={{ padding: "20px 28px 28px" }}>
          {loading ? (
            <div style={{ padding: 24, color: "#9CA3AF", fontSize: 14 }}>Loading…</div>
          ) : tab === "inbox" ? (
            <EdiInbox documents={state.documents} partners={state.partners} onGenerate={generate} onDownload={downloadRaw} />
          ) : tab === "identity" ? (
            <EdiIdentity partners={state.partners} />
          ) : tab === "partners" ? (
            <EdiPartners partners={state.partners} retailers={state.retailers} transports={state.transports} onChange={refresh} />
          ) : (
            <EdiUpload onUpload={uploadFile} busy={uploadBusy} error={uploadError} lastImport={lastImport} />
          )}
        </div>
      </div>
    </div>
  );
}

function EdiInbox({ documents, partners, onGenerate, onDownload }) {
  if (documents.length === 0) {
    return <div style={{ padding: "18px 0", color: "#6B7280", fontSize: 14, lineHeight: 1.55 }}>No EDI documents yet. Upload a test 850 to see it land here, matched to a retailer.</div>;
  }
  const partnerName = (id) => partners.find(p => p.id === id)?.retailerName || "Unmatched";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {documents.map(d => (
        <div key={d.id} style={{ border: "1px solid #F3F4F6", borderRadius: 12, padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: d.direction === "inbound" ? "#EFF6FF" : "#F0FDF4", color: d.direction === "inbound" ? "#1D4ED8" : "#166534", letterSpacing: "0.02em" }}>
              {d.direction === "inbound" ? "IN" : "OUT"} · {d.docType}
            </span>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{d.summary?.poNumber || d.controlNumber}</span>
            <span style={{ fontSize: 13, color: "#6B7280" }}>{partnerName(d.partnerId)}</span>
            {d.transport && <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 4, background: "#F3F4F6", color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>{d.transport}</span>}
            <span style={{ marginLeft: "auto", fontSize: 12, color: "#9CA3AF" }}>{new Date(d.createdAt).toLocaleString()}</span>
          </div>
          {d.summary && d.docType === "850" && (
            <div style={{ fontSize: 13, color: "#4B5563", marginBottom: 8 }}>
              {d.summary.lines} line{d.summary.lines === 1 ? "" : "s"} · {d.summary.totalQty} units · ${Number(d.summary.totalValue || 0).toFixed(2)}
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => onDownload(d.id)} style={btnGhost}>Download raw</button>
            {d.docType === "850" && d.partnerId && (
              <>
                <button onClick={() => onGenerate(d.id, "856")} style={btnGhost}>Generate 856 ASN</button>
                <button onClick={() => onGenerate(d.id, "810")} style={btnGhost}>Generate 810 invoice</button>
              </>
            )}
            {d.docType === "850" && !d.partnerId && (
              <span style={{ fontSize: 12, color: "#B45309" }}>Add a trading partner to enable outbound docs.</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* Our identity — company EDI profile + self-signed AS2 cert. What the
   customer fills in here becomes the one-page PDF they hand to each
   retailer's EDI team. Saved per-workspace on the server; private key
   never returns to the frontend. */

function EdiIdentity({ partners }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [certBusy, setCertBusy] = useState(false);

  const load = async () => {
    const r = await fetch("/api/edi/profile");
    if (r.ok) {
      const d = await r.json();
      setData(d);
      setForm(d.profile || emptyProfile());
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch("/api/edi/profile", {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (r.ok) await load();
    } finally { setSaving(false); }
  };

  const generateCert = async () => {
    setCertBusy(true);
    try {
      const r = await fetch("/api/edi/cert/generate", { method: "POST" });
      if (r.ok) await load();
    } finally { setCertBusy(false); }
  };

  const openProfile = (partnerId) => {
    window.open(`/api/edi/profile/${partnerId}.html`, "_blank");
  };

  if (!form) return <div style={{ padding: 24, color: "#9CA3AF", fontSize: 14 }}>Loading…</div>;

  const bind = (k) => ({
    value: form[k] || "",
    onChange: e => setForm({ ...form, [k]: e.target.value }),
    style: input,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2, color: "#374151" }}>AS2 endpoint</div>
        <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 8 }}>Give this URL to the retailer. Every customer has a unique one.</div>
        <div style={{ padding: "10px 12px", borderRadius: 10, background: "#F3F4F6", fontFamily: "SF Mono, Menlo, monospace", fontSize: 13, wordBreak: "break-all" }}>
          {data?.as2Url || "—"}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "#374151" }}>Company</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <input placeholder="Legal name" {...bind("companyName")} />
          <input placeholder="DBA (optional)" {...bind("dbaName")} />
          <input placeholder="DUNS (9 digits)" {...bind("duns")} />
          <input placeholder="GS1 company prefix" {...bind("gs1Prefix")} />
          <input placeholder="ISA sender ID (assigned by retailer)" {...bind("isaId")} />
          <select {...bind("qualifier")}>
            <option value="ZZ">ZZ — Mutually defined</option>
            <option value="01">01 — DUNS</option>
            <option value="14">14 — DUNS+4</option>
            <option value="08">08 — UCC/EAN</option>
          </select>
          <input placeholder="Address line 1" {...bind("addressLine1")} style={{ ...input, gridColumn: "1 / -1" }} />
          <input placeholder="City" {...bind("city")} />
          <input placeholder="State" {...bind("state")} />
          <input placeholder="ZIP" {...bind("zip")} />
          <input placeholder="Country" {...bind("country")} />
        </div>
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "#374151" }}>Contacts</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <input placeholder="Business contact name" {...bind("businessContactName")} />
          <input placeholder="Technical contact name" {...bind("technicalContactName")} />
          <input placeholder="Business email" {...bind("businessContactEmail")} />
          <input placeholder="Technical email" {...bind("technicalContactEmail")} />
          <input placeholder="Business phone" {...bind("businessContactPhone")} />
          <input placeholder="Technical phone" {...bind("technicalContactPhone")} />
        </div>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={save} disabled={saving} style={btnPrimary}>{saving ? "Saving…" : "Save profile"}</button>
      </div>

      <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2, color: "#374151" }}>AS2 certificate</div>
        <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 8 }}>Self-signed X.509 used to sign outbound EDI messages. Send the public PEM to the retailer's EDI team.</div>
        {data?.cert ? (
          <div style={{ padding: "12px 14px", border: "1px solid #E5E7EB", borderRadius: 10, background: "#FAFAFA" }}>
            <div style={{ fontSize: 13, marginBottom: 4 }}><strong>{data.cert.subject}</strong></div>
            <div style={{ fontSize: 12, color: "#6B7280" }}>Fingerprint (SHA-1): <span style={{ fontFamily: "SF Mono, Menlo, monospace" }}>{data.cert.fingerprint}</span></div>
            <div style={{ fontSize: 12, color: "#6B7280" }}>Valid {new Date(data.cert.notBefore).toISOString().slice(0,10)} → {new Date(data.cert.notAfter).toISOString().slice(0,10)}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <a href="/api/edi/cert/public.pem" download="edi-public.pem" style={btnGhost}>Download public PEM</a>
              <button onClick={generateCert} disabled={certBusy} style={btnGhost}>{certBusy ? "Generating…" : "Regenerate"}</button>
            </div>
          </div>
        ) : (
          <button onClick={generateCert} disabled={certBusy} style={btnPrimary}>
            {certBusy ? "Generating…" : "Generate certificate"}
          </button>
        )}
      </div>

      <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2, color: "#374151" }}>Connection profile</div>
        <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 10 }}>One-page PDF the retailer's EDI team uses to set up the link on their side. Customize per retailer.</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <button onClick={() => openProfile("generic")} style={btnGhost}>Generic profile (PDF)</button>
          {partners.map(p => (
            <button key={p.id} onClick={() => openProfile(p.id)} style={btnGhost}>
              {p.retailerName} profile (PDF)
            </button>
          ))}
          {partners.length === 0 && (
            <div style={{ fontSize: 12, color: "#9CA3AF" }}>Add a trading partner to export a retailer-specific profile with their onboarding checklist.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function emptyProfile() {
  return {
    companyName: "", dbaName: "", duns: "", gs1Prefix: "", isaId: "", qualifier: "ZZ",
    addressLine1: "", addressLine2: "", city: "", state: "", zip: "", country: "US",
    businessContactName: "", businessContactEmail: "", businessContactPhone: "",
    technicalContactName: "", technicalContactEmail: "", technicalContactPhone: "",
  };
}

/* Partners tab: pick retailer first, see a hint card describing how that
   retailer connects (AS2 vs VAN vs SFTP), then fill only the fields that
   actually apply to the chosen transport. Users should never see an AS2
   URL field for Trader Joe's (VAN-only) or VAN credentials for Target
   (AS2-only). */

function EdiPartners({ partners, retailers, transports, onChange }) {
  const [retailerId, setRetailerId] = useState("");
  const [transport, setTransport] = useState("");
  const [isaId, setIsaId] = useState("");
  const [ourIsaId, setOurIsaId] = useState("");
  const [qualifier, setQualifier] = useState("ZZ");
  /* Transport-specific config */
  const [as2, setAs2] = useState({ theirUrl: "", theirAs2Id: "", ourAs2Id: "", theirCertPem: "" });
  const [van, setVan] = useState({ host: "", port: 22, username: "", password: "", inboxPath: "/in", outboxPath: "/out", archivePath: "/in/archive" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [testResult, setTestResult] = useState(null);

  const retailer = retailers.find(r => r.id === retailerId);
  const effectiveTransport = transport || retailer?.transport || "";
  const t = transports?.[effectiveTransport];

  useEffect(() => {
    if (retailer) {
      setTransport(retailer.transport);
      setQualifier(retailer.isaQualifier || "ZZ");
      setIsaId(retailer.name.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15));
    }
  }, [retailerId]); // eslint-disable-line

  const add = async () => {
    setError(""); setBusy(true);
    try {
      const r = await fetch("/api/edi/partners", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          retailer: retailerId, isaId, ourIsaId, qualifier,
          transport: effectiveTransport,
          as2: effectiveTransport === "as2" ? as2 : null,
          van: (effectiveTransport === "van" || effectiveTransport === "sftp") ? van : null,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || "Failed"); return; }
      setRetailerId(""); setTransport(""); setIsaId(""); setOurIsaId(""); setQualifier("ZZ");
      setAs2({ theirUrl: "", theirAs2Id: "", ourAs2Id: "", theirCertPem: "" });
      setVan({ host: "", port: 22, username: "", password: "", inboxPath: "/in", outboxPath: "/out", archivePath: "/in/archive" });
      onChange();
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    const r = await fetch(`/api/edi/partners/${id}`, { method: "DELETE" });
    if (r.ok) onChange();
  };

  const testConnection = async (partnerId) => {
    setTestResult({ partnerId, loading: true });
    const r = await fetch(`/api/edi/test/${partnerId}`, { method: "POST" });
    const d = await r.json().catch(() => ({}));
    setTestResult({ partnerId, ...d });
  };

  const pollVan = async (partnerId) => {
    setTestResult({ partnerId, loading: true, poll: true });
    const r = await fetch(`/api/edi/van/poll/${partnerId}`, { method: "POST" });
    const d = await r.json().catch(() => ({}));
    setTestResult({ partnerId, poll: true, ...d, ok: r.ok });
    if (r.ok) onChange();
  };

  /* Group retailers by transport for the picker — scannable and teaches
     the user which transport each retailer uses. */
  const grouped = {};
  (retailers || []).forEach(r => {
    const key = r.transport || "other";
    (grouped[key] = grouped[key] || []).push(r);
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "#374151" }}>Add trading partner</div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 4 }}>Retailer</div>
          <select value={retailerId} onChange={e => setRetailerId(e.target.value)} style={input}>
            <option value="">Choose a retailer…</option>
            {Object.entries(grouped).map(([tid, list]) => (
              <optgroup key={tid} label={transports?.[tid]?.label || tid.toUpperCase()}>
                {list.map(r => <option key={r.id} value={r.id}>{r.name} — {r.transportLabel || r.transport}</option>)}
              </optgroup>
            ))}
          </select>
        </div>

        {retailer && (
          <div style={{ padding: "12px 14px", borderRadius: 10, background: "#F9FAFB", border: "1px solid #E5E7EB", marginBottom: 12 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              <span style={pill("#1F2937", "#fff")}>{retailer.transportLabel || effectiveTransport}</span>
              <span style={pill("#F3F4F6", "#374151")}>Docs: {retailer.docs?.join(" · ")}</span>
              {retailer.timelineDays && <span style={pill("#F3F4F6", "#374151")}>~{retailer.timelineDays} days to certify</span>}
              {retailer.van && <span style={pill("#FEF3C7", "#92400E")}>Via {retailer.van}</span>}
            </div>
            {t?.hint && <div style={{ fontSize: 13, color: "#4B5563", lineHeight: 1.5, marginBottom: 6 }}>{t.hint}</div>}
            {retailer.gotchas && <div style={{ fontSize: 12, color: "#92400E", lineHeight: 1.5, marginBottom: 6 }}><strong>Watch out:</strong> {retailer.gotchas}</div>}
            {retailer.whoApproves && <div style={{ fontSize: 12, color: "#6B7280" }}>Approved by: {retailer.whoApproves}</div>}
            {retailer.onboardingUrl && <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>Onboarding: <a href={retailer.onboardingUrl} target="_blank" rel="noreferrer" style={{ color: "#2563EB", textDecoration: "none" }}>{retailer.onboardingUrl.replace(/^https?:\/\//, "")}</a></div>}
          </div>
        )}

        {retailerId && (
          <>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4, marginBottom: 4 }}>Transport</div>
            <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
              {Object.values(transports || {}).map(tx => (
                <button key={tx.id} onClick={() => setTransport(tx.id)} style={{
                  padding: "6px 12px", borderRadius: 8, cursor: "pointer",
                  border: `1px solid ${effectiveTransport === tx.id ? "#000" : "#E5E7EB"}`,
                  background: effectiveTransport === tx.id ? "#000" : "#fff",
                  color: effectiveTransport === tx.id ? "#fff" : "#374151",
                  fontSize: 12, fontWeight: 500, fontFamily: "Inter, sans-serif",
                }}>{tx.label}</button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 10, lineHeight: 1.5 }}>
              <strong>Usually:</strong> {t?.usually}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
              <div>
                <div style={labelStyle}>Their ISA ID</div>
                <input placeholder="e.g. TARGET" value={isaId} onChange={e => setIsaId(e.target.value)} style={input} />
              </div>
              <div>
                <div style={labelStyle}>Qualifier</div>
                <select value={qualifier} onChange={e => setQualifier(e.target.value)} style={input}>
                  <option value="ZZ">ZZ — Mutually defined</option>
                  <option value="01">01 — DUNS</option>
                  <option value="14">14 — DUNS+4</option>
                  <option value="08">08 — UCC/EAN</option>
                </select>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={labelStyle}>Your ISA ID (how they know you)</div>
                <input placeholder="e.g. HOLYNAT" value={ourIsaId} onChange={e => setOurIsaId(e.target.value)} style={input} />
              </div>
            </div>

            {effectiveTransport === "as2" && (
              <div style={{ padding: 12, border: "1px solid #E5E7EB", borderRadius: 10, marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>AS2 connection details <span style={{ color: "#9CA3AF", fontWeight: 400 }}>(from the retailer's EDI team)</span></div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <input placeholder="Their AS2 URL — where we post to (e.g. https://edi.target.com/as2)" value={as2.theirUrl} onChange={e => setAs2({ ...as2, theirUrl: e.target.value })} style={input} />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <input placeholder="Their AS2-From ID" value={as2.theirAs2Id} onChange={e => setAs2({ ...as2, theirAs2Id: e.target.value })} style={input} />
                    <input placeholder="Our AS2-To ID (what they call us)" value={as2.ourAs2Id} onChange={e => setAs2({ ...as2, ourAs2Id: e.target.value })} style={input} />
                  </div>
                  <textarea placeholder="Their public certificate (PEM, begins with -----BEGIN CERTIFICATE-----)" value={as2.theirCertPem} onChange={e => setAs2({ ...as2, theirCertPem: e.target.value })} style={{ ...input, minHeight: 90, fontFamily: "SF Mono, Menlo, monospace", fontSize: 11 }} />
                </div>
                <div style={{ fontSize: 11, color: "#6B7280", marginTop: 6, lineHeight: 1.5 }}>
                  They post POs to <strong style={{ fontFamily: "SF Mono, monospace" }}>{window.location.origin}/as2/&lt;your workspace&gt;</strong>. Export your connection profile (Our identity tab) to send them our side.
                </div>
              </div>
            )}

            {(effectiveTransport === "van" || effectiveTransport === "sftp") && (
              <div style={{ padding: 12, border: "1px solid #E5E7EB", borderRadius: 10, marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                  {effectiveTransport === "van" ? `VAN mailbox (${retailer?.van || "SPS Commerce / TrueCommerce / Infor Nexus"})` : "SFTP server"}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8, marginBottom: 8 }}>
                  <input placeholder="Host (e.g. sftp.spscommerce.com)" value={van.host} onChange={e => setVan({ ...van, host: e.target.value })} style={input} />
                  <input type="number" placeholder="Port" value={van.port} onChange={e => setVan({ ...van, port: Number(e.target.value) })} style={input} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                  <input placeholder="Username" value={van.username} onChange={e => setVan({ ...van, username: e.target.value })} style={input} />
                  <input type="password" placeholder="Password" value={van.password} onChange={e => setVan({ ...van, password: e.target.value })} style={input} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  <input placeholder="Inbox path" value={van.inboxPath} onChange={e => setVan({ ...van, inboxPath: e.target.value })} style={input} />
                  <input placeholder="Outbox path" value={van.outboxPath} onChange={e => setVan({ ...van, outboxPath: e.target.value })} style={input} />
                  <input placeholder="Archive path" value={van.archivePath} onChange={e => setVan({ ...van, archivePath: e.target.value })} style={input} />
                </div>
                <div style={{ fontSize: 11, color: "#6B7280", marginTop: 6, lineHeight: 1.5 }}>
                  {retailer?.van === "SPS Commerce" && "SPS sends credentials once your Trader Joe's buyer triggers enablement."}
                  {retailer?.van === "Infor Nexus" && "Infor Nexus sends SFTP credentials after Whole Foods category manager approves."}
                  {!retailer?.van && "Retailer or VAN sends credentials after vendor setup."}
                </div>
              </div>
            )}
          </>
        )}

        {error && <div style={{ color: "#B91C1C", fontSize: 13, marginTop: 8 }}>{error}</div>}
        <button onClick={add} disabled={busy || !retailerId || !isaId} style={{ ...btnPrimary, marginTop: 10, opacity: (!retailerId || !isaId) ? 0.5 : 1 }}>
          {busy ? "Saving…" : "Add partner"}
        </button>
      </div>

      <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "#374151" }}>Active ({partners.length})</div>
        {partners.length === 0 ? (
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>None yet.</div>
        ) : partners.map(p => (
          <div key={p.id} style={{ padding: "10px 12px", border: "1px solid #F3F4F6", borderRadius: 10, marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                  {p.retailerName}
                  <span style={pill("#F3F4F6", "#374151")}>{transports?.[p.transport]?.label || p.transport}</span>
                </div>
                <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>ISA {p.isaId} ({p.qualifier}) · ours: {p.ourIsaId}</div>
              </div>
              <button onClick={() => testConnection(p.id)} style={btnGhost}>Test connection</button>
              {(p.transport === "van" || p.transport === "sftp") && p.van?.host && (
                <button onClick={() => pollVan(p.id)} style={btnGhost}>Poll inbox</button>
              )}
              <button onClick={() => remove(p.id)} style={btnGhost}>Remove</button>
            </div>
            {testResult?.partnerId === p.id && (
              <div style={{ marginTop: 8, borderRadius: 8, overflow: "hidden", border: `1px solid ${testResult.ok ? "#BBF7D0" : testResult.poll ? "#BBF7D0" : "#FECACA"}` }}>
                {testResult.loading ? (
                  <div style={{ padding: "10px 12px", fontSize: 12, color: "#6B7280" }}>Running diagnostic…</div>
                ) : testResult.poll ? (
                  <div style={{ padding: "10px 12px", fontSize: 12, background: "#F0FDF4", color: "#166534" }}>
                    Polled. Imported {testResult.files?.length || 0} file{(testResult.files?.length || 0) === 1 ? "" : "s"}{testResult.skipped ? `, skipped ${testResult.skipped}` : ""}.
                  </div>
                ) : testResult.steps ? (
                  <div style={{ background: testResult.ok ? "#F0FDF4" : "#FEF2F2" }}>
                    <div style={{ padding: "8px 12px", fontSize: 12, fontWeight: 600, color: testResult.ok ? "#166534" : "#B91C1C", borderBottom: `1px solid ${testResult.ok ? "#BBF7D0" : "#FECACA"}` }}>
                      {testResult.ok ? "All checks passed — connection is ready" : "Some checks failed"}
                    </div>
                    <div style={{ padding: "6px 12px 10px" }}>
                      {testResult.steps.map((s, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "4px 0", fontSize: 12 }}>
                          <span style={{ fontSize: 13, lineHeight: 1.2, color: s.ok ? "#16A34A" : "#DC2626", fontWeight: 700, marginTop: 1 }}>{s.ok ? "✓" : "✗"}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: "#111827", fontWeight: 500 }}>{s.name}</div>
                            {s.detail && <div style={{ color: s.ok ? "#6B7280" : "#B91C1C", fontSize: 11, marginTop: 1, fontFamily: s.ok ? "inherit" : "inherit" }}>{s.detail}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: "10px 12px", fontSize: 12, background: "#FEF2F2", color: "#B91C1C" }}>{testResult.error || "Failed"}</div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function pill(bg, color) {
  return {
    display: "inline-block", padding: "2px 8px", borderRadius: 99,
    background: bg, color, fontSize: 11, fontWeight: 500,
  };
}

const labelStyle = { fontSize: 12, color: "#6B7280", marginBottom: 4 };

function EdiUpload({ onUpload, busy, error, lastImport }) {
  const [drag, setDrag] = useState(false);
  const ref = useRef(null);
  return (
    <div>
      <div
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) onUpload(f); }}
        onClick={() => ref.current?.click()}
        style={{
          border: `1.5px dashed ${drag ? "#000" : "#D1D5DB"}`,
          borderRadius: 12, padding: "32px 20px", textAlign: "center",
          background: drag ? "#F9FAFB" : "#fff", cursor: "pointer",
          transition: "border-color 0.15s, background 0.15s",
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Drop an 850 file here</div>
        <div style={{ fontSize: 13, color: "#6B7280" }}>.edi, .txt, or .x12 — or click to browse</div>
        <input ref={ref} type="file" accept=".edi,.txt,.x12,text/plain" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); }} />
      </div>
      {busy && <div style={{ marginTop: 12, fontSize: 13, color: "#6B7280" }}>Parsing…</div>}
      {error && <div style={{ marginTop: 12, fontSize: 13, color: "#B91C1C" }}>{error}</div>}
      {lastImport?.order && (
        <div style={{ marginTop: 16, padding: 14, borderRadius: 12, background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#166534", marginBottom: 6 }}>
            Imported PO {lastImport.order.poNumber}
            {lastImport.matchedPartner ? ` — matched ${lastImport.matchedPartner.retailerName}` : " — no partner matched"}
          </div>
          <div style={{ fontSize: 13, color: "#166534" }}>
            {lastImport.order.lineCount} lines · {lastImport.order.totalQty} units · ${lastImport.order.totalValue.toFixed(2)}
          </div>
        </div>
      )}
    </div>
  );
}

const input = { padding: "10px 12px", borderRadius: 10, border: "1px solid #E5E7EB", background: "#fff", fontFamily: "Inter, sans-serif", fontSize: 14, color: "#111827" };
const btnPrimary = { display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", borderRadius: 10, background: "#000", color: "#fff", border: "none", fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" };
const btnGhost = { padding: "6px 12px", borderRadius: 8, background: "#F3F4F6", color: "#111827", border: "none", fontSize: 12, fontWeight: 500, fontFamily: "Inter, sans-serif", cursor: "pointer" };

function IntegrationCard({ name, tagline, icon: Icon, brandColor, brandTint, connected, status, comingSoon, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={comingSoon}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        textAlign: "left",
        background: "#fff",
        border: `1px solid ${hover && !comingSoon ? "#D1D5DB" : "#F3F4F6"}`,
        borderRadius: 14,
        padding: "20px 20px 16px",
        cursor: comingSoon ? "default" : "pointer",
        fontFamily: "Inter, sans-serif",
        display: "flex", flexDirection: "column", gap: 14,
        minHeight: 180,
        transition: "border-color 0.15s, transform 0.15s, box-shadow 0.15s",
        transform: hover && !comingSoon ? "translateY(-1px)" : "translateY(0)",
        boxShadow: hover && !comingSoon ? "0 6px 18px rgba(0,0,0,0.05)" : "none",
        opacity: comingSoon ? 0.6 : 1,
      }}
    >
      <div style={{
        width: 52, height: 52, borderRadius: 14,
        background: brandTint,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={26} color={brandColor} strokeWidth={1.75} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#000", marginBottom: 4, letterSpacing: "-0.01em" }}>{name}</div>
        <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.5 }}>{tagline}</div>
      </div>
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        fontSize: 12, fontWeight: 500,
        padding: "4px 10px", borderRadius: 99,
        background: connected ? "#F0FDF4" : comingSoon ? "#F3F4F6" : "#FEF3C7",
        color: connected ? "#16A34A" : comingSoon ? "#9CA3AF" : "#B45309",
        alignSelf: "flex-start",
      }}>
        <span style={{ width: 6, height: 6, borderRadius: 3, background: connected ? "#16A34A" : comingSoon ? "#9CA3AF" : "#D97706" }} />
        {status}
      </span>
    </button>
  );
}

function IntegrationModal({
  title, icon: Icon, brandColor, brandTint, description, onClose,
  connected, connectedSubtitle, onDisconnect, connectedActions,
  steps, connectForm,
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9000,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24, fontFamily: "Inter, sans-serif",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 16,
          width: "100%", maxWidth: 560, maxHeight: "88vh",
          overflow: "auto",
          boxShadow: "0 24px 64px rgba(0,0,0,0.24)",
        }}
      >
        <div style={{ padding: "28px 32px 20px", borderBottom: "1px solid #F3F4F6" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: brandTint, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon size={24} color={brandColor} strokeWidth={1.75} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#000", letterSpacing: "-0.01em" }}>{title}</div>
              <div style={{ fontSize: 13, color: connected ? "#16A34A" : "#9CA3AF", fontWeight: 500, marginTop: 2 }}>
                {connected ? `Connected${connectedSubtitle ? ` · ${connectedSubtitle}` : ""}` : "Not connected"}
              </div>
            </div>
            <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6, color: "#6B7280" }} aria-label="Close">
              <X size={22} strokeWidth={1.6} />
            </button>
          </div>
          <div style={{ fontSize: 14, color: "#52525B", lineHeight: 1.6 }}>{description}</div>
        </div>

        {connected ? (
          <div style={{ padding: "24px 32px 28px" }}>
            <div style={{ fontSize: 14, color: "#16A34A", fontWeight: 600, marginBottom: 14 }}>You&rsquo;re all set.</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {connectedActions}
              <button
                onClick={onDisconnect}
                style={{
                  padding: "10px 14px", borderRadius: 10, border: "1px solid #FCA5A5",
                  background: "#FEF2F2", color: "#DC2626",
                  fontSize: 14, fontWeight: 500, fontFamily: "Inter, sans-serif",
                  cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                <Unlink size={14} strokeWidth={1.6} /> Disconnect
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: "20px 32px 28px" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#18181B", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              How to connect
            </div>
            <ol style={{ margin: 0, paddingLeft: 0, listStyle: "none", marginBottom: 22 }}>
              {steps.map((s, i) => (
                <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
                  <span style={{
                    flexShrink: 0, width: 22, height: 22, borderRadius: 11,
                    background: brandTint, color: brandColor,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 700, marginTop: 1,
                  }}>{i + 1}</span>
                  <span style={{ fontSize: 14, color: "#374151", lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: s }} />
                </li>
              ))}
            </ol>
            {connectForm}
          </div>
        )}
      </div>
    </div>
  );
}

/* Shipping provider cards — same visual language as the Integrations grid.
   Each card is a logo + name + status pill; clicking opens a modal with
   setup instructions and the per-provider credential inputs. The "active
   provider" selection lives as a button inside each modal. */
function ShippingProviderGrid({
  settings, shopConnected, provider, saveProvider,
  apiKey, setApiKey, keyMode, setKeyMode, saveApiKey,
  shippoKey, setShippoKey, saveShippo,
  ssKey, setSsKey, ssSecret, setSsSecret, saveShipStation,
  saving,
}) {
  const [open, setOpen] = useState(null);

  const cards = [
    {
      id: "easypost",
      name: "EasyPost",
      tagline: "One API for USPS, UPS, FedEx, DHL and more.",
      brandColor: "#0369A1", brandTint: "#E0F2FE",
      configured: settings?.easypostConfigured,
    },
    {
      id: "shippo",
      name: "Shippo",
      tagline: "Rate-shop and print labels across major carriers.",
      brandColor: "#6D28D9", brandTint: "#F5F3FF",
      configured: settings?.shippoConfigured,
    },
    {
      id: "shipstation",
      name: "ShipStation",
      tagline: "Multi-carrier shipping built for ecommerce volume.",
      brandColor: "#0F766E", brandTint: "#ECFDF5",
      configured: settings?.shipstationConfigured,
    },
    {
      id: "shopify",
      name: "Shopify Shipping",
      tagline: "Buy labels directly in your Shopify admin — no key needed.",
      brandColor: "#16A34A", brandTint: "#F0FDF4",
      /* Only usable once Shopify itself is connected. */
      configured: Boolean(shopConnected),
    },
  ];

  /* "Active" only when the provider is selected AND usable.
       - EasyPost/Shippo/ShipStation need API credentials to count as usable.
       - Shopify Shipping just needs Shopify connected (tracked via the
         card's `configured` flag from the parent). */
  const isUsable = (c) => Boolean(c.configured);
  const statusLabel = (c) => {
    if (provider === c.id && isUsable(c)) return "Active";
    if (c.id === "shopify") return c.configured ? "Ready to use" : "Connect Shopify first";
    return c.configured ? "Configured" : "Not connected";
  };
  const statusColor = (c) => {
    if (provider === c.id && isUsable(c)) return { bg: "#F0FDF4", fg: "#16A34A", dot: "#16A34A" };
    if (c.configured) return { bg: "#EFF6FF", fg: "#2563EB", dot: "#2563EB" };
    return { bg: "#FEF3C7", fg: "#B45309", dot: "#D97706" };
  };

  return (
    <>
      <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 20, marginTop: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#000", marginBottom: 6 }}>Pick your shipping provider</div>
        <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 14, lineHeight: 1.55 }}>
          Each provider has its own account. Click a card to see how to hook it up.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
          {cards.map(c => {
            const s = statusColor(c);
            const isActive = provider === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setOpen(c.id)}
                style={{
                  textAlign: "left",
                  background: "#fff",
                  border: `1px solid ${isActive ? "#000" : "#F3F4F6"}`,
                  borderRadius: 14,
                  padding: "18px 18px 14px",
                  cursor: "pointer",
                  fontFamily: "Inter, sans-serif",
                  display: "flex", flexDirection: "column", gap: 12,
                  minHeight: 160,
                  transition: "border-color 0.15s, transform 0.15s, box-shadow 0.15s",
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = "#D1D5DB"; e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.05)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = isActive ? "#000" : "#F3F4F6"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <div style={{ width: 46, height: 46, borderRadius: 12, background: c.brandTint, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Truck size={22} color={c.brandColor} strokeWidth={1.75} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#000", marginBottom: 3, letterSpacing: "-0.01em" }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.5 }}>{c.tagline}</div>
                </div>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  fontSize: 11, fontWeight: 600,
                  padding: "4px 10px", borderRadius: 99,
                  background: s.bg, color: s.fg,
                  alignSelf: "flex-start",
                  letterSpacing: "0.02em", textTransform: "uppercase",
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: 3, background: s.dot }} />
                  {statusLabel(c)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {open === "easypost" && (
        <IntegrationModal
          title="EasyPost"
          icon={Truck}
          brandColor="#0369A1"
          brandTint="#E0F2FE"
          description="EasyPost is an aggregator — one API, every major carrier (USPS, UPS, FedEx, DHL, regional). You sign up for EasyPost, they handle the carrier accounts."
          onClose={() => setOpen(null)}
          connected={settings?.easypostConfigured}
          connectedSubtitle={provider === "easypost" ? "Active" : (settings?.easypostKeyMode === "production" ? "Production key" : "Test key")}
          onDisconnect={null}
          connectedActions={provider !== "easypost" && (
            <button
              onClick={() => saveProvider("easypost")}
              style={{ padding: "10px 14px", borderRadius: 10, border: "none", background: "#000", color: "#fff", fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
            >
              Set as active provider
            </button>
          )}
          steps={[
            "Sign up at <strong>easypost.com</strong> (free — you pay only when you buy labels).",
            "In your EasyPost dashboard, go to <strong>API Keys</strong>. Use a Test key to try things out; switch to Production when you&rsquo;re ready for real charges.",
            "Paste the key below and click Save. We encrypt it on our server — the browser never sees it again.",
            "Click <strong>Set as active provider</strong> if EasyPost isn&rsquo;t already active.",
          ]}
          connectForm={(
            <div>
              <div style={{ display: "flex", gap: 8, alignItems: "stretch", marginBottom: 10 }}>
                <select value={keyMode} onChange={e => setKeyMode(e.target.value)} style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff" }}>
                  <option value="test">Test</option>
                  <option value="production">Production</option>
                </select>
                <input
                  type="password"
                  value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                  placeholder={settings?.easypostConfigured ? "•••••••• (update to replace)" : "Paste your EasyPost API key"}
                  style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff", outline: "none" }}
                />
                <button
                  onClick={() => { saveApiKey(); if (provider !== "easypost") saveProvider("easypost"); }}
                  disabled={!apiKey.trim() || saving}
                  style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: (!apiKey.trim() || saving) ? "#D1D5DB" : "#0369A1", color: "#fff", fontSize: 14, fontWeight: 600, cursor: (!apiKey.trim() || saving) ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}
                >Save</button>
              </div>
              {provider !== "easypost" && settings?.easypostConfigured && (
                <button onClick={() => saveProvider("easypost")} style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
                  Set as active provider
                </button>
              )}
            </div>
          )}
        />
      )}

      {open === "shippo" && (
        <IntegrationModal
          title="Shippo"
          icon={Truck}
          brandColor="#6D28D9"
          brandTint="#F5F3FF"
          description="Shippo is another aggregator — similar to EasyPost, different UX. Print labels across USPS, UPS, FedEx, DHL, and more with one token."
          onClose={() => setOpen(null)}
          connected={settings?.shippoConfigured}
          connectedSubtitle={provider === "shippo" ? "Active" : null}
          onDisconnect={null}
          connectedActions={provider !== "shippo" && (
            <button
              onClick={() => saveProvider("shippo")}
              style={{ padding: "10px 14px", borderRadius: 10, border: "none", background: "#000", color: "#fff", fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
            >
              Set as active provider
            </button>
          )}
          steps={[
            "Sign up or log in at <strong>goshippo.com</strong>.",
            "Go to <strong>API</strong> → <strong>Generate token</strong>. Test and live tokens both work.",
            "Paste the token below and click Save.",
            "Click <strong>Set as active provider</strong> to have Roaster use Shippo.",
          ]}
          connectForm={(
            <div>
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <input
                  type="password"
                  value={shippoKey}
                  onChange={e => setShippoKey(e.target.value)}
                  placeholder={settings?.shippoConfigured ? "•••••••• (update to replace)" : "Paste your Shippo token"}
                  style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff", outline: "none" }}
                />
                <button
                  onClick={() => { saveShippo(); if (provider !== "shippo") saveProvider("shippo"); }}
                  disabled={!shippoKey.trim() || saving}
                  style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: (!shippoKey.trim() || saving) ? "#D1D5DB" : "#6D28D9", color: "#fff", fontSize: 14, fontWeight: 600, cursor: (!shippoKey.trim() || saving) ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}
                >Save</button>
              </div>
              {provider !== "shippo" && settings?.shippoConfigured && (
                <button onClick={() => saveProvider("shippo")} style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
                  Set as active provider
                </button>
              )}
            </div>
          )}
        />
      )}

      {open === "shipstation" && (
        <IntegrationModal
          title="ShipStation"
          icon={Truck}
          brandColor="#0F766E"
          brandTint="#ECFDF5"
          description="ShipStation supports 40+ carriers with bulk label printing, rules automation, and an established ecosystem. You manage carrier accounts inside ShipStation."
          onClose={() => setOpen(null)}
          connected={settings?.shipstationConfigured}
          connectedSubtitle={provider === "shipstation" ? "Active" : null}
          onDisconnect={null}
          connectedActions={provider !== "shipstation" && (
            <button
              onClick={() => saveProvider("shipstation")}
              style={{ padding: "10px 14px", borderRadius: 10, border: "none", background: "#000", color: "#fff", fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
            >
              Set as active provider
            </button>
          )}
          steps={[
            "Sign in to ShipStation at <strong>shipstation.com</strong>.",
            "Go to <strong>Settings</strong> → <strong>Account</strong> → <strong>API Settings</strong>.",
            "Click <strong>Generate API Keys</strong>. You&rsquo;ll get both an <strong>API Key</strong> and an <strong>API Secret</strong> — you need both.",
            "Paste them below and click Save.",
            "Click <strong>Set as active provider</strong>.",
          ]}
          connectForm={(
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 10 }}>
                <input
                  type="password"
                  value={ssKey}
                  onChange={e => setSsKey(e.target.value)}
                  placeholder={settings?.shipstationConfigured ? "•••••••• API Key" : "API Key"}
                  style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff", outline: "none" }}
                />
                <input
                  type="password"
                  value={ssSecret}
                  onChange={e => setSsSecret(e.target.value)}
                  placeholder={settings?.shipstationConfigured ? "•••••••• API Secret" : "API Secret"}
                  style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff", outline: "none" }}
                />
                <button
                  onClick={() => { saveShipStation(); if (provider !== "shipstation") saveProvider("shipstation"); }}
                  disabled={!ssKey.trim() || !ssSecret.trim() || saving}
                  style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: (!ssKey.trim() || !ssSecret.trim() || saving) ? "#D1D5DB" : "#0F766E", color: "#fff", fontSize: 14, fontWeight: 600, cursor: (!ssKey.trim() || !ssSecret.trim() || saving) ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}
                >Save</button>
              </div>
              {provider !== "shipstation" && settings?.shipstationConfigured && (
                <button onClick={() => saveProvider("shipstation")} style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
                  Set as active provider
                </button>
              )}
            </div>
          )}
        />
      )}

      {open === "shopify" && (
        <IntegrationModal
          title="Shopify Shipping"
          icon={Truck}
          brandColor="#16A34A"
          brandTint="#F0FDF4"
          description="Shopify doesn&rsquo;t let third-party apps buy labels on your behalf — that has to happen inside your Shopify admin. When this provider is active, Roaster adds a &lsquo;Print in Shopify&rsquo; button on every order so you can buy the label there with one click."
          onClose={() => setOpen(null)}
          connected={provider === "shopify"}
          connectedSubtitle={provider === "shopify" ? "Active" : null}
          onDisconnect={null}
          connectedActions={null}
          steps={[
            "Click <strong>Use Shopify Shipping</strong> below to make it the active provider in Roaster.",
            "When you hit the Ship button on any order, we&rsquo;ll send you straight to that order in your Shopify admin.",
            "In Shopify, click <strong>Buy shipping label</strong>, pick a rate, and print.",
          ]}
          connectForm={provider !== "shopify" ? (
            <button
              onClick={() => { saveProvider("shopify"); setOpen(null); }}
              style={{ padding: "12px 20px", borderRadius: 10, border: "none", background: "#000", color: "#fff", fontSize: 15, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
            >
              Use Shopify Shipping
            </button>
          ) : null}
        />
      )}
    </>
  );
}

/* Small copy-to-clipboard box for the scopes list the merchant pastes
   into Shopify's "Configure Admin API access scopes" field. */
function ScopesBlock({ scopes }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(scopes); setCopied(true); setTimeout(() => setCopied(false), 1400); }
    catch { /* ignore */ }
  };
  return (
    <div style={{ background: "#F9FAFB", border: "1px solid #F3F4F6", borderRadius: 10, padding: "10px 12px", display: "flex", alignItems: "center", gap: 10 }}>
      <code style={{ flex: 1, fontSize: 12, fontFamily: "monospace", color: "#374151", overflow: "auto", whiteSpace: "nowrap" }}>{scopes}</code>
      <button
        onClick={copy}
        style={{
          padding: "5px 10px", borderRadius: 6, border: "1px solid #E5E7EB",
          background: copied ? "#F0FDF4" : "#fff", color: copied ? "#16A34A" : "#374151",
          fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}

/* Shopify modal — bespoke because Shopify supports multiple connected
   stores per workspace. Instead of the generic connected/not-connected
   toggle, we render a list of connected stores with per-store Sync and
   Disconnect, and always show the "add another store" form below.

   Two connect paths:
     OAuth install (preferred — needs the app to be Public or Custom with
       the store in the right Plus org)
     Manual token (fallback — the merchant generates an Admin API token
       inside their own Shopify admin and pastes it here). The fallback
       exists so new customers can onboard before our Public app clears
       App Store review. */
function ShopifyModal({
  onClose, stores,
  connectInput, setConnectInput, connectError, setConnectError,
  connectShopify, disconnectShopify, fetchShopifyOrders,
  user,
}) {
  const hasStores = stores.length > 0;
  const [shopHandle, setShopHandle] = useState("");
  const [inputError, setInputError] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);
  // "none" | "pending" | "approved"
  const [requestStatus, setRequestStatus] = useState(null);
  const [approvedInstallUrl, setApprovedInstallUrl] = useState(null);

  // Check if there's an existing pending/approved request for this workspace.
  // Poll every 10s while pending so the modal flips to the Install state as
  // soon as the platform admin approves — no manual refresh required.
  useEffect(() => {
    let cancelled = false;
    const poll = () => {
      fetch("/api/shopify/request-status")
        .then(r => r.ok ? r.json() : { status: "none" })
        .then(d => {
          if (cancelled) return;
          setRequestStatus(d.status);
          if (d.status === "pending") setRequested(true);
          if (d.status === "approved" && d.installUrl) setApprovedInstallUrl(d.installUrl);
        })
        .catch(() => { if (!cancelled) setRequestStatus("none"); });
    };
    poll();
    const id = setInterval(() => {
      // Stop polling once we've reached a terminal state (approved).
      if (approvedInstallUrl) return;
      poll();
    }, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [approvedInstallUrl]);

  // Wipe the workspace's pending/approved request and snap the modal
  // back to the empty input state. Useful when the install URL points
  // at a paused/dead dev store, or the user typed the wrong slug.
  const handleResetRequest = async () => {
    try {
      await fetch("/api/shopify/request-status", { method: "DELETE" });
    } catch { /* best-effort; UI still resets */ }
    setApprovedInstallUrl(null);
    setRequested(false);
    setRequestStatus("none");
    setShopHandle("");
    setInputError("");
  };

  // Pull the human-readable shop slug out of the install URL so the
  // approved copy can name the specific store ("…on your <slug>
  // Shopify store"). Install URL shape: https://<slug>.myshopify.com/...
  const approvedShopSlug = (() => {
    if (!approvedInstallUrl) return "";
    try {
      return new URL(approvedInstallUrl).hostname.replace(/\.myshopify\.com$/, "");
    } catch { return ""; }
  })();

  const handleConnect = async () => {
    const cleaned = shopHandle.trim().toLowerCase()
      .replace(/https?:\/\//, "").replace(/\/.*$/, "").replace(/\.myshopify\.com$/, "");
    if (!/^[a-z0-9][a-z0-9-]{1,}$/.test(cleaned)) { setInputError("Enter a valid store handle."); return; }
    setRequesting(true);
    setInputError("");
    try {
      const res = await fetch("/api/shopify/request-access", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shop: cleaned + ".myshopify.com" }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setInputError(data.error || "Something went wrong. Try again.");
        setRequesting(false);
        return;
      }
      setRequested(true);
    } catch {
      setInputError("Network error. Please try again.");
      setRequesting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9000,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24, fontFamily: "Inter, sans-serif",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 16,
          width: "100%", maxWidth: 560, maxHeight: "88vh", overflow: "auto",
          boxShadow: "0 24px 64px rgba(0,0,0,0.24)",
        }}
      >
        <div style={{ padding: "28px 32px 20px", borderBottom: "1px solid #F3F4F6" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: "#F0FDF4", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ShoppingBag size={24} color="#95BF47" strokeWidth={1.75} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#000", letterSpacing: "-0.01em" }}>Shopify</div>
              <div style={{ fontSize: 13, color: hasStores ? "#16A34A" : "#9CA3AF", fontWeight: 500, marginTop: 2 }}>
                {hasStores
                  ? (stores.length === 1 ? `${stores.length} store connected` : `${stores.length} stores connected`)
                  : "Not connected"}
              </div>
            </div>
            <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6, color: "#6B7280" }} aria-label="Close">
              <X size={22} strokeWidth={1.6} />
            </button>
          </div>
          <div style={{ fontSize: 14, color: "#52525B", lineHeight: 1.6 }}>
            Roaster pulls your Shopify orders and pushes production tags back. Connect as many stores as you need — each one counts toward the same workspace.
          </div>
        </div>

        {/* Connected stores */}
        {hasStores && (
          <div style={{ padding: "22px 32px 8px" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#18181B", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Connected stores
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {stores.map(s => (
                <div key={s.shop} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 14px", borderRadius: 10,
                  background: "#F9FAFB", border: "1px solid #F3F4F6",
                }}>
                  <Store size={16} color="#16A34A" strokeWidth={1.6} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.shop}</div>
                    {s.connectedAt && <div style={{ fontSize: 12, color: "#9CA3AF" }}>connected {new Date(s.connectedAt).toLocaleDateString()}</div>}
                  </div>
                  <button
                    onClick={() => fetchShopifyOrders(true)}
                    title="Sync orders"
                    style={{ border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 500, padding: "6px 10px", borderRadius: 8, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 5 }}
                  >
                    <RefreshCw size={12} strokeWidth={1.6} /> Sync
                  </button>
                  <button
                    onClick={() => disconnectShopify(s.shop)}
                    title="Disconnect this store"
                    style={{ border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#DC2626", fontSize: 13, fontWeight: 500, padding: "6px 10px", borderRadius: 8, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 5 }}
                  >
                    <Unlink size={12} strokeWidth={1.6} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Connect form */}
        <div style={{ padding: hasStores ? "18px 32px 28px" : "20px 32px 28px" }}>
          {hasStores && (
            <div style={{ fontSize: 12, fontWeight: 700, color: "#9CA3AF", marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Connect another store
            </div>
          )}

          {/* Approved → show install button */}
          {requestStatus === "approved" && approvedInstallUrl ? (
            <div style={{ textAlign: "center", padding: "12px 0 4px" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                <CheckCircle2 size={36} color="#16A34A" strokeWidth={1.5} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#000", marginBottom: 6 }}>You're approved!</div>
              <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.6, marginBottom: 20 }}>
                Click below to install the connector on your{" "}
                {approvedShopSlug && (
                  <strong style={{ color: "#000", fontWeight: 600 }}>{approvedShopSlug}</strong>
                )}
                {approvedShopSlug ? " " : ""}Shopify store.
              </div>
              <a
                href={approvedInstallUrl}
                style={{
                  display: "inline-block",
                  padding: "12px 28px", borderRadius: 10, border: "none",
                  background: "#000", color: "#fff",
                  fontSize: 14, fontWeight: 600, textDecoration: "none",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Install Connector →
              </a>
              {/* Escape hatch — wrong store, dead dev store, etc. */}
              <div style={{ marginTop: 14 }}>
                <button
                  onClick={handleResetRequest}
                  style={{
                    border: "none", background: "transparent",
                    color: "#9CA3AF", fontSize: 12, fontWeight: 500,
                    cursor: "pointer", padding: 4,
                    textDecoration: "underline", textUnderlineOffset: 3,
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Wrong store? Reset and start over
                </button>
              </div>
            </div>
          ) : requestStatus === null ? null : (
          <>
          {/* Pending approval notice — only while no request is in flight */}
          {!requested && !approvedInstallUrl && (
            <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, padding: "12px 14px", marginBottom: 16, fontSize: 13, color: "#92400E", lineHeight: 1.5 }}>
              <strong style={{ fontWeight: 600 }}>App approval pending.</strong> Our Shopify app is awaiting review. In the meantime, enter your store name below and we'll connect it manually for you.
            </div>
          )}

          {approvedInstallUrl ? (
            /* Approved — merchant can install. Hits Shopify's OAuth flow,
               which redirects back to /api/shopify/callback to finalize. */
            <div style={{ textAlign: "center", padding: "12px 0 4px" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                <CheckCircle2 size={36} color="#16A34A" strokeWidth={1.5} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#000", marginBottom: 6 }}>You're approved</div>
              <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.6, marginBottom: 18 }}>
                Tap Install to finish connecting your store to Shopify.
              </div>
              <button
                onClick={() => { window.location.href = approvedInstallUrl; }}
                style={{
                  padding: "12px 28px", borderRadius: 10, border: "none",
                  background: "#000", color: "#fff",
                  fontSize: 14, fontWeight: 600, cursor: "pointer",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Install
              </button>
            </div>
          ) : requested ? (
            /* Pending confirmation state */
            <div style={{ textAlign: "center", padding: "12px 0 4px" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                <CheckCircle2 size={36} color="#16A34A" strokeWidth={1.5} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#000", marginBottom: 6 }}>Request received</div>
              <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.6 }}>
                We'll send you a confirmation once your store has been connected.
              </div>
              <div style={{ marginTop: 14 }}>
                <button
                  onClick={handleResetRequest}
                  style={{
                    border: "none", background: "transparent",
                    color: "#9CA3AF", fontSize: 12, fontWeight: 500,
                    cursor: "pointer", padding: 4,
                    textDecoration: "underline", textUnderlineOffset: 3,
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Wrong store? Cancel and start over
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1, display: "flex", alignItems: "center", border: `1px solid ${inputError ? "#FCA5A5" : "#E5E7EB"}`, borderRadius: 10, overflow: "hidden", background: "#fff" }}>
                  <input
                    value={shopHandle}
                    onChange={e => { setShopHandle(e.target.value); setInputError(""); }}
                    onKeyDown={e => e.key === "Enter" && handleConnect()}
                    placeholder="your-store"
                    style={{ flex: 1, padding: "11px 14px", border: "none", fontSize: 14, fontFamily: "Inter, sans-serif", background: "transparent", outline: "none" }}
                  />
                  <span style={{ paddingRight: 12, fontSize: 13, color: "#9CA3AF", whiteSpace: "nowrap" }}>.myshopify.com</span>
                </div>
                <button
                  onClick={handleConnect}
                  disabled={!shopHandle.trim() || requesting}
                  style={{
                    padding: "11px 22px", borderRadius: 10, border: "none",
                    background: !shopHandle.trim() || requesting ? "#E5E7EB" : "#000000",
                    color: !shopHandle.trim() || requesting ? "#9CA3AF" : "#fff",
                    fontSize: 14, fontWeight: 600, cursor: !shopHandle.trim() || requesting ? "default" : "pointer",
                    fontFamily: "Inter, sans-serif", whiteSpace: "nowrap",
                  }}
                >
                  {requesting ? "Sending…" : "Request access"}
                </button>
              </div>
              {inputError && <div style={{ fontSize: 13, color: "#DC2626", marginTop: 8 }}>{inputError}</div>}
            </>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Costs settings ────────────────────────────────────────────────
   Indirect cost inputs that the analytics layer rolls into COGS:
     - per-role $/hr (Roaster, Packer, Cupper, Inventory, Delivery)
     - utilities $/hr while roasting (gas, electricity)
     - lb/hour roast capacity (drives the time estimates and the
       hourly multipliers above)
   Saves on blur (auto-save). Per-role hourly cost is the loaded
   labor rate — base wage + payroll taxes + benefits — so analytics
   gives us a true COGS number, not just direct labor. */
function CostRatesCard({ rates, onSave }) {
  const [local, setLocal] = useState(() => rates || null);
  useEffect(() => { if (rates) setLocal(rates); }, [rates]);
  if (!local) return <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>Loading…</div>;

  const numField = (path, value, placeholder, hint) => (
    <div>
      <label style={labelCss}>{value.label}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {value.prefix && <span style={{ fontSize: 13, color: "#6B7280" }}>{value.prefix}</span>}
        <input
          type="number" min="0" step="0.01"
          defaultValue={value.cur}
          placeholder={placeholder}
          style={inputCss}
          onBlur={(e) => {
            const v = e.target.value === "" ? null : Number(e.target.value);
            const next = JSON.parse(JSON.stringify(local));
            // Path is dot-delimited e.g. "roles.roaster"
            const [a, b] = path.split(".");
            next[a] = next[a] || {};
            next[a][b] = v == null ? 0 : v;
            setLocal(next);
            onSave({ [a]: { [b]: next[a][b] } });
          }}
        />
        {value.suffix && <span style={{ fontSize: 12, color: "#6B7280", whiteSpace: "nowrap" }}>{value.suffix}</span>}
      </div>
      {hint && <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 4 }}>{hint}</div>}
    </div>
  );
  const labelCss = { display: "block", fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 };
  const inputCss = { padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff", outline: "none", flex: 1, minWidth: 0, boxSizing: "border-box" };
  const cardCss  = { background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: 20, marginBottom: 14 };
  const sectionCss = { fontSize: 14, fontWeight: 700, color: "#000", marginBottom: 4, letterSpacing: "-0.01em" };
  const sectionHintCss = { fontSize: 12, color: "#6B7280", marginBottom: 14 };

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#000", margin: 0, letterSpacing: "-0.015em" }}>Cost rates &amp; throughput</h1>
        <p style={{ fontSize: 13, color: "#6B7280", margin: "4px 0 0", lineHeight: 1.5 }}>
          What it costs to operate, per hour. The analytics layer blends these into COGS alongside green-coffee cost. Roasting time estimates use the lb/hour throughput.
        </p>
      </div>

      {/* Throughput */}
      <div style={cardCss}>
        <div style={sectionCss}>Roasting throughput</div>
        <div style={sectionHintCss}>How many pounds of green you can put through the roaster per hour. Drives every "Est. roasting" readout and scales the hourly cost into a per-pound figure.</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, alignItems: "end" }}>
          {numField(
            "throughput.lbPerHour",
            { label: "Pounds per hour", cur: local.throughput.lbPerHour, suffix: "lb / hr" },
            "60",
            "Typical: 30–60 lb/hr for a 5kg sample roaster, 100–250 lb/hr for a 25kg production roaster."
          )}
          <div style={{ padding: "10px 14px", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, color: "#3F3F46" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Hourly direct cost</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#000", fontVariantNumeric: "tabular-nums" }}>
              ${((Number(local.roles.roaster) || 0) + (Number(local.utilities.gasPerHr) || 0) + (Number(local.utilities.electricPerHr) || 0)).toFixed(2)} / hr
            </div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
              Roaster + gas + electric. ≈ ${((Number(local.roles.roaster) || 0) + (Number(local.utilities.gasPerHr) || 0) + (Number(local.utilities.electricPerHr) || 0)) / Math.max(1, Number(local.throughput.lbPerHour) || 60) > 0 ? (((Number(local.roles.roaster) || 0) + (Number(local.utilities.gasPerHr) || 0) + (Number(local.utilities.electricPerHr) || 0)) / Math.max(1, Number(local.throughput.lbPerHour) || 60)).toFixed(3) : "0.000"} per pound roasted.
            </div>
          </div>
        </div>
      </div>

      {/* Roles */}
      <div style={cardCss}>
        <div style={sectionCss}>Hourly labor cost by role</div>
        <div style={sectionHintCss}>Loaded labor rate — base wage plus payroll taxes and benefits — so the analytics roll-up reflects true cost, not just take-home pay.</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          {[
            ["roaster",   "Roaster"],
            ["packer",    "Packer"],
            ["cupper",    "Cupper"],
            ["inventory", "Inventory"],
            ["delivery",  "Delivery"],
          ].map(([key, label]) => numField(
            `roles.${key}`,
            { label, cur: local.roles[key], prefix: "$", suffix: "/ hr" },
            "0.00"
          ))}
        </div>
      </div>

      {/* Utilities */}
      <div style={cardCss}>
        <div style={sectionCss}>Utilities while roasting</div>
        <div style={sectionHintCss}>Cost per hour of running the roaster. Pulled from your utility bills divided by roasting hours.</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {numField(
            "utilities.gasPerHr",
            { label: "Gas", cur: local.utilities.gasPerHr, prefix: "$", suffix: "/ hr" },
            "0.00"
          )}
          {numField(
            "utilities.electricPerHr",
            { label: "Electricity", cur: local.utilities.electricPerHr, prefix: "$", suffix: "/ hr" },
            "0.00"
          )}
        </div>
      </div>
    </div>
  );
}

function ShippingSettingsCard({ settings, loading, onSave, shopConnected }) {
  const [apiKey, setApiKey] = useState("");
  const [keyMode, setKeyMode] = useState(settings?.easypostKeyMode || "test");
  const [fromAddress, setFromAddress] = useState(settings?.fromAddress || {});
  const [presets, setPresets] = useState(settings?.parcelPresets || []);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [saveError, setSaveError] = useState("");
  const [newPreset, setNewPreset] = useState({ name: "", length: 8, width: 6, height: 4, weight: 16 });
  /* Multi-provider state: active provider + per-provider credentials. */
  const [provider, setProvider] = useState(settings?.provider || "easypost");
  const [shippoKey, setShippoKey] = useState("");
  const [ssKey, setSsKey] = useState("");
  const [ssSecret, setSsSecret] = useState("");

  // Sync when settings load.
  useEffect(() => {
    if (!settings) return;
    setKeyMode(settings.easypostKeyMode || "test");
    setFromAddress(settings.fromAddress || {});
    setPresets(settings.parcelPresets || []);
    setProvider(settings.provider || "easypost");
  }, [settings]);

  const save = async (patch) => {
    setSaving(true);
    setSaveMsg("");
    setSaveError("");
    const res = await onSave(patch);
    setSaving(false);
    if (res.ok) {
      setSaveMsg("Saved");
      setTimeout(() => setSaveMsg(""), 2000);
    } else {
      setSaveError(res.error || "Save failed");
    }
  };

  const saveProvider = (next) => { setProvider(next); save({ provider: next }); };
  const saveApiKey = () => {
    if (!apiKey.trim()) return;
    save({ easypostApiKey: apiKey.trim(), easypostKeyMode: keyMode });
    setApiKey("");
  };
  const saveShippo = () => {
    if (!shippoKey.trim()) return;
    save({ shippoApiKey: shippoKey.trim() });
    setShippoKey("");
  };
  const saveShipStation = () => {
    if (!ssKey.trim() || !ssSecret.trim()) return;
    save({ shipstationApiKey: ssKey.trim(), shipstationApiSecret: ssSecret.trim() });
    setSsKey(""); setSsSecret("");
  };

  const saveFromAddress = () => {
    save({ fromAddress });
  };

  const addPreset = () => {
    if (!newPreset.name.trim()) return;
    const next = [...presets, { ...newPreset }];
    setPresets(next);
    save({ parcelPresets: next });
    setNewPreset({ name: "", length: 8, width: 6, height: 4, weight: 16 });
  };

  const removePreset = (name) => {
    const next = presets.filter(p => p.name !== name);
    setPresets(next);
    save({ parcelPresets: next });
  };

  return (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 24, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: "#F0F9FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Truck size={20} color="#0369A1" strokeWidth={1.6} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: "#000000" }}>Shipping</div>
          <div style={{ fontSize: 15, color: "#6B7280", marginTop: 1 }}>EasyPost-powered label purchase, all rates (USPS, UPS, FedEx, DHL) through one API key.</div>
        </div>
        {settings?.easypostConfigured && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 500,
            padding: "3px 10px", borderRadius: 99,
            background: "#F0FDF4", color: "#16A34A",
          }}>
            <span style={{ width: 6, height: 6, borderRadius: 3, background: "#16A34A" }} />
            Connected
          </span>
        )}
      </div>

      {loading && !settings && (
        <div style={{ fontSize: 13, color: "#9CA3AF" }}>Loading shipping settings…</div>
      )}

      {/* Provider cards — click any to open a setup modal */}
      <ShippingProviderGrid
        settings={settings}
        shopConnected={shopConnected}
        provider={provider}
        saveProvider={saveProvider}
        apiKey={apiKey} setApiKey={setApiKey}
        keyMode={keyMode} setKeyMode={setKeyMode}
        saveApiKey={saveApiKey}
        shippoKey={shippoKey} setShippoKey={setShippoKey}
        saveShippo={saveShippo}
        ssKey={ssKey} setSsKey={setSsKey}
        ssSecret={ssSecret} setSsSecret={setSsSecret}
        saveShipStation={saveShipStation}
        saving={saving}
      />

      {/* From address */}
      <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 16, marginTop: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#000", marginBottom: 4 }}>Return address</div>
        <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 10, lineHeight: 1.5 }}>
          Used as the &ldquo;From&rdquo; on every label.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <input
            placeholder="Company / name"
            value={fromAddress.name || ""}
            onChange={e => setFromAddress(p => ({ ...p, name: e.target.value }))}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff" }}
          />
          <input
            placeholder="Phone"
            value={fromAddress.phone || ""}
            onChange={e => setFromAddress(p => ({ ...p, phone: e.target.value }))}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff" }}
          />
          <input
            placeholder="Street"
            value={fromAddress.street1 || ""}
            onChange={e => setFromAddress(p => ({ ...p, street1: e.target.value }))}
            style={{ gridColumn: "span 2", padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff" }}
          />
          <input
            placeholder="Suite / apt (optional)"
            value={fromAddress.street2 || ""}
            onChange={e => setFromAddress(p => ({ ...p, street2: e.target.value }))}
            style={{ gridColumn: "span 2", padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff" }}
          />
          <input
            placeholder="City"
            value={fromAddress.city || ""}
            onChange={e => setFromAddress(p => ({ ...p, city: e.target.value }))}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff" }}
          />
          <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", gap: 10 }}>
            <input
              placeholder="ST"
              maxLength="2"
              value={fromAddress.state || ""}
              onChange={e => setFromAddress(p => ({ ...p, state: e.target.value.toUpperCase() }))}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff" }}
            />
            <input
              placeholder="ZIP"
              value={fromAddress.zip || ""}
              onChange={e => setFromAddress(p => ({ ...p, zip: e.target.value }))}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff" }}
            />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
          <button
            onClick={saveFromAddress}
            disabled={saving}
            style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: saving ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}
          >
            Save address
          </button>
        </div>
      </div>

      {/* Parcel presets */}
      <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 16, marginTop: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#000", marginBottom: 4 }}>Parcel presets</div>
        <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 10, lineHeight: 1.5 }}>
          Pick the right box/bag at ship time. Dimensions in inches, weight in ounces.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {presets.map(p => (
            <div key={p.name} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "10px 12px", borderRadius: 8, border: "1px solid #F3F4F6", background: "#F9FAFB",
            }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#000" }}>{p.name}</div>
                <div style={{ fontSize: 12, color: "#6B7280", marginTop: 1 }}>
                  {p.length}×{p.width}×{p.height}&quot; · {p.weight} oz
                </div>
              </div>
              <button
                onClick={() => removePreset(p.name)}
                style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#B91C1C", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                <Trash2 size={11} /> Remove
              </button>
            </div>
          ))}
          {presets.length === 0 && (
            <div style={{ fontSize: 13, color: "#9CA3AF", padding: "8px 0" }}>No presets yet. Add one below.</div>
          )}
        </div>

        <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: "1px dashed #E5E7EB", background: "#fff" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.08, marginBottom: 8 }}>New preset</div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr auto", gap: 8, alignItems: "center" }}>
            <input
              placeholder="e.g. 1× 12oz bag"
              value={newPreset.name}
              onChange={e => setNewPreset(p => ({ ...p, name: e.target.value }))}
              style={{ padding: "7px 10px", borderRadius: 6, border: "1px solid #E5E7EB", fontSize: 13, fontFamily: "Inter, sans-serif", background: "#fff" }}
            />
            <input
              type="number"
              placeholder="L"
              value={newPreset.length}
              onChange={e => setNewPreset(p => ({ ...p, length: parseFloat(e.target.value) || 0 }))}
              style={{ padding: "7px 10px", borderRadius: 6, border: "1px solid #E5E7EB", fontSize: 13, fontFamily: "Inter, sans-serif", background: "#fff" }}
            />
            <input
              type="number"
              placeholder="W"
              value={newPreset.width}
              onChange={e => setNewPreset(p => ({ ...p, width: parseFloat(e.target.value) || 0 }))}
              style={{ padding: "7px 10px", borderRadius: 6, border: "1px solid #E5E7EB", fontSize: 13, fontFamily: "Inter, sans-serif", background: "#fff" }}
            />
            <input
              type="number"
              placeholder="H"
              value={newPreset.height}
              onChange={e => setNewPreset(p => ({ ...p, height: parseFloat(e.target.value) || 0 }))}
              style={{ padding: "7px 10px", borderRadius: 6, border: "1px solid #E5E7EB", fontSize: 13, fontFamily: "Inter, sans-serif", background: "#fff" }}
            />
            <input
              type="number"
              placeholder="oz"
              value={newPreset.weight}
              onChange={e => setNewPreset(p => ({ ...p, weight: parseFloat(e.target.value) || 0 }))}
              style={{ padding: "7px 10px", borderRadius: 6, border: "1px solid #E5E7EB", fontSize: 13, fontFamily: "Inter, sans-serif", background: "#fff" }}
            />
            <button
              onClick={addPreset}
              disabled={!newPreset.name.trim()}
              style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid #000", background: !newPreset.name.trim() ? "#D1D5DB" : "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: !newPreset.name.trim() ? "default" : "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <Plus size={12} /> Add
            </button>
          </div>
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6, display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr auto", gap: 8 }}>
            <div>Name</div>
            <div>Length (in)</div>
            <div>Width (in)</div>
            <div>Height (in)</div>
            <div>Weight (oz)</div>
            <div />
          </div>
        </div>
      </div>

      {saveError && (
        <div style={{ marginTop: 12, padding: 10, background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, fontSize: 13, color: "#B91C1C" }}>
          {saveError}
        </div>
      )}
      {saveMsg && (
        <div style={{ marginTop: 12, padding: 10, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, fontSize: 13, color: "#166534", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Check size={13} /> {saveMsg}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   WHOLESALE — variant inline edit row
   ═══════════════════════════════════════════════════════ */
function VariantEditRow({ variant, onSave, onCancel, onUploadPhoto, uploading }) {
  const [form, setForm] = useState({
    name: variant.name || "",
    price: variant.price != null ? String(variant.price) : "",
    cost: variant.cost != null ? String(variant.cost) : "",
    weightOz: variant.weightOz != null ? String(variant.weightOz) : "",
    active: variant.active !== false,
    sku: variant.sku || "",
  });
  const fs = { padding: "7px 10px", borderRadius: 7, border: "1px solid #E5E7EB", background: "#fff", fontFamily: "Inter, sans-serif", fontSize: 13, color: "#111827", boxSizing: "border-box", width: "100%" };
  const sf = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const photoSrc = (fn) => `/api/wholesale/photos/${encodeURIComponent(fn)}`;
  return (
    <div style={{ padding: "12px 14px", background: "#FAFAFA" }}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
        <input value={form.name} onChange={sf("name")} placeholder="Variant name" style={fs} />
        <input type="number" value={form.price} onChange={sf("price")} placeholder="Price $" style={fs} />
        <input type="number" value={form.cost} onChange={sf("cost")} placeholder="Cost $" style={fs} />
        <input type="number" value={form.weightOz} onChange={sf("weightOz")} placeholder="Weight oz" style={fs} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={form.active} onChange={sf("active")} />
          <span style={{ fontSize: 12, color: "#6B7280" }}>Active</span>
        </label>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          {(variant.photos || []).map(fn => (
            <img key={fn} src={photoSrc(fn)} alt="" style={{ width: 28, height: 28, borderRadius: 4, objectFit: "cover" }} />
          ))}
          <label style={{ width: 28, height: 28, borderRadius: 4, border: "1.5px dashed #D1D5DB", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) onUploadPhoto(f); e.target.value = ""; }} />
            {uploading ? <Loader size={10} color="#9CA3AF" /> : <Plus size={10} color="#9CA3AF" />}
          </label>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button onClick={onCancel} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#6B7280", fontSize: 12, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
          <button onClick={() => onSave({ name: form.name.trim(), price: form.price ? parseFloat(form.price) : null, cost: form.cost ? parseFloat(form.cost) : null, weightOz: form.weightOz ? parseFloat(form.weightOz) : null, active: form.active, sku: form.sku })} disabled={!form.name.trim()} style={{ padding: "5px 10px", borderRadius: 6, border: "none", background: !form.name.trim() ? "#D1D5DB" : "#000", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Save</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   WHOLESALE — product detail page (inline, replaces modal for editing)
   ═══════════════════════════════════════════════════════ */
function WholesaleProductDetail({ product: initialProduct, onBack, onUpdated, onDeleted }) {
  const [product, setProduct] = useState(initialProduct);
  const [form, setForm] = useState({
    name: initialProduct.name || "",
    category: initialProduct.category || "",
    description: initialProduct.description || "",
    weightOz: initialProduct.weightOz != null ? String(initialProduct.weightOz) : "",
    cost: initialProduct.cost != null ? String(initialProduct.cost) : "",
    price: initialProduct.price != null ? String(initialProduct.price) : "",
    active: initialProduct.active !== false,
  });
  const [photos, setPhotos] = useState(initialProduct.photos || []);
  const [variants, setVariants] = useState(initialProduct.variants || []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [variantPhotoUploading, setVariantPhotoUploading] = useState(null);
  const [editingVariant, setEditingVariant] = useState(null);
  const [newVar, setNewVar] = useState({ name: "", sku: "", price: "", cost: "", weightOz: "" });
  const [deleting, setDeleting] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(0);

  const photoSrc = (fn) => `/api/wholesale/photos/${encodeURIComponent(fn)}`;
  const sf = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const fld = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontFamily: "Inter, sans-serif", fontSize: 14, color: "#111827", boxSizing: "border-box" };
  const lbl = { display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 };

  const sync = (p) => { setProduct(p); onUpdated(p); };

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Product name is required"); return; }
    setSaving(true); setError(""); setSaved(false);
    try {
      const r = await fetch(`/api/wholesale/products/${product.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name.trim(), category: form.category, description: form.description, weightOz: form.weightOz ? parseFloat(form.weightOz) : null, cost: form.cost ? parseFloat(form.cost) : null, price: form.price ? parseFloat(form.price) : null, active: form.active }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Save failed");
      const updated = { ...d.product, photos, variants };
      sync(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const r = await fetch(`/api/wholesale/products/${product.id}`, { method: "DELETE" });
      if (r.ok) { onDeleted(product.id); onBack(); }
    } catch { /* ignore */ }
    finally { setDeleting(false); }
  };

  const readFile = (file) => new Promise((res, rej) => { const rd = new FileReader(); rd.onload = (e) => res(e.target.result); rd.onerror = rej; rd.readAsDataURL(file); });

  const uploadPhoto = async (file) => {
    setPhotoUploading(true);
    try {
      const dataUrl = await readFile(file);
      const r = await fetch(`/api/wholesale/products/${product.id}/photos`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ photoData: dataUrl }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Upload failed");
      setPhotos(d.photos);
      setSelectedPhoto(d.photos.length - 1);
      sync({ ...product, photos: d.photos, variants });
    } catch (e) { setError(e.message); }
    finally { setPhotoUploading(false); }
  };

  const removePhoto = async (fn, idx) => {
    const r = await fetch(`/api/wholesale/products/${product.id}/photos/${encodeURIComponent(fn)}`, { method: "DELETE" });
    const d = await r.json();
    if (r.ok) {
      setPhotos(d.photos);
      setSelectedPhoto(Math.min(selectedPhoto, d.photos.length - 1));
      sync({ ...product, photos: d.photos, variants });
    }
  };

  const addVariant = async () => {
    if (!newVar.name.trim()) return;
    const r = await fetch(`/api/wholesale/products/${product.id}/variants`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: newVar.name.trim(), sku: newVar.sku, price: newVar.price ? parseFloat(newVar.price) : null, cost: newVar.cost ? parseFloat(newVar.cost) : null, weightOz: newVar.weightOz ? parseFloat(newVar.weightOz) : null }) });
    const d = await r.json();
    if (r.ok) { setVariants(d.product.variants); setNewVar({ name: "", sku: "", price: "", cost: "", weightOz: "" }); sync(d.product); }
  };

  const removeVariant = async (vid) => {
    const r = await fetch(`/api/wholesale/products/${product.id}/variants/${vid}`, { method: "DELETE" });
    const d = await r.json();
    if (r.ok) { setVariants(d.product.variants); sync(d.product); }
  };

  const patchVariant = async (vid, changes) => {
    const r = await fetch(`/api/wholesale/products/${product.id}/variants/${vid}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(changes) });
    const d = await r.json();
    if (r.ok) { setVariants(d.product.variants); sync(d.product); }
  };

  const uploadVariantPhoto = async (vid, file) => {
    setVariantPhotoUploading(vid);
    try {
      const dataUrl = await readFile(file);
      const r = await fetch(`/api/wholesale/products/${product.id}/variants/${vid}/photos`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ photoData: dataUrl }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Upload failed");
      const updated = variants.map(v => v.id === vid ? { ...v, photos: d.photos } : v);
      setVariants(updated);
      sync({ ...product, photos, variants: updated });
    } catch (e) { setError(e.message); }
    finally { setVariantPhotoUploading(null); }
  };

  const activeCover = photos[selectedPhoto] || photos[0] || null;

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280", padding: 0, fontSize: 14, fontFamily: "Inter, sans-serif", display: "flex", alignItems: "center", gap: 5 }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 11L5 7L9 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
          Products
        </button>
        <span style={{ color: "#D1D5DB", fontSize: 14 }}>/</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>{form.name || product.name}</span>
        <span style={{ marginLeft: 4, fontSize: 12, fontWeight: 500, padding: "2px 8px", borderRadius: 99, background: form.active ? "#F0FDF4" : "#F3F4F6", color: form.active ? "#16A34A" : "#9CA3AF" }}>
          {form.active ? "Active" : "Inactive"}
        </span>
      </div>

      {error && <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C", fontSize: 13 }}>{error}</div>}

      {/* ── Two-column: photo left / info right ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24, alignItems: "start" }}>
        {/* Photo column */}
        <div>
          {/* Main photo */}
          <div style={{ aspectRatio: "1/1", borderRadius: 12, overflow: "hidden", background: "#F9FAFB", border: "1px solid #F3F4F6", marginBottom: 10, position: "relative" }}>
            {activeCover
              ? <img src={photoSrc(activeCover)} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#D1D5DB", gap: 8 }}>
                  <Package size={40} strokeWidth={1} />
                  <span style={{ fontSize: 13, color: "#D1D5DB" }}>No photos</span>
                </div>
            }
            {activeCover && (
              <button onClick={() => removePhoto(activeCover, selectedPhoto)} style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.55)", border: "none", borderRadius: "50%", width: 26, height: 26, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}><X size={12} /></button>
            )}
          </div>
          {/* Thumbnails + upload */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {photos.map((fn, i) => (
              <button key={fn} onClick={() => setSelectedPhoto(i)} style={{ width: 54, height: 54, borderRadius: 8, overflow: "hidden", padding: 0, border: selectedPhoto === i ? "2px solid #111" : "2px solid transparent", cursor: "pointer", background: "#F9FAFB", flexShrink: 0 }}>
                <img src={photoSrc(fn)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </button>
            ))}
            <label style={{ width: 54, height: 54, borderRadius: 8, border: "1.5px dashed #D1D5DB", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", gap: 2, background: "#FAFAFA", flexShrink: 0 }}>
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ""; }} />
              {photoUploading ? <Loader size={14} color="#9CA3AF" style={{ animation: "spin 0.7s linear infinite" }} /> : <Plus size={14} color="#9CA3AF" />}
              <span style={{ fontSize: 9, color: "#9CA3AF", lineHeight: 1 }}>Add</span>
            </label>
          </div>
        </div>

        {/* Info column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
            <div><label style={lbl}>Product name *</label><input value={form.name} onChange={sf("name")} placeholder="e.g. Ethiopia Yirgacheffe" style={fld} /></div>
            <div><label style={lbl}>Category</label><input value={form.category} onChange={sf("category")} placeholder="Single Origin" style={fld} /></div>
          </div>
          <div><label style={lbl}>Description</label><textarea value={form.description} onChange={sf("description")} placeholder="Flavor notes, processing, origin details…" rows={4} style={{ ...fld, resize: "vertical" }} /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><label style={lbl}>Weight (oz)</label><input type="number" min="0" step="0.1" value={form.weightOz} onChange={sf("weightOz")} placeholder="12" style={fld} /></div>
            <div><label style={lbl}>Cost ($)</label><input type="number" min="0" step="0.01" value={form.cost} onChange={sf("cost")} placeholder="8.00" style={fld} /></div>
            <div><label style={lbl}>Price ($)</label><input type="number" min="0" step="0.01" value={form.price} onChange={sf("price")} placeholder="18.00" style={fld} /></div>
          </div>
          {/* Active toggle */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderRadius: 10, border: "1px solid #F3F4F6", background: "#FAFAFA" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>Active</div>
              <div style={{ fontSize: 12, color: "#9CA3AF" }}>Visible to buyers</div>
            </div>
            <button onClick={() => setForm(f => ({ ...f, active: !f.active }))} style={{ width: 40, height: 22, borderRadius: 11, background: form.active ? "#000" : "#E5E7EB", border: "none", cursor: "pointer", position: "relative", flexShrink: 0, transition: "background 0.15s" }}>
              <span style={{ position: "absolute", top: 2, left: form.active ? 20 : 2, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.15s", boxShadow: "0 1px 3px rgba(0,0,0,0.15)" }} />
            </button>
          </div>
          {/* Actions */}
          <div style={{ display: "flex", gap: 8, paddingTop: 4 }}>
            <button onClick={handleSave} disabled={saving || !form.name.trim()} style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: saving || !form.name.trim() ? "#D1D5DB" : "#000", color: "#fff", fontSize: 14, fontWeight: 600, cursor: saving || !form.name.trim() ? "default" : "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
              {saving ? "Saving…" : saved ? <><Check size={14} strokeWidth={2.5} /> Saved</> : "Save changes"}
            </button>
            <button onClick={handleDelete} disabled={deleting} style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #FECACA", background: "#FEF2F2", color: "#B91C1C", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Variants section ── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#111" }}>Variants {variants.length > 0 && <span style={{ fontSize: 13, fontWeight: 500, color: "#9CA3AF" }}>({variants.length})</span>}</div>
        </div>
        {variants.length > 0 && (
          <div>
            {variants.map((v, i) => (
              <div key={v.id} style={{ borderBottom: i < variants.length - 1 ? "1px solid #F9FAFB" : "none" }}>
                {editingVariant === v.id ? (
                  <VariantEditRow
                    variant={v}
                    onSave={(ch) => { patchVariant(v.id, ch); setEditingVariant(null); }}
                    onCancel={() => setEditingVariant(null)}
                    onUploadPhoto={(file) => uploadVariantPhoto(v.id, file)}
                    uploading={variantPhotoUploading === v.id}
                  />
                ) : (
                  <div style={{ padding: "12px 20px", display: "flex", alignItems: "center", gap: 12 }}>
                    {(v.photos || []).length > 0
                      ? <img src={photoSrc(v.photos[0])} alt="" style={{ width: 36, height: 36, borderRadius: 6, objectFit: "cover", border: "1px solid #F3F4F6", flexShrink: 0 }} />
                      : <div style={{ width: 36, height: 36, borderRadius: 6, background: "#F9FAFB", border: "1px solid #F3F4F6", flexShrink: 0 }} />
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>{v.name}</div>
                      <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 1 }}>
                        {[v.sku && `SKU: ${v.sku}`, v.price != null && `$${v.price.toFixed(2)}`, v.weightOz != null && `${v.weightOz} oz`, v.active === false && "Inactive"].filter(Boolean).join(" · ") || "No details"}
                      </div>
                    </div>
                    <button onClick={() => setEditingVariant(v.id)} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 13, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Edit</button>
                    <button onClick={() => removeVariant(v.id)} style={{ padding: "6px 8px", borderRadius: 6, border: "none", background: "#FEF2F2", color: "#B91C1C", cursor: "pointer", display: "flex", alignItems: "center" }}><Trash2 size={13} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {/* Add variant row */}
        <div style={{ padding: "16px 20px", background: "#FAFAFA", borderTop: variants.length > 0 ? "1px solid #F3F4F6" : "none" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Add variant</div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
            <input value={newVar.name} onChange={e => setNewVar(v => ({ ...v, name: e.target.value }))} placeholder="Name (e.g. 12 oz)" style={{ ...fld, fontSize: 13, padding: "8px 10px" }} onKeyDown={e => e.key === "Enter" && addVariant()} />
            <input type="number" value={newVar.price} onChange={e => setNewVar(v => ({ ...v, price: e.target.value }))} placeholder="Price $" style={{ ...fld, fontSize: 13, padding: "8px 10px" }} />
            <input type="number" value={newVar.cost} onChange={e => setNewVar(v => ({ ...v, cost: e.target.value }))} placeholder="Cost $" style={{ ...fld, fontSize: 13, padding: "8px 10px" }} />
            <input type="number" value={newVar.weightOz} onChange={e => setNewVar(v => ({ ...v, weightOz: e.target.value }))} placeholder="Oz" style={{ ...fld, fontSize: 13, padding: "8px 10px" }} />
          </div>
          <button onClick={addVariant} disabled={!newVar.name.trim()} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: !newVar.name.trim() ? "#E5E7EB" : "#000", color: !newVar.name.trim() ? "#9CA3AF" : "#fff", fontSize: 13, fontWeight: 600, cursor: !newVar.name.trim() ? "default" : "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Plus size={13} /> Add variant
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   WHOLESALE — product modal (create + edit)
   ═══════════════════════════════════════════════════════ */
function WholesaleProductModal({ initial, onClose, onSaved }) {
  const isNew = initial === "new";
  const product = isNew ? null : initial;
  const [form, setForm] = useState({
    name: product?.name || "",
    category: product?.category || "",
    description: product?.description || "",
    weightOz: product?.weightOz != null ? String(product.weightOz) : "",
    cost: product?.cost != null ? String(product.cost) : "",
    price: product?.price != null ? String(product.price) : "",
    active: product?.active !== false,
  });
  const [photos, setPhotos] = useState(product?.photos || []);
  const [variants, setVariants] = useState(product?.variants || []);
  const [newVar, setNewVar] = useState({ name: "", sku: "", price: "", cost: "", weightOz: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("details");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [variantPhotoUploading, setVariantPhotoUploading] = useState(null);
  const [editingVariant, setEditingVariant] = useState(null);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const photoSrc = (fn) => `/api/wholesale/photos/${encodeURIComponent(fn)}`;
  const sf = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const fld = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontFamily: "Inter, sans-serif", fontSize: 14, color: "#111827", boxSizing: "border-box" };
  const lbl = { display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 };
  const tabSty = (t) => ({ fontSize: 13, fontWeight: 500, padding: "6px 12px", borderRadius: 8, border: "none", background: tab === t ? "#000" : "transparent", color: tab === t ? "#fff" : "#6B7280", cursor: "pointer", fontFamily: "Inter, sans-serif" });

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Product name is required"); return; }
    setSaving(true); setError("");
    try {
      const body = { name: form.name.trim(), category: form.category, description: form.description, weightOz: form.weightOz ? parseFloat(form.weightOz) : null, cost: form.cost ? parseFloat(form.cost) : null, price: form.price ? parseFloat(form.price) : null };
      if (isNew) {
        const r = await fetch("/api/wholesale/products", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Failed to create");
        onSaved(d.product);
      } else {
        const r = await fetch(`/api/wholesale/products/${product.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...body, active: form.active }) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Save failed");
        onSaved({ ...d.product, variants });
      }
    } catch (e) { setError(e.message || "Failed to save"); }
    finally { setSaving(false); }
  };

  const readFile = (file) => new Promise((res, rej) => { const rd = new FileReader(); rd.onload = (e) => res(e.target.result); rd.onerror = rej; rd.readAsDataURL(file); });

  const uploadPhoto = async (file) => {
    if (!product) return;
    setPhotoUploading(true);
    try {
      const dataUrl = await readFile(file);
      const r = await fetch(`/api/wholesale/products/${product.id}/photos`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ photoData: dataUrl }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Upload failed");
      setPhotos(d.photos);
      onSaved({ ...product, photos: d.photos, variants });
    } catch (e) { setError(e.message); }
    finally { setPhotoUploading(false); }
  };

  const removePhoto = async (fn) => {
    if (!product) return;
    const r = await fetch(`/api/wholesale/products/${product.id}/photos/${encodeURIComponent(fn)}`, { method: "DELETE" });
    const d = await r.json();
    if (r.ok) { setPhotos(d.photos); onSaved({ ...product, photos: d.photos, variants }); }
  };

  const addVariant = async () => {
    if (!newVar.name.trim() || !product) return;
    const r = await fetch(`/api/wholesale/products/${product.id}/variants`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: newVar.name.trim(), sku: newVar.sku, price: newVar.price ? parseFloat(newVar.price) : null, cost: newVar.cost ? parseFloat(newVar.cost) : null, weightOz: newVar.weightOz ? parseFloat(newVar.weightOz) : null }) });
    const d = await r.json();
    if (r.ok) { setVariants(d.product.variants); setNewVar({ name: "", sku: "", price: "", cost: "", weightOz: "" }); onSaved(d.product); }
  };

  const removeVariant = async (vid) => {
    if (!product) return;
    const r = await fetch(`/api/wholesale/products/${product.id}/variants/${vid}`, { method: "DELETE" });
    const d = await r.json();
    if (r.ok) { setVariants(d.product.variants); onSaved(d.product); }
  };

  const patchVariant = async (vid, changes) => {
    if (!product) return;
    const r = await fetch(`/api/wholesale/products/${product.id}/variants/${vid}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(changes) });
    const d = await r.json();
    if (r.ok) { setVariants(d.product.variants); onSaved(d.product); }
  };

  const uploadVariantPhoto = async (vid, file) => {
    if (!product) return;
    setVariantPhotoUploading(vid);
    try {
      const dataUrl = await readFile(file);
      const r = await fetch(`/api/wholesale/products/${product.id}/variants/${vid}/photos`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ photoData: dataUrl }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Upload failed");
      const updated = variants.map(v => v.id === vid ? { ...v, photos: d.photos } : v);
      setVariants(updated);
      onSaved({ ...product, photos, variants: updated });
    } catch (e) { setError(e.message); }
    finally { setVariantPhotoUploading(null); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 9000, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 640, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#111" }}>{isNew ? "New product" : (form.name || "Edit product")}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4, borderRadius: 6, display: "flex" }}><X size={18} /></button>
        </div>
        {/* Tabs (edit only) */}
        {!isNew && (
          <div style={{ padding: "12px 24px 0", display: "flex", gap: 4, flexShrink: 0 }}>
            <button style={tabSty("details")} onClick={() => setTab("details")}>Details</button>
            <button style={tabSty("photos")} onClick={() => setTab("photos")}>Photos{photos.length > 0 ? ` (${photos.length})` : ""}</button>
            <button style={tabSty("variants")} onClick={() => setTab("variants")}>Variants{variants.length > 0 ? ` (${variants.length})` : ""}</button>
          </div>
        )}
        <div style={{ padding: 24 }}>
          {error && <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C", fontSize: 13 }}>{error}</div>}

          {/* ── Details ── */}
          {(isNew || tab === "details") && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
                <div><label style={lbl}>Product name *</label><input value={form.name} onChange={sf("name")} placeholder="e.g. Ethiopia Yirgacheffe" style={fld} /></div>
                <div><label style={lbl}>Category</label><input value={form.category} onChange={sf("category")} placeholder="e.g. Single Origin" style={fld} /></div>
              </div>
              <div><label style={lbl}>Description</label><textarea value={form.description} onChange={sf("description")} placeholder="Flavor notes, processing, origin details…" rows={3} style={{ ...fld, resize: "vertical" }} /></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div><label style={lbl}>Weight (oz)</label><input type="number" min="0" step="0.1" value={form.weightOz} onChange={sf("weightOz")} placeholder="12" style={fld} /></div>
                <div><label style={lbl}>Cost ($)</label><input type="number" min="0" step="0.01" value={form.cost} onChange={sf("cost")} placeholder="8.00" style={fld} /></div>
                <div><label style={lbl}>Price ($)</label><input type="number" min="0" step="0.01" value={form.price} onChange={sf("price")} placeholder="18.00" style={fld} /></div>
              </div>
              {!isNew && (
                <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.active} onChange={sf("active")} />
                  <span style={{ fontSize: 14, color: "#374151" }}>Active (visible to buyers)</span>
                </label>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 4 }}>
                <button onClick={onClose} style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
                <button onClick={handleSave} disabled={saving || !form.name.trim()} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: saving || !form.name.trim() ? "#D1D5DB" : "#000", color: "#fff", fontSize: 14, fontWeight: 600, cursor: saving || !form.name.trim() ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>
                  {saving ? "Saving…" : isNew ? "Create product" : "Save changes"}
                </button>
              </div>
            </div>
          )}

          {/* ── Photos ── */}
          {!isNew && tab === "photos" && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 10, marginBottom: 12 }}>
                {photos.map(fn => (
                  <div key={fn} style={{ position: "relative", borderRadius: 10, overflow: "hidden", aspectRatio: "1/1", background: "#F9FAFB", border: "1px solid #F3F4F6" }}>
                    <img src={photoSrc(fn)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <button onClick={() => removePhoto(fn)} style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.55)", border: "none", borderRadius: "50%", width: 22, height: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}><X size={11} /></button>
                  </div>
                ))}
                <label style={{ borderRadius: 10, border: "1.5px dashed #D1D5DB", aspectRatio: "1/1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", gap: 4, background: "#FAFAFA" }}>
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ""; }} />
                  {photoUploading ? <Loader size={16} color="#9CA3AF" style={{ animation: "spin 0.7s linear infinite" }} /> : <Plus size={16} color="#9CA3AF" />}
                  <span style={{ fontSize: 11, color: "#9CA3AF" }}>Add photo</span>
                </label>
              </div>
              {photos.length === 0 && !photoUploading && <div style={{ textAlign: "center", padding: "24px 0", color: "#9CA3AF", fontSize: 14 }}>No photos yet — click "Add photo" to upload.</div>}
            </div>
          )}

          {/* ── Variants ── */}
          {!isNew && tab === "variants" && (
            <div>
              {variants.length > 0 && (
                <div style={{ marginBottom: 20, display: "flex", flexDirection: "column", gap: 6 }}>
                  {variants.map(v => (
                    <div key={v.id} style={{ border: "1px solid #F3F4F6", borderRadius: 10, overflow: "hidden" }}>
                      {editingVariant === v.id ? (
                        <VariantEditRow
                          variant={v}
                          onSave={(ch) => { patchVariant(v.id, ch); setEditingVariant(null); }}
                          onCancel={() => setEditingVariant(null)}
                          onUploadPhoto={(file) => uploadVariantPhoto(v.id, file)}
                          uploading={variantPhotoUploading === v.id}
                        />
                      ) : (
                        <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>{v.name}</div>
                            <div style={{ fontSize: 12, color: "#9CA3AF" }}>{[v.price != null && `$${v.price.toFixed(2)}`, v.weightOz != null && `${v.weightOz} oz`, v.active === false && "Inactive"].filter(Boolean).join(" · ") || "No details"}</div>
                          </div>
                          {(v.photos || []).length > 0 && (
                            <div style={{ display: "flex", gap: 3 }}>
                              {(v.photos || []).slice(0, 3).map(fn => <img key={fn} src={photoSrc(fn)} alt="" style={{ width: 26, height: 26, borderRadius: 4, objectFit: "cover" }} />)}
                            </div>
                          )}
                          <button onClick={() => setEditingVariant(v.id)} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 12, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Edit</button>
                          <button onClick={() => removeVariant(v.id)} style={{ padding: "5px 8px", borderRadius: 6, border: "none", background: "#FEF2F2", color: "#B91C1C", fontSize: 12, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "flex", alignItems: "center" }}><Trash2 size={12} /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* Add new variant row */}
              <div style={{ background: "#F9FAFB", borderRadius: 10, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 10 }}>Add variant</div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
                  <input value={newVar.name} onChange={e => setNewVar(v => ({ ...v, name: e.target.value }))} placeholder="Name (e.g. 12 oz)" style={{ ...fld, fontSize: 13, padding: "8px 10px" }} />
                  <input type="number" value={newVar.price} onChange={e => setNewVar(v => ({ ...v, price: e.target.value }))} placeholder="Price $" style={{ ...fld, fontSize: 13, padding: "8px 10px" }} />
                  <input type="number" value={newVar.cost} onChange={e => setNewVar(v => ({ ...v, cost: e.target.value }))} placeholder="Cost $" style={{ ...fld, fontSize: 13, padding: "8px 10px" }} />
                  <input type="number" value={newVar.weightOz} onChange={e => setNewVar(v => ({ ...v, weightOz: e.target.value }))} placeholder="Oz" style={{ ...fld, fontSize: 13, padding: "8px 10px" }} />
                </div>
                <button onClick={addVariant} disabled={!newVar.name.trim()} style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: !newVar.name.trim() ? "#D1D5DB" : "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: !newVar.name.trim() ? "default" : "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Plus size={13} /> Add variant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   WHOLESALE — customers view
   ═══════════════════════════════════════════════════════ */
// Net terms = timing only (no payment method). Net 0 means due
// immediately; the rest are calendar offsets from invoice date.
const NET_TERMS_OPTIONS = [
  { value: 0,  label: "Net 0" },
  { value: 15, label: "Net 15" },
  { value: 30, label: "Net 30" },
  { value: 45, label: "Net 45" },
  { value: 60, label: "Net 60" },
  { value: 90, label: "Net 90" },
];
// Payment method = how the bill gets paid.
//   - card  is the only viable Net 0 option (instant settlement)
//   - ach / check require >0 days to clear, so they're allowed on
//     Net 15+ only. UI gate enforces this — see netAllowsMethod().
const PAYMENT_METHOD_OPTIONS = [
  { value: "card",  label: "Credit Card" },
  { value: "ach",   label: "ACH / Bank Transfer" },
  { value: "check", label: "Check" },
];
// Returns true if the (term, method) pair is valid.
//   Net 0   → card only (instant settlement)
//   Net 15+ → ACH or Check only (cards aren't used on extended terms
//             in B2B coffee — they tie up a credit line on the buyer
//             side and the processing fee eats the AR cushion)
function netAllowsMethod(termValue, method) {
  if (Number(termValue) === 0) return method === "card";
  return method === "ach" || method === "check";
}
/* ── Shopify customer import modal ────────────────────────────────
   Lists Shopify customers (or those derived from order history when
   the customers API isn't authorized), letting the operator add them
   to the wholesale roster without sending an invite/notification.
   Add / Skip per row + bulk Add all. Skipped IDs persist locally. */
function ShopifyCustomerImportModal({ existingEmails, onClose, onCustomerCreated }) {
  const [shopCustomers, setShopCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [skipTab, setSkipTab] = useState("available"); // "available" | "skipped"
  const [skippedIds, setSkippedIds] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:wholesale:skippedShopifyCustomerIds");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:wholesale:skippedShopifyCustomerIds", JSON.stringify([...skippedIds])); }
    catch { /* ignore */ }
  }, [skippedIds]);
  const [addedEmails, setAddedEmails] = useState(new Set());
  const [addingId, setAddingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    setLoading(true); setError("");
    fetch("/api/shopify/customers", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.error || `HTTP ${r.status}`)))
      .then(d => setShopCustomers(d.customers || []))
      .catch(err => setError(typeof err === "string" ? err : "Couldn't load Shopify customers."))
      .finally(() => setLoading(false));
  }, []);

  const norm = (s) => String(s || "").trim().toLowerCase();
  const skipCustomer = (id) => setSkippedIds(prev => new Set([...prev, String(id)]));
  const unskipCustomer = (id) => setSkippedIds(prev => { const n = new Set(prev); n.delete(String(id)); return n; });

  const isExisting = (c) => existingEmails.has(norm(c.email)) || addedEmails.has(norm(c.email));

  const addCustomer = async (sc) => {
    setAddingId(sc.id);
    try {
      const fullName = [sc.first_name, sc.last_name].filter(Boolean).join(" ").trim();
      const addr = sc.default_address || (sc.addresses && sc.addresses[0]) || {};
      const r = await fetch("/api/wholesale/customers", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fullName,
          company: addr.company || "",
          email: norm(sc.email),
          phone: sc.phone || addr.phone || "",
          address: {
            address1: addr.address1 || "",
            address2: addr.address2 || "",
            city: addr.city || "",
            province: addr.province_code || addr.province || "",
            zip: addr.zip || "",
            country: addr.country_code || addr.country || "",
          },
          netTerms: 30,
          paymentMethod: "net_terms",
          notes: "",
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert(d.error || "Couldn't add customer.");
        return;
      }
      const d = await r.json();
      setAddedEmails(prev => new Set([...prev, norm(sc.email)]));
      // Surface in the parent list immediately. NOTE: no invite is
      // sent — the operator can hit "Send invite" on the customer
      // record later if/when they want.
      onCustomerCreated && onCustomerCreated(d.customer);
    } finally {
      setAddingId(null);
    }
  };

  const addAll = async () => {
    const queue = shopCustomers.filter(sc => !isExisting(sc) && !skippedIds.has(String(sc.id)) && norm(sc.email));
    if (queue.length === 0) return;
    setBulkBusy(true);
    for (const sc of queue) {
      try { await addCustomer(sc); } catch { /* keep going */ }
    }
    setBulkBusy(false);
  };

  const visible = shopCustomers.filter(sc => {
    const sid = String(sc.id);
    const skipped = skippedIds.has(sid);
    return skipTab === "skipped" ? skipped : !skipped;
  });

  const availCount = shopCustomers.filter(sc => !skippedIds.has(String(sc.id))).length;
  const skipCount  = shopCustomers.filter(sc =>  skippedIds.has(String(sc.id))).length;
  const remaining  = shopCustomers.filter(sc => !skippedIds.has(String(sc.id)) && !isExisting(sc) && norm(sc.email)).length;

  const fmtMoneyStr = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "Inter, sans-serif" }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 720, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#000" }}>Import customers from Shopify</h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6B7280" }}>Adds them as wholesale customers. <strong>No invite email is sent</strong> — you can manually invite later from the customer record.</p>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", color: "#9CA3AF", fontSize: 22, cursor: "pointer", padding: 4 }}>×</button>
        </div>

        {!loading && !error && shopCustomers.length > 0 && (
          <div style={{ padding: "12px 24px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", gap: 8 }}>
              {[
                { k: "available", l: "Available", n: availCount },
                { k: "skipped",   l: "Skipped",   n: skipCount },
              ].map(t => (
                <button
                  key={t.k}
                  onClick={() => setSkipTab(t.k)}
                  style={{ padding: "6px 11px", borderRadius: 99, border: `1px solid ${skipTab === t.k ? "#000" : "#E5E7EB"}`, background: skipTab === t.k ? "#000" : "#fff", color: skipTab === t.k ? "#fff" : "#3F3F46", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                >{t.l} <span style={{ opacity: 0.6, marginLeft: 4 }}>{t.n}</span></button>
              ))}
            </div>
            <button
              onClick={addAll}
              disabled={bulkBusy || remaining === 0 || skipTab !== "available"}
              style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #000", background: bulkBusy || remaining === 0 || skipTab !== "available" ? "#F4F4F5" : "#000", color: bulkBusy || remaining === 0 || skipTab !== "available" ? "#9CA3AF" : "#fff", fontSize: 12, fontWeight: 600, cursor: bulkBusy || remaining === 0 || skipTab !== "available" ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}
            >
              {bulkBusy ? "Adding…" : remaining === 0 ? "All added" : `+ Add all (${remaining})`}
            </button>
          </div>
        )}

        <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
          {loading && <div style={{ textAlign: "center", padding: "40px 0", color: "#9CA3AF", fontSize: 13 }}>Loading Shopify customers…</div>}
          {error && <div style={{ margin: "16px 24px", padding: "10px 14px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, color: "#991B1B", fontSize: 13 }}>{error}</div>}
          {!loading && !error && shopCustomers.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#9CA3AF", fontSize: 13 }}>No Shopify customers found.</div>
          )}
          {!loading && !error && visible.length === 0 && shopCustomers.length > 0 && (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#9CA3AF", fontSize: 13 }}>{skipTab === "skipped" ? "Nothing skipped." : "Nothing left to add."}</div>
          )}
          {!loading && !error && visible.map(sc => {
            const sid = String(sc.id);
            const fullName = [sc.first_name, sc.last_name].filter(Boolean).join(" ").trim() || (norm(sc.email) || "(no name)");
            const exists  = isExisting(sc);
            const skipped = skippedIds.has(sid);
            const adding = addingId === sid;
            return (
              <div key={sid} style={{ padding: "10px 24px", borderBottom: "1px solid #F9FAFB", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#000", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fullName}</div>
                  <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
                    {[sc.email, sc.orders_count != null ? `${sc.orders_count} order${sc.orders_count === 1 ? "" : "s"}` : null, sc.total_spent != null ? fmtMoneyStr(sc.total_spent) : null].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  {exists ? (
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#16A34A" }}>✓ Added</span>
                  ) : skipped ? (
                    <button onClick={() => unskipCustomer(sid)} style={{ padding: "6px 11px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Unskip</button>
                  ) : !norm(sc.email) ? (
                    <span style={{ fontSize: 11, color: "#9CA3AF" }}>No email on file</span>
                  ) : (
                    <>
                      <button onClick={() => addCustomer(sc)} disabled={adding} style={{ padding: "6px 11px", borderRadius: 6, border: "none", background: adding ? "#D1D5DB" : "#000", color: "#fff", fontSize: 12, fontWeight: 600, cursor: adding ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>
                        {adding ? "…" : "+ Add"}
                      </button>
                      <button onClick={() => skipCustomer(sid)} disabled={adding} style={{ padding: "6px 11px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#6B7280", fontSize: 12, fontWeight: 500, cursor: adding ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>
                        Skip
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function netTermsLabel(val) {
  const opt = NET_TERMS_OPTIONS.find(o => String(o.value) === String(val));
  return opt?.label ?? `Net ${val}`;
}

const BLANK_CUSTOMER = { name: "", company: "", email: "", phone: "", netTerms: 30, paymentMethod: "card", notes: "" };

function WholesaleCustomerDrawer({ customer, products = [], onClose, onSaved, onDeleted }) {
  const isNew = !customer;
  const [form, setForm] = useState(customer ? {
    name: customer.name, company: customer.company, email: customer.email,
    phone: customer.phone, netTerms: customer.netTerms,
    paymentMethod: customer.paymentMethod, notes: customer.notes,
    visibleProductIds: Array.isArray(customer.visibleProductIds) ? customer.visibleProductIds : [],
    visibleVariantIds: Array.isArray(customer.visibleVariantIds) ? customer.visibleVariantIds : [],
  } : { ...BLANK_CUSTOMER, visibleProductIds: [], visibleVariantIds: [] });
  const [expandedProductId, setExpandedProductId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteUrl, setInviteUrl] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const sf = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const fld = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontFamily: "Inter, sans-serif", fontSize: 14, color: "#111827", boxSizing: "border-box" };
  const lbl = { display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 };

  const handleSave = async () => {
    if (!form.name.trim() && !form.company.trim()) { setError("Name or company is required"); return; }
    setSaving(true); setError("");
    try {
      const url = isNew ? "/api/wholesale/customers" : `/api/wholesale/customers/${customer.id}`;
      const method = isNew ? "POST" : "PATCH";
      const r = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Save failed");
      onSaved(d.customer);
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleInvite = async () => {
    if (!customer) return;
    setInviting(true);
    try {
      const r = await fetch(`/api/wholesale/customers/${customer.id}/invite`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Failed");
      setInviteUrl(d.inviteUrl);
      onSaved(d.customer);
      // Surface the email-send result. Server wrapped sendEmail and
      // returned { emailSent, emailError, sentTo } so the UI can show
      // exactly what happened ("delivered to ..." vs "couldn't send: …").
      if (d.emailSent === false && d.emailError) {
        setError(`Invite saved, but email didn't send: ${d.emailError}`);
      } else if (d.emailSent && d.sentTo) {
        setError(""); // clear any prior error
      }
    } catch (e) { setError(e.message); }
    finally { setInviting(false); }
  };

  const handleDelete = async () => {
    if (!customer || !window.confirm(`Remove ${customer.company || customer.name}?`)) return;
    setDeleting(true);
    try {
      const r = await fetch(`/api/wholesale/customers/${customer.id}`, { method: "DELETE" });
      if (r.ok) { onDeleted(customer.id); onClose(); }
    } catch { /* ignore */ }
    finally { setDeleting(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 9000, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 420, height: "100%", background: "#fff", boxShadow: "-16px 0 48px rgba(0,0,0,0.12)", display: "flex", flexDirection: "column", overflowY: "auto" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#111" }}>{isNew ? "New customer" : (form.company || form.name || "Edit customer")}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4, borderRadius: 6, display: "flex" }}><X size={18} /></button>
        </div>

        <div style={{ padding: 24, flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
          {error && <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C", fontSize: 13 }}>{error}</div>}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><label style={lbl}>Business name</label><input value={form.company} onChange={sf("company")} placeholder="Blue Bottle Café" style={fld} /></div>
            <div><label style={lbl}>Contact name</label><input value={form.name} onChange={sf("name")} placeholder="Jane Smith" style={fld} /></div>
          </div>
          <div><label style={lbl}>Email</label><input type="email" value={form.email} onChange={sf("email")} placeholder="jane@bluebottle.com" style={fld} /></div>
          <div><label style={lbl}>Phone</label><input type="tel" value={form.phone} onChange={sf("phone")} placeholder="+1 555 000 0000" style={fld} /></div>

          <div style={{ height: 1, background: "#F3F4F6" }} />

          {/* Payment terms */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 12 }}>Payment terms</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={lbl}>Net terms</label>
                <select
                  value={String(form.netTerms)}
                  onChange={e => {
                    const next = Number(e.target.value);
                    setForm(f => ({
                      ...f,
                      netTerms: next,
                      // Snap the payment method into the valid set for
                      // the new term: Net 0 → card; Net 15+ → ach.
                      paymentMethod: next === 0
                        ? "card"
                        : (f.paymentMethod === "card" ? "ach" : f.paymentMethod),
                    }));
                  }}
                  style={{ ...fld, appearance: "none", cursor: "pointer" }}
                >
                  {NET_TERMS_OPTIONS.map(o => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Payment method</label>
                <select
                  value={netAllowsMethod(form.netTerms, form.paymentMethod)
                    ? form.paymentMethod
                    : (Number(form.netTerms) === 0 ? "card" : "ach")}
                  onChange={sf("paymentMethod")}
                  style={{ ...fld, appearance: "none", cursor: "pointer" }}
                >
                  {PAYMENT_METHOD_OPTIONS
                    .filter(o => netAllowsMethod(form.netTerms, o.value))
                    .map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {Number(form.netTerms) === 0 && (
                  <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>Net 0 settles instantly — card only.</div>
                )}
                {Number(form.netTerms) > 0 && (
                  <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>Cards aren't offered on extended terms — pick ACH or Check.</div>
                )}
              </div>
            </div>
          </div>

          <div><label style={lbl}>Notes</label><textarea value={form.notes} onChange={sf("notes")} placeholder="Delivery preferences, account notes…" rows={3} style={{ ...fld, resize: "vertical" }} /></div>

          {/* ── Visible products + variants ────────────────────────
               Per-customer catalog scoping at TWO levels:
               - visibleProductIds: which products this customer sees
               - visibleVariantIds: when set, narrows to specific
                 variants within those products
               Empty everywhere = sees the whole store (default).
               Click the chevron to expand a product and tick variants. */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <label style={{ ...lbl, marginBottom: 0 }}>Visible products</label>
              {((form.visibleProductIds || []).length > 0 || (form.visibleVariantIds || []).length > 0) && (
                <button type="button" onClick={() => setForm(f => ({ ...f, visibleProductIds: [], visibleVariantIds: [] }))} style={{ background: "transparent", border: "none", color: "#9CA3AF", fontSize: 11, fontWeight: 600, cursor: "pointer", textDecoration: "underline", padding: 0, fontFamily: "Inter, sans-serif" }}>
                  Show all (clear)
                </button>
              )}
            </div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 8 }}>
              {(form.visibleProductIds || []).length === 0 && (form.visibleVariantIds || []).length === 0
                ? "Sees every product in the store. Tick to restrict; expand to scope variants."
                : (() => {
                    const np = (form.visibleProductIds || []).length;
                    const nv = (form.visibleVariantIds || []).length;
                    const parts = [];
                    if (np > 0) parts.push(`${np} of ${products.length} product${np === 1 ? "" : "s"}`);
                    if (nv > 0) parts.push(`${nv} variant${nv === 1 ? "" : "s"} pinned`);
                    return `Sees ${parts.join(" · ")}.`;
                  })()}
            </div>
            <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid #E5E7EB", borderRadius: 8, background: "#fff" }}>
              {products.length === 0 ? (
                <div style={{ padding: "16px", color: "#9CA3AF", fontSize: 13, textAlign: "center" }}>No products yet — add some on the Products tab.</div>
              ) : products.map((p, i) => {
                const variants = (p.variants || []).filter(v => v.active !== false);
                const isExpanded = expandedProductId === p.id;
                const productChecked = (form.visibleProductIds || []).includes(p.id);
                const variantSelections = (form.visibleVariantIds || []).filter(id => variants.some(v => v.id === id));
                const hasVariantPin = variantSelections.length > 0;
                return (
                  <div key={p.id} style={{ borderTop: i === 0 ? "none" : "1px solid #F3F4F6" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px" }}>
                      <input
                        type="checkbox"
                        checked={productChecked}
                        onChange={() => setForm(f => {
                          const cur = new Set(f.visibleProductIds || []);
                          if (cur.has(p.id)) cur.delete(p.id); else cur.add(p.id);
                          return { ...f, visibleProductIds: [...cur] };
                        })}
                        style={{ width: 14, height: 14, cursor: "pointer", accentColor: "#000" }}
                      />
                      <span style={{ fontSize: 13, color: "#111", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</span>
                      {hasVariantPin && (
                        <span style={{ fontSize: 10, color: "#6B7280", padding: "1px 6px", background: "#F4F4F5", borderRadius: 99, fontWeight: 600 }}>
                          {variantSelections.length}/{variants.length}
                        </span>
                      )}
                      {variants.length > 0 && productChecked && (
                        <button
                          type="button"
                          onClick={() => setExpandedProductId(isExpanded ? null : p.id)}
                          style={{ background: "transparent", border: "none", color: hasVariantPin ? "#1D4ED8" : "#9CA3AF", cursor: "pointer", padding: "2px 6px", fontSize: 11, fontWeight: 600, textDecoration: "underline", fontFamily: "Inter, sans-serif" }}
                        >
                          {isExpanded ? "Hide variants" : (hasVariantPin ? "Edit variants" : "Pick variants")}
                        </button>
                      )}
                      {p.archivedAt && <span style={{ fontSize: 10, color: "#9CA3AF" }}>archived</span>}
                    </div>
                    {isExpanded && variants.length > 0 && (
                      <div style={{ paddingBottom: 6, paddingLeft: 36, background: "#FAFAFA", borderTop: "1px solid #F3F4F6" }}>
                        <div style={{ fontSize: 11, color: "#9CA3AF", padding: "6px 0" }}>
                          {hasVariantPin
                            ? "Only these variants are shown to this customer."
                            : "All variants shown — tick to restrict."}
                        </div>
                        {variants.map(v => {
                          const checked = (form.visibleVariantIds || []).includes(v.id);
                          return (
                            <label key={v.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 0", cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => setForm(f => {
                                  const cur = new Set(f.visibleVariantIds || []);
                                  if (cur.has(v.id)) cur.delete(v.id); else cur.add(v.id);
                                  return { ...f, visibleVariantIds: [...cur] };
                                })}
                                style={{ width: 13, height: 13, cursor: "pointer", accentColor: "#000" }}
                              />
                              <span style={{ fontSize: 12, color: "#3F3F46" }}>{v.name || "Default"}</span>
                              {v.price != null && <span style={{ fontSize: 11, color: "#9CA3AF" }}>${Number(v.price).toFixed(2)}</span>}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Invite link */}
          {!isNew && (
            <div style={{ background: "#F9FAFB", borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111", marginBottom: 4 }}>Ordering portal invite</div>
              {customer.invitedAt
                ? <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 8 }}>Last invited {new Date(customer.invitedAt).toLocaleDateString()}</div>
                : <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 8 }}>Not yet invited</div>
              }
              {inviteUrl
                ? (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <input readOnly value={inviteUrl} style={{ ...fld, fontSize: 12, flex: 1, color: "#374151", background: "#fff" }} onClick={e => e.target.select()} />
                    <button onClick={() => { navigator.clipboard.writeText(inviteUrl); }} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", cursor: "pointer", fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 600, flexShrink: 0 }}>Copy</button>
                  </div>
                ) : (
                  <button onClick={handleInvite} disabled={inviting} style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: inviting ? "#D1D5DB" : "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: inviting ? "default" : "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <UserPlus size={13} /> {inviting ? "Generating…" : customer.invitedAt ? "Resend invite" : "Send invite"}
                  </button>
                )
              }
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid #F3F4F6", flexShrink: 0, display: "flex", gap: 8 }}>
          {!isNew && (
            <button onClick={handleDelete} disabled={deleting} style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid #FECACA", background: "#FEF2F2", color: "#B91C1C", cursor: "pointer", display: "flex", alignItems: "center" }}><Trash2 size={14} /></button>
          )}
          <button onClick={onClose} style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: "10px 0", borderRadius: 8, border: "none", background: saving ? "#D1D5DB" : "#000", color: "#fff", fontSize: 14, fontWeight: 600, cursor: saving ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>
            {saving ? "Saving…" : isNew ? "Add customer" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function WholesaleCustomersView({ customers, products = [], config, onCustomerCreated, onCustomerUpdated, onCustomerDeleted }) {
  const [drawer, setDrawer] = useState(null); // null | "new" | customer obj
  const [search, setSearch] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const filtered = customers.filter(c => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (c.name + c.company + c.email).toLowerCase().includes(q);
  });

  const initials = (c) => {
    const src = c.company || c.name || "?";
    return src.split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase();
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ flex: 1, position: "relative" }}>
          <Search size={14} color="#9CA3AF" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customers…" style={{ width: "100%", padding: "9px 12px 9px 34px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontFamily: "Inter, sans-serif", fontSize: 14, color: "#111", boxSizing: "border-box" }} />
        </div>
        <button onClick={() => setImportOpen(true)} style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <Download size={14} /> Import from Shopify
        </button>
        <button onClick={() => setDrawer("new")} style={{ padding: "9px 16px", borderRadius: 8, border: "none", background: "#000", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <UserPlus size={14} /> Create client
        </button>
      </div>

      {importOpen && (
        <ShopifyCustomerImportModal
          existingEmails={new Set(customers.map(c => (c.email || "").trim().toLowerCase()).filter(Boolean))}
          onClose={() => setImportOpen(false)}
          onCustomerCreated={onCustomerCreated}
        />
      )}

      {customers.length === 0 ? (
        <div style={{ textAlign: "center", padding: "80px 0" }}>
          <Users size={32} color="#D1D5DB" strokeWidth={1.2} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 16, fontWeight: 500, color: "#374151", marginBottom: 6 }}>No customers yet</div>
          <div style={{ fontSize: 14, color: "#9CA3AF" }}>Add your first wholesale customer to get started.</div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#9CA3AF", fontSize: 14 }}>No customers match your search.</div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", overflow: "hidden" }}>
          {filtered.map((c, i) => (
            <div
              key={c.id}
              onClick={() => setDrawer(c)}
              style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 14, cursor: "pointer", borderBottom: i < filtered.length - 1 ? "1px solid #F9FAFB" : "none", transition: "background 0.08s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#FAFAFA"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              {/* Avatar */}
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#374151", flexShrink: 0, letterSpacing: "-0.5px" }}>
                {initials(c)}
              </div>
              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>{c.company || c.name}</div>
                <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 1 }}>
                  {[c.company && c.name, c.email].filter(Boolean).join(" · ")}
                </div>
              </div>
              {/* Invite / login state — quick at-a-glance signal so the
                  operator knows who's been invited, who's accepted, and
                  who's logged in recently. */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, flexShrink: 0, marginRight: 12, minWidth: 130 }}>
                {(() => {
                  const fmt = (ts) => ts ? new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
                  if (c.lastLoggedInAt) return (
                    <>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#047857", letterSpacing: "0.04em", textTransform: "uppercase" }}>Active</span>
                      <span style={{ fontSize: 11, color: "#9CA3AF" }}>Last login {fmt(c.lastLoggedInAt)}</span>
                    </>
                  );
                  if (c.invitedAt) return (
                    <>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#1D4ED8", letterSpacing: "0.04em", textTransform: "uppercase" }}>Invited</span>
                      <span style={{ fontSize: 11, color: "#9CA3AF" }}>Sent {fmt(c.invitedAt)}</span>
                    </>
                  );
                  return <span style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", letterSpacing: "0.04em", textTransform: "uppercase" }}>Not invited</span>;
                })()}
              </div>
              {/* Terms badge */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 6, background: "#F3F4F6", color: "#374151" }}>{netTermsLabel(c.netTerms)}</span>
                {c.invitedAt && (
                  <span style={{ fontSize: 11, color: "#9CA3AF", display: "flex", alignItems: "center", gap: 3 }}>
                    <Check size={10} strokeWidth={2.5} color="#10B981" /> Invited
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Drawer */}
      {drawer && (
        <WholesaleCustomerDrawer
          customer={drawer === "new" ? null : drawer}
          products={products}
          onClose={() => setDrawer(null)}
          onSaved={(c) => {
            if (drawer === "new") {
              onCustomerCreated(c);
              // Switch the drawer from "new" mode to the freshly-created
              // customer so the operator can immediately Send invite,
              // edit terms, etc. without needing to find the row again.
              setDrawer(c);
            } else {
              onCustomerUpdated(c);
            }
          }}
          onDeleted={onCustomerDeleted}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   SUPER-ADMIN PLAN SIMULATOR
   Only rendered for john@elionlabs.org. Lets the
   super-admin see every plan/addon combo without touching
   the database. Override lives in sessionStorage.
   ═══════════════════════════════════════════════════════ */
function PlanSimulatorPanel({ usage, planSim, simPlans, simAddons, onApply, onReset, onClose }) {
  const ACTIVE_COLOR = "#22D3EE";
  const ADDON_LABELS = { wholesale_lite: "Wholesale Lite", wholesale: "Wholesale Lite" };

  const ALL_MODULES = ["production", "lab", "finance", "analytics", "inventory"];
  const MODULE_LABELS = { production: "Production", lab: "Lab", finance: "Finance", analytics: "Analytics", inventory: "Inventory" };

  // Bundle presets — clicking fills the module override with the bundle's
  // module set so the paywall reflects what a customer on that bundle sees.
  const BUNDLE_PRESETS = [
    { id: "bundle_starter",    label: "Starter",    sub: "Prod + Inv",       modules: ["production", "inventory"] },
    { id: "bundle_operations", label: "Operations", sub: "+ Finance",        modules: ["finance", "inventory", "production"] },
    { id: "bundle_quality",    label: "Quality",    sub: "Lab + Prod + Ana", modules: ["lab", "production", "analytics"] },
    { id: "bundle_suite",      label: "Suite",      sub: "All 5 modules",    modules: ALL_MODULES },
  ];

  const [selPlan, setSelPlan] = useState(planSim?.plan || usage?.plan || "roaster");
  const [selAddons, setSelAddons] = useState(new Set(planSim?.addons || Object.keys(usage?.addons || {}).filter(k => usage.addons[k]?.status === "active")));
  // Modules: when the user picks individual modules in the simulator, the
  // sim drives the entitlement directly (overrides the plan-feature list).
  // Default = whatever the workspace currently owns, or all 5.
  const [selModules, setSelModules] = useState(new Set(planSim?.modules || usage?.modules || ALL_MODULES));
  const [moduleOverride, setModuleOverride] = useState(Array.isArray(planSim?.modules));
  const [onTrial, setOnTrial] = useState(planSim?.onTrial ?? false);
  const [importGrace, setImportGrace] = useState(planSim?.importGraceDaysLeft ?? 0);

  const toggleAddon = (k) => setSelAddons(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleModule = (k) => setSelModules(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const handleReset = () => {
    setSelPlan("custom");
    setSelAddons(new Set(["wholesale"]));
    setOnTrial(false);
    setImportGrace(0);
    onApply({ plan: "custom", addons: ["wholesale"], onTrial: false, importGraceDaysLeft: 0 });
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1200, display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pointerEvents: "none" }}>
      <div
        style={{
          marginLeft: 78, marginBottom: 12, background: "#0F172A",
          borderRadius: 14, padding: "20px 22px", width: 320,
          boxShadow: "0 8px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(34,211,238,0.2)",
          pointerEvents: "all", color: "#fff",
          fontFamily: "Inter, system-ui, sans-serif",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SlidersHorizontal size={14} style={{ color: "#22D3EE" }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: "#22D3EE", letterSpacing: "0.04em", textTransform: "uppercase" }}>Plan Simulator</span>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", color: "rgba(255,255,255,0.4)", padding: 2 }}><X size={14} /></button>
        </div>

        {/* Real plan indicator */}
        <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 7, padding: "6px 10px", marginBottom: 14, fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
          Real plan: <strong style={{ color: "rgba(255,255,255,0.7)" }}>{usage?.plan || "—"}</strong>
          {usage?.onTrial && " · trial"}
          {(Object.keys(usage?.addons || {}).filter(k => usage.addons[k]?.status === "active")).map(k => ` + ${k}`)}
        </div>

        {/* Bundle presets — quick-fill for the modules picker below */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Bundle preset</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {BUNDLE_PRESETS.map(p => {
              const setKey = [...p.modules].sort().join("|");
              const curKey = [...selModules].sort().join("|");
              const active = moduleOverride && setKey === curKey;
              return (
                <button
                  key={p.id}
                  onClick={() => { setSelModules(new Set(p.modules)); setModuleOverride(true); }}
                  style={{
                    padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                    border: active ? `1.5px solid ${ACTIVE_COLOR}` : "1.5px solid rgba(255,255,255,0.15)",
                    background: active ? ACTIVE_COLOR : "transparent",
                    color: active ? "#0F172A" : "rgba(255,255,255,0.55)",
                    fontSize: 13, fontWeight: 600, textAlign: "left",
                    transition: "all 0.12s",
                  }}
                >
                  {p.label}
                  <span style={{ fontSize: 10, opacity: 0.75, display: "block", marginTop: 1 }}>{p.sub}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Add-ons */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Add-ons</div>
          {["wholesale_lite"].map(k => (
            <label key={k} style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer", marginBottom: 6 }}>
              <div
                onClick={() => toggleAddon(k)}
                style={{
                  width: 18, height: 18, borderRadius: 5, border: selAddons.has(k) ? "none" : "1.5px solid rgba(255,255,255,0.25)",
                  background: selAddons.has(k) ? "#22D3EE" : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0,
                }}
              >
                {selAddons.has(k) && <Check size={11} style={{ color: "#0F172A", strokeWidth: 3 }} />}
              </div>
              <span style={{ fontSize: 13, color: selAddons.has(k) ? "#fff" : "rgba(255,255,255,0.55)" }}>{ADDON_LABELS[k] || k}</span>
            </label>
          ))}
        </div>

        {/* Modules — when enabled, drives the paywall directly */}
        <div style={{ marginBottom: 14, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Modules (paywall)</div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={moduleOverride}
                onChange={e => setModuleOverride(e.target.checked)}
                style={{ width: 13, height: 13, accentColor: "#22D3EE", cursor: "pointer" }}
              />
              <span style={{ fontSize: 11, color: moduleOverride ? "#22D3EE" : "rgba(255,255,255,0.4)" }}>Override</span>
            </label>
          </div>
          {moduleOverride && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5, marginBottom: 6 }}>
              {ALL_MODULES.map(k => (
                <label key={k} style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", padding: "5px 7px", borderRadius: 6, background: selModules.has(k) ? "rgba(34,211,238,0.1)" : "transparent" }}>
                  <div
                    onClick={() => toggleModule(k)}
                    style={{
                      width: 14, height: 14, borderRadius: 4, border: selModules.has(k) ? "none" : "1.5px solid rgba(255,255,255,0.25)",
                      background: selModules.has(k) ? "#22D3EE" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0,
                    }}
                  >
                    {selModules.has(k) && <Check size={9} style={{ color: "#0F172A", strokeWidth: 3 }} />}
                  </div>
                  <span style={{ fontSize: 12, color: selModules.has(k) ? "#fff" : "rgba(255,255,255,0.55)" }}>{MODULE_LABELS[k]}</span>
                </label>
              ))}
            </div>
          )}
          {moduleOverride && (
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <button onClick={() => setSelModules(new Set())} style={{ flex: 1, padding: "5px 0", borderRadius: 6, border: "1px solid rgba(255,255,255,0.15)", background: "transparent", color: "rgba(255,255,255,0.55)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>None</button>
              <button onClick={() => setSelModules(new Set(ALL_MODULES))} style={{ flex: 1, padding: "5px 0", borderRadius: 6, border: "1px solid rgba(255,255,255,0.15)", background: "transparent", color: "rgba(255,255,255,0.55)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>All</button>
            </div>
          )}
        </div>

        {/* Toggles */}
        <div style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer" }}>
            <div
              onClick={() => setOnTrial(v => !v)}
              style={{
                width: 18, height: 18, borderRadius: 5, border: onTrial ? "none" : "1.5px solid rgba(255,255,255,0.25)",
                background: onTrial ? "#F59E0B" : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0,
              }}
            >
              {onTrial && <Check size={11} style={{ color: "#fff", strokeWidth: 3 }} />}
            </div>
            <span style={{ fontSize: 13, color: onTrial ? "#F59E0B" : "rgba(255,255,255,0.55)" }}>On trial</span>
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", minWidth: 120 }}>Import grace days</span>
            <input
              type="number" min="0" max="7" value={importGrace}
              onChange={e => setImportGrace(Math.max(0, Math.min(7, Number(e.target.value))))}
              style={{ width: 48, padding: "4px 6px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.08)", color: "#fff", fontSize: 13, textAlign: "center" }}
            />
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>/ 7</span>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => onApply({
              plan: selPlan,
              addons: [...selAddons],
              modules: moduleOverride ? [...selModules] : undefined,
              onTrial,
              importGraceDaysLeft: importGrace,
            })}
            style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", background: "#22D3EE", color: "#0F172A", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
          >
            Apply
          </button>
          <button
            onClick={handleReset}
            style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)", background: "transparent", color: "rgba(255,255,255,0.6)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            Reset to admin
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   BILLING — InvoiceModal, POModal, BillingSettingsView
   ═══════════════════════════════════════════════════════ */

const BLANK_LINE_ITEM = () => ({ id: `li-${Math.random().toString(36).slice(2)}`, description: "", qty: 1, unitPrice: 0 });

function InvoiceModal({ mode, initial, billingSettings, wholesaleCustomers = [], onClose, onSaved, effectiveDemoMode }) {
  const today = new Date().toISOString().slice(0, 10);
  const addDays = (d, n) => { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt.toISOString().slice(0, 10); };
  const termsDays = billingSettings?.paymentTermsDays || 30;

  const [form, setForm] = useState(() => initial ? {
    clientName: initial.clientName || "",
    clientEmail: initial.clientEmail || "",
    clientAddress: initial.clientAddress || "",
    lineItems: initial.lineItems?.length ? initial.lineItems.map(l => ({ ...l })) : [BLANK_LINE_ITEM()],
    taxPct: initial.taxPct ?? 0,
    invoiceDate: initial.invoiceDate || today,
    dueDate: initial.dueDate || addDays(today, termsDays),
    notes: initial.notes || "",
    status: initial.status || "draft",
  } : {
    clientName: "", clientEmail: "", clientAddress: "",
    lineItems: [BLANK_LINE_ITEM()],
    taxPct: 0,
    invoiceDate: today,
    dueDate: addDays(today, termsDays),
    notes: "", status: "draft",
  });

  const [saving, setSaving] = useState(false);
  const [sendNow, setSendNow] = useState(false);
  const [error, setError] = useState("");

  const subtotal = form.lineItems.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unitPrice) || 0), 0);
  const taxAmt = +(subtotal * (Number(form.taxPct) || 0) / 100).toFixed(2);
  const total = +(subtotal + taxAmt).toFixed(2);

  const setLine = (i, k, v) => setForm(f => ({ ...f, lineItems: f.lineItems.map((l, j) => j === i ? { ...l, [k]: v } : l) }));
  const addLine = () => setForm(f => ({ ...f, lineItems: [...f.lineItems, BLANK_LINE_ITEM()] }));
  const removeLine = (i) => setForm(f => ({ ...f, lineItems: f.lineItems.filter((_, j) => j !== i) }));

  const clientSuggestions = [...new Set(wholesaleCustomers.map(c => c.company || `${c.firstName || ""} ${c.lastName || ""}`.trim()).filter(Boolean))];

  const handleSave = async () => {
    if (!form.clientName.trim()) { setError("Client name required"); return; }
    if (!form.lineItems.some(l => l.description || Number(l.unitPrice) > 0)) { setError("Add at least one line item"); return; }
    setSaving(true); setError("");
    try {
      if (effectiveDemoMode) {
        const now = Date.now();
        const todayStr = new Date().toISOString().slice(0, 10);
        const inv = {
          id: initial?.id || `inv_demo_${now}`,
          number: initial?.number || `INV-${String(1020 + Math.floor(Math.random() * 80)).padStart(4, "0")}`,
          ...form,
          subtotal: +subtotal.toFixed(2), taxAmt, total,
          status: sendNow ? "sent" : form.status,
          sentAt: sendNow ? now : (initial?.sentAt || null),
          paidAt: initial?.paidAt || null,
          createdAt: initial?.createdAt || now,
        };
        onSaved(inv);
      } else {
        const endpoint = mode === "new" ? "/api/billing/invoices" : `/api/billing/invoices/${initial.id}`;
        const method = mode === "new" ? "POST" : "PATCH";
        const r = await fetch(endpoint, {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...form, sendNow }),
        });
        if (!r.ok) { const e = await r.json(); setError(e.error || "Save failed"); setSaving(false); return; }
        const { invoice } = await r.json();
        onSaved(invoice);
      }
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 680, maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px", borderBottom: "1px solid #F3F4F6" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111827" }}>{mode === "new" ? "New invoice" : `Edit ${initial?.number}`}</div>
            <div style={{ fontSize: 13, color: "#9CA3AF", marginTop: 2 }}>Total: <strong style={{ color: "#111827" }}>${total.toFixed(2)}</strong></div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#6B7280", padding: 4 }}><X size={20} /></button>
        </div>
        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {error && <div style={{ background: "#FEF2F2", color: "#DC2626", padding: "8px 12px", borderRadius: 7, fontSize: 13, marginBottom: 14 }}>{error}</div>}
          {/* Client */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Client name *</label>
              <input list="inv-client-list" value={form.clientName} onChange={e => setForm(f => ({ ...f, clientName: e.target.value }))}
                style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7, border: "1px solid #D1D5DB", fontSize: 14 }} />
              <datalist id="inv-client-list">{clientSuggestions.map(s => <option key={s} value={s} />)}</datalist>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Client email</label>
              <input type="email" value={form.clientEmail} onChange={e => setForm(f => ({ ...f, clientEmail: e.target.value }))}
                style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7, border: "1px solid #D1D5DB", fontSize: 14 }} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Billing address</label>
            <input value={form.clientAddress} onChange={e => setForm(f => ({ ...f, clientAddress: e.target.value }))}
              placeholder="123 Main St, City, ST 00000"
              style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7, border: "1px solid #D1D5DB", fontSize: 14 }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Invoice date</label>
              <input type="date" value={form.invoiceDate} onChange={e => setForm(f => ({ ...f, invoiceDate: e.target.value }))}
                style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7, border: "1px solid #D1D5DB", fontSize: 14 }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Due date</label>
              <input type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
                style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7, border: "1px solid #D1D5DB", fontSize: 14 }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Tax %</label>
              <input type="number" min="0" max="100" step="0.1" value={form.taxPct} onChange={e => setForm(f => ({ ...f, taxPct: e.target.value }))}
                style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7, border: "1px solid #D1D5DB", fontSize: 14 }} />
            </div>
          </div>
          {/* Line items */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Line items</label>
              <button onClick={addLine} style={{ fontSize: 12, fontWeight: 500, color: "#2563EB", border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                <Plus size={12} /> Add line
              </button>
            </div>
            <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 70px 90px 80px 32px", gap: 0, background: "#F9FAFB", padding: "6px 10px", borderBottom: "1px solid #E5E7EB" }}>
                {["Description","Qty","Unit price","Total",""].map(h => (
                  <div key={h} style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</div>
                ))}
              </div>
              {form.lineItems.map((l, i) => (
                <div key={l.id} style={{ display: "grid", gridTemplateColumns: "1fr 70px 90px 80px 32px", gap: 0, padding: "6px 10px", borderBottom: i < form.lineItems.length - 1 ? "1px solid #F3F4F6" : "none", alignItems: "center" }}>
                  <input value={l.description} onChange={e => setLine(i, "description", e.target.value)}
                    placeholder="e.g. Ethiopia Yirgacheffe 5lb × 12"
                    style={{ padding: "5px 6px", borderRadius: 5, border: "1px solid #E5E7EB", fontSize: 13, width: "100%", boxSizing: "border-box" }} />
                  <input type="number" min="0" value={l.qty} onChange={e => setLine(i, "qty", e.target.value)}
                    style={{ padding: "5px 6px", borderRadius: 5, border: "1px solid #E5E7EB", fontSize: 13, width: "100%", boxSizing: "border-box", marginLeft: 4 }} />
                  <input type="number" min="0" step="0.01" value={l.unitPrice} onChange={e => setLine(i, "unitPrice", e.target.value)}
                    style={{ padding: "5px 6px", borderRadius: 5, border: "1px solid #E5E7EB", fontSize: 13, width: "100%", boxSizing: "border-box", marginLeft: 4 }} />
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", textAlign: "right", paddingRight: 4 }}>
                    ${((Number(l.qty) || 0) * (Number(l.unitPrice) || 0)).toFixed(2)}
                  </div>
                  <button onClick={() => removeLine(i)} disabled={form.lineItems.length === 1}
                    style={{ border: "none", background: "transparent", cursor: form.lineItems.length === 1 ? "not-allowed" : "pointer", color: "#DC2626", opacity: form.lineItems.length === 1 ? 0.3 : 1, padding: 4 }}>
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
            {/* Totals */}
            <div style={{ textAlign: "right", marginTop: 8 }}>
              {Number(form.taxPct) > 0 && (
                <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 2 }}>Subtotal: ${subtotal.toFixed(2)} · Tax ({form.taxPct}%): ${taxAmt.toFixed(2)}</div>
              )}
              <div style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>Total ${total.toFixed(2)}</div>
            </div>
          </div>
          {/* Notes */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Notes / payment instructions</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2} placeholder="Net-30 terms. Payable by ACH or check."
              style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7, border: "1px solid #D1D5DB", fontSize: 13, resize: "vertical", fontFamily: "Inter, sans-serif" }} />
          </div>
        </div>
        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid #F3F4F6", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151", cursor: "pointer" }}>
            <input type="checkbox" checked={sendNow} onChange={e => setSendNow(e.target.checked)} style={{ width: 14, height: 14 }} />
            Send by email now
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 7, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 500, cursor: "pointer", color: "#374151" }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: "8px 20px", borderRadius: 7, border: "none", background: "#111827", color: "#fff", fontSize: 14, fontWeight: 600, cursor: saving ? "wait" : "pointer" }}>
              {saving ? "Saving…" : sendNow ? "Save & send" : "Save invoice"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function POModal({ mode, initial, greenLots = [], onClose, onSaved, effectiveDemoMode }) {
  const today = new Date().toISOString().slice(0, 10);
  const addDays = (d, n) => { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt.toISOString().slice(0, 10); };

  const [form, setForm] = useState(() => initial ? {
    supplierName: initial.supplierName || "",
    supplierEmail: initial.supplierEmail || "",
    lotId: initial.lotId || "",
    lotName: initial.lotName || "",
    lineItems: initial.lineItems?.length ? initial.lineItems.map(l => ({ ...l })) : [BLANK_LINE_ITEM()],
    poDate: initial.poDate || today,
    expectedDate: initial.expectedDate || addDays(today, 45),
    notes: initial.notes || "",
    status: initial.status || "draft",
  } : {
    supplierName: "", supplierEmail: "", lotId: "", lotName: "",
    lineItems: [BLANK_LINE_ITEM()],
    poDate: today,
    expectedDate: addDays(today, 45),
    notes: "", status: "draft",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const total = form.lineItems.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unitPrice) || 0), 0);
  const setLine = (i, k, v) => setForm(f => ({ ...f, lineItems: f.lineItems.map((l, j) => j === i ? { ...l, [k]: v } : l) }));
  const addLine = () => setForm(f => ({ ...f, lineItems: [...f.lineItems, BLANK_LINE_ITEM()] }));
  const removeLine = (i) => setForm(f => ({ ...f, lineItems: f.lineItems.filter((_, j) => j !== i) }));

  const handleSave = async () => {
    if (!form.supplierName.trim()) { setError("Supplier name required"); return; }
    setSaving(true); setError("");
    try {
      if (effectiveDemoMode) {
        const now = Date.now();
        const po = {
          id: initial?.id || `po_demo_${now}`,
          number: initial?.number || `PO-${String(10 + Math.floor(Math.random() * 90)).padStart(4, "0")}`,
          ...form,
          subtotal: +total.toFixed(2), total: +total.toFixed(2),
          createdAt: initial?.createdAt || now,
        };
        onSaved(po);
      } else {
        const endpoint = mode === "new" ? "/api/billing/pos" : `/api/billing/pos/${initial.id}`;
        const method = mode === "new" ? "POST" : "PATCH";
        const r = await fetch(endpoint, {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        if (!r.ok) { const e = await r.json(); setError(e.error || "Save failed"); setSaving(false); return; }
        const { po } = await r.json();
        onSaved(po);
      }
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 640, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px", borderBottom: "1px solid #F3F4F6" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111827" }}>{mode === "new" ? "New purchase order" : `Edit ${initial?.number}`}</div>
            <div style={{ fontSize: 13, color: "#9CA3AF", marginTop: 2 }}>Total: <strong style={{ color: "#111827" }}>${total.toFixed(2)}</strong></div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#6B7280", padding: 4 }}><X size={20} /></button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {error && <div style={{ background: "#FEF2F2", color: "#DC2626", padding: "8px 12px", borderRadius: 7, fontSize: 13, marginBottom: 14 }}>{error}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Supplier name *</label>
              <input value={form.supplierName} onChange={e => setForm(f => ({ ...f, supplierName: e.target.value }))}
                placeholder="e.g. Mercanta Coffee Hunters"
                style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7, border: "1px solid #D1D5DB", fontSize: 14 }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Supplier email</label>
              <input type="email" value={form.supplierEmail} onChange={e => setForm(f => ({ ...f, supplierEmail: e.target.value }))}
                style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7, border: "1px solid #D1D5DB", fontSize: 14 }} />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Link to inventory lot (optional)</label>
            <select value={form.lotId} onChange={e => { const lot = greenLots.find(l => l.id === e.target.value); setForm(f => ({ ...f, lotId: e.target.value, lotName: lot?.name || "" })); }}
              style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7, border: "1px solid #D1D5DB", fontSize: 14 }}>
              <option value="">— no lot —</option>
              {greenLots.filter(l => !l.archivedAt).map(l => <option key={l.id} value={l.id}>{l.name} ({l.origin})</option>)}
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>PO date</label>
              <input type="date" value={form.poDate} onChange={e => setForm(f => ({ ...f, poDate: e.target.value }))}
                style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7, border: "1px solid #D1D5DB", fontSize: 14 }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Expected delivery</label>
              <input type="date" value={form.expectedDate || ""} onChange={e => setForm(f => ({ ...f, expectedDate: e.target.value }))}
                style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7, border: "1px solid #D1D5DB", fontSize: 14 }} />
            </div>
          </div>
          {/* Line items */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Line items</label>
              <button onClick={addLine} style={{ fontSize: 12, fontWeight: 500, color: "#2563EB", border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                <Plus size={12} /> Add line
              </button>
            </div>
            <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 70px 90px 80px 32px", background: "#F9FAFB", padding: "6px 10px", borderBottom: "1px solid #E5E7EB" }}>
                {["Description","Qty","Unit price","Total",""].map(h => (
                  <div key={h} style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</div>
                ))}
              </div>
              {form.lineItems.map((l, i) => (
                <div key={l.id} style={{ display: "grid", gridTemplateColumns: "1fr 70px 90px 80px 32px", padding: "6px 10px", borderBottom: i < form.lineItems.length - 1 ? "1px solid #F3F4F6" : "none", alignItems: "center" }}>
                  <input value={l.description} onChange={e => setLine(i, "description", e.target.value)}
                    placeholder="Green coffee description"
                    style={{ padding: "5px 6px", borderRadius: 5, border: "1px solid #E5E7EB", fontSize: 13, width: "100%", boxSizing: "border-box" }} />
                  <input type="number" min="0" value={l.qty} onChange={e => setLine(i, "qty", e.target.value)}
                    style={{ padding: "5px 6px", borderRadius: 5, border: "1px solid #E5E7EB", fontSize: 13, width: "100%", boxSizing: "border-box", marginLeft: 4 }} />
                  <input type="number" min="0" step="0.01" value={l.unitPrice} onChange={e => setLine(i, "unitPrice", e.target.value)}
                    style={{ padding: "5px 6px", borderRadius: 5, border: "1px solid #E5E7EB", fontSize: 13, width: "100%", boxSizing: "border-box", marginLeft: 4 }} />
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", textAlign: "right", paddingRight: 4 }}>
                    ${((Number(l.qty) || 0) * (Number(l.unitPrice) || 0)).toFixed(2)}
                  </div>
                  <button onClick={() => removeLine(i)} disabled={form.lineItems.length === 1}
                    style={{ border: "none", background: "transparent", cursor: form.lineItems.length === 1 ? "not-allowed" : "pointer", color: "#DC2626", opacity: form.lineItems.length === 1 ? 0.3 : 1, padding: 4 }}>
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
            <div style={{ textAlign: "right", marginTop: 6 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>Total ${total.toFixed(2)}</div>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2} style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7, border: "1px solid #D1D5DB", fontSize: 13, resize: "vertical", fontFamily: "Inter, sans-serif" }} />
          </div>
        </div>
        <div style={{ padding: "14px 24px", borderTop: "1px solid #F3F4F6", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 7, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 500, cursor: "pointer", color: "#374151" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: "8px 20px", borderRadius: 7, border: "none", background: "#111827", color: "#fff", fontSize: 14, fontWeight: 600, cursor: saving ? "wait" : "pointer" }}>
            {saving ? "Saving…" : "Save PO"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Email settings — per-workspace outbound identity ───────────
   Each workspace picks a handle on the shared verified domain (e.g.
   `progeny@finance.elionlabs.org`), a display name shown in the From:
   header, and a Reply-To that routes replies back to them. The handle
   must be unique across workspaces. After save, a "Send test" button
   delivers a preview email to the current user so they can confirm
   deliverability before sending to real customers. */
function EmailSettingsCard({ userEmail }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok, error, to}
  const [testTo, setTestTo] = useState(userEmail || "");
  // Customer picker for the preview — an empty string means "use sample
  // data". When set, the test email renders with that real customer's
  // outstanding orders (still delivered to `testTo`, not to them).
  const [previewClientId, setPreviewClientId] = useState("");
  const [clientsList, setClientsList] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [server, setServer] = useState(null);
  const [form, setForm] = useState({ localPart: "", displayName: "", replyTo: "" });
  const [errors, setErrors] = useState({});
  const [availability, setAvailability] = useState(null); // {available, reason}
  const [checking, setChecking] = useState(false);

  // Once userEmail arrives (auth load may resolve after mount), prefill
  // the test-recipient field if the user hasn't already changed it.
  useEffect(() => {
    if (userEmail && !testTo) setTestTo(userEmail);
  }, [userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lightweight client list for the preview-customer picker. Pulled
  // once when the user is on the Email settings page; small enough
  // (just id + name + email + counts) to drop into a <select>.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api/clients", { credentials: "same-origin" });
        if (!r.ok) return;
        const d = await r.json();
        const list = (d.clients || [])
          .map(c => ({
            id: c.id,
            name: c.name || c.company || c.email || "(unnamed)",
            email: c.email || "",
            totalDue: Number(c.totalDue) || 0,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setClientsList(list);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api/workspace/email", { credentials: "same-origin" });
        if (!r.ok) throw new Error("load failed");
        const d = await r.json();
        setServer(d);
        setForm({
          localPart: d.localPart || "",
          displayName: d.displayName || d.teamName || "",
          replyTo: d.replyTo || "",
        });
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  // Live uniqueness check, debounced.
  useEffect(() => {
    const lp = (form.localPart || "").trim().toLowerCase();
    if (!lp || lp === server?.localPart) { setAvailability(null); return; }
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`/api/workspace/email/check?localPart=${encodeURIComponent(lp)}`, { credentials: "same-origin" });
        const d = await r.json();
        setAvailability(d);
      } catch { setAvailability(null); }
      finally { setChecking(false); }
    }, 350);
    return () => { clearTimeout(t); setChecking(false); };
  }, [form.localPart, server?.localPart]);

  const domain = server?.domain || "finance.elionlabs.org";
  const previewLocal = (form.localPart || "").trim().toLowerCase() || "your-handle";
  const previewName = (form.displayName || "").trim() || "Your Workspace";
  const previewFrom = `${previewName} <${previewLocal}@${domain}>`;

  const dirty = !!server && (
    form.localPart !== (server.localPart || "") ||
    form.displayName !== (server.displayName || "") ||
    form.replyTo !== (server.replyTo || "")
  );

  const submit = async () => {
    setErrors({});
    setTestResult(null);
    setSaving(true);
    try {
      const r = await fetch("/api/workspace/email", {
        method: "PUT", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          localPart: form.localPart.trim().toLowerCase(),
          displayName: form.displayName.trim(),
          replyTo: form.replyTo.trim(),
        }),
      });
      const d = await r.json();
      if (!r.ok) {
        setErrors(d.field ? { [d.field]: d.error } : { _: d.error || "Save failed." });
        return;
      }
      setServer(prev => ({ ...(prev || {}), ...d }));
      setForm({ localPart: d.localPart, displayName: d.displayName, replyTo: d.replyTo });
      setSavedAt(Date.now());
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      // If the user picked a real customer, fetch their unpaid orders
      // and ride along on the test request — so the preview renders
      // with that customer's actual data rather than the sample set.
      const body = { to: testTo.trim() || undefined };
      if (previewClientId) {
        setPreviewLoading(true);
        try {
          const r = await fetch(`/api/clients/${encodeURIComponent(previewClientId)}/orders`, { credentials: "same-origin" });
          if (r.ok) {
            const d = await r.json();
            const unpaid = (d.orders || []).filter(o => o.unpaid).map(o => ({
              number: o.number, date: o.date, total: o.total, outstanding: o.outstanding,
              orderStatusUrl: o.orderStatusUrl,
              fulfillmentStatus: o.fulfillmentStatus, fulfilledAt: o.fulfilledAt,
              rawId: o.rawId, id: o.id, overdue: !!o.overdue,
              lineItems: Array.isArray(o.lineItems) ? o.lineItems : [],
            }));
            if (unpaid.length === 0) {
              setTestResult({ ok: false, error: "That customer has no outstanding orders. Pick one with unpaid invoices, or leave on Sample data." });
              setTesting(false); setPreviewLoading(false);
              return;
            }
            body.client = { id: d.client?.id, name: d.client?.name, email: d.client?.email, company: d.client?.company };
            body.orders = unpaid;
            body.totals = d.totals || null;
          }
        } finally { setPreviewLoading(false); }
      }
      const r = await fetch("/api/workspace/email/test", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) setTestResult({ ok: false, error: d.error || "Send failed." });
      else if (d.devMode) setTestResult({ ok: true, devMode: true, to: d.to });
      else if (!d.delivered) setTestResult({ ok: false, error: d.error || "SMTP returned no delivery." });
      else setTestResult({ ok: true, to: d.to });
    } catch (err) {
      setTestResult({ ok: false, error: err.message || "Send failed." });
    } finally { setTesting(false); }
  };

  if (loading) {
    return <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 24, marginBottom: 16, fontSize: 14, color: "#6B7280" }}>Loading…</div>;
  }

  const labelStyle = { display: "block", fontSize: 12, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: "#6B7280", marginBottom: 6 };
  const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", color: "#000", outline: "none", boxSizing: "border-box" };
  const errStyle = { marginTop: 6, fontSize: 12, color: "#991B1B" };

  // Local-part availability hint
  let lpHint = null;
  const lpTrim = (form.localPart || "").trim().toLowerCase();
  if (errors.localPart) lpHint = <div style={errStyle}>{errors.localPart}</div>;
  else if (lpTrim && lpTrim === server?.localPart) lpHint = <div style={{ marginTop: 6, fontSize: 12, color: "#166534" }}>Saved.</div>;
  else if (checking) lpHint = <div style={{ marginTop: 6, fontSize: 12, color: "#6B7280" }}>Checking…</div>;
  else if (availability?.available === true) lpHint = <div style={{ marginTop: 6, fontSize: 12, color: "#166534" }}>Available.</div>;
  else if (availability && availability.available === false) lpHint = <div style={errStyle}>{availability.reason}</div>;

  return (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 24, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Mail size={20} color="#6B7280" strokeWidth={1.6} />
        </div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 600, color: "#000" }}>Sending email</div>
          <div style={{ fontSize: 14, color: "#6B7280", marginTop: 1 }}>How customers see your name when you email them statements, invoices, or receipts.</div>
        </div>
      </div>

      {/* Live preview */}
      <div style={{ marginBottom: 20, padding: "14px 16px", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6B7280", marginBottom: 6 }}>From header preview</div>
        <div style={{ fontSize: 14, color: "#000", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", wordBreak: "break-all" }}>{previewFrom}</div>
      </div>

      {/* Handle */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Email handle</label>
        <div style={{ display: "flex", alignItems: "stretch", border: `1px solid ${errors.localPart || availability?.available === false ? "#FCA5A5" : "#E5E7EB"}`, borderRadius: 8, overflow: "hidden", background: "#fff" }}>
          <input
            type="text"
            value={form.localPart}
            onChange={e => setForm(f => ({ ...f, localPart: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))}
            placeholder="progeny"
            autoCapitalize="off" autoCorrect="off" spellCheck={false}
            style={{ ...inputStyle, border: "none", borderRadius: 0, flex: 1, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
          />
          <div style={{ display: "flex", alignItems: "center", padding: "0 12px", background: "#F4F4F5", color: "#6B7280", fontSize: 13, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", borderLeft: "1px solid #E5E7EB", whiteSpace: "nowrap" }}>
            @{domain}
          </div>
        </div>
        {lpHint || <div style={{ marginTop: 6, fontSize: 12, color: "#9CA3AF" }}>Lowercase letters, numbers, and dashes. 3–32 characters.</div>}
      </div>

      {/* Display name */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Display name</label>
        <input
          type="text"
          value={form.displayName}
          onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
          placeholder="Progeny Coffee"
          style={{ ...inputStyle, borderColor: errors.displayName ? "#FCA5A5" : "#E5E7EB" }}
        />
        {errors.displayName ? <div style={errStyle}>{errors.displayName}</div> : <div style={{ marginTop: 6, fontSize: 12, color: "#9CA3AF" }}>Shown to customers in their inbox. Defaults to your workspace name.</div>}
      </div>

      {/* Reply-to */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Public contact email</label>
        <input
          type="email"
          value={form.replyTo}
          onChange={e => setForm(f => ({ ...f, replyTo: e.target.value }))}
          placeholder="hello@progenycoffee.com"
          autoCapitalize="off" autoCorrect="off" spellCheck={false}
          style={{ ...inputStyle, borderColor: errors.replyTo ? "#FCA5A5" : "#E5E7EB" }}
        />
        {errors.replyTo ? <div style={errStyle}>{errors.replyTo}</div> : <div style={{ marginTop: 6, fontSize: 12, color: "#9CA3AF" }}>The address customers see and reply to. Use anything you'd publicly publish — it doesn't have to match your login. Defaults to your account email.</div>}
      </div>

      {errors._ && <div style={{ marginBottom: 16, padding: "10px 12px", borderRadius: 8, background: "#FEE2E2", color: "#991B1B", fontSize: 13 }}>{errors._}</div>}

      {/* Save row */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button
          onClick={submit}
          disabled={saving || !dirty || (availability && availability.available === false)}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 16px", borderRadius: 8, border: "1px solid #000", background: dirty ? "#000" : "#F4F4F5", color: dirty ? "#fff" : "#9CA3AF", fontSize: 14, fontWeight: 600, cursor: saving || !dirty ? "default" : "pointer", fontFamily: "Inter, sans-serif", opacity: saving ? 0.7 : 1 }}
        >
          {saving ? "Saving…" : (server?.configured && !dirty ? "Saved" : "Save")}
        </button>
        {savedAt && !dirty && (
          <span style={{ fontSize: 12, color: "#6B7280" }}>Saved {new Date(savedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
        )}
      </div>

      {/* Inbound — customers can email <handle>@<domain> to request
          their statement and the system auto-replies. The address is
          the same as the outbound identity, so there's nothing extra
          to configure here once the workspace handle is set. */}
      {server?.configured && !dirty && (
        <div style={{ marginTop: 18, padding: "16px 18px", borderTop: "1px solid #F3F4F6", borderRadius: 0, background: "#F9FAFB", marginLeft: -24, marginRight: -24, paddingLeft: 24, paddingRight: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#000", marginBottom: 4 }}>Customers can request their own statement</div>
          <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.5, marginBottom: 10 }}>
            When a customer emails the address below, our system looks them up by sender email and auto-replies with their current statement (or a friendly note if their account is current). Nothing to configure on your end — just publish the address.
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 13, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: "#000" }}>
            {server.localPart}@{server.domain}
            <button
              onClick={() => navigator.clipboard?.writeText(`${server.localPart}@${server.domain}`)}
              title="Copy"
              style={{ background: "none", border: "none", padding: 2, cursor: "pointer", color: "#6B7280", display: "flex", alignItems: "center" }}
            >
              <Copy size={13} strokeWidth={2} />
            </button>
          </div>
        </div>
      )}

      {/* Send-sample row — appears once email is configured.
         Lets the user preview the exact statement layout customers
         see (logo, signed PDF links, Pay Now button, xlsx attachment)
         by sending it to themselves or any address they type. */}
      {server?.configured && !dirty && (
        <div style={{ marginTop: 18, padding: "16px 18px", borderTop: "1px solid #F3F4F6" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#000", marginBottom: 4 }}>Preview the customer experience</div>
          <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 12, lineHeight: 1.5 }}>
            Sends the actual statement layout — with your logo, signed PDF links, the Pay Now button, and an Excel attachment — to the address below. A "Sample preview" banner makes it obvious it isn't a real statement, so it's safe to send to anyone. Optionally borrow a real customer's outstanding orders to see how their statement will look.
          </div>

          {/* Customer picker — defaults to "Sample data" for an
              account-agnostic walkthrough. When a real customer is
              chosen, the email is rendered from THEIR data (orders,
              name, totals) but still goes to the address below — so
              the customer never receives anything during testing. */}
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: "#6B7280", marginBottom: 4 }}>Use whose data</label>
            <select
              value={previewClientId}
              onChange={e => setPreviewClientId(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", color: "#000", outline: "none", background: "#fff", boxSizing: "border-box", cursor: "pointer" }}
            >
              <option value="">Sample data (3 fake orders)</option>
              {clientsList.length > 0 && <option disabled>──────────</option>}
              {clientsList
                .filter(c => c.totalDue > 0)
                .map(c => (
                  <option key={c.id} value={c.id}>{c.name} — ${c.totalDue.toFixed(2)} outstanding</option>
                ))}
              {clientsList.filter(c => c.totalDue > 0).length === 0 && clientsList.length > 0 && (
                <option disabled>No customers with outstanding balances</option>
              )}
            </select>
          </div>

          <div style={{ marginBottom: 4, fontSize: 11, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: "#6B7280" }}>Send to</div>
          <div style={{ display: "flex", gap: 8, alignItems: "stretch", flexWrap: "wrap" }}>
            <input
              type="email"
              value={testTo}
              onChange={e => setTestTo(e.target.value)}
              placeholder="recipient@example.com"
              autoCapitalize="off" autoCorrect="off" spellCheck={false}
              style={{ flex: "1 1 240px", minWidth: 200, padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 14, fontFamily: "Inter, sans-serif", color: "#000", outline: "none", boxSizing: "border-box" }}
            />
            <button
              onClick={sendTest}
              disabled={testing || !testTo.trim()}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 16px", borderRadius: 8, border: "1px solid #000", background: testing || !testTo.trim() ? "#F4F4F5" : "#000", color: testing || !testTo.trim() ? "#9CA3AF" : "#fff", fontSize: 14, fontWeight: 600, cursor: testing || !testTo.trim() ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}
            >
              <Send size={14} strokeWidth={2} /> {testing ? "Sending…" : "Send sample"}
            </button>
          </div>

          {testResult && (
            <div style={{ marginTop: 12, padding: "12px 14px", borderRadius: 8, background: testResult.ok ? "#DCFCE7" : "#FEE2E2", color: testResult.ok ? "#166534" : "#991B1B", fontSize: 13, lineHeight: 1.5 }}>
              {testResult.ok && testResult.devMode && (
                <>SMTP isn't configured on this environment, so the sample was logged to the server console instead of being delivered to {testResult.to}.</>
              )}
              {testResult.ok && !testResult.devMode && (() => {
                const chosen = clientsList.find(c => c.id === previewClientId);
                return (
                  <>Sample sent to <strong>{testResult.to}</strong>{chosen ? <> using <strong>{chosen.name}</strong>'s data</> : null}. Should arrive in a few seconds.</>
                );
              })()}
              {!testResult.ok && (
                <>Couldn't send: {testResult.error}</>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BillingSettingsView({ settings, onSaved, effectiveDemoMode }) {
  const [form, setForm] = useState({
    companyName: settings.companyName || "",
    address: settings.address || "",
    email: settings.email || "",
    phone: settings.phone || "",
    website: settings.website || "",
    paymentTermsDays: settings.paymentTermsDays ?? 30,
    paymentInstructions: settings.paymentInstructions || "",
    scheduleEnabled: settings.scheduleEnabled || false,
    scheduleFrequency: settings.scheduleFrequency || "weekly",
    scheduleDay: settings.scheduleDay || "monday",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try {
      if (!effectiveDemoMode) {
        await fetch("/api/billing/settings", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      }
      onSaved({ ...settings, ...form });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally { setSaving(false); }
  };

  const field = (label, key, type = "text", placeholder = "") => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>{label}</label>
      <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7, border: "1px solid #D1D5DB", fontSize: 14 }} />
    </div>
  );

  return (
    <div style={{ background: "#fff", border: "1px solid #F3F4F6", borderRadius: 12, padding: "24px 28px", maxWidth: 560 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 20 }}>Business info</div>
      {field("Company name", "companyName", "text", "Elion Roaster")}
      {field("Address", "address", "text", "123 Roaster Lane, City, ST 00000")}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>{field("Billing email", "email", "email", "billing@yourcompany.com")}</div>
        <div>{field("Phone", "phone", "tel", "(555) 000-0000")}</div>
      </div>
      {field("Website", "website", "text", "yourcompany.com")}
      {field("Default payment terms (days)", "paymentTermsDays", "number", "30")}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Payment instructions</label>
        <textarea value={form.paymentInstructions} onChange={e => setForm(f => ({ ...f, paymentInstructions: e.target.value }))}
          rows={3} placeholder="ACH preferred. Routing … · Account …"
          style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7, border: "1px solid #D1D5DB", fontSize: 13, resize: "vertical", fontFamily: "Inter, sans-serif" }} />
      </div>

      <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 18, marginTop: 4, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 12 }}>Automated overdue reminders</div>
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 10 }}>
          <input type="checkbox" checked={form.scheduleEnabled} onChange={e => setForm(f => ({ ...f, scheduleEnabled: e.target.checked }))} style={{ width: 15, height: 15 }} />
          <span style={{ fontSize: 14, color: "#374151" }}>Send overdue invoice reminders automatically</span>
        </label>
        {form.scheduleEnabled && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: 25 }}>
            <span style={{ fontSize: 13, color: "#6B7280" }}>Frequency:</span>
            <select value={form.scheduleFrequency} onChange={e => setForm(f => ({ ...f, scheduleFrequency: e.target.value }))}
              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #D1D5DB", fontSize: 13 }}>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
            </select>
            <span style={{ fontSize: 13, color: "#6B7280" }}>on</span>
            <select value={form.scheduleDay} onChange={e => setForm(f => ({ ...f, scheduleDay: e.target.value }))}
              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #D1D5DB", fontSize: 13 }}>
              {["monday","tuesday","wednesday","thursday","friday"].map(d => (
                <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
              ))}
            </select>
          </div>
        )}
        {settings.scheduleLastRun && (
          <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 8, marginLeft: 25 }}>
            Last run: {new Date(settings.scheduleLastRun).toLocaleString()}
          </div>
        )}
      </div>

      <button onClick={handleSave} disabled={saving} style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: "#111827", color: "#fff", fontSize: 14, fontWeight: 600, cursor: saving ? "wait" : "pointer" }}>
        {saving ? "Saving…" : saved ? "✓ Saved" : "Save settings"}
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   WHOLESALE — orders management
   Lists every order placed via the public shop. Supports
   per-row status transitions: approve, mark delivered,
   mark paid, cancel.
   ═══════════════════════════════════════════════════════ */
function WholesaleOrdersView({ orders, onOrderUpdated }) {
  const [filter, setFilter] = useState("active");  // active | delivered | paid | rejected | all
  const [busy, setBusy] = useState(null);

  const patch = async (id, status) => {
    setBusy(id);
    try {
      const r = await fetch(`/api/wholesale/orders/${encodeURIComponent(id)}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (r.ok) {
        const d = await r.json();
        onOrderUpdated && onOrderUpdated(d.order);
      }
    } catch { /* ignore */ }
    finally { setBusy(null); }
  };

  const STATUS_TINT = {
    pending:   { bg: "#FEF3C7", fg: "#92400E", border: "#FDE68A", label: "Pending" },
    approved:  { bg: "#DBEAFE", fg: "#1E40AF", border: "#BFDBFE", label: "Approved" },
    delivered: { bg: "#D1FAE5", fg: "#065F46", border: "#A7F3D0", label: "Delivered" },
    paid:      { bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0", label: "Paid" },
    rejected:  { bg: "#FEE2E2", fg: "#991B1B", border: "#FECACA", label: "Cancelled" },
  };

  const filtered = (orders || []).filter(o => {
    if (filter === "all") return true;
    if (filter === "active") return !["paid", "rejected"].includes(o.status);
    return o.status === filter;
  }).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  const counts = {
    active:    (orders || []).filter(o => !["paid", "rejected"].includes(o.status)).length,
    delivered: (orders || []).filter(o => o.status === "delivered").length,
    paid:      (orders || []).filter(o => o.status === "paid").length,
    rejected:  (orders || []).filter(o => o.status === "rejected").length,
    all:       (orders || []).length,
  };

  const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
  const fmtDate = (ms) => ms
    ? new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";

  return (
    <div>
      {/* Segment filter */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          { v: "active",    label: "Active" },
          { v: "delivered", label: "Delivered" },
          { v: "paid",      label: "Paid" },
          { v: "rejected",  label: "Cancelled" },
          { v: "all",       label: "All" },
        ].map(opt => (
          <button
            key={opt.v}
            onClick={() => setFilter(opt.v)}
            style={{
              padding: "6px 14px", borderRadius: 99,
              border: `1px solid ${filter === opt.v ? "#000" : "#E5E7EB"}`,
              background: filter === opt.v ? "#000" : "#fff",
              color: filter === opt.v ? "#fff" : "#374151",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
              fontFamily: "Inter, sans-serif",
            }}
          >
            {opt.label} <span style={{ opacity: 0.6, marginLeft: 4 }}>{counts[opt.v]}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div style={{ background: "#FAFAFA", border: "1px dashed #E5E7EB", borderRadius: 12, padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>
          {filter === "active" ? "No active orders. New orders from the public shop appear here." : "No orders matching this filter."}
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 110px 110px 110px 200px", columnGap: 12, padding: "10px 16px", background: "#F9FAFB", borderBottom: "1px solid #E5E7EB", fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            <div>Order</div>
            <div>Customer</div>
            <div style={{ textAlign: "right" }}>Total</div>
            <div>Date</div>
            <div>Status</div>
            <div style={{ textAlign: "right" }}>Actions</div>
          </div>
          {filtered.map(o => {
            const tint = STATUS_TINT[o.status] || STATUS_TINT.pending;
            return (
              <div key={o.id} style={{ display: "grid", gridTemplateColumns: "120px 1fr 110px 110px 110px 200px", columnGap: 12, alignItems: "center", padding: "12px 16px", borderTop: "1px solid #F3F4F6", fontFamily: "Inter, sans-serif" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#000", fontVariantNumeric: "tabular-nums" }}>#{o.number}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#000", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {o.customerCompany || o.customerName || o.customerEmail}
                  </div>
                  <div style={{ fontSize: 11, color: "#9CA3AF", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {(o.lineItems || []).length} item{(o.lineItems || []).length === 1 ? "" : "s"} · {o.customerEmail}
                  </div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(o.total)}</div>
                <div style={{ fontSize: 12, color: "#6B7280", fontVariantNumeric: "tabular-nums" }}>{fmtDate(o.createdAt)}</div>
                <div>
                  <span style={{ display: "inline-block", padding: "3px 9px", borderRadius: 99, fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", background: tint.bg, color: tint.fg, border: `1px solid ${tint.border}`, whiteSpace: "nowrap" }}>
                    {tint.label}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                  {o.status === "pending" && (
                    <button disabled={busy === o.id} onClick={() => patch(o.id, "approved")} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 12, fontWeight: 600, cursor: busy === o.id ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>Approve</button>
                  )}
                  {(o.status === "approved" || o.status === "pending") && (
                    <button disabled={busy === o.id} onClick={() => patch(o.id, "delivered")} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 12, fontWeight: 500, cursor: busy === o.id ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>Mark delivered</button>
                  )}
                  {o.status === "delivered" && (
                    <button disabled={busy === o.id} onClick={() => patch(o.id, "paid")} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 12, fontWeight: 600, cursor: busy === o.id ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>Mark paid</button>
                  )}
                  {o.status !== "rejected" && o.status !== "paid" && (
                    <button disabled={busy === o.id} onClick={() => { if (window.confirm(`Cancel order #${o.number}?`)) patch(o.id, "rejected"); }} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #FECACA", background: "#fff", color: "#B91C1C", fontSize: 12, fontWeight: 600, cursor: busy === o.id ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   WHOLESALE — main view (store settings + products grid)
   ═══════════════════════════════════════════════════════ */
function WholesaleView({ tab, setTab, config, products, customers, orders = [], onOrderUpdated, loading, productModal, setProductModal, header, onConfigSaved, onProductCreated, onProductUpdated, onProductDeleted, onCustomerCreated, onCustomerUpdated, onCustomerDeleted, shopConnected, connectedShop }) {
  const blankForm = { name: "", tagline: "", about: "", accentColor: "#000000", slug: "", customDomain: "", published: false };
  const [selectedProduct, setSelectedProduct] = useState(null); // product detail page
  const [storeForm, setStoreForm] = useState(blankForm);
  const [logoPreview, setLogoPreview] = useState(null); // data URL for new logo
  const [logoData, setLogoData] = useState(null);
  const [storeSaving, setStoreSaving] = useState(false);
  const [storeSaved, setStoreSaved] = useState(false);
  const [storeError, setStoreError] = useState("");
  const [shopLinkCopied, setShopLinkCopied] = useState(false);

  // Shopify import state
  const [shopifyProducts, setShopifyProducts] = useState([]);
  const [shopifyLoading, setShopifyLoading] = useState(false);
  const [shopifyError, setShopifyError] = useState("");
  const [importingIds, setImportingIds] = useState(new Set());
  const [importedIds, setImportedIds] = useState(new Set());

  // Build set of already-imported Shopify product IDs
  const alreadyImported = new Set(products.filter(p => p.shopifyProductId).map(p => String(p.shopifyProductId)));

  useEffect(() => {
    if (tab !== "import" || !shopConnected) return;
    setShopifyLoading(true); setShopifyError("");
    fetch(`/api/shopify/products?shop=${encodeURIComponent(connectedShop)}&limit=100`)
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.error || "Failed")))
      .then(d => setShopifyProducts(d.products || []))
      .catch(e => setShopifyError(typeof e === "string" ? e : "Could not load Shopify products"))
      .finally(() => setShopifyLoading(false));
  }, [tab, shopConnected, connectedShop]);

  const stripHtml = (html) => {
    try { return new DOMParser().parseFromString(html || "", "text/html").body.textContent?.trim() || ""; }
    catch { return (html || "").replace(/<[^>]+>/g, "").trim(); }
  };

  const urlToDataUrl = async (url) => {
    const r = await fetch(url);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const blob = await r.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Bulk import — runs importProduct sequentially over every Shopify
  // product not already in the store. Sequential (not parallel) so
  // we don't slam Shopify's per-app rate limit (40 req/s burst, 2 req/s
  // sustained on classic plans). Live progress drives the button label.
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  // Skipped Shopify product ids — operator hid them from the main
  // import list (e.g. discontinued SKUs, samples, retail-only items).
  // Persists per-browser; not shared across workspaces, but cheap.
  const [skippedShopifyIds, setSkippedShopifyIds] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:wholesale:skippedShopifyIds");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:wholesale:skippedShopifyIds", JSON.stringify([...skippedShopifyIds])); }
    catch { /* ignore */ }
  }, [skippedShopifyIds]);
  const skipShopifyProduct   = (sid) => setSkippedShopifyIds(prev => new Set([...prev, String(sid)]));
  const unskipShopifyProduct = (sid) => setSkippedShopifyIds(prev => { const n = new Set(prev); n.delete(String(sid)); return n; });
  // Sub-tab on the import page: "available" (default) | "skipped".
  const [importSubTab, setImportSubTab] = useState("available");
  const importAll = async () => {
    const queue = shopifyProducts.filter(sp => {
      const sid = String(sp.id);
      return !alreadyImported.has(sid) && !importedIds.has(sid) && !skippedShopifyIds.has(sid);
    });
    if (queue.length === 0) return;
    setBulkImporting(true);
    setBulkProgress({ done: 0, total: queue.length });
    for (let i = 0; i < queue.length; i++) {
      try { await importProduct(queue[i]); }
      catch (e) { console.warn("Bulk import row failed:", e.message); }
      setBulkProgress({ done: i + 1, total: queue.length });
    }
    setBulkImporting(false);
  };

  const importProduct = async (sp) => {
    const sid = String(sp.id);
    setImportingIds(s => new Set([...s, sid]));
    try {
      // 1. Create base product
      const firstVar = sp.variants?.[0];
      const weightOz = firstVar?.weight
        ? (firstVar.weight_unit === "lb" ? firstVar.weight * 16 : firstVar.weight_unit === "g" ? firstVar.weight / 28.35 : firstVar.weight)
        : null;
      const r = await fetch("/api/wholesale/products", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: sp.title,
          category: sp.product_type || "",
          description: stripHtml(sp.body_html),
          price: firstVar?.price ? parseFloat(firstVar.price) : null,
          weightOz: weightOz ? Math.round(weightOz * 10) / 10 : null,
          shopifyProductId: sp.id,
        }),
      });
      const { product } = await r.json();
      if (!r.ok || !product) throw new Error("Create failed");

      // 2. Import variants (if more than one, or if options are meaningful)
      const meaningfulVariants = sp.variants?.length > 1 || (sp.variants?.[0]?.title !== "Default Title");
      if (meaningfulVariants) {
        for (const v of (sp.variants || [])) {
          const vWeightOz = v.weight
            ? (v.weight_unit === "lb" ? v.weight * 16 : v.weight_unit === "g" ? v.weight / 28.35 : v.weight)
            : null;
          await fetch(`/api/wholesale/products/${product.id}/variants`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: v.title,
              sku: v.sku || "",
              price: v.price ? parseFloat(v.price) : null,
              weightOz: vWeightOz ? Math.round(vWeightOz * 10) / 10 : null,
            }),
          });
        }
      }

      // 3. Import first image
      const imgSrc = sp.images?.[0]?.src;
      if (imgSrc) {
        try {
          const dataUrl = await urlToDataUrl(imgSrc);
          await fetch(`/api/wholesale/products/${product.id}/photos`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ photoData: dataUrl }),
          });
        } catch (e) { console.warn("Image import failed:", e.message); }
      }

      // 4. Refresh product and update state
      const refreshed = await fetch("/api/wholesale/products").then(r2 => r2.ok ? r2.json() : null);
      const full = (refreshed?.products || []).find(x => x.id === product.id);
      if (full) onProductCreated(full);
      setImportedIds(s => new Set([...s, sid]));
    } catch (e) {
      console.warn("Import failed:", e);
    } finally {
      setImportingIds(s => { const n = new Set(s); n.delete(sid); return n; });
    }
  };
  const [copied, setCopied] = useState(false);

  // Sync form from config
  useEffect(() => {
    if (!config) return;
    setStoreForm({
      name: config.name || "",
      tagline: config.tagline || "",
      about: config.about || "",
      accentColor: config.accentColor || "#000000",
      slug: config.slug || "",
      customDomain: config.customDomain || "",
      published: !!config.published,
    });
    if (config.logoFilename) setLogoPreview(`/api/wholesale/photos/${encodeURIComponent(config.logoFilename)}`);
  }, [config]);

  const sfm = (k) => (e) => setStoreForm(f => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const saveStore = async () => {
    setStoreSaving(true); setStoreError(""); setStoreSaved(false);
    try {
      const body = { ...storeForm };
      if (logoData) body.logoData = logoData;
      const r = await fetch("/api/wholesale/config", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Save failed");
      onConfigSaved(d.config);
      setStoreSaved(true);
      setTimeout(() => setStoreSaved(false), 2500);
    } catch (e) { setStoreError(e.message); }
    finally { setStoreSaving(false); }
  };

  const readLogo = (file) => {
    const rd = new FileReader();
    rd.onload = (e) => { setLogoPreview(e.target.result); setLogoData(e.target.result); };
    rd.readAsDataURL(file);
  };

  const storefrontUrl = config?.slug ? `${window.location.origin}/shop/${config.slug}` : null;

  const fld = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontFamily: "Inter, sans-serif", fontSize: 14, color: "#111827", boxSizing: "border-box" };
  const lbl = { display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 };

  const priceRange = (p) => {
    const prices = [p.price, ...(p.variants || []).map(v => v.price)].filter(x => x != null);
    if (prices.length === 0) return null;
    const mn = Math.min(...prices), mx = Math.max(...prices);
    return mn === mx ? `$${mn.toFixed(2)}` : `$${mn.toFixed(2)} – $${mx.toFixed(2)}`;
  };

  return (
    <div>
      {header}
      {loading && <div style={{ textAlign: "center", padding: "40px 0", color: "#9CA3AF" }}>Loading…</div>}
      {!loading && (
        <>
          {/* ── Store settings tab ── */}
          {tab === "store" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 600 }}>
              {storeError && <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C", fontSize: 13 }}>{storeError}</div>}

              {/* Storefront URL */}
              {storefrontUrl && (
                <div style={{ background: "#F9FAFB", borderRadius: 10, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                  <Globe size={15} color="#6B7280" strokeWidth={1.6} />
                  <span style={{ fontSize: 13, color: "#6B7280", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{storefrontUrl}</span>
                  <button onClick={() => { navigator.clipboard.writeText(storefrontUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }} style={{ fontSize: 12, fontWeight: 500, padding: "4px 10px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", cursor: "pointer", fontFamily: "Inter, sans-serif", flexShrink: 0 }}>{copied ? "Copied!" : "Copy"}</button>
                  <a href={storefrontUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#6B7280", display: "flex", alignItems: "center" }}><ExternalLink size={13} /></a>
                </div>
              )}

              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
                <div><label style={lbl}>Store name</label><input value={storeForm.name} onChange={sfm("name")} placeholder="My Roastery" style={fld} /></div>
                <div>
                  <label style={lbl}>Slug (URL)</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <span style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid #F3F4F6", background: "#F9FAFB", fontSize: 14, color: "#9CA3AF", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>{window.location.host}/shop/</span>
                    <input value={storeForm.slug} onChange={sfm("slug")} placeholder="my-roastery" style={{ ...fld, flex: 1 }} />
                    {storeForm.slug && (
                      <>
                        <button
                          type="button"
                          onClick={async () => {
                            const url = `${window.location.origin}/shop/${storeForm.slug}`;
                            try {
                              await navigator.clipboard.writeText(url);
                              setShopLinkCopied(true);
                              setTimeout(() => setShopLinkCopied(false), 1500);
                            } catch { /* ignore */ }
                          }}
                          title={`Copy ${window.location.origin}/shop/${storeForm.slug}`}
                          style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: shopLinkCopied ? "#ECFDF5" : "#fff", color: shopLinkCopied ? "#047857" : "#000", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
                        >
                          {shopLinkCopied ? (<><Check size={13} strokeWidth={2.4} /> Copied</>) : (<><Copy size={13} strokeWidth={2} /> Copy link</>)}
                        </button>
                        <a
                          href={`${window.location.origin}/shop/${storeForm.slug}`}
                          target="_blank"
                          rel="noreferrer noopener"
                          title="Open store in new tab"
                          style={{ padding: "9px 11px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 13, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                        >↗</a>
                      </>
                    )}
                  </div>
                </div>
                <div><label style={lbl}>Tagline</label><input value={storeForm.tagline} onChange={sfm("tagline")} placeholder="Specialty coffee, direct trade" style={fld} /></div>
                <div><label style={lbl}>About</label><textarea value={storeForm.about} onChange={sfm("about")} placeholder="A few sentences about your roastery…" rows={3} style={{ ...fld, resize: "vertical" }} /></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <label style={lbl}>Accent color</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <input type="color" value={storeForm.accentColor} onChange={sfm("accentColor")} style={{ width: 40, height: 36, borderRadius: 8, border: "1px solid #E5E7EB", cursor: "pointer", padding: 2, background: "#fff" }} />
                      <input value={storeForm.accentColor} onChange={sfm("accentColor")} placeholder="#000000" style={{ ...fld, flex: 1 }} />
                    </div>
                  </div>
                  <div>
                    <label style={lbl}>Logo</label>
                    <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff" }}>
                      {logoPreview ? <img src={logoPreview} alt="Logo" style={{ height: 28, maxWidth: 100, objectFit: "contain" }} /> : <span style={{ fontSize: 14, color: "#9CA3AF" }}>Upload logo</span>}
                      <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) readLogo(f); e.target.value = ""; }} />
                    </label>
                  </div>
                </div>
                {/* Custom domain — operator can point shop.theircompany.com
                    at the wholesale store. CNAME instructions inline so
                    they don't have to dig for the target hostname. */}
                <div style={{ paddingTop: 4, borderTop: "1px solid #F3F4F6" }}>
                  <label style={lbl}>Custom domain <span style={{ color: "#9CA3AF", fontWeight: 400 }}>(optional)</span></label>
                  <input
                    value={storeForm.customDomain || ""}
                    onChange={sfm("customDomain")}
                    placeholder="shop.yourdomain.com"
                    style={fld}
                  />
                  {storeForm.customDomain && (
                    <div style={{ marginTop: 8, padding: "10px 12px", borderRadius: 8, background: "#F9FAFB", border: "1px solid #E5E7EB", fontSize: 12, color: "#3F3F46", lineHeight: 1.55 }}>
                      <div style={{ fontWeight: 600, color: "#000", marginBottom: 4 }}>To activate:</div>
                      <div>1. In your DNS provider, create a <strong>CNAME</strong> record:</div>
                      <div style={{ marginLeft: 14, marginTop: 4, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 11 }}>
                        <strong>{storeForm.customDomain}</strong> → <strong>roaster.elionlabs.org</strong>
                      </div>
                      <div style={{ marginTop: 6 }}>2. Email <a href="mailto:hello@elionlabs.org" style={{ color: "#000" }}>hello@elionlabs.org</a> so we can issue an SSL certificate (one-time, ~10 min).</div>
                      <div style={{ marginTop: 6, color: "#9CA3AF" }}>Once active, customers can visit <strong>https://{storeForm.customDomain}</strong> directly — no /shop/{storeForm.slug || "your-slug"} in the URL.</div>
                    </div>
                  )}
                </div>
                {/* Published toggle */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 4, borderTop: "1px solid #F3F4F6" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>Published</div>
                    <div style={{ fontSize: 13, color: "#9CA3AF" }}>Make your storefront publicly visible</div>
                  </div>
                  <button onClick={() => setStoreForm(f => ({ ...f, published: !f.published }))} style={{ width: 44, height: 24, borderRadius: 12, background: storeForm.published ? "#000" : "#E5E7EB", border: "none", cursor: "pointer", position: "relative", flexShrink: 0, transition: "background 0.15s" }}>
                    <span style={{ position: "absolute", top: 3, left: storeForm.published ? 23 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.15s", boxShadow: "0 1px 3px rgba(0,0,0,0.15)" }} />
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button onClick={saveStore} disabled={storeSaving} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: storeSaving ? "#D1D5DB" : "#000", color: "#fff", fontSize: 14, fontWeight: 600, cursor: storeSaving ? "default" : "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 8 }}>
                  {storeSaving ? "Saving…" : storeSaved ? <><Check size={14} /> Saved</> : "Save settings"}
                </button>
              </div>
            </div>
          )}

          {/* ── Products tab ── */}
          {tab === "products" && (
            selectedProduct ? (
              <WholesaleProductDetail
                product={selectedProduct}
                onBack={() => setSelectedProduct(null)}
                onUpdated={(p) => { setSelectedProduct(p); onProductUpdated(p); }}
                onDeleted={(id) => { setSelectedProduct(null); onProductDeleted(id); }}
              />
            ) : (
            <div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
                <button onClick={() => setProductModal("new")} style={{ padding: "9px 16px", borderRadius: 8, border: "none", background: "#000", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Plus size={14} /> Add product
                </button>
              </div>

              {products.length === 0 ? (
                <div style={{ textAlign: "center", padding: "80px 0" }}>
                  <ShoppingBag size={32} color="#D1D5DB" strokeWidth={1.2} style={{ marginBottom: 12 }} />
                  <div style={{ fontSize: 16, fontWeight: 500, color: "#374151", marginBottom: 6 }}>No products yet</div>
                  <div style={{ fontSize: 14, color: "#9CA3AF" }}>Add your first wholesale product to get started.</div>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
                  {products.map(p => {
                    const cover = p.photos?.[0];
                    const range = priceRange(p);
                    return (
                      <div
                        key={p.id}
                        onClick={() => setSelectedProduct(p)}
                        style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", overflow: "hidden", cursor: "pointer", transition: "box-shadow 0.12s, border-color 0.12s" }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.07)"; e.currentTarget.style.borderColor = "#E5E7EB"; }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "#F3F4F6"; }}
                      >
                        <div style={{ aspectRatio: "2/1", background: "#F9FAFB", overflow: "hidden" }}>
                          {cover
                            ? <img src={`/api/wholesale/photos/${encodeURIComponent(cover)}`} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#D1D5DB", fontSize: 28 }}>☕</div>
                          }
                        </div>
                        <div style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                            <div>
                              <div style={{ fontSize: 15, fontWeight: 600, color: "#111", lineHeight: 1.3 }}>{p.name}</div>
                              <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>{[p.category, (p.variants || []).length > 0 && `${p.variants.length} variant${p.variants.length !== 1 ? "s" : ""}`].filter(Boolean).join(" · ")}</div>
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 500, padding: "2px 8px", borderRadius: 99, background: p.active !== false ? "#F0FDF4" : "#F3F4F6", color: p.active !== false ? "#16A34A" : "#9CA3AF", flexShrink: 0 }}>
                              {p.active !== false ? "Active" : "Inactive"}
                            </span>
                          </div>
                          {range && <div style={{ fontSize: 15, fontWeight: 700, color: "#000", marginTop: 8 }}>{range}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            )
          )}
        </>
      )}

      {/* ── Customers tab ── */}
      {tab === "customers" && (
        <WholesaleCustomersView
          customers={customers || []}
          products={products || []}
          config={config}
          onCustomerCreated={onCustomerCreated}
          onCustomerUpdated={onCustomerUpdated}
          onCustomerDeleted={onCustomerDeleted}
        />
      )}

      {tab === "orders" && (
        <WholesaleOrdersView
          orders={orders}
          onOrderUpdated={onOrderUpdated}
        />
      )}

      {/* ── Shopify import tab ── */}
      {tab === "import" && (
        <div>
          <div style={{ marginBottom: 20, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#111", marginBottom: 4 }}>Import from Shopify</div>
              <div style={{ fontSize: 14, color: "#6B7280" }}>
                Pulls products with their variants, weight, SKU, and first photo. Add them one at a time — or use <strong style={{ color: "#000" }}>Import all</strong> to grab everything new in one click.
              </div>
            </div>
            {(() => {
              const remaining = shopifyProducts.filter(sp => {
                const sid = String(sp.id);
                return !alreadyImported.has(sid) && !importedIds.has(sid) && !skippedShopifyIds.has(sid);
              }).length;
              const disabled = bulkImporting || remaining === 0 || importSubTab !== "available";
              return (
                <button
                  onClick={importAll}
                  disabled={disabled}
                  style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #000", background: disabled ? "#F4F4F5" : "#000", color: disabled ? "#9CA3AF" : "#fff", fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}
                >
                  {bulkImporting
                    ? <><Loader size={12} style={{ animation: "spin 0.7s linear infinite" }} /> Importing {bulkProgress.done}/{bulkProgress.total}…</>
                    : remaining === 0
                      ? "All imported"
                      : <><Plus size={12} strokeWidth={2.4} /> Import all ({remaining})</>}
                </button>
              );
            })()}
          </div>

          {/* Sub-tabs: Available (default — non-skipped, non-imported)
              vs Skipped (the operator's parked items). Counts update
              live as products are skipped/unskipped. */}
          {!shopifyLoading && !shopifyError && shopifyProducts.length > 0 && (() => {
            const availCount = shopifyProducts.filter(sp => !skippedShopifyIds.has(String(sp.id))).length;
            const skipCount  = shopifyProducts.filter(sp =>  skippedShopifyIds.has(String(sp.id))).length;
            const tab = (key, label, count) => {
              const active = importSubTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setImportSubTab(key)}
                  style={{ padding: "6px 12px", borderRadius: 99, border: `1px solid ${active ? "#000" : "#E5E7EB"}`, background: active ? "#000" : "#fff", color: active ? "#fff" : "#3F3F46", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                >{label} <span style={{ opacity: 0.6, marginLeft: 4 }}>{count}</span></button>
              );
            };
            return (
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                {tab("available", "Available",  availCount)}
                {tab("skipped",   "Skipped",    skipCount)}
              </div>
            );
          })()}

          {shopifyLoading && (
            <div style={{ textAlign: "center", padding: "48px 0", color: "#9CA3AF" }}>Loading your Shopify products…</div>
          )}
          {shopifyError && (
            <div style={{ padding: "12px 16px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C", fontSize: 14, marginBottom: 16 }}>{shopifyError}</div>
          )}
          {!shopifyLoading && !shopifyError && shopifyProducts.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 0", color: "#9CA3AF", fontSize: 14 }}>No products found in your Shopify store.</div>
          )}
          {!shopifyLoading && shopifyProducts.length > 0 && (() => {
            const visible = shopifyProducts.filter(sp => {
              const sid = String(sp.id);
              const isSkipped = skippedShopifyIds.has(sid);
              return importSubTab === "skipped" ? isSkipped : !isSkipped;
            });
            if (visible.length === 0) {
              return (
                <div style={{ background: "#FAFAFA", borderRadius: 12, padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 13, border: "1px dashed #E5E7EB" }}>
                  {importSubTab === "skipped" ? "Nothing skipped." : "Nothing left to import."}
                </div>
              );
            }
            return (
            <div style={{ display: "flex", flexDirection: "column", gap: 1, background: "#F3F4F6", borderRadius: 12, overflow: "hidden" }}>
              {visible.map(sp => {
                const sid = String(sp.id);
                const isImported = alreadyImported.has(sid) || importedIds.has(sid);
                const isImporting = importingIds.has(sid);
                const isSkipped = skippedShopifyIds.has(sid);
                const cover = sp.images?.[0]?.src;
                const firstVar = sp.variants?.[0];
                const prices = (sp.variants || []).map(v => parseFloat(v.price)).filter(p => !isNaN(p));
                const minP = prices.length ? Math.min(...prices) : null;
                const maxP = prices.length ? Math.max(...prices) : null;
                const priceStr = minP == null ? null : minP === maxP ? `$${minP.toFixed(2)}` : `$${minP.toFixed(2)} – $${maxP.toFixed(2)}`;

                return (
                  <div key={sp.id} style={{ background: "#fff", padding: "14px 16px", display: "flex", alignItems: "center", gap: 14 }}>
                    {/* Thumbnail */}
                    <div style={{ width: 52, height: 52, borderRadius: 8, overflow: "hidden", background: "#F9FAFB", flexShrink: 0, border: "1px solid #F3F4F6" }}>
                      {cover
                        ? <img src={cover} alt={sp.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#D1D5DB", fontSize: 20 }}>☕</div>
                      }
                    </div>
                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#111", marginBottom: 2 }}>{sp.title}</div>
                      <div style={{ fontSize: 12, color: "#9CA3AF" }}>
                        {[sp.product_type, sp.variants?.length > 1 && `${sp.variants.length} variants`, priceStr].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    {/* Action */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                      {isImported ? (
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#16A34A", display: "flex", alignItems: "center", gap: 5 }}>
                          <Check size={13} strokeWidth={2.5} /> In store
                        </span>
                      ) : isSkipped ? (
                        <button
                          onClick={() => unskipShopifyProduct(sid)}
                          style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                        >
                          Unskip
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => importProduct(sp)}
                            disabled={isImporting}
                            style={{
                              padding: "7px 14px", borderRadius: 8,
                              border: "none",
                              background: isImporting ? "#D1D5DB" : "#000",
                              color: "#fff", fontSize: 13, fontWeight: 600,
                              cursor: isImporting ? "default" : "pointer",
                              fontFamily: "Inter, sans-serif",
                              display: "inline-flex", alignItems: "center", gap: 6,
                              transition: "background 0.1s",
                            }}
                          >
                            {isImporting ? (
                              <><Loader size={12} style={{ animation: "spin 0.7s linear infinite" }} /> Importing…</>
                            ) : (
                              <><Plus size={12} /> Add to store</>
                            )}
                          </button>
                          <button
                            onClick={() => skipShopifyProduct(sid)}
                            disabled={isImporting}
                            title="Skip — moves to the Skipped tab; bulk Import all won't include it"
                            style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#6B7280", fontSize: 13, fontWeight: 500, cursor: isImporting ? "default" : "pointer", fontFamily: "Inter, sans-serif" }}
                          >
                            Skip
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            );
          })()}
        </div>
      )}

      {/* New product modal (creation only) */}
      {productModal === "new" && (
        <WholesaleProductModal
          initial="new"
          onClose={() => setProductModal(null)}
          onSaved={(p) => {
            onProductCreated(p);
            setProductModal(null);
            // Navigate to detail page for the new product
            setSelectedProduct(p);
            setTab("products");
          }}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   PLAN GATE — wraps features that require an upgrade
   ═══════════════════════════════════════════════════════ */
function PlanGate({ feature, usage, children }) {
  const features = usage?.features || [];
  const hasIt = features.includes("*") || features.includes(feature);
  if (!usage || hasIt) return children;

  const planNeeded = feature === "quickbooks" || feature === "invoicing" || feature === "wholesale" || feature === "forecasting" ? "Growth" : "Roaster";

  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "64px 32px", textAlign: "center", gap: 16,
    }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Lock size={22} color="#6B7280" strokeWidth={1.6} />
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, color: "#111" }}>Available on {planNeeded}+</div>
      <div style={{ fontSize: 14, color: "#6B7280", maxWidth: 320, lineHeight: 1.55 }}>
        Upgrade your plan to unlock this feature.
      </div>
      <a
        href="/api/billing/checkout"
        onClick={async (e) => {
          e.preventDefault();
          const r = await fetch("/api/billing/checkout", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({ plan: planNeeded.toLowerCase(), interval: "monthly" }),
          });
          const data = await r.json();
          if (data.url) window.location.href = data.url;
          else if (!data.configured) alert("Billing not configured yet.");
        }}
        style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "10px 20px", borderRadius: 10, background: "#111", color: "#fff",
          fontSize: 14, fontWeight: 600, textDecoration: "none", cursor: "pointer",
          border: "none", fontFamily: "Inter, sans-serif",
        }}
      >
        Upgrade to {planNeeded}
      </a>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   PLAN & BILLING PAGE
   ═══════════════════════════════════════════════════════ */
function PlanBillingPage({ usage, billingLoading }) {
  const [interval, setInterval] = useState("monthly");
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  // Proration confirm state: { plan, amountDue, credit, charge } | null
  const [prorationConfirm, setProrationConfirm] = useState(null);
  const [prorationLoading, setProrationLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  const meta = usage?.meta || {};
  const onTrial = usage?.onTrial || false;
  const currentPlan = onTrial ? null : (usage?.plan || null); // null during trial — no plan "selected"
  const currentInterval = meta.billingInterval || null; // "monthly" | "annual" | null
  const effectiveLimit = usage?.limit === Infinity ? null : usage?.limit;

  const plans = [
    { id: "starter", label: "Starter", price: 99, annualPrice: 84, orders: "300 orders/mo", features: ["Orders & Shopify sync", "Production planning", "Roast log", "Packing lists", "Blend receipts"] },
    { id: "roaster", label: "Roaster", price: 199, annualPrice: 169, orders: "2,000 orders/mo", features: ["Everything in Starter", "Live roast monitor", "Inventory", "Cupping & QC", "Routes & analytics"] },
    { id: "growth", label: "Growth", price: 399, annualPrice: 339, orders: "Unlimited orders", features: ["Everything in Roaster", "Demand forecasting", "Purchase orders", "QuickBooks / Xero sync"] },
  ];

  const [addonLoading, setAddonLoading] = useState(null);
  const hasWholesale = usage?.addons?.wholesale?.status === "active";
  const txRate = "$0.20";

  const handleAddonCheckout = async (addonKey) => {
    setAddonLoading(addonKey);
    try {
      const r = await fetch("/api/billing/addon/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ addon: addonKey }),
      });
      const data = await r.json();
      if (data.url) window.location.href = data.url;
      else if (!data.configured) alert("Billing not configured yet.");
      else alert(data.error || "Something went wrong.");
    } catch { alert("Something went wrong."); }
    finally { setAddonLoading(null); }
  };

  // Standard checkout (new subscription or plan change via Stripe Checkout)
  const handleUpgrade = async (planId) => {
    // If on a monthly plan and switching to annual on same plan → proration flow
    if (interval === "annual" && planId === currentPlan && currentInterval === "monthly" && meta.stripeSubscriptionId) {
      setProrationLoading(true);
      try {
        const r = await fetch("/api/billing/proration-preview", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ plan: planId }),
        });
        const data = await r.json();
        if (data.error) { alert(data.error); return; }
        setProrationConfirm({ plan: planId, ...data });
      } catch { alert("Something went wrong."); }
      finally { setProrationLoading(false); }
      return;
    }

    setCheckoutLoading(planId);
    try {
      const r = await fetch("/api/billing/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ plan: planId, interval }),
      });
      const data = await r.json();
      if (data.url) window.location.href = data.url;
      else if (!data.configured) alert("Billing not configured yet. Set STRIPE_SECRET_KEY.");
    } catch { alert("Something went wrong."); }
    finally { setCheckoutLoading(null); }
  };

  // Confirm proration upgrade monthly → annual
  const handleConfirmUpgrade = async () => {
    if (!prorationConfirm) return;
    setUpgradeLoading(true);
    try {
      const r = await fetch("/api/billing/upgrade-annual", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ plan: prorationConfirm.plan }),
      });
      const data = await r.json();
      if (data.ok) {
        setProrationConfirm(null);
        window.location.href = window.location.pathname + "?billing=upgraded";
      } else {
        alert(data.error || "Something went wrong.");
      }
    } catch { alert("Something went wrong."); }
    finally { setUpgradeLoading(false); }
  };

  const handlePortal = async () => {
    const r = await fetch("/api/billing/portal", { method: "POST", credentials: "same-origin" });
    const data = await r.json();
    if (data.url) window.location.href = data.url;
    else alert(data.error || "No billing account found.");
  };

  if (billingLoading) return <div style={{ padding: 32, color: "#9CA3AF", fontSize: 14 }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 860, padding: "0 0 48px" }}>

      {/* Status card */}
      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "20px 24px", marginBottom: 20, display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#111" }}>
              {onTrial ? "Free trial" : (plans.find(p => p.id === currentPlan)?.label || "—") + " plan"}
            </span>
            {onTrial ? (
              <span style={{ fontSize: 11, fontWeight: 600, background: "#FEF3C7", color: "#92400E", padding: "2px 8px", borderRadius: 99 }}>Trial</span>
            ) : meta.status === "past_due" ? (
              <span style={{ fontSize: 11, fontWeight: 600, background: "#FEF2F2", color: "#DC2626", padding: "2px 8px", borderRadius: 99 }}>Payment failed</span>
            ) : (
              <span style={{ fontSize: 11, fontWeight: 600, background: "#F0FDF4", color: "#16A34A", padding: "2px 8px", borderRadius: 99 }}>Active</span>
            )}
          </div>
          {onTrial && meta.trialEnd && (
            <div style={{ fontSize: 13, color: "#D97706" }}>Full Roaster features until {meta.trialEnd} — pick a plan before it ends.</div>
          )}
          {meta.periodEnd && !usage?.onTrial && (
            <div style={{ fontSize: 13, color: "#6B7280" }}>Renews {meta.periodEnd}</div>
          )}
          {meta.status === "past_due" && (
            <div style={{ fontSize: 13, color: "#DC2626" }}>Update your payment method to avoid interruption.</div>
          )}
        </div>
        {meta.stripeCustomerId && (
          <button onClick={handlePortal} style={{ border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 500, padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
            Manage billing
          </button>
        )}
      </div>

      {/* Usage */}
      {usage && (
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "20px 24px", marginBottom: 28 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>Orders this month</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 28, fontWeight: 800, color: "#111", letterSpacing: "-0.02em" }}>{(usage.current || 0).toLocaleString()}</span>
            <span style={{ fontSize: 14, color: "#9CA3AF" }}>/ {effectiveLimit != null ? effectiveLimit.toLocaleString() : "Unlimited"}</span>
            {effectiveLimit && <span style={{ fontSize: 13, fontWeight: 600, color: usage.atLimit ? "#DC2626" : usage.nearLimit ? "#D97706" : "#6B7280" }}>{usage.pct}%</span>}
          </div>
          {effectiveLimit && (
            <div style={{ height: 6, background: "#F3F4F6", borderRadius: 99, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.min(100, usage.pct || 0)}%`, background: usage.atLimit ? "#DC2626" : usage.nearLimit ? "#F59E0B" : "#000", borderRadius: 99, transition: "width 0.3s" }} />
            </div>
          )}
          <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 8 }}>Resets on the 1st of each month</div>
        </div>
      )}

      {/* Interval toggle */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
        {["monthly", "annual"].map(i => (
          <button key={i} onClick={() => setInterval(i)} style={{
            padding: "7px 16px", borderRadius: 8, border: "1px solid #E5E7EB", fontFamily: "Inter, sans-serif",
            background: interval === i ? "#111" : "#fff", color: interval === i ? "#fff" : "#374151",
            fontSize: 13, fontWeight: 500, cursor: "pointer",
          }}>
            {i === "monthly" ? "Monthly" : "Annual (save 15%)"}
          </button>
        ))}
      </div>

      {/* Plan cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 }}>
        {plans.map(plan => {
          // True "current" = plan matches AND interval matches (or interval unknown = treat as current)
          const planMatches = !onTrial && plan.id === currentPlan;
          // isMonthlyToAnnual: same plan, user toggled to annual, not already on annual, has active sub
          const isMonthlyToAnnual = planMatches && currentInterval !== "annual" && interval === "annual" && !!meta.stripeSubscriptionId;
          const isCurrent = planMatches && !isMonthlyToAnnual && (interval === "monthly" || currentInterval === "annual");
          const price = interval === "annual" ? plan.annualPrice : plan.price;
          const btnLoading = checkoutLoading === plan.id || (prorationLoading && isMonthlyToAnnual);
          return (
            <div key={plan.id} style={{
              background: "#fff", border: `${isCurrent || isMonthlyToAnnual ? "2px" : "1px"} solid ${isCurrent ? "#111" : isMonthlyToAnnual ? "#06B6D4" : "#E5E7EB"}`,
              borderRadius: 12, padding: "20px", display: "flex", flexDirection: "column", gap: 12,
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6B7280", marginBottom: 4 }}>{plan.label}</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                  <span style={{ fontSize: 28, fontWeight: 800, color: "#111", letterSpacing: "-0.02em" }}>${price}</span>
                  <span style={{ fontSize: 13, color: "#9CA3AF" }}>/mo</span>
                </div>
                <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>{plan.orders}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                {plan.features.map(f => (
                  <div key={f} style={{ fontSize: 13, color: "#374151", display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <span style={{ color: "#16A34A", fontWeight: 700, flexShrink: 0 }}>✓</span>{f}
                  </div>
                ))}
              </div>
              {isMonthlyToAnnual && (
                <div style={{ fontSize: 11, color: "#0E7490", background: "#ECFEFF", border: "1px solid #A5F3FC", borderRadius: 6, padding: "5px 9px", fontWeight: 500 }}>
                  Pro-rated upgrade — pay only for the rest of your cycle
                </div>
              )}
              <button
                onClick={() => !isCurrent && handleUpgrade(plan.id)}
                disabled={isCurrent || btnLoading}
                style={{
                  padding: "10px", borderRadius: 8,
                  border: isCurrent ? "1px solid #E5E7EB" : isMonthlyToAnnual ? "none" : "none",
                  background: isCurrent ? "#F9FAFB" : isMonthlyToAnnual ? "#06B6D4" : "#111",
                  color: isCurrent ? "#9CA3AF" : "#fff",
                  fontSize: 13, fontWeight: 600, cursor: isCurrent ? "default" : "pointer",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                {btnLoading ? "Loading…" : isCurrent ? "Current plan" : isMonthlyToAnnual ? "Switch to annual →" : onTrial ? "Choose plan" : "Select"}
              </button>
            </div>
          );
        })}
      </div>

      {/* Add-ons */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>Add-ons</div>
        <div style={{
          background: "#fff",
          border: `${hasWholesale ? "2px" : "1px"} solid ${hasWholesale ? "#111" : "#E5E7EB"}`,
          borderRadius: 12, padding: "20px 24px",
          display: "flex", alignItems: "center", gap: 24,
        }}>
          {/* Pricing */}
          <div style={{ flexShrink: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6B7280", marginBottom: 4 }}>Wholesale</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <span style={{ fontSize: 26, fontWeight: 800, color: "#111", letterSpacing: "-0.02em" }}>$99</span>
              <span style={{ fontSize: 12, color: "#9CA3AF" }}>/mo</span>
              <span style={{ fontSize: 12, color: "#9CA3AF", margin: "0 2px" }}>+</span>
              <span style={{ fontSize: 20, fontWeight: 800, color: "#111", letterSpacing: "-0.02em" }}>$0.20</span>
              <span style={{ fontSize: 12, color: "#9CA3AF" }}>/order</span>
            </div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>Flat rate, all plans · added on top of base subscription</div>
          </div>

          {/* Divider */}
          <div style={{ width: 1, alignSelf: "stretch", background: "#F3F4F6", flexShrink: 0 }} />

          {/* Features */}
          <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: "6px 20px" }}>
            {["B2B buyer portal", "Wholesale order management", "Customer pricing tiers", "Net terms & invoicing", "Bulk order imports"].map(f => (
              <div key={f} style={{ fontSize: 13, color: "#374151", display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ color: "#16A34A", fontWeight: 700, fontSize: 11 }}>✓</span>{f}
              </div>
            ))}
          </div>

          {/* CTA */}
          <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 10 }}>
            {hasWholesale && <span style={{ fontSize: 11, fontWeight: 600, background: "#F0FDF4", color: "#16A34A", padding: "3px 9px", borderRadius: 99 }}>Active</span>}
            {hasWholesale ? (
              <button onClick={handlePortal} style={{ border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 500, padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>
                Manage
              </button>
            ) : (
              <button
                onClick={() => handleAddonCheckout("wholesale")}
                disabled={!!addonLoading}
                style={{ border: "none", background: "#111", color: "#fff", fontSize: 13, fontWeight: 600, padding: "9px 18px", borderRadius: 8, cursor: addonLoading ? "default" : "pointer", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap", opacity: addonLoading ? 0.6 : 1 }}
              >
                {addonLoading === "wholesale" ? "Loading…" : "Add Wholesale"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Custom */}
      <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 12, padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>Need more? Custom plan</div>
          <div style={{ fontSize: 13, color: "#6B7280" }}>EDI, SSO, white-label, hardware integrations, dedicated support.</div>
        </div>
        <a href="mailto:hello@elionlabs.org?subject=Custom plan inquiry" style={{ padding: "9px 16px", borderRadius: 8, background: "#111", color: "#fff", fontSize: 13, fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap" }}>
          Talk to us
        </a>
      </div>

      {/* ── Proration confirmation modal ── */}
      {prorationConfirm && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setProrationConfirm(null)}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "28px 32px", width: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.18)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111", marginBottom: 6 }}>Switch to Annual?</div>
            <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 20 }}>
              You'll be pro-rated for the remaining days of your current monthly cycle. Here's what happens today:
            </div>

            <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10, padding: "16px 18px", marginBottom: 20, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: "#6B7280" }}>Annual plan charge</span>
                <span style={{ fontWeight: 600, color: "#111" }}>${((prorationConfirm.charge || 0) / 100).toFixed(2)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: "#6B7280" }}>Unused monthly credit</span>
                <span style={{ fontWeight: 600, color: "#16A34A" }}>−${((prorationConfirm.credit || 0) / 100).toFixed(2)}</span>
              </div>
              <div style={{ borderTop: "1px solid #E5E7EB", paddingTop: 10, display: "flex", justifyContent: "space-between", fontSize: 15 }}>
                <span style={{ fontWeight: 700, color: "#111" }}>Charged today</span>
                <span style={{ fontWeight: 800, color: "#111" }}>${((prorationConfirm.amountDue || 0) / 100).toFixed(2)}</span>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={handleConfirmUpgrade}
                disabled={upgradeLoading}
                style={{ flex: 1, padding: "10px 0", background: "#111", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
              >
                {upgradeLoading ? "Upgrading…" : "Confirm switch"}
              </button>
              <button
                onClick={() => setProrationConfirm(null)}
                style={{ flex: 1, padding: "10px 0", background: "#F3F4F6", color: "#374151", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   INVENTORY — Overview dashboard + Receiving log
   ═══════════════════════════════════════════════════════ */
function InventoryOverview({
  activeLots, totalGreenLbs, totalInventoryValue, lowStockCount, agingCount, staleCount,
  oldestLot, dailyBurn, daysRunway, burnPerDay, byOrigin, byVendor, aging,
  lotsToReorder, lotsAgingSoon, onJumpToLow, onJumpToAging, onOpenLot,
  greenAgingDays, greenStaleDays,
}) {
  const fmtLbs = (n) => `${Math.round(n).toLocaleString()} lb`;
  const fmtMoneyShort = (n) => n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Math.round(n)}`;
  const kpis = [
    { l: "Green available",  v: fmtLbs(totalGreenLbs) },
    { l: "Inventory value",  v: totalInventoryValue > 0 ? `$${Math.round(totalInventoryValue).toLocaleString()}` : "—" },
    { l: "Days runway",      v: daysRunway != null ? `${daysRunway}d` : "—", sub: dailyBurn > 0.5 ? `${Math.round(dailyBurn)} lb/day` : "no burn yet" },
    { l: "Active lots",      v: activeLots.filter(l => l.currentWeightLbs > 0).length },
    { l: "Low stock",        v: lowStockCount, color: lowStockCount > 0 ? "#DC2626" : "#000" },
    { l: "Aging / Stale",    v: `${agingCount} / ${staleCount}`, color: staleCount > 0 ? "#DC2626" : agingCount > 0 ? "#EA580C" : "#000" },
  ];
  return (
    <div>
      {/* KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12, marginBottom: 16 }}>
        {kpis.map(s => (
          <div key={s.l} style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", padding: "14px 16px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6 }}>{s.l}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color || "#000", marginTop: 6, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{s.v}</div>
            {s.sub && <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* Charts row 1: by origin + by vendor */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <BarsCard title="Stock by origin" rows={byOrigin.slice(0, 6)} fmt={fmtLbs} />
        <BarsCard title="Stock by vendor" rows={byVendor.slice(0, 6)} fmt={fmtLbs} />
      </div>

      {/* Charts row 2: aging donut + 30-day burn sparkline */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16, marginBottom: 16 }}>
        <AgingCard aging={aging} agingDays={greenAgingDays} staleDays={greenStaleDays} />
        <BurnCard perDay={burnPerDay} totalBurn={burnPerDay.reduce((s, n) => s + n, 0)} dailyBurn={dailyBurn} />
      </div>

      {/* Alerts */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <AlertsCard
          title="Reorder needed"
          tint="#FEE2E2"
          border="#FCA5A5"
          fg="#991B1B"
          ctaLabel="See low-stock lots"
          onCta={lotsToReorder.length > 0 ? onJumpToLow : null}
          empty="No lots below their reorder point."
          rows={lotsToReorder.slice(0, 5).map(l => ({
            id: l.id,
            lot: l,
            primary: l.name,
            secondary: `${l.origin || ""}${l.origin && l.supplier ? " · " : ""}${l.supplier || ""}`,
            right: `${Math.round(l.currentWeightLbs)} / ${l.lowStockLbs || 20} lb`,
          }))}
          onOpenLot={onOpenLot}
        />
        <AlertsCard
          title="Aging soon"
          tint="#FEF3C7"
          border="#FDE68A"
          fg="#CC785C"
          ctaLabel="See aging lots"
          onCta={lotsAgingSoon.length > 0 ? onJumpToAging : null}
          empty="Nothing in the aging window."
          rows={lotsAgingSoon.slice(0, 5).map(l => ({
            id: l.id,
            lot: l,
            primary: l.name,
            secondary: `${l.origin || ""}${l.origin && l.supplier ? " · " : ""}${l.supplier || ""}`,
            right: `${l.ageDays}d old`,
          }))}
          onOpenLot={onOpenLot}
        />
      </div>
    </div>
  );
}

function BarsCard({ title, rows, fmt }) {
  // Vertical-bar version of the chart — paired bars where the lead
  // (top) bar is hatched black and the rest are solid light gray.
  // Dotted horizontal rules at quartiles give the eye a baseline
  // without competing for attention.
  const max = Math.max(1, ...rows.map(r => r.lbs));
  const W = 460, H = 180, padTop = 10, padBot = 32, padX = 12;
  const usableH = H - padTop - padBot;
  const slotW = rows.length > 0 ? (W - padX * 2) / rows.length : 0;
  const barW = Math.max(8, Math.min(36, slotW * 0.55));
  const yFor = (v) => padTop + usableH - (v / max) * usableH;
  const gridY = [0.25, 0.5, 0.75, 1.0].map(p => padTop + usableH - p * usableH);
  const patternId = `hatch-${title.replace(/\s+/g, "-")}`;
  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#000", letterSpacing: "-0.01em" }}>{title}</div>
        <button style={{ width: 24, height: 24, border: "1px solid #E5E7EB", borderRadius: 6, background: "#fff", color: "#9CA3AF", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
          <svg width="11" height="11" viewBox="0 0 12 12"><path d="M3 9 L9 3 M5 3 L9 3 L9 7" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" /></svg>
        </button>
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 13, color: "#9CA3AF", padding: "20px 0", textAlign: "center" }}>No data yet.</div>
      ) : (
        <>
          <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block" }}>
            <defs>
              <pattern id={patternId} patternUnits="userSpaceOnUse" width="3" height="3">
                <rect width="3" height="3" fill="#000" />
                <line x1="0" y1="0" x2="3" y2="0" stroke="#fff" strokeWidth="0.6" opacity="0.55" />
              </pattern>
            </defs>
            {gridY.map((y, i) => (
              <line key={i} x1={padX} x2={W - padX} y1={y} y2={y} stroke="#E5E7EB" strokeWidth="1" strokeDasharray="2 4" />
            ))}
            {rows.map((r, i) => {
              const slotX = padX + i * slotW + (slotW - barW) / 2;
              const h = Math.max(2, (r.lbs / max) * usableH);
              const isLead = i === 0;
              return (
                <g key={r.name}>
                  <rect
                    x={slotX} y={H - padBot - h}
                    width={barW} height={h}
                    rx={3}
                    fill={isLead ? `url(#${patternId})` : "#C9CDD4"}
                  />
                  <text
                    x={slotX + barW / 2} y={H - padBot + 14}
                    fontSize="9" fill="#000" fontFamily="Inter, sans-serif"
                    textAnchor="middle"
                  >{(r.name || "").length > 9 ? `${r.name.slice(0, 8)}…` : r.name}</text>
                </g>
              );
            })}
          </svg>
          <div style={{ display: "flex", gap: 14, fontSize: 12, color: "#6B7280", marginTop: 10 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 9, height: 9, background: `url(#${patternId})`, borderRadius: 2, display: "inline-block", backgroundColor: "#000" }} />
              <span style={{ color: "#000" }}>●</span> Top
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 9, height: 9, background: "#C9CDD4", borderRadius: 2, display: "inline-block" }} />
              Others
            </span>
            <span style={{ marginLeft: "auto", color: "#9CA3AF" }}>peak {fmt(max)}</span>
          </div>
        </>
      )}
    </div>
  );
}

function AgingCard({ aging, agingDays, staleDays }) {
  const total = aging.fresh + aging.aging + aging.stale;
  // Three slices: dominant slice (largest by lbs) gets the hatched
  // black fill, the rest fall to neutral grays. Refined dashboard
  // aesthetic — no traffic-light colors.
  const sliceData = [
    { k: "fresh", label: `< ${agingDays} days`,            value: aging.fresh },
    { k: "aging", label: `${agingDays}–${staleDays} days`, value: aging.aging },
    { k: "stale", label: `${staleDays}+ days`,             value: aging.stale },
  ];
  const sortedByValue = [...sliceData].sort((a, b) => b.value - a.value);
  const dominantKey = sortedByValue[0]?.k;
  const fillFor = (k) => k === dominantKey ? "url(#aging-hatch)" : k === "stale" ? "#9AA0AA" : "#C9CDD4";
  const dotFor  = (k) => k === dominantKey ? "#000" : k === "stale" ? "#9AA0AA" : "#C9CDD4";

  const r = 56, stroke = 16, c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#000", letterSpacing: "-0.01em" }}>Aging distribution</div>
        <button style={{ width: 24, height: 24, border: "1px solid #E5E7EB", borderRadius: 6, background: "#fff", color: "#9CA3AF", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
          <svg width="11" height="11" viewBox="0 0 12 12"><path d="M3 9 L9 3 M5 3 L9 3 L9 7" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" /></svg>
        </button>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 18 }}>
        <svg width={150} height={150} viewBox="0 0 150 150">
          <defs>
            <pattern id="aging-hatch" patternUnits="userSpaceOnUse" width="3" height="3">
              <rect width="3" height="3" fill="#000" />
              <line x1="0" y1="0" x2="3" y2="0" stroke="#fff" strokeWidth="0.6" opacity="0.55" />
            </pattern>
          </defs>
          <circle cx="75" cy="75" r={r} fill="none" stroke="#F4F4F5" strokeWidth={stroke} />
          {total > 0 && sliceData.map((s, i) => {
            const len = (s.value / total) * c;
            if (len <= 0) return null;
            const dash = `${len} ${c - len}`;
            const el = (
              <circle key={i} cx="75" cy="75" r={r} fill="none"
                stroke={fillFor(s.k)}
                strokeWidth={stroke}
                strokeDasharray={dash}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
                transform="rotate(-90 75 75)" />
            );
            offset += len;
            return el;
          })}
          <text x="75" y="73" textAnchor="middle" fontSize="22" fontWeight="700" fill="#000" fontFamily="Inter, sans-serif" letterSpacing="-0.02em">{Math.round(total).toLocaleString()}</text>
          <text x="75" y="92" textAnchor="middle" fontSize="9" fontWeight="700" fill="#9CA3AF" fontFamily="Inter, sans-serif" letterSpacing="0.12em">LBS ON HAND</text>
        </svg>
      </div>
      <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", fontSize: 12 }}>
        {sliceData.map(s => (
          <span key={s.k} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#3F3F46" }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: dotFor(s.k), display: "inline-block" }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function BurnCard({ perDay, totalBurn, dailyBurn }) {
  // Smooth the spiky per-day series with a 7-day rolling average so
  // we have a calm shape to draw — matches the reference's
  // soft-mountain aesthetic instead of the previous spiky bars.
  const rolling = perDay.map((_, i) => {
    const start = Math.max(0, i - 6);
    const w = perDay.slice(start, i + 1);
    return w.reduce((s, v) => s + v, 0) / w.length;
  });
  const max = Math.max(1, ...rolling, ...perDay);
  const W = 480, H = 110, padTop = 14, padBot = 4, padX = 0;
  const usableH = H - padTop - padBot;
  const stepX = (W - padX * 2) / Math.max(1, rolling.length - 1);
  const points = rolling.map((v, i) => [padX + i * stepX, padTop + usableH - (v / max) * usableH]);

  // Cubic-bezier smoothing through the points (Catmull-Rom-ish).
  const tension = 0.32;
  const smoothPath = (() => {
    if (points.length < 2) return "";
    const d = [`M${points[0][0].toFixed(1)},${points[0][1].toFixed(1)}`];
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[Math.max(0, i - 1)];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[Math.min(points.length - 1, i + 2)];
      const cp1x = p1[0] + (p2[0] - p0[0]) * tension;
      const cp1y = p1[1] + (p2[1] - p0[1]) * tension;
      const cp2x = p2[0] - (p3[0] - p1[0]) * tension;
      const cp2y = p2[1] - (p3[1] - p1[1]) * tension;
      d.push(`C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`);
    }
    return d.join(" ");
  })();
  const fillPath = `${smoothPath} L${(W - padX).toFixed(1)},${(H - padBot).toFixed(1)} L${padX.toFixed(1)},${(H - padBot).toFixed(1)} Z`;
  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#000", letterSpacing: "-0.01em" }}>30-day burn rate</div>
        <div style={{ fontSize: 13, color: "#3F3F46", fontVariantNumeric: "tabular-nums" }}>
          <strong style={{ color: "#000", fontWeight: 700 }}><UnitWeight valueLbs={totalBurn} decimals={0} /></strong>
          <span style={{ marginLeft: 10, color: "#6B7280" }}><UnitWeight valueLbs={dailyBurn} decimals={0} suffix="/day avg" /></span>
        </div>
      </div>
      <svg width="100%" height={H + 20} viewBox={`0 0 ${W} ${H + 20}`} preserveAspectRatio="none" style={{ display: "block" }}>
        <defs>
          <linearGradient id="burnFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1F2937" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#1F2937" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={fillPath} fill="url(#burnFill)" />
        <path d={smoothPath} fill="none" stroke="#000" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <text x={padX + 4} y={H + 16} fontSize="10" fill="#9CA3AF" fontFamily="Inter, sans-serif">30d ago</text>
        <text x={W - padX - 4} y={H + 16} fontSize="10" fill="#9CA3AF" fontFamily="Inter, sans-serif" textAnchor="end">today</text>
      </svg>
    </div>
  );
}

function AlertsCard({ title, rows, tint, border, fg, ctaLabel, onCta, empty, onOpenLot }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6 }}>{title}</div>
        {onCta && (
          <button onClick={onCta} style={{ fontSize: 12, color: "#000", fontWeight: 600, background: "none", border: "none", cursor: "pointer", fontFamily: "Inter, sans-serif", padding: 0 }}>{ctaLabel} →</button>
        )}
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: "20px 0 8px", fontSize: 13, color: "#9CA3AF" }}>{empty}</div>
      ) : rows.map((r, i) => (
        <button
          key={r.id}
          onClick={() => onOpenLot?.(r.lot)}
          style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "11px 0", border: "none", background: "transparent", borderTop: i === 0 ? "none" : "1px solid #F3F4F6", textAlign: "left", cursor: "pointer", fontFamily: "Inter, sans-serif" }}
        >
          <div style={{ width: 3, height: 26, borderRadius: 99, background: fg, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#000", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.primary}</div>
            <div style={{ fontSize: 11, color: "#6B7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 2 }}>{r.secondary}</div>
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 99, background: tint, color: fg, border: `1px solid ${border}`, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{r.right}</span>
        </button>
      ))}
    </div>
  );
}

/* Read-only strip showing the production days the user defined under
   Orders → Planning. Drops into Roasting Planning and Packing so each
   stage knows which day's work is which. metric switches the per-day
   summary between roasted lbs ("roast") and bag units ("units"). */
function ProductionDaysStrip({ productionDays = [], activeDayId, visibleOrders = [], orderDayMap = {}, planRows = [], metric = "roast" }) {
  if (!productionDays || productionDays.length === 0) return null;
  const ordersByDay = {};
  for (const o of visibleOrders) {
    const d = orderDayMap[o.id] || productionDays[0]?.id;
    if (!d) continue;
    if (!ordersByDay[d]) ordersByDay[d] = { orders: 0, units: 0, batches: 0, roastedLbs: 0 };
    ordersByDay[d].orders += 1;
    ordersByDay[d].units += (o.items || []).reduce((s, it) => s + (Number(it.qty) || 0), 0);
  }
  for (const r of planRows) {
    const o = visibleOrders.find(x => x.id === r.sourceOrderId || x.shopifyId === r.sourceOrderId);
    const d = (o && orderDayMap[o.id]) || productionDays[0]?.id;
    if (!d) continue;
    if (!ordersByDay[d]) ordersByDay[d] = { orders: 0, units: 0, batches: 0, roastedLbs: 0 };
    ordersByDay[d].batches += Number(r.batches) || 1;
    ordersByDay[d].roastedLbs += Number(r.roastedLbs) || 0;
  }
  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "12px 14px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6 }}>Production days · from Planning</div>
        <div style={{ fontSize: 11, color: "#9CA3AF" }}>Read-only · edit in Orders → Planning</div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {productionDays.map(d => {
          const stats = ordersByDay[d.id] || { orders: 0, units: 0, batches: 0, roastedLbs: 0 };
          const isActive = d.id === activeDayId;
          return (
            <div key={d.id} style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              padding: "8px 14px", borderRadius: 10,
              background: isActive ? "#000" : "#F9FAFB",
              color: isActive ? "#fff" : "#000",
              border: `1px solid ${isActive ? "#000" : "#E5E7EB"}`,
              fontFamily: "Inter, sans-serif", cursor: "default",
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "-0.01em" }}>{d.label}</span>
              {d.date && (
                <span style={{ fontSize: 11, color: isActive ? "rgba(255,255,255,0.6)" : "#9CA3AF" }}>
                  {new Date(d.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </span>
              )}
              <span style={{ display: "inline-flex", gap: 8, fontSize: 11, fontVariantNumeric: "tabular-nums", color: isActive ? "rgba(255,255,255,0.78)" : "#3F3F46" }}>
                <span>{stats.orders} ord</span>
                <span>·</span>
                {metric === "roast" ? (
                  <>
                    <span>{stats.batches} btch</span>
                    <span>·</span>
                    <span>{Math.round(stats.roastedLbs)} lb</span>
                  </>
                ) : (
                  <span>{stats.units} units</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Working documents view (Inventory) ─────────────────────────────
   Surfaces every Purchase Order in the workspace as a read-only
   working document. Non-editable on this side — the source of truth
   stays on the Purchasing tab. Clicking a document opens its public
   receiving page (mobile/iPad-friendly), which is what warehouse
   staff actually use to check off line items as they arrive. */
function WorkingDocsView() {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/purchasing/pos", { credentials: "same-origin" });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setPos(d.pos || []);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // From the Receiving operator's perspective: a "sent" PO is just an
  // open shipment waiting to land — calling it "Open" reads more
  // naturally than "Sent" (which describes the seller's view).
  const STATUS_TINT = {
    draft:     { bg: "#F4F4F5", fg: "#52525B", label: "Draft" },
    sent:      { bg: "#EFF6FF", fg: "#1D4ED8", label: "Open" },
    partial:   { bg: "#FEF3C7", fg: "#92400E", label: "Partially received" },
    received:  { bg: "#ECFDF5", fg: "#047857", label: "Received" },
    invoiced:  { bg: "#F5F3FF", fg: "#6D28D9", label: "Invoiced" },
    cancelled: { bg: "#F4F4F5", fg: "#A1A1AA", label: "Cancelled" },
  };
  const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  if (pos == null) {
    return <div style={{ padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>Loading…</div>;
  }

  // Receiving = open work. Drafts haven't been issued yet; cancelled
  // and fully-received POs belong in the Receiving log, not here.
  // Sorted by expected date, soonest first so the earliest arrivals
  // surface at the top.
  const list = pos
    .filter(p => p.status === "sent" || p.status === "partial")
    .sort((a, b) => (a.expectedDate || "").localeCompare(b.expectedDate || ""));

  const totalBags = (po) => (po.lineItems || []).reduce((s, l) => {
    const u = String(l.unit || "").toLowerCase();
    if (!u.includes("bag")) return s;
    return s + (Number(l.qty) || 0);
  }, 0);

  return (
    <div>
      <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#000", margin: 0, lineHeight: "28px", letterSpacing: "-0.01em" }}>Receiving</h1>
          <p style={{ fontSize: 14, color: "#6B7280", margin: "4px 0 0", lineHeight: 1.5 }}>Read-only mirror of Finance → POs. Click a row to open its receiving page.</p>
        </div>
      </div>
      {list.length === 0 ? (
        <div style={{ background: "#FAFAFA", borderRadius: 12, padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 14, border: "1px dashed #E5E7EB" }}>
          Nothing to receive yet. POs you create in <strong style={{ color: "#000" }}>Finance</strong> will show up here.
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 110px 110px 110px 130px", padding: "10px 16px", background: "#F9FAFB", fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid #E5E7EB" }}>
            <span>PO #</span><span>Vendor</span><span style={{ textAlign: "right" }}>Bags</span><span>Issued</span><span>Expected</span><span style={{ textAlign: "right" }}>Status</span>
          </div>
          {list.map(po => {
            const tint = STATUS_TINT[po.status] || STATUS_TINT.draft;
            const bags = totalBags(po);
            return (
              <div
                key={po.id}
                onClick={() => window.open(`/receive/${po.id}`, "_blank")}
                style={{ display: "grid", gridTemplateColumns: "120px 1fr 110px 110px 110px 130px", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #F3F4F6", cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                onMouseEnter={e => e.currentTarget.style.background = "#FAFAFA"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: "#000", fontVariantNumeric: "tabular-nums" }}>{po.number}</span>
                <span style={{ fontSize: 14, color: "#000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{po.vendorSnapshot?.name || "—"}</span>
                <span style={{ fontSize: 13, color: "#3F3F46", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{bags || "—"}</span>
                <span style={{ fontSize: 13, color: "#6B7280" }}>{fmtDate(po.issueDate)}</span>
                <span style={{ fontSize: 13, color: "#6B7280" }}>{fmtDate(po.expectedDate)}</span>
                <span style={{ textAlign: "right" }}>
                  <span style={{ display: "inline-block", padding: "3px 9px", borderRadius: 99, fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", background: tint.bg, color: tint.fg }}>{tint.label}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RoastedStockView({ roastedSkus, setRoastedSkus, roastBatches, greenLots, canEdit }) {
  const fmtMoneyShort = (n) => n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Math.round(n)}`;
  // Total bags + roasted lbs across active SKUs.
  const active = roastedSkus.filter(s => s.status !== "discontinued");
  const totalBags = active.reduce((s, x) => s + (Number(x.onHandBags) || 0), 0);
  const totalLbs = active.reduce((s, x) => s + ((Number(x.onHandBags) || 0) * ((Number(x.sizeOz) || 12) / 16)), 0);
  const totalRetail = active.reduce((s, x) => s + ((Number(x.onHandBags) || 0) * (Number(x.retailPrice) || 0)), 0);
  // Cost basis derived from green lot. If lot has pricePerLb, the cost
  // per bag = (sizeLbs * costPerLb) / yield (~0.86 default).
  const costPerBag = (sku) => {
    const lot = greenLots.find(l => l.id === sku.sourceLotId);
    if (!lot || !lot.pricePerLb) return null;
    const sizeLbs = (Number(sku.sizeOz) || 12) / 16;
    const yieldFactor = 0.86;
    return +(sizeLbs * lot.pricePerLb / yieldFactor).toFixed(2);
  };
  const totalCost = active.reduce((acc, x) => acc + ((Number(x.onHandBags) || 0) * (costPerBag(x) || 0)), 0);
  const stockoutSkus = active.filter(x => (Number(x.onHandBags) || 0) <= 5).length;
  const lowSupplySkus = active.filter(x => x.avgDailySales > 0 && ((Number(x.onHandBags) || 0) / x.avgDailySales) < 7).length;

  // 30-day roast volume by SKU for a "roast trend" mini sparkline.
  const skuTrend = (skuCode) => {
    const buckets = new Array(30).fill(0);
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    for (const b of roastBatches) {
      if (b.skuCode !== skuCode || b.at < cutoff) continue;
      const idx = 29 - Math.floor((Date.now() - b.at) / (24 * 60 * 60 * 1000));
      if (idx >= 0 && idx < 30) buckets[idx] += b.bagsProduced || 0;
    }
    return buckets;
  };

  const adjust = (skuCode, delta) => setRoastedSkus(prev => prev.map(s => s.skuCode === skuCode ? { ...s, onHandBags: Math.max(0, (Number(s.onHandBags) || 0) + delta) } : s));

  const kpis = [
    { l: "Total bags",        v: totalBags.toLocaleString() },
    { l: "Roasted on hand",   v: <UnitWeight valueLbs={totalLbs} decimals={0} /> },
    { l: "Retail value",      v: fmtMoneyShort(totalRetail) },
    { l: "Cost basis",        v: fmtMoneyShort(totalCost), sub: totalCost > 0 ? `${Math.round((1 - totalCost / Math.max(1, totalRetail)) * 100)}% gross` : "no cost data" },
    { l: "Active SKUs",       v: active.length },
    { l: "Stockout / low",    v: `${stockoutSkus} / ${lowSupplySkus}`, color: stockoutSkus > 0 ? "#991B1B" : lowSupplySkus > 0 ? "#CC785C" : "#000" },
  ];
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12, marginBottom: 16 }}>
        {kpis.map(s => (
          <div key={s.l} style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", padding: "14px 16px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6 }}>{s.l}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color || "#000", marginTop: 6, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{s.v}</div>
            {s.sub && <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.6fr 90px 80px 110px 110px 100px 100px 130px", padding: "12px 18px", background: "#F9FAFB", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6 }}>
          <span>SKU</span><span>Size</span><span style={{ textAlign: "right" }}>On hand</span><span style={{ textAlign: "right" }}>Days supply</span><span>30-day roasts</span><span style={{ textAlign: "right" }}>Cost / bag</span><span style={{ textAlign: "right" }}>Retail</span><span style={{ textAlign: "right" }}>Adjust</span>
        </div>
        {active.map((sku) => {
          const cost = costPerBag(sku);
          const margin = cost && sku.retailPrice ? Math.round((1 - cost / sku.retailPrice) * 100) : null;
          const days = sku.avgDailySales > 0 ? Math.round((Number(sku.onHandBags) || 0) / sku.avgDailySales) : null;
          const out = (Number(sku.onHandBags) || 0) <= 5;
          const low = !out && days != null && days < 7;
          const trend = skuTrend(sku.skuCode);
          const tMax = Math.max(1, ...trend);
          return (
            <div key={sku.skuCode} style={{ display: "grid", gridTemplateColumns: "1.6fr 90px 80px 110px 110px 100px 100px 130px", alignItems: "center", padding: "12px 18px", borderTop: "1px solid #F3F4F6" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#000", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sku.name}</div>
                <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
                  {sku.skuCode} · {sku.grind}
                  {sku.lastRoastAt ? ` · last roast ${Math.round((Date.now() - sku.lastRoastAt) / (24 * 60 * 60 * 1000))}d ago` : ""}
                </div>
              </div>
              <div style={{ fontSize: 13, color: "#3F3F46" }}>{sku.sizeLabel}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: out ? "#991B1B" : low ? "#CC785C" : "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{(sku.onHandBags || 0).toLocaleString()}</div>
              <div style={{ textAlign: "right" }}>
                {days != null ? (
                  <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 9px", borderRadius: 99,
                    background: out ? "#FEE2E2" : low ? "#FEF3C7" : "#F4F4F5",
                    color:      out ? "#991B1B" : low ? "#CC785C" : "#3F3F46",
                    border: `1px solid ${out ? "#FCA5A5" : low ? "#FDE68A" : "#E5E7EB"}`,
                    fontVariantNumeric: "tabular-nums",
                  }}>{days}d</span>
                ) : <span style={{ fontSize: 11, color: "#9CA3AF" }}>—</span>}
              </div>
              <div>
                <svg width="92" height="22" viewBox="0 0 92 22" preserveAspectRatio="none" style={{ display: "block" }}>
                  {trend.some(v => v > 0) ? (() => {
                    const stepX = 92 / Math.max(1, trend.length - 1);
                    const points = trend.map((v, i) => `${(i * stepX).toFixed(1)},${(20 - (v / tMax) * 18).toFixed(1)}`).join(" ");
                    return <polyline points={points} fill="none" stroke="#06B6D4" strokeWidth="1.4" strokeLinejoin="round" />;
                  })() : null}
                </svg>
              </div>
              <div style={{ fontSize: 13, fontWeight: 500, color: cost ? "#000" : "#9CA3AF", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {cost ? `$${cost.toFixed(2)}` : "—"}
                {margin != null && <div style={{ fontSize: 10, color: "#6B7280", marginTop: 1 }}>{margin}% margin</div>}
              </div>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>${(Number(sku.retailPrice) || 0).toFixed(2)}</div>
              <div style={{ display: "inline-flex", gap: 4, justifyContent: "flex-end" }}>
                {canEdit && (
                  <>
                    <button onClick={() => adjust(sku.skuCode, -1)} style={iconBtnStyle}>−</button>
                    <button onClick={() => adjust(sku.skuCode, +1)} style={iconBtnStyle}>+</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
        {active.length === 0 && (
          <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>No roasted SKUs yet.</div>
        )}
      </div>
    </div>
  );
}

function RoastBatchesView({ roastBatches, roastedSkus, greenLots }) {
  const [filter, setFilter] = useState("all");
  // Compute 30-day yield + green-burn rollup.
  const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const recent = roastBatches.filter(b => (b.at || 0) >= cutoff);
  const greenIn30 = recent.reduce((s, b) => s + (Number(b.greenLbsIn) || 0), 0);
  const roastedOut30 = recent.reduce((s, b) => s + (Number(b.roastedLbsOut) || 0), 0);
  const avgYield = greenIn30 > 0 ? roastedOut30 / greenIn30 : null;
  const flagged = recent.filter(b => b.qcStatus === "flagged").length;

  const filtered = roastBatches.filter(b => {
    if (filter === "all") return true;
    if (filter === "flagged") return b.qcStatus === "flagged";
    return b.skuCode === filter;
  }).slice(0, 200);

  const skuOptions = Array.from(new Set(roastBatches.map(b => b.skuCode))).sort();
  const skuLabel = (code) => roastedSkus.find(s => s.skuCode === code)?.name || code;
  const lotLabel = (id) => greenLots.find(l => l.id === id)?.name || "(unknown lot)";

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
        {[
          { l: "30-day batches", v: recent.length },
          { l: "Green roasted",  v: <UnitWeight valueLbs={greenIn30} decimals={0} /> },
          { l: "Avg yield",      v: avgYield ? `${(avgYield * 100).toFixed(1)}%` : "—", sub: avgYield ? `${(((1 - avgYield) * 100)).toFixed(1)}% loss` : null },
          { l: "QC flagged",     v: flagged, color: flagged > 0 ? "#CC785C" : "#000" },
        ].map(s => (
          <div key={s.l} style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", padding: "14px 16px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6 }}>{s.l}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color || "#000", marginTop: 6, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{s.v}</div>
            {s.sub && <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* Roast use vs. inventory — twelve weeks of paired bars.
          Roast use = green lbs roasted in that week. Inventory =
          estimated total green stock at the END of that week, derived
          by walking back from the current on-hand and adding what's
          been roasted since (since we don't keep daily snapshots). */}
      {(() => {
        const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
        const now = Date.now();
        const currentInventoryLbs = (greenLots || [])
          .filter(l => !l.archivedAt)
          .reduce((s, l) => s + (Number(l.currentWeightLbs) || 0), 0);
        // Build 12 weekly buckets — index 0 = oldest, 11 = current.
        const buckets = [];
        const weekStart = (offset) => {
          const d = new Date(now - offset * WEEK_MS);
          // Snap to Monday-start of that week.
          const day = (d.getDay() + 6) % 7; // 0 = Mon
          d.setHours(0, 0, 0, 0);
          d.setDate(d.getDate() - day);
          return d.getTime();
        };
        for (let i = 11; i >= 0; i--) {
          const start = weekStart(i);
          const end = start + WEEK_MS;
          buckets.push({ start, end, label: new Date(start).toLocaleDateString("en-US", { month: "short", day: "numeric" }), roasted: 0, inventoryEnd: 0 });
        }
        for (const b of (roastBatches || [])) {
          const at = Number(b.at) || 0;
          if (!at) continue;
          const idx = buckets.findIndex(x => at >= x.start && at < x.end);
          if (idx >= 0) buckets[idx].roasted += Number(b.greenLbsIn) || 0;
        }
        // Walk back from current → inventoryEnd[i] = current + sum of
        // weeks AFTER i (each future week's roasted use). End of the
        // most recent week ≈ current inventory.
        let trailing = 0;
        for (let i = buckets.length - 1; i >= 0; i--) {
          buckets[i].inventoryEnd = currentInventoryLbs + trailing;
          trailing += buckets[i].roasted;
        }
        const maxLeft  = Math.max(1, ...buckets.map(b => b.roasted));
        const maxRight = Math.max(1, ...buckets.map(b => b.inventoryEnd));
        const max = Math.max(maxLeft, maxRight);
        const W = 760, H = 180, padTop = 10, padBottom = 24, padX = 16;
        const usableW = W - padX * 2;
        const usableH = H - padTop - padBottom;
        const groupW = usableW / buckets.length;
        const barW = Math.max(6, Math.floor((groupW - 6) / 2));
        return (
          <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "14px 18px", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#000", letterSpacing: "-0.005em" }}>Roast use vs. inventory</div>
              <div style={{ display: "flex", gap: 14, fontSize: 11, color: "#6B7280" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 10, height: 10, background: "#000", borderRadius: 2, display: "inline-block" }} /> Roasted (lb)
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 10, height: 10, background: "#06B6D4", borderRadius: 2, display: "inline-block" }} /> Inventory end-of-week (lb)
                </span>
              </div>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H, display: "block" }}>
              {/* axis baseline */}
              <line x1={padX} x2={W - padX} y1={H - padBottom} y2={H - padBottom} stroke="#E5E7EB" strokeWidth={1} />
              {buckets.map((b, i) => {
                const groupX = padX + i * groupW + (groupW - barW * 2 - 6) / 2;
                const hRoasted = Math.round((b.roasted / max) * usableH);
                const hInv     = Math.round((b.inventoryEnd / max) * usableH);
                return (
                  <g key={i}>
                    <rect
                      x={groupX} y={H - padBottom - hRoasted}
                      width={barW} height={Math.max(0, hRoasted)}
                      fill="#000" rx={2}
                    >
                      <title>{b.label} · Roasted: {Math.round(b.roasted)} lb</title>
                    </rect>
                    <rect
                      x={groupX + barW + 6} y={H - padBottom - hInv}
                      width={barW} height={Math.max(0, hInv)}
                      fill="#06B6D4" rx={2}
                    >
                      <title>{b.label} · Inventory end: {Math.round(b.inventoryEnd)} lb</title>
                    </rect>
                    {/* week label every other bucket to avoid crowding */}
                    {i % 2 === 1 && (
                      <text x={groupX + barW + 3} y={H - padBottom + 14} fontSize="10" fill="#9CA3AF" textAnchor="middle" fontFamily="Inter, sans-serif">{b.label}</text>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
        );
      })()}

      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        {[
          { v: "all", label: "All batches" },
          { v: "flagged", label: `Flagged · ${flagged}` },
        ].map(t => (
          <button key={t.v} onClick={() => setFilter(t.v)} style={{
            padding: "6px 12px", borderRadius: 7,
            border: `1px solid ${filter === t.v ? "#000" : "#E5E7EB"}`,
            background: filter === t.v ? "#000" : "#fff",
            color: filter === t.v ? "#fff" : "#3F3F46",
            fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
          }}>{t.label}</button>
        ))}
        <select value={skuOptions.includes(filter) ? filter : ""} onChange={(e) => setFilter(e.target.value || "all")} style={{ padding: "6px 12px", borderRadius: 7, border: "1px solid #E5E7EB", background: "#fff", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#3F3F46", marginLeft: "auto" }}>
          <option value="">Filter by SKU…</option>
          {skuOptions.map(s => <option key={s} value={s}>{skuLabel(s)}</option>)}
        </select>
      </div>

      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "120px 130px 1.4fr 1fr 90px 80px 80px 90px", padding: "12px 18px", background: "#F9FAFB", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6 }}>
          <span>Date</span><span>Batch ID</span><span>SKU</span><span>Green lot</span><span style={{ textAlign: "right" }}>Green lb</span><span style={{ textAlign: "right" }}>Yield</span><span style={{ textAlign: "right" }}>Bags</span><span style={{ textAlign: "right" }}>QC</span>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>No batches match this filter.</div>
        ) : filtered.map((b) => (
          <div key={b.id} style={{ display: "grid", gridTemplateColumns: "120px 130px 1.4fr 1fr 90px 80px 80px 90px", alignItems: "center", padding: "11px 18px", borderTop: "1px solid #F3F4F6" }}>
            <span style={{ fontSize: 13, color: "#3F3F46" }}>{new Date(b.at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
            <span style={{ fontSize: 12, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: "#3F3F46" }}>{b.id.replace("BATCH-2026-", "#")}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#000", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{skuLabel(b.skuCode)}</span>
            <span style={{ fontSize: 12, color: "#6B7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lotLabel(b.inputLotId)}</span>
            <span style={{ fontSize: 13, color: "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{Number(b.greenLbsIn).toFixed(1)}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: b.yieldPct < 0.84 ? "#CC785C" : "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{(b.yieldPct * 100).toFixed(1)}%</span>
            <span style={{ fontSize: 13, color: "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{b.bagsProduced}</span>
            <span style={{ textAlign: "right" }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 99,
                background: b.qcStatus === "flagged" ? "#FEF3C7" : "#DCFCE7",
                color:      b.qcStatus === "flagged" ? "#CC785C" : "#166534",
                border: `1px solid ${b.qcStatus === "flagged" ? "#FDE68A" : "#86EFAC"}`,
                textTransform: "uppercase", letterSpacing: 0.4,
              }}>{b.qcStatus}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SuppliesView({ supplies, setSupplies, canEdit }) {
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | "new" | item
  const categories = useMemo(() => {
    const set = new Set(supplies.map(s => s.category || "Other"));
    return ["all", ...Array.from(set).sort()];
  }, [supplies]);
  const filtered = useMemo(() => {
    if (filter === "all") return supplies;
    if (filter === "low") return supplies.filter(s => (s.onHand || 0) <= (s.reorderAt || 0));
    return supplies.filter(s => (s.category || "Other") === filter);
  }, [supplies, filter]);
  const lowCount = supplies.filter(s => (s.onHand || 0) <= (s.reorderAt || 0)).length;
  const totalValue = supplies.reduce((acc, s) => acc + (Number(s.onHand) || 0) * (Number(s.costPerUnit) || 0), 0);
  const upsert = (data) => {
    const id = data.id || `sup-${Date.now().toString(36)}`;
    setSupplies(prev => {
      const i = prev.findIndex(x => x.id === id);
      const merged = { ...data, id };
      return i >= 0 ? prev.map((x, j) => j === i ? merged : x) : [...prev, merged];
    });
  };
  const remove = (id) => {
    if (!window.confirm("Remove this item from supplies?")) return;
    setSupplies(prev => prev.filter(x => x.id !== id));
  };
  const adjust = (id, delta) => {
    setSupplies(prev => prev.map(x => x.id === id ? { ...x, onHand: Math.max(0, (Number(x.onHand) || 0) + delta) } : x));
  };
  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        {[
          { l: "Items tracked", v: supplies.length },
          { l: "Low / out of stock", v: lowCount, color: lowCount > 0 ? "#DC2626" : "#000" },
          { l: "Stock value", v: `$${Math.round(totalValue).toLocaleString()}` },
          { l: "Categories", v: categories.length - 1 },
        ].map(s => (
          <div key={s.l} style={{ flex: 1, background: "#fff", borderRadius: 10, border: "1px solid #F3F4F6", padding: "12px 16px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4 }}>{s.l}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: s.color || "#000", marginTop: 2 }}>{s.v}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        {categories.map(c => (
          <button key={c} onClick={() => setFilter(c)} style={{
            padding: "6px 12px", borderRadius: 7,
            border: `1px solid ${filter === c ? "#000" : "#E5E7EB"}`,
            background: filter === c ? "#000" : "#fff",
            color: filter === c ? "#fff" : "#374151",
            fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
          }}>{c === "all" ? "All" : c}</button>
        ))}
        {lowCount > 0 && (
          <button onClick={() => setFilter("low")} style={{
            padding: "6px 12px", borderRadius: 7,
            border: `1px solid ${filter === "low" ? "#DC2626" : "#FECACA"}`,
            background: filter === "low" ? "#DC2626" : "#FEF2F2",
            color: filter === "low" ? "#fff" : "#B91C1C",
            fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif",
            display: "inline-flex", alignItems: "center", gap: 5,
          }}>Reorder needed · {lowCount}</button>
        )}
        {canEdit && (
          <button onClick={() => setEditing("new")} style={{
            marginLeft: "auto",
            padding: "7px 12px", borderRadius: 8,
            border: "1px solid #000", background: "#000", color: "#fff",
            fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif",
            display: "inline-flex", alignItems: "center", gap: 5,
          }}><Plus size={12} /> New item</button>
        )}
      </div>

      <div style={{ background: "#fff", border: "1px solid #F3F4F6", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 110px 100px 110px 110px 130px", padding: "10px 16px", background: "#F9FAFB", fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4 }}>
          <span>Item</span><span>Category</span><span style={{ textAlign: "right" }}>On hand</span><span style={{ textAlign: "right" }}>Reorder at</span><span style={{ textAlign: "right" }}>$ value</span><span style={{ textAlign: "right" }}>Actions</span>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>No items match this filter.</div>
        ) : filtered.map((s, i) => {
          const low = (s.onHand || 0) <= (s.reorderAt || 0);
          const value = (Number(s.onHand) || 0) * (Number(s.costPerUnit) || 0);
          return (
            <div key={s.id} style={{
              display: "grid", gridTemplateColumns: "1fr 110px 100px 110px 110px 130px",
              alignItems: "center", padding: "12px 16px",
              borderTop: "1px solid #F9FAFB",
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
                  {[s.sku, s.vendor].filter(Boolean).join(" · ")}
                  {s.lastReceivedAt ? ` · received ${new Date(s.lastReceivedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
                </div>
              </div>
              <div style={{ fontSize: 12, color: "#374151" }}>{s.category || "Other"}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: low ? "#DC2626" : "#111", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {Math.round(s.onHand || 0).toLocaleString()}
                <span style={{ fontSize: 11, fontWeight: 400, color: "#9CA3AF", marginLeft: 4 }}>{s.unit || "ea"}</span>
              </div>
              <div style={{ fontSize: 13, color: "#6B7280", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{(s.reorderAt || 0).toLocaleString()}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{value > 0 ? `$${Math.round(value).toLocaleString()}` : "—"}</div>
              <div style={{ display: "inline-flex", gap: 4, justifyContent: "flex-end" }}>
                {canEdit && (
                  <>
                    <button onClick={() => adjust(s.id, -1)} title="−1" style={iconBtnStyle}>−</button>
                    <button onClick={() => adjust(s.id, +1)} title="+1" style={iconBtnStyle}>+</button>
                    <button onClick={() => setEditing(s)} title="Edit" style={iconBtnStyle}><Pencil size={12} /></button>
                    <button onClick={() => remove(s.id)} title="Remove" style={{ ...iconBtnStyle, color: "#B91C1C", border: "1px solid #FCA5A5", background: "#FEF2F2" }}><Trash2 size={12} /></button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <SupplyEditModal
          item={editing === "new" ? null : editing}
          existingCategories={categories.filter(c => c !== "all")}
          onClose={() => setEditing(null)}
          onSave={(data) => { upsert(data); setEditing(null); }}
        />
      )}
    </div>
  );
}

const iconBtnStyle = {
  width: 28, height: 28, padding: 0, borderRadius: 6,
  border: "1px solid #E5E7EB", background: "#fff", color: "#374151",
  cursor: "pointer", fontFamily: "Inter, sans-serif",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  fontSize: 14, fontWeight: 600,
};

function SupplyEditModal({ item, existingCategories, onClose, onSave }) {
  const [form, setForm] = useState({
    name: item?.name || "",
    sku: item?.sku || "",
    category: item?.category || existingCategories[0] || "Bags",
    unit: item?.unit || "each",
    onHand: item?.onHand || 0,
    reorderAt: item?.reorderAt || 0,
    reorderQty: item?.reorderQty || 0,
    costPerUnit: item?.costPerUnit || 0,
    vendor: item?.vendor || "",
  });
  const [saving, setSaving] = useState(false);
  const isNew = !item;
  const submit = (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) return;
    setSaving(true);
    onSave({ ...item, ...form, name: form.name.trim() });
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 520, padding: 24, fontFamily: "Inter, sans-serif", boxShadow: "0 18px 48px rgba(0,0,0,0.18)" }}>
        <h2 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 700, color: "#000" }}>{isNew ? "New supply item" : `Edit ${item.name}`}</h2>
        <SF label="Name *"><input autoFocus value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} style={sFInput} /></SF>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <SF label="SKU"><input value={form.sku} onChange={(e) => setForm(f => ({ ...f, sku: e.target.value }))} style={sFInput} /></SF>
          <SF label="Category"><input list="sup-cats" value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} style={sFInput} />
            <datalist id="sup-cats">{existingCategories.map(c => <option key={c} value={c} />)}</datalist>
          </SF>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <SF label="On hand"><input type="number" min="0" value={form.onHand} onChange={(e) => setForm(f => ({ ...f, onHand: Number(e.target.value) || 0 }))} style={sFInput} /></SF>
          <SF label="Unit"><input value={form.unit} placeholder="each / case / roll" onChange={(e) => setForm(f => ({ ...f, unit: e.target.value }))} style={sFInput} /></SF>
          <SF label="Cost / unit"><input type="number" min="0" step="0.01" value={form.costPerUnit} onChange={(e) => setForm(f => ({ ...f, costPerUnit: Number(e.target.value) || 0 }))} style={sFInput} /></SF>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <SF label="Reorder at"><input type="number" min="0" value={form.reorderAt} onChange={(e) => setForm(f => ({ ...f, reorderAt: Number(e.target.value) || 0 }))} style={sFInput} /></SF>
          <SF label="Reorder qty"><input type="number" min="0" value={form.reorderQty} onChange={(e) => setForm(f => ({ ...f, reorderQty: Number(e.target.value) || 0 }))} style={sFInput} /></SF>
        </div>
        <SF label="Vendor"><input value={form.vendor} onChange={(e) => setForm(f => ({ ...f, vendor: e.target.value }))} style={sFInput} /></SF>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <button type="button" onClick={onClose} style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Cancel</button>
          <button type="submit" disabled={saving || !form.name.trim()} style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>{isNew ? "Create" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

function SF({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}
const sFInput = {
  width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 7,
  border: "1px solid #E5E7EB", fontSize: 13, fontFamily: "Inter, sans-serif", outline: "none",
};

function InventoryReceivingLog({ entries, onOpenLot }) {
  // Click a row → inline detail panel. Mirrors what the Cupping
  // sessions list does so the operator stays on the page instead of
  // bouncing into the lot detail.
  const [openId, setOpenId] = useState(null);
  const fmtLbs = (n) => `${Math.round(Number(n) || 0).toLocaleString()} lb`;

  // Pull PO receipts so this view reflects what was actually received
  // through the PO/Receiving flow — not just manual lot intake. A PO
  // line stamped { received: { status, qty, ts } } counts as a receipt
  // even if it never minted a green-inventory lot event. We keep both
  // sources on the same timeline.
  const [poReceipts, setPoReceipts] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/purchasing/pos", { credentials: "same-origin" });
        if (!r.ok) return;
        const d = await r.json();
        const out = [];
        for (const po of (d.pos || [])) {
          for (const line of (po.lineItems || [])) {
            const rec = line.received;
            if (!rec || !rec.ts) continue;
            // Only count meaningful events — partial / received.
            if (rec.status !== "received" && rec.status !== "partial") continue;
            // Best-effort weight estimate from qty + unit. "bags" are
            // assumed 154 lb (default for green coffee bags); other
            // units are passed through as-is.
            const u = String(line.unit || "").toLowerCase();
            const qty = Number(rec.qty) || 0;
            const lbs = u.includes("bag") ? qty * 154
                      : u.includes("lb")  ? qty
                      : u === "kg"        ? qty * 2.20462
                      : 0;
            const unitPrice = Number(line.unitPrice) || 0;
            const value = unitPrice > 0 ? unitPrice * qty : 0;
            out.push({
              id: `po:${po.id}:${line.id}:${rec.ts}`,
              at: rec.ts,
              lotId: null,
              lotName: line.description || "(unnamed line)",
              origin: "",
              supplier: po.vendorSnapshot?.name || "",
              weightLbs: lbs,
              beforeLbs: null,
              afterLbs: null,
              value,
              pricePerLb: 0,
              note: rec.notes || "",
              poNumber: po.number || null,
              poId: po.id || null,
              poStatus: rec.status,
              receivedQty: qty,
              receivedUnit: line.unit || "",
            });
          }
        }
        if (!cancelled) setPoReceipts(out);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Merge lot-event receipts with PO line receipts, dedupe by id, sort
  // by date desc.
  const merged = (() => {
    const seen = new Set();
    const all = [];
    for (const e of [...(entries || []), ...poReceipts]) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      all.push(e);
    }
    return all.sort((a, b) => (b.at || 0) - (a.at || 0));
  })();
  return (
    <div>
      <div style={{ fontSize: 14, color: "#6B7280", marginBottom: 12 }}>{merged.length} receipt{merged.length === 1 ? "" : "s"} on file</div>
      {merged.length === 0 ? (
        <div style={{ background: "#FAFAFA", borderRadius: 10, padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>No receipts yet — receive a lot from Add source, or mark line items received on a PO, to populate the log.</div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #F3F4F6", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "120px 110px 1fr 1fr 160px 110px", padding: "10px 16px", background: "#F9FAFB", fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4 }}>
            <span>Date</span><span>PO</span><span>Item / Lot</span><span>Vendor / Origin</span><span style={{ textAlign: "right" }}>Stock change</span><span style={{ textAlign: "right" }}>Cost</span>
          </div>
          {merged.map((e, i) => {
            const isOpen = openId === e.id;
            return (
              <div key={e.id}>
                <button
                  onClick={() => setOpenId(isOpen ? null : e.id)}
                  style={{ display: "grid", gridTemplateColumns: "120px 110px 1fr 1fr 160px 110px", alignItems: "center", padding: "12px 16px", borderTop: i === 0 ? "none" : "1px solid #F3F4F6", background: isOpen ? "#FAFAFA" : "transparent", border: "none", textAlign: "left", cursor: "pointer", fontFamily: "Inter, sans-serif", width: "100%" }}
                >
                  <span style={{ fontSize: 13, color: "#374151" }}>{e.at ? new Date(e.at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</span>
                  <span style={{ fontSize: 12, color: e.poNumber ? "#000" : "#9CA3AF", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: e.poNumber ? 600 : 400 }}>{e.poNumber || "—"}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.lotName}</span>
                  <span style={{ fontSize: 13, color: "#6B7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{[e.supplier, e.origin].filter(Boolean).join(" · ")}</span>
                  <span style={{ fontSize: 13, color: "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {e.beforeLbs != null && e.afterLbs != null ? (
                      <>
                        <span style={{ color: "#9CA3AF" }}>{fmtLbs(e.beforeLbs)}</span>
                        <span style={{ color: "#9CA3AF", margin: "0 6px" }}>→</span>
                        <span style={{ fontWeight: 600 }}>{fmtLbs(e.afterLbs)}</span>
                      </>
                    ) : (
                      <span style={{ fontWeight: 600 }}>+ {e.receivedQty != null ? `${e.receivedQty} ${e.receivedUnit || "u"}` : fmtLbs(e.weightLbs)}</span>
                    )}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: e.value > 0 ? "#111" : "#9CA3AF", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{e.value > 0 ? `$${Math.round(e.value).toLocaleString()}` : "—"}</span>
                </button>

                {isOpen && (
                  <div style={{ padding: "16px 18px 18px", background: "#FAFAFA", borderTop: "1px solid #F3F4F6" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: e.note ? 14 : 0 }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Received</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: "#000", fontVariantNumeric: "tabular-nums" }}>
                          + {e.receivedQty != null ? `${e.receivedQty} ${e.receivedUnit || ""}` : fmtLbs(e.weightLbs)}
                        </div>
                        {e.pricePerLb > 0 && <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>at ${e.pricePerLb.toFixed(2)}/lb</div>}
                        {e.poStatus && (
                          <div style={{ marginTop: 4, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: e.poStatus === "received" ? "#047857" : "#92400E" }}>{e.poStatus === "received" ? "Fully received" : "Partial"}</div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{e.beforeLbs != null ? "Lot stock" : "Inventory"}</div>
                        {e.beforeLbs != null && e.afterLbs != null ? (
                          <div style={{ fontSize: 16, color: "#000", fontVariantNumeric: "tabular-nums" }}>
                            <span style={{ color: "#9CA3AF" }}>{fmtLbs(e.beforeLbs)}</span>
                            <span style={{ color: "#000", margin: "0 8px" }}>→</span>
                            <span style={{ fontWeight: 700 }}>{fmtLbs(e.afterLbs)}</span>
                          </div>
                        ) : (
                          <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.45 }}>
                            Not yet posted to a green-coffee lot. Open the PO to mint a lot from this receipt.
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Source</div>
                        <div style={{ fontSize: 13, color: "#000" }}>{e.supplier || "—"}</div>
                        {e.origin && <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>{e.origin}</div>}
                      </div>
                    </div>
                    {e.note && (
                      <div style={{ padding: "10px 12px", background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, color: "#3F3F46", lineHeight: 1.55 }}>
                        {e.note}
                      </div>
                    )}
                    <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {e.lotId && (
                        <button
                          onClick={(ev) => { ev.stopPropagation(); onOpenLot?.(e.lotId); }}
                          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                        >
                          Open lot →
                        </button>
                      )}
                      {e.poNumber && (
                        <button
                          onClick={(ev) => { ev.stopPropagation(); if (e.poId) window.open(`/receive/${e.poId}`, "_blank"); }}
                          disabled={!e.poId}
                          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: e.poId ? "#000" : "#9CA3AF", fontSize: 12, fontWeight: 500, cursor: e.poId ? "pointer" : "default", fontFamily: "Inter, sans-serif" }}
                        >
                          PO {e.poNumber} ↗
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════════════════ */
export default function App({ isDemo = false }) {
  /* ─── Auth / role context ─────────────────────────────────────
     AuthGate guarantees `user` is non-null here (it renders Login
     instead of App when unauthenticated), but we defensively fall
     back to an "admin" shape if the context is somehow missing. */
  const { user, logout } = useAuth();
  const role = user?.role || "admin";
  const cap = (c) => can(user, c);

  /* ─── Super-admin plan simulator ────────────────────────────────────
     Only available to john@elionlabs.org. Stores a temporary
     plan/addon override in sessionStorage so it survives hot-reloads
     but resets on sign-out. When active, `effectiveUsage` replaces
     `usage` everywhere in the UI without touching the server. */
  const isSuperAdmin = user?.email === "j@elionlabs.org" || user?.email === "john@elionlabs.org";
  const [planSim, setPlanSim] = useState(() => {
    try {
      const raw = sessionStorage.getItem("pr:planSim");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [planSimOpen, setPlanSimOpen] = useState(false);

  useEffect(() => {
    try {
      if (planSim) sessionStorage.setItem("pr:planSim", JSON.stringify(planSim));
      else sessionStorage.removeItem("pr:planSim");
    } catch { /* ignore */ }
  }, [planSim]);

  // Plan definitions mirrored client-side for the simulator
  const SIM_PLANS = {
    starter: { label: "Starter", ordersPerMonth: 300, features: ["orders","production","roast_log","packing","blend_receipts"] },
    roaster: { label: "Roaster", ordersPerMonth: 2000, features: ["orders","production","roast_log","packing","blend_receipts","live_roast","green_inventory","coffees","cupping","routes","analytics","green_market"] },
    growth:  { label: "Growth",  ordersPerMonth: Infinity, features: ["orders","production","roast_log","packing","blend_receipts","live_roast","green_inventory","coffees","cupping","routes","analytics","green_market","forecasting","purchase_orders","invoicing","quickbooks"] },
    custom:  { label: "Custom",  ordersPerMonth: Infinity, features: ["*"] },
  };
  const SIM_ADDONS = ["wholesale"];

  // effectiveUsage is declared after `usage` state below — search "Build effectiveUsage"

  /* Page state — persisted to localStorage so a refresh keeps you on the
     current tab instead of bouncing back to Orders. The capability check
     below still runs, so if a user's role doesn't grant access to the
     saved page they still get redirected to Orders. */
  const [page, setPage] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:page");
      return raw || "orders";
    } catch { return "orders"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:page", page); } catch { /* ignore */ }
  }, [page]);

  /* Upgrade wall — set to a module/addon key when the user clicks a
     locked nav item. Renders the per-module benefits + CTA in place
     of the page content. Cleared by clicking another (unlocked) nav
     item or hitting "Back to app". */
  const [upgradeModule, setUpgradeModule] = useState(null);
  // Dismiss the wall on any page change — covers OAuth redirects,
  // modal "Go to settings" buttons, deep links, etc. without each
  // call site having to remember to clear it manually.
  useEffect(() => { setUpgradeModule(null); }, [page]);

  /* Page-bounce (role) — plan-feature bounce is below effectiveUsage declaration
     to avoid a temporal dead zone (const effectiveUsage is declared later). */
  useEffect(() => {
    // "sell" (Invoicing) shares the Finance capability — it's the
    // same module/permission, just a different surface.
    const capPage = page === "sell" ? "purchasing" : page;
    if (!cap(`page:${capPage}`)) setPage("orders");
  }, [user?.role, page]); // eslint-disable-line react-hooks/exhaustive-deps
  // Demo mode — when enabled, every data-driven view is seeded from a
  // synthetic dataset. Real data is snapshotted on toggle-on and restored
  // on toggle-off, so the demo is fully clickable without touching the
  // actual Shopify / inventory / cupping records on disk.
  //
  // Tri-state: null (user hasn't picked yet — triggers the first-login
  // picker modal), true (show demo data), false (start blank). Once the
  // user picks, the value persists to localStorage and the modal never
  // fires again for that browser.
  const [demoResetDone, setDemoResetDone] = useState(false);
  const [demoMode, setDemoMode] = useState(() => {
    if (isDemo) return true; // public demo URL — always demo mode
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:demoMode:v3");
      if (raw === "1") return true;
      if (raw === "0") return false;
      return null; // never decided — surface the picker modal
    } catch { return null; }
  });
  useEffect(() => {
    if (demoMode === null) return; // don't persist the "not yet decided" state
    try { localStorage.setItem("pr:demoMode:v3", demoMode ? "1" : "0"); } catch { /* ignore */ }
  }, [demoMode]);

  /* Welcome modal visibility — only shows for admins who just created the
     team. Onboarding.jsx sets `pr:just-created-team=1` on successful
     create-team; we read it once and rely on click-handlers to clear it. */
  // Mirror of showWelcome — once the welcome picker is dismissed we
  // surface a "Connect your store" modal next, so paid signups don't
  // wander around looking for the integration. Flips to false when
  // the user picks Skip or hits Connect.
  const [showStoreConnect, setShowStoreConnect] = useState(false);
  const [showWelcome] = useState(() => {
    try { return localStorage.getItem("pr:just-created-team") === "1"; }
    catch { return false; }
  });

  /* Which Settings section is currently visible. The Settings sub-nav
     behaves like tabs — one section visible at a time — instead of the
     original long-scroll page. Persisted to localStorage so the user
     stays on the same tab across refreshes. */
  const [settingsSection, setSettingsSection] = useState(() => {
    try { return localStorage.getItem("pr:settingsSection") || "integrations"; }
    catch { return "integrations"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:settingsSection", settingsSection); } catch { /* ignore */ }
  }, [settingsSection]);

  /* RoR trailing-window (seconds) — controls how RoR is derived from
     BT samples in the live roast chart. Options: 1, 15, 30, 60. */
  const [rorWindowSec, setRorWindowSec] = useState(() => {
    try { return Number(localStorage.getItem("pr:rorWindowSec")) || 30; }
    catch { return 30; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:rorWindowSec", String(rorWindowSec)); } catch { /* ignore */ }
  }, [rorWindowSec]);

  /* ── Billing / usage state ──────────────────────────────────────
     `usage` holds the /api/usage response: { plan, current, limit,
     pct, nearLimit, atLimit, features, meta }. `billingLoading` gates
     the PlanBillingPage skeleton. `billingSuccessBanner` fires once
     when the user returns from a Stripe Checkout success redirect. */
  const [usage, setUsage] = useState(null);

  // Build effectiveUsage — real usage overlaid with plan simulator values when active.
  // MUST be declared after `usage` to avoid a temporal dead zone error (const TDZ).
  const effectiveUsage = useMemo(() => {
    // Platform owners (e.g. j@elionlabs.org) bypass the paywall —
    // every module + every add-on always active so cross-workspace
    // support / debugging never hits a "Locked" wall. The simulator
    // can still override on top for testing customer experiences.
    const ownerBypass = isPlatformOwner(user) && !planSim;
    if (ownerBypass && usage) {
      return {
        ...usage,
        plan: "custom",
        label: "Platform owner",
        features: ["*"],
        modules: ["production", "lab", "finance", "analytics", "inventory"],
        addons: { wholesale_lite: { status: "active" } },
        atLimit: false, nearLimit: false, pct: 0,
        limit: Infinity,
        onTrial: false,
        _platformOwner: true,
      };
    }
    if (!planSim || !usage) return usage;
    const simPlan = SIM_PLANS[planSim.plan] || SIM_PLANS.roaster;
    const simAddons = {};
    (planSim.addons || []).forEach(k => { simAddons[k] = { status: "active" }; });
    // When the simulator sets `modules`, derive features from the
    // module → feature map so the paywall fires for unowned modules.
    const MODULE_FEATURES = {
      production: ["orders","production","roast_log","packing","blend_receipts","live_roast","coffees","routes","green_market"],
      lab:        ["cupping"],
      finance:    ["purchase_orders","invoicing","quickbooks"],
      analytics:  ["analytics","forecasting"],
      inventory:  ["green_inventory"],
    };
    let simModules = null;
    let simFeatures = simPlan.features;
    if (Array.isArray(planSim.modules)) {
      simModules = planSim.modules.slice();
      const fset = new Set();
      for (const m of simModules) for (const f of (MODULE_FEATURES[m] || [])) fset.add(f);
      simFeatures = Array.from(fset);
    }
    return {
      ...usage,
      plan: planSim.plan,
      label: simPlan.label,
      features: simFeatures,
      modules: simModules || usage.modules,
      addons: simAddons,
      limit: simPlan.ordersPerMonth === Infinity ? Infinity : simPlan.ordersPerMonth,
      onTrial: planSim.onTrial ?? false,
      atLimit: false, nearLimit: false, pct: 0,
      importGraceDaysLeft: planSim.importGraceDaysLeft ?? 0,
      _simulated: true,
    };
  }, [usage, planSim]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Bounce to Orders when the active plan doesn't include the required feature.
     Must be declared AFTER effectiveUsage to avoid a temporal dead zone error —
     the dependency array is evaluated immediately when the component function runs. */
  useEffect(() => {
    if (!effectiveUsage) return;
    const PAGE_FEATURE = {
      cupping: "cupping", products: "coffees", inventory: "green_inventory",
      analytics: "analytics", billing: "invoicing",
    };
    const required = PAGE_FEATURE[page];
    if (required) {
      const feats = effectiveUsage.features || [];
      if (!feats.includes("*") && !feats.includes(required)) { setPage("orders"); return; }
    }
    if (page === "wholesale") {
      // Accept either the new "wholesale_lite" key or the legacy
      // "wholesale" one (back-compat with older persisted addon
      // records). Without this, toggling Wholesale Lite on in the
      // simulator wouldn't unlock the page — it'd bounce to orders.
      const addons = effectiveUsage.addons || {};
      const hasWholesale = addons.wholesale_lite?.status === "active"
        || addons.wholesale?.status === "active";
      // Demo mode: nav entry shows (showInDemo on the nav definition)
      // even without an active addon so prospects can poke around — so
      // the page-level guard has to bypass too. demoMode is declared
      // higher up in this component (effectiveDemoMode is declared
      // *after* this effect, so referencing it here triggers a TDZ
      // crash when the dep array evaluates).
      if (!hasWholesale && demoMode !== true) setPage("orders");
    }
  }, [page, effectiveUsage?.plan, effectiveUsage?.addons, demoMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // If the active settings section is gated behind a feature the workspace no
  // longer has, fall back to Store Connect. Must live AFTER effectiveUsage
  // declaration — dep arrays are evaluated eagerly and would TDZ otherwise.
  useEffect(() => {
    if (!effectiveUsage) return;
    const feats = effectiveUsage.features || [];
    const hasFeat = (f) => feats.includes("*") || feats.includes(f);
    const gated = {
      devices: "live_roast",
      roasting: "live_roast",
      inventorySettings: "green_inventory",
      reports: "analytics",
    };
    if (gated[settingsSection] && !hasFeat(gated[settingsSection])) {
      setSettingsSection("integrations");
    }
  }, [effectiveUsage, settingsSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const [billingLoading, setBillingLoading] = useState(true);
  const [billingSuccessBanner, setBillingSuccessBanner] = useState(false);
  const [branding, setBranding] = useState({ logoBase64: null, logoName: null });
  const [brandingLoading, setBrandingLoading] = useState(false);

  /* ── Shopify connection state ────────────────────────────────────
     Declared early because `effectiveDemoMode` (derived from
     `shopConnected`) is referenced inside many useEffect dependency
     arrays below. Dependency arrays are evaluated eagerly at render
     time, so any `const` they reference MUST be declared before that
     useEffect call — otherwise we hit a Temporal Dead Zone error and
     the app never mounts.
     ──────────────────────────────────────────────────────────────── */
  const [shopifyStatus, setShopifyStatus] = useState({ configured: false, stores: [] });
  const [shopifyLoading, setShopifyLoading] = useState(true);
  /* SOS Inventory connection state — mirrors the Shopify pattern so
     Settings can show a real Connect/Disconnect card. */
  const [sosStatus, setSosStatus] = useState({ configured: false, connected: false });
  const [sosLoading, setSosLoading] = useState(true);
  const [qboStatus, setQboStatus] = useState({ configured: false, connected: false });
  const [qboLoading, setQboLoading] = useState(true);
  // Init from demo seed ONLY when the persisted demoMode flag explicitly
  // says we're in demo. Real-mode users get an empty list on first paint
  // so the table doesn't flash "demo (53 orders incl. EDI/Transfer) →
  // real (4 unfulfilled Shopify orders)" on every page load.
  const [shopifyOrders, setShopifyOrders] = useState(() => {
    try {
      const flag = typeof localStorage !== "undefined" ? localStorage.getItem("pr:demoMode:v3") : null;
      if (flag === "1") return JSON.parse(JSON.stringify(DEMO_SHOPIFY_ORDERS));
      return [];
    } catch { return []; }
  });
  const [shopifyOrdersLoading, setShopifyOrdersLoading] = useState(false);
  const [shopifyOrdersError, setShopifyOrdersError] = useState("");
  // Wholesale Lite orders — placed by customers through the Elion-
  // hosted storefront. We pull them in alongside Shopify orders so
  // the Roaster Orders view shows everything in one feed.
  const [wholesaleOrders, setWholesaleOrders] = useState([]);
  const [connectInput, setConnectInput] = useState("");
  const [connectError, setConnectError] = useState("");

  const shopConnected = shopifyStatus.stores.length > 0;
  const connectedShop = shopConnected ? shopifyStatus.stores[0].shop : null;

  /* Safety net: if the user connects Shopify while demo mode is on, flip
     demo off automatically. The Settings toggle hides on connect, so
     without this they'd be stuck in demo forever.
     The shipping-provider auto-activation effect lives further down,
     after `shippingSettings` and `saveShippingSettings` are declared — a
     useEffect up here couldn't reference them without a TDZ crash. */
  useEffect(() => {
    /* When a store is connected, kill demo mode immediately and clear the
       stored flag so dummy data never comes back while connected. */
    if (shopConnected && demoMode !== false) {
      setDemoMode(false);
      try { localStorage.removeItem("pr:demoMode:v3"); } catch { /* ignore */ }
    }
  }, [shopConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─────── Effective demo mode ───────────────────────────────────
     Strict boolean — whichever the user picked. While demoMode is still
     null (first-login picker not yet answered), treat it as off so the
     modal can render over a blank app and the user's first deliberate
     choice is what they see.
     When a store is connected, demo mode is always off regardless of
     what demoMode state says.
     ──────────────────────────────────────────────────────────────── */
  const effectiveDemoMode = !shopConnected && demoMode === true;

  // Ref-based snapshot — survives re-renders without triggering them.
  const realDataSnapshotRef = useRef(null);
  const [orders, setOrders] = useState(INIT_ORDERS);
  // Excluded products — line items matching these productIds are hidden from the
  // order list and the production plan. Persisted to localStorage so the choice
  // sticks across reloads. Shape: [{ productId, name, addedAt }]
  const [excludedProducts, setExcludedProducts] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:excludedProducts");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:excludedProducts", JSON.stringify(excludedProducts)); } catch { /* ignore */ }
  }, [excludedProducts]);
  const excludedIds = useMemo(() => new Set(excludedProducts.map(p => p.productId)), [excludedProducts]);
  const addExcluded = (productId, name) => {
    setExcludedProducts(prev => prev.find(p => p.productId === productId) ? prev : [...prev, { productId, name, addedAt: Date.now() }]);
  };
  const removeExcluded = (productId) => {
    setExcludedProducts(prev => prev.filter(p => p.productId !== productId));
  };
  // Which order to show in the detail modal
  const [selectedOrderId, setSelectedOrderId] = useStickyState("selectedOrderId", null);
  // Search query for the Orders page — matches order id, customer name, or any
  // line-item product name (case insensitive). Empty string = no filter.
  const [orderSearch, setOrderSearch] = useState("");
  // Live order-number search results — paid/shipped orders pulled on
  // demand so search can surface them even though the bulk feed only
  // loads unshipped orders.
  const [liveSearchOrders, setLiveSearchOrders] = useState([]);
  const [liveSearchLoading, setLiveSearchLoading] = useState(false);
  // Inventory overrides — let the user edit stock levels inline on the Inventory page.
  // Stored as { [productId]: { greenStockLbs, roastedStockLbs } } and merged into
  // PRODUCTS_BASE to produce `products`. Persisted to localStorage.
  const [inventoryOverrides, setInventoryOverrides] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:inventoryOverrides");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:inventoryOverrides", JSON.stringify(inventoryOverrides)); } catch { /* ignore */ }
  }, [inventoryOverrides]);
  /* User-created coffees from /api/products — appear alongside the demo
     catalog when demo is on; replace it entirely when demo is off. */
  const [localProducts, setLocalProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const fetchLocalProducts = useCallback(async () => {
    if (effectiveDemoMode) { setLocalProducts([]); setProductsLoaded(true); return; }
    try {
      const r = await fetch("/api/products");
      if (r.ok) { const d = await r.json(); setLocalProducts(d.products || []); }
    } catch { /* ignore */ }
    finally { setProductsLoaded(true); }
  }, [effectiveDemoMode]);
  useEffect(() => { fetchLocalProducts(); }, [fetchLocalProducts]);
  const [addCoffeeOpen, setAddCoffeeOpen] = useState(false);

  const products = useMemo(() => {
    const demo = effectiveDemoMode ? PRODUCTS_BASE : [];
    const merged = [...localProducts, ...demo];
    return merged.map(p => ({ ...p, ...(inventoryOverrides[p.id] || {}) }));
  }, [effectiveDemoMode, inventoryOverrides, localProducts]);

  const createProduct = async (fields) => {
    const r = await fetch("/api/products", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(fields),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) return { ok: false, error: d.error || "Save failed" };
    setLocalProducts(prev => [d.product, ...prev]);
    return { ok: true, product: d.product };
  };

  const deleteProduct = async (id) => {
    const r = await fetch(`/api/products/${id}`, { method: "DELETE" });
    if (r.ok) setLocalProducts(prev => prev.filter(p => p.id !== id));
  };
  const updateStock = (id, field, value) => {
    const n = parseFloat(value);
    const clean = Number.isFinite(n) && n >= 0 ? n : 0;
    setInventoryOverrides(prev => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: clean },
    }));
  };
  // Weight loss — green beans lose mass when roasted. Default applies to every
  // product unless a per-product override says otherwise. Stored as a decimal
  // (0.18 = 18%). Persisted to localStorage.
  const [defaultLossPct, setDefaultLossPct] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:defaultLossPct");
      const n = raw != null ? parseFloat(raw) : NaN;
      return Number.isFinite(n) ? n : 0.18;
    } catch { return 0.18; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:defaultLossPct", String(defaultLossPct)); } catch { /* ignore */ }
  }, [defaultLossPct]);
  const [lossOverrides, setLossOverrides] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:lossOverrides");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:lossOverrides", JSON.stringify(lossOverrides)); } catch { /* ignore */ }
  }, [lossOverrides]);
  // Roaster batch size — how much green coffee a single batch holds, in kg.
  // Default ~31.75 kg (70 lb). 1 standard green bag = 154 lb = ~2.2 batches.
  const [batchSizeKg, setBatchSizeKg] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:batchSizeKg");
      const n = raw != null ? parseFloat(raw) : NaN;
      return Number.isFinite(n) && n > 0 ? n : 31.75;
    } catch { return 31.75; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:batchSizeKg", String(batchSizeKg)); } catch { /* ignore */ }
  }, [batchSizeKg]);
  // Display unit for the batch-size input. Source of truth stays in kg
  // so all downstream math (greenLbs / batches) is unaffected — this is
  // purely a UI preference, persisted across sessions.
  const [batchUnit, setBatchUnit] = useState(() => {
    try {
      const v = typeof localStorage !== "undefined" && localStorage.getItem("pr:batchUnit");
      return v === "lb" ? "lb" : "kg";
    } catch { return "kg"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:batchUnit", batchUnit); } catch { /* ignore */ }
  }, [batchUnit]);
  const KG_TO_LB = 2.20462;
  const batchSizeLbs = batchSizeKg * KG_TO_LB;
  /* Operations cost rates + throughput. Pulled from
     /api/settings/cost-rates on mount; null while loading. The
     analytics layer + roast estimator both read these. */
  const [costRates, setCostRates] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/settings/cost-rates", { credentials: "same-origin" });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setCostRates(d.rates || null);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const lbPerHour = Math.max(1, Number(costRates?.throughput?.lbPerHour) || 60);
  // Roast time per batch is now derived from the configured throughput
  // and the batch's green weight. We keep the legacy 12-min fallback
  // for code paths still using a flat "minutes per batch" estimate.
  const minutesPerBatch = 12;

  // Green coffee aging thresholds — configurable in Settings → Inventory
  const [greenAgingDays, setGreenAgingDays] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:greenAgingDays");
      const n = raw != null ? parseInt(raw, 10) : NaN;
      return Number.isFinite(n) && n > 0 ? n : 180;
    } catch { return 180; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:greenAgingDays", String(greenAgingDays)); } catch { /* ignore */ }
  }, [greenAgingDays]);
  const greenStaleDays = Math.round(greenAgingDays * 1.5);

  const setProductLoss = (productId, pctInput) => {
    // pctInput is a percent (0-100). Convert to decimal. Empty string clears override.
    if (pctInput === "" || pctInput == null) {
      setLossOverrides(prev => {
        const next = { ...prev }; delete next[productId]; return next;
      });
      return;
    }
    const n = parseFloat(pctInput);
    if (!Number.isFinite(n)) return;
    const clamped = Math.max(0, Math.min(99, n)) / 100;
    setLossOverrides(prev => ({ ...prev, [productId]: clamped }));
  };
  // Produced orders — captured when the user hits "Mark as produced" on a
  // scheduled order. Stored as an array of { shopifyId, customer, items,
  // producedAt } snapshots so analytics survives even if the order leaves
  // Shopify later. Persisted to localStorage.
  // Always seed with demo data at mount — `shopConnected` is false at mount
  // (the status fetch is async), so `effectiveDemoMode` is always true on
  // first render. When the status fetch lands and a real store is connected
  // with demoMode=false, the sync effect below pulls the real localStorage
  // snapshot back in.
  // Init producedOrders synchronously: if real localStorage data
  // exists AND we're not in a fresh demo session, use it; otherwise
  // fall back to the demo seed. Without this, the first render uses
  // the demo seed (so real Shopify orders don't match producedIds and
  // all show up) and a post-mount effect swaps to the real list,
  // making the table flash "everything → filtered" on every load.
  const [producedOrders, setProducedOrders] = useState(() => {
    try {
      // demoMode === true means the user explicitly opted into demo.
      // If they have, seed demo and ignore localStorage so the demo
      // workspace stays clean.
      const demoFlag = typeof localStorage !== "undefined" ? localStorage.getItem("pr:demoMode:v3") : null;
      if (demoFlag === "1") return JSON.parse(JSON.stringify(DEMO_PRODUCED_ORDERS));
      const raw = typeof localStorage !== "undefined" ? localStorage.getItem("pr:producedOrders") : null;
      if (raw) return JSON.parse(raw);
      // No persisted real data → demo seed is the visible placeholder.
      return JSON.parse(JSON.stringify(DEMO_PRODUCED_ORDERS));
    } catch { return JSON.parse(JSON.stringify(DEMO_PRODUCED_ORDERS)); }
  });
  useEffect(() => {
    // Persist only when we're showing real data so the demo seed doesn't
    // pollute what gets restored on toggle-off.
    if (effectiveDemoMode) return;
    try { localStorage.setItem("pr:producedOrders", JSON.stringify(producedOrders)); } catch { /* ignore */ }
  }, [producedOrders, effectiveDemoMode]);
  // When demoMode flips at runtime (toggle inside the app), swap to the
  // appropriate source. The init above handles the page-load case
  // synchronously; this effect handles in-session toggles.
  const producedRestoredRef = useRef(false);
  useEffect(() => {
    if (effectiveDemoMode) { producedRestoredRef.current = false; return; }
    if (producedRestoredRef.current) return;
    producedRestoredRef.current = true;
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:producedOrders");
      if (raw) {
        const parsed = JSON.parse(raw);
        // Only overwrite if the persisted value actually differs — the
        // sync init above usually already loaded it.
        setProducedOrders(curr => JSON.stringify(curr) === JSON.stringify(parsed) ? curr : parsed);
      }
    } catch { /* ignore */ }
  }, [effectiveDemoMode]);
  const producedIds = useMemo(() => new Set(producedOrders.map(p => p.shopifyId || p.id)), [producedOrders]);
  const markProduced = (order) => {
    const key = order.shopifyId || order.id;
    if (producedIds.has(key)) return;
    setProducedOrders(prev => [...prev, {
      shopifyId: order.shopifyId || null,
      id: order.id,
      customer: order.customer,
      date: order.date,
      items: order.items,
      total: order.total,
      shippingAddress: order.shippingAddress || null,
      producedAt: Date.now(),
    }]);
  };
  const undoProduced = (key) => {
    setProducedOrders(prev => prev.filter(p => (p.shopifyId || p.id) !== key));
  };

  // Skipped orders — user dismissed them from the active workflow. Stored as
  // { shopifyId, id, skippedAt } and auto-cleared once skippedAt is older than
  // skippedClearDays (default 30, configurable 5/15/30).
  const [skippedOrders, setSkippedOrders] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:skippedOrders");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [skippedClearDays, setSkippedClearDays] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:skippedClearDays");
      const n = raw ? parseInt(raw, 10) : NaN;
      return [5, 15, 30].includes(n) ? n : 30;
    } catch { return 30; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:skippedClearDays", String(skippedClearDays)); } catch { /* ignore */ }
  }, [skippedClearDays]);
  // Auto-prune skipped entries older than skippedClearDays on load and whenever
  // the period changes. Anything past the cutoff drops out of storage.
  useEffect(() => {
    const cutoff = Date.now() - skippedClearDays * 24 * 60 * 60 * 1000;
    setSkippedOrders(prev => {
      const next = prev.filter(s => (s.skippedAt || 0) > cutoff);
      if (next.length !== prev.length) {
        try { localStorage.setItem("pr:skippedOrders", JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      }
      return prev;
    });
  }, [skippedClearDays]);
  useEffect(() => {
    try { localStorage.setItem("pr:skippedOrders", JSON.stringify(skippedOrders)); } catch { /* ignore */ }
  }, [skippedOrders]);
  const skippedIds = useMemo(() => new Set(skippedOrders.map(s => s.shopifyId || s.id)), [skippedOrders]);
  const skipOrder = (order) => {
    const key = order.shopifyId || order.id;
    if (skippedIds.has(key)) return;
    setSkippedOrders(prev => [...prev, {
      shopifyId: order.shopifyId || null,
      id: order.id,
      customer: order.customer,
      date: order.date,
      items: order.items,
      total: order.total,
      skippedAt: Date.now(),
    }]);
  };
  const unskipOrder = (key) => {
    setSkippedOrders(prev => prev.filter(s => (s.shopifyId || s.id) !== key));
  };

  /* ───────── Shipping (EasyPost-backed) ─────────────────────────────
     Per-workspace shipping settings + purchased labels. State lives on
     the server behind /api/shipping/*; a thin local cache keeps the UI
     responsive. `effectiveDemoMode` short-circuits the fetches so the
     demo experience never hits the network.
     ──────────────────────────────────────────────────────────────── */
  const [shippingSettings, setShippingSettings] = useState(null); // { easypostConfigured, fromAddress, parcelPresets, ... }
  const [shippingSettingsLoading, setShippingSettingsLoading] = useState(false);
  const [shipments, setShipments] = useState([]); // purchased labels, most-recent first
  const [selectedForShip, setSelectedForShip] = useState(() => new Set()); // order ids
  const [shipModal, setShipModal] = useState(null); // { order } — single-order flow
  const [batchShipModal, setBatchShipModal] = useState(null); // { orders } — bulk flow

  const fetchShippingSettings = useCallback(async () => {
    if (effectiveDemoMode) {
      setShippingSettings({
        easypostConfigured: false,
        easypostKeyMode: "test",
        fromAddress: {},
        parcelPresets: [],
        defaultLabelFormat: "PDF",
        defaultMode: "test",
      });
      return;
    }
    setShippingSettingsLoading(true);
    try {
      const r = await fetch("/api/shipping/settings");
      if (r.ok) {
        const d = await r.json();
        setShippingSettings(d);
      }
    } catch (err) {
      console.error("Failed to load shipping settings", err);
    } finally {
      setShippingSettingsLoading(false);
    }
  }, [effectiveDemoMode]);
  useEffect(() => { fetchShippingSettings(); }, [fetchShippingSettings]);

  const saveShippingSettings = async (patch) => {
    if (effectiveDemoMode) {
      setShippingSettings(prev => ({ ...(prev || {}), ...patch }));
      return { ok: true };
    }
    try {
      const r = await fetch("/api/shipping/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        return { ok: false, error: d.error || "Save failed" };
      }
      const d = await r.json();
      setShippingSettings(d);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || "Network error" };
    }
  };

  /* Auto-activate Shopify Shipping as the provider on first Shopify-connect,
     but only when the user hasn't set up another provider yet. Moved here
     because `shippingSettings` and `saveShippingSettings` need to exist
     first — referencing them higher up throws a TDZ error at render. */
  useEffect(() => {
    if (!shopConnected) return;
    if (!shippingSettings) return;
    const alreadyOtherProvider =
      shippingSettings.easypostConfigured ||
      shippingSettings.shippoConfigured ||
      shippingSettings.shipstationConfigured;
    if (alreadyOtherProvider) return;
    if (shippingSettings.provider === "shopify") return;
    saveShippingSettings({ provider: "shopify" });
  }, [shopConnected, shippingSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchShipments = useCallback(async () => {
    if (effectiveDemoMode) { setShipments([]); return; }
    try {
      const r = await fetch("/api/shipping/shipments");
      if (r.ok) {
        const d = await r.json();
        setShipments(d.shipments || []);
      }
    } catch (err) {
      console.error("Failed to load shipments", err);
    }
  }, [effectiveDemoMode]);
  useEffect(() => { fetchShipments(); }, [fetchShipments]);

  // Map: orderId → most-recent shipment (so the Ready row can show "Shipped").
  const shipmentByOrderId = useMemo(() => {
    const m = new Map();
    for (const s of shipments) {
      const key = s.orderId;
      if (!key) continue;
      const existing = m.get(key);
      if (!existing || (s.purchasedAt || 0) > (existing.purchasedAt || 0)) {
        m.set(key, s);
      }
    }
    return m;
  }, [shipments]);

  const getRatesForOrder = async ({ order, parcel }) => {
    if (effectiveDemoMode) {
      // Deterministic demo rates so the flow is testable offline.
      return {
        ok: true,
        rates: [
          { id: "demo_usps_gnd", carrier: "USPS", service: "GroundAdvantage", rate: "8.42", delivery_days: 4 },
          { id: "demo_usps_prio", carrier: "USPS", service: "Priority", rate: "11.10", delivery_days: 2 },
          { id: "demo_ups_gnd", carrier: "UPSDAP", service: "Ground", rate: "12.78", delivery_days: 3 },
        ],
      };
    }
    try {
      const r = await fetch("/api/shipping/rates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          toAddress: order.shippingAddress || null,
          parcel,
        }),
      });
      const d = await r.json();
      if (!r.ok) return { ok: false, error: d.error || "Rate lookup failed" };
      return { ok: true, rates: d.rates || [], shipmentId: d.shipmentId };
    } catch (err) {
      return { ok: false, error: err.message || "Network error" };
    }
  };

  const buyLabelsForJobs = async (jobs) => {
    if (effectiveDemoMode) {
      // Fake a label purchase without actually hitting the network.
      const results = jobs.map(j => ({
        ok: true,
        orderId: j.orderId,
        shipment: {
          id: `sh_demo_${Math.random().toString(36).slice(2, 8)}`,
          orderId: j.orderId,
          tracking: `DEMO${Math.random().toString(36).slice(2, 12).toUpperCase()}`,
          carrier: j.carrier || "USPS",
          service: j.service || "GroundAdvantage",
          cost: j.cost || "8.42",
          labelUrl: null,
          purchasedAt: Date.now(),
        },
      }));
      setShipments(prev => [...results.map(r => r.shipment), ...prev]);
      return { ok: true, results };
    }
    try {
      const r = await fetch("/api/shipping/labels", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobs }),
      });
      const d = await r.json();
      if (!r.ok) return { ok: false, error: d.error || "Label purchase failed" };
      // Refresh the shipment cache so the Ready tab picks up the new label.
      await fetchShipments();
      return { ok: true, results: d.results || [] };
    } catch (err) {
      return { ok: false, error: err.message || "Network error" };
    }
  };

  // Pick a sensible default parcel preset for a produced order. First preset
  // wins until we build smarter heuristics (per-item weight estimation, etc).
  const defaultParcelFor = (/* order */) => {
    const presets = shippingSettings?.parcelPresets || [];
    return presets[0] || { name: "Default 12oz", length: 7, width: 5, height: 3, weight: 14 };
  };

  const toggleSelectedForShip = (key) => {
    setSelectedForShip(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const clearSelectedForShip = () => setSelectedForShip(new Set());

  /* ───────── Green inventory (server-backed, event-sourced) ─────────
     Every weight change is an event; current weight is derived. State lives
     on the server at /api/inventory so it's consistent across devices. */
  const [greenLots, setGreenLots] = useState(() => JSON.parse(JSON.stringify(DEMO_LOTS)));
  const [greenLotsLoading, setGreenLotsLoading] = useState(false);
  const [selectedLotId, setSelectedLotId] = useStickyState("selectedLotId", null);
  const [addLotOpen, setAddLotOpen] = useState(false);
  const [usageGranularity, setUsageGranularity] = useState("weekly");
  const fetchGreenLots = useCallback(async () => {
    if (effectiveDemoMode) return; // Demo seed owns the state while demo mode is on (incl. no-shop-connected fallback).
    setGreenLotsLoading(true);
    try {
      const r = await fetch("/api/inventory/lots");
      const d = await r.json();
      setGreenLots(d.lots || []);
    } catch (err) {
      console.error("Failed to load green inventory", err);
    } finally {
      setGreenLotsLoading(false);
    }
  }, [effectiveDemoMode]);
  useEffect(() => { fetchGreenLots(); }, [fetchGreenLots]);
  const createGreenLot = async (payload) => {
    if (effectiveDemoMode) {
      const newLot = {
        ...payload,
        id: `lot_demo_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`,
        ageDays: 0,
        currentWeightLbs: parseFloat(payload.initialWeightLbs ?? payload.currentWeightLbs ?? 0),
        initialWeightLbs: parseFloat(payload.initialWeightLbs ?? payload.currentWeightLbs ?? 0),
        events: [],
      };
      setGreenLots(prev => [...prev, newLot]);
      return newLot;
    }
    const r = await fetch("/api/inventory/lots", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error((await r.json()).error || "Create failed");
    await fetchGreenLots();
    return (await r.json()).lot;
  };
  const addLotEvent = async (lotId, body) => {
    if (effectiveDemoMode) {
      const delta = body.deltaLbs != null
        ? Number(body.deltaLbs)
        : body.lbsAdded != null
          ? Number(body.lbsAdded)
          : body.lbsUsed != null
            ? -Number(body.lbsUsed)
            : 0;
      setGreenLots(prev => prev.map(l => l.id === lotId
        ? { ...l, currentWeightLbs: Math.max(0, (l.currentWeightLbs || 0) + delta), events: [...(l.events || []), { ...body, ts: Date.now() }] }
        : l));
      return;
    }
    const r = await fetch(`/api/inventory/lots/${lotId}/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error((await r.json()).error || "Event failed");
    await fetchGreenLots();
  };
  const reconcileLot = async (lotId, physicalWeightLbs, note) => {
    if (effectiveDemoMode) {
      const w = parseFloat(physicalWeightLbs);
      setGreenLots(prev => prev.map(l => l.id === lotId
        ? { ...l, currentWeightLbs: Math.max(0, w), events: [...(l.events || []), { type: "RECONCILE", physicalWeightLbs: w, note, ts: Date.now() }] }
        : l));
      return;
    }
    const r = await fetch(`/api/inventory/lots/${lotId}/reconcile`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ physicalWeightLbs: parseFloat(physicalWeightLbs), note }),
    });
    if (!r.ok) throw new Error((await r.json()).error || "Reconcile failed");
    await fetchGreenLots();
  };
  const archiveLot = async (lotId) => {
    if (effectiveDemoMode) {
      setGreenLots(prev => prev.map(l => l.id === lotId ? { ...l, archivedAt: new Date().toISOString() } : l));
      return;
    }
    await fetch(`/api/inventory/lots/${lotId}`, { method: "DELETE" });
    await fetchGreenLots();
  };
  const selectedLot = useMemo(
    () => selectedLotId ? greenLots.find(l => l.id === selectedLotId) : null,
    [selectedLotId, greenLots]
  );

  /* ───────── Cupping / QC scorecards (server-backed) ───────── */
  // Cupping records used to be one-coffee-per-record; they're now
  // sessions with a `coffees` array. Wrap any legacy record on the way
  // in so the UI only ever has to think about one shape.
  const wrapLegacyCupping = (c) => {
    if (!c) return c;
    if (Array.isArray(c.coffees)) return c;
    return {
      ...c,
      name: c.name || c.coffeeName || "Cupping",
      coffees: [{
        id: `cof_legacy_${c.id}`,
        coffeeName: c.coffeeName || "",
        roastDate: c.roastDate || "",
        scores: c.scores || {},
        descriptors: c.descriptors || {},
        defects: c.defects || 0,
        notes: c.notes || "",
        total: c.total || 0,
      }],
    };
  };
  const [cuppings, setCuppings] = useState(() => JSON.parse(JSON.stringify(DEMO_CUPPINGS)).map(wrapLegacyCupping));
  const [cuppingsLoading, setCuppingsLoading] = useState(false);
  const [addCuppingOpen, setAddCuppingOpen] = useState(false);
  // Sample-side adders. "single" opens AddSampleModal for one sample;
  // "bulk" opens BulkAddSamplesModal for a paste-table of N samples.
  const [addSampleMode, setAddSampleMode] = useState(null); // null | "single" | "bulk"
  // QC notes modal. mode: "complete" opens before stamping a batch
  // as roasted; "edit" opens for a batch already in completed.
  const [qcModal, setQcModal] = useState(null); // null | { batch, mode }
  const [cuppingStatusFilter, setCuppingStatusFilter] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:cuppingStatusFilter");
      if (["all", "pending", "approved", "rejected"].includes(raw)) return raw;
      return "all";
    } catch { return "all"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:cuppingStatusFilter", cuppingStatusFilter); } catch { /* ignore */ }
  }, [cuppingStatusFilter]);
  const [selectedCuppingId, setSelectedCuppingId] = useStickyState("selectedCuppingId", null);
  const [hoveredCuppingId, setHoveredCuppingId] = useState(null);
  const fetchCuppings = useCallback(async () => {
    if (effectiveDemoMode) return;
    setCuppingsLoading(true);
    try {
      const r = await fetch("/api/cuppings");
      const d = await r.json();
      setCuppings((d.cuppings || []).map(wrapLegacyCupping));
    } catch (err) { console.error(err); }
    finally { setCuppingsLoading(false); }
  }, [effectiveDemoMode]);
  useEffect(() => { fetchCuppings(); }, [fetchCuppings]);

  /* Standalone samples — pre-cupping intake. Live alongside cuppings
     so the New cupping modal can pick from them and pull them into a
     session at create time. Auto-cleared in demo mode (we keep things
     simple there — no separate fixture set). */
  const [samples, setSamples] = useState([]);
  const fetchSamples = useCallback(async () => {
    if (effectiveDemoMode) { setSamples(JSON.parse(JSON.stringify(DEMO_SAMPLES))); return; }
    try {
      const r = await fetch("/api/samples", { credentials: "same-origin" });
      if (r.ok) { const d = await r.json(); setSamples(d.samples || []); }
    } catch { /* ignore */ }
  }, [effectiveDemoMode]);
  useEffect(() => { fetchSamples(); }, [fetchSamples]);
  const createSamples = async (payloads) => {
    if (effectiveDemoMode) {
      const recs = payloads.map(p => ({ ...p, id: `samp_demo_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`, createdAt: Date.now() }));
      setSamples(prev => [...recs, ...prev]);
      return recs;
    }
    const r = await fetch("/api/samples", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ samples: payloads }),
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Save failed");
    await fetchSamples();
    return (await r.json()).samples;
  };
  const deleteSample = async (id) => {
    if (effectiveDemoMode) { setSamples(prev => prev.filter(s => s.id !== id)); return; }
    await fetch(`/api/samples/${id}`, { method: "DELETE", credentials: "same-origin" });
    await fetchSamples();
  };

  const createCupping = async (payload) => {
    if (effectiveDemoMode) {
      // Move picked samples from the standalone store INTO the new
      // session's coffees. The real server does this atomically; in
      // demo mode we replicate it client-side so the bubble counts +
      // Samples-view "Not in session" tags update immediately.
      const attachIds = Array.isArray(payload.attachSampleIds) ? payload.attachSampleIds : [];
      const attached = (samples || []).filter(s => attachIds.includes(s.id));
      const coffees = attached.map((s, i) => ({
        id: s.id || `cof_${Date.now().toString(36)}_${i}`,
        coffeeName: s.coffeeName || "(unnamed)",
        sourcedFrom: s.sourcedFrom || "",
        roastDate: s.roastDate || "",
        origin: s.origin || "",
        process: s.process || "",
        variety: s.variety || "",
        expectedNotes: s.expectedNotes || "",
        scores: {},
        defects: 0,
        total: 0,
        status: "pending",
        notes: "",
      }));
      const { attachSampleIds: _drop, ...rest } = payload || {};
      const newC = {
        ...rest,
        id: `cup_demo_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`,
        status: payload.status || "pending",
        coffees,
        total: 0,
      };
      setCuppings(prev => [newC, ...prev]);
      // Drop the moved samples from the standalone list.
      if (attachIds.length > 0) {
        setSamples(prev => prev.filter(s => !attachIds.includes(s.id)));
      }
      return newC;
    }
    const r = await fetch("/api/cuppings", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error((await r.json()).error || "Failed");
    await fetchCuppings();
    // If samples were attached, they were moved into the session on the
    // server — refresh the standalone list so they drop out of pickers.
    await fetchSamples();
    return (await r.json()).cupping;
  };
  const deleteCupping = async (id) => {
    if (effectiveDemoMode) { setCuppings(prev => prev.filter(c => c.id !== id)); return; }
    await fetch(`/api/cuppings/${id}`, { method: "DELETE" });
    await fetchCuppings();
  };
  // Update the coffees array for a cupping session. Recomputes the
  // session's average score (matches what the server does on its side).
  const updateCuppingCoffees = async (id, coffees) => {
    const computeTotal = (cof) => {
      // SCA total. Untouched secondary checks default to 10 (full
      // marks). But the coffee only counts as "scored" when at least
      // one primary attribute is set — otherwise we'd treat a coffee
      // with nothing but the secondary defaults as a 30 / Below
      // Specialty cup, which is a fake score.
      const s = cof.scores;
      if (!s) return 0;
      const primaryKeys = ["fragrance", "flavor", "aftertaste", "acidity", "body", "balance", "overall"];
      if (!primaryKeys.some(k => s[k] != null)) return 0;
      const primary = primaryKeys.reduce((acc, k) => acc + (Number(s[k]) || 0), 0);
      const secVal = (k) => (s[k] != null ? Number(s[k]) : 10);
      const secondary = secVal("uniformity") + secVal("cleanCup") + secVal("sweetness");
      return +(primary + secondary - (Number(cof.defects) || 0)).toFixed(2);
    };
    const enriched = coffees.map(c => ({ ...c, total: computeTotal(c) }));
    const avg = enriched.length > 0
      ? +(enriched.reduce((s, x) => s + (x.total || 0), 0) / enriched.length).toFixed(2)
      : 0;
    if (effectiveDemoMode) {
      setCuppings(prev => prev.map(c => c.id === id ? { ...c, coffees: enriched, total: avg } : c));
      return;
    }
    const r = await fetch(`/api/cuppings/${id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ coffees: enriched }),
    });
    if (!r.ok) throw new Error((await r.json()).error || "Failed");
    const data = await r.json();
    setCuppings(prev => prev.map(c => c.id === id ? wrapLegacyCupping(data.cupping) : c));
  };
  // Patch session metadata (name, notes, cupper, source, date).
  const updateCuppingMeta = async (id, patch) => {
    if (effectiveDemoMode) {
      setCuppings(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
      return;
    }
    const r = await fetch(`/api/cuppings/${id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!r.ok) throw new Error((await r.json()).error || "Failed");
    const data = await r.json();
    setCuppings(prev => prev.map(c => c.id === id ? wrapLegacyCupping(data.cupping) : c));
  };
  const updateCuppingStatus = async (id, status) => {
    if (effectiveDemoMode) {
      setCuppings(prev => prev.map(c => c.id === id ? { ...c, status } : c));
      return;
    }
    const r = await fetch(`/api/cuppings/${id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (!r.ok) throw new Error((await r.json()).error || "Failed");
    await fetchCuppings();
  };
  const selectedCupping = useMemo(
    () => selectedCuppingId ? cuppings.find(c => c.id === selectedCuppingId) : null,
    [selectedCuppingId, cuppings]
  );

  // Email for automated production reports. The UI captures + validates; the
  // actual send-from-server plumbing (SMTP / SendGrid / etc.) isn't wired yet.
  const [reportEmail, setReportEmail] = useState(() => {
    try {
      return (typeof localStorage !== "undefined" && localStorage.getItem("pr:reportEmail")) || "";
    } catch { return ""; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:reportEmail", reportEmail); } catch { /* ignore */ }
  }, [reportEmail]);

  // Batch helpers wired to the "Schedule all" and "All Produced" buttons.
  //
  // Hits a server batch endpoint that walks the orders sequentially at
  // Shopify's leak rate. Two reliability tricks:
  //   - chunk into small groups so a single request never sits open
  //     for minutes (and a mid-flight machine restart only loses one
  //     chunk at most, not the whole run);
  //   - on completion (or any failure), refetch orders from Shopify
  //     so the UI always reflects the actual server-side state, not
  //     our optimistic guess.
  // Live progress UI for Schedule all / Remove all. Surfaced in the
  // toolbar so the user can see how many orders are processed without
  // having to refresh. null = no job in flight.
  const [tagJobProgress, setTagJobProgress] = useState(null);
  // Background-job pattern: server enqueues a job (durably to disk),
  // we poll until it's done. Survives mid-batch deploys: the new
  // machine reads the job file on boot and resumes where the old one
  // stopped. The HTTP call to start the job is short and unaffected
  // by long-running work.
  const tagsBatchInChunks = async ({ orderIds, action, tag = "production", onProgress }) => {
    let jobId;
    // Start. Retry if the start itself fails (e.g. machine restart
    // during the POST).
    for (let attempt = 0; attempt < 6; attempt++) {
      try {
        const r = await fetch("/api/shopify/orders/tags-batch", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ shop: connectedShop, action, tag, orderIds }),
        });
        const data = await r.json().catch(() => ({}));
        if (r.ok && data.jobId) { jobId = data.jobId; break; }
      } catch { /* retry */ }
      await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
    }
    if (!jobId) {
      console.error("tags-batch: failed to start job after retries");
      return new Set(orderIds.map(String));
    }
    // Poll. Tolerate transient errors (502/503 during a restart) by
    // continuing the loop. We only stop when status === "done".
    let lastDone = 0;
    let consecutiveErrors = 0;
    while (true) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const r = await fetch(`/api/shopify/orders/tags-batch/${jobId}`);
        if (!r.ok) { consecutiveErrors++; if (consecutiveErrors > 30) break; continue; }
        consecutiveErrors = 0;
        const data = await r.json();
        if (data.done > lastDone) {
          lastDone = data.done;
          if (onProgress) onProgress({ done: data.done, total: data.total });
        }
        if (data.status === "done") {
          const failed = new Set(
            (data.results || []).filter(x => !x.ok).map(x => String(x.orderId))
          );
          return failed;
        }
      } catch {
        consecutiveErrors++;
        if (consecutiveErrors > 30) break;
      }
    }
    console.warn("tags-batch: gave up polling after sustained errors");
    return new Set(); // unknown — refetch will reconcile
  };

  const scheduleAll = async () => {
    if (shopConnected && !effectiveDemoMode) {
      const toTag = untagged;
      if (toTag.length === 0) return;
      // If a specific day is active, every order being tagged here
      // should land in that day. Apply the assignment up front so the
      // UI reflects it even before the Shopify round-trip completes.
      if (activeDayId) {
        setOrderDayMap(m => {
          const next = { ...m };
          for (const o of toTag) next[String(o.shopifyId || o.id)] = activeDayId;
          return next;
        });
      }
      setShopifyOrders(prev => prev.map(o =>
        toTag.find(t => t.shopifyId === o.shopifyId) ? { ...o, tagged: true } : o
      ));
      setTagJobProgress({ action: "schedule", done: 0, total: toTag.length });
      const failed = await tagsBatchInChunks({
        orderIds: toTag.map(o => o.shopifyId),
        action: "add",
        onProgress: ({ done, total }) =>
          setTagJobProgress(p => p ? { ...p, done, total } : p),
      });
      if (failed.size > 0) {
        console.warn(`Schedule all: ${failed.size} of ${toTag.length} chunk-failed; refetching for truth`);
      }
      // Always refetch — so even if a chunk timed out / the machine
      // restarted, the UI ends up reflecting whatever Shopify actually
      // shows now, not our optimistic guess.
      try { await fetchShopifyOrders(true); } catch { /* ignore */ }
      setTagJobProgress(null);
    } else if (effectiveDemoMode) {
      setShopifyOrders(prev => prev.map(o => ({ ...o, tagged: true, tags: "production" })));
    } else {
      setOrders(prev => prev.map(o => ({ ...o, tagged: true })));
    }
  };
  const markAllProduced = () => {
    // Scope to the active day when one is selected — "All ready" should
    // only finish that day's orders, not every scheduled order in view.
    const scope = activeDayId ? taggedForActiveDay : tagged;
    if (scope.length === 0) return;
    const now = Date.now();
    setProducedOrders(prev => {
      const existing = new Set(prev.map(p => p.shopifyId || p.id));
      const additions = scope
        .filter(o => !existing.has(o.shopifyId || o.id))
        .map(o => ({
          shopifyId: o.shopifyId || null,
          id: o.id,
          customer: o.customer,
          date: o.date,
          items: o.items,
          total: o.total,
          shippingAddress: o.shippingAddress || null,
          producedAt: now,
        }));
      return [...prev, ...additions];
    });
  };
  // Remove the production tag from every currently-tagged order in
  // view. Same server-batch + chunked + refetch flow as scheduleAll.
  const removeAll = async () => {
    // When a day is active, only untag orders assigned to THAT day.
    // No active day → behave like before (untag everything tagged).
    const scope = activeDayId ? taggedForActiveDay : tagged;
    if (shopConnected && !effectiveDemoMode) {
      const toUntag = scope;
      if (toUntag.length === 0) return;
      setShopifyOrders(prev => prev.map(o =>
        toUntag.find(t => t.shopifyId === o.shopifyId) ? { ...o, tagged: false } : o
      ));
      setTagJobProgress({ action: "remove", done: 0, total: toUntag.length });
      const failed = await tagsBatchInChunks({
        orderIds: toUntag.map(o => o.shopifyId),
        action: "remove",
        onProgress: ({ done, total }) =>
          setTagJobProgress(p => p ? { ...p, done, total } : p),
      });
      if (failed.size > 0) {
        console.warn(`Remove all: ${failed.size} of ${toUntag.length} chunk-failed; refetching for truth`);
      }
      try { await fetchShopifyOrders(true); } catch { /* ignore */ }
      setTagJobProgress(null);
    } else if (effectiveDemoMode) {
      const scopeIds = new Set(scope.map(o => o.shopifyId || o.id));
      setShopifyOrders(prev => prev.map(o =>
        scopeIds.has(o.shopifyId || o.id) ? { ...o, tagged: false, tags: "" } : o
      ));
    } else {
      const scopeIds = new Set(scope.map(o => o.shopifyId || o.id));
      setOrders(prev => prev.map(o =>
        scopeIds.has(o.shopifyId || o.id) ? { ...o, tagged: false } : o
      ));
    }
  };
  const [selectedProduct, setSelectedProduct] = useStickyState("selectedProduct", null);
  // Coffees & Blends sub-tabs: "green" lists every approved sample as
  // an editable green-coffee record; "products" is the existing
  // products list (a product is built from one or more green coffees).
  const [coffeesView, setCoffeesView] = useStickyState("coffeesView", "products");
  const [greenCoffees, setGreenCoffees] = useState([]);
  const [greenCoffeesLoaded, setGreenCoffeesLoaded] = useState(false);
  const fetchGreenCoffees = useCallback(async () => {
    try {
      const r = await fetch("/api/green-coffees", { credentials: "same-origin" });
      if (r.ok) { const d = await r.json(); setGreenCoffees(d.coffees || []); }
    } catch { /* ignore */ }
    finally { setGreenCoffeesLoaded(true); }
  }, []);
  useEffect(() => { fetchGreenCoffees(); }, [fetchGreenCoffees]);
  // Read-only cupping sheet modal for a coffee — opened from the
  // product detail header. Lives at App scope so the modal renders
  // alongside the rest of the global modals.
  const [coffeeCuppingSheet, setCoffeeCuppingSheet] = useState(null); // { product, sample, alternates: [] } | null
  // Orders sub-tab (All / Pending / Production / Ready / Skipped / Clients)
  // — persisted so a refresh on any tab lands you back on that tab. Routes
  // used to live here and was moved under Production; any legacy `routes`
  // value falls through to `all` via the whitelist below.
  // Orders sub-nav always opens on "All orders". The filter switches
  // freely within the session but isn't persisted across reloads —
  // landing on a stale "Pending" view caused too many "where are my
  // orders?" moments.
  const [orderFilter, setOrderFilter] = useStickyState("orderFilter", "all");
  // Channel filter — narrows the order list to one channel. "all"
  // shows every channel.
  const [orderChannelFilter, setOrderChannelFilter] = useStickyState("orderChannelFilter", "all");
  // Clients drill-down: null = list view, otherwise id of the selected client
  const [selectedClientId, setSelectedClientId] = useStickyState("selectedClientId", null);
  const [clientForecastEnabled, setClientForecastEnabled] = useState(true);
  const [clientSearch, setClientSearch] = useState("");

  /* ── EasyRoutes: facility + alternate locations ──────────────
     Each location has { id, name, address, lat, lng, kind }.
     kind: "facility" (default depot), "departure", "arrival", or "both".
     First-time users are prompted to add their facility; after that
     they can manage the list from the EasyRoutes page.
     ─────────────────────────────────────────────────────────── */
  const [routeLocations, setRouteLocations] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:routeLocations");
      if (raw) return JSON.parse(raw);
    } catch { /* fall through */ }
    // Seed a facility so first-time / un-connected users don't hit the
    // EasyRoutes first-run popup on a cold cache. Always seeded at mount
    // because `effectiveDemoMode` is always true at mount (shopConnected
    // resolves asynchronously from the status fetch).
    return [
      {
        id: "loc_demo_facility",
        name: "Elion Roastery",
        address: "1580 Evans Ave, San Francisco, CA 94124",
        lat: 37.7410, lng: -122.3869, kind: "facility",
      },
    ];
  });
  useEffect(() => {
    try { localStorage.setItem("pr:routeLocations", JSON.stringify(routeLocations)); } catch { /* ignore */ }
  }, [routeLocations]);
  // Departure/arrival selections default to facility when present.
  const facilityLocation = useMemo(() => routeLocations.find(l => l.kind === "facility") || null, [routeLocations]);
  const [routeDepartureId, setRouteDepartureId] = useState(null);
  const [routeArrivalId, setRouteArrivalId] = useState(null);
  const [routeSetupOpen, setRouteSetupOpen] = useState(false);
  const [routeLocationModal, setRouteLocationModal] = useState(null); // null | "new" | {location}
  // Ordered list of order keys (shopifyId || id) that the user has explicitly
  // added to today's route. Kept in localStorage so the plan survives reloads.
  // Orders from the Ready bucket that aren't in this list are shown in the
  // "Ready orders" picker at the bottom of the page.
  const [routeStopKeys, setRouteStopKeys] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:routeStopKeys");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:routeStopKeys", JSON.stringify(routeStopKeys)); } catch { /* ignore */ }
  }, [routeStopKeys]);
  // Inventory filter (All / Low / Moderate / Good) — persisted.
  const [inventoryFilter, setInventoryFilter] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:inventoryFilter");
      if (["all", "low", "aging", "moderate", "good"].includes(raw)) return raw;
      return "all";
    } catch { return "all"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:inventoryFilter", inventoryFilter); } catch { /* ignore */ }
  }, [inventoryFilter]);
  // Inventory page sub-views. Overview was folded into "lots" (All
  // sources) so there's a single landing.
  //   "lots"      — KPIs + dashboards + lots table (default)
  //   "supplies"  — packaging + lab consumables
  //   "roasted"   — finished-goods inventory (bag SKUs)
  //   "batches"   — roast-batch history
  //   "receiving" — chronological receipt log
  const [inventoryView, setInventoryView] = useState(() => {
    try {
      const raw = localStorage.getItem("pr:inventoryView");
      // Migrate the old "overview" landing and the (now-removed)
      // "roasted" tab back to the default "lots" view.
      if (!raw || raw === "overview" || raw === "roasted") return "lots";
      return raw;
    } catch { return "lots"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:inventoryView", inventoryView); } catch { /* ignore */ }
  }, [inventoryView]);
  // Supplies inventory — bags, boxes, valves, labels, etc. Currently
  // client-only, seeded from DEMO_SUPPLIES on first run.
  const [supplies, setSupplies] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:supplies");
      return raw ? JSON.parse(raw) : JSON.parse(JSON.stringify(DEMO_SUPPLIES));
    } catch { return JSON.parse(JSON.stringify(DEMO_SUPPLIES)); }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:supplies", JSON.stringify(supplies)); } catch { /* ignore */ }
  }, [supplies]);
  // Roasted-stock SKUs — finished goods at the bag level, keyed by
  // skuCode. onHandBags is mutable (a roast batch increments,
  // shipments decrement). Seeded from DEMO_ROASTED_SKUS.
  const [roastedSkus, setRoastedSkus] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:roastedSkus");
      return raw ? JSON.parse(raw) : JSON.parse(JSON.stringify(DEMO_ROASTED_SKUS));
    } catch { return JSON.parse(JSON.stringify(DEMO_ROASTED_SKUS)); }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:roastedSkus", JSON.stringify(roastedSkus)); } catch { /* ignore */ }
  }, [roastedSkus]);
  // Roast batches — append-only log of every roast that's ever
  // happened. The roasted-stock dashboard reads this for "last
  // roasted", yield trends, and the Batch history sub-view.
  const [roastBatches, setRoastBatches] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:roastBatches");
      return raw ? JSON.parse(raw) : JSON.parse(JSON.stringify(DEMO_ROAST_BATCHES));
    } catch { return JSON.parse(JSON.stringify(DEMO_ROAST_BATCHES)); }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:roastBatches", JSON.stringify(roastBatches)); } catch { /* ignore */ }
  }, [roastBatches]);
  /* prodView = the Production *section* (Roasting / Packing / Routes) —
     one of "breakdown" | "packing" | "routes". Each section gets its
     own sub-nav showing only itself + Activity. The Activity view is
     context-aware and rendered when prodTab flips to "activity". */
  const [prodView, setProdView] = useState(() => {
    if (role === "packer") return "packing";
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:prodView");
      if (["breakdown", "packing", "routes"].includes(raw)) return raw;
      /* Legacy migration: pre-refactor "activity" was a prodView value.
         Now it's a prodTab; fall back to the Roasting section. */
      if (raw === "activity") return "breakdown";
      return "breakdown";
    } catch { return "breakdown"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:prodView", prodView); } catch { /* ignore */ }
  }, [prodView]);
  // Guard: if a packer is already viewing production, force packing view.
  useEffect(() => {
    if (role === "packer" && prodView !== "packing") setProdView("packing");
  }, [role, prodView]);
  /* Roaster's preferred order for the batch plan cards — an ordered
     list of product IDs. Persisted so the order they dragged into last
     session survives a reload. Plans for products not in this list
     fall to the bottom in their natural plan order. */
  const [batchPlanOrder, setBatchPlanOrder] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:batchPlanOrder");
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:batchPlanOrder", JSON.stringify(batchPlanOrder)); } catch { /* ignore */ }
  }, [batchPlanOrder]);
  /* Transient drag-and-drop state for the batch plan card list. These
     are deliberately NOT persisted — they're pointer-level state that
     resets the moment the drag ends. */
  const [draggedPlanId, setDraggedPlanId] = useState(null);
  const [dragOverPlanId, setDragOverPlanId] = useState(null);

  /* ── Roasting launch pad ──────────────────────────────────────
     A three-layer flow: Batches (queue) → Next (single slot on deck)
     → Current (the actively-roasting batch). When Current clears,
     Next auto-promotes and the simulator fires with the batch's
     product name. Completed batches drop into a "Roasted today"
     stack at the bottom, newest first. All persisted so an accidental
     reload mid-roast day doesn't lose state. */
  const [roastingQueue, setRoastingQueue] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:roastingQueue");
      const d = raw ? JSON.parse(raw) : null;
      if (d && typeof d === "object") {
        const batchNumbers = d.batchNumbers && typeof d.batchNumbers === "object" ? { ...d.batchNumbers } : {};
        let nextBatchNum = (d.nextBatchNum && d.nextBatchNum > 100) ? d.nextBatchNum : 247;
        const completed = Array.isArray(d.completed) ? d.completed.map(c => {
          if (c.batchNum && parseInt(c.batchNum.replace("B-", ""), 10) >= 247) return c;
          const newNum = `B-${String(nextBatchNum).padStart(3, "0")}`;
          batchNumbers[c.batchId] = newNum;
          nextBatchNum++;
          return { ...c, batchNum: newNum };
        }) : [];
        return {
          nextId: d.nextId || null,
          currentId: d.currentId || null,
          currentRoastId: d.currentRoastId || null,
          completed,
          batchNumbers,
          nextBatchNum,
        };
      }
    } catch { /* fall through */ }
    return { nextId: null, currentId: null, currentRoastId: null, completed: [], batchNumbers: {}, nextBatchNum: 247 };
  });
  useEffect(() => {
    if (effectiveDemoMode) return; // demo data managed separately
    try { localStorage.setItem("pr:roastingQueue", JSON.stringify(roastingQueue)); } catch { /* ignore */ }
  }, [roastingQueue, effectiveDemoMode]);
  // Seed roastingQueue with demo sessions when in demo mode
  useEffect(() => {
    if (effectiveDemoMode) {
      setRoastingQueue(JSON.parse(JSON.stringify(DEMO_ROASTING_QUEUE)));
    }
  }, [effectiveDemoMode]);
  /* Transient drag state for the launch pad. */
  const [draggedBatchId, setDraggedBatchId] = useState(null);
  const [dragOverZone, setDragOverZone] = useState(null); // "next" | "batches" | null
  const [selectedCompletedId, setSelectedCompletedId] = useStickyState("selectedCompletedId", null);

  /* Sub-tab within each Production section — "main" shows the section's
     own workspace (batch breakdown / packing list / route planner),
     "activity" shows the dashboard scoped to that section. Persisted,
     and bootstrapped from the legacy `pr:prodView=activity` signal so
     existing users don't lose their place after this refactor. */
  const [prodTab, setProdTab] = useState(() => {
    try {
      const legacy = typeof localStorage !== "undefined" && localStorage.getItem("pr:prodView");
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:prodTab");
      if (["main", "activity", "planning"].includes(raw)) return raw;
      if (legacy === "activity") return "activity";
      return "planning";
    } catch { return "planning"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:prodTab", prodTab); } catch { /* ignore */ }
  }, [prodTab]);
  // Plan vs. Roasting view inside the Roasting breakdown page. Persisted
  // so the operator's choice survives reloads.
  const [roastView, setRoastView] = useState(() => {
    try { return localStorage.getItem("pr:roastView") === "roasting" ? "roasting" : "plan"; }
    catch { return "plan"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:roastView", roastView); } catch { /* ignore */ }
  }, [roastView]);
  // Wholesale
  const [wholesaleTab, setWholesaleTab] = useStickyState("wholesaleTab", "store"); // "store" | "products" | "customers" | "import"
  const [wholesaleConfig, setWholesaleConfig] = useState(null);
  const [wholesaleProducts, setWholesaleProducts] = useState([]);
  const [wholesaleCustomers, setWholesaleCustomers] = useState([]);
  const [wholesaleLoading, setWholesaleLoading] = useState(false);
  const [wholesaleProductModal, setWholesaleProductModal] = useState(null); // null | "new" | product obj
  useEffect(() => {
    if (page !== "wholesale") return;
    setWholesaleLoading(true);
    Promise.all([
      fetch("/api/wholesale/config").then(r => r.ok ? r.json() : null),
      fetch("/api/wholesale/products").then(r => r.ok ? r.json() : null),
      fetch("/api/wholesale/customers").then(r => r.ok ? r.json() : null),
    ]).then(([cfg, prods, custs]) => {
      const loadedProducts = prods?.products || [];
      const loadedConfig = cfg?.config || {};
      setWholesaleConfig(loadedConfig);
      setWholesaleProducts(loadedProducts);
      setWholesaleCustomers(custs?.customers || []);
      // Auto-seed demo products on first visit when demo mode is on and store is empty
      if (effectiveDemoMode && loadedProducts.length === 0 && !loadedConfig.name) {
        seedWholesaleDemoData();
      }
    }).finally(() => setWholesaleLoading(false));
  }, [page, effectiveDemoMode]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── Billing state ─────────────────────────────────────────────────── */
  const [billingView, setBillingView] = useStickyState("billingView", "invoices"); // "invoices" | "pos" | "settings"
  const [purchasingView, setPurchasingView] = useState(() => {
    try { return localStorage.getItem("pr:purchasingView") || "pos"; } catch { return "pos"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:purchasingView", purchasingView); } catch { /* ignore */ }
  }, [purchasingView]);
  // Sell-side (Invoicing) lives in its own main tab now. Same
  // PurchasingPage component, separate persisted view so switching
  // between Finance (buy side) and Invoicing (sell side) doesn't fight
  // over one localStorage key.
  const [sellView, setSellView] = useState(() => {
    try { return localStorage.getItem("pr:sellView") || "clients"; } catch { return "clients"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:sellView", sellView); } catch { /* ignore */ }
  }, [sellView]);
  // Bridge: side-nav "New …" CTA lives in App.jsx so it can use the
  // shared PanelCTA, but the modals live in PurchasingPage. Bumping
  // this counter signals the page to open the matching new-modal.
  const [purchasingNewSignal, setPurchasingNewSignal] = useState(null);
  const triggerPurchasingNew = (kind) => setPurchasingNewSignal({ kind, n: Date.now() });
  const [billingInvoices, setBillingInvoices] = useState([]);
  const [billingPOs, setBillingPOs] = useState([]);
  const [billingSettings, setBillingSettings] = useState({});
  const [billingLoading2, setBillingLoading2] = useState(false);
  const [invoiceModal, setInvoiceModal] = useState(null); // null | "new" | invoice obj (edit)
  const [poModal, setPOModal] = useState(null); // null | "new" | po obj (edit)
  const [billingFilter, setBillingFilter] = useStickyState("billingFilter", "all"); // "all"|"draft"|"sent"|"overdue"|"paid"
  const [billingGroup, setBillingGroup] = useState("date"); // "date"|"client"
  const [billingSettingsModal, setBillingSettingsModal] = useState(false);

  const todayStr = new Date().toISOString().slice(0, 10);

  const invoiceStatusLabel = (s) => ({
    draft: "Draft", sent: "Sent", overdue: "Overdue", paid: "Paid", void: "Void",
  }[s] || s);
  const invoiceStatusColor = (s) => ({
    draft: { bg: "#F3F4F6", fg: "#6B7280" },
    sent: { bg: "#EFF6FF", fg: "#2563EB" },
    overdue: { bg: "#FEF2F2", fg: "#DC2626" },
    paid: { bg: "#F0FDF4", fg: "#16A34A" },
    void: { bg: "#F3F4F6", fg: "#9CA3AF" },
  }[s] || { bg: "#F3F4F6", fg: "#6B7280" });
  const poStatusColor = (s) => ({
    draft: { bg: "#F3F4F6", fg: "#6B7280" },
    sent: { bg: "#EFF6FF", fg: "#2563EB" },
    confirmed: { bg: "#FFF7ED", fg: "#C2410C" },
    received: { bg: "#F0FDF4", fg: "#16A34A" },
    cancelled: { bg: "#F3F4F6", fg: "#9CA3AF" },
  }[s] || { bg: "#F3F4F6", fg: "#6B7280" });

  // Compute overdue status client-side (same logic as server)
  const withOverdue = (inv) =>
    inv.status === "sent" && inv.dueDate && inv.dueDate < todayStr
      ? { ...inv, status: "overdue" }
      : inv;

  const loadBillingData = useCallback(() => {
    if (effectiveDemoMode) {
      setBillingInvoices(DEMO_INVOICES.map(withOverdue));
      setBillingPOs(DEMO_POS);
      setBillingSettings(DEMO_BILLING_SETTINGS);
      return;
    }
    setBillingLoading2(true);
    Promise.all([
      fetch("/api/billing/invoices").then(r => r.ok ? r.json() : { invoices: [] }),
      fetch("/api/billing/pos").then(r => r.ok ? r.json() : { pos: [] }),
      fetch("/api/billing/settings").then(r => r.ok ? r.json() : {}),
    ]).then(([invData, posData, settingsData]) => {
      setBillingInvoices((invData.invoices || []).map(withOverdue));
      setBillingPOs(posData.pos || []);
      setBillingSettings(settingsData || {});
    }).catch(() => {}).finally(() => setBillingLoading2(false));
  }, [effectiveDemoMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (page === "billing") loadBillingData();
    else if (page === "settings" && settingsSection === "finance") loadBillingData();
  }, [page, settingsSection, loadBillingData]);

  // Analytics sub-view: "profitability" (default) or "produced" (produced-by-product) — persisted.
  const [analyticsView, setAnalyticsView] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:analyticsView");
      if (["profitability", "produced"].includes(raw)) return raw;
      return "profitability";
    } catch { return "profitability"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:analyticsView", analyticsView); } catch { /* ignore */ }
  }, [analyticsView]);
  const [profitabilityWindow, setProfitabilityWindow] = useState(() => {
    try { return localStorage.getItem("pr:profitabilityWindow") || "all"; }
    catch { return "all"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:profitabilityWindow", profitabilityWindow); } catch { /* ignore */ }
  }, [profitabilityWindow]);
  const [forecastEnabled, setForecastEnabled] = useState(true);
  const [profitabilityOrders, setProfitabilityOrders] = useState(() => DEMO_PROFITABILITY_ORDERS);
  const [profitabilityLoading, setProfitabilityLoading] = useState(false);
  const [profitabilityError, setProfitabilityError] = useState("");

  // Profitability data fetch — server keeps a persistent snapshot of
  // Shopify history so refreshing the tab never re-hits Shopify. Pass
  // `force=true` to bypass the cache and pull a fresh snapshot.
  const [profitabilityFetchedAt, setProfitabilityFetchedAt] = useState(null);
  const fetchProfitabilityOrders = useCallback(async (force = false) => {
    if (effectiveDemoMode) return;
    if (!shopConnected) return;
    setProfitabilityLoading(true);
    setProfitabilityError("");
    try {
      const url = `/api/shopify/orders/history?shop=${encodeURIComponent(connectedShop)}&days=${profitabilityWindow}${force ? "&refresh=1" : ""}`;
      const r = await fetch(url);
      const raw = await r.text();
      let d; try { d = JSON.parse(raw); } catch { d = { error: raw }; }
      if (!r.ok) { setProfitabilityError(`Shopify returned ${r.status}: ${d.error || ""}`); return; }
      setProfitabilityOrders(d.orders || []);
      setProfitabilityFetchedAt(d.fetchedAt || null);
    } catch (err) {
      setProfitabilityError(`Network error: ${err.message}`);
    } finally { setProfitabilityLoading(false); }
  }, [shopConnected, connectedShop, profitabilityWindow, effectiveDemoMode]);
  useEffect(() => { fetchProfitabilityOrders(false); }, [fetchProfitabilityOrders]);

  // Check Shopify status on mount & after OAuth redirect
  useEffect(() => {
    fetch("/api/usage")
      .then(r => r.json())
      .then(data => { setUsage(data); setBillingLoading(false); })
      .catch(() => setBillingLoading(false));

    fetch("/api/shopify/status")
      .then(r => r.json())
      .then(data => { setShopifyStatus(data); setShopifyLoading(false); })
      .catch(() => setShopifyLoading(false));

    fetch("/api/sos/status")
      .then(r => r.json())
      .then(data => { setSosStatus(data); setSosLoading(false); })
      .catch(() => setSosLoading(false));

    fetch("/api/qbo/status")
      .then(r => r.json())
      .then(data => { setQboStatus(data); setQboLoading(false); })
      .catch(() => setQboLoading(false));

    fetch("/api/workspace/branding")
      .then(r => r.json())
      .then(data => setBranding(data || {}))
      .catch(() => {});

    // Check for OAuth redirect success
    const params = new URLSearchParams(window.location.search);
    if (params.get("shopify") === "connected") {
      setPage("settings");
      window.history.replaceState({}, "", "/app");
      // Re-fetch status
      fetch("/api/shopify/status").then(r => r.json()).then(setShopifyStatus);
    }
    if (params.get("sos") === "connected") {
      setPage("settings");
      window.history.replaceState({}, "", "/app");
      fetch("/api/sos/status").then(r => r.json()).then(setSosStatus);
    }
    if (params.get("qbo") === "connected") {
      setPage("settings");
      window.history.replaceState({}, "", "/app");
      fetch("/api/qbo/status").then(r => r.json()).then(setQboStatus);
    }
    if (params.get("billing") === "success") {
      setBillingSuccessBanner(true);
      window.history.replaceState({}, "", "/app");
      // Poll until webhook has updated the plan (webhook fires async after redirect)
      let attempts = 0;
      const poll = () => {
        fetch("/api/usage").then(r => r.json()).then(data => {
          setUsage(data);
          // Keep polling until plan is confirmed active (not still trialing with no sub)
          if (data.onTrial && !data.meta?.stripeSubscriptionId && attempts < 8) {
            attempts++;
            setTimeout(poll, 1500);
          }
        }).catch(() => {});
      };
      poll();
    }
  }, []);

  const disconnectSos = useCallback(async () => {
    try {
      await fetch("/api/sos/disconnect", { method: "POST" });
      const r = await fetch("/api/sos/status");
      setSosStatus(await r.json());
    } catch { /* ignore */ }
  }, []);

  const disconnectQbo = useCallback(async () => {
    try {
      await fetch("/api/qbo/disconnect", { method: "POST" });
      const r = await fetch("/api/qbo/status");
      setQboStatus(await r.json());
    } catch { /* ignore */ }
  }, []);

  // Fetch Shopify orders when connected
  // Pull wholesale-lite orders periodically. Cheap (one workspace
  // file read) so a 30s tick is fine. Also fires on mount.
  const fetchWholesaleOrders = useCallback(async () => {
    try {
      const r = await fetch("/api/wholesale/orders", { credentials: "same-origin" });
      if (!r.ok) return;
      const d = await r.json();
      setWholesaleOrders(d.orders || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    fetchWholesaleOrders();
    const t = setInterval(fetchWholesaleOrders, 30000);
    return () => clearInterval(t);
  }, [fetchWholesaleOrders]);

  const fetchShopifyOrders = useCallback(async (force = false) => {
    if (effectiveDemoMode) return;
    if (!shopConnected) return;
    setShopifyOrdersLoading(true);
    setShopifyOrdersError("");
    try {
      const r = await fetch(`/api/shopify/orders?shop=${encodeURIComponent(connectedShop)}${force ? "&refresh=1" : ""}`);
      const raw = await r.text();
      let data;
      try { data = JSON.parse(raw); } catch { data = { error: raw }; }

      if (!r.ok) {
        console.error("Shopify orders fetch failed", r.status, data);
        setShopifyOrdersError(`Shopify returned ${r.status}: ${data.error || raw.slice(0, 400)}`);
        setShopifyOrdersLoading(false);
        return;
      }

      const orderList = data.orders || [];
      console.log(`✅ Fetched ${orderList.length} orders from Shopify`, data);

      // Tagged state is persisted via the Shopify "production" tag —
      // that's the canonical source of truth across sessions, devices,
      // and refreshes. Map and update inside one setState callback so
      // the table doesn't flash between renders.
      const mapped = orderList.map(mapShopifyOrder);
      // Defensive: drop fully-fulfilled orders in case Shopify's
      // fulfillment_status=unshipped filter lags after a fulfillment.
      setShopifyOrders(mapped.filter(o => o.fulfillmentStatus !== "fulfilled"));
      setShopifyOrdersLoading(false);
    } catch (err) {
      console.error("Shopify orders fetch error", err);
      setShopifyOrdersError(`Network error: ${err.message}`);
      setShopifyOrdersLoading(false);
    }
  }, [shopConnected, connectedShop, effectiveDemoMode]);

  useEffect(() => { fetchShopifyOrders(); }, [fetchShopifyOrders]);

  // Connect to Shopify
  const connectShopify = () => {
    const raw = connectInput.trim();
    if (!raw) { setConnectError("Enter your store handle"); return; }

    // Normalize anything the user pastes into a canonical <handle>.myshopify.com
    let handle = raw.toLowerCase().replace(/^https?:\/\//, "").replace(/\/$/, "");

    // Case: pasted from admin URL (admin.shopify.com/store/<handle>/...)
    const adminMatch = handle.match(/admin\.shopify\.com\/store\/([^/?#]+)/);
    if (adminMatch) {
      handle = adminMatch[1];
    } else if (handle.endsWith(".myshopify.com")) {
      // Case: full myshopify URL — strip path, keep just domain
      handle = handle.split("/")[0].replace(".myshopify.com", "");
    } else if (handle.includes(".")) {
      // Case: custom domain — not supported, OAuth requires myshopify handle
      setConnectError("Use your Shopify store handle, not a custom domain. Find it in Shopify admin → Settings → Domains.");
      return;
    }

    // Strip any remaining slashes/invalid chars
    handle = handle.replace(/[^a-z0-9-]/g, "");
    if (!handle) { setConnectError("That doesn't look like a valid store handle"); return; }

    const shop = `${handle}.myshopify.com`;
    window.location.href = `/api/shopify/auth?shop=${encodeURIComponent(shop)}`;
  };

  // Disconnect from Shopify
  const disconnectShopify = (shop) => {
    // Default to the first connected store for backwards compat when no
    // explicit shop is passed (legacy single-store call sites).
    const target = shop || connectedShop;
    if (!target) return;
    // Imported customers + their order snapshots stay on the workspace
    // by default — that's the whole point of the import. We surface the
    // choice up front so the operator can opt in to a hard wipe if they
    // really want a clean slate.
    const keepData = window.confirm(
      `Disconnect ${target} from Roaster?\n\n` +
      `OK = keep all imported customers, orders, and history (recommended). The Shopify link is removed but every customer profile stays intact and you can keep marking orders paid in Roaster.\n\n` +
      `Cancel = stop disconnect.\n\n` +
      `(Hold Shift while clicking Disconnect to also delete imported customer data — this is permanent.)`
    );
    if (!keepData) return;
    fetch("/api/shopify/disconnect", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ shop: target }),
    }).then(() => {
      setShopifyStatus(prev => ({ ...prev, stores: (prev.stores || []).filter(s => s.shop !== target) }));
      // Wipe live order cache (the persisted shopifyUnpaidOrders on
      // each client record stays — that's the snapshot the customer
      // detail page falls back to when Shopify isn't connected).
      if (target === connectedShop) setShopifyOrders([]);
    });
  };

  /* ─── Demo mode swap ──────────────────────────────────────────
     When toggling ON, snapshot the live Shopify/cupping/lot/produced
     state into a ref and replace it with deep-cloned demo data so the
     user can schedule, mark ready, approve cuppings, drag routes —
     every click mutates the in-memory demo copy, not their real data.
     When toggling OFF, restore the snapshot.
     ──────────────────────────────────────────────────────────── */
  const clone = (x) => JSON.parse(JSON.stringify(x));
  /* Seed wholesale demo data into the server — wipes existing products,
     creates DEMO_WHOLESALE_PRODUCTS with variants + photos, sets store config. */
  const seedWholesaleDemoData = async () => {
    // Helper: fetch a remote image URL and return a base64 data URL
    const urlToDataUrl = async (url) => {
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    };

    try {
      // 1. Wipe existing products
      const existing = await fetch("/api/wholesale/products").then(r => r.ok ? r.json() : { products: [] });
      await Promise.all((existing.products || []).map(p =>
        fetch(`/api/wholesale/products/${p.id}`, { method: "DELETE" })
      ));
      // 2. Save store config
      await fetch("/api/wholesale/config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(DEMO_WHOLESALE_CONFIG),
      });
      // 3. Create products + variants + photos sequentially
      const created = [];
      for (const pd of DEMO_WHOLESALE_PRODUCTS) {
        const { variants: vDefs, photoUrl, ...fields } = pd;
        const pr = await fetch("/api/wholesale/products", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fields),
        });
        if (!pr.ok) continue;
        const { product } = await pr.json();

        // Upload product photo
        if (photoUrl) {
          try {
            const dataUrl = await urlToDataUrl(photoUrl);
            await fetch(`/api/wholesale/products/${product.id}/photos`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ photoData: dataUrl }),
            });
          } catch (e) {
            console.warn(`Photo upload skipped for "${pd.name}":`, e.message);
          }
        }

        // Create variants
        for (const v of (vDefs || [])) {
          await fetch(`/api/wholesale/products/${product.id}/variants`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(v),
          });
        }
      }
      // 4. Seed customers (wipe first, then recreate)
      const existingCusts = await fetch("/api/wholesale/customers").then(r => r.ok ? r.json() : { customers: [] });
      await Promise.all((existingCusts.customers || []).map(c =>
        fetch(`/api/wholesale/customers/${c.id}`, { method: "DELETE" })
      ));
      for (const cd of DEMO_WHOLESALE_CUSTOMERS) {
        await fetch("/api/wholesale/customers", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(cd),
        });
      }
      // 5. Fetch final state and update React state
      const [refreshedProds, refreshedCusts] = await Promise.all([
        fetch("/api/wholesale/products").then(r => r.ok ? r.json() : null),
        fetch("/api/wholesale/customers").then(r => r.ok ? r.json() : null),
      ]);
      setWholesaleConfig(DEMO_WHOLESALE_CONFIG);
      setWholesaleProducts(refreshedProds?.products || []);
      setWholesaleCustomers(refreshedCusts?.customers || []);
    } catch (e) {
      console.warn("seedWholesaleDemoData failed:", e);
    }
  };

  const resetDemoData = () => {
    setShopifyOrders(clone(DEMO_SHOPIFY_ORDERS));
    setProducedOrders([]);
    setCuppings(clone(DEMO_CUPPINGS));
    setGreenLots(clone(DEMO_LOTS));
    setProfitabilityOrders(DEMO_PROFITABILITY_ORDERS);
    setRoastingQueue({ nextId: null, currentId: null, currentRoastId: null, completed: [], batchNumbers: {}, nextBatchNum: 1 });
    setBatchPlanOrder([]);
    seedWholesaleDemoData();
    try {
      localStorage.removeItem("pr:roastingQueue");
      localStorage.removeItem("pr:batchPlanOrder");
      localStorage.removeItem("pr:producedOrders");
      localStorage.removeItem("pr:skippedOrders");
    } catch { /* ignore */ }
  };

  const toggleDemoMode = () => {
    // If no Shopify is connected, demo is the only meaningful state — flipping
    // the pref won't turn demo "off" (effectiveDemoMode stays true via the
    // shopConnected branch) so we avoid wiping shopifyOrders into a snapshot
    // that was never populated. Just update the pref silently.
    if (!shopConnected) {
      setDemoMode(d => !d);
      return;
    }
    if (demoMode) {
      // Restore real state from snapshot.
      const s = realDataSnapshotRef.current;
      if (s) {
        setShopifyOrders(s.shopifyOrders || []);
        setProducedOrders(s.producedOrders || []);
        setCuppings(s.cuppings || []);
        setGreenLots(s.greenLots || []);
        setProfitabilityOrders(s.profitabilityOrders || []);
      }
      realDataSnapshotRef.current = null;
      setDemoMode(false);
    } else {
      // Snapshot the real state, then seed demo values.
      realDataSnapshotRef.current = {
        shopifyOrders,
        producedOrders,
        cuppings,
        greenLots,
        profitabilityOrders,
      };
      setShopifyOrders(clone(DEMO_SHOPIFY_ORDERS));
      setProducedOrders(clone(DEMO_PRODUCED_ORDERS));
      setCuppings(clone(DEMO_CUPPINGS));
      setGreenLots(clone(DEMO_LOTS));
      setProfitabilityOrders(DEMO_PROFITABILITY_ORDERS);
      setDemoMode(true);
      seedWholesaleDemoData();
    }
  };

  // Tag/untag order on Shopify — optimistic update, fire-and-forget.
  // Flips local state immediately so the UI feels instant; reverts if the API call fails.
  const toggleShopifyTag = async (order) => {
    const action = order.tagged ? "remove" : "add";
    const wasTagged = order.tagged;
    // Optimistic flip
    setShopifyOrders(prev => prev.map(o =>
      o.shopifyId === order.shopifyId ? { ...o, tagged: !wasTagged } : o
    ));
    try {
      const r = await fetch(`/api/shopify/orders/${order.shopifyId}/tags`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shop: connectedShop, tags: "production", action }),
      });
      if (!r.ok) throw new Error(await r.text());
    } catch (err) {
      console.error("Tag update failed, reverting:", err);
      // Revert on failure
      setShopifyOrders(prev => prev.map(o =>
        o.shopifyId === order.shopifyId ? { ...o, tagged: wasTagged } : o
      ));
    }
  };

  // Active orders source:
  //   - shopConnected → real Shopify orders
  //   - effectiveDemoMode → seeded demo dataset
  //   - neither → empty array (no mock fallback; user explicitly opted out of demo)
  // Adapt a wholesale-lite order to the same shape the Orders pipeline
  // expects (id, customer, date, items, total, channel, etc.) so it can
  // sit in the same list as Shopify orders without special-casing.
  const wholesaleAsOrders = useMemo(() => {
    return (wholesaleOrders || [])
      .filter(o => o.status !== "rejected")
      .map(o => ({
        // Use the human-readable order number ("#WL-0001") as the
        // surface identifier so the Orders table matches the Shopify
        // pattern ("#5182"). The internal storage id is kept on a
        // separate field for any code that still needs to address the
        // canonical record.
        id: o.number ? `#${o.number}` : o.id,
        internalId: o.id,
        shopifyId: null,
        kind: "wholesale_lite",
        channel: "wholesale_lite",
        number: o.number,
        customer: o.customerCompany || o.customerName || o.customerEmail,
        customerEmail: o.customerEmail,
        // Same short date format Shopify orders use ("May 14") so the
        // table column stays consistent across channels.
        date: o.createdAt
          ? new Date(o.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })
          : null,
        items: (o.lineItems || []).map(l => ({
          productId: l.productId,
          name: l.productName,
          variant: l.variantName,
          sku: l.sku,
          qty: l.qty,
          unitPrice: l.unitPrice,
        })),
        total: o.total,
        outstanding: o.total,
        unpaid: o.status !== "paid",
        overdue: false,
        financialStatus: o.status === "paid" ? "paid" : "pending",
        fulfillmentStatus: "unfulfilled",
        fulfilledAt: null,
        tagged: o.status === "approved",
        shippingAddress: o.shippingAddress || null,
      }));
  }, [wholesaleOrders]);

  const activeOrders = shopConnected
    ? [...shopifyOrders, ...wholesaleAsOrders]
    : effectiveDemoMode ? shopifyOrders : wholesaleAsOrders;
  // visibleOrders applies the excluded-products filter: drops line items whose
  // productId is excluded, and then drops orders that have no items left.
  // Everything that feeds lists, counts, or the production plan reads from this.
  const visibleOrders = useMemo(() => {
    // Produced and skipped orders are tracked separately — drop them from the
    // active workflow views. Analytics reads producedOrders directly; the
    // Skipped tab reads skippedListForDisplay instead of visibleOrders.
    let os = activeOrders;
    if (producedIds.size > 0) os = os.filter(o => !producedIds.has(o.shopifyId || o.id));
    if (skippedIds.size > 0) os = os.filter(o => !skippedIds.has(o.shopifyId || o.id));
    if (excludedIds.size === 0) return os;
    return os
      .map(o => ({ ...o, items: o.items.filter(i => !excludedIds.has(i.productId)) }))
      .filter(o => o.items.length > 0);
  }, [activeOrders, excludedIds, producedIds, skippedIds]);
  // Skipped view — pulls from the live orders list whenever one matches a
  // skipped id. If the order has left Shopify we fall back to the snapshot we
  // kept in skippedOrders at skip time.
  const skippedListForDisplay = useMemo(() => {
    return skippedOrders.map(s => {
      const key = s.shopifyId || s.id;
      const live = activeOrders.find(o => (o.shopifyId || o.id) === key);
      return live || s;
    });
  }, [skippedOrders, activeOrders]);
  // Date filter for the Produced tab. Windows are calculated from "now" when
  // the filter is applied so "today" follows the rolling clock, not a fixed
  // calendar date cached at session start.
  const [producedDateFilter, setProducedDateFilter] = useState(() => {
    try { return localStorage.getItem("pr:producedDateFilter") || "all"; }
    catch { return "all"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:producedDateFilter", producedDateFilter); } catch { /* ignore */ }
  }, [producedDateFilter]);
  // Production → Activity uses its own date window so navigating between
  // Ready (Produced) and Activity doesn't fight over the same filter.
  const [activityDateFilter, setActivityDateFilter] = useState(() => {
    try { return localStorage.getItem("pr:activityDateFilter") || "90d"; }
    catch { return "30d"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:activityDateFilter", activityDateFilter); } catch { /* ignore */ }
  }, [activityDateFilter]);
  const [expandedActivityDays, setExpandedActivityDays] = useState(() => new Set());
  // Produced view — newest first, each entry carries its producedAt so the UI
  // can show "Produced on X" and let the user undo. Filtered by date window.
  // Snapshot may have been taken before we started capturing shippingAddress;
  // hydrate it from the live Shopify order list when possible so the route
  // page can place the stop on the map instead of leaving it at the depot.
  const producedListForDisplay = useMemo(() => {
    const liveByKey = new Map(
      (shopifyOrders || []).map(o => [String(o.shopifyId || o.id), o])
    );
    const hydrate = (o) => {
      const has = o.shippingAddress && (o.shippingAddress.address1 || o.shippingAddress.latitude != null);
      if (has) return o;
      const live = liveByKey.get(String(o.shopifyId || o.id));
      if (live && live.shippingAddress) {
        return { ...o, shippingAddress: live.shippingAddress };
      }
      return o;
    };
    const sorted = [...producedOrders]
      .sort((a, b) => (b.producedAt || 0) - (a.producedAt || 0))
      .map(hydrate);
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const cutoffs = {
      today: now - dayMs,
      "7": now - 7 * dayMs,
      "15": now - 15 * dayMs,
      "30": now - 30 * dayMs,
      year: now - 365 * dayMs,
    };
    const cutoff = cutoffs[producedDateFilter];
    if (!cutoff) return sorted;
    return sorted.filter(o => (o.producedAt || 0) >= cutoff);
  }, [producedOrders, shopifyOrders, producedDateFilter]);

  const toggle = id => {
    const order = (shopConnected || effectiveDemoMode)
      ? shopifyOrders.find(o => o.id === id)
      : null;
    // If we're tagging an order (not untagging) and the user has a
    // specific production day active in the side-nav, drop the order
    // straight into that day. Otherwise the order picks up the
    // default day via dayIdForOrder().
    const wasTagged = order ? order.tagged : false;
    if (!wasTagged && activeDayId) {
      const key = (order ? (order.shopifyId || order.id) : id);
      setOrderDay(key, activeDayId);
    }
    if (shopConnected && !effectiveDemoMode) {
      if (order) toggleShopifyTag(order);
    } else if (effectiveDemoMode) {
      // Demo mode seeds into shopifyOrders; flip the tag locally.
      setShopifyOrders(prev => prev.map(o => o.id === id ? { ...o, tagged: !o.tagged, tags: o.tagged ? "" : "production" } : o));
    } else {
      setOrders(prev => prev.map(o => o.id === id ? { ...o, tagged: !o.tagged } : o));
    }
  };
  const tagged = visibleOrders.filter(o => o.tagged);
  const untagged = visibleOrders.filter(o => !o.tagged);

  /* ─── Production days ─────────────────────────────────────────────
     Named buckets that scheduled orders can be assigned to (one
     order = one day). The Roast Planning page renders one section
     per day; every other planning surface (preview, sub-nav stats)
     also re-scopes to the active day. Always at least one day —
     deleting the last is blocked. Persisted to localStorage. */
  const [productionDays, setProductionDays] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:productionDays");
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    } catch { /* ignore */ }
    return [{ id: "day-1", label: "Day 1", date: null, createdAt: Date.now() }];
  });
  useEffect(() => {
    try { localStorage.setItem("pr:productionDays", JSON.stringify(productionDays)); }
    catch { /* ignore */ }
  }, [productionDays]);
  // orderKey → dayId. Orders not present here fall back to the first day.
  const [orderDayMap, setOrderDayMap] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:orderDayMap");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:orderDayMap", JSON.stringify(orderDayMap)); }
    catch { /* ignore */ }
  }, [orderDayMap]);
  // Active day for the Planning page + the Orders preview. "" = "All days".
  const [activeDayId, setActiveDayId] = useState(() => {
    try { return typeof localStorage !== "undefined" ? (localStorage.getItem("pr:activeDayId") || "") : ""; }
    catch { return ""; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:activeDayId", activeDayId); }
    catch { /* ignore */ }
  }, [activeDayId]);

  // Day-id resolver: orders without an explicit assignment fall back
  // to the first day so the user never has to "set" anything to see
  // a working plan on Day 1.
  const dayIdForOrder = useCallback((order) => {
    if (!order) return null;
    const key = String(order.shopifyId || order.id);
    const explicit = orderDayMap[key];
    if (explicit && productionDays.some(d => d.id === explicit)) return explicit;
    return productionDays[0]?.id || null;
  }, [orderDayMap, productionDays]);

  // Routes-only feed. Only orders that are:
  //   1. Still UNFULFILLED in Shopify (present in shopifyOrders, which
  //      is server-filtered to fulfillment_status=unshipped),
  //   2. Not already shipped via the in-app ship flow, and
  //   3. (When a production day is active) assigned to that day so the
  //      shift lead can route one day's deliveries at a time.
  // Declared below `activeDayId` and `dayIdForOrder` (referenced in
  // the filter) — moving it above triggers a TDZ "Cannot access … before
  // initialization" crash at first render.
  const routeReadyOrders = useMemo(() => {
    // Union of (orders scheduled to a production day) + (orders marked
    // produced/ready to ship). Deduped by key. The shift lead wants to
    // see everything assigned to a day, not just the roasted bucket.
    const merged = new Map();
    for (const o of (tagged || [])) {
      const key = String(o.shopifyId || o.id);
      if (!merged.has(key)) merged.set(key, o);
    }
    for (const o of (producedListForDisplay || [])) {
      const key = String(o.shopifyId || o.id);
      if (!merged.has(key)) merged.set(key, o);
    }
    const unfulfilledKeys = new Set(
      (shopifyOrders || []).map(o => String(o.shopifyId || o.id))
    );
    return Array.from(merged.values()).filter(o => {
      const key = o.shopifyId || o.id;
      // Must still be unfulfilled in Shopify (when we have a snapshot)
      // and not already shipped via the in-app ship flow.
      if (unfulfilledKeys.size > 0 && !unfulfilledKeys.has(String(key))) return false;
      if (shipmentByOrderId.get(key)) return false;
      if (activeDayId) {
        const dayId = dayIdForOrder(o);
        if (dayId !== activeDayId) return false;
      }
      return true;
    });
  }, [tagged, producedListForDisplay, shopifyOrders, shipmentByOrderId, activeDayId, dayIdForOrder]);

  /* ─── Cupping sources ─────────────────────────────────────────────
     Reusable list of where coffees come from (importer, farm, broker,
     etc.) — places this roastery buys their green from. Used by the
     "Add coffee" form's Sourced-from dropdown so the user doesn't
     retype a partner's name on every coffee. Managed via a full-page
     list in Cupping & QC → Sources. Persisted to localStorage. */
  const [cuppingSources, setCuppingSources] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:cuppingSources");
      // First-run: seed from the demo set so the Add-coffee form has
      // realistic options out of the box.
      if (raw == null) return JSON.parse(JSON.stringify(DEMO_CUPPING_SOURCES));
      const parsed = JSON.parse(raw);
      let i = 0;
      return parsed.map(c => c && c.id
        ? c
        : { ...c, id: `src-${Date.now().toString(36)}-${i++}` });
    } catch { return JSON.parse(JSON.stringify(DEMO_CUPPING_SOURCES)); }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:cuppingSources", JSON.stringify(cuppingSources)); }
    catch { /* ignore */ }
  }, [cuppingSources]);
  const upsertCuppingSource = (data) => {
    const name = (data?.name || "").trim();
    if (!name) return null;
    if (data?.id) {
      setCuppingSources(s => s.map(x => x.id === data.id ? { ...x, ...data, name } : x));
      return data.id;
    }
    // Same id-stomping fix as upsertCupper.
    const { id: _drop, ...rest } = data || {};
    const id = `src-${Date.now().toString(36)}`;
    setCuppingSources(s => [...s, { ...rest, name, id, createdAt: Date.now() }]);
    return id;
  };
  const removeCuppingSource = (id) => {
    setCuppingSources(s => s.filter(x => x.id !== id));
  };
  // Cupping main view: "sessions" (default), "sources", "cuppers", or "coffees".
  const [cuppingView, setCuppingView] = useStickyState("cuppingView", "sessions");
  // Status filter for the Coffees aggregate page.
  const [coffeeListFilter, setCoffeeListFilter] = useStickyState("coffeeListFilter", "all"); // all | approved | rejected | pending
  // null = no modal; "new" = adding; otherwise an existing source / cupper.
  const [editingSource, setEditingSource] = useState(null);
  const [editingCupper, setEditingCupper] = useState(null);

  /* ─── Cuppers ─────────────────────────────────────────────────────
     Named list of people who participate in cupping sessions. Used to
     attribute per-cupper scoring records on a coffee so you can tab
     through each cupper's results inside a session. */
  const [cuppers, setCuppers] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:cuppers");
      // First-run: seed from the demo cupper roster.
      if (raw == null) return JSON.parse(JSON.stringify(DEMO_CUPPERS));
      const parsed = JSON.parse(raw);
      let i = 0;
      return parsed.map(c => c && c.id
        ? c
        : { ...c, id: `cup-${Date.now().toString(36)}-${i++}` });
    } catch { return JSON.parse(JSON.stringify(DEMO_CUPPERS)); }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:cuppers", JSON.stringify(cuppers)); }
    catch { /* ignore */ }
  }, [cuppers]);
  // Auto-import any cupper that shows up in a session's records but
  // isn't on the roster yet — this happens when sessions arrive from
  // the server, when records are seeded from demo data, or when a
  // guest cupper accepts an invite. Without this, the cupper-tab UI
  // and the "+ Cupper" dropdown disagree about who actually exists.
  useEffect(() => {
    if (!Array.isArray(cuppings) || cuppings.length === 0) return;
    const known = new Set(cuppers.map(x => x.id));
    const found = new Map(); // cupperId -> { name }
    for (const session of cuppings) {
      for (const co of (session.coffees || [])) {
        for (const rec of (co.cuppings || [])) {
          if (!rec?.cupperId || known.has(rec.cupperId) || found.has(rec.cupperId)) continue;
          const name = (rec.cupperName || "").trim();
          if (name) found.set(rec.cupperId, { name });
        }
      }
    }
    if (found.size === 0) return;
    setCuppers(prev => {
      const prevIds = new Set(prev.map(x => x.id));
      const additions = [];
      for (const [id, { name }] of found.entries()) {
        if (!prevIds.has(id)) additions.push({ id, name, email: "", createdAt: Date.now() });
      }
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
  }, [cuppings]); // eslint-disable-line react-hooks/exhaustive-deps
  const upsertCupper = (data) => {
    const name = (data?.name || "").trim();
    if (!name) return null;
    if (data?.id) {
      setCuppers(s => s.map(x => x.id === data.id ? { ...x, ...data, name } : x));
      return data.id;
    }
    // Drop a possibly-null id from the form before spreading so we
    // don't blow away the generated one and end up creating a record
    // that's impossible to update (every save would create a fresh
    // duplicate). Same fix applied to upsertCuppingSource.
    const { id: _drop, ...rest } = data || {};
    const id = `cup-${Date.now().toString(36)}`;
    setCuppers(s => [...s, { ...rest, name, id, createdAt: Date.now() }]);
    return id;
  };
  const removeCupper = (id) => {
    setCuppers(s => s.filter(x => x.id !== id));
  };

  const addProductionDay = (label) => {
    const id = `day-${Date.now().toString(36)}`;
    const next = label || `Day ${productionDays.length + 1}`;
    setProductionDays(p => [...p, { id, label: next, date: null, createdAt: Date.now() }]);
    setActiveDayId(id);
    return id;
  };
  const renameProductionDay = (id, label) => {
    setProductionDays(p => p.map(d => d.id === id ? { ...d, label: label || d.label } : d));
  };
  // Cancelling a day unschedules every order in it: we walk the
  // tagged list, find the ones assigned to this day, fire the batch
  // unschedule (which strips the production tag on Shopify and shows
  // progress in the toolbar), then drop the day from the map. Orders
  // are NOT moved to another day — they go back to untagged so the
  // operator can re-schedule them deliberately.
  /* A day is locked once any roast has been completed against an
     order in it. We use the completed entry's stamped productId to
     map back; in-progress (currentId / nextId) roasts aren't checked
     here because mapping batch→day requires planRows which only
     exists inside the production render. The completed signal is
     enough in practice — the user marks each batch as it finishes. */
  const dayHasRoastingStarted = useCallback((dayId) => {
    if (!dayId) return false;
    const ordersInDay = tagged.filter(o => dayIdForOrder(o) === dayId);
    if (ordersInDay.length === 0) return false;
    const productIdsInDay = new Set(
      ordersInDay.flatMap(o => (o.items || []).map(i => i.productId).filter(Boolean))
    );
    if (productIdsInDay.size === 0) return false;
    const completed = roastingQueue.completed || [];
    return completed.some(c => productIdsInDay.has(c.productId));
  }, [tagged, dayIdForOrder, roastingQueue]);

  const removeProductionDay = async (id) => {
    if (productionDays.length <= 1) return; // never delete the last day
    if (dayHasRoastingStarted(id)) {
      alert("This day is locked — at least one roast has started against its orders. Finish those roasts (or unmark them) before deleting the day.");
      return;
    }
    const ordersInDay = tagged.filter(o => dayIdForOrder(o) === id);

    if (ordersInDay.length > 0 && shopConnected && !effectiveDemoMode) {
      setShopifyOrders(prev => prev.map(o =>
        ordersInDay.find(t => t.shopifyId === o.shopifyId) ? { ...o, tagged: false } : o
      ));
      setTagJobProgress({ action: "remove", done: 0, total: ordersInDay.length });
      try {
        await tagsBatchInChunks({
          orderIds: ordersInDay.map(o => o.shopifyId),
          action: "remove",
          onProgress: ({ done, total }) =>
            setTagJobProgress(p => p ? { ...p, done, total } : p),
        });
        try { await fetchShopifyOrders(true); } catch { /* ignore */ }
      } finally {
        setTagJobProgress(null);
      }
    } else if (ordersInDay.length > 0 && effectiveDemoMode) {
      setShopifyOrders(prev => prev.map(o =>
        ordersInDay.find(t => t.shopifyId === o.shopifyId) ? { ...o, tagged: false, tags: "" } : o
      ));
    }

    // Clear the day mapping for those orders, then drop the day.
    setOrderDayMap(m => {
      const next = { ...m };
      for (const k of Object.keys(next)) if (next[k] === id) delete next[k];
      return next;
    });
    setProductionDays(p => p.filter(d => d.id !== id));
    if (activeDayId === id) setActiveDayId("");
  };
  const setOrderDay = (orderKey, dayId) => {
    setOrderDayMap(m => ({ ...m, [String(orderKey)]: dayId }));
  };

  // Tagged orders filtered to the active day (when one is picked).
  // "" = All days; everything tagged is included.
  const taggedForActiveDay = useMemo(() => {
    if (!activeDayId) return tagged;
    return tagged.filter(o => dayIdForOrder(o) === activeDayId);
  }, [tagged, activeDayId, dayIdForOrder]);
  // The selected order for the detail modal — read from activeOrders so that
  // excluded items are still shown in the modal (with a toggle to re-include).
  const selectedOrder = useMemo(
    () => selectedOrderId ? activeOrders.find(o => o.id === selectedOrderId) : null,
    [selectedOrderId, activeOrders]
  );

  const navItems = (() => {
    /* Build the full nav, then drop anything this role isn't allowed to
       see. Separators are collapsed away when they'd bookend nothing, so
       a packer doesn't see empty dividers. */
    /* Top-level nav, three rail groups separated by dividers:
         1. Lab & Coffees  — sensory + catalog
         2. Inventory      — green stock
         3. Orders → Roasting → Packing → Routes — production flow
       Separators are collapsed away when they'd bookend nothing
       (a packer doesn't see empty dividers). */
    const raw = [
      { id: "cupping",   icon: Droplets,    capability: "page:cupping",   feature: "cupping",         module: "lab",        title: "Cupping & QC" },
      { id: "products",  icon: Coffee,       capability: "page:products",  feature: "coffees",          module: "production", title: "Coffees & Blends" },
      { type: "sep" },
      { id: "purchasing", icon: ClipboardList, capability: "page:purchasing",                            module: "finance",    title: "Purchasing" },
      { id: "inventory", icon: Warehouse,    capability: "page:inventory", feature: "green_inventory",  module: "inventory",  title: "Inventory" },
      { type: "sep" },
      { id: "orders",    icon: ScrollText,   capability: "page:orders",                                 module: "production", title: "Production Planning" },
      { id: "roasting",  icon: Flame,        capability: "prod:breakdown", page: "production", prodView: "breakdown", module: "production", title: "Roasting" },
      { id: "packing",   icon: Package,      capability: "prod:packing",   page: "production", prodView: "packing",   module: "production", title: "Packing" },
      { id: "routes",    icon: Truck,        capability: "prod:breakdown", page: "production", prodView: "routes",    feature: "routes", module: "production", title: "Ship" },
      { type: "sep" },
      { id: "analytics", icon: BarChart3,    capability: "page:analytics", feature: "analytics",        module: "analytics",  title: "Analytics" },
      { id: "wholesale", icon: ShoppingBag,  capability: "page:wholesale", addon: "wholesale_lite", showInDemo: true, title: "Wholesale" },
      { id: "sell",      icon: FileText,     capability: "page:purchasing",                            module: "finance",    title: "Invoicing" },
      /* Help → opens Elion Discuss in a new tab. Cyan accent. */
      { id: "help", icon: HelpCircle, pinBottom: true, external: "https://discuss.elionlabs.org/?tab=roaster", accentColor: "#24D3EF", hoverBg: "#0891B2", title: "Help — open Elion Discuss" },
    ];
    // Plan-feature helpers — safe even before usage loads (show all while loading)
    const planFeatures = effectiveUsage?.features || null;
    const ownedModules = effectiveUsage?.modules || null;
    const hasFeat = (f) => !planFeatures || planFeatures.includes("*") || planFeatures.includes(f);
    const hasAddonFn = (k) => effectiveUsage?.addons?.[k]?.status === "active";
    const hasModule = (m) => !ownedModules || ownedModules.includes(m);

    // Modular paywall: items the user's *role* can't access stay hidden
    // (a packer should never see Settings → Team). Items they could
    // access but haven't paid for stay visible with a lock icon and
    // route to the upgrade wall on click.
    const allowed = raw.filter(n => {
      if (n.type === "sep") return true;
      if (!cap(n.capability)) return false;
      if (effectiveDemoMode && n.hideInDemo) return false;
      return true;
    }).map(n => {
      if (n.type === "sep" || n.external || n.pinBottom) return n;
      const demoBypass = effectiveDemoMode && n.showInDemo;
      if (demoBypass) return n;
      // Locked = role allows it, but the workspace doesn't own the module
      // / addon / feature backing it.
      let locked = false;
      if (n.module && !hasModule(n.module)) locked = true;
      if (n.addon  && !hasAddonFn(n.addon)) locked = true;
      if (n.feature && !hasFeat(n.feature)) locked = true;
      return locked ? { ...n, locked: true } : n;
    });
    const out = [];
    for (let i = 0; i < allowed.length; i++) {
      const n = allowed[i];
      if (n.type === "sep") {
        const prev = out[out.length - 1];
        const next = allowed[i + 1];
        if (!prev || prev.type === "sep" || !next || next.type === "sep") continue;
      }
      out.push(n);
    }
    return out;
  })();

  const plan = useMemo(() => {
    // Per-unit roasted lbs from a line item. Tries the Shopify `grams`
    // field first (per Shopify's spec, this is single-unit weight), then
    // parses the variant title (e.g. "5lb / Whole Beans" → 5), then the
    // product name. Returns null if nothing parsed — the caller treats
    // qty as lbs in that case (back-compat with manual-entry orders that
    // never carried weight info).
    const variantLbs = (s) => {
      if (!s) return null;
      const lb = String(s).match(/(\d+(?:\.\d+)?)\s*lb/i);
      if (lb) return parseFloat(lb[1]);
      const oz = String(s).match(/(\d+(?:\.\d+)?)\s*oz/i);
      if (oz) return parseFloat(oz[1]) / 16;
      const g = String(s).match(/(\d+(?:\.\d+)?)\s*g\b/i);
      if (g) return parseFloat(g[1]) / 453.592;
      return null;
    };
    const lbsPerUnit = (i) => {
      if (i.grams && i.grams > 0) return i.grams / 453.592;
      return variantLbs(i.variant) ?? variantLbs(i.name);
    };
    const m = {};
    // Plan is scoped to the active day: when a day is selected, only
    // orders assigned to that day feed into the per-coffee batch math.
    // "All days" still totals across everything.
    taggedForActiveDay.forEach(o => o.items.forEach(i => {
      if (!m[i.productId]) {
        // Match the line-item productId against the local catalog; fall back to
        // the line item's own name when it's a Shopify product we don't have
        // in PRODUCTS_BASE (different id space). Without this fallback the
        // Product column renders blank for Shopify orders.
        const p = products.find(x => x.id === i.productId);
        m[i.productId] = { ...(p || {}), id: i.productId, name: p?.name || i.name, orderedLbs: 0, orderIds: [] };
      }
      // qty × per-unit weight = roasted lbs needed.
      // Old code added `qty` directly into orderedLbs, which silently
      // collapsed e.g. "125 × 5 lb bags = 625 lb" down to "125 lb" and
      // produced a 5× under-count of batches in the planner. If we can't
      // resolve a per-unit weight at all, fall back to qty-as-lbs so
      // legacy/manual orders still tally instead of disappearing.
      const perUnit = lbsPerUnit(i);
      m[i.productId].orderedLbs += perUnit ? i.qty * perUnit : i.qty;
      m[i.productId].orderIds.push(o.id);
    }));
    // Apply weight loss: customer ordered X lb roasted → need X / (1 - loss) lb green.
    // Look up per-product loss override first, else default. lossPct is stored as
    // a decimal (0.18 = 18%).
    return Object.values(m).map(p => {
      const loss = (typeof lossOverrides[p.id] === "number" ? lossOverrides[p.id] : defaultLossPct);
      const roastedLbs = p.orderedLbs;
      const greenLbs = Math.ceil(roastedLbs / (1 - loss));
      return {
        ...p,
        roastedLbs,            // what the customer ordered (finished weight)
        greenLbs,              // green needed to produce that (after loss)
        toRoast: greenLbs,     // alias kept for backwards-compat
        batches: Math.ceil(greenLbs / batchSizeLbs),
        // Standard green bag = 154 lb (was 130 — bug). Decimal so callers
        // can show "1.36 bags" if they want; floor-rounding belongs to
        // whoever cares about whole-bag granularity.
        greenBags: +(greenLbs / 154).toFixed(2),
        lossPct: loss,
      };
    });
  }, [taggedForActiveDay, lossOverrides, defaultLossPct, products, batchSizeLbs]);

  /* Expand the aggregated plan into individual batch records. Each
     batch gets a stable id `${productId}-b${index}` so its identity
     survives across renders and persisted-queue state. The last batch
     may be partial (smaller than batchSizeLbs) to use up the remainder. */
  const expandedBatches = useMemo(() => {
    const out = [];
    for (const p of (plan || [])) {
      const n = p.batches || 0;
      for (let i = 0; i < n; i++) {
        const isLast = i === n - 1;
        const fullSize = batchSizeLbs;
        const greenLbs = isLast
          ? Math.max(0, +(p.greenLbs - fullSize * (n - 1)).toFixed(1)) || fullSize
          : fullSize;
        const coffee = products.find(x => x.id === p.id) || {};
        const profile = PROFILES[p.id];
        /* Stock rolls up at the coffee level — if there's enough
           green for the whole plan we call every batch "ready";
           below 100% we downgrade. */
        const stock = p.greenStockLbs || 0;
        const stockStatus = stock >= p.greenLbs ? "ready"
          : stock > 0 ? "low"
          : "none";
        out.push({
          id: `${p.id}-b${i}`,
          productId: p.id,
          productName: p.name,
          batchIndex: i,
          totalBatches: n,
          greenLbs,
          roastedLbs: +(greenLbs * (1 - p.lossPct)).toFixed(1),
          lossPct: p.lossPct,
          sku: p.sku,
          roastLevel: coffee.roastLevel || null,
          origin: coffee.origin || null,
          chargeTemp: profile?.chargeTemp || null,
          dropTemp: profile?.dropTemp || null,
          totalTime: profile?.totalTime || null,
          fcTime: profile?.fcTime || null,
          stockStatus,
        });
      }
    }
    return out;
  }, [plan, products, batchSizeLbs]);

  /* Remaining = expanded minus whatever is currently staged in Next,
     roasting in Current, or already completed. Ordered first by
     batchPlanOrder (user drag order for coffees), then natural plan
     order within a coffee's batches. */
  const remainingBatches = useMemo(() => {
    const taken = new Set([
      roastingQueue.nextId,
      roastingQueue.currentId,
      ...(roastingQueue.completed || []).map(c => c.batchId),
    ].filter(Boolean));
    const list = expandedBatches.filter(b => !taken.has(b.id));
    /* Re-use the existing per-coffee order the user set via drag. */
    const coffeeOrderIdx = new Map(batchPlanOrder.map((id, i) => [id, i]));
    return list.sort((a, b) => {
      const ia = coffeeOrderIdx.has(a.productId) ? coffeeOrderIdx.get(a.productId) : Infinity;
      const ib = coffeeOrderIdx.has(b.productId) ? coffeeOrderIdx.get(b.productId) : Infinity;
      if (ia !== ib) return ia - ib;
      if (a.productId !== b.productId) return 0;
      return a.batchIndex - b.batchIndex;
    });
  }, [expandedBatches, roastingQueue, batchPlanOrder]);

  const nextBatch = expandedBatches.find(b => b.id === roastingQueue.nextId) || null;
  const currentBatch = expandedBatches.find(b => b.id === roastingQueue.currentId) || null;

  /* Next is a planning slot, not a trigger. NOTHING fires a roast
     automatically — the operator has to click Roast Now on the live
     card, which picks up whatever's staged in Next. This callback
     moves Next → Current, captures the roastId, AND auto-advances
     the next batch from the queue into Next so the operator always
     sees what's coming after the current roast. */
  const startNextBatch = useCallback(async () => {
    const batch = expandedBatches.find(b => b.id === roastingQueue.nextId);
    const productName = batch
      ? `${batch.productName} · Batch ${batch.batchIndex + 1} of ${batch.totalBatches}`
      : (remainingBatches[0]?.productName || "House Blend");
    try {
      const r = await fetch("/api/demo/roast/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productName }),
      });
      if (!r.ok) return null;
      const d = await r.json();
      const autoNext = batch ? (remainingBatches[0] || null) : null;
      setRoastingQueue(q => {
        const newQ = {
          ...q,
          currentId: batch ? q.nextId : null,
          currentRoastId: d.roastId || null,
          nextId: batch ? (autoNext ? autoNext.id : null) : q.nextId,
        };
        if (autoNext && !newQ.batchNumbers?.[autoNext.id]) {
          const num = newQ.nextBatchNum || 1;
          newQ.nextBatchNum = num + 1;
          newQ.batchNumbers = { ...(newQ.batchNumbers || {}), [autoNext.id]: `B-${String(num).padStart(3, "0")}` };
        }
        return newQ;
      });
      return d.roastId || null;
    } catch { return null; }
  }, [roastingQueue.nextId, expandedBatches, remainingBatches]);

  /* Poll the server for the current roast's status. When the operator
     hits Save (status → "ended") we archive it into `completed` and
     clear currentId so the next batch auto-promotes. If the record
     404s (operator hit Cancel → DELETE /api/roasts/:id), we return the
     batch to the top of the remaining queue. */
  useEffect(() => {
    const roastId = roastingQueue.currentRoastId;
    if (!roastId || !roastingQueue.currentId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`/api/roasts/${encodeURIComponent(roastId)}`);
        if (cancelled) return;
        if (r.status === 404) {
          /* Roast was deleted → operator canceled. Send the batch back
             to the queue (drops currentId; remaining recomputes). */
          setRoastingQueue(q => ({ ...q, currentId: null, currentRoastId: null }));
          return;
        }
        if (!r.ok) return;
        const d = await r.json();
        const roast = d.roast;
        if (!roast) return;
        if (roast.status === "ended") {
          /* Archive the completed batch. Snapshot the samples + events
             so the detail modal can render the finished curve even if
             the record gets purged server-side later. */
          const batch = expandedBatches.find(b => b.id === roastingQueue.currentId);
          if (!batch) { setRoastingQueue(q => ({ ...q, currentId: null, currentRoastId: null })); return; }
          const lastSample = (roast.samples || [])[roast.samples.length - 1];
          const completedEntry = {
            batchId: batch.id,
            batchNum: roastingQueue.batchNumbers?.[batch.id] || null,
            roastId: roast.id,
            productName: batch.productName,
            batchIndex: batch.batchIndex,
            totalBatches: batch.totalBatches,
            sku: batch.sku,
            roastLevel: batch.roastLevel,
            startedAt: roast.startedAt,
            completedAt: roast.endedAt || Date.now(),
            finalBt: lastSample?.bt ?? null,
            events: roast.events || [],
            samples: roast.samples || [],
          };
          setRoastingQueue(q => ({
            ...q,
            currentId: null,
            currentRoastId: null,
            completed: [completedEntry, ...(q.completed || [])],
          }));
        }
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 2500);
    return () => { cancelled = true; clearInterval(t); };
  }, [roastingQueue.currentRoastId, roastingQueue.currentId, expandedBatches]);

  /* Manual queue actions wired to the drag handlers below. */
  const stageBatch = useCallback((batchId) => {
    setRoastingQueue(q => {
      if (q.batchNumbers?.[batchId]) return { ...q, nextId: batchId };
      const num = q.nextBatchNum || 1;
      return {
        ...q,
        nextId: batchId,
        nextBatchNum: num + 1,
        batchNumbers: { ...(q.batchNumbers || {}), [batchId]: `B-${String(num).padStart(3, "0")}` },
      };
    });
  }, []);
  const unstageBatch = useCallback(() => {
    setRoastingQueue(q => ({ ...q, nextId: null }));
  }, []);
  const clearCompletedToday = useCallback(() => {
    setRoastingQueue(q => ({ ...q, completed: [] }));
  }, []);

  // Live order-number search. When the query contains an order-number
  // token, ask Shopify for it across ALL statuses so paid/shipped
  // orders surface even though the bulk feed only loads unshipped.
  useEffect(() => {
    const q = orderSearch.trim();
    const numToken = q.replace(/^#/, "").match(/^[0-9]{2,}(-[a-z0-9]+)?$/i);
    if (!numToken || !shopConnected || effectiveDemoMode) {
      setLiveSearchOrders([]);
      setLiveSearchLoading(false);
      return;
    }
    let cancelled = false;
    setLiveSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`/api/shopify/orders/by-name?shop=${encodeURIComponent(connectedShop)}&q=${encodeURIComponent(q)}`);
        const d = r.ok ? await r.json() : { orders: [] };
        if (!cancelled) setLiveSearchOrders((d.orders || []).map(mapShopifyOrder));
      } catch {
        if (!cancelled) setLiveSearchOrders([]);
      } finally {
        if (!cancelled) setLiveSearchLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [orderSearch, shopConnected, connectedShop, effectiveDemoMode]);

  const filteredOrders = useMemo(() => {
    let base;
    if (orderFilter === "production") {
      // Scheduled view is the canonical place to assign orders to
      // production days, so we re-scope by activeDayId here too. "All
      // days" (activeDayId = "") shows everything tagged.
      base = activeDayId ? taggedForActiveDay : tagged;
    }
    else if (orderFilter === "skipped") base = skippedListForDisplay;
    else if (orderFilter === "produced") base = producedListForDisplay;
    else if (orderFilter === "all" && activeDayId) {
      // All filter + a specific day picked = "show me orders I can
      // still schedule into this day" (i.e. untagged).
      base = untagged;
    }
    else base = visibleOrders;
    // Apply channel filter (orderChannelFilter) before search so the
    // search only narrows what's already visible per channel.
    if (orderChannelFilter && orderChannelFilter !== "all") {
      base = base.filter(o => deriveOrderChannel(o) === orderChannelFilter);
    }
    const q = orderSearch.trim().toLowerCase();
    if (!q) return base;
    // Merge in live-fetched orders (paid/shipped included) that aren't
    // already in the local set, so a number search surfaces them too.
    if (liveSearchOrders.length > 0) {
      const haveIds = new Set(base.map(o => String(o.shopifyId || o.id)));
      const extra = liveSearchOrders.filter(o => !haveIds.has(String(o.shopifyId || o.id)));
      if (extra.length > 0) base = [...base, ...extra];
    }
    return base.filter(o => {
      if (String(o.id).toLowerCase().includes(q)) return true;
      if (String(o.customer || "").toLowerCase().includes(q)) return true;
      return (o.items || []).some(i =>
        String(i.name || "").toLowerCase().includes(q) ||
        String(i.sku || "").toLowerCase().includes(q)
      );
    });
  }, [visibleOrders, orderFilter, tagged, untagged, taggedForActiveDay, activeDayId, skippedListForDisplay, producedListForDisplay, orderSearch, orderChannelFilter, liveSearchOrders]);

  // Roast plan preview at the top of the Orders tab — collapsed by
  // default. Persisted so the user's open/closed preference survives
  // reloads.
  const [ordersPlanOpen, setOrdersPlanOpen] = useState(() => {
    try { return localStorage.getItem("pr:ordersPlanOpen") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("pr:ordersPlanOpen", ordersPlanOpen ? "1" : "0"); }
    catch { /* ignore */ }
  }, [ordersPlanOpen]);

  // Column sort for the Orders table. null sortKey = the natural order
  // we get from Shopify (newest first). Click a header to sort by that
  // column; click again to toggle direction; click a third time to
  // clear back to natural order. Persisted to localStorage so the
  // user's preference survives reloads.
  const [orderSort, setOrderSort] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" && localStorage.getItem("pr:orderSort");
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === "object" && "key" in parsed) return parsed;
    } catch { /* ignore */ }
    return { key: null, dir: "asc" };
  });
  useEffect(() => {
    try { localStorage.setItem("pr:orderSort", JSON.stringify(orderSort)); } catch { /* ignore */ }
  }, [orderSort]);

  const toggleOrderSort = (key) => {
    setOrderSort(curr => {
      if (curr.key !== key) return { key, dir: "asc" };
      if (curr.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: "asc" }; // 3rd click → clear
    });
  };

  const displayedOrders = useMemo(() => {
    if (!orderSort.key) return filteredOrders;
    const itemQty = (o) => (o.items || []).reduce((s, i) => s + (i.qty || 0), 0);
    const productLabel = (o) => {
      const its = o.items || [];
      if (its.length === 0) return "";
      return String(its[0].name || "").toLowerCase();
    };
    const dateMs = (o) => {
      // Prefer the raw Shopify timestamp; fall back to producedAt or 0.
      if (o.created_at) return new Date(o.created_at).getTime() || 0;
      if (o.producedAt) return o.producedAt;
      return 0;
    };
    const get = (o) => {
      switch (orderSort.key) {
        case "id":       return String(o.id || "").toLowerCase();
        case "customer": return String(o.customer || "").toLowerCase();
        case "product":  return productLabel(o);
        case "items":    return (o.items || []).length;
        case "qty":      return itemQty(o);
        case "date":     return dateMs(o);
        default:         return 0;
      }
    };
    const dir = orderSort.dir === "asc" ? 1 : -1;
    const sorted = [...filteredOrders].sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [filteredOrders, orderSort]);

  const filteredInventory = useMemo(() => {
    if (inventoryFilter === "low") return products.filter(p => p.greenStockLbs < 100);
    if (inventoryFilter === "moderate") return products.filter(p => p.greenStockLbs >= 100 && p.greenStockLbs < 250);
    if (inventoryFilter === "good") return products.filter(p => p.greenStockLbs >= 250);
    return products;
  }, [inventoryFilter, products]);

  const totalGreen = products.reduce((s, p) => s + p.greenStockLbs, 0);
  const totalRoasted = products.reduce((s, p) => s + p.roastedStockLbs, 0);
  const lowStock = products.filter(p => p.greenStockLbs < 100);

  // Packing list — aggregate tagged line items by product name + size + packing.
  // Variant format from Shopify looks like "5lb / Whole Beans" or "15lb Container / Ground - Drip".
  // We split on " / " to extract size and packing; everything else falls through empty.
  const packingList = useMemo(() => {
    const rows = new Map();
    tagged.forEach(o => (o.items || []).forEach(i => {
      const parts = (i.variant || "").split(" / ");
      const size = parts[0] || "";
      const packing = parts[1] || "";
      const key = `${i.name}||${size}||${packing}`;
      const curr = rows.get(key) || { name: i.name, size, packing, qty: 0 };
      curr.qty += i.qty;
      rows.set(key, curr);
    }));
    return Array.from(rows.values()).sort((a, b) =>
      a.name.localeCompare(b.name) || a.size.localeCompare(b.size) || a.packing.localeCompare(b.packing)
    );
  }, [tagged]);
  const packingTotalQty = packingList.reduce((s, r) => s + r.qty, 0);
  // Estimated packing time: rough average of 60s per unit (weigh ~15 + fill ~15
  // + label ~10 + seal ~10 + box ~10). Exposed as a formatted string "1h 33m".
  // Easy to tune later via a Settings input if you want more accuracy.
  const secondsPerUnit = 60;
  const packingTimeSec = packingTotalQty * secondsPerUnit;
  const formatDuration = (s) => {
    if (!s) return "0m";
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    return `${m}m`;
  };

  // FSMA 204 traceability report: opens a printable HTML window listing every
  // green lot (with full event history) and every Shopify order in the date
  // range, plus correlations matched on product-name heuristics. User uses
  // browser print → "Save as PDF" to archive.
  const downloadFSMAReport = async () => {
    if (!shopConnected) { alert("Connect Shopify first"); return; }
    const to = new Date().toISOString().slice(0, 10);
    const from = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    let data;
    try {
      const r = await fetch(`/api/fsma-report?shop=${encodeURIComponent(connectedShop)}&from=${from}&to=${to}`);
      data = await r.json();
      if (!r.ok) throw new Error(data.error || "Failed");
    } catch (err) {
      alert("Couldn't generate report: " + err.message);
      return;
    }
    const win = window.open("", "_blank", "width=1000,height=1200");
    if (!win) return;
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—";
    const origin = window.location.origin;

    const lotSections = (data.correlations || []).map(({ lot, matches }) => {
      const events = (lot.events || []).slice().sort((a, b) => (a.at || 0) - (b.at || 0));
      return `
        <section class="lot">
          <div class="lot-head">
            <div>
              <div class="lot-name">${esc(lot.name)}</div>
              <div class="lot-meta">${[lot.origin, lot.process, lot.farm, lot.variety].filter(Boolean).map(esc).join(" · ") || "—"}</div>
            </div>
            <div class="lot-stats">
              <span><strong>${(lot.currentWeightLbs ?? 0).toFixed(1)}</strong> lb current</span>
              <span>${lot.ageDays ?? "—"} days old</span>
              <span>Arrived ${esc(lot.arrivalDate || "—")}</span>
              ${lot.supplier ? `<span>From ${esc(lot.supplier)}</span>` : ""}
            </div>
          </div>
          <table class="events">
            <thead><tr><th>Date</th><th>Event</th><th>Δ lb</th><th>Note</th></tr></thead>
            <tbody>
              ${events.map(e => `<tr>
                <td>${fmtDate(new Date(e.at).toISOString())}</td>
                <td class="cap">${esc(e.type)}</td>
                <td class="num">${e.weightLbs >= 0 ? "+" : ""}${e.weightLbs.toFixed(2)}</td>
                <td>${esc(e.note || "")}</td>
              </tr>`).join("")}
            </tbody>
          </table>
          <div class="sub-head">Orders containing this coffee (matched by product name)</div>
          ${matches.length === 0 ? `<div class="empty">No matched orders in window.</div>` : `
            <table class="orders">
              <thead><tr><th>Order</th><th>Customer</th><th>Product</th><th>SKU</th><th class="num">Qty</th><th>Date</th></tr></thead>
              <tbody>
                ${matches.map(m => `<tr>
                  <td>${esc(m.orderId)}</td>
                  <td>${esc(m.customer)}</td>
                  <td>${esc(m.product)}</td>
                  <td class="mono">${esc(m.sku || "")}</td>
                  <td class="num">${m.qty}</td>
                  <td>${fmtDate(m.date)}</td>
                </tr>`).join("")}
              </tbody>
            </table>
          `}
        </section>
      `;
    }).join("");

    win.document.write(`<!doctype html>
<html><head><title>FSMA 204 Traceability Report — ${data.window.from} to ${data.window.to}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Inter', -apple-system, sans-serif; margin: 0; padding: 36px 44px; color: #000000; background: #fff; }
  .page-head { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #111; padding-bottom: 18px; margin-bottom: 24px; }
  .page-head h1 { margin: 0 0 4px; font-size: 26px; letter-spacing: -0.01em; }
  .page-head .sub { color: #6B7280; font-size: 13px; }
  .page-head .logo img { height: 24px; display: block; }
  .summary { display: flex; gap: 18px; margin-bottom: 28px; }
  .summary .stat { flex: 1; background: #F9FAFB; border-radius: 8px; padding: 14px 16px; }
  .summary .label { font-size: 11px; color: #6B7280; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; }
  .summary .value { font-size: 22px; font-weight: 700; margin-top: 4px; }
  section.lot { margin-bottom: 36px; page-break-inside: avoid; }
  .lot-head { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid #E5E7EB; }
  .lot-name { font-size: 18px; font-weight: 700; color: #111; }
  .lot-meta { font-size: 13px; color: #6B7280; margin-top: 3px; }
  .lot-stats { font-size: 12px; color: #374151; display: flex; gap: 14px; }
  .lot-stats strong { color: #111; }
  .sub-head { font-size: 11px; color: #6B7280; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; margin: 18px 0 8px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  thead th { text-align: left; padding: 8px 10px; background: #F9FAFB; color: #6B7280; font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 600; border-bottom: 1px solid #E5E7EB; }
  tbody td { padding: 8px 10px; border-bottom: 1px solid #F3F4F6; color: #374151; }
  td.num, th.num { text-align: right; }
  td.mono { font-family: ui-monospace, monospace; font-size: 12px; color: #6B7280; }
  td.cap { text-transform: capitalize; font-weight: 600; color: #111; }
  .empty { font-size: 13px; color: #9CA3AF; font-style: italic; padding: 8px 0; }
  footer { margin-top: 40px; padding-top: 18px; border-top: 2px solid #111; font-size: 11px; color: #6B7280; display: flex; justify-content: space-between; letter-spacing: 0.06em; }
  @media print { body { padding: 24px 30px; } }
</style>
</head><body>
  <div class="page-head">
    <div>
      <h1>Traceability Report</h1>
      <div class="sub">FSMA 204 ready · Green origin → customer order chain · ${fmtDate(data.window.from + "T00:00:00Z")} – ${fmtDate(data.window.to + "T00:00:00Z")}</div>
    </div>
    <div class="logo"><img src="${origin}/brand/elion-roaster.svg" alt="Elion" /></div>
  </div>
  <div class="summary">
    <div class="stat"><div class="label">Store</div><div class="value" style="font-size:14px; font-weight:600;">${esc(data.shop)}</div></div>
    <div class="stat"><div class="label">Green Lots</div><div class="value">${data.lotCount}</div></div>
    <div class="stat"><div class="label">Orders in Window</div><div class="value">${data.orderCount}</div></div>
    <div class="stat"><div class="label">Generated</div><div class="value" style="font-size:14px; font-weight:600;">${fmtDate(data.generatedAt)}</div></div>
  </div>
  ${lotSections || '<div class="empty">No green sources on file. Add sources in Inventory to populate this report.</div>'}
  <footer>
    <span>ELION ROASTER · TRACEABILITY REPORT</span>
    <span>ELIONLABS.ORG</span>
  </footer>
  <script>window.addEventListener('load', function(){ setTimeout(function(){ window.print(); }, 400); });<\/script>
</body></html>`);
    win.document.close();
  };

  // Open a printable packing-list window and kick off the browser print dialog
  // so the user can "Save as PDF" natively. Title uses the packing-list.svg
  // brand lockup; footer centers the brand logo between "Production
  // powered by" and "elionlabs.org". All text is Inter.
  const downloadPackingPDF = () => {
    const win = window.open("", "_blank", "width=900,height=1200");
    if (!win) return;
    const dateLabel = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const origin = window.location.origin;
    const companyLogoHtml = branding.logoBase64
      ? `<div class="corner-logo"><img src="${branding.logoBase64}" alt="Company logo" /></div>`
      : "";
    const rowsHTML = packingList.map((r, idx) => `
      <tr>
        <td class="done"><span class="chk"></span></td>
        <td class="num">${idx + 1}</td>
        <td class="product"><strong>${esc(r.name)}</strong>${r.size ? ` <span class="size">&mdash; ${esc(r.size)}</span>` : ""}</td>
        <td class="qty">${r.qty}</td>
        <td class="packing">${esc(r.packing) || "&mdash;"}</td>
      </tr>
    `).join("");
    win.document.write(`<!doctype html>
<html><head><title>Packing list &mdash; ${dateLabel}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #111; }
  body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, Helvetica, Arial, sans-serif; padding: 40px 48px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  .header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 8px; gap: 16px; }
  .title { height: 20px; }
  .title img { height: 100%; width: auto; display: block; }
  .corner-logo { height: 36px; }
  .corner-logo img { height: 100%; width: auto; display: block; max-width: 160px; object-fit: contain; }
  .sub { color: #6B7280; font-size: 13px; margin-bottom: 32px; font-weight: 400; }

  table { width: 100%; border-collapse: collapse; }
  thead th { text-align: left; padding: 12px 0; border-bottom: 1px solid #E5E7EB; font-size: 10px; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 500; }
  tbody td { padding: 16px 0; border-bottom: 1px solid #F3F4F6; font-size: 14px; color: #374151; vertical-align: middle; }

  th.done, td.done { width: 58px; padding-left: 4px; }
  th.num, td.num { width: 44px; color: #9CA3AF; }
  th.product, td.product { padding-right: 16px; color: #111; }
  td.product strong { font-weight: 700; }
  td.product .size { font-weight: 400; color: #6B7280; }
  th.qty, td.qty { width: 80px; }

  .chk { width: 22px; height: 22px; border: 1.8px solid #111; border-radius: 6px; display: inline-block; vertical-align: middle; }

  footer { margin-top: 44px; padding-top: 22px; border-top: 2.5px solid #111; display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; font-size: 11px; letter-spacing: 0.12em; color: #111; text-transform: uppercase; font-weight: 700; }
  footer .right { text-align: right; }
  footer .logo { height: 26px; justify-self: center; }
  footer .logo img { height: 100%; width: auto; display: block; }

  @media print { body { padding: 28px 36px; } .title { height: 18px; } }
</style>
</head><body>
  <div class="header">
    <div class="title"><img src="${origin}/brand/packing-list.svg" alt="Packing List" /></div>
    ${companyLogoHtml}
  </div>
  <div class="sub">${dateLabel} &middot; ${tagged.length} scheduled order${tagged.length === 1 ? "" : "s"} &middot; ${formatDuration(packingTimeSec)} est. packing</div>
  <table>
    <thead>
      <tr>
        <th class="done">Done</th>
        <th class="num">#</th>
        <th class="product">Product</th>
        <th class="qty">Qty</th>
        <th class="packing">Variante</th>
      </tr>
    </thead>
    <tbody>${rowsHTML}</tbody>
  </table>
  <footer>
    <div>Production powered by</div>
    <div class="logo"><img src="${origin}/brand/elion-roaster.svg" alt="Elion" /></div>
    <div class="right">roaster.elionlabs.org</div>
  </footer>
  <script>
    // Wait for webfont + both SVGs to load before triggering print.
    window.addEventListener('load', function(){
      var imgs = Array.from(document.images);
      Promise.all(imgs.map(function(img){ return img.complete ? null : new Promise(function(r){ img.addEventListener('load', r); img.addEventListener('error', r); }); }))
        .then(function(){ setTimeout(function(){ window.print(); }, 200); });
    });
  <\/script>
</body></html>`);
    win.document.close();
  };

  // Per-order packing list PDF. Accepts an array of orders and renders
  // one page per order (CSS page-break-after: always between them). Each
  // page has the order header (id, customer, date, ship-to address) and
  // a tidy items table. Same brand chrome as the aggregated packing list.
  const downloadOrderPackingPDF = (orders) => {
    const list = (Array.isArray(orders) ? orders : [orders]).filter(Boolean);
    if (list.length === 0) return;
    const win = window.open("", "_blank", "width=900,height=1200");
    if (!win) return;
    const dateLabel = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const origin = window.location.origin;
    const companyLogoHtml = branding.logoBase64
      ? `<div class="corner-logo"><img src="${branding.logoBase64}" alt="Company logo" /></div>`
      : "";

    const formatAddress = (a) => {
      if (!a) return "";
      const line1 = [a.address1, a.address2].filter(Boolean).join(" ");
      const line2 = [a.city, a.province_code || a.province, a.zip].filter(Boolean).join(", ");
      return [a.name, line1, line2].filter(Boolean).join("<br>");
    };

    const pagesHTML = list.map((o, pageIdx) => {
      const items = (o.items || []).filter(i => !excludedIds.has(i.productId));
      const totalUnits = items.reduce((s, i) => s + (i.qty || 0), 0);
      const itemsHTML = items.map((i, idx) => `
        <tr>
          <td class="done"><span class="chk"></span></td>
          <td class="num">${idx + 1}</td>
          <td class="product"><strong>${esc(i.name)}</strong>${i.variant ? ` <span class="size">&mdash; ${esc(i.variant)}</span>` : ""}</td>
          <td class="qty">${i.qty || 0}</td>
          <td class="sku">${esc(i.sku) || "&mdash;"}</td>
        </tr>
      `).join("");
      const addrHTML = formatAddress(o.shippingAddress);
      return `
        <section class="page${pageIdx > 0 ? " break" : ""}">
          <div class="header">
            <div class="title"><img src="${origin}/brand/packing-list.svg" alt="Packing List" /></div>
            ${companyLogoHtml}
          </div>
          <div class="order-head">
            <div class="order-id">${esc(o.id)}</div>
            <div class="order-meta">
              <div><span class="lbl">Customer</span><span class="val">${esc(o.customer || "—")}</span></div>
              <div><span class="lbl">Date</span><span class="val">${esc(o.date || "")}</span></div>
              ${typeof o.total === "number" ? `<div><span class="lbl">Total</span><span class="val">$${o.total.toFixed(2)}</span></div>` : ""}
              <div><span class="lbl">Items</span><span class="val">${items.length} line${items.length === 1 ? "" : "s"} · ${totalUnits} unit${totalUnits === 1 ? "" : "s"}</span></div>
            </div>
            ${addrHTML ? `<div class="ship-to"><span class="lbl">Ship to</span><div class="addr">${addrHTML}</div></div>` : ""}
          </div>
          <table>
            <thead>
              <tr>
                <th class="done">Done</th>
                <th class="num">#</th>
                <th class="product">Product</th>
                <th class="qty">Qty</th>
                <th class="sku">SKU</th>
              </tr>
            </thead>
            <tbody>${itemsHTML || `<tr><td colspan="5" style="padding:24px 0;color:#9CA3AF;">No items.</td></tr>`}</tbody>
          </table>
          <footer>
            <div>Production powered by</div>
            <div class="logo"><img src="${origin}/brand/elion-roaster.svg" alt="Elion" /></div>
            <div class="right">roaster.elionlabs.org</div>
          </footer>
        </section>
      `;
    }).join("");

    const titleLabel = list.length === 1
      ? `Packing list — ${list[0].id}`
      : `Packing lists — ${list.length} orders`;

    win.document.write(`<!doctype html>
<html><head><title>${esc(titleLabel)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #111; }
  body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, Helvetica, Arial, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  .page { padding: 40px 48px; min-height: 100vh; display: flex; flex-direction: column; }
  .page.break { page-break-before: always; break-before: page; }

  .header { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; margin-bottom: 20px; }
  .title { height: 20px; }
  .title img { height: 100%; width: auto; display: block; }
  .corner-logo { height: 36px; }
  .corner-logo img { height: 100%; width: auto; display: block; max-width: 160px; object-fit: contain; }

  .order-head { padding: 16px 0 24px; border-bottom: 2px solid #111; margin-bottom: 24px; }
  .order-id { font-size: 28px; font-weight: 800; color: #000; letter-spacing: -0.01em; margin-bottom: 14px; }
  .order-meta { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px 24px; margin-bottom: 14px; }
  .order-meta > div { display: flex; gap: 8px; align-items: baseline; }
  .order-meta .lbl, .ship-to .lbl { font-size: 10px; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 600; min-width: 60px; }
  .order-meta .val { font-size: 14px; color: #111; font-weight: 500; }
  .ship-to { display: flex; gap: 8px; align-items: flex-start; padding-top: 8px; border-top: 1px solid #F3F4F6; }
  .ship-to .addr { font-size: 14px; color: #111; line-height: 1.5; }

  table { width: 100%; border-collapse: collapse; }
  thead th { text-align: left; padding: 12px 0; border-bottom: 1px solid #E5E7EB; font-size: 10px; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 500; }
  tbody td { padding: 14px 0; border-bottom: 1px solid #F3F4F6; font-size: 14px; color: #374151; vertical-align: middle; }

  th.done, td.done { width: 58px; padding-left: 4px; }
  th.num, td.num { width: 44px; color: #9CA3AF; }
  th.product, td.product { padding-right: 16px; color: #111; }
  td.product strong { font-weight: 700; }
  td.product .size { font-weight: 400; color: #6B7280; }
  th.qty, td.qty { width: 70px; }
  th.sku, td.sku { width: 130px; color: #6B7280; font-variant-numeric: tabular-nums; }

  .chk { width: 22px; height: 22px; border: 1.8px solid #111; border-radius: 6px; display: inline-block; vertical-align: middle; }

  footer { margin-top: auto; padding-top: 22px; border-top: 2.5px solid #111; display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; font-size: 11px; letter-spacing: 0.12em; color: #111; text-transform: uppercase; font-weight: 700; }
  footer .right { text-align: right; }
  footer .logo { height: 26px; justify-self: center; }
  footer .logo img { height: 100%; width: auto; display: block; }

  @media print {
    .page { padding: 28px 36px; }
    .title { height: 18px; }
  }
</style>
</head><body>
  ${pagesHTML}
  <script>
    window.addEventListener('load', function(){
      var imgs = Array.from(document.images);
      Promise.all(imgs.map(function(img){ return img.complete ? null : new Promise(function(r){ img.addEventListener('load', r); img.addEventListener('error', r); }); }))
        .then(function(){ setTimeout(function(){ window.print(); }, 200); });
    });
  <\/script>
</body></html>`);
    win.document.close();
  };

  // Ultra-compact roast plan print — single table, section rows per coffee.
  const printPlanningPDF = () => {
    const win = window.open("", "_blank", "width=860,height=1100");
    if (!win) return;
    const dateLabel = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const completedIds = new Set((roastingQueue.completed || []).map(c => c.batchId));

    const seenIds = new Set();
    const groups = [];
    for (const p of plan) {
      if (seenIds.has(p.id)) continue;
      seenIds.add(p.id);
      const batches = expandedBatches.filter(b => b.productId === p.id);
      const done = batches.filter(b => completedIds.has(b.id)).length;
      groups.push({ ...p, batches, done, bags: Math.ceil((p.greenLbs || 0) / 154) });
    }

    // Build one flat table with section-header rows between coffees
    // Pre-batch index map for the print
    const printPreMap = new Map(expandedBatches.map((b, i) => [b.id, `PB-${String(i + 1).padStart(3, "0")}`]));

    const rowsHTML = groups.map(g => {
      const batchRows = g.batches.map(b => {
        const finalNum = roastingQueue.batchNumbers?.[b.id] || null;
        const preNum = printPreMap.get(b.id) || "—";
        const isRoasted = completedIds.has(b.id);
        const batchCell = isRoasted && finalNum
          ? `<span class="bn-final">${esc(finalNum)}</span> <span class="bn-pre">${esc(preNum)}</span>`
          : `<span class="bn-pre-only">${esc(preNum)}</span>`;
        return `<tr class="${isRoasted ? "done" : ""}">
          <td class="chk"><span class="box ${isRoasted ? "box-done" : ""}"></span></td>
          <td class="bn">${batchCell}</td>
          <td class="coffee"></td>
          <td class="num">${Math.ceil(b.roastedLbs)}</td>
          <td class="num">${Math.ceil(b.greenLbs)}</td>
          <td class="st ${isRoasted ? "st-done" : "st-q"}">${isRoasted ? "✓" : ""}</td>
        </tr>`;
      }).join("");
      return `<tr class="section-row">
          <td colspan="2"></td>
          <td class="section-name">${esc(g.name)}</td>
          <td class="section-meta">${g.batches.length} batch${g.batches.length !== 1 ? "es" : ""} · ${g.bags} bag${g.bags !== 1 ? "s" : ""}</td>
          <td class="section-meta">${Math.ceil(g.roastedLbs)} lb</td>
          <td class="section-meta">${g.done}/${g.batches.length}</td>
        </tr>${batchRows}`;
    }).join("");

    const origin = window.location.origin;
    const coLogo = branding.logoBase64 ? `<img class="co-logo" src="${branding.logoBase64}" alt="logo" />` : "";

    win.document.write(`<!doctype html>
<html><head><title>Roast plan — ${dateLabel}</title>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet">
<style>
  *{box-sizing:border-box}
  html,body{margin:0;padding:0;background:#fff;color:#111}
  body{font-family:'Inter',-apple-system,sans-serif;padding:20px 28px 16px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .top{display:flex;justify-content:space-between;align-items:center;margin-bottom:2px}
  .pr-logo{height:13px;width:auto}
  .co-logo{height:22px;width:auto;max-width:110px;object-fit:contain}
  .sub{color:#9CA3AF;font-size:9px;margin:0 0 10px;letter-spacing:.02em}
  table{width:100%;border-collapse:collapse}
  thead tr{border-bottom:1.5px solid #111}
  th{font-size:9px;font-weight:700;color:#9CA3AF;text-transform:uppercase;letter-spacing:.07em;padding:3px 6px;text-align:left;white-space:nowrap}
  th.r{text-align:right}
  td{font-size:10px;padding:3px 6px;color:#374151;border-bottom:1px solid #F3F4F6}
  tr:last-child td{border-bottom:none}
  .section-row td{background:#F9FAFB;border-top:1px solid #E5E7EB;border-bottom:1px solid #E5E7EB;padding:4px 6px}
  .section-name{font-size:11px;font-weight:700;color:#111}
  .section-meta{font-size:9px;color:#9CA3AF;font-weight:600}
  .chk{width:20px;padding-left:4px;padding-right:2px}
  .box{display:inline-block;width:10px;height:10px;border:1px solid #D1D5DB;border-radius:2px;vertical-align:middle}
  .box-done{background:#22C55E;border-color:#22C55E}
  .bn{width:100px}
  .bn-final{display:inline-block;background:#111;color:#fff;border-radius:99px;padding:1px 7px;font-size:9px;font-weight:700;letter-spacing:.02em;vertical-align:middle}
  .bn-pre{display:inline-block;background:#F3F4F6;color:#9CA3AF;border-radius:99px;padding:1px 6px;font-size:9px;font-weight:600;vertical-align:middle}
  .bn-pre-only{display:inline-block;background:#F9FAFB;color:#6B7280;border-radius:99px;padding:1px 7px;font-size:9px;font-weight:600;border:1px dashed #D1D5DB;vertical-align:middle}
  .coffee{width:auto}
  .num{text-align:right;width:60px;color:#374151}
  tr.done td{color:#D1D5DB}
  tr.done .bn{color:#D1D5DB}
  .st{text-align:right;font-weight:700;font-size:10px;width:36px}
  .st-done{color:#22C55E}
  .st-q{color:#E5E7EB}
  .footer{margin-top:14px;display:flex;align-items:center;justify-content:center;gap:5px;color:#D1D5DB;font-size:9px;border-top:1px solid #F3F4F6;padding-top:10px}
  .footer img{height:9px}
  @media print{body{padding:14px 20px 12px}}
</style></head><body>
<div class="top">
  <img class="pr-logo" src="${origin}/brand/elion-roaster.svg" alt="Elion Roaster" />
  ${coLogo}
</div>
<p class="sub">Roast plan — ${dateLabel}</p>
<table>
  <thead><tr>
    <th class="chk"></th>
    <th style="width:70px">Batch</th>
    <th>Coffee</th>
    <th class="r">Roasted</th>
    <th class="r">Green</th>
    <th class="r">Done</th>
  </tr></thead>
  <tbody>${rowsHTML}</tbody>
</table>
<div class="footer">
  Production powered by <img src="${origin}/brand/elion-roaster.svg" alt="Elion" /> elionlabs.org
</div>
<script>window.addEventListener('load',function(){setTimeout(function(){window.print()},200)})<\/script>
</body></html>`);
    win.document.close();
  };

  // All products referenced by visible orders or the local catalog, used in the
  // Settings page to let the user set per-product weight-loss overrides.
  const productsForOverrides = useMemo(() => {
    const m = new Map();
    visibleOrders.forEach(o => (o.items || []).forEach(i => {
      if (i.productId != null && !m.has(i.productId)) m.set(i.productId, { productId: i.productId, name: i.name });
    }));
    products.forEach(p => { if (!m.has(p.id)) m.set(p.id, { productId: p.id, name: p.name }); });
    return Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [visibleOrders, products]);

  // Production per product — aggregates line-item qty across orders the user
  // has explicitly marked as produced. This is the only source of truth for
  // Analytics; scheduled/pending orders don't count until actually produced.
  const productionByProduct = useMemo(() => {
    const rows = new Map();
    producedOrders.forEach(o => (o.items || []).forEach(i => {
      // Re-apply exclusion filter — the user may have excluded a product after
      // producing an order; we should still respect the exclusion in reporting.
      if (excludedIds.has(i.productId)) return;
      const curr = rows.get(i.name) || { name: i.name, qty: 0, orders: new Set() };
      curr.qty += i.qty;
      curr.orders.add(o.shopifyId || o.id);
      rows.set(i.name, curr);
    }));
    return Array.from(rows.values())
      .map(r => ({ name: r.name, qty: r.qty, orders: r.orders.size }))
      .sort((a, b) => b.qty - a.qty);
  }, [producedOrders, excludedIds]);

  /* ── Profitability data — derived from historical Shopify orders + green
     inventory costs. Revenue is precise (from Shopify line totals). Cost is
     estimated: avg green cost / (1 − loss%) × lbs sold. ─────────────────── */
  const avgGreenCostPerLb = useMemo(() => {
    const lots = greenLots.filter(l => !l.archivedAt && l.pricePerLb != null);
    if (lots.length === 0) return null;
    // Weight the average by current on-hand — bigger lots influence more.
    const totals = lots.reduce((acc, l) => {
      const w = Math.max(l.currentWeightLbs || 0, 1);
      acc.sum += (l.pricePerLb || 0) * w;
      acc.weight += w;
      return acc;
    }, { sum: 0, weight: 0 });
    return totals.weight > 0 ? totals.sum / totals.weight : null;
  }, [greenLots]);

  // Parse "5lb", "10oz", "15lb Container" → lbs
  const parseVariantLbs = (v) => {
    if (!v) return null;
    const lb = v.match(/(\d+(?:\.\d+)?)\s*lb/i);
    if (lb) return parseFloat(lb[1]);
    const oz = v.match(/(\d+(?:\.\d+)?)\s*oz/i);
    if (oz) return parseFloat(oz[1]) / 16;
    const g = v.match(/(\d+(?:\.\d+)?)\s*g\b/i);
    if (g) return parseFloat(g[1]) / 453.592;
    return null;
  };

  const profitabilityData = useMemo(() => {
    const orders = profitabilityOrders || [];
    const now = Date.now();
    // "all" means we take every order returned; numeric windows filter by time.
    const isAll = profitabilityWindow === "all";
    const windowStart = isAll
      ? (orders.length > 0
          ? Math.min(...orders.map(o => new Date(o.created_at).getTime()))
          : now)
      : now - profitabilityWindow * 24 * 60 * 60 * 1000;

    const filteredOrders = isAll ? orders : orders.filter(o => {
      const t = new Date(o.created_at).getTime();
      return t >= windowStart;
    });

    // Compute data span early — used downstream in forecast horizon + trend.
    const oldestOrderTime = orders.length > 0
      ? Math.min(...orders.map(o => new Date(o.created_at).getTime()))
      : null;
    const dataSpanDays = oldestOrderTime != null
      ? Math.max(1, Math.floor((now - oldestOrderTime) / (24 * 60 * 60 * 1000)))
      : 0;
    const windowExceedsData = !isAll && profitabilityWindow > dataSpanDays;

    // Per-day revenue series — start at the first order in the window so the
    // chart isn't padded with flat zero days before you had any sales.
    const perDay = new Map();
    const dayKey = (date) => date.toISOString().slice(0, 10);
    const firstOrderTime = filteredOrders.length > 0
      ? Math.min(...filteredOrders.map(o => new Date(o.created_at).getTime()))
      : null;
    const seedStart = firstOrderTime != null ? Math.max(windowStart, firstOrderTime) : null;
    if (seedStart != null) {
      for (let d = new Date(seedStart); d.getTime() <= now; d.setDate(d.getDate() + 1)) {
        perDay.set(dayKey(d), { date: dayKey(d), revenue: 0, cost: 0, orders: 0 });
      }
    }

    // Per-product + per-customer rollups
    const productMap = new Map();
    const customerMap = new Map();
    let totalRevenue = 0, totalCost = 0, totalUnits = 0, totalLbs = 0;

    for (const o of filteredOrders) {
      if (o.cancelled_at) continue;
      const date = new Date(o.created_at);
      const dk = dayKey(date);
      const orderRevenue = parseFloat(o.total_price || 0);
      totalRevenue += orderRevenue;

      const customer = o.customer
        ? `${o.customer.first_name || ""} ${o.customer.last_name || ""}`.trim() || (o.billing_address?.name || "Guest")
        : (o.billing_address?.name || "Guest");

      if (!customerMap.has(customer)) {
        customerMap.set(customer, { name: customer, revenue: 0, orders: 0, units: 0 });
      }
      const cRow = customerMap.get(customer);
      cRow.revenue += orderRevenue;
      cRow.orders += 1;

      const dayRow = perDay.get(dk) || { date: dk, revenue: 0, cost: 0, orders: 0 };
      dayRow.revenue += orderRevenue;
      dayRow.orders += 1;

      let orderCost = 0;
      for (const li of (o.line_items || [])) {
        if (excludedIds.has(li.product_id)) continue;
        const liRevenue = parseFloat(li.price || 0) * (li.quantity || 0);
        const variantLbsUnit = parseVariantLbs(li.variant_title) || 0;
        const lbsFromGrams = li.grams ? (li.grams / 453.592) : 0; // grams is per-unit * quantity per Shopify spec
        const lbs = (lbsFromGrams || variantLbsUnit * (li.quantity || 0)) || 0;
        const loss = (typeof lossOverrides[li.product_id] === "number" ? lossOverrides[li.product_id] : defaultLossPct);
        const costPerLbRoasted = avgGreenCostPerLb != null ? avgGreenCostPerLb / (1 - loss) : 0;
        const liCost = lbs * costPerLbRoasted;
        orderCost += liCost;
        totalLbs += lbs;
        totalUnits += li.quantity || 0;
        cRow.units += li.quantity || 0;

        if (!productMap.has(li.title)) {
          productMap.set(li.title, { name: li.title, revenue: 0, cost: 0, units: 0, lbs: 0, orders: 0 });
        }
        const pRow = productMap.get(li.title);
        pRow.revenue += liRevenue;
        pRow.cost += liCost;
        pRow.units += li.quantity || 0;
        pRow.lbs += lbs;
        pRow.orders += 1;
      }
      totalCost += orderCost;
      dayRow.cost += orderCost;
      perDay.set(dk, dayRow);
    }

    const daily = Array.from(perDay.values()).sort((a, b) => a.date.localeCompare(b.date));

    // Trend: compare last-30-days daily avg to the 30 days before that. If
    // sales are growing, forecast grows; if declining, forecast declines.
    // Must be computed here because byProduct / byCustomer forecasts also
    // use it, and they're built below.
    const trendRecentStart = now - 30 * 24 * 60 * 60 * 1000;
    const trendPriorStart = now - 60 * 24 * 60 * 60 * 1000;
    let trRecentSum = 0, trRecentCount = 0, trPriorSum = 0, trPriorCount = 0;
    for (const d of daily) {
      const t = new Date(d.date).getTime();
      if (t >= trendRecentStart && t <= now) { trRecentSum += d.revenue; trRecentCount += 1; }
      else if (t >= trendPriorStart && t < trendRecentStart) { trPriorSum += d.revenue; trPriorCount += 1; }
    }
    const recentDailyAvg = trRecentCount > 0 ? trRecentSum / trRecentCount : 0;
    const priorDailyAvg = trPriorCount > 0 ? trPriorSum / trPriorCount : 0;
    const rawGrowth = priorDailyAvg > 0 ? (recentDailyAvg - priorDailyAvg) / priorDailyAvg : 0;
    const growthRate = Math.max(-0.4, Math.min(0.4, rawGrowth));

    // Per-product / per-customer forecast: always "next 30 days", computed
    // from each one's last 60 days of activity. A customer who disappeared 6
    // months ago correctly projects to ~$0. A customer ordering weekly at
    // $500 projects to ~$2,000 (× trend). Consistent regardless of selected
    // window so the number always means the same thing.
    const forecastProjectionDays = 30;
    const recentLookbackDays = 60;
    const recentLookbackStart = now - recentLookbackDays * 24 * 60 * 60 * 1000;
    const productRecent = new Map();
    const customerRecent = new Map();
    for (const o of filteredOrders) {
      const t = new Date(o.created_at).getTime();
      if (t < recentLookbackStart || o.cancelled_at) continue;
      const customer = o.customer
        ? `${o.customer.first_name || ""} ${o.customer.last_name || ""}`.trim() || (o.billing_address?.name || "Guest")
        : (o.billing_address?.name || "Guest");
      customerRecent.set(customer, (customerRecent.get(customer) || 0) + parseFloat(o.total_price || 0));
      for (const li of (o.line_items || [])) {
        if (excludedIds.has(li.product_id)) continue;
        const rev = parseFloat(li.price || 0) * (li.quantity || 0);
        productRecent.set(li.title, (productRecent.get(li.title) || 0) + rev);
      }
    }
    const effectiveLookback = Math.min(recentLookbackDays, Math.max(dataSpanDays, 1));
    const trendFactor = 1 + growthRate * 0.5;
    const projectRevenue = (recentRev) => forecastEnabled && effectiveLookback > 0
      ? (recentRev / effectiveLookback) * forecastProjectionDays * trendFactor
      : 0;

    const byProduct = Array.from(productMap.values())
      .map(p => ({
        ...p,
        margin: p.revenue - p.cost,
        marginPct: p.revenue > 0 ? ((p.revenue - p.cost) / p.revenue) * 100 : 0,
        forecastRevenue: projectRevenue(productRecent.get(p.name) || 0),
      }))
      .sort((a, b) => b.revenue - a.revenue);
    const byCustomer = Array.from(customerMap.values())
      .map(c => ({ ...c, forecastRevenue: projectRevenue(customerRecent.get(c.name) || 0) }))
      .sort((a, b) => b.revenue - a.revenue);

    const marginPct = totalRevenue > 0 ? ((totalRevenue - totalCost) / totalRevenue) * 100 : 0;

    /* ── Demand forecast ──────────────────────────────────────
       Day-of-week seasonal mean applied to the next 30 days. Simple,
       transparent, and surprisingly accurate for roasters on a weekly
       ordering cadence. Computes mean revenue by DoW from the last 60
       days of actual data, then projects forward.                       */
    const forecastLookbackMs = 60 * 24 * 60 * 60 * 1000;
    const lookbackStart = now - forecastLookbackMs;
    const dowSum = [0, 0, 0, 0, 0, 0, 0];
    const dowDays = [0, 0, 0, 0, 0, 0, 0];
    const seen = new Set();
    for (const d of daily) {
      const t = new Date(d.date).getTime();
      if (t < lookbackStart) continue;
      const dow = new Date(d.date).getUTCDay();
      if (seen.has(d.date)) continue;
      seen.add(d.date);
      dowSum[dow] += d.revenue;
      dowDays[dow] += 1;
    }
    const dowAvg = dowSum.map((s, i) => dowDays[i] > 0 ? s / dowDays[i] : 0);
    const hasEnoughData = dowDays.reduce((s, n) => s + n, 0) >= 7;

    // Horizon scales with the view so forecast always occupies ~1/3 of the
    // chart on the right, with a floor of 7 days and a ceiling of 2 years
    // (longer projections based on DoW seasonality aren't credible).
    const viewDays = isAll ? Math.max(dataSpanDays, 30) : profitabilityWindow;
    const forecastHorizon = forecastEnabled
      ? Math.max(7, Math.min(730, Math.round(viewDays / 2)))
      : 0;
    const todayKey = new Date(now).toISOString().slice(0, 10);
    const forecastPoints = [];
    if (hasEnoughData && forecastEnabled) {
      const startTs = new Date(now);
      // Grab the last `forecastHorizon` days of actual daily revenue — we'll
      // replay this forward so the forecast carries the same day-to-day
      // volatility (zeros, spikes, weekly rhythm) as the recent past. A flat
      // projected line lies about certainty; a mirrored-past line tells the
      // truth: "we expect the next N days to look like the last N days,
      // trended up/down by X%."
      const actualsMirror = [];
      for (let i = forecastHorizon - 1; i >= 0; i--) {
        const d = new Date(startTs);
        d.setDate(startTs.getDate() - i);
        const key = d.toISOString().slice(0, 10);
        const dayData = perDay.get(key);
        actualsMirror.push(dayData ? dayData.revenue : 0);
      }
      // If the mirrored history is too sparse (mostly zeros), fall back to the
      // DoW mean for those days so the forecast isn't a flat-zero line.
      const nonZeroCount = actualsMirror.filter(v => v > 0).length;
      const useMirror = nonZeroCount >= Math.min(actualsMirror.length * 0.3, 14);
      for (let i = 1; i <= forecastHorizon; i++) {
        const d = new Date(startTs);
        d.setDate(startTs.getDate() + i);
        const dow = d.getUTCDay();
        // Trend ramps linearly from 1 to (1 + growthRate) across the horizon.
        const trendMult = 1 + growthRate * (i / forecastHorizon);
        const base = useMirror ? actualsMirror[i - 1] : dowAvg[dow];
        forecastPoints.push({
          date: d.toISOString().slice(0, 10),
          forecast: +(base * trendMult).toFixed(2),
          isForecast: true,
        });
      }
    }

    // Merge actuals + forecast into one series; ensure the last actual point
    // has the same `forecast` value as the first forecast point so the line
    // visually continues without a gap.
    const actualSeries = daily.map(d => ({ ...d, forecast: null }));
    if (actualSeries.length > 0 && forecastPoints.length > 0) {
      actualSeries[actualSeries.length - 1].forecast = actualSeries[actualSeries.length - 1].revenue;
    }
    const forecastSeries = forecastPoints.map(p => ({
      date: p.date,
      revenue: null,
      cost: null,
      forecast: p.forecast,
      isForecast: true,
    }));
    let combined = [...actualSeries, ...forecastSeries];

    // Auto-choose granularity: daily for short windows, weekly for medium,
    // monthly when the span is a year+ or "All". This keeps roughly 10-30
    // points on the chart regardless of window length.
    const granularity = isAll || profitabilityWindow > 180
      ? "monthly"
      : profitabilityWindow > 30
        ? "weekly"
        : "daily";

    if (granularity !== "daily") {
      const bucketKey = granularity === "weekly"
        ? (d) => {
            // Monday-anchored week
            const dt = new Date(d + "T00:00:00");
            const day = dt.getUTCDay();
            const diff = (day + 6) % 7;
            dt.setUTCDate(dt.getUTCDate() - diff);
            return dt.toISOString().slice(0, 10);
          }
        : (d) => {
            // First day of month
            return d.slice(0, 7) + "-01";
          };
      const todayBucket = bucketKey(todayKey);

      const buckets = new Map();
      for (const d of combined) {
        const k = bucketKey(d.date);
        if (!buckets.has(k)) buckets.set(k, { date: k, revenue: 0, cost: 0, forecast: 0, orders: 0, hasActual: false, hasForecast: false });
        const row = buckets.get(k);
        if (d.revenue != null) { row.revenue += d.revenue; row.hasActual = true; }
        if (d.cost != null) { row.cost += d.cost; }
        if (d.forecast != null) { row.forecast += d.forecast; row.hasForecast = true; }
        if (d.orders) row.orders += d.orders;
      }
      // The bucket containing today is a partial week/month (actuals up to
      // today, forecast after). Drop its forecast portion so the bucket is
      // pure actual. Forecast begins at the NEXT complete bucket. The stitch
      // below then connects the two lines cleanly without a visual step.
      if (buckets.has(todayBucket) && buckets.get(todayBucket).hasActual) {
        const row = buckets.get(todayBucket);
        row.forecast = 0;
        row.hasForecast = false;
      }
      combined = Array.from(buckets.values())
        .sort((a, b) => a.date.localeCompare(b.date))
        .map(b => ({
          date: b.date,
          revenue: b.hasActual ? b.revenue : null,
          cost: b.hasActual ? b.cost : null,
          forecast: b.hasForecast ? b.forecast : null,
          orders: b.orders,
        }));

      // FORECAST: Standard Theta method (Assimakopoulos & Nikolopoulos 2000;
      // formulas from Hyndman & Billah 2003 and Nixtla statsforecast). Theta
      // won the M3 competition on short business series and is the conceptual
      // basis for the M4 winner. We chose it over Holt-Winters because:
      //   • HW initialization needs ≥2 full periods (Hyndman, hw-initialization).
      //     With 12 monthly buckets we never enter the seasonal branch and the
      //     fallback (damped Holt with φ=0.9) flatlines by construction.
      //   • Theta separates trend (θ=0 line, OLS) from local curvature
      //     (θ=2 line, SES). The trend component does NOT damp, so the curve
      //     retains visible slope at long horizons.
      //   • Seasonality enters via classical decomposition guarded by a
      //     Bartlett ACF test — same code path handles non-seasonal cleanly.
      //
      // Partial-current-bucket handling: the bucket containing `today` is
      // dropped from training (it would otherwise drag the level toward zero
      // mid-month). Its actual value is preserved for display; the forecast
      // resumes from the next complete bucket.
      const period = granularity === "weekly" ? 52 : 12;
      const todayBucketKey = bucketKey(todayKey);
      const isPartial = (b) => b.date === todayBucketKey;
      const trainingBuckets = combined.filter(b => b.revenue != null && !isPartial(b));
      const forecastIdxs = combined.map((b, i) => (b.forecast != null && b.revenue == null) ? i : -1).filter(i => i >= 0);

      if (trainingBuckets.length >= 2 && forecastIdxs.length > 0) {
        const rawY = trainingBuckets.map(b => b.revenue || 0);
        // Light winsorization — Theta is sensitive to outliers in B_T.
        const median = (arr) => {
          const s = [...arr].sort((a, b) => a - b);
          const n = s.length; if (!n) return 0;
          return n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
        };
        const med = median(rawY);
        const mad = median(rawY.map(v => Math.abs(v - med))) || (med * 0.1) || 1;
        const yIn = rawY.map(v => Math.max(med - 4 * mad, Math.min(med + 4 * mad, v)));

        const sum = (a) => a.reduce((s, v) => s + v, 0);
        const mean = (a) => a.length ? sum(a) / a.length : 0;

        // ── Seasonality test: Bartlett-adjusted ACF at lag m, 90% CI.
        // ρ_m vs q · √((1 + 2Σρ_i²)/T); q≈1.645 at 90%.
        const acf = (a, lag) => {
          const mu = mean(a); const T = a.length;
          if (T <= lag + 1) return 0;
          let num = 0, den = 0;
          for (let i = 0; i < T; i++) den += (a[i] - mu) ** 2;
          for (let i = lag; i < T; i++) num += (a[i] - mu) * (a[i - lag] - mu);
          return den > 0 ? num / den : 0;
        };
        let seasonal = false;
        let seasonalIdx = new Array(period).fill(1);
        if (yIn.length >= 2 * period) {
          const rhoM = acf(yIn, period);
          let s = 0;
          for (let i = 1; i < period; i++) s += acf(yIn, i) ** 2;
          const threshold = 1.645 * Math.sqrt((1 + 2 * s) / yIn.length);
          if (Math.abs(rhoM) > threshold) seasonal = true;
        }

        // ── Multiplicative decomposition (only if seasonal). Centered MA
        // of length `period`, then average ratio y/MA per season slot.
        // Reseasonalize forecasts by multiplying by the season index.
        let y = yIn.slice();
        if (seasonal) {
          const half = Math.floor(period / 2);
          const ma = new Array(yIn.length).fill(null);
          for (let t = half; t < yIn.length - half; t++) {
            let acc = 0;
            for (let k = -half; k <= half; k++) acc += yIn[t + k];
            // For even period, use 2×m MA (centered)
            if (period % 2 === 0) {
              acc -= 0.5 * (yIn[t - half] + yIn[t + half]);
              ma[t] = acc / period;
            } else {
              ma[t] = acc / period;
            }
          }
          const slotSums = new Array(period).fill(0);
          const slotCounts = new Array(period).fill(0);
          for (let t = 0; t < yIn.length; t++) {
            if (ma[t] && ma[t] > 0) {
              slotSums[t % period] += yIn[t] / ma[t];
              slotCounts[t % period] += 1;
            }
          }
          const rawIdx = slotSums.map((s, i) => slotCounts[i] > 0 ? s / slotCounts[i] : 1);
          // Normalize so indices average to 1.
          const idxMean = mean(rawIdx) || 1;
          seasonalIdx = rawIdx.map(v => v / idxMean);
          y = yIn.map((v, t) => v / seasonalIdx[t % period]);
        }

        const T = y.length;
        const h = forecastIdxs.length;

        // ── θ=0 line: OLS trend B_T · t + A_T (statsforecast closed form).
        // Indexed t = 1..T per the paper.
        const sumY = sum(y);
        let sumTY = 0; for (let t = 0; t < T; t++) sumTY += (t + 1) * y[t];
        const B = T > 1
          ? (6 / (T * T - 1)) * ((2 / T) * sumTY - ((T + 1) / T) * sumY)
          : 0;
        const A = sumY / T - ((T + 1) / 2) * B;

        // ── θ=2 line: 2*y - trend, forecast via SES with α optimized by
        // grid search (in-sample SSE). Cheap: 17 evaluations.
        const z2 = y.map((v, t) => 2 * v - (A + B * (t + 1)));
        const sesForecast = (series, alpha, steps) => {
          let L = series[0];
          for (let t = 1; t < series.length; t++) L = alpha * series[t] + (1 - alpha) * L;
          return new Array(steps).fill(L);
        };
        const sesSSE = (series, alpha) => {
          let L = series[0], sse = 0;
          for (let t = 1; t < series.length; t++) {
            sse += (series[t] - L) ** 2;
            L = alpha * series[t] + (1 - alpha) * L;
          }
          return sse;
        };
        let bestAlpha = 0.3, bestSSE = Infinity;
        for (let a = 0.1; a <= 0.9 + 1e-9; a += 0.05) {
          const e = sesSSE(z2, a);
          if (e < bestSSE) { bestSSE = e; bestAlpha = a; }
        }
        const z2Fc = sesForecast(z2, bestAlpha, h);

        // ── Combine θ=0 (extrapolated linearly) and θ=2 (SES) with 50/50
        // weight, reseasonalize, floor at 0.
        const lastT = T;
        const projected = [];
        for (let k = 0; k < h; k++) {
          const trendFc = A + B * (lastT + k + 1);
          const blended = 0.5 * trendFc + 0.5 * z2Fc[k];
          const seasonMult = seasonalIdx[(lastT + k) % period];
          projected.push(blended * seasonMult);
        }

        for (let k = 0; k < forecastIdxs.length; k++) {
          combined[forecastIdxs[k]].forecast = +Math.max(0, projected[k] || 0).toFixed(2);
        }
      }

      // Stitch actual → forecast so the line is continuous at the handoff.
      const lastActualIdx = combined.findIndex((w, i, arr) => w.revenue != null && (arr[i+1]?.revenue == null));
      if (lastActualIdx >= 0 && combined[lastActualIdx].forecast == null) {
        combined[lastActualIdx].forecast = combined[lastActualIdx].revenue;
      }
    }

    const forecast7d = forecastPoints.slice(0, 7).reduce((s, p) => s + (p.forecast || 0), 0);
    const forecast30d = forecastPoints.slice(0, 30).reduce((s, p) => s + (p.forecast || 0), 0);

    return {
      totalRevenue, totalCost, totalMargin: totalRevenue - totalCost, marginPct,
      totalUnits, totalLbs, orderCount: filteredOrders.length,
      daily: combined, byProduct, byCustomer,
      dataSpanDays, windowExceedsData,
      firstOrderAt: firstOrderTime,
      forecast7d, forecast30d, forecastHorizon, hasForecast: hasEnoughData && forecastEnabled,
      todayKey, granularity, growthRate,
    };
  }, [profitabilityOrders, profitabilityWindow, excludedIds, avgGreenCostPerLb, defaultLossPct, lossOverrides, forecastEnabled]);

  /* ═════════════════════════ CLIENTS ═════════════════════════
     Aggregate raw Shopify orders into per-customer profiles. Keyed by
     customer.id when Shopify provides one (the reliable identity across
     name changes), falling back to the display name for guest checkouts.
     Always computed over the full `profitabilityOrders` set so lifetime
     totals don't flicker when the Analytics window changes.
     ═════════════════════════════════════════════════════════ */
  const clientsList = useMemo(() => {
    const map = new Map();
    for (const o of (profitabilityOrders || [])) {
      if (o.cancelled_at) continue;
      const cid = o.customer?.id ? String(o.customer.id) : null;
      const name = o.customer
        ? `${o.customer.first_name || ""} ${o.customer.last_name || ""}`.trim() || (o.billing_address?.name || "Guest")
        : (o.billing_address?.name || "Guest");
      const key = cid || `name:${name}`;
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          shopifyCustomerId: cid,
          name,
          email: o.customer?.email || o.email || "",
          company: o.billing_address?.company || o.customer?.default_address?.company || "",
          city: o.billing_address?.city || "",
          province: o.billing_address?.province_code || o.billing_address?.province || "",
          orders: [],
          totalRevenue: 0,
          orderCount: 0,
          firstOrder: Infinity,
          lastOrder: 0,
        });
      }
      const c = map.get(key);
      c.orders.push(o);
      c.totalRevenue += parseFloat(o.total_price || 0);
      c.orderCount += 1;
      const t = new Date(o.created_at).getTime();
      if (t > c.lastOrder) c.lastOrder = t;
      if (t < c.firstOrder) c.firstOrder = t;
    }
    return Array.from(map.values()).map(c => ({
      ...c,
      avgOrder: c.orderCount > 0 ? c.totalRevenue / c.orderCount : 0,
      orders: c.orders.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    })).sort((a, b) => b.totalRevenue - a.totalRevenue);
  }, [profitabilityOrders]);

  const selectedClient = useMemo(
    () => (selectedClientId ? (clientsList.find(c => c.id === selectedClientId) || null) : null),
    [selectedClientId, clientsList]
  );

  // Per-client analytics: daily revenue + simple forecast. Mirrors the main
  // profitability forecast but without cost/margin — we don't attribute cost
  // per-customer, just revenue.
  const clientAnalytics = useMemo(() => {
    if (!selectedClient) return null;
    const now = Date.now();
    const orders = selectedClient.orders;
    const firstOrderTime = selectedClient.firstOrder;
    if (!isFinite(firstOrderTime)) return null;

    const dayKey = (d) => new Date(d).toISOString().slice(0, 10);
    const perDay = new Map();
    for (let d = new Date(firstOrderTime); d.getTime() <= now; d.setDate(d.getDate() + 1)) {
      perDay.set(dayKey(d), { date: dayKey(d), revenue: 0 });
    }
    for (const o of orders) {
      if (o.cancelled_at) continue;
      const k = dayKey(o.created_at);
      const row = perDay.get(k) || { date: k, revenue: 0 };
      row.revenue += parseFloat(o.total_price || 0);
      perDay.set(k, row);
    }
    const daily = Array.from(perDay.values()).sort((a, b) => a.date.localeCompare(b.date));
    const dataSpanDays = Math.max(1, Math.floor((now - firstOrderTime) / 86400000));

    // Trend from last 30 vs prior 30 days
    const tR = now - 30 * 86400000, tP = now - 60 * 86400000;
    let rS = 0, rN = 0, pS = 0, pN = 0;
    for (const d of daily) {
      const t = new Date(d.date).getTime();
      if (t >= tR) { rS += d.revenue; rN++; }
      else if (t >= tP) { pS += d.revenue; pN++; }
    }
    const recentAvg = rN > 0 ? rS / rN : 0;
    const priorAvg = pN > 0 ? pS / pN : 0;
    const rawGrowth = priorAvg > 0 ? (recentAvg - priorAvg) / priorAvg : 0;
    const growthRate = Math.max(-0.5, Math.min(0.5, rawGrowth));

    // Projected next 30 days = last 60-day daily avg × 30 × trend
    const lb60 = now - 60 * 86400000;
    let lbSum = 0, lbDays = 0;
    for (const d of daily) {
      if (new Date(d.date).getTime() >= lb60) { lbSum += d.revenue; lbDays++; }
    }
    const effLb = Math.max(1, Math.min(60, lbDays));
    const projected30d = clientForecastEnabled
      ? (lbSum / effLb) * 30 * (1 + growthRate * 0.5)
      : 0;

    // Forecast horizon ~= half the data span, clamped
    const forecastHorizon = clientForecastEnabled
      ? Math.max(7, Math.min(180, Math.round(dataSpanDays / 2)))
      : 0;

    let combined = daily.map(d => ({ ...d, forecast: null }));
    if (clientForecastEnabled && daily.length >= 2) {
      // Mirror the last N days forward with trend applied
      const srcTail = daily.slice(Math.max(0, daily.length - forecastHorizon)).map(d => d.revenue);
      for (let i = 1; i <= forecastHorizon; i++) {
        const d = new Date(now); d.setDate(d.getDate() + i);
        const src = srcTail.length > 0 ? srcTail[(i - 1) % srcTail.length] : 0;
        const trend = 1 + growthRate * (i / forecastHorizon);
        combined.push({ date: d.toISOString().slice(0, 10), revenue: null, forecast: +(src * trend).toFixed(2) });
      }
      // Stitch last actual → first forecast so the line is continuous
      for (let i = combined.length - 1; i >= 0; i--) {
        if (combined[i].revenue != null) { combined[i].forecast = combined[i].revenue; break; }
      }
    }

    // Bucket to weekly/monthly when span is long — same rules as main analytics
    const granularity = dataSpanDays > 180 ? "monthly" : dataSpanDays > 30 ? "weekly" : "daily";
    const todayKey = new Date(now).toISOString().slice(0, 10);
    if (granularity !== "daily") {
      const bucketKey = granularity === "weekly"
        ? (d) => {
            const dt = new Date(d + "T00:00:00");
            const diff = (dt.getUTCDay() + 6) % 7;
            dt.setUTCDate(dt.getUTCDate() - diff);
            return dt.toISOString().slice(0, 10);
          }
        : (d) => d.slice(0, 7) + "-01";
      const tb = bucketKey(todayKey);
      const buckets = new Map();
      for (const d of combined) {
        const k = bucketKey(d.date);
        if (!buckets.has(k)) buckets.set(k, { date: k, revenue: 0, forecast: 0, hasActual: false, hasForecast: false });
        const row = buckets.get(k);
        if (d.revenue != null) { row.revenue += d.revenue; row.hasActual = true; }
        if (d.forecast != null) { row.forecast += d.forecast; row.hasForecast = true; }
      }
      if (buckets.has(tb) && buckets.get(tb).hasActual) {
        const row = buckets.get(tb);
        row.forecast = 0; row.hasForecast = false;
      }
      combined = Array.from(buckets.values()).sort((a, b) => a.date.localeCompare(b.date)).map(b => ({
        date: b.date,
        revenue: b.hasActual ? b.revenue : null,
        forecast: b.hasForecast ? b.forecast : null,
      }));
      // Stitch
      const lastIdx = combined.findIndex((w, i, arr) => w.revenue != null && (arr[i + 1]?.revenue == null));
      if (lastIdx >= 0 && combined[lastIdx].forecast == null) combined[lastIdx].forecast = combined[lastIdx].revenue;
    }

    return { daily: combined, projected30d, growthRate, granularity, dataSpanDays, hasForecast: clientForecastEnabled && daily.length >= 2 };
  }, [selectedClient, clientForecastEnabled]);

  /* ── Page titles + subtitles ── */
  const pageInfo = {
    orders: { title: "Production Planning", subtitle: "Manage incoming Shopify orders and tag for production." },
    production: { title: "Production", subtitle: "Plan roast batches from tagged orders." },
    analytics: { title: "Analytics", subtitle: "Production volume across products — recorded when orders are marked ready." },
    cupping: { title: "Cupping & QC", subtitle: "SCA-style scorecards, per-coffee history, quality trends over time." },
    inventory: { title: "Inventory", subtitle: "Track green and roasted stock levels." },
    products: { title: "Products", subtitle: "Coffee catalog with roast profiles." },
    settings: { title: "Settings", subtitle: "Manage integrations and sync preferences." },
    wholesale: { title: "Wholesale", subtitle: "Build your B2B catalog and public storefront." },
    sell: { title: "Invoicing", subtitle: "Customer invoices, statements, and receivables." },
    billing: { title: "Billing", subtitle: "Invoices, purchase orders, and payment tracking." },
  };

  /* ═══════════════════════════════════════════
     SECONDARY PANEL — per-page nav items
     Matches reference: title row + icon+label list
     ═══════════════════════════════════════════ */
  const renderPanel = () => {
    const panelTitle = (title) => (
      <div style={{ padding: "10px 16px 8px" }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: "#18181B", letterSpacing: "-0.01em" }}>{title}</span>
      </div>
    );

    switch (page) {
      case "orders": return (
        <div style={{ padding: "0 8px" }}>
          {panelTitle("Production Planning")}
          {/* All orders (formerly "All days") + each production day.
              "+" button adds a new day inline. */}
          {(() => {
            const dayCounts = {};
            for (const o of tagged) {
              const id = dayIdForOrder(o);
              if (id) dayCounts[id] = (dayCounts[id] || 0) + 1;
            }
            return (
              <>
                <PanelNavItem
                  icon={LayoutGrid}
                  label="All orders"
                  badge={tagged.length}
                  active={!activeDayId}
                  onClick={() => {
                    setOrderFilter("all");
                    setSelectedClientId(null);
                    setActiveDayId("");
                  }}
                />
                {productionDays.map(d => {
                  const isActive = activeDayId === d.id;
                  const locked = dayHasRoastingStarted(d.id);
                  return (
                    <DayNavRow
                      key={d.id}
                      label={d.label}
                      count={dayCounts[d.id] || 0}
                      active={isActive}
                      locked={locked}
                      canDelete={productionDays.length > 1 && !locked}
                      onClick={() => {
                        setSelectedClientId(null);
                        setActiveDayId(d.id);
                      }}
                      onRename={() => {
                        const v = window.prompt("Rename this day", d.label);
                        if (v != null && v.trim()) renameProductionDay(d.id, v.trim());
                      }}
                      onDelete={() => {
                        const count = tagged.filter(o => dayIdForOrder(o) === d.id).length;
                        const msg = count > 0
                          ? `Cancel "${d.label}"? Its ${count} order${count === 1 ? "" : "s"} will be unscheduled (production tag removed).`
                          : `Delete "${d.label}"?`;
                        if (window.confirm(msg)) removeProductionDay(d.id);
                      }}
                    />
                  );
                })}
                <PanelNavItem
                  icon={Plus}
                  label="Add day"
                  active={false}
                  onClick={() => {
                    const label = window.prompt("Name this day", `Day ${productionDays.length + 1}`);
                    if (label && label.trim()) {
                      const id = addProductionDay(label.trim());
                      setSelectedClientId(null);
                      setActiveDayId(id);
                    }
                  }}
                />
              </>
            );
          })()}
          {orderFilter === "clients" && (
            <div style={{ padding: "8px 14px", marginTop: 8 }}>
              {[
                { l: "Clients", v: clientsList.length },
                { l: "Total revenue", v: `$${Math.round(clientsList.reduce((s, c) => s + c.totalRevenue, 0)).toLocaleString()}` },
                { l: "Avg per client", v: clientsList.length ? `$${Math.round(clientsList.reduce((s, c) => s + c.totalRevenue, 0) / clientsList.length).toLocaleString()}` : "—" },
              ].map(s => (
                <div key={s.l} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                  <span style={{ fontSize: 15, color: "#6B7280" }}>{s.l}</span>
                  <span style={{ fontSize: 15, fontWeight: 600, color: "#1F2937" }}>{s.v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
      case "production": {
        /* Each Production section has its own two-item sub-nav: the
           section's main workspace plus an Activity dashboard scoped to
           that section. Only the current section's items appear — the
           black rail handles cross-section navigation. */
        const sectionMeta = {
          breakdown: { icon: Flame,         label: "Roast Batches", panelTitle: "Roasting" },
          packing:   { icon: ClipboardList, label: "Packing",       panelTitle: "Packing" },
          routes:    { icon: Truck,         label: "Route",         panelTitle: "Ship" },
        };
        const meta = sectionMeta[prodView] || sectionMeta.breakdown;
        return (
        <div style={{ padding: "0 8px" }}>
          {panelTitle(meta.panelTitle)}
          {/* Planning sub-tab — only on the Roasting section */}
          {prodView === "breakdown" && (
            <PanelNavItem icon={ScrollText} label="Planning" active={prodTab === "planning"} onClick={() => setProdTab("planning")} />
          )}
          {/* Live roast workspace — the roaster-side view (live curve,
              on-deck batch, batch queue). Was unreachable from the nav. */}
          {prodView === "breakdown" && (
            <PanelNavItem icon={Radio} label="Live roast" active={prodTab === "main"} onClick={() => setProdTab("main")} />
          )}
          {/* Ship section: just the Route workspace. */}
          {prodView === "routes" && (
            <PanelNavItem icon={Truck} label="Route" active={prodTab === "main"} onClick={() => setProdTab("main")} />
          )}
          {/* Other sections keep the legacy single sub-nav row */}
          {prodView !== "breakdown" && prodView !== "routes" && (
            <PanelNavItem icon={meta.icon} label={meta.label} active={prodTab === "main"} onClick={() => setProdTab("main")} />
          )}
          {cap("prod:activity") && (
            <PanelNavItem icon={Activity} label="Activity" active={prodTab === "activity"} onClick={() => setProdTab("activity")} />
          )}
          {/* Production days — read-only mirror of the Orders sidebar.
              Source-of-truth lives in Orders → Planning; Roasting and
              Packing both surface the same days here so a shift lead
              can scope the page without context-switching. */}
          {(prodView === "breakdown" || prodView === "packing" || prodView === "routes") && productionDays.length > 0 && (() => {
            const dayCounts = {};
            for (const o of (tagged || [])) {
              const id = dayIdForOrder(o);
              if (id) dayCounts[id] = (dayCounts[id] || 0) + 1;
            }
            return (
              <>
                <div style={{ height: 1, background: "#E4E4E7", margin: "8px 14px" }} />
                <div style={{ padding: "12px 14px 4px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                    Production days
                  </div>
                </div>
                <PanelNavItem
                  icon={LayoutGrid}
                  label="All days"
                  badge={tagged.length || undefined}
                  active={!activeDayId}
                  onClick={() => setActiveDayId("")}
                />
                {productionDays.map(d => (
                  <PanelNavItem
                    key={d.id}
                    icon={Calendar}
                    label={d.label}
                    badge={dayCounts[d.id] || undefined}
                    active={activeDayId === d.id}
                    onClick={() => setActiveDayId(d.id)}
                  />
                ))}
              </>
            );
          })()}
          {/* Hairline separator between the production-day rows and
              the live stats rollup below. Makes the two zones visually
              distinct in Roasting / Packing / Route panels. */}
          <div style={{ height: 1, background: "#E4E4E7", margin: "8px 14px" }} />
          <div style={{ padding: "8px 14px" }}>
            {(() => {
              let rows;
              if (prodTab === "activity") {
                /* Activity stats are scoped by prodView — roasting stats
                   on the Roasting section, packing on the Packing section,
                   routes on the Routes section. */
                const actWindow = {
                  "today": 24 * 60 * 60 * 1000,
                  "7d":    7 * 24 * 60 * 60 * 1000,
                  "30d":   30 * 24 * 60 * 60 * 1000,
                  "90d":   90 * 24 * 60 * 60 * 1000,
                  "180d":  180 * 24 * 60 * 60 * 1000,
                }[activityDateFilter];
                const actCutoff = actWindow != null ? Date.now() - actWindow : 0;
                const actOrders = (producedOrders || []).filter(o => (o.producedAt || 0) >= actCutoff);
                const actLbs = actOrders.reduce((s, o) => s + (o.items || []).reduce((a, i) => a + (i.qty || 0), 0), 0);
                const actUnits = actOrders.reduce((s, o) => s + (o.items || []).reduce((a, i) => a + (i.qty || 0), 0), 0);
                const labels = { "today": "today", "7d": "last 7d", "30d": "last 30d", "90d": "last 90d", "180d": "last 6mo", "all": "all time" };
                if (prodView === "packing") {
                  rows = [
                    { l: "Window", v: labels[activityDateFilter] || activityDateFilter },
                    { l: "Orders packed", v: actOrders.length },
                    { l: "Units packed", v: Math.round(actUnits).toLocaleString() },
                  ];
                } else if (prodView === "routes") {
                  rows = [
                    { l: "Window", v: labels[activityDateFilter] || activityDateFilter },
                    { l: "Deliveries", v: actOrders.length },
                  ];
                } else {
                  rows = [
                    { l: "Window", v: labels[activityDateFilter] || activityDateFilter },
                    { l: "Roasts", v: actOrders.length },
                    { l: "Roasted", v: <UnitWeight valueLbs={actLbs} decimals={0} /> },
                  ];
                }
              } else if (prodView === "packing") {
                rows = [
                  { l: "Units total", v: packingTotalQty },
                  { l: "Orders", v: tagged.length },
                  { l: "Est. packing", v: formatDuration(packingTimeSec) },
                ];
              } else if (prodView === "routes") {
                // Mirror the actual Route feed (only unfulfilled-and-not-
                // yet-shipped orders), not the full producedListForDisplay
                // — that count balloons because of legacy "produced"
                // entries for orders that shipped outside Roaster.
                rows = [
                  { l: "Stops in route", v: routeStopKeys.length },
                  { l: "Ready to deliver", v: routeReadyOrders.length },
                ];
              } else {
                const totalRoasted = plan.reduce((s, p) => s + p.roastedLbs, 0);
                const totalGreen   = plan.reduce((s, p) => s + p.greenLbs, 0);
                const totalBatches = plan.reduce((s, p) => s + p.batches, 0);
                rows = [
                  { l: "Orders", v: tagged.length },
                  { l: "Roasted", v: <UnitWeight valueLbs={totalRoasted} decimals={0} /> },
                  { l: "Green",   v: <UnitWeight valueLbs={totalGreen}   decimals={0} /> },
                  { l: "Batches", v: totalBatches },
                  // Throughput-based estimate: green pounds ÷ lbPerHour.
                  // Falls back to the per-batch flat estimate when we
                  // don't have green pounds (shouldn't happen).
                  { l: "Est. roasting", v: totalGreen > 0
                      ? formatDuration((totalGreen / lbPerHour) * 3600)
                      : formatDuration(totalBatches * minutesPerBatch * 60) },
                  { l: "Green Bags", v: Math.ceil(totalGreen / 154) },
                ];
              }
              return rows.map(s => (
                <div key={s.l} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                  <span style={{ fontSize: 15, color: "#6B7280" }}>{s.l}</span>
                  <span style={{ fontSize: 15, fontWeight: 600, color: "#1F2937" }}>{s.v}</span>
                </div>
              ));
            })()}
          </div>
        </div>
        );
      }
      case "analytics": return (
        <div style={{ padding: "0 8px" }}>
          {panelTitle("Analytics")}
          <PanelNavItem icon={TrendingUp} label="Profitability" active={analyticsView === "profitability"} onClick={() => setAnalyticsView("profitability")} />
          <PanelNavItem icon={BarChart3} label="Produced by product" active={analyticsView === "produced"} onClick={() => setAnalyticsView("produced")} />
          <div style={{ padding: "8px 14px" }}>
            {analyticsView === "profitability" ? [
              { l: "Revenue", v: `$${Math.round(profitabilityData.totalRevenue).toLocaleString()}` },
              { l: "Est. cost", v: `$${Math.round(profitabilityData.totalCost).toLocaleString()}` },
              { l: "Margin", v: `${profitabilityData.marginPct.toFixed(1)}%` },
              { l: "Orders", v: profitabilityData.orderCount },
            ].map(s => (
              <div key={s.l} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                <span style={{ fontSize: 15, color: "#6B7280" }}>{s.l}</span>
                <span style={{ fontSize: 15, fontWeight: 600, color: "#1F2937" }}>{s.v}</span>
              </div>
            )) : [
              { l: "Ready", v: producedOrders.length },
              { l: "Unique products", v: productionByProduct.length },
              { l: "Units total", v: productionByProduct.reduce((s, p) => s + p.qty, 0) },
            ].map(s => (
              <div key={s.l} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                <span style={{ fontSize: 15, color: "#6B7280" }}>{s.l}</span>
                <span style={{ fontSize: 15, fontWeight: 600, color: "#1F2937" }}>{s.v}</span>
              </div>
            ))}
          </div>
        </div>
      );
      case "cupping": {
        const filtered = cuppingStatusFilter === "all"
          ? cuppings
          : cuppings.filter(c => (cuppingStatusFilter === "pending" ? (!c.status || c.status === "pending") : c.status === cuppingStatusFilter));
        const sessionTitle = (s) => s.name || s.coffeeName || "Cupping session";
        const sessionStatusColor = (s) => {
          const st = s.status || "pending";
          if (st === "approved") return "#16A34A";
          if (st === "rejected") return "#DC2626";
          return "#EA580C";
        };
        return (
          <div style={{ padding: "0 8px" }}>
            {panelTitle("Cupping & QC")}
            {cap("cupping:write") && (
              <PanelCTA label="New cupping" onClick={() => setAddCuppingOpen(true)} />
            )}
            {cap("cupping:write") && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, margin: "0 4px 12px" }}>
                <button
                  onClick={() => setAddSampleMode("single")}
                  style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                  title="Intake one sample"
                ><Plus size={12} strokeWidth={2.2} /> Add sample</button>
                <button
                  onClick={() => setAddSampleMode("bulk")}
                  style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000", fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                  title="Bulk-intake several samples"
                ><Plus size={12} strokeWidth={2.2} /> Bulk add</button>
              </div>
            )}
            <PanelNavItem icon={Droplets} label="All sessions" active={!selectedCuppingId && cuppingView === "sessions"} onClick={() => { setCuppingStatusFilter("all"); setSelectedCuppingId(null); setCuppingView("sessions"); }} />
            <PanelNavItem icon={Building2} label="Green Vendors" active={cuppingView === "sources"} onClick={() => { setCuppingView("sources"); setSelectedCuppingId(null); }} />
            {/* Cuppers moved to Settings → Team — same admin context as
                user-roster management. Keep the route alive so any old
                deep-links still work, but it's not on the panel. */}
            <PanelNavItem icon={Coffee} label="Samples" active={cuppingView === "coffees"} onClick={() => { setCuppingView("coffees"); setSelectedCuppingId(null); }} />

            {/* ── Sessions list ───────────────────────────────────────
                One row per session. Click → main view switches to the
                session detail page. Hover reveals rename/delete. */}
            {cuppings.length > 0 && (
              <>
                <div style={{
                  padding: "12px 14px 4px",
                  fontSize: 11, fontWeight: 700, color: "#9CA3AF",
                  textTransform: "uppercase", letterSpacing: "0.07em",
                }}>
                  Sessions
                </div>
                {cuppings.map(s => (
                  <SessionNavRow
                    key={s.id}
                    label={sessionTitle(s)}
                    count={(s.coffees || []).length}
                    sessionCode={getSessionCode(s)}
                    active={selectedCuppingId === s.id}
                    onClick={() => { setCuppingView("sessions"); setSelectedCuppingId(s.id); }}
                    onRename={() => {
                      const v = window.prompt("Rename this session", sessionTitle(s));
                      if (v != null && v.trim()) {
                        updateCuppingMeta(s.id, { name: v.trim() }).catch(err => alert(err.message || "Couldn't rename"));
                      }
                    }}
                    onDelete={() => {
                      if (window.confirm(`Delete "${sessionTitle(s)}"? This can't be undone.`)) {
                        deleteCupping(s.id);
                        if (selectedCuppingId === s.id) setSelectedCuppingId(null);
                      }
                    }}
                  />
                ))}
              </>
            )}

          </div>
        );
      }
      case "purchasing": {
        // Finance is now buy-side only — the sell side (Customers /
        // Invoices / Due) moved to its own "Invoicing" main tab.
        // Coerce any persisted sell-side view to a buy-side default so
        // the Finance page never renders a view it has no nav for.
        const BUY_VIEWS = new Set(["vendors", "contracts", "pos", "recurring"]);
        const pv = BUY_VIEWS.has(purchasingView) ? purchasingView : "pos";
        if (pv !== purchasingView) setPurchasingView(pv);
        const newCta = pv === "contracts"
          ? { label: "New contract", kind: "contract" }
          : pv === "recurring"
            ? { label: "New recurring rule", kind: "recurring" }
            : pv === "vendors"
              ? { label: "New vendor", kind: "vendor" }
              : { label: "New PO", kind: "po" };
        return (
          <div style={{ padding: "0 8px" }}>
            {panelTitle("Purchasing")}
            <PanelCTA icon={newCta.icon} label={newCta.label} onClick={() => triggerPurchasingNew(newCta.kind)} />
            <div style={{ padding: "10px 14px 4px", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em" }}>Buy side</div>
            <PanelNavItem icon={Building2} label="Vendors" active={pv === "vendors"} onClick={() => setPurchasingView("vendors")} />
            <PanelNavItem icon={Handshake} label="Contracts" active={pv === "contracts"} onClick={() => setPurchasingView("contracts")} />
            <PanelNavItem icon={ClipboardList} label="Purchase orders" active={pv === "pos"} onClick={() => setPurchasingView("pos")} />
            <PanelNavItem icon={Repeat} label="Recurring" active={pv === "recurring"} onClick={() => setPurchasingView("recurring")} />
          </div>
        );
      }
      case "sell": {
        // Sell-side / Invoicing — receivables management. Same
        // PurchasingPage component, sell-only views.
        const SELL_VIEWS = new Set(["clients", "invoices", "due"]);
        const sv = SELL_VIEWS.has(sellView) ? sellView : "clients";
        if (sv !== sellView) setSellView(sv);
        const showCta = sv !== "due";
        const newCta = sv === "invoices"
          ? { label: "New invoice", kind: "invoice" }
          : { label: "Import from Shopify", kind: "client-import", icon: Download };
        return (
          <div style={{ padding: "0 8px" }}>
            {panelTitle("Invoicing")}
            {showCta && <PanelCTA icon={newCta.icon} label={newCta.label} onClick={() => triggerPurchasingNew(newCta.kind)} />}
            <PanelNavItem icon={Users} label="Customers" active={sv === "clients"} onClick={() => setSellView("clients")} />
            <PanelNavItem icon={FileText} label="Invoices" active={sv === "invoices"} onClick={() => setSellView("invoices")} />
            <PanelNavItem icon={AlertCircle} label="Due invoices" active={sv === "due"} onClick={() => setSellView("due")} />
          </div>
        );
      }
      case "inventory": {
        const activeLots = greenLots.filter(l => !l.archivedAt);
        const totalGreenLbs = activeLots.reduce((s, l) => s + (l.currentWeightLbs || 0), 0);
        const lowStockCount = activeLots.filter(l => l.currentWeightLbs <= (l.lowStockLbs || 20)).length;
        const agingCount = activeLots.filter(l => l.ageDays >= greenAgingDays).length;
        return (
          <div style={{ padding: "0 8px" }}>
            {panelTitle("Inventory")}
            {cap("inventory:write") && (
              <PanelCTA label="Add source" onClick={() => setAddLotOpen(true)} />
            )}
            {/* Three sections: Green coffee → Supplies → Roasting.
                Tighter top spacing on the first section so it sits
                right under the CTA — subsequent sections are split by
                a divider for breathing room. */}
            <div style={{ padding: "2px 14px 4px", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em" }}>Green coffee</div>
            <PanelNavItem icon={Warehouse} label="Overview" active={inventoryView === "lots"} onClick={() => { setInventoryView("lots"); setInventoryFilter("all"); }} />
            <PanelNavItem icon={ClipboardList} label="Receiving" active={inventoryView === "docs"} onClick={() => setInventoryView("docs")} />
            <PanelNavItem icon={ScrollText} label="Receiving log" active={inventoryView === "receiving"} onClick={() => setInventoryView("receiving")} />
            <div style={{ height: 1, background: "#E4E4E7", margin: "8px 14px" }} />
            <div style={{ padding: "2px 14px 4px", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em" }}>Supplies</div>
            <PanelNavItem icon={Package} label="Supplies" active={inventoryView === "supplies"} onClick={() => setInventoryView("supplies")} badge={supplies.filter(s => (s.onHand || 0) <= (s.reorderAt || 0)).length || undefined} />
            <PanelNavItem icon={ScrollText} label="Supplies log" active={inventoryView === "supplies-log"} onClick={() => setInventoryView("supplies-log")} />
            <div style={{ height: 1, background: "#E4E4E7", margin: "8px 14px" }} />
            <div style={{ padding: "2px 14px 4px", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em" }}>Roasting</div>
            <PanelNavItem icon={Layers} label="Green inventory" active={inventoryView === "lots"} onClick={() => { setInventoryView("lots"); setInventoryFilter("all"); }} />
            <PanelNavItem icon={Flame} label="Roasting log" active={inventoryView === "batches"} onClick={() => setInventoryView("batches")} />
              <div style={{ padding: "8px 14px" }}>
              {[
                { l: "Green available", v: <UnitWeight valueLbs={totalGreenLbs} decimals={0} /> },
                { l: "Sources", v: activeLots.length },
                { l: "Low stock", v: lowStockCount },
                { l: `Aging ${greenAgingDays}d+`, v: agingCount },
              ].map(s => (
                <div key={s.l} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                  <span style={{ fontSize: 15, color: "#6B7280" }}>{s.l}</span>
                  <span style={{ fontSize: 15, fontWeight: 600, color: "#1F2937" }}>{s.v}</span>
                </div>
              ))}
            </div>
          </div>
        );
      }
      case "products": {
        const ctaLabel = coffeesView === "green" ? null : "New coffee";
        return (
          <div style={{ padding: "0 8px" }}>
            {panelTitle("Coffees & Blends")}
            {cap("page:products") && ctaLabel && (
              <PanelCTA label={ctaLabel} onClick={() => setAddCoffeeOpen(true)} />
            )}
            <PanelNavItem
              icon={Layers}
              label="Green coffee"
              active={coffeesView === "green"}
              badge={greenCoffees.filter(g => !g.archivedAt).length || undefined}
              onClick={() => { setCoffeesView("green"); setSelectedProduct(null); }}
            />
            <PanelNavItem
              icon={Coffee}
              label="Products"
              active={coffeesView === "products"}
              onClick={() => { setCoffeesView("products"); setSelectedProduct(null); }}
            />
          </div>
        );
      }
      case "wholesale": return (
        <div style={{ padding: "0 8px" }}>
          {panelTitle("Wholesale")}
          <PanelCTA label="Add product" onClick={() => setWholesaleProductModal("new")} />
          {wholesaleConfig?.slug && (
            <a
              href={`${window.location.origin}/shop/${wholesaleConfig.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "7px 10px", borderRadius: 8, marginBottom: 2,
                fontSize: 13, fontWeight: 500, color: "#374151",
                textDecoration: "none", fontFamily: "Inter, sans-serif",
                border: "1px solid #E5E7EB", background: "#F9FAFB",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#F3F4F6"}
              onMouseLeave={e => e.currentTarget.style.background = "#F9FAFB"}
            >
              <ExternalLink size={13} color="#6B7280" />
              Preview site
            </a>
          )}
          <PanelNavItem icon={Store} label="Store settings" active={wholesaleTab === "store"} onClick={() => setWholesaleTab("store")} />
          <PanelNavItem icon={Package} label="Products" active={wholesaleTab === "products"} onClick={() => setWholesaleTab("products")} badge={wholesaleProducts.length || undefined} />
          <PanelNavItem icon={Users} label="Customers" active={wholesaleTab === "customers"} onClick={() => setWholesaleTab("customers")} badge={wholesaleCustomers.length || undefined} />
          <PanelNavItem icon={ClipboardList} label="Orders" active={wholesaleTab === "orders"} onClick={() => setWholesaleTab("orders")} badge={(wholesaleOrders || []).filter(o => o.status !== "rejected").length || undefined} />
          {shopConnected && (
            <PanelNavItem icon={ShoppingBag} label="Import from Shopify" active={wholesaleTab === "import"} onClick={() => setWholesaleTab("import")} />
          )}
        </div>
      );
      case "settings": {
        const sFeats = effectiveUsage?.features || [];
        const sFeat  = (f) => sFeats.includes("*") || sFeats.includes(f);
        return (
          <div style={{ padding: "0 8px" }}>
            {panelTitle("Settings")}
            {!effectiveDemoMode && (
              <PanelNavItem icon={CreditCard} label="Plan & Billing" active={settingsSection === "billing"} onClick={() => setSettingsSection("billing")} />
            )}
            <PanelNavItem icon={ImageIcon} label="Branding" active={settingsSection === "branding"} onClick={() => setSettingsSection("branding")} />
            <PanelNavItem icon={Mail} label="Email" active={settingsSection === "email"} onClick={() => setSettingsSection("email")} />
            <PanelNavItem icon={Link} label="Store Connect" active={settingsSection === "integrations"} onClick={() => setSettingsSection("integrations")} />
            {sFeat("live_roast") && <PanelNavItem icon={Cpu} label="Devices" active={settingsSection === "devices"} onClick={() => setSettingsSection("devices")} />}
            {sFeat("live_roast") && <PanelNavItem icon={Flame} label="Roasting" active={settingsSection === "roasting"} onClick={() => setSettingsSection("roasting")} />}
            {sFeat("green_inventory") && <PanelNavItem icon={Warehouse} label="Inventory" active={settingsSection === "inventorySettings"} onClick={() => setSettingsSection("inventorySettings")} />}
            <PanelNavItem icon={DollarSign} label="Costs" active={settingsSection === "costs"} onClick={() => setSettingsSection("costs")} />
            <PanelNavItem icon={FileText} label="Finance" active={settingsSection === "finance"} onClick={() => setSettingsSection("finance")} />
            <PanelNavItem icon={Users} label="Team" active={settingsSection === "team"} onClick={() => setSettingsSection("team")} />
            {sFeat("analytics") && <PanelNavItem icon={ClipboardList} label="Reports" active={settingsSection === "reports"} onClick={() => setSettingsSection("reports")} />}
            <PanelNavItem icon={Truck} label="Shipping" active={settingsSection === "shipping"} onClick={() => setSettingsSection("shipping")} />
            <PanelNavItem icon={Settings} label="Preferences" active={settingsSection === "preferences"} onClick={() => setSettingsSection("preferences")} />
            <PanelNavItem icon={HelpCircle} label="Support" rightIcon={ExternalLink} onClick={() => setSettingsSection("support")} active={settingsSection === "support"} />
          </div>
        );
      }
      case "billing": {
        const overdueCnt = billingInvoices.filter(i => i.status === "overdue").length;
        const unpaidCnt = billingInvoices.filter(i => ["sent","overdue"].includes(i.status)).length;
        return (
          <div style={{ padding: "0 8px" }}>
            {panelTitle("Billing")}
            <button
              onClick={() => setInvoiceModal("new")}
              style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%",
                margin: "0 0 10px", padding: "9px 14px",
                background: "#111827", color: "#fff", border: "none", borderRadius: 8,
                fontSize: 13, fontWeight: 600, cursor: "pointer", letterSpacing: "0.01em",
              }}
            >
              <FilePlus size={14} />
              New invoice
            </button>
            <div style={{ height: 1, background: "#E4E4E7", margin: "0 6px 8px" }} />
            <PanelNavItem icon={FileText} label="All invoices" active={billingView === "invoices" && billingFilter === "all"} onClick={() => { setBillingView("invoices"); setBillingFilter("all"); }} badge={billingInvoices.length || undefined} />
            <PanelNavItem icon={AlertCircle} label="Overdue" active={billingView === "invoices" && billingFilter === "overdue"} onClick={() => { setBillingView("invoices"); setBillingFilter("overdue"); }} badge={overdueCnt || undefined} />
            <PanelNavItem icon={Clock} label="Unpaid" active={billingView === "invoices" && billingFilter === "unpaid"} onClick={() => { setBillingView("invoices"); setBillingFilter("unpaid"); }} badge={unpaidCnt || undefined} />
            <PanelNavItem icon={BadgeCheck} label="Paid" active={billingView === "invoices" && billingFilter === "paid"} onClick={() => { setBillingView("invoices"); setBillingFilter("paid"); }} />
            <div style={{ height: 1, background: "#E4E4E7", margin: "8px 6px" }} />
            <button
              onClick={() => setPOModal("new")}
              style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%",
                margin: "0 0 8px", padding: "9px 14px",
                background: "transparent", color: "#374151", border: "1px solid #E5E7EB", borderRadius: 8,
                fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}
            >
              <Package size={14} />
              New PO
            </button>
            <PanelNavItem icon={Package} label="Purchase orders" active={billingView === "pos"} onClick={() => setBillingView("pos")} badge={billingPOs.length || undefined} />
            <div style={{ height: 1, background: "#E4E4E7", margin: "8px 6px" }} />
            <PanelNavItem icon={Building2} label="Business info" active={billingView === "settings"} onClick={() => setBillingView("settings")} />
          </div>
        );
      }
      default: return null;
    }
  };

  /* ═══════════════════════════════════════════
     MAIN CONTENT AREA
     ═══════════════════════════════════════════ */
  const renderContent = () => {
    const info = pageInfo[page] || {};
    /* Settings has a sub-nav; swap the header's subtitle to match the
       active section so the page label never lies about what's on screen. */
    const settingsHeader = {
      billing:      { title: "Plan & Billing", subtitle: "Manage your plan, view usage, and update payment." },
      integrations: { title: "Store Connect", subtitle: "Connect the services that feed Roaster." },
      devices:      { title: "Devices",       subtitle: "Pair a Elion bridge to stream live roast data from your Loring or Probat." },
      roasting:          { title: "Roasting",   subtitle: "Configure roasting display and calculation preferences." },
      inventorySettings: { title: "Inventory",  subtitle: "Green coffee aging thresholds and inventory defaults." },
      team:         { title: "Team",          subtitle: "Invite people, manage roles and access." },
      reports:      { title: "Reports",       subtitle: "Automated emails and production summaries." },
      shipping:     { title: "Shipping",      subtitle: "Pick a label provider and set up your return address." },
      preferences:  { title: "Preferences",   subtitle: "How Roaster behaves day-to-day." },
      support:      { title: "Support",       subtitle: "Reach us when you need a hand." },
    };
    /* Production has three sections (Roasting / Packing / Routes) each
       with a main view and an Activity dashboard. Header tracks both
       prodView and prodTab so the page title always matches what's
       actually on screen. Activity sub-pages skip the swap — the
       Activity view renders its own header. */
    const productionHeader = {
      breakdown: { title: "Roasting", subtitle: "Plan roast batches from tagged orders." },
      packing:   { title: "Packing",  subtitle: "Combined pack breakdown across all scheduled orders." },
      routes:    { title: "Routes",   subtitle: "Plan today's deliveries from the Ready queue." },
    };
    const effectiveInfo = page === "settings"
      ? (settingsHeader[settingsSection] || info)
      : page === "production" && prodTab !== "activity"
        ? (productionHeader[prodView] || info)
        : info;

    const graceDays = effectiveUsage?.importGraceDaysLeft ?? 0;
    const header = (
      <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: "#000000", margin: 0, lineHeight: "28px" }}>{effectiveInfo.title}</h1>
          <p style={{ fontSize: 16, color: "#6B7280", margin: "2px 0 0", lineHeight: "20px" }}>{effectiveInfo.subtitle}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, marginTop: 2 }}>
          <NotificationsBell />
        </div>
      </div>
    );

    /* Upgrade wall — when the user clicks a locked nav item, replace
       the page body with the per-module benefits + upgrade CTA. The
       header bar stays for context. Cleared by clicking any unlocked
       nav item. */
    if (upgradeModule) {
      return <UpgradeWall
        moduleKey={upgradeModule}
        onDismiss={() => setUpgradeModule(null)}
        companyName={billingSettings?.companyName || branding?.companyName || null}
      />;
    }

    switch (page) {
      case "orders": {
        if (orderFilter === "clients") {
          return <ClientsView
            clients={clientsList}
            selectedClient={selectedClient}
            setSelectedClientId={setSelectedClientId}
            analytics={clientAnalytics}
            forecastEnabled={clientForecastEnabled}
            setForecastEnabled={setClientForecastEnabled}
            connectedShop={connectedShop}
            shopConnected={shopConnected}
            clientSearch={clientSearch}
            setClientSearch={setClientSearch}
          />;
        }
        return (
        <div>
          {header}
          {/* ── Status filter pills ── */}
          {(orderFilter !== "clients" && orderFilter !== "produced") && (
            <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
              {[
                { label: "All", value: "all" },
                { label: "Scheduled", value: "production" },
                { label: "Skipped", value: "skipped" },
              ].map(f => (
                <button
                  key={f.value}
                  onClick={() => { setOrderFilter(f.value); setSelectedClientId(null); }}
                  style={{
                    padding: "7px 14px", borderRadius: 8,
                    border: `1px solid ${orderFilter === f.value ? "#000000" : "#E5E7EB"}`,
                    background: orderFilter === f.value ? "#000000" : "#fff",
                    color: orderFilter === f.value ? "#fff" : "#374151",
                    fontSize: 14, fontWeight: 500, cursor: "pointer",
                    fontFamily: "Inter, sans-serif", transition: "all 0.1s",
                  }}
                >{f.label}</button>
              ))}
            </div>
          )}
          {/* ── Channel filter pills ──
              Only render channels that actually exist in the current
              order set, gated by what's configured (Wholesale requires
              the addon; Shopify requires a connected store). And only
              show the row at all when there's more than one channel to
              choose between. */}
          {(orderFilter !== "clients" && orderFilter !== "produced") && (() => {
            const presentChannels = new Set(visibleOrders.map(deriveOrderChannel).filter(Boolean));
            const hasWholesaleAddon = (effectiveUsage?.addons?.wholesale_lite?.status === "active")
              || (effectiveUsage?.addons?.wholesale?.status === "active");
            const candidates = [
              { label: "Shopify",   value: "shopify",        show: shopConnected || presentChannels.has("shopify") },
              { label: "EDI",       value: "edi",            show: presentChannels.has("edi") },
              { label: "Transfer",  value: "transfer",       show: presentChannels.has("transfer") },
              { label: "Wholesale", value: "wholesale_lite", show: hasWholesaleAddon },
            ].filter(c => c.show);
            if (candidates.length < 2) return null;  // single channel → no filter row
            const items = [{ label: "All", value: "all" }, ...candidates];
            return (
              <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em", marginRight: 4 }}>Channel</span>
                {items.map(f => {
                  const active = orderChannelFilter === f.value;
                  const tint = f.value !== "all" ? CHANNEL_TINT[f.value] : null;
                  return (
                    <button
                      key={f.value}
                      onClick={() => setOrderChannelFilter(f.value)}
                      style={{
                        padding: "5px 11px", borderRadius: 99,
                        border: `1px solid ${active ? (tint?.fg || "#000") : "#E5E7EB"}`,
                        background: active ? (tint?.bg || "#000") : "#fff",
                        color: active ? (tint?.fg || "#fff") : "#3F3F46",
                        fontSize: 12, fontWeight: 600, cursor: "pointer",
                        fontFamily: "Inter, sans-serif", transition: "all 0.1s",
                      }}
                    >{f.label}</button>
                  );
                })}
              </div>
            );
          })()}
          {/* Production-day editing lives entirely in the Orders side-nav now. */}
          {!shopConnected && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "#FFFBEB", borderRadius: 8, border: "1px solid #FDE68A", marginBottom: 16 }}>
              <AlertTriangle size={16} color="#D97706" strokeWidth={1.6} />
              <span style={{ fontSize: 15, color: "#92400E", flex: 1 }}>Showing mock data. <button onClick={() => { setPage("settings"); setSettingsSection("integrations"); }} style={{ background: "none", border: "none", color: "#2563EB", cursor: "pointer", fontFamily: "Inter, sans-serif", fontSize: 15, fontWeight: 500, padding: 0, textDecoration: "underline" }}>Connect Shopify</button> to see real orders.</span>
            </div>
          )}
          {shopConnected && shopifyOrdersError && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 16px", background: "#FEF2F2", borderRadius: 8, border: "1px solid #FECACA", marginBottom: 16 }}>
              <AlertTriangle size={16} color="#DC2626" strokeWidth={1.6} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#991B1B", marginBottom: 2 }}>Couldn't load orders</div>
                <div style={{ fontSize: 14, color: "#B91C1C", wordBreak: "break-word" }}>{shopifyOrdersError}</div>
              </div>
            </div>
          )}
          {shopConnected && !shopifyOrdersError && !shopifyOrdersLoading && shopifyOrders.length === 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "#F9FAFB", borderRadius: 8, border: "1px solid #E5E7EB", marginBottom: 16 }}>
              <AlertTriangle size={16} color="#6B7280" strokeWidth={1.6} />
              <span style={{ fontSize: 15, color: "#374151" }}>Connected to {connectedShop}, but no orders returned. Shopify only returns orders from the last 60 days by default — older orders need the <code style={{ fontFamily: "Inter, sans-serif", fontSize: 14 }}>read_all_orders</code> scope (requires Shopify approval).</span>
            </div>
          )}
          {orderFilter === "skipped" && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "#F9FAFB", borderRadius: 8, border: "1px solid #E5E7EB", marginBottom: 16 }}>
              <SkipForward size={16} color="#6B7280" strokeWidth={1.6} />
              <span style={{ fontSize: 14, color: "#374151" }}>Skipped orders are automatically cleared every {skippedClearDays} day{skippedClearDays === 1 ? "" : "s"}. Change this in Settings.</span>
            </div>
          )}
          {orderFilter === "produced" && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: 13, color: "#6B7280" }}>Show:</span>
              <select
                value={producedDateFilter}
                onChange={e => setProducedDateFilter(e.target.value)}
                style={{
                  padding: "7px 12px", borderRadius: 8, border: "1px solid #E5E7EB",
                  background: "#fff", fontSize: 14, fontWeight: 500, color: "#000000",
                  fontFamily: "Inter, sans-serif", outline: "none", cursor: "pointer",
                }}
              >
                <option value="today">Today</option>
                <option value="7">Past 7 days</option>
                <option value="15">Past 15 days</option>
                <option value="30">Past 30 days</option>
                <option value="year">Past year</option>
                <option value="all">All time</option>
              </select>
              <span style={{ fontSize: 13, color: "#9CA3AF", marginLeft: "auto" }}>
                {producedListForDisplay.length} order{producedListForDisplay.length === 1 ? "" : "s"}
              </span>
            </div>
          )}
          {/* ── Roast plan preview (collapsed by default) ──────────────
               Same per-coffee rollup we show on the Roast Planning page,
               surfaced at the top of Orders so the user can sanity-check
               batches/bags/lbs without leaving this view. */}
          {(
            <div style={{
              background: "#FAFAFA", border: "1px solid #E5E7EB",
              borderRadius: 12, overflow: "hidden", marginBottom: 16,
            }}>
              {(() => {
                const totals = plan.reduce((a, p) => ({
                  batches: a.batches + (p.batches || 0),
                  bags:    a.bags    + Math.ceil((p.greenLbs || 0) / 154),
                  lb:      a.lb      + (p.roastedLbs || 0),
                }), { batches: 0, bags: 0, lb: 0 });
                return (
                  <>
                    <button
                      type="button"
                      onClick={() => setOrdersPlanOpen(o => !o)}
                      style={{
                        width: "100%", textAlign: "left",
                        padding: "12px 16px",
                        display: "flex", alignItems: "center", gap: 10,
                        background: "transparent", border: "none",
                        cursor: "pointer", fontFamily: "Inter, sans-serif",
                      }}
                    >
                      <span style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        color: "#9CA3AF",
                        transform: ordersPlanOpen ? "rotate(90deg)" : "rotate(0deg)",
                        transition: "transform 0.15s",
                      }}>
                        <ChevronRight size={14} strokeWidth={2} />
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Roast plan preview</span>
                      <span style={{ fontSize: 13, color: "#6B7280" }}>
                        · {plan.length} coffee{plan.length === 1 ? "" : "s"} · {totals.batches} batches · {totals.bags} green bags · {Math.ceil(totals.lb).toLocaleString()} lb roasted
                      </span>
                    </button>
                    {ordersPlanOpen && (
                      <div style={{ borderTop: "1px solid #E5E7EB" }}>
                        <div style={{
                          padding: "10px 20px", borderBottom: "1px solid #E5E7EB",
                          fontSize: 11, fontWeight: 700, color: "#9CA3AF",
                          textTransform: "uppercase", letterSpacing: "0.07em",
                          display: "grid", gridTemplateColumns: "1fr 90px 90px 90px",
                          columnGap: 8,
                        }}>
                          <div>Coffee</div>
                          <div style={{ textAlign: "right" }}>Batches</div>
                          <div style={{ textAlign: "right" }}>Green bags</div>
                          <div style={{ textAlign: "right" }}>Roasted</div>
                        </div>
                        {plan.length === 0 && (
                          <div style={{ padding: "16px 20px", fontSize: 13, color: "#9CA3AF", background: "#fff", borderBottom: "1px solid #E5E7EB" }}>
                            No coffees scheduled yet — add orders below to populate the plan.
                          </div>
                        )}
                        {plan.map((p, idx) => {
                          const bags = Math.ceil((p.greenLbs || 0) / 154);
                          return (
                            <div key={p.id} style={{
                              padding: "10px 20px",
                              borderBottom: idx < plan.length - 1 ? "1px solid #F3F4F6" : "1px solid #E5E7EB",
                              display: "grid", gridTemplateColumns: "1fr 90px 90px 90px",
                              columnGap: 8, alignItems: "center",
                              background: "#fff",
                            }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{p.name}</div>
                              <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", textAlign: "right" }}>{p.batches}</div>
                              <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", textAlign: "right" }}>{bags}</div>
                              <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", textAlign: "right" }}>{Math.ceil(p.roastedLbs).toLocaleString()} lb</div>
                            </div>
                          );
                        })}
                        <div style={{
                          padding: "12px 20px",
                          display: "grid", gridTemplateColumns: "1fr 90px 90px 90px",
                          columnGap: 8, alignItems: "center",
                          background: "#FAFAFA",
                        }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total</div>
                          <div style={{ fontSize: 15, fontWeight: 800, color: "#111827", textAlign: "right" }}>{totals.batches}</div>
                          <div style={{ fontSize: 15, fontWeight: 800, color: "#111827", textAlign: "right" }}>{totals.bags}</div>
                          <div style={{ fontSize: 15, fontWeight: 800, color: "#111827", textAlign: "right" }}>{Math.ceil(totals.lb).toLocaleString()} lb</div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
            <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
              <Search size={15} color="#9CA3AF" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
              <input
                value={orderSearch}
                onChange={e => setOrderSearch(e.target.value)}
                placeholder="Search by order #, customer, or product..."
                style={{ width: "100%", padding: "8px 12px 8px 36px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#000000", fontSize: 15, fontFamily: "Inter, sans-serif", outline: "none", boxSizing: "border-box" }}
              />
            </div>
            {/* Contextual batch buttons */}
            {orderFilter === "all" && untagged.length > 0 && (
              <button
                onClick={scheduleAll}
                disabled={!!tagJobProgress}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 12px", borderRadius: 8, border: "1px solid #000000", background: "#000000", fontSize: 14, fontWeight: 500, color: "#fff", cursor: tagJobProgress ? "default" : "pointer", opacity: tagJobProgress ? 0.5 : 1, fontFamily: "Inter, sans-serif" }}
              >
                <Plus size={13} strokeWidth={1.8} /> Schedule all
              </button>
            )}
            {(orderFilter === "all" || orderFilter === "production") && (activeDayId ? taggedForActiveDay.length : tagged.length) > 0 && (
              <button
                onClick={() => {
                  const scope = activeDayId ? taggedForActiveDay : tagged;
                  const dayLabel = activeDayId ? " in this day" : "";
                  if (window.confirm(`Remove the production tag from ${scope.length} scheduled order${scope.length === 1 ? "" : "s"}${dayLabel}?`)) removeAll();
                }}
                disabled={!!tagJobProgress}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 12px", borderRadius: 8, border: "1px solid #FCA5A5", background: "#FEF2F2", fontSize: 14, fontWeight: 500, color: "#B91C1C", cursor: tagJobProgress ? "default" : "pointer", opacity: tagJobProgress ? 0.5 : 1, fontFamily: "Inter, sans-serif" }}
              >
                <X size={13} strokeWidth={1.8} /> Remove all{activeDayId ? " in day" : ""}
              </button>
            )}
            {orderFilter === "production" && (activeDayId ? taggedForActiveDay.length : tagged.length) > 0 && (
              <button onClick={markAllProduced} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 12px", borderRadius: 8, border: "1px solid #16A34A", background: "#16A34A", fontSize: 14, fontWeight: 500, color: "#fff", cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
                <Check size={13} strokeWidth={1.8} /> All ready{activeDayId ? " in day" : ""}
              </button>
            )}
            {orderFilter === "produced" && selectedForShip.size > 0 && (
              <button
                onClick={() => {
                  const chosen = filteredOrders.filter(o => selectedForShip.has(o.shopifyId || o.id));
                  if (chosen.length === 0) return;
                  setBatchShipModal({ orders: chosen });
                }}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 12px", borderRadius: 8, border: "1px solid #0369A1", background: "#0369A1", fontSize: 14, fontWeight: 500, color: "#fff", cursor: "pointer", fontFamily: "Inter, sans-serif" }}
              >
                <Truck size={13} strokeWidth={1.8} /> Ship selected ({selectedForShip.size})
              </button>
            )}
            {shopConnected && (
              <button onClick={() => fetchShopifyOrders(true)} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 12px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 500, color: "#374151", cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
                <RefreshCw size={13} strokeWidth={1.6} /> Refresh
              </button>
            )}
            {/* Live progress while a Schedule/Remove all job is running */}
            {tagJobProgress && (() => {
              const { done, total, action } = tagJobProgress;
              const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
              const left = Math.max(0, total - done);
              const verb = action === "remove" ? "Removing" : "Scheduling";
              const accent = action === "remove" ? "#DC2626" : "#000000";
              return (
                <div style={{
                  display: "inline-flex", flexDirection: "column", gap: 4,
                  padding: "4px 12px", minWidth: 180, marginLeft: 4,
                }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12, fontFamily: "Inter, sans-serif" }}>
                    <span style={{ fontWeight: 600, color: "#111827" }}>{verb}…</span>
                    <span style={{ color: "#6B7280", fontVariantNumeric: "tabular-nums" }}>
                      {done} / {total}
                    </span>
                    <span style={{ color: "#9CA3AF", fontVariantNumeric: "tabular-nums" }}>
                      · {left} left
                    </span>
                  </div>
                  <div style={{
                    height: 4, borderRadius: 99, background: "#F3F4F6", overflow: "hidden",
                  }}>
                    <div style={{
                      height: "100%", width: `${pct}%`, background: accent,
                      borderRadius: 99, transition: "width 0.4s ease",
                    }} />
                  </div>
                </div>
              );
            })()}
          </div>
          {/* Wrap the table in a constrained container — table-layout
              fixed + percentage widths means the total never exceeds
              100% of the parent so the rightmost actions cell can't
              clip. */}
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <colgroup>
              {orderFilter === "produced" && <col style={{ width: 36 }} />}
              <col style={{ width: 180 }} />{/* Order # + channel pill */}
              <col style={{ width: "20%" }} />{/* Customer */}
              <col />{/* Products — flex */}
              <col style={{ width: 56 }} />{/* Items */}
              <col style={{ width: 60 }} />{/* Qty */}
              <col style={{ width: 70 }} />{/* Date */}
              <col style={{ width: orderFilter === "produced" ? 280 : 290 }} />{/* Actions */}
            </colgroup>
            <thead>
              <tr>
                {orderFilter === "produced" && (
                  <th style={{ padding: "10px 14px", textAlign: "left", borderBottom: "1px solid #F3F4F6" }}>
                    <input
                      type="checkbox"
                      checked={displayedOrders.length > 0 && displayedOrders.every(o => selectedForShip.has(o.shopifyId || o.id))}
                      onChange={e => {
                        e.stopPropagation();
                        if (e.target.checked) {
                          setSelectedForShip(new Set(displayedOrders.map(o => o.shopifyId || o.id)));
                        } else {
                          clearSelectedForShip();
                        }
                      }}
                      onClick={e => e.stopPropagation()}
                      style={{ cursor: "pointer" }}
                    />
                  </th>
                )}
                {[
                  { h: "Order",    k: "id" },
                  { h: "Customer", k: "customer" },
                  { h: "Products", k: "product" },
                  { h: "Items",    k: "items" },
                  { h: "Qty",      k: "qty" },
                  { h: "Date",     k: "date" },
                  { h: "",         k: null },
                ].map(({ h, k }, i) => {
                  const sortable = !!k;
                  const active = sortable && orderSort.key === k;
                  const arrow = active ? (orderSort.dir === "asc" ? "↑" : "↓") : "";
                  return (
                    <th
                      key={`${h}-${i}`}
                      onClick={sortable ? () => toggleOrderSort(k) : undefined}
                      style={{
                        padding: "10px 14px", textAlign: "left",
                        fontSize: 13, fontWeight: 500,
                        color: active ? "#111827" : "#9CA3AF",
                        borderBottom: "1px solid #F3F4F6",
                        cursor: sortable ? "pointer" : "default",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                      {arrow && <span style={{ marginLeft: 4, fontSize: 11 }}>{arrow}</span>}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {displayedOrders.map(o => {
                const key = o.shopifyId || o.id;
                const isSkipped = skippedIds.has(key);
                const isProducedView = orderFilter === "produced";
                const producedAt = o.producedAt
                  ? new Date(o.producedAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                  : null;
                const existingShipment = isProducedView ? shipmentByOrderId.get(key) : null;
                const isSelectedForShip = isProducedView && selectedForShip.has(key);
                return (
                <tr key={o.id} style={{ borderBottom: "1px solid #F9FAFB", cursor: "pointer", background: isSelectedForShip ? "#F0F9FF" : "transparent" }}
                  onClick={() => setSelectedOrderId(o.id)}
                  onMouseEnter={e => { if (!isSelectedForShip) e.currentTarget.style.background = "#F9FAFB"; }}
                  onMouseLeave={e => { if (!isSelectedForShip) e.currentTarget.style.background = "transparent"; }}>
                  {isProducedView && (
                    <td style={{ padding: "12px 14px" }} onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelectedForShip}
                        onChange={() => toggleSelectedForShip(key)}
                        onClick={e => e.stopPropagation()}
                        style={{ cursor: "pointer" }}
                      />
                    </td>
                  )}
                  <td style={{ padding: "12px 14px", fontWeight: 600, fontSize: 15, color: "#000000" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      {o.id}
                      <ChannelPill channel={deriveOrderChannel(o)} />
                      {(() => {
                        const fs = String(o.financialStatus || "").toLowerCase();
                        if (fs === "paid") return (
                          <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0" }}>Paid</span>
                        );
                        if (fs === "refunded" || fs === "partially_refunded") return (
                          <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", background: "#F4F4F5", color: "#52525B", border: "1px solid #E4E4E7" }}>Refunded</span>
                        );
                        if (fs && fs !== "pending") return (
                          <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" }}>{fs.replace(/_/g, " ")}</span>
                        );
                        return null;
                      })()}
                    </span>
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 15, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.customer}</td>
                  <td style={{ padding: "12px 14px", fontSize: 15, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={(o.items || []).map(i => i.name).join(", ")}>{(o.items || []).map(i => i.name).join(", ")}</td>
                  <td style={{ padding: "12px 14px", fontSize: 15, color: "#374151" }}>{(o.items || []).length}</td>
                  <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 600, color: "#000000" }}>{(o.items || []).reduce((a, i) => a + i.qty, 0)}</td>
                  <td style={{ padding: "12px 14px", fontSize: 15, color: "#9CA3AF" }}>{isProducedView && producedAt ? producedAt : o.date}</td>
                  <td style={{ padding: "12px 14px", textAlign: "right" }}>
                    <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end", alignItems: "center" }}>
                      {isProducedView ? (
                        <>
                          {existingShipment ? (
                            <span
                              title={existingShipment.tracking || ""}
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 5,
                                padding: "4px 10px", borderRadius: 99,
                                background: "#F0FDF4", color: "#16A34A",
                                fontSize: 13, fontWeight: 500,
                              }}
                            >
                              <Truck size={11} strokeWidth={1.8} /> Shipped
                            </span>
                          ) : (
                            <button
                              onClick={e => { e.stopPropagation(); setShipModal({ order: o }); }}
                              style={{
                                padding: "5px 12px", borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                                border: "1px solid #0369A1", background: "#0369A1", color: "#fff",
                                display: "inline-flex", alignItems: "center", gap: 4,
                              }}
                            >
                              <Truck size={11} strokeWidth={1.8} /> Ship
                            </button>
                          )}
                          <button onClick={e => { e.stopPropagation(); undoProduced(key); }} style={{
                            padding: "5px 12px", borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                            border: "1px solid #E5E7EB", background: "#fff", color: "#374151",
                            display: "inline-flex", alignItems: "center", gap: 4,
                          }}>
                            Undo
                          </button>
                        </>
                      ) : isSkipped ? (
                        <button onClick={e => { e.stopPropagation(); unskipOrder(key); }} style={{
                          padding: "5px 12px", borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                          border: "1px solid #E5E7EB", background: "#fff", color: "#374151",
                          display: "inline-flex", alignItems: "center", gap: 4,
                        }}>
                          Unskip
                        </button>
                      ) : (
                        <>
                          {/* Production-day picker (only meaningful for tagged orders) */}
                          {o.tagged && (
                            <select
                              value={dayIdForOrder(o)}
                              onClick={e => e.stopPropagation()}
                              onChange={e => {
                                e.stopPropagation();
                                const v = e.target.value;
                                if (v === "__new__") {
                                  const label = window.prompt("Name this day", `Day ${productionDays.length + 1}`);
                                  if (!label || !label.trim()) return;
                                  const id = addProductionDay(label.trim());
                                  setOrderDay(o.shopifyId || o.id, id);
                                } else {
                                  setOrderDay(o.shopifyId || o.id, v);
                                }
                              }}
                              style={{
                                padding: "4px 22px 4px 10px", borderRadius: 99,
                                border: "1px solid #E5E7EB", background: "#fff",
                                color: "#374151", fontSize: 12, fontWeight: 500,
                                fontFamily: "Inter, sans-serif", cursor: "pointer",
                                appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
                                backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\\"http://www.w3.org/2000/svg\\" width=\\"10\\" height=\\"10\\" viewBox=\\"0 0 24 24\\" fill=\\"none\\" stroke=\\"%239CA3AF\\" stroke-width=\\"2.4\\" stroke-linecap=\\"round\\" stroke-linejoin=\\"round\\"><polyline points=\\"6 9 12 15 18 9\\"></polyline></svg>")',
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 7px center",
                              }}
                            >
                              {productionDays.map(d => (
                                <option key={d.id} value={d.id}>{d.label}</option>
                              ))}
                              <option value="__new__">+ New day…</option>
                            </select>
                          )}
                          <button onClick={e => { e.stopPropagation(); toggle(o.id); }} style={{
                            padding: "5px 12px", borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                            border: `1px solid ${o.tagged ? "#E5E7EB" : "#000000"}`,
                            background: o.tagged ? "#fff" : "#000000",
                            color: o.tagged ? "#6B7280" : "#fff",
                            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
                            width: 104,
                          }}>
                            {o.tagged ? <><X size={11} /> Remove</> : <><Plus size={11} /> Schedule</>}
                          </button>
                          {o.tagged && orderFilter === "production" && (
                            <button onClick={e => { e.stopPropagation(); markProduced(o); }} style={{
                              padding: "5px 12px", borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                              border: "1px solid #16A34A",
                              background: "#16A34A",
                              color: "#fff",
                              display: "inline-flex", alignItems: "center", gap: 4,
                            }}>
                              <Check size={11} /> Ready
                            </button>
                          )}
                          {!o.tagged && (
                            <button onClick={e => { e.stopPropagation(); skipOrder(o); }} title="Skip this order" style={{
                              padding: "5px 12px", borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                              border: "1px solid #E5E7EB", background: "#fff", color: "#6B7280",
                              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
                              width: 104,
                            }}>
                              <SkipForward size={11} /> Skip
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
      }

      case "production": {
        /* Each Production section has two sub-tabs:
             prodTab="main"     → the section's workspace
             prodTab="activity" → a scoped Activity dashboard
           The routes workspace is owned by EasyRoutesView; we only skip
           to it when NOT on the Activity tab. */
        if (prodView === "routes" && prodTab === "main") {
          return <EasyRoutesView
            readyOrders={routeReadyOrders}
            locations={routeLocations}
            setLocations={setRouteLocations}
            facilityLocation={facilityLocation}
            departureId={routeDepartureId}
            setDepartureId={setRouteDepartureId}
            arrivalId={routeArrivalId}
            setArrivalId={setRouteArrivalId}
            setupOpen={routeSetupOpen}
            setSetupOpen={setRouteSetupOpen}
            locationModal={routeLocationModal}
            setLocationModal={setRouteLocationModal}
            routeStopKeys={routeStopKeys}
            setRouteStopKeys={setRouteStopKeys}
          />;
        }
        // Ship → Ready: redirect to the Orders → Ready view, which
        // already handles the produced-orders table, ship action,
        // selection, etc. We just hop the user there in one click and
        // let that page do the rendering.
        if (prodView === "routes" && prodTab === "ready") {
          // Delegate by switching the page router on next render.
          setTimeout(() => { setPage("orders"); setOrderFilter("produced"); }, 0);
          return null;
        }
        if (prodView === "packing") {
          const totalBatches = plan.reduce((s, p) => s + p.batches, 0);
          return (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #F3F4F6", gap: 16 }}>
                <div>
                  <h1 style={{ fontSize: 20, fontWeight: 600, color: "#000000", margin: 0, lineHeight: "28px" }}>Packing list</h1>
                  <p style={{ fontSize: 16, color: "#6B7280", margin: "2px 0 0", lineHeight: "20px" }}>Combined pack breakdown across all scheduled orders.</p>
                </div>
                <button
                  onClick={downloadPackingPDF}
                  disabled={packingList.length === 0}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "8px 14px", borderRadius: 8,
                    border: "1px solid #000000", background: "#000000",
                    color: "#fff", fontSize: 15, fontWeight: 500,
                    cursor: packingList.length === 0 ? "not-allowed" : "pointer",
                    opacity: packingList.length === 0 ? 0.4 : 1,
                    fontFamily: "Inter, sans-serif", whiteSpace: "nowrap", flexShrink: 0,
                  }}
                >
                  <ClipboardList size={14} strokeWidth={1.8} /> Print Packing List
                </button>
              </div>
              <ProductionDaysStrip
                productionDays={productionDays}
                activeDayId={activeDayId}
                visibleOrders={visibleOrders || []}
                orderDayMap={orderDayMap}
                planRows={plan}
                metric="units"
              />
              <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
                {[
                  { l: "Units total", v: packingTotalQty },
                  { l: "Scheduled orders", v: tagged.length },
                  { l: "Est. packing time", v: formatDuration(packingTimeSec) },
                ].map(s => (
                  <div key={s.l} style={{ flex: 1, background: "#fff", borderRadius: 10, border: "1px solid #F3F4F6", padding: "14px 16px" }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4 }}>{s.l}</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#000000", marginTop: 4 }}>{s.v}</div>
                  </div>
                ))}
              </div>
              {packingList.length === 0 ? (
                <div style={{ background: "#F9FAFB", borderRadius: 12, padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 16 }}>
                  No scheduled orders yet. Tag an order as Scheduled in the Orders tab to see it appear here.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {[
                        { l: "#", w: 40 },
                        { l: "Product", w: null },
                        { l: "Qty", w: 80 },
                        { l: "Variante", w: null },
                      ].map(h => (
                        <th key={h.l} style={{
                          padding: "12px 14px", textAlign: "left",
                          fontSize: 11, fontWeight: 500, color: "#9CA3AF",
                          borderBottom: "1px solid #E5E7EB",
                          textTransform: "uppercase", letterSpacing: "0.08em",
                          width: h.w ? h.w : undefined,
                        }}>{h.l}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {packingList.map((r, idx) => (
                      <tr key={`${r.name}-${r.size}-${r.packing}`} style={{ borderBottom: "1px solid #F3F4F6" }}>
                        <td style={{ padding: "14px 14px", fontSize: 14, color: "#9CA3AF" }}>{idx + 1}</td>
                        <td style={{ padding: "14px 14px", fontSize: 15, color: "#000000" }}>
                          <span style={{ fontWeight: 700 }}>{r.name}</span>
                          {r.size && <span style={{ fontWeight: 400, color: "#6B7280" }}>{` — ${r.size}`}</span>}
                        </td>
                        <td style={{ padding: "14px 14px", fontSize: 15, color: "#000000" }}>{r.qty}</td>
                        <td style={{ padding: "14px 14px", fontSize: 15, color: "#374151" }}>{r.packing || <span style={{ color: "#D1D5DB" }}>—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        }

        if (prodTab === "planning") {
          const planRows = expandedBatches;
          const completedBatchIds = new Set((roastingQueue.completed || []).map(c => c.batchId));
          const getBatchStatus = (batchId) => {
            if (completedBatchIds.has(batchId))          return { label: "Roasted",  bg: "#D1FAE5", color: "#059669" };
            if (batchId === roastingQueue.currentId)     return { label: "Roasting", bg: "#FEF3C7", color: "#D97706" };
            if (batchId === roastingQueue.nextId)        return { label: "Up Next",  bg: "#F3E8FF", color: "#7C3AED" };
            return { label: "Queued", bg: "#EFF6FF", color: "#2563EB" };
          };
          // Quick-mark a batch as done without going through the live roast flow.
          // Assigns the next B-XXX roast number if the batch doesn't have one yet.
          // Also draws down green inventory: finds the most-recently-active
          // lot for the product and decrements its weight by batch.greenLbs.
          // If no plausible lot is found we tag the batch as "untracked"
          // so unmark can no-op cleanly.
          const findLotForBatch = (batch) => {
            const candidates = (greenLots || []).filter(l => !l.archivedAt && (l.currentWeightLbs || 0) > 0);
            // Strongest signal: explicit productId on the lot.
            let m = candidates.find(l => l.productId && l.productId === batch.productId);
            if (m) return m;
            // Fallback: name includes product name.
            const pn = String(batch.productName || "").toLowerCase().trim();
            if (pn) {
              m = candidates.find(l => String(l.name || "").toLowerCase().includes(pn) || String(l.coffeeName || "").toLowerCase().includes(pn));
              if (m) return m;
            }
            return null;
          };
          // Open the QC modal first; the actual mark happens in
          // the modal's save handler. If qc is passed (from the
          // modal) we go straight through.
          const markBatchRoasted = (batch, qc = null) => {
            if (!qc) {
              // No QC payload yet — open the modal and let the user
              // fill in roasted weight, color, reject reason, etc.
              setQcModal({ batch, mode: "complete" });
              return;
            }
            if (completedBatchIds.has(batch.id)) return;
            const now = Date.now();
            const greenLbs = Number(batch.greenLbs) || 0;
            const lot = greenLbs > 0 ? findLotForBatch(batch) : null;
            // Rejected batches: the green was consumed, so we draw
            // it down anyway (per spec — the bag was used). We do
            // NOT put rejected coffee into producedOrders (it's not
            // sellable).
            const drawdown = lot
              ? { lotId: lot.id, lotName: lot.name, lbs: greenLbs }
              : { untracked: true, lbs: greenLbs };
            if (lot) {
              setGreenLots(prev => prev.map(l => l.id === lot.id
                ? { ...l, currentWeightLbs: Math.max(0, (Number(l.currentWeightLbs) || 0) - greenLbs) }
                : l));
            }
            const finalRoastedLbs = qc.roastedLbs != null ? Number(qc.roastedLbs) : (Number(batch.roastedLbs) || 0);
            setRoastingQueue(q => {
              const existingNum = q.batchNumbers?.[batch.id];
              const num = existingNum || `B-${String(q.nextBatchNum || 1).padStart(3, "0")}`;
              const completedEntry = {
                batchId: batch.id,
                batchNum: num,
                preBatchNum: batch.preBatchNum || null,
                roastId: null,
                productName: batch.productName,
                productId: batch.productId,
                batchIndex: batch.batchIndex,
                totalBatches: batch.totalBatches,
                sku: batch.sku,
                roastLevel: batch.roastLevel,
                greenLbs,
                roastedLbs: finalRoastedLbs,
                startedAt: now,
                completedAt: now,
                finalBt: null,
                events: [],
                samples: [],
                drawdown,
                roastedByUserId: user?.id || null,
                roastedByName: user?.name || null,
                qc: {
                  roastedLbs: finalRoastedLbs,
                  color: qc.color || null,
                  rejected: !!qc.rejected,
                  rejectReason: qc.rejected ? (qc.rejectReason || "") : null,
                  notes: qc.notes || "",
                  byUserId: user?.id || null,
                  byName: user?.name || null,
                  ts: now,
                },
              };
              return {
                ...q,
                ...(q.currentId === batch.id ? { currentId: null, currentRoastId: null } : {}),
                ...(q.nextId === batch.id ? { nextId: null } : {}),
                batchNumbers: { ...(q.batchNumbers || {}), [batch.id]: num },
                nextBatchNum: existingNum ? (q.nextBatchNum || 1) : (q.nextBatchNum || 1) + 1,
                completed: [completedEntry, ...(q.completed || [])],
              };
            });
            // Only sellable batches feed producedOrders. Rejected
            // batches stay logged in completed but don't pretend
            // they have units available to ship.
            if (!qc.rejected) {
              const syntheticOrder = {
                shopifyId: `batch_roast_${batch.id}`,
                id: `#batch-${batch.id}`,
                customer: "Roast batch",
                date: new Date(now).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
                items: [{ productId: batch.productId, name: batch.productName, variant: batch.roastLevel || "Whole Bean", sku: batch.sku || "", qty: Math.ceil(finalRoastedLbs || 1), grams: 340 }],
                total: 0,
                shippingAddress: { address1: "", city: "", province: "", province_code: "", zip: "", latitude: null, longitude: null, name: "" },
                producedAt: now,
              };
              setProducedOrders(prev => [syntheticOrder, ...prev]);
            }
          };
          // Cancel a roasted batch — remove from completed, remove B-XXX,
          // remove synthetic producedOrder, and restore any green-lot
          // weight that was drawn down on mark.
          const unmarkBatchRoasted = (batch) => {
            const completedEntry = (roastingQueue.completed || []).find(c => c.batchId === batch.id);
            if (completedEntry?.drawdown && !completedEntry.drawdown.untracked && completedEntry.drawdown.lotId) {
              const restoreLbs = Number(completedEntry.drawdown.lbs) || 0;
              setGreenLots(prev => prev.map(l => l.id === completedEntry.drawdown.lotId
                ? { ...l, currentWeightLbs: (Number(l.currentWeightLbs) || 0) + restoreLbs }
                : l));
            }
            setRoastingQueue(q => {
              const newBatchNumbers = { ...(q.batchNumbers || {}) };
              delete newBatchNumbers[batch.id];
              return {
                ...q,
                completed: (q.completed || []).filter(c => c.batchId !== batch.id),
                batchNumbers: newBatchNumbers,
              };
            });
            setProducedOrders(prev => prev.filter(o => o.shopifyId !== `batch_roast_${batch.id}`));
          };

          // Group batches by coffee
          const seenProductIds = new Set();
          const coffeeGroups = [];
          for (const p of plan) {
            if (seenProductIds.has(p.id)) continue;
            seenProductIds.add(p.id);
            const batches = planRows.filter(b => b.productId === p.id);
            const doneBatches = batches.filter(b => completedBatchIds.has(b.id)).length;
            const totalBags = Math.ceil((p.greenLbs || 0) / 154);
            const pct = batches.length > 0 ? Math.round((doneBatches / batches.length) * 100) : 0;
            const allDone = batches.length > 0 && doneBatches === batches.length;
            coffeeGroups.push({ ...p, batches, doneBatches, totalBags, pct, allDone });
          }

          // Pre-batch index map: stable P-XXX per batch based on plan order
          const preBatchIndexMap = new Map(expandedBatches.map((b, i) => [b.id, `PB-${String(i + 1).padStart(3, "0")}`]));

          // Per-row Bags removed — meaningless at the batch level since a
          // single 154 lb green bag spans ~2.2 batches. Total green bags
          // for the coffee live in the group header instead.
          const batchCols = "160px 90px 110px 1fr";
          const batchHdrs = ["Batch #", "Roasted", "Green size", ""];

          // Build per-day order counts for the day tabs.
          const dayCounts = (() => {
            const m = {};
            for (const o of tagged) {
              const id = dayIdForOrder(o);
              if (!id) continue;
              m[id] = (m[id] || 0) + 1;
            }
            return m;
          })();

          return (
            <div>
              {/* Planning header with print button */}
              <div style={{ marginBottom: 16, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
                <div>
                  <h1 style={{ fontSize: 20, fontWeight: 600, color: "#000000", margin: 0, lineHeight: "28px" }}>Roast Planning</h1>
                  <p style={{ fontSize: 16, color: "#6B7280", margin: "2px 0 0", lineHeight: "20px" }}>Per-coffee batch breakdown for scheduled orders.</p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, marginTop: 2 }}>
                  <NotificationsBell />
                  <button
                    onClick={printPlanningPDF}
                    disabled={planRows.length === 0}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "8px 14px", borderRadius: 8,
                      border: "1px solid #000000", background: "#000000",
                      color: "#fff", fontSize: 15, fontWeight: 500,
                      cursor: planRows.length === 0 ? "not-allowed" : "pointer",
                      opacity: planRows.length === 0 ? 0.4 : 1,
                      fontFamily: "Inter, sans-serif", whiteSpace: "nowrap", flexShrink: 0,
                    }}
                  >
                    <ClipboardList size={14} strokeWidth={1.8} /> Print Planning
                  </button>
                </div>
              </div>
              <ProductionDaysStrip
                productionDays={productionDays}
                activeDayId={activeDayId}
                visibleOrders={visibleOrders || []}
                orderDayMap={orderDayMap}
                planRows={planRows || []}
                metric="roast"
              />
              {/* Day-scoping happens via the side-nav rows under
                  "Production days". Show a slim breadcrumb so the user
                  knows what day the view is currently scoped to. */}
              <div style={{
                marginBottom: 24, paddingBottom: 16,
                borderBottom: "1px solid #F3F4F6",
                fontSize: 13, color: "#9CA3AF",
              }}>
                Showing&nbsp;
                <span style={{ fontWeight: 600, color: "#374151" }}>
                  {activeDayId
                    ? (productionDays.find(d => d.id === activeDayId)?.label || "Day")
                    : "All days"}
                </span>
                <span style={{ marginLeft: 6 }}>
                  · {(activeDayId ? (dayCounts[activeDayId] || 0) : tagged.length)} order
                  {(activeDayId ? (dayCounts[activeDayId] || 0) : tagged.length) === 1 ? "" : "s"}
                </span>
                <span style={{ marginLeft: 8 }}>
                  · Manage days in <span style={{ fontWeight: 600, color: "#6B7280" }}>Orders → Scheduled</span>
                </span>
              </div>
              {(
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {/* Plan / Roasting toggle — splits the rollup from the
                      batch-level roasting interface so the operator can
                      either review the day's plan or focus on roasting
                      without scrolling past the other. */}
                  <div style={{ display: "inline-flex", border: "1px solid #E5E7EB", borderRadius: 8, padding: 2, background: "#fff", alignSelf: "flex-start" }}>
                    {[
                      { v: "plan",     label: "Plan" },
                      { v: "roasting", label: "Roasting" },
                    ].map(opt => (
                      <button
                        key={opt.v}
                        onClick={() => setRoastView(opt.v)}
                        style={{
                          padding: "6px 14px", borderRadius: 6, border: "none",
                          background: roastView === opt.v ? "#000" : "transparent",
                          color: roastView === opt.v ? "#fff" : "#52525B",
                          fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif",
                          cursor: "pointer",
                        }}
                      >{opt.label}</button>
                    ))}
                  </div>
                  {/* ── Top-of-page summary (Plan view) ─────────────────
                       Quick rollup of every coffee on the plan + a totals
                       row. Always rendered — empty days show a single
                       Total row with zeros so the layout never collapses. */}
                  {roastView === "plan" && (() => {
                    const totals = coffeeGroups.reduce((a, g) => ({
                      batches: a.batches + g.batches.length,
                      bags:    a.bags    + g.totalBags,
                      lb:      a.lb      + (g.roastedLbs || 0),
                    }), { batches: 0, bags: 0, lb: 0 });
                    return (
                      <div style={{
                        background: "#FAFAFA", border: "1px solid #E5E7EB",
                        borderRadius: 12, overflow: "hidden",
                      }}>
                        <div style={{
                          padding: "10px 20px", borderBottom: "1px solid #E5E7EB",
                          fontSize: 11, fontWeight: 700, color: "#9CA3AF",
                          textTransform: "uppercase", letterSpacing: "0.07em",
                          display: "grid", gridTemplateColumns: "1fr 90px 90px 90px",
                          columnGap: 8,
                        }}>
                          <div>Coffee</div>
                          <div style={{ textAlign: "right" }}>Batches</div>
                          <div style={{ textAlign: "right" }}>Green bags</div>
                          <div style={{ textAlign: "right" }}>Roasted</div>
                        </div>
                        {coffeeGroups.map((g, idx) => (
                          <div key={g.id} style={{
                            padding: "10px 20px",
                            borderBottom: idx < coffeeGroups.length - 1 ? "1px solid #F3F4F6" : "1px solid #E5E7EB",
                            display: "grid", gridTemplateColumns: "1fr 90px 90px 90px",
                            columnGap: 8, alignItems: "center",
                            background: "#fff",
                          }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{g.name}</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", textAlign: "right" }}>{g.batches.length}</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", textAlign: "right" }}>{g.totalBags}</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", textAlign: "right" }}><UnitWeight valueLbs={g.roastedLbs} decimals={0} /></div>
                          </div>
                        ))}
                        {/* Totals row */}
                        <div style={{
                          padding: "12px 20px",
                          display: "grid", gridTemplateColumns: "1fr 90px 90px 90px",
                          columnGap: 8, alignItems: "center",
                          background: "#FAFAFA",
                        }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total</div>
                          <div style={{ fontSize: 15, fontWeight: 800, color: "#111827", textAlign: "right" }}>{totals.batches}</div>
                          <div style={{ fontSize: 15, fontWeight: 800, color: "#111827", textAlign: "right" }}>{totals.bags}</div>
                          <div style={{ fontSize: 15, fontWeight: 800, color: "#111827", textAlign: "right" }}><UnitWeight valueLbs={totals.lb} decimals={0} /></div>
                        </div>
                      </div>
                    );
                  })()}

                  {planRows.length === 0 && (
                    <div style={{ background: "#F9FAFB", borderRadius: 12, padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>
                      No orders scheduled yet. Tag an order as Scheduled in Orders to fill in the plan.
                    </div>
                  )}

                  {roastView === "roasting" && coffeeGroups.map(group => (
                    <div key={group.id} style={{
                      border: `1.5px solid ${group.allDone ? "#BBF7D0" : "#E5E7EB"}`,
                      borderRadius: 14,
                      overflow: "hidden",
                      background: "#fff",
                    }}>
                      {/* ── Group header ─────────────────────────────── */}
                      <div style={{
                        background: group.allDone ? "#F0FDF4" : "#FAFAFA",
                        padding: "16px 20px",
                        borderBottom: `1px solid ${group.allDone ? "#BBF7D0" : "#E5E7EB"}`,
                      }}>
                        {/* Top row: name + stats + done label */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                          <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>{group.name}</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                            <div style={{ textAlign: "center" }}>
                              <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", lineHeight: 1 }}>{group.batches.length}</div>
                              <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>batches</div>
                            </div>
                            <div style={{ textAlign: "center" }}>
                              <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", lineHeight: 1 }}>{group.totalBags}</div>
                              <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>green bags</div>
                            </div>
                            <div style={{ textAlign: "center" }}>
                              <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", lineHeight: 1 }}>{Math.ceil(group.roastedLbs)}</div>
                              <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>lb roasted</div>
                            </div>
                          </div>
                        </div>
                        {/* Progress bar + label */}
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ flex: 1, height: 5, background: "#E5E7EB", borderRadius: 99, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${group.pct}%`, background: group.allDone ? "#22C55E" : "#111827", borderRadius: 99, transition: "width 0.35s ease" }} />
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: group.allDone ? "#16A34A" : "#6B7280", whiteSpace: "nowrap" }}>
                            {group.allDone ? "All roasted ✓" : `${group.doneBatches} of ${group.batches.length} done`}
                          </div>
                        </div>
                      </div>

                      {/* ── Batch rows ────────────────────────────────── */}
                      {/* Column headers */}
                      <div style={{ display: "grid", gridTemplateColumns: batchCols, columnGap: 8, padding: "10px 20px", borderBottom: "1px solid #F3F4F6" }}>
                        {batchHdrs.map(h => (
                          <div key={h} style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em" }}>{h}</div>
                        ))}
                      </div>
                      {group.batches.map((b, idx) => {
                        const finalNum = roastingQueue.batchNumbers?.[b.id] || null;
                        const preNum = preBatchIndexMap.get(b.id);
                        const isRoasted = completedBatchIds.has(b.id);
                        const st = getBatchStatus(b.id);
                        return (
                          <div key={b.id} style={{
                            display: "grid", gridTemplateColumns: batchCols, columnGap: 8,
                            alignItems: "center", padding: "12px 20px",
                            borderBottom: idx < group.batches.length - 1 ? "1px solid #F9FAFB" : "none",
                            background: isRoasted ? "#FAFFFE" : "#fff",
                          }}>
                            {/* Batch # — pre (P-XXX) → final (B-XXX) */}
                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              {isRoasted && finalNum ? (
                                <>
                                  <span style={{ display: "inline-block", background: "#111827", color: "#fff", borderRadius: 99, padding: "4px 11px", fontSize: 12, fontWeight: 700, letterSpacing: "0.02em" }}>{finalNum}</span>
                                  <span style={{ display: "inline-block", background: "#F3F4F6", color: "#9CA3AF", borderRadius: 99, padding: "3px 9px", fontSize: 11, fontWeight: 600, letterSpacing: "0.01em" }}>{preNum}</span>
                                </>
                              ) : (
                                <span style={{ display: "inline-block", background: "#F3F4F6", color: "#6B7280", borderRadius: 99, padding: "4px 11px", fontSize: 12, fontWeight: 600, letterSpacing: "0.02em", border: "1px dashed #D1D5DB" }}>{preNum}</span>
                              )}
                            </div>
                            {/* Roasted lbs */}
                            <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}><UnitWeight valueLbs={b.roastedLbs} decimals={0} /></div>
                            {/* Green size */}
                            <div style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}><UnitWeight valueLbs={b.greenLbs} decimals={0} suffix=" green" /></div>
                            {/* Action — right-aligned */}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
                              {isRoasted ? (
                                <>
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#D1FAE5", color: "#059669", borderRadius: 99, padding: "5px 14px", fontSize: 13, fontWeight: 600 }}>
                                    <Check size={12} strokeWidth={2.5} /> Roasted
                                  </span>
                                  <button
                                    onClick={() => unmarkBatchRoasted(b)}
                                    title="Undo roasted"
                                    style={{
                                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                                      width: 26, height: 26, border: "1px solid #E5E7EB",
                                      borderRadius: 99, background: "#fff", cursor: "pointer",
                                      color: "#9CA3AF", padding: 0, fontFamily: "Inter, sans-serif",
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = "#FEE2E2"; e.currentTarget.style.borderColor = "#FECACA"; e.currentTarget.style.color = "#EF4444"; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.color = "#9CA3AF"; }}
                                  >
                                    <X size={12} strokeWidth={2.5} />
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => markBatchRoasted(b)}
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: 5,
                                    background: st.label === "Roasting" ? "#FEF3C7" : "#F3F4F6",
                                    color: st.label === "Roasting" ? "#D97706" : "#374151",
                                    border: "none", borderRadius: 99, padding: "5px 14px",
                                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                                    fontFamily: "Inter, sans-serif",
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.background = "#D1FAE5"; e.currentTarget.style.color = "#059669"; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = st.label === "Roasting" ? "#FEF3C7" : "#F3F4F6"; e.currentTarget.style.color = st.label === "Roasting" ? "#D97706" : "#374151"; }}
                                >
                                  <Flame size={12} strokeWidth={2} /> Roasted
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }

        // (intentionally blank — dropthrough to next prodTab below)
        if (prodTab === "activity") {
          /* Cutoff for the selected date window. "all" means no cutoff. */
          const windowMs = {
            "today": 24 * 60 * 60 * 1000,
            "7d":    7 * 24 * 60 * 60 * 1000,
            "30d":   30 * 24 * 60 * 60 * 1000,
            "90d":   90 * 24 * 60 * 60 * 1000,
            "180d":  180 * 24 * 60 * 60 * 1000,
          }[activityDateFilter];
          const cutoff = windowMs != null ? Date.now() - windowMs : 0;
          /* Activity = every order that's been marked produced at least once.
             Sort newest first so the most recent batches land at the top. */
          const activityOrders = (producedOrders || [])
            .filter(o => (o.producedAt || 0) >= cutoff)
            .sort((a, b) => (b.producedAt || 0) - (a.producedAt || 0));

          /* Roll the flat order list into day buckets. Key by a YYYY-MM-DD
             string in local time so "today" doesn't drift across the UTC
             boundary for late-night roasters. */
          const dayKey = (ts) => {
            const d = new Date(ts);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          };
          const dayLabel = (ts) => {
            const d = new Date(ts);
            const today = new Date();
            const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
            if (dayKey(ts) === dayKey(today.getTime())) return "Today";
            if (dayKey(ts) === dayKey(yesterday.getTime())) return "Yesterday";
            return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: d.getFullYear() === today.getFullYear() ? undefined : "numeric" });
          };
          /* Aggregate lbs per order by looking up the product's loss override
             (or default) — items are in finished/roasted lb, which is what
             the operator actually cares about for batch history. */
          const orderRoastedLbs = (o) => (o.items || []).reduce((s, i) => s + (i.qty || 0), 0);
          const days = [];
          const daysByKey = new Map();
          for (const o of activityOrders) {
            const k = dayKey(o.producedAt || 0);
            let bucket = daysByKey.get(k);
            if (!bucket) {
              bucket = { key: k, ts: o.producedAt || 0, orders: [], roastedLbs: 0, productTotals: new Map() };
              daysByKey.set(k, bucket);
              days.push(bucket);
            }
            bucket.orders.push(o);
            const oLbs = orderRoastedLbs(o);
            bucket.roastedLbs += oLbs;
            /* Per-product aggregation for the "what got roasted" caption. */
            for (const i of (o.items || [])) {
              const name = i.name || "—";
              bucket.productTotals.set(name, (bucket.productTotals.get(name) || 0) + (i.qty || 0));
            }
          }
          /* Top-of-page totals mirror the day buckets. */
          const totalRoastedLbs = activityOrders.reduce((s, o) => s + orderRoastedLbs(o), 0);
          const uniqueProducts = new Set();
          for (const o of activityOrders) for (const i of (o.items || [])) uniqueProducts.add(i.name || "—");

          const windowOpts = [
            { v: "7d",   l: "7 days" },
            { v: "30d",  l: "30 days" },
            { v: "90d",  l: "90 days" },
            { v: "180d", l: "6 months" },
            { v: "all",  l: "All time" },
          ];

          /* Relabel the header, summary tiles and per-day caption based on
             which Production section the user came from. The underlying
             data (producedOrders grouped by day) is the same — framing
             changes so the activity dashboard reads as Roasting / Packing
             / Routes history instead of one generic view. */
          const activityFrame = {
            breakdown: {
              title: "Roasting activity",
              subtitle: "Every roast you've logged, grouped by day.",
              emptyAll: "Nothing roasted yet. Mark a scheduled order as Ready to see it appear here.",
              tiles: (vals) => [
                { l: "Roasts", v: vals.count },
                { l: "Roasted", v: `${Math.round(vals.lbs).toLocaleString()} lb` },
                { l: "Unique coffees", v: vals.uniqueCount },
                { l: "Active days", v: vals.days },
              ],
              dayLineRight: (day) => `${Math.round(day.roastedLbs).toLocaleString()} lb roasted`,
            },
            packing: {
              title: "Packing activity",
              subtitle: "Every packing session, grouped by day.",
              emptyAll: "Nothing packed yet. Orders show up here once they're marked Ready.",
              tiles: (vals) => [
                { l: "Packing sessions", v: vals.count },
                { l: "Units packed", v: Math.round(vals.lbs).toLocaleString() },
                { l: "Unique products", v: vals.uniqueCount },
                { l: "Active days", v: vals.days },
              ],
              dayLineRight: (day) => `${Math.round(day.roastedLbs).toLocaleString()} units`,
            },
            routes: {
              title: "Routes activity",
              subtitle: "Deliveries completed, grouped by day.",
              emptyAll: "No deliveries logged yet. Finish a driver route to see it appear here.",
              tiles: (vals) => [
                { l: "Deliveries", v: vals.count },
                { l: "Delivered", v: `${Math.round(vals.lbs).toLocaleString()} lb` },
                { l: "Unique customers", v: vals.uniqueCount },
                { l: "Active days", v: vals.days },
              ],
              dayLineRight: (day) => `${Math.round(day.roastedLbs).toLocaleString()} lb delivered`,
            },
          }[prodView] || {};

          return (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #F3F4F6", gap: 16 }}>
                <div>
                  <h1 style={{ fontSize: 20, fontWeight: 600, color: "#000000", margin: 0, lineHeight: "28px" }}>{activityFrame.title}</h1>
                  <p style={{ fontSize: 16, color: "#6B7280", margin: "2px 0 0", lineHeight: "20px" }}>{activityFrame.subtitle}</p>
                </div>
                <div style={{ display: "inline-flex", gap: 4, background: "#F3F4F6", borderRadius: 8, padding: 3, flexShrink: 0 }}>
                  {windowOpts.map(o => (
                    <button
                      key={o.v}
                      onClick={() => setActivityDateFilter(o.v)}
                      style={{
                        padding: "6px 12px", borderRadius: 6, border: "none",
                        background: activityDateFilter === o.v ? "#fff" : "transparent",
                        color: activityDateFilter === o.v ? "#000000" : "#6B7280",
                        fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                        boxShadow: activityDateFilter === o.v ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                      }}
                    >{o.l}</button>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
                {activityFrame.tiles({ count: activityOrders.length, lbs: totalRoastedLbs, uniqueCount: uniqueProducts.size, days: days.length }).map(s => (
                  <div key={s.l} style={{ flex: 1, background: "#fff", borderRadius: 10, border: "1px solid #F3F4F6", padding: "14px 16px" }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4 }}>{s.l}</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#000000", marginTop: 4 }}>{s.v}</div>
                  </div>
                ))}
              </div>

              {days.length > 0 && (() => {
                const chartData = [...days].reverse().map(day => ({
                  date: new Date(day.ts).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
                  lbs: Math.round(day.roastedLbs),
                  batches: day.orders.length,
                }));
                // Default brush window: show last 30 days (or all if fewer)
                const brushStart = Math.max(0, chartData.length - 30);
                return (
                  <div className="pr-activity-chart" style={{ background: "#fff", border: "1px solid #F3F4F6", borderRadius: 12, padding: "20px 16px 12px", marginBottom: 24 }}>
                    <style>{`
                      .pr-activity-chart .recharts-brush-slide { fill: #111827 !important; fill-opacity: 1 !important; cursor: grab; }
                      .pr-activity-chart .recharts-brush-slide:active { cursor: grabbing; }
                      .pr-activity-chart .recharts-brush-traveller rect { fill: #111827 !important; stroke: none !important; }
                      .pr-activity-chart .recharts-brush-traveller line { display: none !important; }
                    `}</style>
                    <ResponsiveContainer width="100%" height={220}>
                      <ComposedChart data={chartData} margin={{ top: 4, right: 24, left: 0, bottom: 8 }}>
                        <CartesianGrid vertical={false} stroke="#F3F4F6" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                        <YAxis yAxisId="lbs" orientation="left" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={v => `${v} lb`} width={52} />
                        <YAxis yAxisId="batches" orientation="right" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} allowDecimals={false} width={32} />
                        <Tooltip
                          contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12, fontFamily: "Inter, sans-serif" }}
                          formatter={(value, name) => name === "lbs" ? [`${value} lb`, "Roasted"] : [value, "Batches"]}
                        />
                        <Bar yAxisId="lbs" dataKey="lbs" fill="#111827" radius={[4, 4, 0, 0]} maxBarSize={40} />
                        <Line yAxisId="batches" dataKey="batches" stroke="#22D3EE" strokeWidth={2} dot={{ r: 3, fill: "#22D3EE", strokeWidth: 0 }} activeDot={{ r: 5 }} />
                        {chartData.length > 14 && (
                          <Brush
                            dataKey="date"
                            startIndex={brushStart}
                            height={10}
                            travellerWidth={10}
                            fill="#F3F4F6"
                            stroke="none"
                            tickFormatter={() => ""}
                          />
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                    <div style={{ display: "flex", gap: 16, justifyContent: "flex-end", marginTop: 8 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#6B7280" }}>
                        <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: "#111827" }} />
                        Pounds roasted
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#6B7280" }}>
                        <span style={{ display: "inline-block", width: 16, height: 2, background: "#22D3EE", borderRadius: 1 }} />
                        Batches
                      </span>
                    </div>
                  </div>
                );
              })()}

              {(() => {
                if (days.length === 0) return (
                  <div style={{ background: "#F9FAFB", borderRadius: 12, padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 16 }}>
                    {activityDateFilter === "all" ? activityFrame.emptyAll : "Nothing in this window. Try a wider range."}
                  </div>
                );

                // Bucket roast sessions by day key
                const sessionsByDay = new Map();
                if (prodView === "breakdown") {
                  for (const c of (roastingQueue.completed || [])) {
                    const k = dayKey(c.startedAt || c.completedAt || 0);
                    if (!sessionsByDay.has(k)) sessionsByDay.set(k, []);
                    sessionsByDay.get(k).push(c);
                  }
                }

                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {days.map((day, idx) => {
                      const isFirst = idx === 0;
                      // Expanded if: first day (default) and not explicitly collapsed,
                      // OR explicitly expanded via the set.
                      const explicitlyToggled = expandedActivityDays.has(day.key);
                      const isOpen = isFirst ? !explicitlyToggled : explicitlyToggled;
                      const toggle = () => setExpandedActivityDays(prev => {
                        const next = new Set(prev);
                        next.has(day.key) ? next.delete(day.key) : next.add(day.key);
                        return next;
                      });

                      const productTotalsList = Array.from(day.productTotals.entries()).sort((a, b) => b[1] - a[1]);
                      const daySessions = sessionsByDay.get(day.key) || [];

                      return (
                        <div key={day.key} style={{ border: "1px solid #F3F4F6", borderRadius: 12, background: "#fff", overflow: "hidden" }}>
                          {/* ── Clickable header ── */}
                          <div
                            onClick={toggle}
                            style={{ padding: "14px 18px", background: isOpen ? "#fff" : "#FAFAFA", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", borderBottom: isOpen ? "1px solid #F3F4F6" : "none", transition: "background 0.15s" }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.18s", color: "#9CA3AF" }}>
                                <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: "#000000", letterSpacing: "-0.01em" }}>{dayLabel(day.ts)}</div>
                                {!isOpen && (
                                  <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 1 }}>{new Date(day.ts).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</div>
                                )}
                              </div>
                            </div>
                            <div style={{ fontSize: 13, color: "#374151", display: "inline-flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
                              {daySessions.length > 0 && (
                                <span style={{ fontSize: 12, color: "#6B7280" }}>{daySessions.length} roast session{daySessions.length !== 1 ? "s" : ""}</span>
                              )}
                              <span><strong style={{ color: "#000000" }}>{day.orders.length}</strong> order{day.orders.length !== 1 ? "s" : ""}</span>
                              <span style={{ fontWeight: 600, color: "#000000" }}>{activityFrame.dayLineRight(day)}</span>
                            </div>
                          </div>

                          {/* ── Expanded body ── */}
                          {isOpen && (
                            <>
                              {productTotalsList.length > 0 && (
                                <div style={{ padding: "10px 18px", borderBottom: "1px solid #F3F4F6", display: "flex", gap: 6, flexWrap: "wrap" }}>
                                  {productTotalsList.map(([name, qty]) => (
                                    <span key={name} style={{ fontSize: 12, fontWeight: 500, padding: "4px 10px", borderRadius: 99, background: "#F3F4F6", color: "#374151" }}>
                                      {name} · <strong style={{ color: "#000000" }}>{Math.round(qty)} lb</strong>
                                    </span>
                                  ))}
                                </div>
                              )}
                              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <tbody>
                                  {day.orders.map((o, oidx) => {
                                    const time = new Date(o.producedAt || 0).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
                                    const lbs = orderRoastedLbs(o);
                                    const itemList = (o.items || []).map(i => `${i.qty || 0} lb ${i.name || ""}`).join(" · ");
                                    const okey = o.shopifyId || o.id || oidx;
                                    return (
                                      <tr key={okey} style={{ borderBottom: "1px solid #F3F4F6" }}>
                                        <td style={{ padding: "12px 18px", fontSize: 12, color: "#9CA3AF", width: 76, whiteSpace: "nowrap", verticalAlign: "top" }}>{time}</td>
                                        <td style={{ padding: "12px 14px", fontSize: 14, fontWeight: 600, color: "#000000", width: 150, whiteSpace: "nowrap", verticalAlign: "top" }}>{o.id}</td>
                                        <td style={{ padding: "12px 14px", fontSize: 14, color: "#374151", whiteSpace: "nowrap", verticalAlign: "top", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }} title={o.customer}>{o.customer}</td>
                                        <td style={{ padding: "12px 14px", fontSize: 13, color: "#6B7280", verticalAlign: "top", lineHeight: 1.5 }}>{itemList || "—"}</td>
                                        <td style={{ padding: "12px 18px", fontSize: 13, fontWeight: 600, color: "#000000", textAlign: "right", whiteSpace: "nowrap", verticalAlign: "top" }}>{Math.round(lbs).toLocaleString()} lb</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              {daySessions.length > 0 && (
                                <div style={{ padding: "12px 18px 14px", borderTop: "1px solid #F3F4F6" }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
                                    Roast sessions · {daySessions.length}
                                  </div>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    {daySessions.map(c => (
                                      <CompletedRoastRow
                                        key={c.batchId}
                                        entry={c}
                                        onClick={() => setSelectedCompletedId(c.batchId)}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          );
        }

        // Default: breakdown
        const totalGreenLbs = plan.reduce((s, p) => s + p.greenLbs, 0);
        const totalBatches = plan.reduce((s, p) => s + p.batches, 0);
        /* Format a decimal minute count (e.g. 12.5) as mm:ss. */
        const fmtMinToClock = (mins) => {
          if (mins == null || !isFinite(mins)) return "—";
          const m = Math.floor(mins);
          const s = Math.round((mins - m) * 60);
          return `${m}:${String(s).padStart(2, "0")}`;
        };
        /* Break a product's total green weight into individual batch
           sizes — full-size batches until the last, which may be
           smaller to use up the remainder. Returns [15, 15, 15, 8] for
           53 lb at a 15 lb batch size. */
        const batchSplit = (totalGreen, batchSize, batches) => {
          if (!batches) return [];
          const out = [];
          for (let i = 0; i < batches - 1; i++) out.push(batchSize);
          const remainder = Math.max(0, +(totalGreen - batchSize * (batches - 1)).toFixed(1));
          out.push(remainder > 0 ? remainder : batchSize);
          return out;
        };
        return (
          <div>
            {header}

            <LiveRoastCard
              pendingBatch={nextBatch}
              onStartRequested={startNextBatch}
              rorWindowSec={rorWindowSec}
              /* The sandbox store has no paired hardware, so it gets the
                 same "Roast now" simulator CTA as demo mode. */
              demoMode={effectiveDemoMode || connectedShop === "acme-coffee-sandbox.myshopify.com"}
            />

            {/* ═══ Launch pad — three layers (Current / Next / Batches)
                 with Roasted today below. Current = the live card above.
                 Nothing auto-starts; the operator clicks Start on the
                 live card to fire whatever is staged in Next. Save /
                 Cancel are hold-to-confirm on the live card. Rollup
                 stats live inline on the Batches header so they don't
                 compete with the primary flow. */}

            {/* Original-era comment kept for history: ═══ Launch pad —
                 three layers (Current / Next / Batches) with Roasted
                 today below. Drag a batch from Batches onto the Next
                 slot; when the live card is idle, Next auto-
                 promotes and starts roasting. On Save, the current
                 batch lands in Roasted today and Next advances again.
                 On Cancel, the current batch returns to Batches. ═══ */}

            {/* ── NEXT ─────────────────────────────────────────────── */}
            <div style={{ marginTop: 4 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.8 }}>
                  Next · on deck
                </div>
                <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                  {currentBatch
                    ? "Queued for after the current roast"
                    : nextBatch
                      ? "Tap Start on the live card when ready"
                      : "Stage the batch you'll roast next"}
                </div>
              </div>
              <div
                onDragOver={(e) => { if (draggedBatchId) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverZone("next"); } }}
                onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOverZone(z => z === "next" ? null : z); }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (draggedBatchId) stageBatch(draggedBatchId);
                  setDraggedBatchId(null); setDragOverZone(null);
                }}
                style={{
                  minHeight: 76,
                  background: nextBatch ? "linear-gradient(135deg, rgba(34,211,238,0.06) 0%, rgba(167,139,250,0.04) 100%)" : "#FAFAFA",
                  border: `1.5px dashed ${dragOverZone === "next" ? "#22D3EE" : nextBatch ? "rgba(34,211,238,0.45)" : "#E5E7EB"}`,
                  borderRadius: 12, padding: 10,
                  transition: "border-color 0.15s, background 0.15s",
                  display: "flex", alignItems: "center",
                }}
              >
                {nextBatch ? (
                  <BatchRow
                    batch={nextBatch}
                    batchNum={roastingQueue.batchNumbers?.[nextBatch.id] || null}
                    variant="next"
                    onReturn={unstageBatch}
                  />
                ) : (
                  <div style={{ flex: 1, textAlign: "center", color: "#9CA3AF", fontSize: 13, padding: "14px 10px" }}>
                    Drag a batch here to stage it for roasting.
                  </div>
                )}
              </div>
            </div>

            {/* ── BATCHES queue ──────────────────────────────────────── */}
            <div style={{ marginTop: 20 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8, gap: 12, flexWrap: "wrap" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.8 }}>
                  Batches
                </div>
                {/* Rollup stats inline so they ride alongside the queue
                    label instead of taking up their own tile row. */}
                <div style={{ fontSize: 11, color: "#9CA3AF", display: "inline-flex", gap: 12, flexWrap: "wrap", fontVariantNumeric: "tabular-nums" }}>
                  <span>{remainingBatches.length} left</span>
                  <span>·</span>
                  <span>{Math.ceil(totalGreenLbs).toLocaleString()} lb green</span>
                  <span>·</span>
                  <span>{Math.ceil(plan.reduce((s, p) => s + p.roastedLbs, 0)).toLocaleString()} lb roasted</span>
                  <span>·</span>
                  <span>~{formatDuration(totalBatches * minutesPerBatch * 60)}</span>
                  <span>·</span>
                  <span>{batchSizeLbs.toFixed(1)} lb each</span>
                </div>
              </div>
              {plan.length === 0 ? (
                <div style={{ background: "#F9FAFB", borderRadius: 12, padding: 40, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>
                  No orders scheduled yet. Tag an order as Scheduled in Orders to see batches here.
                </div>
              ) : remainingBatches.length === 0 ? (
                <div style={{ background: "#F9FAFB", borderRadius: 12, padding: 28, textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>
                  All batches staged or roasted. Nice day.
                </div>
              ) : (
                <div
                  onDragOver={(e) => { if (draggedBatchId) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverZone("batches"); } }}
                  onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOverZone(z => z === "batches" ? null : z); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    /* Dropping back onto the queue = unstage. If the
                       dragged batch wasn't in Next (it was already in
                       queue), this is a no-op. */
                    if (draggedBatchId === roastingQueue.nextId) unstageBatch();
                    setDraggedBatchId(null); setDragOverZone(null);
                  }}
                  style={{
                    display: "flex", flexDirection: "column", gap: 6,
                    padding: dragOverZone === "batches" ? 6 : 0,
                    borderRadius: 10,
                    background: dragOverZone === "batches" ? "rgba(34,211,238,0.05)" : "transparent",
                    transition: "background 0.15s, padding 0.15s",
                  }}
                >
                  {remainingBatches.map(b => (
                    <BatchRow
                      key={b.id}
                      batch={b}
                      batchNum={roastingQueue.batchNumbers?.[b.id] || null}
                      variant="queue"
                      isDragging={draggedBatchId === b.id}
                      onDragStart={(e) => { setDraggedBatchId(b.id); e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", b.id); } catch { /* ignore */ } }}
                      onDragEnd={() => { setDraggedBatchId(null); setDragOverZone(null); }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* ── ROASTED TODAY ──────────────────────────────────────── */}
            {(roastingQueue.completed || []).length > 0 && (
              <div style={{ marginTop: 28, paddingTop: 16, borderTop: "1px solid #F3F4F6" }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.8 }}>
                    Roasted today · {roastingQueue.completed.length}
                  </div>
                  <button
                    onClick={clearCompletedToday}
                    style={{ fontSize: 11, color: "#9CA3AF", background: "transparent", border: "none", cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                  >Clear</button>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {roastingQueue.completed.map(c => (
                    <CompletedRoastRow
                      key={c.batchId}
                      entry={c}
                      onClick={() => setSelectedCompletedId(c.batchId)}
                    />
                  ))}
                </div>
              </div>
            )}

          </div>
        );
      }

      case "analytics": {
        if (analyticsView === "produced") {
          return (
            <div>
              {header}
              <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
                {[
                  { l: "Ready", v: producedOrders.length },
                  { l: "Unique products", v: productionByProduct.length },
                  { l: "Units total", v: productionByProduct.reduce((s, p) => s + p.qty, 0) },
                  { l: "Top product", v: productionByProduct[0]?.name || "—" },
                ].map(s => (
                  <div key={s.l} style={{ flex: 1, background: "#fff", borderRadius: 10, border: "1px solid #F3F4F6", padding: "14px 16px" }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4 }}>{s.l}</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#000000", marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.v}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 20, marginBottom: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#000000", marginBottom: 14 }}>Units produced by product</div>
                {productionByProduct.length === 0 ? (
                  <div style={{ padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 15 }}>Nothing ready yet. Mark a scheduled order as ready to record it here.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(220, productionByProduct.length * 28)}>
                    <ReBarChart data={productionByProduct} layout="vertical" margin={{ left: 12, right: 24, top: 8, bottom: 8 }}>
                      <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 12, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 13, fill: "#374151" }} axisLine={false} tickLine={false} width={110} />
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, fontFamily: "Inter" }} />
                      <Bar dataKey="qty" fill="#000000" radius={[0, 4, 4, 0]} />
                    </ReBarChart>
                  </ResponsiveContainer>
                )}
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Product", "Orders", "Units (lb)"].map(h => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: h === "Product" ? "left" : "right", fontSize: 13, fontWeight: 500, color: "#9CA3AF", borderBottom: "1px solid #F3F4F6" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {productionByProduct.map(p => (
                    <tr key={p.name} style={{ borderBottom: "1px solid #F9FAFB" }}>
                      <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 500, color: "#000000" }}>{p.name}</td>
                      <td style={{ padding: "12px 14px", fontSize: 15, color: "#6B7280", textAlign: "right" }}>{p.orders}</td>
                      <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 600, color: "#000000", textAlign: "right" }}>{p.qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        // PROFITABILITY view — default
        const { daily, byProduct, byCustomer, totalRevenue, totalCost, totalMargin, marginPct, totalUnits, totalLbs, orderCount, dataSpanDays, windowExceedsData, firstOrderAt, forecast7d, forecast30d, forecastHorizon, hasForecast, todayKey, granularity, growthRate } = profitabilityData;
        const fmt = (n) => `$${Math.round(n).toLocaleString()}`;
        const fmtShort = (n) => n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Math.round(n)}`;
        const maxProductRevenue = Math.max(1, ...byProduct.map(p => p.revenue));
        const windows = [
          { d: 7, l: "7d" }, { d: 30, l: "30d" }, { d: 90, l: "90d" },
          { d: 180, l: "180d" }, { d: 365, l: "1y" }, { d: "all", l: "All" },
        ];
        return (
          <div>
            {/* Header with window selector */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #F3F4F6", gap: 16 }}>
              <div>
                <h1 style={{ fontSize: 20, fontWeight: 600, color: "#000000", margin: 0, lineHeight: "28px" }}>Profitability</h1>
                <p style={{ fontSize: 16, color: "#6B7280", margin: "2px 0 0", lineHeight: "20px" }}>
                  Revenue, estimated cost, and margin {profitabilityWindow === "all" ? "across all orders" : `across ${profitabilityWindow} days`}.
                  {avgGreenCostPerLb == null && " Add green sources with $/lb in Inventory to enable cost estimates."}
                </p>
              </div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                {/* Forecast toggle — pill switch, slightly taller than the window pills */}
                <button
                  onClick={() => setForecastEnabled(v => !v)}
                  title={forecastEnabled ? "Hide forecast" : "Show forecast"}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 10,
                    padding: "8px 14px 8px 12px", borderRadius: 10,
                    border: `1px solid ${forecastEnabled ? "#2563EB" : "#E5E7EB"}`,
                    background: forecastEnabled ? "#2563EB" : "#fff",
                    color: forecastEnabled ? "#fff" : "#6B7280",
                    fontSize: 15, fontWeight: 500, cursor: "pointer",
                    fontFamily: "Inter, sans-serif", whiteSpace: "nowrap",
                  }}
                >
                  <span style={{
                    width: 28, height: 16, borderRadius: 8, position: "relative",
                    background: forecastEnabled ? "#fff" : "#D1D5DB", transition: "background 0.15s",
                  }}>
                    <span style={{
                      position: "absolute", top: 2, left: forecastEnabled ? 14 : 2,
                      width: 12, height: 12, borderRadius: 6,
                      background: forecastEnabled ? "#2563EB" : "#fff",
                      transition: "left 0.15s",
                    }} />
                  </span>
                  Forecast
                </button>
                <div style={{ display: "inline-flex", background: "#F3F4F6", borderRadius: 10, padding: 4, gap: 2 }}>
                  {windows.map(w => (
                    <button
                      key={w.d}
                      onClick={() => setProfitabilityWindow(w.d)}
                      style={{
                        padding: "6px 14px", borderRadius: 8, border: "none",
                        background: profitabilityWindow === w.d ? "#000000" : "transparent",
                        color: profitabilityWindow === w.d ? "#fff" : "#6B7280",
                        fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                      }}
                    >{w.l}</button>
                  ))}
                </div>
              </div>
            </div>

            {profitabilityError && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 16px", background: "#FEF2F2", borderRadius: 8, border: "1px solid #FECACA", marginBottom: 16 }}>
                <AlertTriangle size={16} color="#DC2626" strokeWidth={1.6} />
                <span style={{ fontSize: 14, color: "#B91C1C" }}>{profitabilityError}</span>
              </div>
            )}
            {orderCount > 0 && windowExceedsData && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 16px", background: "#FEF3C7", borderRadius: 8, border: "1px solid #FDE68A", marginBottom: 16 }}>
                <AlertTriangle size={16} color="#D97706" strokeWidth={1.6} style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 14, color: "#92400E" }}>
                  You selected <strong>{profitabilityWindow} days</strong>, but Shopify is only returning the last <strong>{dataSpanDays} days</strong> of orders (default scope limit). Request the <code style={{ fontSize: 13 }}>read_all_orders</code> scope in your Shopify app to unlock older history.
                </span>
              </div>
            )}

            {/* KPIs */}
            <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
              {[
                { l: "Revenue", v: fmt(totalRevenue) },
                { l: "Est. cost", v: fmt(totalCost) },
                { l: "Est. margin", v: fmt(totalMargin), color: totalMargin >= 0 ? "#000000" : "#DC2626" },
                { l: "Margin %", v: `${marginPct.toFixed(1)}%`, color: marginPct >= 30 ? "#16A34A" : marginPct >= 15 ? "#EA580C" : "#DC2626" },
                { l: "Orders", v: orderCount.toLocaleString() },
              ].map(s => (
                <div key={s.l} style={{ flex: 1, background: "#fff", borderRadius: 10, border: "1px solid #F3F4F6", padding: "14px 16px" }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4 }}>{s.l}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: s.color || "#000000", marginTop: 4 }}>{s.v}</div>
                </div>
              ))}
            </div>
            {hasForecast && (
              <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
                {[
                  { l: "Forecast · next 7d", v: fmt(forecast7d) },
                  { l: "Forecast · next 30d", v: fmt(forecast30d) },
                  { l: "Avg daily (30d fwd)", v: fmt(forecast30d / 30) },
                ].map(s => (
                  <div key={s.l} style={{ flex: 1, background: "#EFF6FF", borderRadius: 10, border: "1px solid #BFDBFE", padding: "14px 16px" }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "#1D4ED8", textTransform: "uppercase", letterSpacing: 0.4 }}>{s.l}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "#1E3A8A", marginTop: 4 }}>{s.v}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Revenue + Cost over time — AreaChart matching RoastCurve style */}
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 20, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#000000" }}>
                  Revenue over time
                  {granularity !== "daily" && <span style={{ fontSize: 12, fontWeight: 500, color: "#9CA3AF", marginLeft: 6 }}>· {granularity === "weekly" ? "Weekly" : "Monthly"}</span>}
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: 13, alignItems: "center" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#374151" }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: "#000000" }} /> Revenue
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#6B7280" }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: "#2C5766" }} /> Est. cost
                  </span>
                  {hasForecast && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#1D4ED8" }}>
                      <span style={{ width: 16, height: 2, background: "#2563EB", borderTop: "2px dashed #2563EB" }} /> Forecast
                    </span>
                  )}
                </div>
              </div>
              {profitabilityLoading ? (
                <div style={{ padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>Loading orders from Shopify…</div>
              ) : daily.every(d => d.revenue === 0) ? (
                <div style={{ padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>
                  No orders in this window yet. Shopify's default window only exposes last 60 days — request the <code>read_all_orders</code> scope for older data.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={daily} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#000000" stopOpacity={0.22} />
                        <stop offset="100%" stopColor="#000000" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.22} />
                        <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="fcGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563EB" stopOpacity={0.18} />
                        <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: "#9CA3AF" }}
                      axisLine={false} tickLine={false}
                      tickFormatter={(d) => {
                        const dt = new Date(d);
                        if (granularity === "monthly") {
                          return dt.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
                        }
                        return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
                      }}
                      interval={Math.max(0, Math.floor(daily.length / 8))}
                    />
                    <YAxis tick={{ fontSize: 12, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={fmtShort} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, fontFamily: "Inter", boxShadow: "0 4px 12px rgba(0,0,0,0.04)" }}
                      formatter={(v, name) => v != null ? [fmt(v), name] : null}
                      labelFormatter={(d) => {
                        const dt = new Date(d);
                        if (granularity === "monthly") {
                          return dt.toLocaleDateString("en-US", { month: "long", year: "numeric" });
                        }
                        if (granularity === "weekly") {
                          return `Week of ${dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
                        }
                        return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
                      }}
                    />
                    {hasForecast && todayKey && (
                      <ReferenceLine
                        x={todayKey}
                        stroke="#D1D5DB"
                        strokeDasharray="3 3"
                        label={{ value: "Today", position: "top", fill: "#9CA3AF", fontSize: 11 }}
                      />
                    )}
                    <Area type="monotone" dataKey="revenue" stroke="#000000" strokeWidth={2.5} fill="url(#revGrad)" name="Revenue" dot={false} connectNulls={false} />
                    <Area type="monotone" dataKey="cost" stroke="#06B6D4" strokeWidth={2} fill="url(#costGrad)" name="Est. cost" dot={false} connectNulls={false} />
                    {hasForecast && (
                      <Area type="monotone" dataKey="forecast" stroke="#2563EB" strokeWidth={2} strokeDasharray="5 4" fill="url(#fcGrad)" name="Forecast" dot={false} connectNulls={false} />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Top products by revenue */}
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 20, marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#000000", marginBottom: 14 }}>By product</div>
              {byProduct.length === 0 ? (
                <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>No product data in this window.</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Product", "Units", "Revenue", "Est. cost", "Margin", "Margin %", ...(hasForecast ? ["Next 30d"] : []), ""].map(h => (
                        <th key={h} style={{ padding: "10px 14px", textAlign: h === "Product" || h === "" ? "left" : "right", fontSize: 13, fontWeight: 500, color: h === "Next 30d" ? "#1D4ED8" : "#9CA3AF", borderBottom: "1px solid #F3F4F6" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {byProduct.map(p => (
                      <tr key={p.name} style={{ borderBottom: "1px solid #F9FAFB" }}>
                        <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 600, color: "#000000" }}>{p.name}</td>
                        <td style={{ padding: "12px 14px", fontSize: 15, color: "#6B7280", textAlign: "right" }}>{p.units}</td>
                        <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 600, color: "#000000", textAlign: "right" }}>{fmt(p.revenue)}</td>
                        <td style={{ padding: "12px 14px", fontSize: 15, color: "#6B7280", textAlign: "right" }}>{fmt(p.cost)}</td>
                        <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 500, color: p.margin >= 0 ? "#000000" : "#DC2626", textAlign: "right" }}>{fmt(p.margin)}</td>
                        <td style={{ padding: "12px 14px", fontSize: 14, fontWeight: 500, color: p.marginPct >= 30 ? "#16A34A" : p.marginPct >= 15 ? "#EA580C" : "#DC2626", textAlign: "right" }}>{p.marginPct.toFixed(1)}%</td>
                        {hasForecast && (
                          <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 600, color: "#1D4ED8", textAlign: "right" }}>{fmt(p.forecastRevenue)}</td>
                        )}
                        <td style={{ padding: "12px 14px", width: 140 }}>
                          <div style={{ height: 6, background: "#F3F4F6", borderRadius: 3, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${(p.revenue / maxProductRevenue) * 100}%`, background: "#000000" }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Top customers by revenue */}
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 20, marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#000000", marginBottom: 14 }}>By customer</div>
              {byCustomer.length === 0 ? (
                <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>No customer data in this window.</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Customer", "Orders", "Units", "Revenue", ...(hasForecast ? ["Next 30d"] : [])].map(h => (
                        <th key={h} style={{ padding: "10px 14px", textAlign: h === "Customer" ? "left" : "right", fontSize: 13, fontWeight: 500, color: h === "Next 30d" ? "#1D4ED8" : "#9CA3AF", borderBottom: "1px solid #F3F4F6" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {byCustomer.slice(0, 20).map(c => (
                      <tr key={c.name} style={{ borderBottom: "1px solid #F9FAFB" }}>
                        <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 500, color: "#000000" }}>{c.name}</td>
                        <td style={{ padding: "12px 14px", fontSize: 15, color: "#6B7280", textAlign: "right" }}>{c.orders}</td>
                        <td style={{ padding: "12px 14px", fontSize: 15, color: "#6B7280", textAlign: "right" }}>{c.units}</td>
                        <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 600, color: "#000000", textAlign: "right" }}>{fmt(c.revenue)}</td>
                        {hasForecast && (
                          <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 600, color: "#1D4ED8", textAlign: "right" }}>{fmt(c.forecastRevenue)}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Methodology note */}
            <div style={{ fontSize: 13, color: "#9CA3AF", lineHeight: 1.5, padding: "12px 16px", background: "#F9FAFB", borderRadius: 8 }}>
              <div><strong style={{ color: "#374151" }}>Cost:</strong> weighted-average green cost across active inventory lots{avgGreenCostPerLb != null && ` ($${avgGreenCostPerLb.toFixed(2)}/lb)`} ÷ (1 − loss %) × lbs sold. Weight from line-item grams, or parsed from variant title. Labor and packaging not included yet.</div>
              <div style={{ marginTop: 6 }}><strong style={{ color: "#374151" }}>Next 30d forecast (per product / customer):</strong> that item's revenue over the last 60 days ÷ 60 × 30 × trend factor. A customer who stopped ordering 2 months ago projects to ~$0; an active weekly buyer projects their typical monthly spend.</div>
              <div style={{ marginTop: 6 }}><strong style={{ color: "#374151" }}>Forecast line on chart:</strong> mirrors the most recent {granularity === "monthly" ? "months" : granularity === "weekly" ? "weeks" : "days"} of actual revenue forward, with a trend factor applied (±{Math.abs(growthRate * 100).toFixed(0)}% over the full horizon based on 30d-vs-60d daily-average comparison).</div>
            </div>
          </div>
        );
      }

      case "cupping": {
        // Cupping main view branches: Coffees > Cuppers > Sources >
        // selected session > sessions table (default).
        if (cuppingView === "coffees") {
          // Flatten every coffee across every session, attaching its
          // home session for navigation. Also stamp the per-session
          // auto-ID prefix here (same hash used inside the session
          // detail view) so the Samples table + label printouts agree
          // on labels.
          const recompute = (cof) => sampleLiveTotal(cof);
          const sessionCoffees = cuppings.flatMap(s => {
            const prefix = getSessionCode(s);
            return (s.coffees || []).map((co, idx) => ({
              ...co,
              sessionId: s.id,
              sampleId: co.id,
              sessionName: s.name || s.coffeeName || "Cupping session",
              sessionDate: s.cuppingDate || null,
              liveTotal: recompute(co),
              autoId: `${prefix}-${String(idx + 1).padStart(2, "0")}`,
              isStandalone: false,
            }));
          });
          // Also surface standalone samples — coffees that have landed
          // on the bench but haven't been attached to a cupping session
          // yet. They appear in the same table with an "Unscored" pill
          // so the operator sees the full intake pipeline in one place.
          const standaloneCoffees = (samples || []).map(sa => ({
            id: sa.id,
            sampleId: sa.id,
            coffeeName: sa.coffeeName || "(unnamed)",
            sourcedFrom: sa.sourcedFrom || "",
            roastDate: sa.roastDate || "",
            origin: sa.origin || "",
            process: sa.process || "",
            variety: sa.variety || "",
            sessionId: null,
            sessionName: "",
            sessionDate: sa.receivedAt ? new Date(sa.receivedAt).toISOString().slice(0, 10) : null,
            liveTotal: 0,
            status: "unscored",
            autoId: null,
            isStandalone: true,
          }));
          const allCoffees = [...sessionCoffees, ...standaloneCoffees];
          // For each sample-name (case-insensitive) collect every
          // session it appears in, so each row can surface "also in
          // X, Y" without losing its per-session score / approval.
          const sessionsByName = (() => {
            const map = new Map();
            for (const co of allCoffees) {
              const k = String(co.coffeeName || "").trim().toLowerCase();
              if (!k) continue;
              if (!map.has(k)) map.set(k, []);
              map.get(k).push({
                sessionId: co.sessionId,
                sessionName: co.sessionName,
                sessionDate: co.sessionDate,
              });
            }
            // Sort each cluster by session date (newest first)
            for (const arr of map.values()) {
              arr.sort((a, b) => String(b.sessionDate || "").localeCompare(String(a.sessionDate || "")));
            }
            return map;
          })();
          const counts = {
            all: allCoffees.length,
            approved: allCoffees.filter(co => co.status === "approved").length,
            rejected: allCoffees.filter(co => co.status === "rejected").length,
            pending: allCoffees.filter(co => !co.isStandalone && (!co.status || co.status === "pending")).length,
            unscored: allCoffees.filter(co => co.isStandalone).length,
          };
          const filtered = allCoffees.filter(co => {
            if (coffeeListFilter === "all") return true;
            if (coffeeListFilter === "pending") return !co.isStandalone && (!co.status || co.status === "pending");
            if (coffeeListFilter === "unscored") return co.isStandalone;
            return co.status === coffeeListFilter;
          });
          const scoreColorLocal = (n) => n >= 90 ? "#B8860B" : n >= 85 ? "#16A34A" : n >= 80 ? "#111111" : "#6B7280";

          return (
            <div>
              <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
                <div>
                  <h1 style={{ fontSize: 22, fontWeight: 700, color: "#000", margin: 0, lineHeight: "28px", letterSpacing: "-0.01em" }}>Samples</h1>
                  <p style={{ fontSize: 14, color: "#6B7280", margin: "4px 0 0", lineHeight: 1.5 }}>Every sample scored across every session, with its current approval status. Click any row to jump to its session.</p>
                </div>
                {filtered.length > 0 && (
                  <div style={{ display: "inline-flex", gap: 6, flexShrink: 0 }}>
                    <button
                      onClick={() => printSampleLabels(filtered, "shipping")}
                      title="Print shipping-size labels — one per page (4×6 in)"
                      style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 5 }}
                    ><ClipboardList size={12} strokeWidth={1.8} /> Print Labels</button>
                  </div>
                )}
              </div>
              {/* Status tabs */}
              <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
                {[
                  { v: "all", label: "All" },
                  { v: "unscored", label: "Not in session" },
                  { v: "pending", label: "Pending" },
                  { v: "approved", label: "Approved" },
                  { v: "rejected", label: "Rejected" },
                ].map(t => {
                  const active = coffeeListFilter === t.v;
                  return (
                    <button
                      key={t.v}
                      onClick={() => setCoffeeListFilter(t.v)}
                      style={{
                        padding: "7px 14px", borderRadius: 8,
                        border: `1px solid ${active ? "#000" : "#E5E7EB"}`,
                        background: active ? "#000" : "#fff",
                        color: active ? "#fff" : "#374151",
                        fontSize: 14, fontWeight: 500, cursor: "pointer",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      {t.label}
                      <span style={{
                        marginLeft: 6, padding: "1px 7px", borderRadius: 99,
                        background: active ? "rgba(255,255,255,0.18)" : "#F3F4F6",
                        color: active ? "#fff" : "#9CA3AF",
                        fontSize: 11, fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                      }}>{counts[t.v]}</span>
                    </button>
                  );
                })}
              </div>
              {filtered.length === 0 ? (
                <div style={{ background: "#F9FAFB", borderRadius: 12, padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 16 }}>
                  {allCoffees.length === 0
                    ? "No samples yet. Open a session and add your first one."
                    : `No ${coffeeListFilter === "pending" ? "pending" : coffeeListFilter} samples.`}
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                  <colgroup>
                    <col />
                    <col style={{ width: 160 }} />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 180 }} />
                    <col style={{ width: 80 }} />
                    <col style={{ width: 220 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      {["Sample", "Source", "Roasted", "Session", "Score", ""].map((h, i) => (
                        <th key={i} style={{ padding: "10px 14px", textAlign: i >= 4 ? "right" : "left", fontSize: 13, fontWeight: 500, color: "#9CA3AF", borderBottom: "1px solid #F3F4F6" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(co => {
                      const status = co.status || "pending";
                      // Mutate status on the home session and persist via
                      // the same updateCuppingCoffees path that the
                      // session view uses. Click on the active button
                      // resets to pending.
                      const cycle = async (target) => {
                        const session = cuppings.find(s => s.id === co.sessionId);
                        if (!session) return;
                        const next = status === target ? "pending" : target;
                        const updated = (session.coffees || []).map(x => x.id === co.id ? { ...x, status: next } : x);
                        try { await updateCuppingCoffees(co.sessionId, updated); }
                        catch (err) { alert(err.message || "Couldn't update"); }
                      };
                      const rowKey = co.isStandalone ? `samp::${co.id}` : `${co.sessionId}::${co.id}`;
                      const onRowClick = () => {
                        if (co.isStandalone) {
                          // Open the New cupping modal pre-picked with this sample
                          // would be ideal; for now nudge the operator to the
                          // session-creation flow.
                          setAddCuppingOpen?.(true);
                          return;
                        }
                        setCuppingView("sessions");
                        setSelectedCuppingId(co.sessionId);
                      };
                      return (
                        <tr key={rowKey} style={{ borderBottom: "1px solid #F9FAFB", cursor: "pointer", opacity: co.isStandalone ? 0.92 : 1 }}
                          onClick={onRowClick}
                          onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <td style={{ padding: "12px 14px", fontWeight: 600, fontSize: 15, color: "#000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{co.coffeeName || <span style={{ color: "#9CA3AF" }}>(unnamed)</span>}</td>
                          <td style={{ padding: "12px 14px", fontSize: 14, color: "#374151" }}>{co.sourcedFrom || "—"}</td>
                          <td style={{ padding: "12px 14px", fontSize: 14, color: "#6B7280" }}>{co.roastDate ? new Date(co.roastDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}</td>
                          <td style={{ padding: "12px 14px", fontSize: 14, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {co.isStandalone ? (
                              <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 99, background: "#FEF3C7", color: "#92400E", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>Not in session</span>
                            ) : (() => {
                              const cluster = sessionsByName.get(String(co.coffeeName || "").trim().toLowerCase()) || [];
                              const others = cluster.filter(x => x.sessionId !== co.sessionId);
                              const tip = others.length > 0
                                ? `${co.sessionName}\nAlso in:\n  ${others.map(x => x.sessionName).join("\n  ")}`
                                : co.sessionName;
                              return (
                                <span title={tip} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{co.sessionName}</span>
                                  {others.length > 0 && (
                                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", color: "#6B7280", background: "#F4F4F5", border: "1px solid #E4E4E7", borderRadius: 99, padding: "2px 7px", whiteSpace: "nowrap" }}>+{others.length}</span>
                                  )}
                                </span>
                              );
                            })()}
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: 16, fontWeight: 700, color: scoreColorLocal(co.liveTotal), textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {co.liveTotal > 0 ? co.liveTotal.toFixed(2) : <span style={{ color: "#D1D5DB", fontWeight: 500 }}>—</span>}
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "right" }} onClick={e => e.stopPropagation()}>
                            {co.isStandalone ? (
                              <span style={{ fontSize: 12, color: "#9CA3AF", fontStyle: "italic" }}>Add to a session to score</span>
                            ) : (() => {
                              const styleFor = (kind, active) => {
                                const tint = kind === "approve"
                                  ? { bg: "#ECFDF5", border: "#BBF7D0", fg: "#047857" }
                                  : { bg: "#FEF2F2", border: "#FECACA", fg: "#B91C1C" };
                                return {
                                  display: "inline-flex", alignItems: "center", gap: 5,
                                  padding: "5px 12px", borderRadius: 6,
                                  border: `1px solid ${active ? tint.border : "#E5E7EB"}`,
                                  background: active ? tint.bg : "transparent",
                                  color: active ? tint.fg : "#9CA3AF",
                                  fontSize: 13, fontWeight: 500, cursor: "pointer",
                                  fontFamily: "Inter, sans-serif",
                                  transition: "background 0.12s, color 0.12s, border-color 0.12s",
                                };
                              };
                              return (
                                <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
                                  <button onClick={() => cycle("approved")} title={status === "approved" ? "Approved — click to reset" : "Approve"} style={styleFor("approve", status === "approved")}>
                                    <Check size={12} strokeWidth={2} /> Approve
                                  </button>
                                  <button onClick={() => cycle("rejected")} title={status === "rejected" ? "Rejected — click to reset" : "Reject"} style={styleFor("reject", status === "rejected")}>
                                    <X size={12} strokeWidth={2} /> Reject
                                  </button>
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        }
        if (cuppingView === "cuppers") {
          return (
            <div>
              <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
                <div>
                  <h1 style={{ fontSize: 22, fontWeight: 700, color: "#000", margin: 0, lineHeight: "28px", letterSpacing: "-0.01em" }}>Cuppers</h1>
                  <p style={{ fontSize: 14, color: "#6B7280", margin: "4px 0 0", lineHeight: 1.5 }}>People who score coffees in your sessions. Each cupper gets their own tab inside a coffee so they can record individual results.</p>
                </div>
                <button
                  onClick={() => setEditingCupper("new")}
                  style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}
                ><Plus size={13} strokeWidth={1.8} /> New cupper</button>
              </div>
              {cuppers.length === 0 ? (
                <div style={{ background: "#F9FAFB", borderRadius: 12, padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 16 }}>
                  No cuppers yet. Click <strong style={{ color: "#374151" }}>New cupper</strong> to add the people on your scoring panel.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                  <colgroup>
                    <col />
                    <col style={{ width: 280 }} />
                    <col style={{ width: 120 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      {["Name", "Email", ""].map((h, i) => (
                        <th key={i} style={{ padding: "10px 14px", textAlign: "left", fontSize: 13, fontWeight: 500, color: "#9CA3AF", borderBottom: "1px solid #F3F4F6" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cuppers.map(cu => (
                      <tr key={cu.id} style={{ borderBottom: "1px solid #F9FAFB", cursor: "pointer" }}
                        onClick={() => setEditingCupper(cu)}
                        onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <td style={{ padding: "12px 14px", fontWeight: 600, fontSize: 15, color: "#000" }}>{cu.name}</td>
                        <td style={{ padding: "12px 14px", fontSize: 14, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cu.email || "—"}</td>
                        <td style={{ padding: "12px 14px", textAlign: "right" }}>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              if (window.confirm(`Delete "${cu.name}"? Their scoring records on coffees stay attributed to the name.`)) removeCupper(cu.id);
                            }}
                            style={{ padding: "5px 10px", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#B91C1C", display: "inline-flex", alignItems: "center", gap: 4 }}
                          ><Trash2 size={11} strokeWidth={1.8} /> Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {editingCupper && (
                <CupperModal
                  cupper={editingCupper === "new" ? null : editingCupper}
                  onClose={() => setEditingCupper(null)}
                  onSave={async (data) => { upsertCupper(data); setEditingCupper(null); }}
                />
              )}
            </div>
          );
        }
        if (cuppingView === "sources") {
          return (
            <div>
              <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
                <div>
                  <h1 style={{ fontSize: 22, fontWeight: 700, color: "#000", margin: 0, lineHeight: "28px", letterSpacing: "-0.01em" }}>Green Vendors</h1>
                  <p style={{ fontSize: 14, color: "#6B7280", margin: "4px 0 0", lineHeight: 1.5 }}>Importers, farms, and brokers you buy your green coffee from. They show up when you cup a sample and as vendor options when you create a Purchasing PO.</p>
                </div>
                <button
                  onClick={() => setEditingSource("new")}
                  style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #000000", background: "#000000", color: "#fff", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}
                ><Plus size={13} strokeWidth={1.8} /> New green vendor</button>
              </div>
              {cuppingSources.length === 0 ? (
                <div style={{ background: "#F9FAFB", borderRadius: 12, padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 16 }}>
                  No sources yet. Click <strong style={{ color: "#374151" }}>New source</strong> to add the importers, farms, or brokers you buy from.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                  <colgroup>
                    <col />
                    <col style={{ width: 130 }} />
                    <col style={{ width: 180 }} />
                    <col style={{ width: 220 }} />
                    <col style={{ width: 120 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      {["Name", "Type", "Region", "Contact", ""].map((h, i) => (
                        <th key={i} style={{ padding: "10px 14px", textAlign: "left", fontSize: 13, fontWeight: 500, color: "#9CA3AF", borderBottom: "1px solid #F3F4F6" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cuppingSources.map(s => {
                      const typeLabel = ({ importer: "Importer", farm: "Farm / direct", broker: "Broker", other: "Other" }[s.type] || s.type || "—");
                      const contactStr = [s.contactName, s.contactEmail].filter(Boolean).join(" · ");
                      return (
                        <tr key={s.id} style={{ borderBottom: "1px solid #F9FAFB", cursor: "pointer" }}
                          onClick={() => setEditingSource(s)}
                          onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <td style={{ padding: "12px 14px", fontWeight: 600, fontSize: 15, color: "#000" }}>{s.name}</td>
                          <td style={{ padding: "12px 14px", fontSize: 14, color: "#374151" }}>{typeLabel}</td>
                          <td style={{ padding: "12px 14px", fontSize: 14, color: "#6B7280" }}>{s.region || "—"}</td>
                          <td style={{ padding: "12px 14px", fontSize: 14, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={contactStr}>{contactStr || "—"}</td>
                          <td style={{ padding: "12px 14px", textAlign: "right" }}>
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                if (window.confirm(`Delete "${s.name}"? Coffees that referenced it keep the text.`)) removeCuppingSource(s.id);
                              }}
                              style={{ padding: "5px 10px", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#B91C1C", display: "inline-flex", alignItems: "center", gap: 4 }}
                            ><Trash2 size={11} strokeWidth={1.8} /> Delete</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {editingSource && (
                <CuppingSourceModal
                  source={editingSource === "new" ? null : editingSource}
                  onClose={() => setEditingSource(null)}
                  onSave={async (data) => { upsertCuppingSource(data); setEditingSource(null); }}
                />
              )}
            </div>
          );
        }
        // When a session is selected from the side-nav, the main view
        // renders that session inline (header + coffees list + status
        // actions). Otherwise it shows the at-a-glance table.
        if (selectedCupping) {
          return (
            <CuppingDetailModal
              cupping={selectedCupping}
              onClose={() => setSelectedCuppingId(null)}
              onDelete={(id) => { deleteCupping(id); setSelectedCuppingId(null); }}
              onSetStatus={updateCuppingStatus}
              onUpdateCoffees={updateCuppingCoffees}
              onUpdateMeta={updateCuppingMeta}
              coffeeSuggestions={Array.from(new Set([
                ...greenLots.filter(l => !l.archivedAt).map(l => l.name).filter(Boolean),
                ...cuppings.flatMap(cc => (cc.coffees || []).map(x => x.coffeeName)).filter(Boolean),
              ]))}
              sources={cuppingSources}
              cuppers={cuppers}
              user={user}
              effectiveDemoMode={effectiveDemoMode}
              onSaveCupper={upsertCupper}
            />
          );
        }
        const filtered = cuppingStatusFilter === "all"
          ? cuppings
          : cuppings.filter(c => (cuppingStatusFilter === "pending" ? (!c.status || c.status === "pending") : c.status === cuppingStatusFilter));
        const scoreColor = (t) => t >= 90 ? "#B8860B" : t >= 85 ? "#16A34A" : t >= 80 ? "#111111" : "#6B7280";
        const scoreGrade = (t) => t >= 90 ? "Outstanding" : t >= 85 ? "Excellent" : t >= 80 ? "Very Good" : "Below Specialty";
        const statusBadge = (s) => {
          const status = s || "pending";
          if (status === "approved") return { label: "Approved", bg: "#F0FDF4", fg: "#16A34A" };
          if (status === "rejected") return { label: "Rejected", bg: "#FEF2F2", fg: "#DC2626" };
          return { label: "Pending", bg: "#FFF7ED", fg: "#EA580C" };
        };
        return (
          <div>
            <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #F3F4F6" }}>
              <h1 style={{ fontSize: 20, fontWeight: 600, color: "#000000", margin: 0, lineHeight: "28px" }}>Cupping & QC</h1>
              <p style={{ fontSize: 16, color: "#6B7280", margin: "2px 0 0", lineHeight: "20px" }}>SCA-style scorecards. Approve or reject coffees from the table or the detail view.</p>
            </div>

            {filtered.length === 0 ? (
              <div style={{ background: "#F9FAFB", borderRadius: 12, padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 16 }}>
                {cuppings.length === 0 ? "No cuppings yet. Click New cupping to log your first scoresheet." : "No cuppings in this status yet."}
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: 70 }} />
                  <col style={{ width: 130 }} />
                  <col />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 150 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 210 }} />
                </colgroup>
                <thead>
                  <tr>
                    {["ID", "Date", "Session", "Coffees", "Cupper", "Avg score", ""].map(h => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: h === "Avg score" ? "right" : "left", fontSize: 13, fontWeight: 500, color: "#9CA3AF", borderBottom: "1px solid #F3F4F6" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => {
                    const isHovered = hoveredCuppingId === c.id;
                    const status = c.status || "pending";
                    const coffees = c.coffees || [];
                    // Recompute on the fly: only count a coffee once it
                    // has at least one primary attribute set, so the
                    // session avg is "—" instead of 30.00 for sessions
                    // where nobody's actually scored anything yet.
                    const recompute = (cof) => {
                      const sc = cof.scores;
                      if (!sc) return 0;
                      const pk = ["fragrance", "flavor", "aftertaste", "acidity", "body", "balance", "overall"];
                      if (!pk.some(k => sc[k] != null)) return 0;
                      const prim = pk.reduce((a, k) => a + (Number(sc[k]) || 0), 0);
                      const secV = (k) => (sc[k] != null ? Number(sc[k]) : 10);
                      const sec = secV("uniformity") + secV("cleanCup") + secV("sweetness");
                      return +(prim + sec - (Number(cof.defects) || 0)).toFixed(2);
                    };
                    const totals = coffees.map(recompute);
                    const scored = coffees.filter((_, i) => totals[i] > 0);
                    const scoredVals = totals.filter(t => t > 0);
                    const avg = scoredVals.length > 0
                      ? +(scoredVals.reduce((s, t) => s + t, 0) / scoredVals.length).toFixed(2)
                      : 0;
                    const sessionTitle = c.name || c.coffeeName || "Cupping session";
                    return (
                      <tr
                        key={c.id}
                        style={{ borderBottom: "1px solid #F9FAFB", cursor: "pointer", background: isHovered ? "#F9FAFB" : "transparent" }}
                        onClick={() => setSelectedCuppingId(c.id)}
                        onMouseEnter={() => setHoveredCuppingId(c.id)}
                        onMouseLeave={() => setHoveredCuppingId(null)}
                      >
                        <td style={{ padding: "12px 14px" }}>
                          <span style={{
                            display: "inline-block",
                            background: "#F4F4F5", color: "#52525B",
                            borderRadius: 5, padding: "3px 8px",
                            fontSize: 11, fontWeight: 700,
                            letterSpacing: "0.04em",
                            fontVariantNumeric: "tabular-nums",
                          }}>{getSessionCode(c)}</span>
                        </td>
                        <td style={{ padding: "12px 14px", fontSize: 14, color: "#6B7280" }}>{c.cuppingDate}</td>
                        <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 600, color: "#000000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sessionTitle}</td>
                        <td style={{ padding: "12px 14px", fontSize: 14, color: "#374151" }}>{coffees.length}</td>
                        <td style={{ padding: "12px 14px", fontSize: 14, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.cupperName || "—"}</td>
                        <td style={{ padding: "12px 14px", fontSize: 18, fontWeight: 700, color: scoreColor(avg), textAlign: "right" }}>
                          {scored.length === 0 ? <span style={{ color: "#D1D5DB", fontSize: 14, fontWeight: 500 }}>—</span> : (avg >= 85 ? <span className="pr-gold">{avg.toFixed(2)}</span> : avg.toFixed(2))}
                        </td>
                        {/* Session-level Approve/Reject removed — status is
                            tracked per-coffee inside the session now. The
                            session row aggregates per-coffee status counts
                            via the score column. */}
                        <td style={{ padding: "12px 14px", textAlign: "right" }}>
                          {(() => {
                            const approvedN = coffees.filter(x => x.status === "approved").length;
                            const rejectedN = coffees.filter(x => x.status === "rejected").length;
                            if (approvedN === 0 && rejectedN === 0) return <span style={{ color: "#D1D5DB", fontSize: 13 }}>—</span>;
                            return (
                              <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
                                {approvedN > 0 && (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, padding: "3px 9px", borderRadius: 6, background: "#F0FDF4", color: "#16A34A" }}>
                                    <Check size={11} /> {approvedN}
                                  </span>
                                )}
                                {rejectedN > 0 && (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, padding: "3px 9px", borderRadius: 6, background: "#FEF2F2", color: "#DC2626" }}>
                                    <X size={11} /> {rejectedN}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        );
      }
      case "purchasing": {
        // Only approved cupping samples are pickable in PO line items.
        // Each sample exposes a flat label so the dropdown's value is
        // human-readable on its own.
        const approvedSamples = (cuppings || []).flatMap(s =>
          (s.coffees || [])
            .filter(co => co.status === "approved")
            .map(co => ({
              id: co.id,
              sessionId: s.id,
              coffeeName: co.coffeeName || "(unnamed)",
              sourcedFrom: co.sourcedFrom || "",
              roastDate: co.roastDate || "",
              label: [co.coffeeName, co.sourcedFrom].filter(Boolean).join(" — "),
            }))
        );
        return <PurchasingPage view={purchasingView} setView={setPurchasingView} newSignal={purchasingNewSignal} greenVendors={cuppingSources} approvedSamples={approvedSamples} />;
      }
      case "sell":
        return <PurchasingPage view={sellView} setView={setSellView} newSignal={purchasingNewSignal} greenVendors={cuppingSources} approvedSamples={[]} />;
      case "inventory": {
        // Derived stats across lots — all weights are server-computed from events.
        const activeLots = greenLots.filter(l => !l.archivedAt);
        const lotsByFilter = inventoryFilter === "low"
          ? activeLots.filter(l => l.currentWeightLbs <= (l.lowStockLbs || 20))
          : inventoryFilter === "aging"
            ? activeLots.filter(l => l.ageDays >= greenAgingDays)
            : activeLots;
        const totalGreenLbs = activeLots.reduce((s, l) => s + (l.currentWeightLbs || 0), 0);
        const lowStockCount = activeLots.filter(l => l.currentWeightLbs <= (l.lowStockLbs || 20)).length;
        const agingCount = activeLots.filter(l => l.ageDays >= greenAgingDays).length;
        const staleCount = activeLots.filter(l => l.ageDays >= greenStaleDays).length;
        const oldestLot = activeLots.slice().sort((a, b) => (b.ageDays || 0) - (a.ageDays || 0))[0];
        // Inventory valuation — sum of (current weight × pricePerLb) for
        // every lot that has a cost recorded. Lots without pricePerLb
        // are excluded from the total but still shown in the count.
        const totalInventoryValue = activeLots.reduce(
          (s, l) => s + ((Number(l.currentWeightLbs) || 0) * (Number(l.pricePerLb) || 0)),
          0
        );
        // 30-day burn rate: sum of negative weight events (roast +
        // sample) over the last 30 days, expressed as lbs/day. Days
        // runway = current stock / burn. Used for reorder forecasting.
        const cutoff30 = Date.now() - 30 * 24 * 60 * 60 * 1000;
        const burnEvents = activeLots.flatMap(l =>
          (l.events || []).filter(e => (e.type === "roast" || e.type === "sample") && (e.at || 0) >= cutoff30 && Number(e.weightLbs) < 0)
        );
        const burn30Lbs = burnEvents.reduce((s, e) => s + Math.abs(Number(e.weightLbs) || 0), 0);
        const dailyBurn = burn30Lbs / 30;
        const daysRunway = dailyBurn > 0.5 ? Math.round(totalGreenLbs / dailyBurn) : null;
        // Per-day burn for the 30-day sparkline.
        const burnPerDay = (() => {
          const buckets = new Array(30).fill(0);
          for (const e of burnEvents) {
            const dayIndex = 29 - Math.floor((Date.now() - (e.at || 0)) / (24 * 60 * 60 * 1000));
            if (dayIndex >= 0 && dayIndex < 30) buckets[dayIndex] += Math.abs(Number(e.weightLbs) || 0);
          }
          return buckets;
        })();
        // Top origins + vendors by current stock.
        const groupBy = (arr, key) => {
          const m = new Map();
          for (const l of arr) {
            const k = (l[key] || "Unknown").trim() || "Unknown";
            const cur = m.get(k) || { name: k, lbs: 0, value: 0, count: 0 };
            cur.lbs += Number(l.currentWeightLbs) || 0;
            cur.value += (Number(l.currentWeightLbs) || 0) * (Number(l.pricePerLb) || 0);
            cur.count += 1;
            m.set(k, cur);
          }
          return Array.from(m.values()).sort((a, b) => b.lbs - a.lbs);
        };
        const byOrigin = groupBy(activeLots.filter(l => l.currentWeightLbs > 0), "origin");
        const byVendor = groupBy(activeLots.filter(l => l.currentWeightLbs > 0), "supplier");
        // Aging buckets for the donut.
        const aging = {
          fresh:  activeLots.filter(l => l.ageDays <  greenAgingDays && l.currentWeightLbs > 0).reduce((s, l) => s + l.currentWeightLbs, 0),
          aging:  activeLots.filter(l => l.ageDays >= greenAgingDays && l.ageDays < greenStaleDays && l.currentWeightLbs > 0).reduce((s, l) => s + l.currentWeightLbs, 0),
          stale:  activeLots.filter(l => l.ageDays >= greenStaleDays && l.currentWeightLbs > 0).reduce((s, l) => s + l.currentWeightLbs, 0),
        };
        const lotsToReorder = activeLots
          .filter(l => l.currentWeightLbs <= (l.lowStockLbs || 20) && l.currentWeightLbs > 0)
          .sort((a, b) => (a.currentWeightLbs / (a.lowStockLbs || 20)) - (b.currentWeightLbs / (b.lowStockLbs || 20)));
        const lotsAgingSoon = activeLots
          .filter(l => l.ageDays >= greenAgingDays && l.ageDays < greenStaleDays && l.currentWeightLbs > 0)
          .sort((a, b) => b.ageDays - a.ageDays);
        // Receiving log — every receipt event flattened across lots,
        // newest first. Each entry carries before/after lot weights
        // so the detail view can show "5 lb → 12 lb" without re-
        // walking the events array client-side.
        const receivingLog = activeLots.flatMap(l => {
          const sorted = [...(l.events || [])].sort((a, b) => (a.at || 0) - (b.at || 0));
          let running = 0;
          const out = [];
          for (const e of sorted) {
            const delta = Number(e.weightLbs) || 0;
            const before = running;
            running += delta;
            if (e.type !== "receipt") continue;
            out.push({
              id: e.id,
              at: e.at,
              lotId: l.id,
              lotName: l.name,
              origin: l.origin,
              supplier: l.supplier,
              weightLbs: delta,
              beforeLbs: before,
              afterLbs: running,
              value: delta * (Number(l.pricePerLb) || 0),
              pricePerLb: Number(l.pricePerLb) || 0,
              note: e.note || "",
              poNumber: e.poNumber || null,
              poId: e.poId || null,
            });
          }
          return out;
        }).sort((a, b) => (b.at || 0) - (a.at || 0));

        // ── Analytics: turnover, COGS, dead stock, forecast ───────
        // 30-day green consumed (lbs + $).
        const cogs30Lbs = activeLots.reduce((acc, l) => {
          const used = (l.events || [])
            .filter(e => (e.type === "roast" || e.type === "sample") && (e.at || 0) >= cutoff30 && Number(e.weightLbs) < 0)
            .reduce((s, e) => s + Math.abs(Number(e.weightLbs) || 0), 0);
          return acc + used;
        }, 0);
        const cogs30Value = activeLots.reduce((acc, l) => {
          const used = (l.events || [])
            .filter(e => (e.type === "roast" || e.type === "sample") && (e.at || 0) >= cutoff30 && Number(e.weightLbs) < 0)
            .reduce((s, e) => s + Math.abs(Number(e.weightLbs) || 0), 0);
          return acc + used * (Number(l.pricePerLb) || 0);
        }, 0);
        // Annualized turnover = (12 × COGS_30d) / avg inventory $.
        const annualCogsValue = cogs30Value * 12;
        const turnoverPerYear = totalInventoryValue > 0 ? annualCogsValue / totalInventoryValue : null;
        // Dead stock = lots with no roast/sample events in the last
        // 60 days that still hold inventory. Summed by $ at cost.
        const cutoff60 = Date.now() - 60 * 24 * 60 * 60 * 1000;
        const deadLots = activeLots.filter(l => {
          if ((l.currentWeightLbs || 0) <= 0) return false;
          const recent = (l.events || []).some(e => (e.type === "roast" || e.type === "sample") && (e.at || 0) >= cutoff60);
          return !recent;
        });
        const deadStockLbs = deadLots.reduce((s, l) => s + (l.currentWeightLbs || 0), 0);
        const deadStockValue = deadLots.reduce((s, l) => s + (l.currentWeightLbs || 0) * (Number(l.pricePerLb) || 0), 0);
        // Demand forecast over the next 30 days at current burn pace.
        const forecastNext30Lbs = dailyBurn * 30;
        const forecastShortfallLbs = Math.max(0, forecastNext30Lbs - totalGreenLbs);
        // Per-lot reorder + forecast list — sorted by lots that will
        // run out soonest.
        const reorderForecast = activeLots
          .filter(l => l.currentWeightLbs > 0)
          .map(l => {
            const used30 = (l.events || [])
              .filter(e => (e.type === "roast" || e.type === "sample") && (e.at || 0) >= cutoff30 && Number(e.weightLbs) < 0)
              .reduce((s, e) => s + Math.abs(Number(e.weightLbs) || 0), 0);
            const lotDailyBurn = used30 / 30;
            const daysLeft = lotDailyBurn > 0.05 ? Math.round(l.currentWeightLbs / lotDailyBurn) : null;
            const suggestedOrder = lotDailyBurn > 0.05
              ? Math.max(0, Math.round(lotDailyBurn * 30 - l.currentWeightLbs))
              : 0;
            return { lot: l, used30, lotDailyBurn, daysLeft, suggestedOrder };
          })
          .filter(r => r.daysLeft != null && r.daysLeft <= 45)
          .sort((a, b) => (a.daysLeft || 0) - (b.daysLeft || 0));
        const statusFor = (l) => {
          if (l.currentWeightLbs <= 0) return { label: "Empty", bg: "#F3F4F6", fg: "#6B7280" };
          if (l.currentWeightLbs <= (l.lowStockLbs || 20)) return { label: "Low stock", bg: "#FEF2F2", fg: "#DC2626" };
          if (l.ageDays >= greenStaleDays) return { label: `Stale (${l.ageDays}d)`, bg: "#FEF2F2", fg: "#DC2626" };
          if (l.ageDays >= greenAgingDays) return { label: `Aging (${l.ageDays}d)`, bg: "#FFF7ED", fg: "#EA580C" };
          return { label: "Good", bg: "#F0FDF4", fg: "#16A34A" };
        };
        return (
          <div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
              <button
                onClick={downloadFSMAReport}
                disabled={!shopConnected || greenLots.length === 0}
                title="Generate FSMA 204 traceability report"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8,
                  border: "1px solid #E5E7EB", background: "#fff", color: "#374151",
                  fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                  whiteSpace: "nowrap",
                  opacity: (!shopConnected || greenLots.length === 0) ? 0.5 : 1,
                }}
              >
                <ClipboardList size={13} strokeWidth={1.8} /> FSMA 204 Report
              </button>
            </div>

            {inventoryView === "receiving" && (
              <InventoryReceivingLog
                entries={receivingLog}
                onOpenLot={(lotId) => {
                  const l = greenLots.find(x => x.id === lotId);
                  if (!l) return;
                  setInventoryView("lots"); setInventoryFilter("all"); setSelectedLotId(l.id);
                }}
              />
            )}

            {inventoryView === "supplies" && (
              <SuppliesView
                supplies={supplies}
                setSupplies={setSupplies}
                canEdit={cap("inventory:write")}
              />
            )}

            {inventoryView === "supplies-log" && (() => {
              // Order supplies by most-recently-received first; surface
              // the dates and current on-hand counts. Per-event
              // ledgering for supplies isn't modelled yet, so this view
              // reads the lastReceivedAt cursor on each supply record.
              const log = (supplies || [])
                .filter(s => s.lastReceivedAt)
                .slice()
                .sort((a, b) => Number(b.lastReceivedAt || 0) - Number(a.lastReceivedAt || 0));
              return (
                <div>
                  <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
                    <div>
                      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#000", margin: 0, lineHeight: "28px", letterSpacing: "-0.01em" }}>Supplies log</h1>
                      <p style={{ fontSize: 14, color: "#6B7280", margin: "4px 0 0", lineHeight: 1.5 }}>Receipts and stock changes for packaging, valves, labels, and other consumables.</p>
                    </div>
                  </div>
                  {log.length === 0 ? (
                    <div style={{ background: "#FAFAFA", borderRadius: 12, padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 14, border: "1px dashed #E5E7EB" }}>
                      No supply receipts yet. Receive a PO or update a supply's on-hand to see it here.
                    </div>
                  ) : (
                    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 120px 100px 130px", padding: "10px 16px", background: "#F9FAFB", fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid #E5E7EB" }}>
                        <span>Received</span><span>Item</span><span>Vendor</span><span style={{ textAlign: "right" }}>On hand</span><span style={{ textAlign: "right" }}>Reorder at</span>
                      </div>
                      {log.map(s => (
                        <div key={s.id} style={{ display: "grid", gridTemplateColumns: "140px 1fr 120px 100px 130px", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #F3F4F6" }}>
                          <span style={{ fontSize: 13, color: "#3F3F46" }}>{new Date(s.lastReceivedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: "#000" }}>{s.name}</div>
                            <div style={{ fontSize: 11, color: "#6B7280" }}>{s.sku || s.category || "—"}</div>
                          </div>
                          <span style={{ fontSize: 12, color: "#6B7280" }}>{s.vendor || "—"}</span>
                          <span style={{ fontSize: 13, color: "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{Number(s.onHand) || 0} {s.unit || ""}</span>
                          <span style={{ fontSize: 12, color: (s.onHand || 0) <= (s.reorderAt || 0) ? "#B91C1C" : "#9CA3AF", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: (s.onHand || 0) <= (s.reorderAt || 0) ? 600 : 400 }}>{s.reorderAt || "—"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {inventoryView === "docs" && <WorkingDocsView />}

            {inventoryView === "batches" && (
              <RoastBatchesView
                roastBatches={roastBatches}
                roastedSkus={roastedSkus}
                greenLots={greenLots}
              />
            )}

            {inventoryView === "lots" && (<>

            {/* KPIs */}
            <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
              {[
                { l: "Green available", v: `${Math.round(totalGreenLbs)} lb` },
                { l: "Inventory value", v: totalInventoryValue > 0 ? `$${Math.round(totalInventoryValue).toLocaleString()}` : "—" },
                { l: "Active sources", v: activeLots.length },
                { l: "Low stock", v: lowStockCount, color: lowStockCount > 0 ? "#DC2626" : "#000000" },
                { l: `Aging ${greenAgingDays}+ days`, v: agingCount, color: agingCount > 0 ? "#EA580C" : "#000000" },
                { l: "Oldest lot", v: oldestLot ? `${oldestLot.ageDays}d` : "—" },
              ].map(s => (
                <div key={s.l} style={{ flex: 1, background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", padding: "14px 16px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6 }}>{s.l}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: s.color || "#000", marginTop: 6, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{s.v}</div>
                </div>
              ))}
            </div>

            {/* Stock distribution + aging snapshot — formerly the
                Overview tab; folded into All sources here. */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <BarsCard title="Stock by origin" rows={byOrigin.slice(0, 6)} fmt={(n) => `${Math.round(n).toLocaleString()} lb`} />
              <BarsCard title="Stock by vendor" rows={byVendor.slice(0, 6)} fmt={(n) => `${Math.round(n).toLocaleString()} lb`} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <AgingCard aging={aging} agingDays={greenAgingDays} staleDays={greenStaleDays} />
              <AlertsCard
                title="Reorder needed"
                tint="#FEE2E2" border="#FCA5A5" fg="#991B1B"
                ctaLabel="See low-stock lots"
                onCta={lotsToReorder.length > 0 ? () => setInventoryFilter("low") : null}
                empty="No lots below their reorder point."
                rows={lotsToReorder.slice(0, 5).map(l => ({
                  id: l.id, lot: l, primary: l.name,
                  secondary: `${l.origin || ""}${l.origin && l.supplier ? " · " : ""}${l.supplier || ""}`,
                  right: `${Math.round(l.currentWeightLbs)} / ${l.lowStockLbs || 20} lb`,
                }))}
                onOpenLot={(l) => { setInventoryFilter("all"); setSelectedLotId(l.id); }}
              />
            </div>

            {/* Analytics — turnover, COGS, dead stock, forecast. */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 12 }}>
              {[
                {
                  l: "Stock turnover",
                  v: turnoverPerYear != null ? `${turnoverPerYear.toFixed(1)}× / yr` : "—",
                  sub: turnoverPerYear != null
                    ? (turnoverPerYear >= 8 ? "On pace · 8-12× is healthy" : turnoverPerYear >= 4 ? "Slow turn — review aging lots" : "Very slow — capital tied up")
                    : null,
                },
                {
                  l: "COGS · 30d",
                  v: cogs30Value > 0 ? `$${Math.round(cogs30Value).toLocaleString()}` : "—",
                  sub: cogs30Lbs > 0 ? `${Math.round(cogs30Lbs).toLocaleString()} lb consumed` : "no green burn yet",
                },
                {
                  l: "Dead stock",
                  v: deadStockValue > 0 ? `$${Math.round(deadStockValue).toLocaleString()}` : "$0",
                  sub: deadLots.length > 0 ? `${deadLots.length} lot${deadLots.length === 1 ? "" : "s"} idle 60d+` : "Everything is moving",
                  color: deadStockValue > 0 ? "#CC785C" : "#000",
                },
                {
                  l: "Forecast · 30d",
                  v: forecastNext30Lbs > 0 ? `${Math.round(forecastNext30Lbs).toLocaleString()} lb` : "—",
                  sub: forecastShortfallLbs > 0
                    ? `Short ${Math.round(forecastShortfallLbs).toLocaleString()} lb at this pace`
                    : daysRunway != null ? `${daysRunway}d runway at current burn` : "no burn yet",
                  color: forecastShortfallLbs > 0 ? "#991B1B" : "#000",
                },
              ].map(s => (
                <div key={s.l} style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", padding: "14px 16px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6 }}>{s.l}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: s.color || "#000", marginTop: 6, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{s.v}</div>
                  {s.sub && <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>{s.sub}</div>}
                </div>
              ))}
            </div>

            {/* Reorder & forecast — per-lot table, sorted by which
                lot will run out soonest at its current burn rate. */}
            <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, marginBottom: 16, overflow: "hidden" }}>
              <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#000", letterSpacing: "-0.01em" }}>Reorder & forecast</div>
                <div style={{ fontSize: 11, color: "#9CA3AF" }}>Lots projected to run out within 45 days</div>
              </div>
              {reorderForecast.length === 0 ? (
                <div style={{ padding: "20px 18px 24px", fontSize: 13, color: "#9CA3AF" }}>Nothing scheduled to deplete in the next 45 days at current burn.</div>
              ) : (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1.6fr 110px 110px 110px 130px", padding: "10px 18px", background: "#F9FAFB", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6 }}>
                    <span>Lot</span><span style={{ textAlign: "right" }}>On hand</span><span style={{ textAlign: "right" }}>Burn / day</span><span style={{ textAlign: "right" }}>Days left</span><span style={{ textAlign: "right" }}>Suggested order</span>
                  </div>
                  {reorderForecast.slice(0, 8).map(({ lot, lotDailyBurn, daysLeft, suggestedOrder }) => (
                    <div key={lot.id} style={{ display: "grid", gridTemplateColumns: "1.6fr 110px 110px 110px 130px", alignItems: "center", padding: "11px 18px", borderTop: "1px solid #F3F4F6" }}>
                      <button onClick={() => { setInventoryFilter("all"); setSelectedLotId(lot.id); }} style={{ background: "none", border: "none", padding: 0, textAlign: "left", cursor: "pointer", minWidth: 0, fontFamily: "Inter, sans-serif" }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#000", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lot.name}</div>
                        <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{[lot.origin, lot.supplier].filter(Boolean).join(" · ")}</div>
                      </button>
                      <span style={{ fontSize: 13, color: "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{Math.round(lot.currentWeightLbs)} lb</span>
                      <span style={{ fontSize: 13, color: "#3F3F46", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{lotDailyBurn.toFixed(1)} lb</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: daysLeft <= 14 ? "#991B1B" : daysLeft <= 30 ? "#CC785C" : "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{daysLeft}d</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {suggestedOrder > 0 ? `+${Math.round(suggestedOrder).toLocaleString()} lb` : "—"}
                      </span>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Dead stock callout — only renders if anything qualifies. */}
            {deadLots.length > 0 && (
              <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, marginBottom: 16, padding: 18 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#000", letterSpacing: "-0.01em" }}>Dead stock</div>
                  <div style={{ fontSize: 11, color: "#9CA3AF" }}>No roast / sample activity in 60+ days</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1.6fr 100px 100px 110px", padding: "10px 0", borderBottom: "1px solid #F3F4F6", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6 }}>
                  <span>Lot</span><span style={{ textAlign: "right" }}>On hand</span><span style={{ textAlign: "right" }}>Age</span><span style={{ textAlign: "right" }}>$ value</span>
                </div>
                {deadLots.slice(0, 6).map(l => (
                  <button key={l.id} onClick={() => { setInventoryFilter("all"); setSelectedLotId(l.id); }} style={{ display: "grid", gridTemplateColumns: "1.6fr 100px 100px 110px", alignItems: "center", padding: "11px 0", borderTop: "1px solid #F9FAFB", background: "transparent", border: "none", textAlign: "left", cursor: "pointer", width: "100%", fontFamily: "Inter, sans-serif" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#000", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.name}</div>
                      <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>{[l.origin, l.supplier].filter(Boolean).join(" · ")}</div>
                    </div>
                    <span style={{ fontSize: 13, color: "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{Math.round(l.currentWeightLbs)} lb</span>
                    <span style={{ fontSize: 13, color: "#CC785C", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{l.ageDays}d</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>${Math.round((l.currentWeightLbs || 0) * (l.pricePerLb || 0)).toLocaleString()}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Usage chart */}
            {(() => {
              const MS_WEEK = 7 * 24 * 60 * 60 * 1000;
              const nowTs = Date.now();
              const sumOrders = (from, to) =>
                (producedOrders || [])
                  .filter(o => (o.producedAt || 0) >= from && (o.producedAt || 0) < to)
                  .reduce((s, o) => s + (o.items || []).reduce((a, j) => a + (j.qty || 0), 0), 0);

              // Build dataset based on granularity
              let raw = [];
              if (usageGranularity === "weekly") {
                raw = Array.from({ length: 26 }, (_, i) => {
                  const ws = nowTs - (25 - i) * MS_WEEK;
                  const we = ws + MS_WEEK;
                  const d = new Date(ws);
                  return {
                    label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
                    lbs: Math.round(sumOrders(ws, we)),
                  };
                });
              } else if (usageGranularity === "monthly") {
                raw = Array.from({ length: 6 }, (_, i) => {
                  const ref = new Date(nowTs);
                  ref.setDate(1);
                  ref.setMonth(ref.getMonth() - (5 - i));
                  const ms = new Date(ref.getFullYear(), ref.getMonth(), 1).getTime();
                  const me = new Date(ref.getFullYear(), ref.getMonth() + 1, 1).getTime();
                  return {
                    label: ref.toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
                    lbs: Math.round(sumOrders(ms, me)),
                  };
                });
              } else {
                // Quarterly — last 4 quarters
                raw = Array.from({ length: 4 }, (_, i) => {
                  const ref = new Date(nowTs);
                  const curQ = Math.floor(ref.getMonth() / 3);
                  const qBack = 3 - i;
                  let q = curQ - qBack;
                  let y = ref.getFullYear();
                  while (q < 0) { q += 4; y--; }
                  const qs = new Date(y, q * 3, 1).getTime();
                  const qe = new Date(y, q * 3 + 3, 1).getTime();
                  return {
                    label: `Q${q + 1} '${String(y).slice(2)}`,
                    lbs: Math.round(sumOrders(qs, qe)),
                  };
                });
              }

              // Add trend line (rolling avg for weekly, linear regression for monthly/quarterly)
              const chartData = raw.map((w, i) => {
                let trend;
                if (usageGranularity === "weekly") {
                  const half = 6;
                  const s = Math.max(0, i - half), e = Math.min(raw.length - 1, i + half);
                  trend = Math.round(raw.slice(s, e + 1).reduce((a, x) => a + x.lbs, 0) / (e - s + 1));
                } else {
                  const n = raw.length, meanX = (n - 1) / 2;
                  const meanY = raw.reduce((s, d) => s + d.lbs, 0) / n;
                  const slope = raw.reduce((s, d, j) => s + (j - meanX) * (d.lbs - meanY), 0) /
                                (raw.reduce((s, _, j) => s + (j - meanX) ** 2, 0) || 1);
                  trend = Math.round(meanY + slope * (i - meanX));
                }
                return { ...w, trend };
              });

              // Brush: always show, default window shows last N bars (drag left for older data)
              const brushWindow = usageGranularity === "weekly" ? 14 : chartData.length;
              const brushStart = Math.max(0, chartData.length - brushWindow);
              const barSz = usageGranularity === "quarterly" ? 140 : usageGranularity === "monthly" ? 90 : 40;

              const toggleBtn = (v, label) => (
                <button
                  key={v}
                  onClick={() => setUsageGranularity(v)}
                  style={{
                    padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 500,
                    cursor: "pointer", fontFamily: "Inter, sans-serif",
                    border: "1px solid " + (usageGranularity === v ? "#111827" : "#E5E7EB"),
                    background: usageGranularity === v ? "#111827" : "#fff",
                    color: usageGranularity === v ? "#fff" : "#6B7280",
                  }}
                >{label}</button>
              );

              return (
                <div className="pr-usage-chart" style={{ marginBottom: 24, background: "#fff", border: "1px solid #F3F4F6", borderRadius: 12, padding: "16px 16px 12px" }}>
                  <style>{`
                    .pr-usage-chart .recharts-brush-slide { fill: #111827 !important; fill-opacity: 1 !important; cursor: grab; }
                    .pr-usage-chart .recharts-brush-slide:active { cursor: grabbing; }
                    .pr-usage-chart .recharts-brush-traveller rect { fill: #111827 !important; stroke: none !important; }
                    .pr-usage-chart .recharts-brush-traveller line { display: none !important; }
                  `}</style>
                  {/* Title row */}
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", lineHeight: 1 }}>Green used</div>
                      <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 3 }}>Lbs roasted over time</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#9CA3AF" }}>
                        <span style={{ width: 10, height: 10, background: "#111827", borderRadius: 2, display: "inline-block" }} /> Lbs
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#9CA3AF" }}>
                        <span style={{ width: 16, height: 2, background: "#22D3EE", display: "inline-block", borderRadius: 1 }} /> Trend
                      </span>
                    </div>
                  </div>
                  {/* Toggle — centred */}
                  <div style={{ display: "flex", justifyContent: "center", gap: 4, marginBottom: 14 }}>
                    {toggleBtn("weekly", "Weekly")}
                    {toggleBtn("monthly", "Monthly")}
                    {toggleBtn("quarterly", "Quarterly")}
                  </div>
                  <ResponsiveContainer width="100%" height={230}>
                    <ComposedChart data={chartData} barCategoryGap="35%" margin={{ top: 4, right: 8, left: 0, bottom: 8 }}>
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                      <YAxis
                        tick={{ fontSize: 10, fill: "#D1D5DB" }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={v => v === 0 ? "" : `${v}`}
                        width={28}
                        tickCount={4}
                      />
                      <CartesianGrid vertical={false} stroke="#F9FAFB" strokeDasharray="0" />
                      <Tooltip
                        formatter={(v, name) => [`${v} lb`, name === "lbs" ? "Used" : "Trend"]}
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #F3F4F6", padding: "4px 10px" }}
                        cursor={{ fill: "#F3F4F6" }}
                      />
                      <Bar dataKey="lbs" radius={[2, 2, 0, 0]} fill="#111827" barSize={barSz} />
                      <Line dataKey="trend" type="monotone" stroke="#22D3EE" strokeWidth={2} dot={false} activeDot={false} />
                      <Brush
                        dataKey="label"
                        startIndex={brushStart}
                        endIndex={chartData.length - 1}
                        height={10}
                        travellerWidth={10}
                        fill="#F3F4F6"
                        stroke="none"
                        tickFormatter={() => ""}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}

            {/* List */}
            {lotsByFilter.length === 0 ? (
              <div style={{ background: "#F9FAFB", borderRadius: 12, padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 16 }}>
                {activeLots.length === 0
                  ? "No green coffee sources yet. Click Add source to record your first arrival."
                  : "No sources match this filter."}
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <colgroup>
                  <col />
                  <col style={{ width: 130 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 130 }} />
                </colgroup>
                <thead>
                  <tr>
                    {["Source", "Origin / process", "Weight (lb)", "Est. bags", "Age", "Location", "Status"].map(h => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 13, fontWeight: 500, color: "#9CA3AF", borderBottom: "1px solid #F3F4F6" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lotsByFilter.map(l => {
                    const status = statusFor(l);
                    const bagsLeft = l.bagSizeLbs && l.currentWeightLbs != null
                      ? (l.currentWeightLbs / l.bagSizeLbs)
                      : null;
                    return (
                      <tr key={l.id} style={{ borderBottom: "1px solid #F9FAFB", cursor: "pointer" }}
                        onClick={() => setSelectedLotId(l.id)}
                        onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <td style={{ padding: "12px 14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <div style={{ fontWeight: 600, fontSize: 15, color: "#000000" }}>{l.name}</div>
                          <div style={{ fontSize: 13, color: "#9CA3AF", marginTop: 1, display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {(l.farm || l.variety) && <span>{[l.farm, l.variety].filter(Boolean).join(" · ")}</span>}
                            {(l.sourceType || l.supplier) && (
                              <span style={{ color: "#6B7280" }}>
                                {l.sourceType && l.supplier ? `${l.sourceType} · ${l.supplier}` : l.sourceType || l.supplier}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "12px 14px", fontSize: 14, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{[l.origin, l.process].filter(Boolean).join(" / ") || <span style={{ color: "#D1D5DB" }}>—</span>}</td>
                        <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 600, color: "#000000" }}>{l.currentWeightLbs?.toFixed(1) ?? "—"}</td>
                        <td style={{ padding: "12px 14px" }}>
                          {bagsLeft != null ? (
                            <span style={{ fontSize: 14, fontWeight: 600, color: bagsLeft < 1 ? "#DC2626" : bagsLeft < 2 ? "#EA580C" : "#111827" }}>
                              {bagsLeft.toFixed(1)}
                              <span style={{ fontSize: 11, fontWeight: 400, color: "#9CA3AF", marginLeft: 3 }}>bags</span>
                            </span>
                          ) : <span style={{ color: "#D1D5DB" }}>—</span>}
                        </td>
                        <td style={{ padding: "12px 14px", fontSize: 14, color: l.ageDays >= greenStaleDays ? "#DC2626" : l.ageDays >= greenAgingDays ? "#EA580C" : "#6B7280" }}>{l.ageDays != null ? `${l.ageDays}d` : "—"}</td>
                        <td style={{ padding: "12px 14px", fontSize: 14, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.location || <span style={{ color: "#D1D5DB" }}>—</span>}</td>
                        <td style={{ padding: "12px 14px" }}>
                          <span style={{ fontSize: 13, fontWeight: 500, padding: "3px 10px", borderRadius: 99, background: status.bg, color: status.fg }}>{status.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            </>)}
          </div>
        );
      }

      case "products": {
        // Green coffee sub-tab: editable list of every approved sample.
        if (coffeesView === "green") {
          return (
            <GreenCoffeeView
              header={header}
              greenCoffees={greenCoffees}
              loaded={greenCoffeesLoaded}
              refresh={fetchGreenCoffees}
            />
          );
        }
        if (selectedProduct) {
          const p = products.find(x => x.id === selectedProduct);
          const pr = PROFILES[selectedProduct];
          // Products list is async — show a placeholder while it's still
          // loading rather than flashing an empty page on refresh.
          if (!p && !productsLoaded) {
            return <div style={{ padding: 48, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>Loading…</div>;
          }
          // Loaded but the persisted id no longer exists (deleted, wrong
          // workspace, etc.) — clear the selection and fall back to the list.
          if (!p) {
            setSelectedProduct(null);
            return null;
          }
          // Find every cupping sample that matches this coffee by name —
          // case-insensitive, supports partial overlap (e.g. "La
          // Esperanza" matches "Colombia Huila — La Esperanza"). Pick
          // the most recent one for the sheet.
          const nameLc = String(p.name || "").toLowerCase();
          const matchingSamples = cuppings.flatMap(s => (s.coffees || []).map(co => ({
            ...co,
            sessionId: s.id,
            sessionName: s.name || s.coffeeName || "Cupping session",
            sessionDate: s.cuppingDate || null,
            cupperName: s.cupperName || co.cupperName || "",
          }))).filter(co => {
            const cn = String(co.coffeeName || "").toLowerCase();
            return cn === nameLc || (nameLc && cn.includes(nameLc));
          }).sort((a, b) => (b.sessionDate || "").localeCompare(a.sessionDate || ""));
          const latestSample = matchingSamples[0] || null;
          return (
            <div>
              <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
                <div>
                  <h1 style={{ fontSize: 20, fontWeight: 600, color: "#000000", margin: 0 }}>{p.name}</h1>
                  <p style={{ fontSize: 16, color: "#6B7280", margin: "2px 0 0" }}>{[p.sub, p.origin, p.altitude].filter(Boolean).join(" · ")}</p>
                </div>
                {latestSample && (
                  <button
                    onClick={() => setCoffeeCuppingSheet({ product: p, sample: latestSample, alternates: matchingSamples })}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", flexShrink: 0 }}
                  ><Droplets size={14} strokeWidth={1.8} /> Cupping Sheet</button>
                )}
              </div>
              <div style={{ display: "flex", gap: 6, marginBottom: 24 }}>
                {[p.process, p.roastLevel, p.variety, p.score != null ? `${p.score} pts` : null].filter(Boolean).map(t => (
                  <span key={t} style={{ fontSize: 14, fontWeight: 500, padding: "2px 10px", borderRadius: 99, background: "#F3F4F6", color: "#6B7280" }}>{t}</span>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 24 }}>
                {[
                  { l: "Cost / lb",   v: p.costPerLb != null ? `$${Number(p.costPerLb).toFixed(2)}` : (p.greenPerLb != null ? `$${Number(p.greenPerLb).toFixed(2)}` : "—") },
                  { l: "Retail",      v: p.retailPrice != null ? `$${Number(p.retailPrice).toFixed(2)}` : "—" },
                  { l: "Green stock", v: p.greenStockLbs != null ? `${p.greenStockLbs} lb` : "—" },
                  { l: "Roasted",     v: p.roastedStockLbs != null ? `${p.roastedStockLbs} lb` : "—" },
                ].map(d => (
                  <div key={d.l} style={{ background: "#fff", borderRadius: 10, border: "1px solid #F3F4F6", padding: "14px 16px" }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "#9CA3AF", marginBottom: 6 }}>{d.l}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "#000000" }}>{d.v}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 24 }}>
                {[
                  { icon: Thermometer, l: "Charge", v: `${pr.chargeTemp}°F`, c: "#374151" },
                  { icon: Flame, l: "First Crack", v: `${pr.fcTime} min`, c: "#6B7280" },
                  { icon: TrendingDown, l: "Drop", v: `${pr.dropTemp}°F · ${pr.totalTime}m`, c: "#16A34A" },
                ].map(d => (
                  <div key={d.l} style={{ background: "#fff", borderRadius: 10, border: "1px solid #F3F4F6", padding: "14px 16px", display: "flex", alignItems: "center", gap: 10 }}>
                    <d.icon size={16} color={d.c} strokeWidth={1.6} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "#9CA3AF" }}>{d.l}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "#000000", marginTop: 2 }}>{d.v}</div>
                    </div>
                  </div>
                ))}
              </div>
              <RoastProfilePanel productId={selectedProduct} />
            </div>
          );
        }
        return (
          <div>
            {header}
            <div>
              {/* One row per coffee — coffee meta on the left, roast curve
                  inline on the right of the same row. Click jumps into the
                  detail view. Blends pick up a small chip + composition hint
                  right under the name. */}
              <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 110px 110px 100px 90px 220px", padding: "10px 18px", background: "#F9FAFB", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6 }}>
                  <span>Coffee</span><span>Process</span><span>Roast</span><span style={{ textAlign: "right" }}>Cost / lb</span><span style={{ textAlign: "right" }}>Score</span><span style={{ textAlign: "right" }}>Roast curve</span>
                </div>
                {products.length === 0 ? (
                  <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>No coffees yet — click "New coffee" to start.</div>
                ) : (() => {
                  // Build a sample lookup so blend rows can resolve their
                  // component sample names + costs without rescanning the
                  // cuppings array on every iteration.
                  const sampleById = new Map();
                  for (const s of (cuppings || [])) {
                    for (const co of (s.coffees || [])) {
                      sampleById.set(co.id, { ...co, sessionId: s.id });
                    }
                  }
                  return products.map((p) => {
                  // Cost-per-lb derivation:
                  //   - blends: weighted average of components' sample costPerLb
                  //   - singles: prefer p.costPerLb, fall back to greenPerLb
                  let costPerLb = null;
                  if (p.isBlend && Array.isArray(p.blendComponents) && p.blendComponents.length > 0) {
                    let weightedCost = 0, totalPct = 0;
                    for (const c of p.blendComponents) {
                      // Sample-based components first; fall back to legacy
                      // product-based components for old data.
                      const samp = c.sampleId ? sampleById.get(c.sampleId) : null;
                      const fallback = !samp && c.productId ? products.find(x => x.id === c.productId) : null;
                      const compCost = samp
                        ? (samp.costPerLb != null ? Number(samp.costPerLb) : null)
                        : (fallback ? (Number(fallback.costPerLb) || Number(fallback.greenPerLb) || null) : null);
                      if (compCost != null) { weightedCost += compCost * (Number(c.pct) || 0) / 100; totalPct += (Number(c.pct) || 0); }
                    }
                    if (totalPct > 0) costPerLb = +(weightedCost * 100 / totalPct).toFixed(2);
                  } else {
                    costPerLb = p.costPerLb != null ? Number(p.costPerLb) : (p.greenPerLb != null ? Number(p.greenPerLb) : null);
                  }
                  return (
                    <button
                      key={p.id}
                      onClick={() => setSelectedProduct(p.id)}
                      style={{ display: "grid", gridTemplateColumns: "1fr 110px 110px 100px 90px 220px", alignItems: "center", padding: "12px 18px", borderTop: "1px solid #F3F4F6", background: "transparent", border: "none", textAlign: "left", cursor: "pointer", width: "100%", fontFamily: "Inter, sans-serif" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#FAFAFA"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: "#000", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</span>
                          {p.isBlend && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99, background: "#EAF1F3", color: "#2C5766", border: "1px solid #B8CED5", textTransform: "uppercase", letterSpacing: 0.4 }}>Blend</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {p.isBlend && Array.isArray(p.blendComponents) && p.blendComponents.length > 0
                            ? p.blendComponents.map(c => {
                                const samp = c.sampleId ? sampleById.get(c.sampleId) : null;
                                const fallback = !samp && c.productId ? products.find(x => x.id === c.productId) : null;
                                const label = samp?.coffeeName || fallback?.name || "?";
                                return `${c.pct}% ${label}`;
                              }).join(" · ")
                            : (p.origin || "—")}
                        </div>
                      </div>
                      <span style={{ fontSize: 12, color: "#3F3F46" }}>{p.process || "—"}</span>
                      <span style={{ fontSize: 12, color: "#3F3F46" }}>{p.roastLevel || "—"}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: costPerLb != null ? "#000" : "#9CA3AF", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {costPerLb != null ? `$${costPerLb.toFixed(2)}` : "—"}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#000", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {p.score != null ? Number(p.score).toFixed(1) : "—"}
                      </span>
                      <div style={{ width: "100%", paddingLeft: 18 }}>
                        <ProductMiniCurve productId={p.id} demoMode={effectiveDemoMode} />
                      </div>
                    </button>
                  );
                });
                })()}
              </div>
            </div>
          </div>
        );
      }

      case "settings": return (
        <div>
          {header}

          {/* ── Demo mode card — shown in Store Connect, hidden once a store is connected ── */}
          {settingsSection === "integrations" && !shopConnected && (
          <div style={{
            background: demoMode ? "#EAF1F3" : "#fff",
            borderRadius: 12,
            border: `1px solid ${demoMode ? "#B8CED5" : "#F3F4F6"}`,
            padding: "20px 24px",
            marginBottom: 16,
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: demoMode ? "#2C5766" : "#F3F4F6",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <LayoutGrid size={18} color={demoMode ? "#fff" : "#6B7280"} strokeWidth={1.6} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#000000" }}>Demo mode</div>
                  {demoMode && (
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 99,
                      background: "#2C5766", color: "#fff", letterSpacing: 0.06, textTransform: "uppercase",
                    }}>On</span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>
                  Swap every view to a synthetic dataset — orders, clients, inventory, cuppings, routes. Your real data stays untouched in the background.
                </div>
                {!shopConnected && (
                  <div style={{ fontSize: 12, color: "#2C5766", marginTop: 6, fontWeight: 500 }}>
                    Demo data is shown until you connect a Shopify store.
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              {demoMode && (
                <button
                  onClick={() => { resetDemoData(); setDemoResetDone(true); setTimeout(() => setDemoResetDone(false), 2200); }}
                  title="Reset all demo data back to a fresh state"
                  style={{
                    padding: "6px 12px", borderRadius: 8,
                    border: `1px solid ${demoResetDone ? "#16A34A" : "#B8CED5"}`,
                    background: demoResetDone ? "#F0FDF4" : "#fff",
                    fontSize: 13, fontWeight: 500,
                    color: demoResetDone ? "#16A34A" : "#2C5766",
                    cursor: "pointer", fontFamily: "Inter, sans-serif",
                    whiteSpace: "nowrap", transition: "all 0.15s",
                    display: "inline-flex", alignItems: "center", gap: 5,
                  }}
                >
                  {demoResetDone ? <><Check size={13} strokeWidth={2.5} /> Done</> : "Reset data"}
                </button>
              )}
              <button
                onClick={toggleDemoMode}
                aria-pressed={demoMode}
                title={
                  !shopConnected
                    ? "Connect Shopify to switch demo off"
                    : demoMode ? "Turn demo mode off" : "Turn demo mode on"
                }
                style={{
                  width: 48, height: 26, borderRadius: 99,
                  border: "none", cursor: "pointer", padding: 0,
                  background: demoMode ? "#2C5766" : "#D1D5DB",
                  position: "relative", transition: "background 0.15s", flexShrink: 0,
                }}
              >
                <div style={{
                  position: "absolute", top: 3, left: demoMode ? 25 : 3,
                  width: 20, height: 20, borderRadius: "50%",
                  background: "#fff", transition: "left 0.15s",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                }} />
              </button>
            </div>
          </div>
          )}

          {/* ── Integrations (friendlier grid + detail modal) ── */}
          {settingsSection === "integrations" && (
            <IntegrationsGrid
              shopConnected={shopConnected}
              connectedShop={connectedShop}
              shopifyStores={shopifyStatus.stores || []}
              connectInput={connectInput}
              setConnectInput={setConnectInput}
              connectError={connectError}
              setConnectError={setConnectError}
              connectShopify={connectShopify}
              disconnectShopify={disconnectShopify}
              fetchShopifyOrders={fetchShopifyOrders}
              sosStatus={sosStatus}
              sosLoading={sosLoading}
              disconnectSos={disconnectSos}
              qboStatus={qboStatus}
              qboLoading={qboLoading}
              disconnectQbo={disconnectQbo}
              user={user}
            />
          )}

          {/* ── Devices (Elion bridge hardware pairing) ── */}
          {settingsSection === "devices" && (
            <DevicesCard demoMode={effectiveDemoMode} />
          )}

          {/* ── Roasting settings ── */}
          {settingsSection === "roasting" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Rate of Rise interval */}
              <div style={{ background: "#fff", border: "1px solid #F3F4F6", borderRadius: 12, padding: "20px 24px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#000000" }}>Rate of Rise interval</div>
                    <div style={{ fontSize: 13, color: "#6B7280", marginTop: 3, maxWidth: 400, lineHeight: 1.5 }}>
                      Trailing window used to calculate RoR from bean temperature samples. A shorter window is more reactive; a longer window is smoother.
                    </div>
                  </div>
                  <div style={{ display: "inline-flex", gap: 6, flexShrink: 0 }}>
                    {[
                      { v: 1,  l: "1s" },
                      { v: 15, l: "15s" },
                      { v: 30, l: "30s" },
                      { v: 60, l: "1 min" },
                    ].map(opt => (
                      <button
                        key={opt.v}
                        onClick={() => setRorWindowSec(opt.v)}
                        style={{
                          padding: "8px 14px", borderRadius: 8,
                          border: `1px solid ${rorWindowSec === opt.v ? "#000000" : "#E5E7EB"}`,
                          background: rorWindowSec === opt.v ? "#000000" : "#fff",
                          color: rorWindowSec === opt.v ? "#fff" : "#374151",
                          fontSize: 14, fontWeight: 500, cursor: "pointer",
                          fontFamily: "Inter, sans-serif", transition: "all 0.1s",
                        }}
                      >{opt.l}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Inventory Settings ── */}
          {settingsSection === "inventorySettings" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={{ background: "#fff", border: "1px solid #F3F4F6", borderRadius: 12, padding: "20px 24px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#000000" }}>Green coffee aging</div>
                    <div style={{ fontSize: 13, color: "#6B7280", marginTop: 3, maxWidth: 440, lineHeight: 1.5 }}>
                      When a lot reaches this age it's flagged as <strong>Aging</strong> (orange). At 1.5× it turns <strong>Stale</strong> (red). Default is 6 months.
                    </div>
                    <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 6 }}>
                      Stale threshold: {greenStaleDays} days ({Math.round(greenStaleDays / 30)} months)
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10, flexShrink: 0 }}>
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      {[
                        { v: 90,  l: "3 mo" },
                        { v: 180, l: "6 mo" },
                        { v: 270, l: "9 mo" },
                        { v: 365, l: "1 yr" },
                      ].map(opt => (
                        <button
                          key={opt.v}
                          onClick={() => setGreenAgingDays(opt.v)}
                          style={{
                            padding: "8px 14px", borderRadius: 8,
                            border: `1px solid ${greenAgingDays === opt.v ? "#000000" : "#E5E7EB"}`,
                            background: greenAgingDays === opt.v ? "#000000" : "#fff",
                            color: greenAgingDays === opt.v ? "#fff" : "#374151",
                            fontSize: 14, fontWeight: 500, cursor: "pointer",
                            fontFamily: "Inter, sans-serif", transition: "all 0.1s",
                          }}
                        >{opt.l}</button>
                      ))}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, color: "#6B7280" }}>Custom:</span>
                      <input
                        type="number"
                        min="30"
                        max="730"
                        step="1"
                        value={greenAgingDays}
                        onChange={e => {
                          const n = parseInt(e.target.value, 10);
                          if (Number.isFinite(n) && n >= 30) setGreenAgingDays(n);
                        }}
                        style={{
                          width: 80, padding: "7px 10px", borderRadius: 8, border: "1px solid #E5E7EB",
                          background: "#fff", fontSize: 14, fontWeight: 600, color: "#000000",
                          fontFamily: "Inter, sans-serif", outline: "none", textAlign: "right",
                        }}
                      />
                      <span style={{ fontSize: 13, color: "#6B7280" }}>days</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Branding ── */}
          {settingsSection === "branding" && (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 24, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ImageIcon size={20} color="#6B7280" strokeWidth={1.6} />
                </div>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 600, color: "#000" }}>Company logo</div>
                  <div style={{ fontSize: 14, color: "#6B7280", marginTop: 1 }}>Appears in the top right of your packing list PDF.</div>
                </div>
              </div>

              {branding.logoBase64 ? (
                <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px", border: "1px solid #E5E7EB", borderRadius: 10, marginBottom: 16 }}>
                  <img src={branding.logoBase64} alt="Company logo" style={{ height: 40, maxWidth: 160, objectFit: "contain" }} />
                  <div style={{ flex: 1, fontSize: 13, color: "#6B7280" }}>{branding.logoName || "Logo uploaded"}</div>
                  <button
                    onClick={async () => {
                      await fetch("/api/workspace/branding/logo", { method: "DELETE", credentials: "same-origin" });
                      setBranding(b => ({ ...b, logoBase64: null, logoName: null }));
                    }}
                    style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8, border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#DC2626", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                  >
                    <Trash2 size={14} strokeWidth={2} /> Remove
                  </button>
                </div>
              ) : (
                <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", border: "2px dashed #E5E7EB", borderRadius: 10, cursor: "pointer", marginBottom: 16, transition: "border-color 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#111"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "#E5E7EB"}
                >
                  <Upload size={18} color="#6B7280" strokeWidth={1.6} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: "#374151" }}>Upload logo</div>
                    <div style={{ fontSize: 12, color: "#9CA3AF" }}>PNG, JPG, SVG — max 500 KB</div>
                  </div>
                  <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: "none" }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 500 * 1024) { alert("Logo must be under 500 KB."); return; }
                      setBrandingLoading(true);
                      const reader = new FileReader();
                      reader.onload = async (ev) => {
                        const base64 = ev.target.result;
                        await fetch("/api/workspace/branding", {
                          method: "POST", credentials: "same-origin",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ logoBase64: base64, logoName: file.name }),
                        });
                        setBranding({ logoBase64: base64, logoName: file.name });
                        setBrandingLoading(false);
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                </label>
              )}
              {brandingLoading && <div style={{ fontSize: 13, color: "#6B7280" }}>Uploading…</div>}
            </div>
          )}

          {/* ── Email — per-workspace customer-facing sending identity ── */}
          {settingsSection === "email" && (
            <EmailSettingsCard userEmail={user?.email} />
          )}

          {/* ── Team (pending requests + members + cuppers + roles — admin-only) ── */}
          {settingsSection === "team" && cap("settings:team") && (
            <>
              <div id="manage-access"><PendingRequestsCard /></div>
              <TeamMembersCard currentUser={user} />
              <RolesCard />

              {/* Cupping panel — list + add/remove. Moved here from
                  Cupping → Cuppers because managing the panel is an
                  admin task that belongs alongside team membership. */}
              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 24, marginTop: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Coffee size={20} color="#6B7280" strokeWidth={1.6} />
                    </div>
                    <div>
                      <div style={{ fontSize: 17, fontWeight: 600, color: "#000" }}>Cuppers</div>
                      <div style={{ fontSize: 14, color: "#6B7280", marginTop: 1 }}>People who score samples in cupping sessions. Each gets their own scoring tab.</div>
                    </div>
                  </div>
                  <button
                    onClick={() => setEditingCupper("new")}
                    style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 6 }}
                  ><Plus size={13} strokeWidth={1.8} /> New cupper</button>
                </div>

                {cuppers.length === 0 ? (
                  <div style={{ padding: "24px", textAlign: "center", color: "#9CA3AF", fontSize: 13, background: "#FAFAFA", borderRadius: 8, border: "1px dashed #E5E7EB" }}>
                    No cuppers yet. Click <strong style={{ color: "#374151" }}>New cupper</strong> to add the people on your scoring panel.
                  </div>
                ) : (
                  <div style={{ border: "1px solid #F3F4F6", borderRadius: 8, overflow: "hidden" }}>
                    {cuppers.map((cu, i) => (
                      <div
                        key={cu.id}
                        onClick={() => setEditingCupper(cu)}
                        onMouseEnter={e => e.currentTarget.style.background = "#FAFAFA"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        style={{ display: "grid", gridTemplateColumns: "1fr 280px 110px", alignItems: "center", padding: "12px 14px", borderTop: i === 0 ? "none" : "1px solid #F3F4F6", cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                      >
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#000" }}>{cu.name}</div>
                        <div style={{ fontSize: 13, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cu.email || "—"}</div>
                        <div style={{ textAlign: "right" }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Delete "${cu.name}"? Their scoring records on coffees stay attributed to the name.`)) removeCupper(cu.id);
                            }}
                            style={{ padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif", border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#B91C1C", display: "inline-flex", alignItems: "center", gap: 4 }}
                          ><Trash2 size={11} strokeWidth={1.8} /> Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Automated Reports Card ── */}
          {settingsSection === "reports" && (
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 24, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ClipboardList size={20} color="#2563EB" strokeWidth={1.6} />
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 600, color: "#000000" }}>Automated reports</div>
                <div style={{ fontSize: 15, color: "#6B7280", marginTop: 1 }}>Email address for daily production reports.</div>
              </div>
            </div>
            <input
              type="email"
              placeholder="production@yourroastery.com"
              value={reportEmail}
              onChange={e => setReportEmail(e.target.value)}
              style={{
                width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid #E5E7EB",
                background: "#fff", fontSize: 15, color: "#000000",
                fontFamily: "Inter, sans-serif", outline: "none", boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "12px 14px", background: "#FEF3C7", borderRadius: 8, marginTop: 10, border: "1px solid #FDE68A" }}>
              <AlertTriangle size={14} color="#D97706" strokeWidth={1.8} style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ fontSize: 13, color: "#92400E", lineHeight: 1.5 }}>
                The address is saved, but sending from the server isn't wired yet. To turn this on we'll need an email service (SendGrid, Mailgun, AWS SES, or SMTP) configured with API keys in the backend. Let me know which one you want to use and I'll wire it up.
              </div>
            </div>
          </div>
          )}

          {/* ── Shipping Card ── */}
          {settingsSection === "shipping" && cap("settings:team") && (
            <ShippingSettingsCard
              settings={shippingSettings}
              loading={shippingSettingsLoading}
              onSave={saveShippingSettings}
              shopConnected={shopConnected}
            />
          )}

          {/* ── Cost rates + throughput ── */}
          {settingsSection === "costs" && (
            <CostRatesCard rates={costRates} onSave={async (next) => {
              const r = await fetch("/api/settings/cost-rates", {
                method: "PUT",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(next),
              });
              if (r.ok) { const d = await r.json(); setCostRates(d.rates); }
            }} />
          )}

          {/* ── Finance: Business info + payment terms + auto-reminders.
               Migrated from the old top-level Billing page. ── */}
          {settingsSection === "finance" && (
            <BillingSettingsView
              settings={billingSettings}
              onSaved={(s) => setBillingSettings(s)}
              effectiveDemoMode={effectiveDemoMode}
            />
          )}

          {/* ── Preferences bucket: skipped retention, batch size, weight loss,
               filtered products. All share the "preferences" sub-nav tab. ── */}
          {settingsSection === "preferences" && (
          <>
          {/* ── Skipped Orders Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 24, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <SkipForward size={20} color="#6B7280" strokeWidth={1.6} />
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 600, color: "#000000" }}>Skipped orders</div>
                <div style={{ fontSize: 15, color: "#6B7280", marginTop: 1 }}>How long skipped orders stay on the Skipped tab before auto-clearing.</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {[5, 15, 30].map(d => {
                const active = skippedClearDays === d;
                return (
                  <button
                    key={d}
                    onClick={() => setSkippedClearDays(d)}
                    style={{
                      flex: 1, padding: "10px 14px", borderRadius: 8,
                      border: `1px solid ${active ? "#000000" : "#E5E7EB"}`,
                      background: active ? "#000000" : "#fff",
                      color: active ? "#fff" : "#374151",
                      fontSize: 14, fontWeight: 600, cursor: "pointer",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    {d} days
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 13, color: "#9CA3AF", marginTop: 10 }}>
              Currently {skippedOrders.length} skipped order{skippedOrders.length === 1 ? "" : "s"} on file.
            </div>
          </div>

          {/* ── Batch Size Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 24, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "#F5F3FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Package size={20} color="#7C3AED" strokeWidth={1.6} />
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 600, color: "#000000" }}>Batch size</div>
                <div style={{ fontSize: 15, color: "#6B7280", marginTop: 1 }}>Green coffee capacity per roast batch. Drives batch counts and estimated roasting time ({minutesPerBatch} min per batch).</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input
                type="number"
                min="0.1"
                step={batchUnit === "kg" ? "0.5" : "1"}
                value={batchUnit === "kg"
                  ? +batchSizeKg.toFixed(2)
                  : +batchSizeLbs.toFixed(1)}
                onChange={e => {
                  const n = parseFloat(e.target.value);
                  if (!Number.isFinite(n) || n <= 0) return;
                  // Always store in kg internally; convert from lb when needed.
                  setBatchSizeKg(batchUnit === "kg" ? n : n / KG_TO_LB);
                }}
                style={{
                  width: 110, padding: "9px 12px", borderRadius: 8, border: "1px solid #E5E7EB",
                  background: "#fff", fontSize: 15, fontWeight: 600, color: "#000000",
                  fontFamily: "Inter, sans-serif", outline: "none", textAlign: "right",
                }}
              />
              {/* kg / lb segmented toggle */}
              <div role="radiogroup" aria-label="Batch size unit" style={{
                display: "inline-flex", border: "1px solid #E5E7EB", borderRadius: 8,
                background: "#F9FAFB", padding: 2,
              }}>
                {["kg", "lb"].map(u => {
                  const active = batchUnit === u;
                  return (
                    <button
                      key={u}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setBatchUnit(u)}
                      style={{
                        padding: "6px 14px", borderRadius: 6, border: "none",
                        background: active ? "#000000" : "transparent",
                        color: active ? "#fff" : "#6B7280",
                        fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif",
                        cursor: "pointer",
                      }}
                    >{u}</button>
                  );
                })}
              </div>
              <span style={{ fontSize: 15, color: "#6B7280" }}>per batch</span>
              <span style={{ fontSize: 14, color: "#9CA3AF", marginLeft: "auto" }}>
                ≈ {batchUnit === "kg" ? `${batchSizeLbs.toFixed(1)} lb` : `${batchSizeKg.toFixed(2)} kg`}
              </span>
            </div>
          </div>

          {/* ── Weight Loss Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 24, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "#FFF7ED", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Flame size={20} color="#EA580C" strokeWidth={1.6} />
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 600, color: "#000000" }}>Weight loss</div>
                <div style={{ fontSize: 15, color: "#6B7280", marginTop: 1 }}>Used to calculate green coffee required from roasted order weight.</div>
              </div>
            </div>

            {/* Default % */}
            <div style={{ background: "#F9FAFB", borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: "#000000", marginBottom: 4 }}>Default loss</div>
              <div style={{ fontSize: 14, color: "#6B7280", marginBottom: 10 }}>Applied to any product that doesn't have a specific override below.</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="number"
                  min="0"
                  max="99"
                  step="0.5"
                  value={(defaultLossPct * 100).toFixed(1).replace(/\.0$/, "")}
                  onChange={e => {
                    const n = parseFloat(e.target.value);
                    if (!Number.isFinite(n)) return;
                    setDefaultLossPct(Math.max(0, Math.min(99, n)) / 100);
                  }}
                  style={{
                    width: 80, padding: "7px 10px", borderRadius: 6, border: "1px solid #E5E7EB",
                    background: "#fff", fontSize: 15, fontWeight: 600, color: "#000000",
                    fontFamily: "Inter, sans-serif", outline: "none", textAlign: "right",
                  }}
                />
                <span style={{ fontSize: 15, color: "#6B7280" }}>% loss</span>
              </div>
            </div>

            {/* Per-product overrides */}
            <div style={{ fontSize: 15, fontWeight: 500, color: "#000000", marginBottom: 6 }}>Per-product overrides</div>
            <div style={{ fontSize: 14, color: "#6B7280", marginBottom: 12 }}>Leave blank to use the default. Set a number (like 22) to override just this coffee.</div>
            <div>
              {productsForOverrides.length === 0 ? (
                <div style={{ background: "#F9FAFB", borderRadius: 8, padding: 12, fontSize: 14, color: "#6B7280" }}>No products to configure yet.</div>
              ) : productsForOverrides.map(p => {
                const override = lossOverrides[p.productId];
                const hasOverride = typeof override === "number";
                return (
                  <div key={p.productId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderBottom: "1px solid #F9FAFB" }}>
                    <div style={{ fontSize: 15, color: "#000000", fontWeight: 500 }}>{p.name}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        type="number"
                        min="0"
                        max="99"
                        step="0.5"
                        placeholder={(defaultLossPct * 100).toFixed(1).replace(/\.0$/, "")}
                        value={hasOverride ? (override * 100).toFixed(1).replace(/\.0$/, "") : ""}
                        onChange={e => setProductLoss(p.productId, e.target.value)}
                        style={{
                          width: 72, padding: "6px 8px", borderRadius: 6, border: "1px solid #E5E7EB",
                          background: "#fff", fontSize: 15, fontWeight: hasOverride ? 600 : 400,
                          color: hasOverride ? "#000000" : "#9CA3AF",
                          fontFamily: "Inter, sans-serif", outline: "none", textAlign: "right",
                        }}
                      />
                      <span style={{ fontSize: 14, color: "#9CA3AF", width: 10 }}>%</span>
                      {hasOverride && (
                        <button
                          onClick={() => setProductLoss(p.productId, "")}
                          title="Reset to default"
                          style={{ background: "transparent", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4 }}
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Excluded Products Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 24, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={20} color="#DC2626" strokeWidth={1.6} />
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 600, color: "#000000" }}>Filtered products</div>
                <div style={{ fontSize: 15, color: "#6B7280", marginTop: 1 }}>Products hidden from orders and production plan</div>
              </div>
            </div>
            {excludedProducts.length === 0 ? (
              <div style={{ background: "#F9FAFB", borderRadius: 8, padding: 16, fontSize: 15, color: "#6B7280", lineHeight: 1.6 }}>
                No products filtered. Click any order, then <strong>Filter</strong> next to a line item (e.g. filters, merch, subscriptions) to hide it from every order and all counts going forward.
              </div>
            ) : (
              <div>
                {excludedProducts.map(p => (
                  <div key={p.productId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#F9FAFB", borderRadius: 8, marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 500, color: "#000000" }}>{p.name}</div>
                      <div style={{ fontSize: 13, color: "#9CA3AF", marginTop: 1 }}>Product ID {p.productId}</div>
                    </div>
                    <button
                      onClick={() => removeExcluded(p.productId)}
                      style={{
                        padding: "6px 12px", borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                        border: "1px solid #E5E7EB", background: "#fff", color: "#374151",
                      }}
                    >
                      Unfilter
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          </>
          )}

          {/* ── Support placeholder ── */}
          {settingsSection === "billing" && <PlanBillingPage usage={effectiveUsage} billingLoading={billingLoading} />}

          {settingsSection === "support" && (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6", padding: 24, marginBottom: 16 }}>
              <div style={{ fontSize: 17, fontWeight: 600, color: "#000000", marginBottom: 4 }}>Support</div>
              <div style={{ fontSize: 14, color: "#6B7280", lineHeight: 1.55 }}>
                Need a hand? Open the Help (?) icon on the left rail to reach us through Discuss, or email <a href="mailto:hello@elionlabs.org" style={{ color: "#2563EB" }}>hello@elionlabs.org</a>.
              </div>
            </div>
          )}

        </div>
      );

      case "wholesale":
        return (
          <WholesaleView
            tab={wholesaleTab}
            setTab={setWholesaleTab}
            config={wholesaleConfig}
            products={wholesaleProducts}
            customers={wholesaleCustomers}
            orders={wholesaleOrders}
            onOrderUpdated={(o) => setWholesaleOrders(prev => prev.map(x => x.id === o.id ? o : x))}
            loading={wholesaleLoading}
            productModal={wholesaleProductModal}
            setProductModal={setWholesaleProductModal}
            header={header}
            onConfigSaved={(cfg) => setWholesaleConfig(cfg)}
            onProductCreated={(p) => setWholesaleProducts(ps => [...ps, p])}
            onProductUpdated={(p) => setWholesaleProducts(ps => ps.map(x => x.id === p.id ? p : x))}
            onProductDeleted={(id) => setWholesaleProducts(ps => ps.filter(x => x.id !== id))}
            onCustomerCreated={(c) => setWholesaleCustomers(cs => [...cs, c])}
            onCustomerUpdated={(c) => setWholesaleCustomers(cs => cs.map(x => x.id === c.id ? c : x))}
            onCustomerDeleted={(id) => setWholesaleCustomers(cs => cs.filter(x => x.id !== id))}
            shopConnected={shopConnected}
            connectedShop={connectedShop}
          />
        );

      case "billing": {
        /* ── Summary bar ── */
        const totalOwed = billingInvoices.filter(i => ["sent","overdue"].includes(i.status)).reduce((s,i) => s + (i.total || 0), 0);
        const totalOverdue = billingInvoices.filter(i => i.status === "overdue").reduce((s,i) => s + (i.total || 0), 0);
        const totalPaidThisMonth = billingInvoices.filter(i => {
          if (i.status !== "paid") return false;
          const pd = new Date(i.paidDate || i.paidAt || 0);
          const now2 = new Date();
          return pd.getFullYear() === now2.getFullYear() && pd.getMonth() === now2.getMonth();
        }).reduce((s,i) => s + (i.total || 0), 0);

        /* ── Filtered invoices ── */
        const filteredInvoices = billingInvoices.filter(i => {
          if (billingFilter === "all") return true;
          if (billingFilter === "unpaid") return ["sent","overdue"].includes(i.status);
          return i.status === billingFilter;
        });

        /* ── Grouped invoices ── */
        const groupedInvoices = (() => {
          if (billingGroup === "client") {
            const m = {};
            filteredInvoices.forEach(i => {
              const k = i.clientName || "Unknown";
              if (!m[k]) m[k] = [];
              m[k].push(i);
            });
            return Object.entries(m).sort((a,b) => a[0].localeCompare(b[0])).map(([k,v]) => ({ key: k, label: k, items: v }));
          }
          // group by month
          const m = {};
          filteredInvoices.forEach(i => {
            const d = new Date(i.invoiceDate || i.createdAt || 0);
            const k = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
            const lbl = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
            if (!m[k]) m[k] = { label: lbl, items: [] };
            m[k].items.push(i);
          });
          return Object.entries(m).sort((a,b) => b[0].localeCompare(a[0])).map(([k,v]) => ({ key: k, ...v }));
        })();

        const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

        /* ── Action handlers ── */
        const sendInvoice = async (inv) => {
          if (effectiveDemoMode) { setBillingInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: "sent", sentAt: Date.now() } : i)); return; }
          const r = await fetch(`/api/billing/invoices/${inv.id}/send`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
          if (r.ok) { const { invoice } = await r.json(); setBillingInvoices(prev => prev.map(i => i.id === inv.id ? withOverdue(invoice) : i)); }
        };

        const markPaid = async (inv) => {
          if (effectiveDemoMode) { setBillingInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: "paid", paidAt: Date.now(), paidDate: todayStr } : i)); return; }
          const r = await fetch(`/api/billing/invoices/${inv.id}/mark-paid`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
          if (r.ok) { const { invoice } = await r.json(); setBillingInvoices(prev => prev.map(i => i.id === inv.id ? invoice : i)); }
        };

        const deleteInvoice = async (inv) => {
          if (!window.confirm(`Delete invoice ${inv.number}? This cannot be undone.`)) return;
          if (effectiveDemoMode) { setBillingInvoices(prev => prev.filter(i => i.id !== inv.id)); return; }
          await fetch(`/api/billing/invoices/${inv.id}`, { method: "DELETE" });
          setBillingInvoices(prev => prev.filter(i => i.id !== inv.id));
        };

        /* ── Invoice row ── */
        const InvoiceRow = ({ inv }) => {
          const sc = invoiceStatusColor(inv.status);
          const daysUntilDue = inv.dueDate ? Math.ceil((new Date(inv.dueDate) - new Date()) / 86400000) : null;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: "1px solid #F3F4F6", transition: "background 0.1s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <div style={{ flex: "0 0 90px" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{inv.number}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 1 }}>{inv.invoiceDate}</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.clientName}</div>
                {inv.clientEmail && <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.clientEmail}</div>}
              </div>
              <div style={{ flex: "0 0 90px", textAlign: "right" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{fmtMoney(inv.total)}</div>
                {inv.dueDate && inv.status !== "paid" && (
                  <div style={{ fontSize: 11, color: daysUntilDue < 0 ? "#DC2626" : daysUntilDue <= 7 ? "#D97706" : "#9CA3AF", marginTop: 1 }}>
                    {daysUntilDue < 0 ? `${Math.abs(daysUntilDue)}d overdue` : daysUntilDue === 0 ? "Due today" : `Due in ${daysUntilDue}d`}
                  </div>
                )}
              </div>
              <div style={{ flex: "0 0 80px", textAlign: "center" }}>
                <span style={{ background: sc.bg, color: sc.fg, fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 99, textTransform: "capitalize" }}>
                  {invoiceStatusLabel(inv.status)}
                </span>
              </div>
              <div style={{ flex: "0 0 160px", display: "flex", gap: 6, justifyContent: "flex-end" }}>
                {inv.status === "draft" && (
                  <button onClick={() => sendInvoice(inv)} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: "pointer", border: "1px solid #111827", background: "#111827", color: "#fff" }}>
                    <Send size={11} /> Send
                  </button>
                )}
                {["sent","overdue"].includes(inv.status) && (
                  <>
                    <button onClick={() => sendInvoice(inv)} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: "pointer", border: "1px solid #D1D5DB", background: "#fff", color: "#374151" }}>
                      <Send size={11} /> Resend
                    </button>
                    <button onClick={() => markPaid(inv)} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "none", background: "#F0FDF4", color: "#16A34A" }}>
                      <Check size={11} /> Paid
                    </button>
                  </>
                )}
                {inv.status === "paid" && (
                  <span style={{ fontSize: 12, color: "#9CA3AF", padding: "5px 0" }}>—</span>
                )}
                <button onClick={() => setInvoiceModal(inv)} style={{ padding: "5px 8px", borderRadius: 6, fontSize: 12, cursor: "pointer", border: "1px solid #E5E7EB", background: "#fff", color: "#6B7280" }}>
                  <Pencil size={12} />
                </button>
                <button onClick={() => deleteInvoice(inv)} style={{ padding: "5px 8px", borderRadius: 6, fontSize: 12, cursor: "pointer", border: "1px solid #FEE2E2", background: "#fff", color: "#DC2626" }}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          );
        };

        /* ── PO row ── */
        const PORow = ({ po }) => {
          const sc = poStatusColor(po.status);
          const advancePOStatus = async (po) => {
            const next = { draft: "sent", sent: "confirmed", confirmed: "received" }[po.status];
            if (!next) return;
            if (effectiveDemoMode) { setBillingPOs(prev => prev.map(p => p.id === po.id ? { ...p, status: next } : p)); return; }
            const r = await fetch(`/api/billing/pos/${po.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: next }) });
            if (r.ok) { const { po: updated } = await r.json(); setBillingPOs(prev => prev.map(p => p.id === po.id ? updated : p)); }
          };
          const deletePO = async (po) => {
            if (!window.confirm(`Delete PO ${po.number}?`)) return;
            if (effectiveDemoMode) { setBillingPOs(prev => prev.filter(p => p.id !== po.id)); return; }
            await fetch(`/api/billing/pos/${po.id}`, { method: "DELETE" });
            setBillingPOs(prev => prev.filter(p => p.id !== po.id));
          };
          const nextLabel = { draft: "Send", sent: "Confirm", confirmed: "Mark received" }[po.status];
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: "1px solid #F3F4F6", transition: "background 0.1s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <div style={{ flex: "0 0 90px" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{po.number}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 1 }}>{po.poDate}</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: "#111827" }}>{po.supplierName}</div>
                {po.lotName && <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 1 }}>↳ {po.lotName}</div>}
              </div>
              <div style={{ flex: "0 0 90px", textAlign: "right" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{fmtMoney(po.total)}</div>
                {po.expectedDate && po.status !== "received" && (
                  <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 1 }}>ETA {po.expectedDate}</div>
                )}
              </div>
              <div style={{ flex: "0 0 100px", textAlign: "center" }}>
                <span style={{ background: sc.bg, color: sc.fg, fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 99, textTransform: "capitalize" }}>
                  {po.status}
                </span>
              </div>
              <div style={{ flex: "0 0 140px", display: "flex", gap: 6, justifyContent: "flex-end" }}>
                {nextLabel && (
                  <button onClick={() => advancePOStatus(po)} style={{ padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: "pointer", border: "1px solid #D1D5DB", background: "#fff", color: "#374151" }}>
                    {nextLabel}
                  </button>
                )}
                <button onClick={() => setPOModal(po)} style={{ padding: "5px 8px", borderRadius: 6, fontSize: 12, cursor: "pointer", border: "1px solid #E5E7EB", background: "#fff", color: "#6B7280" }}>
                  <Pencil size={12} />
                </button>
                <button onClick={() => deletePO(po)} style={{ padding: "5px 8px", borderRadius: 6, fontSize: 12, cursor: "pointer", border: "1px solid #FEE2E2", background: "#fff", color: "#DC2626" }}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          );
        };

        return (
          <div>
            {header}
            {/* ── Summary cards ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
              {[
                { label: "Outstanding", value: fmtMoney(totalOwed), sub: `${billingInvoices.filter(i => ["sent","overdue"].includes(i.status)).length} invoices`, color: "#111827" },
                { label: "Overdue", value: fmtMoney(totalOverdue), sub: `${billingInvoices.filter(i => i.status === "overdue").length} invoices`, color: totalOverdue > 0 ? "#DC2626" : "#111827" },
                { label: "Paid this month", value: fmtMoney(totalPaidThisMonth), sub: `${billingInvoices.filter(i => { const pd = new Date(i.paidDate || i.paidAt || 0); const now2 = new Date(); return i.status === "paid" && pd.getFullYear() === now2.getFullYear() && pd.getMonth() === now2.getMonth(); }).length} invoices`, color: "#16A34A" },
              ].map(card => (
                <div key={card.label} style={{ background: "#fff", border: "1px solid #F3F4F6", borderRadius: 12, padding: "16px 20px" }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "#9CA3AF", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>{card.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: card.color, letterSpacing: "-0.02em" }}>{card.value}</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 4 }}>{card.sub}</div>
                </div>
              ))}
            </div>

            {billingView === "invoices" && (
              <div style={{ background: "#fff", border: "1px solid #F3F4F6", borderRadius: 12, overflow: "hidden" }}>
                {/* Table header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid #F3F4F6" }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
                    Invoices
                    {billingFilter !== "all" && <span style={{ fontSize: 13, fontWeight: 500, color: "#9CA3AF", marginLeft: 8 }}>· {invoiceStatusLabel(billingFilter === "unpaid" ? "sent" : billingFilter)}</span>}
                    <span style={{ fontSize: 13, fontWeight: 500, color: "#9CA3AF", marginLeft: 6 }}>({filteredInvoices.length})</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {/* Group toggle */}
                    <div style={{ display: "flex", background: "#F3F4F6", borderRadius: 7, padding: 2 }}>
                      {[["date","By date"],["client","By client"]].map(([v,l]) => (
                        <button key={v} onClick={() => setBillingGroup(v)} style={{
                          padding: "4px 10px", borderRadius: 5, fontSize: 12, fontWeight: 500, cursor: "pointer", border: "none",
                          background: billingGroup === v ? "#fff" : "transparent",
                          color: billingGroup === v ? "#111827" : "#6B7280",
                          boxShadow: billingGroup === v ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                        }}>{l}</button>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Column headers */}
                <div style={{ display: "flex", gap: 12, padding: "8px 16px", background: "#F9FAFB", borderBottom: "1px solid #F3F4F6" }}>
                  <div style={{ flex: "0 0 90px", fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.05em" }}>Invoice #</div>
                  <div style={{ flex: 1, fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.05em" }}>Client</div>
                  <div style={{ flex: "0 0 90px", textAlign: "right", fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.05em" }}>Amount</div>
                  <div style={{ flex: "0 0 80px", textAlign: "center", fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</div>
                  <div style={{ flex: "0 0 160px" }} />
                </div>
                {/* Groups */}
                {filteredInvoices.length === 0 ? (
                  <div style={{ padding: "48px 16px", textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>No invoices{billingFilter !== "all" ? ` with status "${billingFilter}"` : ""}.</div>
                ) : groupedInvoices.map(group => (
                  <div key={group.key}>
                    <div style={{ padding: "8px 16px", background: "#F9FAFB", borderBottom: "1px solid #F3F4F6", borderTop: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>{group.label}</span>
                      <span style={{ fontSize: 12, color: "#9CA3AF" }}>{fmtMoney(group.items.reduce((s,i) => s+(i.total||0), 0))}</span>
                    </div>
                    {group.items.map(inv => <InvoiceRow key={inv.id} inv={inv} />)}
                  </div>
                ))}
              </div>
            )}

            {billingView === "pos" && (
              <div style={{ background: "#fff", border: "1px solid #F3F4F6", borderRadius: 12, overflow: "hidden" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid #F3F4F6" }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Purchase Orders <span style={{ fontSize: 13, fontWeight: 500, color: "#9CA3AF" }}>({billingPOs.length})</span></div>
                </div>
                <div style={{ display: "flex", gap: 12, padding: "8px 16px", background: "#F9FAFB", borderBottom: "1px solid #F3F4F6" }}>
                  <div style={{ flex: "0 0 90px", fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.05em" }}>PO #</div>
                  <div style={{ flex: 1, fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.05em" }}>Supplier</div>
                  <div style={{ flex: "0 0 90px", textAlign: "right", fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total</div>
                  <div style={{ flex: "0 0 100px", textAlign: "center", fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</div>
                  <div style={{ flex: "0 0 140px" }} />
                </div>
                {billingPOs.length === 0 ? (
                  <div style={{ padding: "48px 16px", textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>No purchase orders yet.</div>
                ) : billingPOs.map(po => <PORow key={po.id} po={po} />)}
              </div>
            )}

            {billingView === "settings" && (
              <BillingSettingsView
                settings={billingSettings}
                onSaved={(s) => setBillingSettings(s)}
                effectiveDemoMode={effectiveDemoMode}
              />
            )}
          </div>
        );
      }

      default: return null;
    }
  };

  /* ═══════════════════════════════════════════
     LAYOUT — 3 columns matching reference exactly
     ═══════════════════════════════════════════ */
  const isFetchingFromShopify = shopifyOrdersLoading || profitabilityLoading || greenLotsLoading;
  return (
    <>
    {/* Minimal top-of-viewport loading bar for any Shopify / server fetch */}
    <style>{`
      @keyframes pr-load-slide {
        0%   { transform: translateX(-100%); }
        100% { transform: translateX(350%); }
      }
    `}</style>
    <div
      aria-hidden={!isFetchingFromShopify}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, height: 2,
        zIndex: 2000, overflow: "hidden", pointerEvents: "none",
        opacity: isFetchingFromShopify ? 1 : 0,
        transition: "opacity 0.2s ease-out",
      }}
    >
      <div style={{
        position: "absolute", top: 0, left: 0,
        width: "30%", height: "100%",
        background: "linear-gradient(90deg, transparent, #000000 50%, transparent)",
        animation: isFetchingFromShopify ? "pr-load-slide 1.2s ease-in-out infinite" : "none",
      }} />
    </div>
    {/* Cupper edit/new modal — used from both the legacy Cupping
        Cuppers view AND the new Settings → Team card. */}
    {editingCupper && (
      <CupperModal
        cupper={editingCupper === "new" ? null : editingCupper}
        onClose={() => setEditingCupper(null)}
        onSave={async (data) => { upsertCupper(data); setEditingCupper(null); }}
      />
    )}
    {/* Add Coffee modal */}
    {addCoffeeOpen && (
      <AddCoffeeModal
        onClose={(created) => {
          setAddCoffeeOpen(false);
          if (created?.id) { setPage("products"); setCoffeesView("products"); setSelectedProduct(created.id); }
        }}
        onCreate={createProduct}
        // Picker source is now green coffees (auto-promoted approved
        // cupping samples) rather than raw samples. The shape mirrors
        // the previous approvedSamples contract — modal stays simple.
        approvedSamples={(greenCoffees || [])
          .filter(g => !g.archivedAt && g.status === "approved")
          .map(g => ({
              id: g.sampleId,
              greenCoffeeId: g.id,
              sessionId: g.sessionId,
              coffeeName: g.coffeeName || "(unnamed)",
              sourcedFrom: g.sourcedFrom || "",
              origin: g.origin || "",
              process: g.process || "",
              score: g.score,
              costPerLb: g.costPerLb,
              pricePerLb: g.pricePerLb,
              descriptors: g.descriptors || {},
              label: [g.coffeeName, g.sourcedFrom].filter(Boolean).join(" — "),
          }))}
      />
    )}
    {/* Add Lot modal */}
    {addLotOpen && (
      <AddLotModal onClose={() => setAddLotOpen(false)} onCreate={async (data) => {
        try { await createGreenLot(data); setAddLotOpen(false); }
        catch (err) { alert(err.message || "Couldn't create lot"); }
      }} />
    )}
    {/* Add Cupping modal */}
    {addCuppingOpen && (
      <AddCuppingModal
        onClose={() => setAddCuppingOpen(false)}
        onCreate={async (data) => { await createCupping(data); setAddCuppingOpen(false); }}
        cupperSuggestions={Array.from(new Set(cuppings.map(c => c.cupperName).filter(Boolean)))}
        sourcingSuggestions={Array.from(new Set([
          ...cuppings.map(c => c.sourcingCompany).filter(Boolean),
          ...greenLots.filter(l => !l.archivedAt).map(l => l.supplier).filter(Boolean),
        ]))}
        availableSamples={samples}
      />
    )}
    {addSampleMode === "single" && (
      <AddSampleModal
        onClose={() => setAddSampleMode(null)}
        onCreate={async (rows) => { await createSamples(rows); }}
      />
    )}
    {addSampleMode === "bulk" && (
      <BulkAddSamplesModal
        onClose={() => setAddSampleMode(null)}
        onCreate={async (rows) => { await createSamples(rows); }}
      />
    )}
    {qcModal && (() => {
      // Resolve the expected weight loss for this batch's product
      // by averaging the bench-set expectedLossPct across the
      // product's green-coffee components, weighted by their %.
      const product = (products || []).find(p => p.id === qcModal.batch.productId);
      let expectedLossPct = null;
      if (product && Array.isArray(product.blendComponents) && product.blendComponents.length > 0) {
        let weighted = 0, total = 0;
        for (const c of product.blendComponents) {
          const gcId = c.greenCoffeeId || (c.sampleId ? `gc-${c.sampleId}` : null);
          const gc = (greenCoffees || []).find(x => x.id === gcId);
          if (!gc || gc.expectedLossPct == null) continue;
          const pct = Number(c.pct) || 0;
          weighted += Number(gc.expectedLossPct) * pct;
          total += pct;
        }
        if (total > 0) expectedLossPct = +(weighted / total).toFixed(2);
      }
      const existingCompleted = qcModal.mode === "edit"
        ? (roastingQueue.completed || []).find(c => c.batchId === qcModal.batch.id)
        : null;
      const onSubmit = async (qc) => {
        const batch = qcModal.batch;
        if (qcModal.mode === "edit") {
          // Edit-only: update qc fields on the existing entry. Honour
          // the rejected toggle if it changed (return or re-take
          // inventory accordingly).
          setRoastingQueue(q => ({
            ...q,
            completed: (q.completed || []).map(c => {
              if (c.batchId !== batch.id) return c;
              const wasRejected = !!c.qc?.rejected;
              const nowRejected = !!qc.rejected;
              const next = {
                ...c,
                roastedLbs: qc.roastedLbs != null ? Number(qc.roastedLbs) : c.roastedLbs,
                qc: { ...(c.qc || {}), ...qc, byUserId: user?.id || c.qc?.byUserId, byName: user?.name || c.qc?.byName, ts: Date.now() },
              };
              if (!wasRejected && nowRejected) {
                setProducedOrders(prev => prev.filter(o => o.shopifyId !== `batch_roast_${c.batchId}`));
              } else if (wasRejected && !nowRejected) {
                setProducedOrders(prev => [{
                  shopifyId: `batch_roast_${c.batchId}`,
                  id: `#batch-${c.batchId}`,
                  customer: "Roast batch",
                  date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" }),
                  items: [{ productId: c.productId, name: c.productName, variant: c.roastLevel || "Whole Bean", sku: c.sku || "", qty: Math.ceil(next.roastedLbs || 1), grams: 340 }],
                  total: 0, shippingAddress: { address1: "", city: "", province: "", province_code: "", zip: "", latitude: null, longitude: null, name: "" },
                  producedAt: Date.now(),
                }, ...prev]);
              }
              return next;
            }),
          }));
          return;
        }
        // mode === "complete": this is the initial mark-roasted with
        // QC. Replicate the production-render markBatchRoasted logic
        // here, against App-scope state.
        if ((roastingQueue.completed || []).some(c => c.batchId === batch.id)) return;
        const now = Date.now();
        const greenLbs = Number(batch.greenLbs) || 0;
        const candidates = (greenLots || []).filter(l => !l.archivedAt && (l.currentWeightLbs || 0) > 0);
        const lot = greenLbs > 0 ? (
          candidates.find(l => l.productId && l.productId === batch.productId) ||
          candidates.find(l => {
            const pn = String(batch.productName || "").toLowerCase().trim();
            return pn && (String(l.name || "").toLowerCase().includes(pn) || String(l.coffeeName || "").toLowerCase().includes(pn));
          }) || null
        ) : null;
        const drawdown = lot
          ? { lotId: lot.id, lotName: lot.name, lbs: greenLbs }
          : { untracked: true, lbs: greenLbs };
        if (lot) {
          setGreenLots(prev => prev.map(l => l.id === lot.id
            ? { ...l, currentWeightLbs: Math.max(0, (Number(l.currentWeightLbs) || 0) - greenLbs) }
            : l));
        }
        const finalRoastedLbs = qc.roastedLbs != null ? Number(qc.roastedLbs) : (Number(batch.roastedLbs) || 0);
        setRoastingQueue(q => {
          const existingNum = q.batchNumbers?.[batch.id];
          const num = existingNum || `B-${String(q.nextBatchNum || 1).padStart(3, "0")}`;
          const completedEntry = {
            batchId: batch.id, batchNum: num,
            preBatchNum: batch.preBatchNum || null, roastId: null,
            productName: batch.productName, productId: batch.productId,
            batchIndex: batch.batchIndex, totalBatches: batch.totalBatches,
            sku: batch.sku, roastLevel: batch.roastLevel,
            greenLbs, roastedLbs: finalRoastedLbs,
            startedAt: now, completedAt: now,
            finalBt: null, events: [], samples: [],
            drawdown,
            roastedByUserId: user?.id || null,
            roastedByName: user?.name || null,
            qc: {
              roastedLbs: finalRoastedLbs,
              color: qc.color || null,
              rejected: !!qc.rejected,
              rejectReason: qc.rejected ? (qc.rejectReason || "") : null,
              notes: qc.notes || "",
              byUserId: user?.id || null,
              byName: user?.name || null,
              ts: now,
            },
          };
          return {
            ...q,
            ...(q.currentId === batch.id ? { currentId: null, currentRoastId: null } : {}),
            ...(q.nextId === batch.id ? { nextId: null } : {}),
            batchNumbers: { ...(q.batchNumbers || {}), [batch.id]: num },
            nextBatchNum: existingNum ? (q.nextBatchNum || 1) : (q.nextBatchNum || 1) + 1,
            completed: [completedEntry, ...(q.completed || [])],
          };
        });
        if (!qc.rejected) {
          setProducedOrders(prev => [{
            shopifyId: `batch_roast_${batch.id}`,
            id: `#batch-${batch.id}`,
            customer: "Roast batch",
            date: new Date(now).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
            items: [{ productId: batch.productId, name: batch.productName, variant: batch.roastLevel || "Whole Bean", sku: batch.sku || "", qty: Math.ceil(finalRoastedLbs || 1), grams: 340 }],
            total: 0,
            shippingAddress: { address1: "", city: "", province: "", province_code: "", zip: "", latitude: null, longitude: null, name: "" },
            producedAt: now,
          }, ...prev]);
        }
      };
      return (
        <QcNotesModal
          batch={qcModal.batch}
          mode={qcModal.mode}
          existingQc={existingCompleted?.qc || null}
          expectedLossPct={expectedLossPct}
          user={user}
          onClose={() => setQcModal(null)}
          onSubmit={onSubmit}
        />
      );
    })()}
    {/* Cupping session detail is rendered inline in the main content
        area; no modal here anymore. */}

    {/* Read-only Cupping Sheet — opened from the coffee detail page.
        Shows the latest cupping sample's radar + descriptors + notes
        in a sheet, no editing. If a coffee has multiple cupping
        records, a chip row at the top lets the user switch between
        them. */}
    {coffeeCuppingSheet && (() => {
      const { product, sample, alternates } = coffeeCuppingSheet;
      const close = () => setCoffeeCuppingSheet(null);
      const swap = (s) => setCoffeeCuppingSheet(curr => curr ? { ...curr, sample: s } : null);
      const sc = sample.scores || null;
      const pk = ["fragrance", "flavor", "aftertaste", "acidity", "body", "balance", "overall"];
      const hasPrim = sc && pk.some(k => sc[k] != null);
      const total = (() => {
        if (!hasPrim) return 0;
        const prim = pk.reduce((a, k) => a + (Number(sc[k]) || 0), 0);
        const secV = (k) => (sc[k] != null ? Number(sc[k]) : 10);
        const sec = secV("uniformity") + secV("cleanCup") + secV("sweetness");
        return +(prim + sec - (Number(sample.defects) || 0)).toFixed(2);
      })();
      const totalColor = total >= 90 ? "#B8860B" : total >= 85 ? "#16A34A" : total >= 80 ? "#111111" : "#6B7280";
      const totalGrade = total >= 90 ? "Outstanding" : total >= 85 ? "Excellent" : total >= 80 ? "Very Good" : total > 0 ? "Below Specialty" : "Unscored";
      const desc = sample.descriptors || {};
      const chips = [
        ...(desc.fragrance || "").split(",").map(s => s.trim()).filter(Boolean),
        ...(desc.flavor    || "").split(",").map(s => s.trim()).filter(Boolean),
        ...(desc.aftertaste|| "").split(",").map(s => s.trim()).filter(Boolean),
      ];
      const sessionLabel = sample.sessionName + (sample.sessionDate ? ` · ${new Date(sample.sessionDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : "");
      return (
        <div onClick={close} style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, fontFamily: "Inter, sans-serif", padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 760, maxHeight: "88vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.24)" }}>
            {/* Header */}
            <div style={{ padding: "22px 26px 16px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em" }}>Cupping Sheet</div>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: "#000", margin: "4px 0 2px", letterSpacing: "-0.01em" }}>{product.name}</h2>
                <div style={{ fontSize: 13, color: "#6B7280" }}>{sessionLabel}{sample.cupperName ? ` · ${sample.cupperName}` : ""}</div>
              </div>
              <button onClick={close} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4 }} aria-label="Close"><X size={18} /></button>
            </div>

            {/* If multiple cupping records exist, let the user pick */}
            {alternates && alternates.length > 1 && (
              <div style={{ padding: "10px 26px", borderBottom: "1px solid #F3F4F6", display: "flex", flexWrap: "wrap", gap: 6 }}>
                {alternates.map(a => {
                  const active = a.id === sample.id && a.sessionId === sample.sessionId;
                  return (
                    <button
                      key={`${a.sessionId}::${a.id}`}
                      onClick={() => swap(a)}
                      style={{ padding: "5px 10px", borderRadius: 7, border: `1px solid ${active ? "#000" : "#E5E7EB"}`, background: active ? "#000" : "#fff", color: active ? "#fff" : "#374151", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                    >{a.sessionName}{a.sessionDate ? ` · ${new Date(a.sessionDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}</button>
                  );
                })}
              </div>
            )}

            {/* Body */}
            <div style={{ padding: "20px 26px 24px" }}>
              {hasPrim ? (
                <>
                  <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 320px", display: "flex", justifyContent: "center" }}>
                      <CuppingRadar scores={sc} size={280} />
                    </div>
                    <div style={{ flex: "1 1 240px", display: "flex", flexDirection: "column", gap: 14 }}>
                      <div style={{ padding: "16px 18px", background: "#F9FAFB", borderRadius: 10, border: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.08em" }}>Total</div>
                          <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>{totalGrade}</div>
                        </div>
                        <div style={{ fontSize: 32, fontWeight: 800, color: totalColor, letterSpacing: "-0.02em" }}>{total.toFixed(2)}</div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                        {pk.map(k => (
                          <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "5px 10px", background: "#FAFAFA", borderRadius: 6, fontSize: 12 }}>
                            <span style={{ color: "#6B7280", textTransform: "capitalize" }}>{k}</span>
                            <span style={{ color: "#111", fontWeight: 600 }}>{(Number(sc[k]) || 0).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {["uniformity", "cleanCup", "sweetness"].map(k => {
                          const full = (sc[k] != null ? Number(sc[k]) : 10) === 10;
                          return (
                            <span key={k} style={{ padding: "3px 9px", borderRadius: 99, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", border: `1px solid ${full ? "#111" : "#FCA5A5"}`, background: full ? "#111" : "#FEF2F2", color: full ? "#fff" : "#B91C1C" }}>
                              {k === "cleanCup" ? "Clean cup" : k} · {full ? "10" : "0"}
                            </span>
                          );
                        })}
                        {(sample.defects || 0) > 0 && (
                          <span style={{ padding: "3px 9px", borderRadius: 99, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#DC2626" }}>−{sample.defects} defects</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {chips.length > 0 && (
                    <div style={{ marginTop: 18 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Descriptors</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {chips.map((d, i) => (
                          <span key={i} style={{ padding: "4px 11px", borderRadius: 99, background: "#fff", border: "1px solid #E5E7EB", fontSize: 13, color: "#111", fontWeight: 500 }}>{d}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {sample.notes && (
                    <div style={{ marginTop: 18, padding: "12px 14px", background: "#FAFAFA", borderLeft: "3px solid #2C5766", borderRadius: "0 8px 8px 0", fontSize: 14, color: "#374151", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                      {sample.notes}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 14 }}>This sample hasn't been scored yet.</div>
              )}
            </div>
          </div>
        </div>
      );
    })()}
    {/* Lot detail modal */}
    {selectedLot && (
      <LotDetailModal
        lot={selectedLot}
        onClose={() => setSelectedLotId(null)}
        onEvent={addLotEvent}
        onReconcile={reconcileLot}
        onArchive={archiveLot}
      />
    )}
    {selectedOrder && (
      <div
        onClick={() => setSelectedOrderId(null)}
        style={{
          position: "fixed", inset: 0, background: "rgba(17, 24, 39, 0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, fontFamily: "Inter, sans-serif",
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: "#fff", borderRadius: 12, width: "90%", maxWidth: 560,
            maxHeight: "85vh", overflowY: "auto",
            boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
          }}
        >
          {/* Modal header */}
          <div style={{ padding: "20px 24px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#000000" }}>{selectedOrder.id}</div>
              <div style={{ fontSize: 15, color: "#6B7280", marginTop: 2 }}>{selectedOrder.customer}</div>
              <div style={{ fontSize: 14, color: "#9CA3AF", marginTop: 4 }}>
                {selectedOrder.date}
                {typeof selectedOrder.total === "number" && ` · $${selectedOrder.total.toFixed(2)}`}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button
                onClick={() => downloadOrderPackingPDF(selectedOrder)}
                title="Download packing list PDF"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "7px 12px", borderRadius: 8,
                  border: "1px solid #000000", background: "#000000",
                  color: "#fff", fontSize: 13, fontWeight: 500,
                  cursor: "pointer", fontFamily: "Inter, sans-serif",
                }}
              >
                <ClipboardList size={13} strokeWidth={1.8} /> Packing list
              </button>
              <button
                onClick={() => setSelectedOrderId(null)}
                style={{ background: "transparent", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4 }}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Line items — one row per product */}
          <div style={{ padding: "8px 8px 16px" }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4, padding: "8px 16px 4px" }}>Items</div>
            {selectedOrder.items.map((item, idx) => {
              const isExcluded = excludedIds.has(item.productId);
              return (
                <div
                  key={`${item.productId}-${idx}`}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                    padding: "12px 16px", borderRadius: 8,
                    background: isExcluded ? "#F9FAFB" : "transparent",
                    margin: "2px 8px",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, opacity: isExcluded ? 0.55 : 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: "#000000", textDecoration: isExcluded ? "line-through" : "none" }}>
                      {item.name}
                    </div>
                    {item.variant && (
                      <div style={{ fontSize: 14, color: "#6B7280", marginTop: 2 }}>{item.variant}</div>
                    )}
                    <div style={{ fontSize: 14, color: "#9CA3AF", marginTop: 4 }}>
                      Qty: <span style={{ color: "#374151", fontWeight: 500 }}>{item.qty}</span>
                      {item.sku && <> · SKU {item.sku}</>}
                    </div>
                  </div>
                  <button
                    onClick={() => isExcluded ? removeExcluded(item.productId) : addExcluded(item.productId, item.name)}
                    style={{
                      padding: "6px 12px", borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif",
                      border: "1px solid #E5E7EB",
                      background: isExcluded ? "#fff" : "#F9FAFB",
                      color: isExcluded ? "#2563EB" : "#6B7280",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isExcluded ? "Unfilter" : "Filter"}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Wholesale-only actions: cancel + mark delivered. Shopify
              orders are managed via Shopify Admin; we don't double-track
              their status. */}
          {selectedOrder.kind === "wholesale_lite" && (() => {
            const internalId = selectedOrder.internalId || selectedOrder.id;
            const wsOrder = (wholesaleOrders || []).find(o => o.id === internalId);
            const status = wsOrder?.status || "pending";
            const patchStatus = async (next) => {
              try {
                const r = await fetch(`/api/wholesale/orders/${encodeURIComponent(internalId)}`, {
                  method: "PATCH",
                  credentials: "same-origin",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ status: next }),
                });
                if (r.ok) {
                  const d = await r.json();
                  setWholesaleOrders(prev => prev.map(o => o.id === internalId ? d.order : o));
                  if (next === "rejected") setSelectedOrderId(null);
                }
              } catch { /* ignore */ }
            };
            return (
              <div style={{ padding: "14px 24px 18px", borderTop: "1px solid #F3F4F6", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div style={{ fontSize: 12, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Status · <span style={{ color: "#374151", fontWeight: 600 }}>{status}</span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {status !== "delivered" && (
                    <button
                      onClick={() => patchStatus("delivered")}
                      style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                    >Mark delivered</button>
                  )}
                  {status === "delivered" && (
                    <button
                      onClick={() => patchStatus("approved")}
                      style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                    >Unmark delivered</button>
                  )}
                  {status !== "rejected" && (
                    <button
                      onClick={() => { if (window.confirm("Cancel this order? This can't be undone.")) patchStatus("rejected"); }}
                      style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #FECACA", background: "#fff", color: "#B91C1C", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                    >Cancel order</button>
                  )}
                </div>
              </div>
            );
          })()}

          <div style={{ padding: "12px 24px 20px", borderTop: "1px solid #F3F4F6", fontSize: 14, color: "#9CA3AF", lineHeight: 1.5 }}>
            Filtered items are hidden from every order and excluded from every count. Manage the full list in Settings.
          </div>
        </div>
      </div>
    )}
    {/* Ship-one modal (Ready tab → Ship button) */}
    {shipModal && (
      <ShipModal
        order={shipModal.order}
        fromAddress={shippingSettings?.fromAddress || {}}
        parcelPresets={shippingSettings?.parcelPresets || []}
        onClose={() => setShipModal(null)}
        onGetRates={getRatesForOrder}
        onBuy={buyLabelsForJobs}
        onGoToSettings={() => { setShipModal(null); setPage("settings"); }}
      />
    )}
    {/* Batch ship modal (Ready tab → Ship selected) */}
    {batchShipModal && (
      <BatchShipModal
        orders={batchShipModal.orders}
        fromAddress={shippingSettings?.fromAddress || {}}
        parcelPresets={shippingSettings?.parcelPresets || []}
        onClose={() => { setBatchShipModal(null); clearSelectedForShip(); }}
        onBuy={buyLabelsForJobs}
        onGoToSettings={() => { setBatchShipModal(null); setPage("settings"); }}
      />
    )}
    {/* Welcome / demo-picker modal — shows only for the admin who just
        created the workspace (flag set in Onboarding.jsx). Joiners never
        see it. Once the creator picks, the flag and demoMode both persist
        so it never fires again. */}
    {demoMode === null && showWelcome && (
      <div style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0, 0, 0, 0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "Inter, sans-serif",
      }}>
        <div style={{
          background: "#fff", borderRadius: 16, padding: "36px 36px 28px",
          maxWidth: 460, width: "92%",
          boxShadow: "0 24px 64px rgba(0,0,0,0.28)",
        }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#000", marginBottom: 10, letterSpacing: "-0.01em" }}>
            Welcome
          </div>
          <div style={{ fontSize: 15, color: "#52525B", lineHeight: 1.55, marginBottom: 24 }}>
            {shopConnected
              ? "Your store is connected. You're ready to go — all data comes directly from your store."
              : "Your roastery operation is about to get a big upgrade. We recommend starting with dummy data, so you can get familiar with our tool — sample data will disappear the moment you connect to your own."}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
            {!shopConnected && (
            <button
              onClick={() => { setDemoMode(true); setShowWelcome(false); setShowStoreConnect(!shopConnected); try { localStorage.removeItem("pr:just-created-team"); } catch { /* ignore */ } }}
              style={{
                padding: "13px 18px", borderRadius: 10, border: "none",
                background: "#000", color: "#fff",
                fontSize: 15, fontWeight: 600, fontFamily: "Inter, sans-serif",
                cursor: "pointer", textAlign: "center",
              }}
            >
              Start with sample data
            </button>
            )}
            <button
              onClick={() => { setDemoMode(false); setShowWelcome(false); setShowStoreConnect(!shopConnected); try { localStorage.removeItem("pr:just-created-team"); } catch { /* ignore */ } }}
              style={{
                padding: "13px 18px", borderRadius: 10, border: "1px solid #E4E4E7",
                background: "#fff", color: "#000",
                fontSize: 15, fontWeight: 600, fontFamily: "Inter, sans-serif",
                cursor: "pointer", textAlign: "center",
              }}
            >
              Start blank
            </button>
          </div>
          <img src="/brand/elion-roaster.svg" alt="Elion Roaster" style={{ height: 28, width: "auto", display: "block", margin: "0 auto" }} />
        </div>
      </div>
    )}

    {/* Store-connect picker — shown right after the welcome modal so
        paid signups land on the integration list instead of having to
        hunt for it in Settings. Only Shopify wired up today; future
        connectors slot in here. */}
    {showStoreConnect && !shopConnected && (
      <div style={{
        position: "fixed", inset: 0, zIndex: 9998,
        background: "rgba(0, 0, 0, 0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "Inter, sans-serif", padding: 24,
      }}>
        <div style={{
          background: "#fff", borderRadius: 16, padding: "32px 32px 24px",
          maxWidth: 460, width: "100%",
          boxShadow: "0 24px 64px rgba(0,0,0,0.28)",
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.2, marginBottom: 6 }}>Step 2 of 2</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#000", marginBottom: 8, letterSpacing: "-0.01em" }}>
            Connect your store
          </div>
          <div style={{ fontSize: 14, color: "#52525B", lineHeight: 1.55, marginBottom: 20 }}>
            Pull orders + push production tags automatically. You can connect more later from Settings → Store Connect.
          </div>

          {/* Shopify card */}
          <div style={{ border: "1px solid #E5E7EB", borderRadius: 12, padding: 18, marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "#95BF47", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 20, fontWeight: 800, fontFamily: "Inter, sans-serif" }}>S</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111" }}>Shopify</div>
                <div style={{ fontSize: 12, color: "#6B7280" }}>Connect your storefront and start syncing in seconds.</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                value={connectInput}
                onChange={(e) => { setConnectInput(e.target.value); setConnectError(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") connectShopify(); }}
                placeholder="your-store.myshopify.com"
                autoFocus
                style={{ flex: 1, padding: "10px 12px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 14, fontFamily: "Inter, sans-serif", outline: "none", color: "#111" }}
              />
              <button
                onClick={connectShopify}
                style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}
              >Connect</button>
            </div>
            {connectError && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#B91C1C", lineHeight: 1.5 }}>{connectError}</div>
            )}
          </div>

          {/* More-coming-soon strip — communicates that other connectors
              are on the roadmap without listing them. */}
          <div style={{ fontSize: 12, color: "#9CA3AF", textAlign: "center", marginBottom: 18, lineHeight: 1.5 }}>
            More connectors coming soon (Square, WooCommerce, custom REST).
          </div>

          <button
            onClick={() => setShowStoreConnect(false)}
            style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "none", background: "transparent", color: "#6B7280", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}
          >I'll do this later</button>
        </div>
      </div>
    )}

    {/* Subtract the demo banner height from the shell so the whole
        layout stays inside the viewport — otherwise paddingTop:41
        + height:100vh stacks past the bottom and the page picks up
        a vertical scroll. Tracks the actual banner padding (10px
        top + 10px bottom + ~21px line-height = 41px). */}
    <div style={{ display: "flex", flexDirection: "column", height: isDemo ? "calc(100vh - 41px)" : "100vh", fontFamily: "Inter, sans-serif", background: "#FFFFFF" }}>

    {/* ── Import grace banner (full-width, shows everywhere) ── */}
    {(effectiveUsage?.importGraceDaysLeft ?? 0) > 0 && (() => {
      const gd = effectiveUsage.importGraceDaysLeft;
      const urgent = gd <= 2;
      return (
        <div style={{
          width: "100%", flexShrink: 0,
          background: urgent
            ? "linear-gradient(90deg, #0E7490 0%, #0891B2 100%)"
            : "linear-gradient(90deg, #06B6D4 0%, #22D3EE 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 8, padding: "8px 20px",
        }}>
          <Clock size={13} strokeWidth={2.5} color={urgent ? "#A5F3FC" : "#083344"} />
          <span style={{ fontSize: 13, fontWeight: 600, color: urgent ? "#ECFEFF" : "#083344", letterSpacing: "0.01em" }}>
            {gd} day{gd !== 1 ? "s" : ""} left on your unlimited order import window.
          </span>
        </div>
      );
    })()}

    <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

      {/* ── COL 1: Icon sidebar (70px, true black, rounded corners) ── */}
      <div style={{
        width: 70, background: "#000000", display: "flex", flexDirection: "column",
        alignItems: "center", flexShrink: 0,
        borderRadius: 14, margin: "8px 0 8px 8px",
        boxShadow: "2px 0 8px rgba(0,0,0,0.12)",
      }}>
        {/* Logo — the Elion Roaster "R" mark. Cyan on the dev Fly
            app so the environment is unmistakable at a glance; white on
            prod. We detect the dev build two ways: Vite's built-in
            import.meta.env.DEV flag (for local npm run dev), and the
            hostname (for the deployed dev.fly.dev machine). */}
        {(() => {
          const isDevVariant = (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.DEV)
            || (typeof window !== "undefined" && /parallel-roaster-dev|roaster-dev/i.test(window.location.hostname));
          const rColor = isDevVariant ? "#24D3EF" : "#ffffff";
          return (
            <div style={{ width: 70, height: 70, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg
                width={38} height={38}
                viewBox="212.97 303.76 185.18 185.18"
                style={{ display: "block", borderRadius: 8 }}
                aria-label="Elion Roaster"
              >
                <rect fill="#010101" x="212.97" y="303.76" width="185.18" height="185.18"/>
                <path fill={rColor} d="M263.15,340.81h56.86c5.59,0,10.02,1.31,14.62,5.42l9.2,8.38c4.93,4.44,6.41,10.19,6.41,14.62v17.09c0,4.44-1.15,10.52-6.41,14.62l-6.9,5.42c-3.78,2.96-4.76,6.74-2.63,11.34l18.24,38.12h-26.29l-15.78-34.84c-1.97-4.11-4.93-6.24-9.53-6.24h-6.24c-5.26,0-8.22,2.79-8.22,8.22v32.86h-23.33v-115.03ZM326.9,384.02v-12.49c0-7.72-4.77-10.68-10.52-10.68h-21.69c-5.26,0-8.22,2.79-8.22,8.22v17.42c0,5.42,2.96,8.22,8.22,8.22h21.69c5.75,0,10.52-2.96,10.52-10.68Z"/>
              </svg>
            </div>
          );
        })()}

        {/* Nav icons — separators inline, Settings pinned to bottom via marginTop:auto */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, paddingTop: 12, paddingBottom: 12, flex: 1, width: "100%" }}>
          {navItems.map((n, i) => {
            if (n.type === "sep") {
              /* Slightly heavier separator so the two nav groups read as
                 distinct sections — user wanted a bit more breathing room
                 and a visible hairline. */
              return <div key={`sep-${i}`} style={{ width: 32, height: 1, background: "rgba(255,255,255,0.22)", margin: "14px 0" }} />;
            }
            /* Nav items that route into the Production page carry a
               prodView — match on both page AND prodView so only the
               actively-viewed sub-tab lights up. Everything else uses
               page-id match. */
            const active = !upgradeModule && (n.prodView
              ? (page === (n.page || "production") && prodView === n.prodView)
              : page === n.id);
            const Icon = n.icon;
            const accent = n.accentColor;
            const isLocked = !!n.locked;
            const baseStyle = {
              width: 28, height: 28, borderRadius: 7, border: "none", cursor: "pointer",
              background: active ? "rgba(255,255,255,0.12)" : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background 0.15s",
              marginTop: n.pinBottom ? "auto" : 0,
              textDecoration: "none",
            };
            const iconColor = accent ? accent : active ? "#fff" : "rgba(255,255,255,0.45)";
            // Default hover on black-rail nav mirrors the active-state bg so
            // hover/active read as the same affordance.
            const hoverBg = n.hoverBg || (accent ? accent : "rgba(255,255,255,0.12)");
            // External links (e.g. Help → Discuss) open in a new tab.
            // Icon inherits color via currentColor so it can transition
            // smoothly with the background on hover.
            if (n.external) {
              return (
                <a
                  key={n.id}
                  href={n.external}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={n.title || ""}
                  style={{
                    ...baseStyle,
                    color: iconColor,
                    transition: "background 0.18s ease, color 0.18s ease",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = hoverBg;
                    e.currentTarget.style.color = "#ffffff";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = iconColor;
                  }}
                >
                  <Icon size={20} strokeWidth={1.8} />
                </a>
              );
            }
            return (
              <button
                key={n.id}
                title={isLocked ? `${n.title || n.id} — upgrade to unlock` : (n.title || n.id)}
                onClick={() => {
                  if (isLocked) {
                    setUpgradeModule(n.module || n.addon || n.id);
                    return;
                  }
                  setUpgradeModule(null);
                  /* Production sub-nav items jump to the production page
                     AND set the sub-view in one click, then reset the
                     prodTab so clicking Roasting from Routes's Activity
                     doesn't dump the user into Roasting→Activity
                     unexpectedly. Roasting → Planning (the only visible
                     sub-tab there); other prodViews → "main". */
                  setPage(n.page || n.id);
                  if (n.prodView) {
                    setProdView(n.prodView);
                    setProdTab(n.prodView === "breakdown" ? "planning" : "main");
                  }
                  if (n.id !== "products") setSelectedProduct(null);
                }}
                style={{
                  ...baseStyle,
                  color: isLocked ? "rgba(255,255,255,0.32)" : iconColor,
                  position: "relative",
                  transition: "background 0.18s ease, color 0.18s ease",
                }}
                onMouseEnter={e => {
                  if (!active) {
                    e.currentTarget.style.background = hoverBg;
                    e.currentTarget.style.color = "#ffffff";
                  }
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = active ? "rgba(255,255,255,0.12)" : "transparent";
                  e.currentTarget.style.color = isLocked ? "rgba(255,255,255,0.32)" : iconColor;
                }}
              >
                <Icon size={20} strokeWidth={active ? 1.8 : 1.4} />
                {isLocked && (
                  <span style={{
                    position: "absolute", right: -2, bottom: -2,
                    width: 14, height: 14, borderRadius: "50%",
                    background: "#000", border: "1.5px solid #fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Lock size={8} color="#fff" strokeWidth={2.6} />
                  </span>
                )}
              </button>
            );
          })}
          {/* ── Super-admin plan simulator toggle (only for john@elionlabs.org) ── */}
          {isSuperAdmin && (
            <button
              onClick={() => setPlanSimOpen(o => !o)}
              title={planSim ? `Simulating: ${planSim.plan}${(planSim.addons||[]).length ? " +" + planSim.addons.join("+") : ""}` : "Plan simulator"}
              style={{
                width: 42, height: 42, borderRadius: 10, border: "none", outline: "none", cursor: "pointer",
                background: planSim ? "rgba(34,211,238,0.10)" : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: planSim ? "#22D3EE" : "rgba(255,255,255,0.3)",
                transition: "background 0.15s, color 0.15s",
                position: "relative",
                boxShadow: planSim ? "inset 0 0 0 1px rgba(34,211,238,0.25)" : "none",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(34,211,238,0.10)"; e.currentTarget.style.color = "#22D3EE"; e.currentTarget.style.boxShadow = "inset 0 0 0 1px rgba(34,211,238,0.25)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = planSim ? "rgba(34,211,238,0.10)" : "transparent"; e.currentTarget.style.color = planSim ? "#22D3EE" : "rgba(255,255,255,0.3)"; e.currentTarget.style.boxShadow = planSim ? "inset 0 0 0 1px rgba(34,211,238,0.25)" : "none"; }}
            >
              <SlidersHorizontal size={15} strokeWidth={1.5} />
              {planSim && <span style={{ position: "absolute", top: 8, right: 8, width: 5, height: 5, borderRadius: "50%", background: "#22D3EE", boxShadow: "0 0 4px #22D3EE" }} />}
            </button>
          )}
          {/* ── Demo team switcher — only in demo mode ── */}
          {user?.isDemo && <DemoUserSwitcher activeUser={user} />}
          {/* ── User avatar → Settings (sits at the very bottom) ── */}
          <button
            onClick={() => { setUpgradeModule(null); setPage("settings"); }}
            title={user ? `${user.name || user.email} — ${ROLE_LABELS[user.role] || user.role}\nSettings` : "Settings"}
            style={{
              width: 42, height: 42, borderRadius: "50%", border: "none", cursor: "pointer",
              background: page === "settings" ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.08)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontSize: 13, fontWeight: 600,
              marginTop: 4,
              outline: page === "settings" ? "2px solid rgba(255,255,255,0.4)" : "none",
              outlineOffset: 2,
              transition: "background 0.15s, outline 0.15s",
            }}
            onMouseEnter={e => { if (page !== "settings") e.currentTarget.style.background = "rgba(255,255,255,0.16)"; }}
            onMouseLeave={e => { if (page !== "settings") e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
          >
            {avatarInitials(user)}
          </button>
          <button
            onClick={logout}
            title="Sign out"
            style={{
              width: 42, height: 42, borderRadius: 10, border: "none", cursor: "pointer",
              background: "transparent", display: "flex", alignItems: "center", justifyContent: "center",
              marginBottom: 6,
              color: "rgba(255,255,255,0.45)",
              transition: "background 0.18s ease, color 0.18s ease",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = "rgba(255,255,255,0.12)";
              e.currentTarget.style.color = "#ffffff";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "rgba(255,255,255,0.45)";
            }}
          >
            <LogOut size={18} strokeWidth={1.4} />
          </button>
        </div>
      </div>

      {/* ── COL 2: Secondary panel (260px, white bg with a divider rail) ── */}
      <div style={{
        width: 260, background: "#FFFFFF",
        borderRight: "1px solid #E4E4E7",
        display: "flex", flexDirection: "column", flexShrink: 0,
      }}>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {renderPanel()}
        </div>
        <div style={{ padding: "20px 20px 18px", borderTop: "1px solid #E4E4E7" }}>
          <div style={{ fontSize: 12, color: "#9CA3AF" }}>© {new Date().getFullYear()} Elion Labs LLC</div>
        </div>
      </div>

      {/* ── COL 3: Main content ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "24px 32px", background: "#fff" }}>
        {billingSuccessBanner && (
          <div
            onClick={() => { setBillingSuccessBanner(false); setPage("orders"); }}
            style={{
              position: "fixed", inset: 0, zIndex: 9999,
              background: "#000", display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              fontFamily: "Inter, sans-serif", cursor: "pointer",
            }}
          >
            <div style={{ textAlign: "center", maxWidth: 520, padding: "0 32px" }}>
              <div style={{ marginBottom: 32 }}>
                <CheckCircle2 size={56} color="#4ADE80" strokeWidth={1.4} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#4ADE80", marginBottom: 16 }}>
                You&rsquo;re in
              </div>
              <div style={{ fontSize: 48, fontWeight: 800, color: "#fff", letterSpacing: "-0.03em", lineHeight: 1.05, marginBottom: 20 }}>
                {usage?.label || "Roaster"} plan<br/>activated.
              </div>
              <div style={{ fontSize: 17, color: "rgba(255,255,255,0.55)", lineHeight: 1.6, marginBottom: 48 }}>
                Your roastery just got a serious upgrade.<br/>Inventory, live roast monitor, cupping, routes — all yours.
              </div>
              <div
                onClick={e => { e.stopPropagation(); setBillingSuccessBanner(false); setPage("orders"); }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 10,
                  padding: "14px 32px", borderRadius: 12, background: "#fff", color: "#000",
                  fontSize: 15, fontWeight: 700, cursor: "pointer", letterSpacing: "-0.01em",
                }}
              >
                Let&rsquo;s go <span style={{ fontSize: 18 }}>→</span>
              </div>
              <div style={{ marginTop: 20, fontSize: 12, color: "rgba(255,255,255,0.25)" }}>click anywhere to continue</div>
            </div>
          </div>
        )}
        {effectiveUsage && effectiveUsage.pct >= 80 && (
          <div style={{
            background: effectiveUsage.atLimit ? "#FEF2F2" : "#FFFBEB",
            border: `1px solid ${effectiveUsage.atLimit ? "#FCA5A5" : "#FDE68A"}`,
            borderRadius: 10, padding: "10px 16px", marginBottom: 16,
            display: "flex", alignItems: "center", gap: 10, fontSize: 13,
            color: effectiveUsage.atLimit ? "#DC2626" : "#92400E",
          }}>
            <AlertTriangle size={15} strokeWidth={1.6} />
            <span style={{ flex: 1 }}>
              {effectiveUsage.atLimit
                ? `Order limit reached — ${(effectiveUsage.current||0).toLocaleString()} of ${(effectiveUsage.limit||0).toLocaleString()} orders synced this month. Upgrade to continue syncing.`
                : `${effectiveUsage.pct}% of your ${(effectiveUsage.limit||0).toLocaleString()} monthly orders used this month.`}
            </span>
            <button onClick={async () => {
              const r = await fetch("/api/billing/checkout", { method: "POST", headers: {"Content-Type":"application/json"}, credentials: "same-origin", body: JSON.stringify({ plan: "roaster", interval: "monthly" }) });
              const data = await r.json();
              if (data.url) window.location.href = data.url;
            }} style={{ border: "none", background: "transparent", color: "inherit", fontWeight: 600, cursor: "pointer", fontSize: 13, fontFamily: "Inter, sans-serif", textDecoration: "underline" }}>
              Upgrade
            </button>
          </div>
        )}
        {renderContent()}
      </div>
    </div>
    </div>
    {/* Super-admin plan simulator panel */}
    {isSuperAdmin && planSimOpen && (
      <PlanSimulatorPanel
        usage={usage}
        planSim={planSim}
        simPlans={SIM_PLANS}
        simAddons={SIM_ADDONS}
        onApply={(sim) => { setPlanSim(sim); setPlanSimOpen(false); }}
        onReset={() => { setPlanSim(null); setPlanSimOpen(false); }}
        onClose={() => setPlanSimOpen(false)}
      />
    )}
    {/* Invoice modal */}
    {invoiceModal && (
      <InvoiceModal
        mode={invoiceModal === "new" ? "new" : "edit"}
        initial={invoiceModal === "new" ? null : invoiceModal}
        billingSettings={billingSettings}
        greenLots={greenLots}
        wholesaleCustomers={wholesaleCustomers}
        onClose={() => setInvoiceModal(null)}
        onSaved={(inv) => {
          if (invoiceModal === "new") {
            setBillingInvoices(prev => [inv, ...prev]);
          } else {
            setBillingInvoices(prev => prev.map(i => i.id === inv.id ? inv : i));
          }
          setInvoiceModal(null);
        }}
        effectiveDemoMode={effectiveDemoMode}
      />
    )}
    {/* PO modal */}
    {poModal && (
      <POModal
        mode={poModal === "new" ? "new" : "edit"}
        initial={poModal === "new" ? null : poModal}
        greenLots={greenLots}
        onClose={() => setPOModal(null)}
        onSaved={(po) => {
          if (poModal === "new") {
            setBillingPOs(prev => [po, ...prev]);
          } else {
            setBillingPOs(prev => prev.map(p => p.id === po.id ? po : p));
          }
          setPOModal(null);
        }}
        effectiveDemoMode={effectiveDemoMode}
      />
    )}
    {/* Global roast-curve modal — shared by Roasting tab and Activity view */}
    {selectedCompletedId && (() => {
      const entry = (roastingQueue.completed || []).find(c => c.batchId === selectedCompletedId);
      if (!entry) return null;
      return <CompletedRoastModal entry={entry} onClose={() => setSelectedCompletedId(null)} />;
    })()}
    </>
  );
}
